import { useState, useRef, useEffect, useMemo, useCallback, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { fieldInputProps, applyBrand, brandTokensFor, isInstalledApp, T, MIDDLEWARE_URL, ISO_DATE, US_DATE, formatOpenedDate, seenOnce, LOGO_SRC, dutiesBrowserOf } from "./shared";
import { Tour, TourNudge, tourSeen, rememberTour } from "./Tour";
import { SearchOrderPage, MatterOrders } from "./SearchOrder";
import { MatterWorkspace, ContactsPage, MatterTabs, BrandSettings, ContractTemplateSettings, ContractTemplateSetup, GlobalSearch, LookNews, NewMatterDialog, NumberingSettings, FolderSettings, StageSettings, RibbonItems, RibbonDivider, RibbonMenu, useRibbonSlot, CONTRACT_TAB_LABEL, featureReady } from "./Practice";
import { configureOnboarding, markSeen, useFirstVisit } from "./onboarding";
import { SignInScreen, isSessionToken, readStoredSession } from "./staffAuth";
import { applyFirmTab } from "./firmTab";
import { AccountSettings, useAccount } from "./teamAdmin";
import { NumberedClauseInput } from "./clauseNumbering";
import { paginate, fillPages, SHEET_FILL_PX, SHEET_CSS } from "./contractPages";

// The pages of a contract are drawn by `contractPages.js`, shared with the
// public new sale page's draft; re-exported for the tests that import them here.
export { paginate, fillPages, SHEET_FILL_PX, SHEET_CSS };

// Neutral ink. The keys did not change when the values did, which is why
// recolouring the whole portal is one constant and not a hundred edits.
//
// `#333` is a near-exact neutral swap for the brown it replaces: #362F2A is
// 18.8% lightness at hue 25°, #333 is 20% at no hue at all. So the weight of
// the page is unchanged and only the cast is gone — the same rule the raised
// surface followed when the tan #EDE4D5 became #E1E1E1 at matching lightness.
//
// `warmGrey` is only ever the text *on* a dark ground — the primary button and
// the toast — so it is white now rather than tan. `mauve` is only the undo
// toast, and it matches the other one.
//
// `red` does not move. It was already #FE5442 and it is still the one accent.
//
// **It lives in `shared.js` now**, with the middleware's address, because this
// app has a second surface: the agent portal. Two copies is how the agent's
// screen ends up a slightly different red, or pointed at a service that has
// moved — and it very nearly did, since that screen was drafted against the
// warm palette this one has already left behind.

// Dashboard row layout — header and rows must stay in step
const GRID_COLS = "1fr 140px 100px 104px 124px";
// The same grid with room for the checkbox, used only while selecting — so the
// ordinary list has no empty gutter waiting for a feature nobody asked for.
const GRID_COLS_SELECTING = `28px ${GRID_COLS}`;
// The list's sortable columns, in column order — the header row on a wide
// screen and the Sort select on a phone read the one list.
const MATTER_SORT_KEYS = [
  { key: "client", label: "Matter" },
  { key: "fileHandler", label: "File handler" },
  { key: "opened", label: "Opened" },
  { key: "searches", label: "Searches" },
  { key: "status", label: "Status" },
];

const TOKEN_KEY = "s32_portal_token";
// Which matter-page layout this tab is using. Its own key, per tab, so trying
// the new one never changes what anybody else sees.
const UI_KEY = "s32_portal_ui";
const MATTERS_KEY = "s32_portal_matters";

// Read ?token= / ?fh= / ?user= once at startup so the portal can be embedded in
// WordPress with the file handler and access key baked into the iframe src.
const readParams = () => {
  let token = "", fh = "", ui = "", user = "", matters = "";
  try {
    const p = new URLSearchParams(window.location.search);
    token = p.get("token") || "";
    fh = (p.get("fh") || "").trim();
    ui = (p.get("ui") || "").trim().toLowerCase();
    matters = (p.get("matters") || "").trim().toLowerCase();
    // Who the portal was opened as. `?fh=` is the same name doing a different
    // job — it filters the dashboard — so either answers, and neither is an
    // identity: it is a name the embed put in a URL.
    user = ((p.get("user") || "").trim() || fh).replace(/\s+/g, " ");
  } catch (e) { /* non-browser environment */ }

  // ?ui=new opts this tab into the redrawn matter page; anything else, and
  // every URL that has never carried the parameter, gets the layout that has
  // always been there. **Absent means old** — the parameter can only ever turn
  // the new one on, so a staff member who has never heard of it cannot arrive
  // at a screen they do not recognise.
  //
  // Remembered per tab so the choice survives a reload, and the URL still wins
  // over the memory: pasting ?ui=new always lands on the new one, whatever the
  // tab last did.
  try {
    if (ui) sessionStorage.setItem(UI_KEY, ui);
    else ui = sessionStorage.getItem(UI_KEY) || "";
  } catch (e) { /* storage blocked — the parameter still works for this load */ }

  // ?matters=all opens this tab on the office's whole book rather than on the
  // section 32 work. Same shape as ?ui= above, and **absent means the s32
  // portal** — the parameter can only ever widen the list, so a staff member
  // who has never heard of it cannot arrive at a screen full of files that are
  // not theirs to prepare. Remembered per tab, and the URL still wins.
  try {
    if (matters) sessionStorage.setItem(MATTERS_KEY, matters);
    else matters = sessionStorage.getItem(MATTERS_KEY) || "";
  } catch (e) { /* storage blocked — the parameter still works for this load */ }

  // Keep the token for this tab, then take it out of the visible URL so it
  // stays out of screenshots, bookmarks and the address bar.
  try {
    if (token) {
      sessionStorage.setItem(TOKEN_KEY, token);
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.toString());
    } else {
      // A signed-in person's session, when this tab was not handed a key —
      // the portal opened on its own rather than from WordPress. The key
      // still wins when both are there, so the embed behaves as it always did.
      token = sessionStorage.getItem(TOKEN_KEY) || readStoredSession();
    }
  } catch (e) { /* storage blocked — token still works for this page load */ }

  return { token, fh, ui, user, matters };
};

// Where the portal is embedded, so a matter can be opened in its own tab.
//
// **A new tab is a new browsing context, so it inherits nothing.** The token
// arrives in the iframe src and is put in `sessionStorage`, which is per-tab by
// design — so a ctrl-click on a matter row opened the Hosting URL with no token
// and an empty store, and the matter would not load. What crosses that boundary
// is the *link*, and the one thing that must never travel in a link is the
// staff key: it opens every matter in the system.
//
// So the link points at the **embedder** instead. WordPress already injects the
// token into the iframe src for whoever is signed in; a new tab that goes
// through WordPress gets one the same way, and no credential is anywhere new.
//
// **`localStorage` is the obvious fix and is a trap.** It is shared across tabs
// on an origin, so it looks right — but this runs in a third-party iframe, and
// Safari, Firefox and current Chrome all partition third-party storage. The
// frame writes to a bucket keyed to the embedder; a top-level tab reads the
// unpartitioned one. It would work for whoever tested it and silently fail for
// somebody else, which is worse than not working at all.
//
// **The embedder has to tell us its own URL**, because we cannot derive it: the
// default referrer policy trims a cross-origin iframe's `document.referrer` to
// the bare origin, so the path and query — which carry who is signed in — are
// not visible here. `?embed=` is that URL, and it is *not* stripped from our own
// URL the way the token is: it is not a secret, and keeping it means a reload
// can still build links.
//
// **It becomes an href, so it is validated rather than trusted.** The parameter
// is on a URL anybody can load, so an unchecked value would turn every matter
// row into an open redirect. It has to be `https:`, and it has to be the same
// origin as whoever is actually framing us — which is the one thing the trimmed
// referrer *does* tell us reliably. Unframed there is no embedder and no
// referrer, and the ordinary hash link is already right.
//
// A value that fails any of that falls back to the hash link, so the worst case
// is the behaviour we had before this existed rather than a link somewhere
// nobody chose. If ctrl-click stops working, that fallback is where to look —
// an embedder sending `Referrer-Policy: no-referrer` leaves nothing to check
// the parameter against, and refusing is the safe direction.
const readEmbed = () => {
  try {
    if (window.top === window.self) return "";            // not embedded
    const said = new URLSearchParams(window.location.search).get("embed") || "";
    if (!said) return "";
    const url = new URL(said, window.location.href);
    if (url.protocol !== "https:") return "";
    const framedBy = document.referrer ? new URL(document.referrer).origin : "";
    if (!framedBy || url.origin !== framedBy) return "";
    return url.toString();
  } catch (e) { return ""; }                              // unparseable, or no browser
};

const EMBED_URL = readEmbed();

// **A matter popped out into a window of its own** — the installed app's
// answer to Smokeball opening each matter as a separate window. It carries no
// portal navigation and no tabs: it is the one matter. The staff key is not in
// its URL; a window opened from this one starts with a copy of this one's
// session storage, which is where the key is kept.
export const POPOUT = (() => {
  try { return new URLSearchParams(window.location.search).get("window") === "matter"; } catch { return false; }
})();

const OPEN_MATTERS_KEY = "provey.openMatters";
const MATTER_CHANNEL = "provey-matter-changes";

// What an open matter's tab says: its number and the client's surname.
export const matterTabLabel = (m = {}) => {
  const ref = m.ref ?? m.matterRef ?? m.id ?? "";
  const surname = String(m.client || "").trim().split(/\s+/).filter(Boolean).pop() || "";
  return surname ? `${ref} · ${surname}` : String(ref);
};

// The address a popped-out matter opens at.
export const popOutUrl = (ref, loc = window.location) => `${loc.origin}${loc.pathname}?window=matter${matterHash(ref)}`;

const { token: ACCESS_TOKEN, fh: FH_FILTER, user: PORTAL_USER, matters: MATTER_SCOPE, ui: UI_CHOICE } = readParams();

// ── Which book this tab is open on ─────────────────────────────────────────
//
// **This is the section 32 portal, and most of what the middleware holds is
// not section 32 work.** Form 265 fires on entry update across the office's
// whole practice, so a matter is opened here for purchase files and for sale
// files finished long before this tool existed — deliberately, since the agent
// portal tracks an agency's whole book. `/matters` holds them back, and this
// screen is built around the ones that *are* ours: the rail, the step card and
// every banner on it are about preparing a statement.
//
// So they are not a tab beside Archived. Archived is part of the s32 workflow
// — a list staff move matters into and out of — and putting the office's other
// files next to it says they are the same kind of thing on the one screen
// whose job is to say they are not.
//
// `?matters=all` is the whole switch, and **absent is the s32 portal**: no
// second request, no extra control, nothing on any row. A tab that has not
// asked for the whole book behaves exactly as it did before this existed.
//
// Presentation rather than access control, exactly like `?fh=` — the endpoint
// serves either list to the same token, and this decides which is asked for.
export const ALL_MATTERS = MATTER_SCOPE === "all";

// The office's record of who has already seen the tour and the help panel.
// Told who is looking here rather than reading the URL itself: `readParams`
// strips the token out of the visible URL and keeps it under a key this file
// owns, so a second reader of either would be a second copy of both.
configureOnboarding({ user: PORTAL_USER, token: ACCESS_TOKEN });

// **The redrawn matter page is the only one now.** It was opt-in behind
// `?ui=new` while both existed, and the switch is gone with the layout it
// chose between: the firm moved across, so a second implementation of the one
// screen staff live in was two places every panel had to be added to — which
// is the drift the `CustomisationSection` note warns about, paid every time.
//
// `?ui=` on an old bookmark is read and ignored rather than refused: a link
// somebody saved should open the matter, not an error about a layout.
//
// **`?ui=practice` is the switch's second life: the main module's look.** The
// Fluent skin — brand title bar, matter tabs, the matter opening on Matter
// details, Contacts — is off until the firm turns it on (`experience` on
// `GET /practice/appearance`), so merging it changes nothing anybody sees. A
// demo opts one browser tab in with `?ui=practice`, remembered for the tab the
// same way as before; `?ui=classic` keeps a tab on the old look after the firm
// has moved. See docs/decisions/practice-module.md.
//
// **The WordPress frame follows the firm's step too**, as staff move across
// together; what the frame cannot do (pop out, the left menu) it leaves out
// at every step.
export const practiceLookFor = (uiChoice, firmExperience, inFrame = false, personal = "") =>
  lookStepFor(uiChoice, firmExperience, inFrame, personal) >= 3;

// **Moving across in steps.** Classic (0) to practice (4), one change at a
// time, so staff learn the new screen a piece at a time rather than in one
// leap; the firm's step is `experience` on `GET /practice/appearance`.
//   1 — matters you open stay open as tabs across the top, and search finds a
//       matter or a person;
//   2 — a matter opens in its tabs (Matter, Memos, Events, Tasks), on the
//       Section 32 page, still in the classic colours;
//   3 — the practice look, still opening on Section 32;
//   4 — practice: a matter opens on Matter details.
// **Classic is never changed by any of this**: step 0 draws exactly what it
// always did. The WordPress frame follows it too. Which step this tab
// draws: `?ui=` first, then the person's own choice for this browser (one
// step back while they settle in — `PERSONAL_LOOK_KEY`), then the firm's.
export const LOOKS = ["classic", "step-1", "step-2", "step-3", "practice"];
export const lookStepFor = (uiChoice, firmExperience, inFrame = false, personal = "") => {
  if (LOOKS.includes(uiChoice)) return LOOKS.indexOf(uiChoice);
  const firm = Math.max(0, LOOKS.indexOf(firmExperience));
  // A person may stay behind the firm, never get ahead of it.
  const own = LOOKS.indexOf(personal);
  return own >= 0 && own < firm ? own : firm;
};
export const PERSONAL_LOOK_KEY = "s32_personal_look";
// Steps 2 and 3 show a matter with these tabs only; the rest arrive with
// practice.
const STEP_TABS = ["matter", "s32", "emails"];
const LOOK_SEEN_KEY = "s32_look_seen";
const PERSONAL_LOOK = (() => {
  try { return window.localStorage.getItem(PERSONAL_LOOK_KEY) || ""; } catch { return ""; }
})();

// Which look the shell is drawing, for the parts that read it rather than
// being handed it (PortalNav's call site is pinned by a test). `null` is the
// classic look; in the practice look it carries what the title bar's search
// needs.
export const PracticeLook = createContext(null);

// **The quick pass on the classic screens, in the practice look only.** The
// shared pieces — Btn, the pane mark, the list tabs — ask which look is on:
// Fluent's 4px controls and the firm's colour in the practice look, the pill
// and the red exactly as before in the classic one.
const useFluent = () => Boolean(useContext(PracticeLook));

// The practice look's left menu, Smokeball's and Teams' shape: an icon with
// its name under it. Its width is what the page moves over by.
export const RAIL_W = 72;
// The title bar's height and the open-matters strip's, which the left menu
// starts under.
export const TITLE_BAR_H = 48;
export const MATTER_TABS_H = 38;
const RAIL_ICONS = {
  matters: "M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
  archived: ["M3 4h18v4H3z", "M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8", "M10 12h4"],
  contacts: ["M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M2 21v-1a6 6 0 0 1 12 0v1", "M16 3.5a4 4 0 0 1 0 7", "M22 21v-1a6 6 0 0 0-4-5.6"],
  orders: ["M6 2h9l5 5v15H6z", "M14 2v6h6", "M9 14h6", "M9 18h4"],
  agents: ["M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M4 21v-1a7 7 0 0 1 16 0v1", "M16 3l2 2-2 2"],
  sharing: ["M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M8.6 13.5l6.8 4", "M15.4 6.5l-6.8 4"],
  settings: ["M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"],
};
const RAIL_LABELS = { "Order searches": "Searches", "Purchaser sharing": "Sharing" };

// Someone viewing without ?fh= is looking at every matter, which is what an
// admin does; a staff member reaches the portal through the WordPress embed
// with their own name baked into the URL. Firm settings are a service-wide
// record, so only the first should be editing them.
//
// **This is presentation, not access control** — exactly like ?fh= itself.
// Removing the parameter from the URL makes you an "admin", and /settings/firm
// is still reachable with the shared token. It keeps a staff member out of a
// screen that isn't theirs; it stops nobody who is trying. Real per-person
// permissions need the WP JWT SSO work.
const IS_ADMIN = !FH_FILTER;

// Compare file handler names loosely — the URL is typed by hand and Firestore
// values carry stray casing and spacing.
const normHandler = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
const matchesHandler = (m, needle) =>
  normHandler(m.fileHandlerName) === normHandler(needle) ||
  normHandler(m.fileHandler) === normHandler(needle);

// **Their own matters first, everybody's one click away.** A staff member
// arrives through the embed with `?fh=` and lands on their own list, as they
// always have; Everyone widens it to the whole office's without making them an
// admin — `IS_ADMIN` still reads the parameter, not this switch. Remembered per
// tab like `?ui=`, so opening a matter and coming back keeps the choice.
// Presentation only, like `?fh=` itself.
const HANDLERS_KEY = "s32_portal_handlers";
export const readAllHandlers = () => {
  try { return sessionStorage.getItem(HANDLERS_KEY) === "all"; } catch (e) { return false; }
};
export const rememberAllHandlers = (all) => {
  try {
    if (all) sessionStorage.setItem(HANDLERS_KEY, "all");
    else sessionStorage.removeItem(HANDLERS_KEY);
  } catch (e) { /* storage blocked — the switch still works until a reload */ }
};
// The name the list is narrowed to, or "" for every matter.
export const handlerFilterFor = (fh, allHandlers) => (fh && !allHandlers ? fh : "");

export const HandlerScopeSwitch = ({ name, all, onChange }) => {
  const opt = (value, label) => (
    <button type="button" aria-pressed={all === value} onClick={() => onChange(value)}
      style={{
        border: "none", borderRadius: 999, padding: "4px 12px", cursor: "pointer",
        fontSize: 12, fontFamily: "Inter, sans-serif",
        background: all === value ? T.white : "transparent",
        boxShadow: all === value ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
        color: all === value ? T.text : T.textSoft,
      }}>
      {label}
    </button>
  );
  return (
    <div role="group" aria-label="Whose matters" title={`Mine is file handler “${name}”`}
      style={{ display: "flex", background: T.bg, borderRadius: 999, padding: 2 }}>
      {opt(false, "Mine")}
      {opt(true, "Everyone")}
    </div>
  );
};

// openedDate arrives from Formidable as a US-format timestamp
// ("08/14/26 02:03PM"). Display it as d/m/Y — month is always the first
// field, so 08/09/26 is 9 August. Anything that doesn't match is shown as-is
// rather than guessed at.
// Who the agent on this matter is, for the header.
//
// **The person leads and the agency backs it up.** `matter.agent` is the
// agency as free text on Form 27 — which is what the header used to show, and
// on a matter whose salesperson somebody had identified it was both the less
// useful half and, where the vendor mistyped it, the wrong one.
//
// It reads the **resolved** fields rather than the matter's own, so a staff
// correction and an adopted directory record both reach it — that is the whole
// point, and the precedence is the middleware's, not a second copy here. The
// raw value stays as the last fallback, for a matter whose contract fields have
// not loaded or that carries nothing else.
const agentDisplay = (matter) => {
  const values = matter?.contractFields?.values || {};
  const contact = String(values["agent.contactName"] || "").trim();
  const agency = String(values["agent.name"] || matter?.agent || "").trim();
  return contact
    ? { name: contact, office: agency }
    : { name: agency, office: "" };
};

// `formatOpenedDate`, and the two shapes it reads, moved to `shared.js` when
// the agent portal started printing the same column — see the note there. The
// sort below reads the same two patterns, so it takes them from the same place.

// The same value as a number, for sorting. Formidable sends M/D/YYYY, so
// string-sorting it would put 10 March before 2 April. Anything unparseable
// sorts last rather than to the epoch — a missing date is not 1970, and a
// matter with no opened date should not head the list.
export const openedDateValue = (raw) => {
  const said = String(raw || "").trim();

  // The middleware's own form, and the raw timestamp behind it. Both sort by
  // the day rather than the moment — a matter opened at 9am and one opened at
  // 4pm were opened on the same day.
  const iso = said.match(ISO_DATE);
  if (iso) {
    const [, year, month, day] = iso;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  const m = said.match(US_DATE);
  if (!m) return null;
  const [, month, day, year] = m;
  const full = year.length === 2 ? `20${year}` : year;
  return new Date(Number(full), Number(month) - 1, Number(day)).getTime();
};

// How far through its searches a matter is, 0..1. Used for sorting, so a matter
// with nothing ordered yet has to sort somewhere deliberate rather than as
// NaN — which compares false against everything and scrambles the whole list.
const searchProgressValue = (m) => {
  const total = m.searchCount || 0;
  if (!total) return -1;
  return (m.returnedCount || 0) / total;
};

const SORT_VALUES = {
  client: (m) => (m.client || "").toLowerCase(),
  fileHandler: (m) => (m.fileHandlerName || m.fileHandler || "").toLowerCase(),
  opened: (m) => openedDateValue(m.openedDate),
  searches: searchProgressValue,
  status: (m) => (m.status || ""),
};

// Sorting that keeps the server's order as a real option.
//
// The API returns matters newest-updated first, which is the most useful
// default and is not reproducible from any single column — so `null` means
// "leave it alone" rather than falling back to some other column and quietly
// changing what people are used to seeing.
//
// A null value always sorts last, in both directions. Sorting by opened date
// should not put every matter with a missing date at the top just because the
// direction was flipped.
export const sortMatters = (matters, sort) => {
  if (!sort?.key || !SORT_VALUES[sort.key]) return matters;
  const value = SORT_VALUES[sort.key];
  const dir = sort.dir === "desc" ? -1 : 1;

  return [...matters].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    const aMissing = va === null || va === undefined || va === "";
    const bMissing = vb === null || vb === undefined || vb === "";
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
};

// The rows between two clicks, inclusive, in the order currently on screen.
//
// Anchored by matter ref rather than by row index: the list re-sorts and
// re-filters under the selection, and an index would quietly point at a
// different matter afterwards — which, for a bulk archive, means archiving
// something nobody clicked.
//
// An anchor that has scrolled out of the filter is not a range at all, so it
// falls back to the one row that was actually clicked.
export const rangeBetween = (refs, anchorRef, targetRef) => {
  const a = refs.indexOf(anchorRef);
  const b = refs.indexOf(targetRef);
  if (a === -1 || b === -1) return [targetRef];
  return a <= b ? refs.slice(a, b + 1) : refs.slice(b, a + 1);
};

// Only for the hint text — the handler accepts both keys either way, because a
// Mac user on an external PC keyboard is not a rare thing.
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

// Every middleware call carries the access key. The middleware is the thing
// that actually enforces it — this header is how it gets there.
const apiFetch = (path, options = {}) => fetch(`${MIDDLEWARE_URL}${path}`, {
  ...options,
  headers: {
    ...(options.headers || {}),
    ...(ACCESS_TOKEN ? { "x-portal-token": ACCESS_TOKEN } : {}),
  },
});

// ── The directory, read from wherever the agent is being looked at ─────────
//
// Both are staff routes and return the full record — an email and a mobile —
// which is exactly why they are not the public typeahead the client form calls.
// A failed read answers with an empty list rather than throwing: not being able
// to offer a longer list is not a reason to break the field it sits under.
// The last stop when the directory has never heard of the agency: ask the web.
//
// **Grounded or nothing** — the middleware discards a model answer that did
// not actually search, because `agent.address` is where notices get served and
// a confident invention there is the worst thing this could produce. What
// comes back is a candidate, never a record and never a write.
const searchAgentOnline = async (matterRef, asked = {}) => {
  const r = await apiFetch(`/agent-search/${encodeURIComponent(matterRef)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(asked),
  });
  const data = await r.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || `The search failed (${r.status})`);
  return data;
};

// The office's own conveyancers, for the cross-check recipient box.
//
// **Nothing stores who they are** — a file handler exists in the middleware
// only as fields on the matters they have been appointed to — so this is an
// observed list rather than a roster, and the box it fills still takes a typed
// address for somebody who has never been appointed to one.
//
// Failing quietly is right here, the same as the two directory reads below: an
// empty suggestion list leaves a plain text box, which is what this was before
// the list existed.
export const fetchFileHandlers = async () => {
  try {
    const r = await apiFetch("/file-handlers");
    const data = await r.json();
    return data?.ok ? (data.handlers || []) : [];
  } catch { return []; }
};

// What the precedent's clauses are, what removing one takes with it, and what
// else in the contract points at it.
//
// **All three are the middleware's**, and deliberately so: the hierarchy is
// read off the citation and the cross-references off the precedent's own
// wording, both of which are properties of the stored template. A copy of
// either rule here would drift from the document, and the way it would drift
// is a screen promising that deleting 9.1 leaves (a) and (b) alone.
//
// Fetched once and held: it is the same answer for every matter, and changes
// only when somebody re-uploads the template.
let clauseTreePromise = null;
export const fetchClauseTree = () => {
  if (!clauseTreePromise) {
    clauseTreePromise = (async () => {
      try {
        const r = await apiFetch("/clause-tree");
        const data = await r.json();
        return data?.ok ? (data.clauses || []) : [];
      } catch { return []; }
    })();
  }
  return clauseTreePromise;
};

// The wording a clause is printing, read off the document itself.
//
// **Not the template's own text.** What is on the page is resolved — B.1's
// definition carries the sunset period as a value — and it is this matter's
// override where there is one, so it is exactly what somebody means to edit.
// Sending the raw body instead would put `{{contract.property.sunsetPeriod}}`
// in the box as the wording to change.
//
// The clause's number is a leading span the emitter writes *outside* the
// editable pair, so it is not wording and must not be seeded into the box —
// saved, it would print the number twice.
export const clauseWording = (el) => {
  if (!el) return "";
  const copy = el.cloneNode(true);
  const first = copy.firstElementChild;
  if (first && /(^|\s)(num|tab)(\s|$)/.test(first.getAttribute?.("class") || "")) {
    first.remove();
  }
  return (copy.textContent || "").replace(/\s+/g, " ").trim();
};

// What pressing Delete actually takes off the page, said on the button rather
// than only in the confirmation behind it.
//
// A clause is not a paragraph: deleting special condition 9 takes 9.1, 9.2 and
// every lettered sub-paragraph under them, and a link reading "Delete Special
// condition 9" says nothing about that. `removes` is the middleware's own
// answer — the family it works out from the citations — so the count here can
// never disagree with the set the save actually removes.
//
// Silent where there is nothing underneath, which is most clauses: "and its 0
// sub-clauses" on a leaf reads as a rule somebody forgot to finish.
export const deleteClauseLabel = (clause, family) => {
  const name = family?.label || clause;
  const n = family?.removes?.length || 0;
  if (!n) return `Delete ${name}`;
  return `Delete ${name} and its ${n} sub-clause${n === 1 ? "" : "s"}`;
};

const fetchOfficeAgents = async (officeId) => {
  try {
    const r = await apiFetch(`/directory/offices/${encodeURIComponent(officeId)}/agents`);
    const data = await r.json();
    return data?.ok ? (data.agents || []) : [];
  } catch { return []; }
};

// The staff search, shaped like a suggestion so one row renders both. It
// returns the raw records with their office attached, where a suggestion has
// already flattened the office's name and address onto the row.
const searchDirectory = async (q) => {
  try {
    const r = await apiFetch(`/directory/search?q=${encodeURIComponent(q)}`);
    const data = await r.json();
    if (!data?.ok) return [];
    return (data.agents || []).slice(0, 8).map(a => ({
      agentId: a.agentId,
      name: a.name || "",
      email: a.email || "",
      officeName: a.office?.contractName || a.office?.tradingName || "",
      officeAddress: [a.office?.address, a.office?.suburb, a.office?.state, a.office?.postcode]
        .filter(Boolean).join(" "),
      matchedBy: "search",
    }));
  } catch { return []; }
};

// ── Drafting a special condition ───────────────────────────────────────────
//
// **It drafts; it never saves.** The one write is the Save button on the
// panel, exactly as if somebody had typed the clause — so what comes back here
// is a suggestion sitting in local state until a person accepts it. That
// separation is the whole safety of the feature, and it is the middleware's
// too: the draft route stores nothing.
//
// **Nothing drafted comes back as `ok`, not as an error.** "That is a matter
// for the section 32, not a special condition" is exactly what a conveyancer
// needs to read, and treating it as a failure would throw the sentence away.
const draftSpecialConditions = async (matterRef, asked = {}) => {
  const r = await apiFetch(`/special-conditions/${encodeURIComponent(matterRef)}/draft`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(asked),
  });
  const data = await r.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || `The drafting failed (${r.status})`);
  return data;
};

// ── Asking the vendors to sign ─────────────────────────────────────────────
//
// **What a vendor's address box says, and what the press will do with it.**
// Both vendor modals show one box per vendor, and both say a cleared box is
// not written to. The middleware used to read only addresses and fall back to
// the client record for anything else, so a cleared box sent the link to the
// very address staff had just removed — usually a mailbox all the vendors
// share. It now reads a present-but-empty position as *do not email*, so every
// box shown is sent, the empty ones as `""`.
//
// **A half-typed address is refused here rather than sent.** The middleware
// ignores one — it must not store a malformed address — and ignoring it means
// falling back to what the box was typed over. `looksLikeEmail` is the one
// shape check every send button uses, and it is stricter than the
// middleware's, so nothing it passes can be ignored there.
export const addressState = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return "blank";
  return looksLikeEmail(v) ? "ok" : "malformed";
};

export const addressPayload = (positions, emails) => Object.fromEntries(
  positions.map(position => [position, String(emails[position] ?? "").trim()]),
);

// Names whose box needs finishing, so the disabled press says why.
export const malformedSentence = (people) => {
  const names = people.map(one => one.name || `Signatory ${one.position}`);
  const list = names.length > 1
    ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
    : names[0];
  return `Finish or clear the address for ${list} — it is not a complete email address.`;
};

//
// **The links come back once and can never be recovered.** The middleware
// stores only hashes, so what this returns is the single moment the plaintext
// exists — which is why the modal shows them rather than closing on success,
// and why asking again mints fresh ones for whoever has not signed.
//
// `send` emails each signatory their own link; without it the links come back
// and staff hand them over. `emails` carries every box the modal shows, keyed by
// position: an address supplies or corrects one, and **an empty string is an
// instruction not to email that vendor** — see `addressPayload`.
const requestSignatures = async (matterRef, body = {}) => {
  const r = await apiFetch(`/signature-request/${encodeURIComponent(matterRef)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // **Who is looking, for the sample matter and nothing else.** It is a name
    // rather than an address — the middleware matches it against the handlers
    // the office is observed to have, and ignores it entirely on every other
    // matter. Release and the cross-check have always sent it; this one did
    // not, so a rehearsal fell through to the office inbox rather than
    // reaching the person who pressed the button.
    body: JSON.stringify({ ...(PORTAL_USER ? { viewer: PORTAL_USER } : {}), ...body }),
  });
  const data = await r.json().catch(() => null);
  if (!data?.ok) {
    const err = new Error(data?.error || `The request failed (${r.status})`);
    err.code = data?.code;
    throw err;
  }
  return data;
};

// ── The section 137B defects report ─────────────────────────────────────────
//
// Staff classify the works and the middleware does the rest: the client's
// email, the page that books the inspection, the release on grey-area works.
// Everything the client is told is the middleware's (`lib/report-137b.js`);
// this only asks, shows where it is up to, and shows what the client answered.
const report137bApi = async (path, { method = "GET", body } = {}) => {
  const r = await apiFetch(path, {
    method,
    ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  const data = await r.json().catch(() => null);
  if (!data?.ok) {
    const err = new Error(data?.error || `The request failed (${r.status})`);
    err.code = data?.code;
    throw err;
  }
  return data;
};
const report137bStatus = (matterRef) => report137bApi(`/report-137b/${encodeURIComponent(matterRef)}`);
const requestReport137b = (matterRef, body) => report137bApi(`/report-137b/${encodeURIComponent(matterRef)}`, {
  method: "POST", body: { ...(PORTAL_USER ? { by: PORTAL_USER } : {}), ...body },
});
const cancelReport137b = (matterRef) => report137bApi(`/report-137b/${encodeURIComponent(matterRef)}/cancel`, {
  method: "POST", body: PORTAL_USER ? { by: PORTAL_USER } : {},
});
// An admin's reset: the answers, uploads and signed PDF removed, so the form
// can be sent again from nothing. The middleware checks the role and the
// typed reference.
const resetReport137b = (matterRef, confirm) => report137bApi(`/report-137b/${encodeURIComponent(matterRef)}/reset`, {
  method: "POST", body: { confirm, ...(PORTAL_USER ? { by: PORTAL_USER } : {}) },
});

// Withdrawing links nobody has used. It never reaches a signature — those come
// back as `kept` — and it deletes nothing.
const cancelSignatures = async (matterRef, positions = null) => {
  const r = await apiFetch(`/signature-request/${encodeURIComponent(matterRef)}/cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(positions ? { positions } : {}),
  });
  const data = await r.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || `It was not withdrawn (${r.status})`);
  return data;
};

// For URLs the browser loads directly — an <iframe src> or a new tab — where
// there's no way to attach a header. The gate accepts ?token= for exactly this.
// **The key a link may carry is not the key this app holds.**
//
// `ACCESS_TOKEN` is the shared staff key: it opens every matter *and* every
// write — the release, the field saves, the matter delete all read `?token=`.
// It used to be appended to every URL built here, so *Open* left it in a new
// tab's address bar and a download left it in the browser's history, which is
// exactly where somebody copies a link from before pasting it into an email.
//
// `DOC_TOKEN` is the read-only key the middleware hands out at
// `/document-token`. It opens the document routes and nothing else, so a
// pasted link stops being a credential. It is fetched over XHR rather than
// read off this app's own URL — putting a second secret in the URL staff
// bookmark would be the same leak wearing a different name.
//
// **The staff key is still the fallback**, so links keep working on a
// deployment where `PORTAL_READ_TOKEN` is not set — the middleware accepts
// either on these routes.
//
// **A signed-in person's session is never that fallback.** It is a per-person
// credential, and a URL is the one place it must not go; until the read token
// arrives their links carry nothing, and the middleware hands them a read
// token of their own (scoped to their firm) on the same route.
let DOC_TOKEN = "";
export const setDocumentToken = (token) => { DOC_TOKEN = token || ""; };
const linkToken = () => DOC_TOKEN || (isSessionToken(ACCESS_TOKEN) ? "" : ACCESS_TOKEN);

const apiUrl = (path) => {
  const token = linkToken();
  return `${MIDDLEWARE_URL}${path}${token ? `${path.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}` : ""}`;
};

// Where to load a matter's compiled PDF from.
//
// compiledPdfPath means it's in our bucket and the link never goes stale.
// Older matters only have compiledPdfUrl — a pdf.co link that expired about an
// hour after it was made, so it will almost certainly show an AccessDenied
// page. Re-compiling replaces it with a stored copy.
//
// The version goes in the path so the browser can cache the file permanently:
// a given version's bytes never change, and a re-compile produces a new URL.
// Without it the preview re-downloads on every view and again on every
// download. Don't "simplify" this back to an unversioned URL.
const compiledPdfSrc = (matter) => {
  if (matter?.compiledPdfPath) {
    const ref = encodeURIComponent(matter.ref);
    const version = matter.compileVersion;
    return apiUrl(version ? `/compiled/${ref}/v${version}` : `/compiled/${ref}`);
  }
  return matter?.compiledPdfUrl || null;
};
const compiledPdfIsStale = (matter) => !matter?.compiledPdfPath && !!matter?.compiledPdfUrl;

// **The compiled contract, drawn by the middleware rather than framed as a
// PDF — and this is now how the portal shows it.**
//
// A browser asked to frame a PDF renders its first page and stops on WebKit,
// so `compiledPdfSrc` in an iframe showed one page of thirty-five on every
// iPhone and iPad. The portal's answer was `PdfInDeviceViewer`: stop
// pretending, and hand the file to the phone's own viewer. Honest, and not
// reading the contract in the portal — the document leaves the app, and so
// does the person.
//
// The middleware already had a real viewer, written for exactly this
// limitation and already drawing this same pack for the vendor and the agent.
// Pointing the frame at it is what makes the compiled contract readable in
// place on a phone, and it costs nothing on a desktop.
//
// **So the halo is not a second view any more.** It is drawn on the pages that
// carry one, in the panel and in the expanded modal alike, and *Show the lot*
// is navigation rather than a mode — see `showLot` in `CompiledDocumentPanel`.
// It is the same mark the agent is shown, on the same coordinates, so what the
// office confirms and what the agent opens cannot disagree.
//
// **A second implementation here would be the drift.** The viewer is a copy of
// `lib/pdf-scroller.js` away from being a third one, and the first to go
// stale.
//
// **And it carries when the lot answer was made**, for the reason `builtKey`
// carries when the signed pair was built. The page bakes the places into the
// markup it serves, and this frame was keyed on the compiled *version* — which
// *Look again* does not change. So a re-run produced new places and the frame
// went on drawing the old ones until the browser was reloaded, which is what
// the firm met on 1760412. A matter with no answer, or an older middleware
// that does not send the time, keys on the version alone exactly as before.
export const compiledViewSrc = (matter) => {
  if (!matter?.compiledPdfPath || !matter?.compileVersion) return null;
  // Into the path rather than onto the built URL: `apiUrl` appends the token
  // and already knows whether it is adding the first parameter or the second.
  const at = matter?.lotSearch?.record?.at;
  return apiUrl(`/compiled-view/${encodeURIComponent(matter.ref)}`
    + (at ? `?lot=${encodeURIComponent(at)}` : ""));
};

// The executed statement — the signed version, with each vendor's mark and date
// written into the form's own signature blocks and no page added.
//
// **Its own URL rather than a flag on the compiled one**, because it is a
// different document: the compiled version is what staff arranged and what the
// vendors signed against, and this is that with their marks on it. Both are
// reachable, which is what lets somebody see at a glance whether the matter has
// an executed statement at all.
// Whether a release will carry the executed copy rather than the compiled one.
//
// The same match the middleware makes: the executed object belongs to one
// version, so a re-compile past it releases the compiled document again. Read
// here only to *say* so — `statementFileFor` is what decides, and a second
// copy of the rule would drift, so this is deliberately the narrowest form of
// it that can be wrong in one direction only: a note that failed to print.
export const sendsExecutedCopy = (matter = {}) =>
  Boolean(matter.signedPdfPath) && matter.signedVersion === matter.compileVersion;

// **The URL carries when the pair was built, and that is not decoration.**
// Both documents live at a path keyed on the *version* — `v6-signed.pdf` — and
// are rebuilt there whenever the signing set changes, so a signature withdrawn
// and given again writes new bytes to the same URL. The service used to serve
// that URL as `immutable`, which on 1760482 left a browser showing a
// certificate from the round that had been withdrawn while the signatures
// beside it read an hour later. The header is a minute now; this is the half
// that also fixes a browser which cached the old answer, and it is the same
// trick a re-compile gets for free by being a different version.
const builtKey = (matter) => {
  const at = matter?.signatures?.builtAt || matter?.signedAt;
  return at ? `?t=${encodeURIComponent(at)}` : "";
};

export const signedStatementSrc = (matter) => (matter?.signedPdfPath
  ? apiUrl(`/signed-statement/${encodeURIComponent(matter.ref)}${builtKey(matter)}`)
  : null);

// The office's own record of the signing — who, as what, when, from where.
//
// **A separate document from the statement, and staff's alone.** It used to be
// a page bound onto the end of the executed copy; the evidence about the act is
// not a disclosure a purchaser reads, and the marks are now in the statement's
// own signature blocks, so it has nothing left to do inside the document.
export const signingCertificateSrc = (matter) => (matter?.signingCertificatePath
  ? apiUrl(`/signing-certificate/${encodeURIComponent(matter.ref)}${builtKey(matter)}`)
  : null);

// Why there is no executed PDF on a matter every vendor has signed.
//
// **The signatures are real either way** — they are on the matter and on the
// timeline. What is missing is the document that binds them, and saying so is
// the difference between a person knowing to look and a person hunting for a
// file that was never produced.
export const SIGNED_PDF_TROUBLE = {
  no_bucket: "The signatures are recorded, but this deployment has no document storage, "
    + "so no executed PDF was produced.",
  version_missing: "The signatures are recorded, but the version they were given against is "
    + "no longer in storage, so no executed PDF could be produced.",
  marks_not_placed: "The signatures are recorded, but the signature blocks could not be "
    + "found in the compiled statement, so the marks were not written into it. The signing "
    + "certificate is on the matter.",
  stamp_failed: "The signatures are recorded, but they could not be written into the "
    + "statement. The service log says why.",
  // These two are about the office's own record, not the document that goes
  // out — so they say the statement is fine rather than reading as a failure
  // of it and sending somebody to look at the wrong thing.
  renderer_unavailable: "The statement carries the signatures, but no browser was available, "
    + "so the signing certificate was not produced.",
  certificate_failed: "The statement carries the signatures, but the signing certificate "
    + "could not be produced. The service log says why.",
  build_failed: "The signatures are recorded, but the executed PDF could not be produced. "
    + "The service log says why.",
};

// **Saving a PDF has to be asked for on the URL, not in the markup.**
//
// `<a download>` is ignored cross-origin, and this app is on the Hosting
// domain while the middleware is on Cloud Run — only `/released/**` is proxied
// through — so a download button built that way silently degrades to a
// navigation and does exactly what Open already does. `?download=1` makes the
// middleware answer `Content-Disposition: attachment`.
//
// A pdf.co link on an old matter is somebody else's URL and takes no such
// parameter; it is returned untouched rather than given one that does nothing,
// and the caller decides whether to offer the button at all.
export const downloadUrl = (url) => {
  if (!url) return null;
  // Compared against the bare host, not `apiUrl("")` — that appends the access
  // token, so a URL built without one would never match its own origin.
  if (!url.startsWith(MIDDLEWARE_URL)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}download=1`;
};

// `location.assign` rather than `window.open`: the response is an attachment,
// so a new tab would open and immediately close itself, which flickers. The
// current page is unaffected — the browser downloads and stays put.
const saveAs = (url) => { if (url) window.location.assign(url); };

// **Copying, where `navigator.clipboard` is not ours to use.**
//
// The portal is served from its own Hosting domain and embedded in the
// office's WordPress, so every screen here runs in a **cross-origin iframe** —
// and the async Clipboard API is refused there unless the *framing* page
// carries `allow="clipboard-write"`. Nothing on this side can grant that, and
// nothing on this side can detect it either: the call simply rejects.
//
// So the Copy buttons had one way to do the job and no second one, and when
// the embed stopped carrying the grant they stopped working — on the SPI,
// which is what somebody pastes into land.vic.gov.au, and on a signing link,
// which is the one thing that cannot be fetched again.
//
// `document.execCommand('copy')` over a selected textarea is **not** subject
// to that permission policy: it is a synchronous copy of a selection made
// inside a user gesture, which is what the modern API replaced rather than
// what it gates. Deprecated, universally supported, and the only thing that
// works from here.
//
// **Tried second, not first.** Where the modern API is available it is the
// better one — it needs no element, cannot disturb a selection the reader
// already had, and reports failure honestly. This is the fallback, not the
// answer.
// **The duties form extension, by its fixed ID** (the middleware repo's
// `extensions/gov-forms`, whose manifest key pins it). The portal hands it a
// matter's code directly, which the extension accepts only from the portal's
// own addresses, and it opens Duties Online. The code never goes on a link:
// the form is the SRO's site. Resolves false when the extension isn't
// installed or doesn't answer, and the caller copies the code instead.
export const DUTIES_EXTENSION_IDS = ["mkinfhbfjjpnkpidhnejmedbgoennknc"];
export const openInDutiesExtension = (code) => new Promise((resolve) => {
  const runtime = window.chrome?.runtime;
  if (!runtime?.sendMessage) { resolve(false); return; }
  let left = DUTIES_EXTENSION_IDS.length;
  let settled = false;
  const settle = (ok) => { if (!settled) { settled = true; clearTimeout(timer); resolve(ok); } };
  const miss = () => { left -= 1; if (left === 0) settle(false); };
  const answered = (reply) => { if (runtime.lastError || !reply?.ok) miss(); else settle(true); };
  const timer = setTimeout(() => settle(false), 1500);
  DUTIES_EXTENSION_IDS.forEach((id) => {
    try { runtime.sendMessage(id, { type: "open", code }, answered); } catch { miss(); }
  });
});

// **Whether the extension is here, before anybody presses.** Asked once per
// page load (the answer can't change without a reload) and shared by the
// ribbon and the rail. Resolves `{ state, version }`: `installed`; `old` (a
// version from before the question existed, which still opens forms);
// `missing`; or `unsupported` (a phone, a tablet, Safari, Firefox).
let dutiesCheck = null;
export const checkDutiesExtension = () => dutiesCheck || (dutiesCheck = new Promise((resolve) => {
  if (dutiesBrowserOf(navigator.userAgent, navigator.maxTouchPoints || 0) !== "ok") { resolve({ state: "unsupported" }); return; }
  // Chrome exposes this to a page only when an installed extension names
  // the page's address, so its absence already says "not installed".
  const runtime = window.chrome?.runtime;
  if (!runtime?.sendMessage) { resolve({ state: "missing" }); return; }
  let left = DUTIES_EXTENSION_IDS.length;
  let settled = false;
  const settle = (r) => { if (!settled) { settled = true; clearTimeout(timer); resolve(r); } };
  const timer = setTimeout(() => settle({ state: "missing" }), 1500);
  DUTIES_EXTENSION_IDS.forEach((id) => {
    try {
      runtime.sendMessage(id, { type: "ping" }, (reply) => {
        if (runtime.lastError) { left -= 1; if (left === 0) settle({ state: "missing" }); return; }
        settle(reply?.ok ? { state: "installed", version: reply.version } : { state: "old" });
      });
    } catch { left -= 1; if (left === 0) settle({ state: "missing" }); }
  });
}));
export const useDutiesExtension = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let live = true;
    checkDutiesExtension().then((r) => { if (live) setStatus(r); });
    return () => { live = false; };
  }, []);
  return status;
};
// The button's tooltip, by what the check found.
export const dutiesTooltip = (status) => ({
  missing: "The duties form extension isn't installed in this browser. Press for how to install it.",
  unsupported: "The duties form fills on a computer, in Chrome or Edge.",
  old: "Opens Duties Online and fills the SRO duties form from this matter. Update the extension to 0.3.6 for the latest.",
}[status?.state] || "Opens Duties Online and fills the SRO duties form from this matter, page by page. You check the answers and save.");

export const copyText = async (text) => {
  const value = String(text ?? "");
  if (!value) return false;

  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    // Fall through — refused, or no clipboard object at all.
  }

  try {
    const pad = document.createElement("textarea");
    pad.value = value;
    // **Off-screen rather than hidden.** `display:none` or the `hidden`
    // attribute cannot hold a selection, so the copy silently does nothing —
    // which is the failure this whole function exists to remove.
    pad.setAttribute("readonly", "");
    pad.style.position = "fixed";
    pad.style.top = "-1000px";
    pad.style.opacity = "0";
    document.body.appendChild(pad);
    pad.select();
    pad.setSelectionRange(0, value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(pad);
    return ok === true;
  } catch {
    return false;
  }
};

// The press behind Duties form, on the classic rail and the practice
// ribbon alike: a four-hour code for this one matter, handed straight to the
// extension, else copied to paste into its panel. Resolves "opened",
// "copied" (no extension answered in this browser), "unsupported" (a phone,
// a tablet or a browser it can't run in; copied all the same) or "failed".
export const openDutiesFormFor = async (ref) => {
  try {
    const res = await apiFetch(`/gov-forms/${encodeURIComponent(ref)}/digital-duties-form/token`, { method: "POST" });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.ok === false) return "failed";
    const code = `ddf1:${ref}:${body.token || ""}`;
    const browser = dutiesBrowserOf(navigator.userAgent, navigator.maxTouchPoints || 0);
    if (browser === "ok" && await openInDutiesExtension(code)) {
      window.dispatchEvent(new CustomEvent("provey:duties-opened"));
      return "opened";
    }
    if (!await copyText(code)) return "failed";
    return browser === "ok" ? "copied" : "unsupported";
  } catch {
    return "failed";
  }
};

// **A phone shows the first page of an embedded PDF and stops.**
//
// iOS Safari renders page one of a PDF in an `<iframe>` and gives no way to
// reach the rest — no scroll, no pager — and Android Chrome mostly declines to
// render one in place at all. So the frame that reads a contract perfectly on
// a desktop is, on a phone, a thirty-five page statutory document showing its
// cover and nothing else, with nothing on screen saying so.
//
// That is the same shape of fault the middleware's byte-range work fixed from
// the other end: a viewer that renders the first page and stops. There the
// bytes were at fault and could be fixed; here it is the browser, and the
// honest answer is to stop pretending — hand the file to the device's own
// viewer, which paginates, zooms and searches properly.
//
// **There is no feature test for this**, so the signal is a proxy: a coarse
// pointer is a phone or a tablet, which is where the limitation lives. Getting
// it wrong is cheap in one direction and not the other, which is what makes a
// proxy acceptable — a false positive costs one tap on a button that says what
// it does, and a false negative is exactly the silent first page we have now.
//
// **Only our own PDFs.** A Drive-filed document embeds through Drive's own
// `/preview`, which is an HTML page and scrolls on a phone like any other, and
// a foreign link is not ours to judge. `downloadUrl` already draws that line —
// it can only add `?download=1` to a URL this service serves — so the same
// test decides both, and they cannot disagree about what is ours.
export const needsDeviceViewer = (url, coarsePointer) =>
  Boolean(url) && Boolean(coarsePointer) && downloadUrl(url) !== url;

// Whether the primary pointer is a finger. Watched rather than read once, so
// the answer survives a tablet being plugged into a mouse — and guarded for
// the test renderer, where `matchMedia` does not exist.
const useCoarsePointer = () => useMediaQuery("(pointer: coarse)");

// Whether a media query matches, watched. The same guards as above, which is
// now this.
export const useMediaQuery = (query) => {
  const read = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const onChange = (e) => setMatches(e.matches);
    // `addListener` is the deprecated spelling and the only one older Safari
    // has; without it the answer would simply never update there.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [query]);
  return matches;
};

// When it was compiled, in words. `compiledAt` is a Firestore Timestamp and
// arrives as {_seconds,…}, which new Date() reads as Invalid Date — so this
// takes the seconds when they're there and falls back to anything parseable.
// Returns "" rather than a broken date: no stamp beats a wrong one.
const timeOf = (raw) => {
  if (!raw) return null;
  const ms = typeof raw?._seconds === "number" ? raw._seconds * 1000 : Date.parse(raw);
  return !ms || Number.isNaN(ms) ? null : ms;
};

const compiledAtText = (matter) => {
  const ms = timeOf(matter?.compiledAt);
  if (!ms) return "";
  return new Date(ms).toLocaleString("en-AU", {
    day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  });
};

// Have the certificates been read since this version was built?
//
// A re-read reads and stops — it deliberately does not rebuild the contract,
// because it is pressed by somebody who wants to see what the certificates say
// rather than to spend a compile. The consequence is that a matter can hold
// values newer than its own PDF, and nothing on the panel would otherwise say
// so: the version, the date and the document all look perfectly current.
//
// Both stamps are missing on a matter that has never compiled or never been
// read, and that is not staleness — it is a matter earlier in its life.
//
// **The middleware answers it exactly where it can, and this defers to that.**
// Comparing the two stamps says only that a read *happened*, which fires for any
// field the read wrote whether or not the contract prints it — on 1760594 that
// was two fields bound to no printing slot, so the matter carried a standing
// instruction to re-compile for a version that would have come out identical.
// `printCheck.warn` is the same question asked properly: the compile records a
// fingerprint of the document it produced, and the read says whether re-rendering
// would differ. `warn` rather than `stale`, because a *cannot tell* still warns
// and composing that here would be a second copy of the rule.
//
// The fallback is this function as it was. An older middleware sends no
// `printCheck`, and so does `GET /matters` for the dashboard rows, and both have
// to keep working.
export const readSinceCompiled = (matter) => {
  const check = matter?.printCheck;
  if (check && typeof check.warn === "boolean") return check.warn;
  const read = timeOf(matter?.extractedFieldsAt);
  const compiled = timeOf(matter?.compiledAt);
  return !!read && !!compiled && read > compiled;
};

// What is wrong with releasing this matter *now*, if anything.
//
// Two states send the wrong document, and neither is visible from the release
// modal, which is a form about who to send it to:
//
// - **There is nothing to send.** `compiledPdfUrl` is pdf.co's and expires
//   within the hour, so the middleware refuses without a stored
//   `compiledPdfPath` — meaning staff fill in the recipient, press Confirm and
//   get an error. The refusal is correct and arrives at the wrong end.
// - **The certificates have been read since this version was built.** The
//   contract data is newer than the PDF, so releasing sends a document the
//   panel beside it already disagrees with. Nothing rebuilds it on its own —
//   a re-read deliberately does not — so this is exactly the case somebody
//   forgets to re-compile.
//
// **A contract with blanks in it is deliberately not on this list.** The form
// is designed to issue with blanks completed at sale, so nearly every matter
// has some — a warning on nearly every release is a warning nobody reads, and
// the card already demotes the button for it. Same reasoning as
// `CONFLICTS_REPORTED` refusing to flag every disagreement.
//
// **Nor is a statement nobody has signed**, for exactly that reason and on the
// firm's instruction: this office routinely sends the contract before the
// vendor signs, so it fired on nearly every release. What the vendor must sign
// before exchange is the law rather than news about this matter.
//
// **And nothing here is a gate any more.** A non-blocking warning is read and
// the button stays live; the *Release anyway* tick that used to hold it is
// gone with the warning that mostly produced it.
export const releaseWarning = (matter = {}) => {
  // **No document is not a warning, it is an impossibility.** Offering
  // "release anyway" here was silly: there is nothing to send, so the press
  // could only ever end in a refusal from the middleware. `blocking` says the
  // difference, and `canRelease` below is what takes the button away.
  if (!matter.compiledPdfPath) {
    return matter.compileVersion
      ? {
          blocking: true,
          title: "There is no stored copy of this contract",
          body: "It was compiled before documents were stored permanently, and that copy has "
            + "expired — so there is nothing to attach. Re-compile first.",
        }
      : {
          blocking: true,
          title: "This matter has no compiled contract",
          body: "Nothing has been compiled yet, so there is no document to send. "
            + "Compile it first.",
        };
  }
  // **A statement nobody has signed is not warned about**, on the firm's
  // instruction, and the reasoning is the one already above about blanks: the
  // office routinely sends a contract before the vendor has signed, so this
  // fired on nearly every release — and a warning on nearly every release is a
  // warning nobody reads. That the purchaser must be given a signed statement
  // is the law rather than news about this matter, and staff know it.
  //
  // **Signatures against a version this matter has compiled past is news**, and
  // it stays: the matter *reads* as signed and is not, which is the one nobody
  // would otherwise catch.
  if (matter.signatures?.stale) {
    return {
      blocking: false,
      title: "What was signed is not what this is",
      body: `The vendors signed v${matter.signatures.version}, and v${matter.compileVersion} `
        + "has been compiled since, so this version carries no signature.",
    };
  }
  // This one *is* a warning: the document exists and is sendable, it is just
  // older than what the certificates now say. Going ahead knowing that is a
  // legitimate choice, so it is a confirmation rather than a refusal.
  if (readSinceCompiled(matter)) {
    return {
      blocking: false,
      title: "The certificates have been read since this version was built",
      body: `The contract data is newer than v${matter.compileVersion || 1}, and nothing rebuilds `
        + "it on its own. Re-compile first, or release this version knowing it does not "
        + "carry what was read.",
    };
  }
  return null;
};

// Whether there is a document to release at all.
//
// The stored copy is the only one that can be sent: `compiledPdfUrl` is
// pdf.co's and expires within the hour, which is why the middleware refuses
// without a path. So this is what decides whether the button is offered —
// a button whose only outcome is a refusal should not be on the screen.
export const canRelease = (matter = {}) => Boolean(matter.compiledPdfPath);

// Whether the agent's download link still opens the contract — what the
// matter menu's *Switch off the agent's download link* is offered on. The rule
// is the middleware's (\`releaseLinkState\`: a token and an expiry still ahead);
// this only reads the two fields the matter already carries, and the route
// refuses with its own sentence where the two ever disagree.
export const agentLinkLive = (matter = {}, now = Date.now()) => {
  if (!matter.releaseLinkToken || !matter.releaseLinkExpiresAt) return false;
  const until = Date.parse(matter.releaseLinkExpiresAt);
  return Number.isFinite(until) && until > now;
};

// Said when an adoption or a correction moved the matter to a different agent
// and the middleware switched the old one's link off (\`revokedLink\`).
export const REVOKED_LINK_NOTE = "The previous agent's download link was switched off, since the matter no longer names them.";

// **What the action is called once the contract has gone out.** Written twice
// before — the step card and the expanded document modal each carried their
// own copy — and the word matters here: staff went looking for *resend* and
// found "Re-release", on a screen that was not offering it at all.
//
// It really is a release again rather than a bare resend: the middleware moves
// `releasedVersion` to what is compiled now, restarts the four weeks, and
// rotates the download token if the recipient has changed. The modal is where
// that is decided — it opens holding the address it went to last time and
// offers to move an existing link without sending anything — so the button is
// only the way in.
export const releaseLabel = (matter = {}) =>
  (matter.status === "released" ? "Resend contract" : "Release to agent");

// **The practice look's one send button.** It opens the send dialog, whose
// Agent and Vendor tabs say who it goes to, so the button names the contract
// rather than a recipient — and there is no second button for the vendor.
export const sendContractLabel = (matter = {}) =>
  (matter.status === "released" ? "Resend contract" : "Release contract");

// Where this matter also lives — the client's own form, and the practice
// management system.
//
// **The templates come from the middleware, never from here.** Both are
// Provey's WordPress, so they are one firm's plumbing rather than the product;
// `settings/tenant` withholds them from a deployment serving anybody else, and
// a copy hardcoded in this bundle would send a client firm's staff to Provey's
// intranet.
//
// A link is offered only where there is something to put in it — the client
// form needs the Form 27 entry id, which a matter has only once that form has
// been submitted with the reference on it.
const linkValue = (matter, needs) =>
  needs === "matterRef" ? (matter.ref || matter.matterRef) : matter[needs];

// Whatever single `{slot}` the URL carries, filled without knowing its name —
// so a link added in the middleware needs no change here. The two slots used to
// be named one by one, which is a list that goes stale silently: a link whose
// slot nobody had added rendered with the placeholder still in it.
//
// Encoded, because it lands in a URL. The values are ids today, and a link is
// the wrong place to assume that forever.
const fillSlot = (url, value) => url.replace(/\{[a-zA-Z]+\}/, encodeURIComponent(value));

// **`prefer` where this matter has what it needs, the plain URL otherwise.**
// The practice link is the case: with the office's own matter id it addresses
// the matter in the practice system's application outright, and without one it
// goes through a redirect page that has to find the matter from its reference.
//
// **The fallback is the ordinary case rather than a migration.** That id
// arrives only from a direct Form 265 post, which fires on *update*, so most of
// the collection carries none — and a middleware that has never heard of
// `prefer` sends no such key, which is what lets the two repositories deploy in
// either order.
export const matterLinks = (matter = {}, links = {}) =>
  Object.entries(links || {}).map(([key, link]) => {
    for (const choice of [link?.prefer, link]) {
      if (!choice?.url || !choice?.needs) continue;
      const value = linkValue(matter, choice.needs);
      if (value) return { key, label: link.label || key, href: fillSlot(choice.url, value) };
    }
    return null;
  }).filter(Boolean);

// **A browser target belongs on a browser link, and only there.** The practice
// link can be the practice system's own `smokeball://` scheme, and a custom
// scheme handed to `target="_blank"` leaves an empty tab behind once the
// operating system takes the URL — the page opens nothing and never closes.
//
// Decided from the **scheme** rather than from the link's name, so a second
// application link later needs nothing here.
export const opensInBrowser = (href) => /^https?:/i.test(String(href || ""));

// ── Reading the client's form beside the contract ──────────────────────────
//
// **A floating window, not a modal, and that is the whole requirement.** Staff
// open the client's form to cross-reference the vendor's own instructions
// against the contract — so anything that covers the contract, or blocks the
// page behind it, defeats the thing it was asked for. There is no backdrop, the
// matter stays live underneath, and the window is dragged out of the way rather
// than dismissed.
//
// **Open in new tab is in its title bar and never hidden.** Two reasons, and
// the second is the load-bearing one. Staff asked for it. And the form is a
// page on another origin: if that host ever sends `X-Frame-Options` or a
// `frame-ancestors` policy, the frame goes blank with no error a script can
// catch — there is no reliable way to detect a refused frame from here. A
// permanent escape in the chrome is what turns that from a dead feature into
// one extra click.
//
// **Only the client's form.** The practice system is a whole application on
// another origin, which is the shape least likely to allow framing and least
// useful in a 700px box; it stays the plain new-tab link it was.

// Where the window opens, and how it is kept reachable.
//
// Right-hand side by default, because the contract is what somebody is reading
// and it sits left of centre on this screen. Sized against the viewport rather
// than fixed, so it is usable on a laptop and not a postage stamp on a monitor.
export const openingBox = (vw, vh) => {
  const width = Math.round(Math.max(360, Math.min(680, vw * 0.42)));
  const height = Math.round(Math.max(320, Math.min(860, vh * 0.72)));
  return { x: Math.max(12, vw - width - 24), y: 84, width, height };
};

// **The title bar has to stay grabbable.** A window dragged off the bottom or
// past the right edge cannot be dragged back, so the clamp keeps a strip of it
// on screen rather than the whole box — dragging most of it off the side is a
// legitimate way to park it.
export const GRAB_MARGIN = 120;
export const clampBox = (box, vw, vh) => ({
  ...box,
  x: Math.min(Math.max(box.x, GRAB_MARGIN - box.width), vw - GRAB_MARGIN),
  y: Math.min(Math.max(box.y, 0), Math.max(0, vh - 40)),
});

export const MIN_W = 320;
export const MIN_H = 240;
export const resizeBox = (box, dx, dy) => ({
  ...box,
  width: Math.max(MIN_W, Math.round(box.width + dx)),
  height: Math.max(MIN_H, Math.round(box.height + dy)),
});

// Zoom is the frame scaled, not the page reloaded at another width: the form
// is somebody else's document and nothing here can ask it to reflow.
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 2;
export const ZOOM_STEP = 0.25;
export const zoomBy = (zoom, step) =>
  Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom + step)) * 100) / 100;

// The frame is sized up by the same factor it is scaled down by, so the
// content fills the window at every zoom instead of leaving a margin.
export const framedSize = (zoom) => `${100 / zoom}%`;

const ClientFormWindow = ({ link, onClose }) => {
  const [box, setBox] = useState(() => openingBox(window.innerWidth, window.innerHeight));
  const [zoom, setZoom] = useState(1);
  const drag = useRef(null);

  // One set of pointer handlers for both gestures: what differs is which of
  // the two pure functions the delta is fed to.
  const begin = (mode) => (e) => {
    // **A press on the bar's own controls is not a grab, and saying so is what
    // makes them work at all.** The title bar carries the zoom chips, the
    // escape link and Close, and this handler ran on every one of them:
    // `preventDefault` on `pointerdown` suppresses the compatibility mouse
    // events, and `setPointerCapture` retargets what follows at the bar — so
    // the click never reached the button. Dragging worked perfectly, which is
    // exactly why it read as three separate things being broken.
    //
    // **The tests could not see it**, because `fireEvent.click` dispatches a
    // click with no pointer sequence in front of it. A guard for this has to
    // press the bar first.
    if (e.target?.closest?.("[data-window-control]")) return;
    e.preventDefault();
    drag.current = { mode, x: e.clientX, y: e.clientY, box };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const from = drag.current;
    if (!from) return;
    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;
    setBox(from.mode === "move"
      ? clampBox({ ...from.box, x: from.box.x + dx, y: from.box.y + dy }, window.innerWidth, window.innerHeight)
      : resizeBox(from.box, dx, dy));
  };
  const end = () => { drag.current = null; };

  const chip = (label, onClick, title) => (
    <button type="button" onClick={onClick} title={title} data-window-control=""
      style={{ background: "none", border: "none", color: T.textFaint, cursor: "pointer", fontSize: 13, lineHeight: 1, padding: "4px 6px", fontFamily: "Inter, sans-serif" }}>
      {label}
    </button>
  );

  // **Rendered into the body, not where it is declared.** `position: fixed`
  // and a z-index are only ever measured inside the nearest ancestor that
  // establishes a stacking context, and this is declared inside the matter's
  // rail card — so the window opened *underneath* the search list, which is
  // what staff reported. A portal has no ancestor to be trapped by, which is
  // a property of the element rather than a number somebody has to keep
  // ahead of every other number on the page.
  return createPortal((
    <div data-client-form-window="" style={{
      position: "fixed", left: box.x, top: box.y, width: box.width, height: box.height,
      zIndex: 150, background: T.white, border: `1px solid ${T.borderMid}`, borderRadius: 12,
      boxShadow: "0 18px 50px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      <div
        data-window-bar=""
        onPointerDown={begin("move")}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 8px 8px 12px", borderBottom: `1px solid ${T.border}`, background: T.bgAlt, cursor: "move", touchAction: "none", flex: "0 0 auto" }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 500, color: T.text, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {link.label}
        </span>
        {chip("\u2212", () => setZoom(z => zoomBy(z, -ZOOM_STEP)), "Zoom out")}
        <span style={{ fontSize: 11, color: T.textFaint, minWidth: 34, textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
        {chip("+", () => setZoom(z => zoomBy(z, ZOOM_STEP)), "Zoom in")}
        <a href={link.href} target="_blank" rel="noopener noreferrer" data-window-control=""
          style={{ fontSize: 11.5, color: ACCENT, textDecoration: "none", padding: "4px 8px", whiteSpace: "nowrap" }}>
          Open in new tab
        </a>
        {chip("\u2715", onClose, "Close")}
      </div>

      <div style={{ flex: 1, position: "relative", overflow: "hidden", background: T.bg }}>
        <iframe
          title={link.label}
          src={link.href}
          style={{
            border: "none", display: "block", background: T.white,
            width: framedSize(zoom), height: framedSize(zoom),
            transform: `scale(${zoom})`, transformOrigin: "0 0",
          }}
        />
      </div>

      {/* Bottom-right grip. Its own pointer handlers rather than the bar's, so
          a drag that starts here resizes and never moves the window. */}
      <div
        onPointerDown={begin("resize")}
        onPointerMove={move}
        onPointerUp={end}
        style={{ position: "absolute", right: 0, bottom: 0, width: 18, height: 18, cursor: "nwse-resize", touchAction: "none" }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke={T.textFaint} strokeWidth="1.4">
          <path d="M16 6 6 16M16 11l-5 5" />
        </svg>
      </div>
    </div>
  ), document.body);
};

// Where the matter lives elsewhere, under the details it belongs with.
//
// **In the rail rather than the header or the overflow menu.** That card is
// already what this matter *is* — handler, agent, when it opened — and where
// else it lives is the same kind of fact, read as often. The overflow menu
// holds Archive and Delete, which are rare and destructive; putting frequent
// navigation behind the same click hides it and mixes two different kinds of
// action.
// **Which links open here rather than away.** Named rather than "everything
// that can be framed", because whether a page allows framing is not something
// this side can ask — see `ClientFormWindow`.
export const FRAMED_LINKS = new Set(["clientForm"]);

export const MatterLinks = ({ matter, tenant, inline = false, buttons = false }) => {
  const links = matterLinks(matter, tenant?.links);
  const [framed, setFramed] = useState(null);
  if (links.length === 0) return null;

  // **A real anchor either way, so a modifier-click still opens a tab.** The
  // window is what a plain click gets; ctrl, cmd, shift and the middle button
  // are how browsers have always meant "not here", and intercepting those
  // would take a behaviour away to add one.
  const openHere = (l) => (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    setFramed(l);
  };

  return (
    // **Inline, it is one more fact on the matter's line** — no rule above it
    // and no top margin, which were the facts card's and pushed the links a
    // line-height below the words beside them.
    // **As buttons, in the practice look's matter header**: the links to the
    // client's form and the practice management system are where the office
    // works from while it still uses them, so they are worth a button.
    <div style={buttons
      ? { display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }
      : inline
      ? { display: "inline-flex", alignItems: "center", gap: 12, flexWrap: "wrap", paddingLeft: 12, borderLeft: `1px solid ${T.border}` }
      : { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4, paddingTop: 9, borderTop: `1px solid ${T.border}` }}>
      {links.map(l => (
        <a key={l.key} href={l.href}
          target={opensInBrowser(l.href) ? "_blank" : undefined}
          rel={opensInBrowser(l.href) ? "noopener noreferrer" : undefined}
          onClick={FRAMED_LINKS.has(l.key) ? openHere(l) : undefined}
          className={buttons ? undefined : "s32-link"}
          style={buttons
            ? { height: 32, padding: "0 12px", fontSize: 14, fontWeight: 500, fontFamily: "Inter, sans-serif", color: T.brand, background: T.brandTint, border: `1px solid ${T.brandStroke}`, borderRadius: 4, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }
            : { fontSize: inline ? 12.5 : 12, color: "#1a7ac4", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
          {l.label}
          {FRAMED_LINKS.has(l.key) ? (
            // Opens beside the contract rather than away from it, so the icon
            // says so: the arrow out of a box is what the other link means.
            <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
            </svg>
          ) : (
            <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          )}
        </a>
      ))}
      {framed && <ClientFormWindow link={framed} onClose={() => setFramed(null)} />}
    </div>
  );
};

// How tall an editing box should be.
//
// **Section 13's attachment list is the case that forced this.** Clicking it
// opens with the generated list seeded in — forty certificate names on forty
// lines — into a two-row box, which is unusable for the one field somebody
// actually needs to read while editing. Sizing from the content fixes that
// field and every other long value with it, and needs nothing new on the
// registry: a short value still gets a short box.
export const editorRows = (value, base = 2) => {
  const lines = String(value ?? "").split("\n").length;
  return Math.min(12, Math.max(base, lines + 1));
};

// **A list over the document opens at one line and grows a line per entry**,
// on the firm's instruction: the s.127 officers box opened four lines tall
// for two names, and in a popover that already holds five fields that is the
// box pushing Save off the screen. A one-line `<input>` would drop the line
// breaks that separate the entries, so it stays a textarea sized to them.
export const listRows = (value) =>
  Math.min(12, Math.max(1, String(value ?? "").split("\n").length));

// The one green in the portal, and after this change it means exactly one
// thing: the contract has gone out. It was spelled "#2a8a52" inline in thirty
// places; a status is the wrong place to learn that a hex was mistyped.
const OK = "#2a8a52";
// The portal's blue, for work in flight and for something to act on. It was a
// literal in a dozen places; named here because `NOTICE_TONE.blue` claims to be
// the same blue the search rows use for *being read…*, and a claim like that
// has to be one value rather than two that happen to match today.
const ACCENT = "#1a7ac4";


// A status is an outline on white, never a tint. Five filled pastel pills in a
// column is five colours competing at the same weight, and the blue and the
// amber were carrying no meaning the label did not already carry.
//
// **Green is released and nothing else.** `Compiled — review` looks like a
// finished state and is the opposite of one: the document exists and nobody
// has checked it, and it has not gone anywhere. Released is the one
// irreversible thing on this screen — the agent and the client hold a copy —
// so it keeps the colour that means *done*, and compiled takes the ink: the
// strongest neutral, which still reads as "this matter has a document".
//
// Red stays for a failed compile. The two before the document exists are grey,
// which is what most matters are most of the time.
const STATUS_CFG = {
  searches_pending:  { label: "Searches pending", color: T.textFaint, border: T.borderMid },
  searches_complete: { label: "Searches complete", color: T.textSoft, border: T.borderMid },
  // `short` is the practice look's tab badge, where the tab says the rest.
  compiled:          { label: "Compiled — review", short: "Compiled", color: T.text, border: T.text },
  compile_failed:    { label: "Compile failed", color: T.red, border: T.red },
  released:          { label: "Released", color: OK, border: OK },
};

const SEARCH_STATUS_CFG = {
  "Returned":     { color: "#2a8a52", dot: "#2a8a52", border: "rgba(42,138,82,0.25)" },
  "Ordered":      { color: T.textFaint, dot: "#d4ccc4", border: T.border },
  "Not Required": { color: T.textFaint, dot: T.textFaint, border: T.border },
};

// A document staff added reads as its own thing, not as a search.
//
// It was showing "Returned" in green, which describes something that was
// ordered and came back — nothing ordered this and nothing returned it. The
// pill is the only thing on the row that says what a line *is*, so an upload
// wearing a certificate's status makes the list a place where orders appear to
// have been placed, and the count of what is outstanding is read off that list.
const UPLOAD_CFG = { color: "#6b5f57", dot: "#a89684", border: "rgba(107,95,87,0.3)", label: "Uploaded" };

// A document staff forwarded to the search inbox rather than uploaded here.
//
// The same thing as an upload in every respect but the door — so the pill says
// *Emailed* where it would say *Uploaded*, and a forwarded file the middleware
// attached to an ordered search keeps its *Returned* pill and carries a small
// *emailed* tag beside the name instead. Either way the hover says who sent it,
// what the file was called when it arrived, and whether the name on the row is
// the model's reading of the page — since scan0001.pdf is usually all there
// was to go on, and a name staff did not type is one worth glancing at.
export const emailedNote = (s) => {
  if (!s?.emailedIn) return null;
  const parts = [s.emailedFrom ? `Emailed in by ${s.emailedFrom}` : "Emailed in"];
  if (s.emailedFileName) parts.push(`as ${s.emailedFileName}`);
  let note = parts.join(" ");
  if (s.namedBy === "ai") note += " — named by AI from the document; rename it if it is wrong";
  return note;
};

// ── Shared ─────────────────────────────────────────────────────────
const Badge = ({ status, short = false }) => {
  const c = STATUS_CFG[status] || STATUS_CFG.searches_pending;
  const label = (short && c.short) || c.label;
  return <span style={{ fontSize: 11, fontWeight: 500, color: c.color, background: c.bg || T.white, border: `1px solid ${c.border}`, padding: "2px 9px", borderRadius: 999, whiteSpace: "nowrap", display: "inline-block", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>;
};

// **`busy` is not `disabled`, and the difference is what somebody reads off
// the screen.** Both stop the press, and only one of them should look like the
// control has gone away: a greyed button at 0.4 says *unavailable*, which on a
// press somebody has just made reads as nothing having happened. So a busy
// button keeps its colour and wears the chase — the rotating rim this codebase
// already uses for "a model is thinking" — and its own border comes off, or
// the two draw as concentric rings.
//
// The wrapper is inset by its own padding so the footprint does not change:
// in a row of buttons a 4px growth nudges every neighbour the moment somebody
// presses one.
// **The lot search is beta, and says so on the press.** A marker is only as
// good as the plan it was read from — scans, faint CAD lettering, sheets
// hundreds deep — and the agent's own *Find unit location* carries the same
// tag and the same sentence, served by the middleware. Staff and agent are
// looking at one mark, so they are told one thing about it.
export const LOT_BETA_HINT = "Beta \u2014 plans vary in quality, so the location shown is not always accurate";
const BetaTag = () => (
  <span data-beta style={{ display: "inline-block", padding: "0 5px", border: "1px solid currentColor",
    borderRadius: 999, fontSize: 9, fontWeight: 600, lineHeight: "13px", letterSpacing: "0.04em",
    textTransform: "uppercase", opacity: 0.65 }}>Beta</span>
);

const Btn = ({ children, onClick, variant = "primary", size = "sm", style: sx = {}, disabled = false, busy = false }) => {
  const fluent = useFluent();
  const off = disabled || busy;
  const base = { display: "inline-flex", alignItems: "center", gap: 5, borderRadius: fluent ? 4 : 999, fontFamily: "Inter, sans-serif", fontWeight: 400, cursor: off ? (busy ? "default" : "not-allowed") : "pointer", border: "1px solid transparent", transition: "all 0.15s", whiteSpace: "nowrap", opacity: disabled && !busy ? 0.4 : 1, flexShrink: 0 };
  const sizes = fluent
    ? { sm: { fontSize: 13, padding: "5px 12px", fontWeight: 600 }, md: { fontSize: 14, padding: "6px 14px", fontWeight: 600 } }
    : { sm: { fontSize: 12, padding: "6px 13px" }, md: { fontSize: 13, padding: "9px 20px" } };
  const variants = {
    primary: fluent ? { background: T.brand, color: T.onBrand, borderColor: T.brand } : { background: T.brown, color: T.warmGrey, borderColor: T.brown },
    ghost:   { background: "transparent", color: T.text, borderColor: T.border },
    warm:    { background: T.bgAlt, color: T.text, borderColor: T.border },
    // For a secondary action sitting *on* a raised panel. `warm` is bgAlt on
    // bgAlt there, so the button had no edge at all — it read as a link, on
    // the card whose whole job is to offer the one thing to do next.
    raised:  { background: T.white, color: T.text, borderColor: T.borderMid },
  };
  // **`disabled` on the element, not only in the styling.** Dropping the
  // handler and greying it stops a mouse and nothing else: the button stayed
  // in the tab order and announced as available, so a keyboard press did
  // nothing at all — which reads as broken rather than as unavailable. It
  // matters most where the flag is a guard rather than a hint: Release and
  // Compile are disabled precisely while work is running, and that is the
  // press that would race it.
  // **The chase is a rim, and only an opaque button makes it one.** The
  // gradient is painted behind the whole control and the button is what covers
  // the middle of it — so `ghost`, whose background is transparent, showed the
  // blue straight through and read as a button filling up rather than a border
  // going round. `ThinkingButton` never had this because it hardcodes white.
  // Only a transparent background is replaced: the filled variants already
  // cover it, and painting over `brown` would change what a primary button is.
  const paint = variants[variant];
  const busySkin = busy
    ? { borderColor: "transparent", background: paint.background === "transparent" ? T.white : paint.background }
    : {};
  const button = (
    <button disabled={off} onClick={off ? undefined : onClick}
      style={{ ...base, ...sizes[size], ...paint, ...sx, ...busySkin }}>
      {children}
    </button>
  );
  return busy ? <span className="s32-chase" style={{ margin: -2 }}>{button}</span> : button;
};

const Label = ({ children }) => (
  <div style={{ fontSize: 10, fontWeight: 600, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{children}</div>
);

const SearchProgress = ({ searches, matter }) => {
  const hasLoadedSearches = Array.isArray(searches) && searches.length > 0;
  const done = hasLoadedSearches
    ? searches.filter(s => s.status === "Returned" || s.status === "Not Required").length
    : (matter?.returnedCount || 0);
  const total = hasLoadedSearches ? searches.length : (matter?.searchCount || 0);
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 56, height: 4, background: T.bgAlt, borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? "#2a8a52" : T.brown, borderRadius: 999, transition: "width 0.4s" }} />
      </div>
      <span style={{ fontSize: 11, color: T.textFaint }}>{done}/{total}</span>
    </div>
  );
};

// Removing one uploaded document.
//
// A plain confirm: this removes one file somebody added themselves, which is
// an ordinary correction — a type-to-confirm here would be ceremony, and
// ceremony people learn to click through is worse than none.
//
// It still confirms rather than offering an Undo toast, because the file is
// gone from the bucket the moment it runs and there would be nothing to
// restore. It also says what does *not* happen: the certificate list moves, but
// nothing recompiles on its own.
const DeleteDocumentModal = ({ doc, onConfirm, onCancel }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, width: "100%", maxWidth: 420, boxShadow: "0 12px 48px rgba(0,0,0,0.14)" }}>
      <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: "0 0 5px", letterSpacing: "-0.02em" }}>Remove this document?</h2>
      <p style={{ fontSize: 13, color: T.textSoft, margin: "0 0 8px", lineHeight: 1.6 }}>
        <strong>{doc.name}</strong> will be deleted from this matter and from storage.
        It cannot be undone — you would need to upload the file again.
      </p>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 18px", lineHeight: 1.6 }}>
        
      </p>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="ghost" size="md" onClick={onCancel}>Cancel</Btn>
        <Btn variant="primary" size="md" onClick={onConfirm}
          style={{ background: T.red, borderColor: T.red, color: T.white }}>
          Remove document
        </Btn>
      </div>
    </div>
  </div>
);

// ── Release modal ──────────────────────────────────────────────────
// Which certificates to read again.
//
// **Most of the time the answer is "none", and the dialog says so.** A
// certificate is read as it returns, an uploaded document is read on upload,
// and a replacement arriving as a new file is read because its fingerprint
// moved — so on a healthy matter everything has already been read, and asking
// again spends a full Gemini pass per document to arrive at the same answers.
//
// So the two options are not equals, and are not presented as equals. The
// cheap one is the default and is described by what it repairs; the expensive
// one names the two cases that genuinely need it, because a reader who does not
// know those cases has no way to choose between "read what's missing" and
// "read everything".
const RereadModal = ({ onConfirm, onCancel }) => {
  const [scope, setScope] = useState("missing");
  const [busy, setBusy] = useState(false);

  const option = (value, title, detail) => (
    <label
      key={value}
      style={{
        display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer",
        border: `1px solid ${scope === value ? T.brown : T.border}`,
        background: scope === value ? "rgba(150,110,80,0.05)" : T.white,
        borderRadius: 10, padding: 12, marginBottom: 8,
      }}
    >
      <input
        type="radio"
        name="reread-scope"
        checked={scope === value}
        onChange={() => setScope(value)}
        style={{ width: 15, height: 15, marginTop: 2, accentColor: T.brown }}
      />
      <span>
        <span style={{ display: "block", fontSize: 13, color: T.text, marginBottom: 2 }}>{title}</span>
        <span style={{ display: "block", fontSize: 11, color: T.textFaint, lineHeight: 1.5 }}>{detail}</span>
      </span>
    </label>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, width: "100%", maxWidth: 460, boxShadow: "0 12px 48px rgba(0,0,0,0.14)" }}>
        <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: "0 0 5px", letterSpacing: "-0.02em" }}>Fetch what the documents say</h2>
        <p style={{ fontSize: 12, color: T.textSoft, margin: "0 0 16px", lineHeight: 1.5 }}>
          Each certificate is read as it arrives, so usually there is nothing to do.
        </p>

        {option("missing", "Read what's missing",
          "Anything that failed to read, or never was.")}
        {option("all", "Read everything again",
          "For a certificate replaced in place, or when the questions have changed. Costs a full read of every document.")}
        {/* **Said once, under the two reads, rather than in both.** The
            planning lookup needs the lot on plan, which is what reading the
            title produces — so it is queued behind either read rather than
            being a thing to remember to press afterwards. */}
        <p style={{ fontSize: 11, color: T.textFaint, margin: "-4px 0 12px 26px", lineHeight: 1.5 }}>
          
        </p>
        {/* Not a read of the certificates at all — one query to the state's
            own planning map, keyed on the parcel the title gives us. It sits
            here because it is the same instinct at the same moment: fetch what
            a document would otherwise have told us. It is described by what it
            answers rather than by the service, and by being free, since the
            other two options are chosen partly on what they cost. */}
        {option("vicmap", "Get the planning data from Vicmap",
          "The zone, the overlays, the planning scheme and the bushfire prone area. Free and immediate, for a matter whose title is already read.")}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          {/* **Cancel is never disabled.** Whatever was started carries on
              regardless — the work is on the server — so a disabled Cancel
              only ever traps somebody on this screen with nothing to press,
              which is exactly what a slow Vicmap call did. Closing the modal
              is not cancelling the work and does not claim to be. */}
          <Btn variant="ghost" size="sm" onClick={onCancel}>Cancel</Btn>
          <Btn
            size="sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try { await onConfirm(scope); } finally { setBusy(false); }
            }}
          >
            {busy ? (scope === "vicmap" ? "Looking up…" : "Starting…")
              : (scope === "vicmap" ? "Look up" : "Read")}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// ── Compiling with something still outstanding ─────────────────────
//
// **It only appears when there is something to say.** A confirmation on every
// compile is one people learn to click through without reading, and then it is
// worth nothing on the matter that needed it — the same reasoning that keeps
// `couldFill` out of the searches review's problem count.
//
// The split is the middleware's (`lib/compile-gate.js`), not one worked out
// here. **Blocking** is a required field nothing has filled or a certificate
// every contract needs that nobody ordered — the statement having a hole a
// purchaser would see, and what the automatic compile now waits for.
// **Advisory** is everything else the searches review reports: worth reading
// before you press the button, and wrong to stop a contract for.
//
// A second copy of that line in the browser would drift, and the way it would
// drift is a matter held for something this screen says is fine.
export const compileOutstandingFor = (matter) => {
  const outstanding = matter?.compileOutstanding;
  if (!outstanding) return null;
  const blocking = outstanding.blocking || [];
  const advisory = outstanding.advisory || [];
  if (blocking.length === 0 && advisory.length === 0) return null;
  return { blocking, advisory };
};

// **And before a release, in the practice look**: the same reminder, so what
// the contract still lacks is in front of somebody at the moment it goes out
// — the searches review especially, which otherwise waits below the document.
// The fields and the review are the middleware's `compileOutstanding`; the
// certificates not back are the search rows, counted the way the matter's
// header counts them (`isNotBack`). Nothing here refuses: staff know the
// steps, this only reminds them what is left.
export const releaseOutstandingFor = (matter) => {
  const outstanding = matter?.compileOutstanding || {};
  const fields = (outstanding.blocking || []).filter(b => b.kind === "field");
  const review = [...(outstanding.blocking || []).filter(b => b.kind !== "field"), ...(outstanding.advisory || [])];
  const notBack = (matter?.searches || []).filter(isNotBack)
    .map(s => ({ kind: "search", key: s.id, label: s.displayName || s.name, detail: s.status || "" }));
  if (fields.length === 0 && review.length === 0 && notBack.length === 0) return null;
  return { fields, review, notBack };
};

export const CompileConfirmModal = ({ matter, outstanding, onConfirm, onCancel, release = false }) => {
  const [busy, setBusy] = useState(false);
  const { blocking = [], advisory = [] } = outstanding;
  const again = release ? matter.status === "released" : Boolean(matter.compileVersion);

  const group = (title, note, items, tone) => items.length === 0 ? null : (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, marginTop: 10, overflow: "hidden" }}>
      <div style={{ padding: "9px 12px 10px", borderBottom: `1px solid ${T.border}`, background: T.bgAlt }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: tone, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</span>
          <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 500 }}>{items.length}</span>
        </div>
        <div style={{ fontSize: 11.5, color: T.textSoft, lineHeight: 1.5, marginTop: 3 }}>{note}</div>
      </div>
      <div style={{ maxHeight: 190, overflowY: "auto" }}>
        {items.map((item, i) => (
          <div key={`${item.kind}-${item.key}-${i}`} style={{ padding: "8px 12px", borderTop: i > 0 ? `1px solid ${T.border}` : "none" }}>
            <div style={{ fontSize: 12, color: T.text }}>{item.label}</div>
            {item.detail && <div style={{ fontSize: 11, color: T.textFaint, lineHeight: 1.45, marginTop: 1 }}>{item.detail}</div>}
            {/* The owner-builder flag can be acted on from here: the reminder
                closes, nothing is released or compiled, and the 137B panel
                opens on the matter. */}
            {item.kind === "flag" && OWNER_BUILDER_FLAGS.includes(item.key) && (
              <div style={{ marginTop: 6 }}>
                <Btn variant="ghost" size="sm" onClick={() => { onCancel(); open137bPanel(); }} disabled={busy}>
                  Ask the client for the 137B report
                </Btn>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, width: "100%", maxWidth: 520, boxShadow: "0 12px 48px rgba(0,0,0,0.14)" }}>
        <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: "0 0 5px", letterSpacing: "-0.02em" }}>
          {release
            ? (again ? "Resend with this outstanding?" : "Release with this outstanding?")
            : (again ? "Re-compile with this outstanding?" : "Compile with this outstanding?")}
        </h2>
        {/* The form is designed to issue with blanks completed at sale, so this
            is never a refusal — it is the list somebody should have seen before
            deciding. Saying so stops it reading as an error. */}
        <p style={{ fontSize: 12, color: T.textSoft, margin: "0 0 4px", lineHeight: 1.55 }}>
          What the contract goes out without if you carry on.
        </p>

        {release ? (
          <>
            {group("Contract data still needed", "Required, and nothing has filled it.", outstanding.fields || [], "#b4433a")}
            {group("Searches review", "Where the searches disagree, or the contract needs something they don't give.", outstanding.review || [], T.text)}
            {group("Certificates not back", "Ordered, and not returned or marked not required.", outstanding.notBack || [], T.text)}
          </>
        ) : (
          <>
            {group("Gaps in the statement",
              "A purchaser would see these. The matter won't compile itself while they stand.",
              blocking, "#b4433a")}
            {group("Worth a look first",
              "None of these stops a contract being built.",
              advisory, T.text)}
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <Btn variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Btn>
          <Btn
            size="sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try { await onConfirm(); } finally { setBusy(false); }
            }}
          >
            {release ? "Continue" : busy ? "Starting…" : again ? "Re-compile anyway" : "Compile anyway"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// ── The covering letter ────────────────────────────────────────────────────
//
// A release is not only "who did it go to". The letter that goes with it tells
// the agent three things the office used to type by hand or forget: that the
// contract is a **draft** while the vendor still owes a section 137B condition
// report, what **material facts** this vendor has disclosed, and anything else
// this matter needs said.
//
// **Only those three are staff's.** The concealment warning, the expiry advice
// and the guide are the firm's standard wording, composed by the middleware
// and identical on every release — see its `lib/release-email.js` for why a
// free-text editor over the whole letter is the wrong shape. The custom
// paragraph is the escape hatch, and it adds rather than replaces.
//
// The middleware suggests all three off the matter's own resolved fields; what
// is below is what staff confirmed.

// The textarea and the list are two shapes of one value. One fact per line is
// how somebody writing three of them actually types, and it is what the letter
// prints as bullets.
export const materialFactsLines = (text) => String(text ?? "")
  .split(/\r?\n+/)
  .map(line => line.trim().replace(/^[-–—•*]\s*/, ""))
  .filter(Boolean);

export const materialFactsText = (list) => (Array.isArray(list) ? list : materialFactsLines(list)).join("\n");

// What the controls open holding. Everything the middleware suggested, with
// the two list-shaped values flattened for the boxes that edit them.
//
// **A default this build has never heard of is carried through untouched.**
// The letter's shape is the middleware's, so a note it starts sending — a
// fourth variable part — has to survive a portal that predates it rather than
// being dropped on the next save.
export const releaseNoteState = (defaults = {}) => ({
  ...defaults,
  greetingName: defaults.greetingName || "",
  customParagraph: defaults.customParagraph || "",
  section137bPending: defaults.section137bPending === true,
  ownersCorporation: defaults.ownersCorporation === true,
  includeMaterialFactsGuide: defaults.includeMaterialFactsGuide !== false,
  materialFacts: Array.isArray(defaults.materialFacts) ? defaults.materialFacts : [],
});

// Clicking a highlighted region of the preview focuses the control that writes
// it — the same instinct as the contract preview's click-to-edit, without
// letting anybody rewrite a statutory warning per matter. The middleware marks
// the regions with `data-release-field` and strips the attribute from what is
// actually sent.
const RELEASE_FIELD_HINT = {
  materialFacts: "Material facts — click to edit",
  customParagraph: "Your own paragraph — click to edit",
};

// Exported for the tests. The guarantees worth pinning are all in the JSX and
// the effect — that the letter is *fetched* rather than composed here, that
// the controls open holding what the middleware suggested, and that what they
// hold reaches the release. Pinning the helpers alone would leave every one of
// those untested.
// ── Who a send button sends to ──────────────────────────────────────────────
//
// **The press names the recipient.** "Confirm release" and "Send" said what
// was about to happen and not to whom — and the address is the half that goes
// wrong: a typo, the previous agent left in the box. Naming it on the button is
// the last place somebody reads before the contract leaves the office.
//
// **The shape check is loose on purpose.** Something, an @, something, a dot,
// something — enough to catch a missing domain or a space, never a judgement
// about which addresses are real. It matters most on the CC: the middleware
// drops anything without an @ so a typo cannot stop the real recipient, which
// means a mistyped CC otherwise vanishes with nothing saying so.
const EMAIL_SHAPE = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;
export const looksLikeEmail = (value) => EMAIL_SHAPE.test(String(value || "").trim());

// A CC box is a list. Blank is fine; anything typed must be addresses.
export const looksLikeEmailList = (value) => {
  const parts = String(value || "").split(/[,;]/).map(a => a.trim()).filter(Boolean);
  return parts.every(looksLikeEmail);
};

export const EMAIL_SHAPE_PROBLEM = "That doesn't look like an email address.";

// Said once the box has been left, not on every keystroke — half an address is
// not a mistake yet. The button is disabled either way, so this is the reason
// beside it rather than the guard.
const AddressProblem = ({ show }) => show ? (
  <p role="alert" style={{ fontSize: 11.5, color: T.red, margin: "4px 0 0", lineHeight: 1.4 }}>
    {EMAIL_SHAPE_PROBLEM}
  </p>
) : null;

const badAddressBorder = (bad) => bad ? { borderColor: T.red } : null;

// Long addresses are cut so the button stays a button; the title carries the
// whole of it.
const shortAddress = (address, max = 30) => {
  const a = String(address || "").trim();
  return a.length > max ? `${a.slice(0, max - 1)}…` : a;
};

// The recipient, the CC count and whether it is the file or a link. `verb` is
// the action's own word for sending the file, so a release reads as one; a
// link is always "Send link", since what goes is the link and not the file.
export const sendLabel = ({ to, cc = "", delivery = "attach", version, verb = "Send" }) => {
  const ccCount = String(cc || "").split(/[,;]/).map(a => a.trim()).filter(Boolean).length;
  const head = delivery === "link"
    ? "Send link"
    : `${verb}${version ? ` v${version}` : ""}`;
  const also = ccCount ? ` +${ccCount} cc` : "";
  return `${head} to ${shortAddress(to)}${also}`;
};

// ── Sending it to a colleague to check ──────────────────────────────────────
//
// **Deliberately not the release modal.** That one collects a recipient, a CC,
// a delivery choice, three variable paragraphs and an acknowledgement, because
// it is sending a legal instrument to somebody outside the office. This is an
// internal read of the same PDF: a recipient and a sentence about what to look
// at. Sharing the screen would put the two presses next to each other, and one
// of them marks the matter released.
//
// **The recipient is a colleague, and the office mailbox is copied.** It opened
// holding `crosscheck@provey.com.au`, which read as sending the contract *to* a
// shared mailbox — and a shared mailbox does not cross-check anything. A person
// does. So the box suggests the office's own file handlers, and the shared
// address is shown beside it as the CC it now is.
//
// **The CC is stated, not offered.** The middleware fixes it and ignores
// anything a caller sends, because it is the record that the check was asked
// for; a control here would imply a choice that does not exist. Saying so is
// still worth the line — an address on a send nobody can see is how somebody
// comes to believe the copy is not going out.
//
// **Typed as well as picked.** A handler appointed to no matter yet appears in
// no list, and a picker that could not reach them would be a worse box than
// the plain one this replaces.
// ── A dialog takes the keyboard (DA-29) ─────────────────────────────
//
// The three send dialogs opened with focus still on the page's button
// behind them: Tab walked the page under the backdrop (the first press
// landed on the matter's hidden ⋮), and Escape did nothing, though nearly
// every other dialog in the portal closes on it. These are the ones that
// email people outside the office, so a keypress acting on the wrong layer
// matters most here.
//
// On open: focus the first field, unless focus is already inside. While
// open: Tab and Shift-Tab stay inside; Escape closes — **never while a send
// is in flight**, so a stray key cannot abandon one. On close: focus goes
// back to whatever opened it. `open` is false for the Agent/Vendor dialog
// that is mounted but hidden behind the other tab.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
export const useDialogKeys = (ref, { open = true, onClose, busy = false } = {}) => {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  useEffect(() => {
    const box = ref.current;
    if (!open || !box) return undefined;
    const opener = document.activeElement;
    // Only what is on screen, where there is a layout to ask (a collapsed
    // section's buttons are not Tab stops); without one, every candidate.
    const inside = () => {
      const all = [...box.querySelectorAll(FOCUSABLE)];
      const laidOut = box.getClientRects().length > 0;
      return laidOut ? all.filter(el => el.getClientRects().length > 0 || el === document.activeElement) : all;
    };
    if (!box.contains(document.activeElement)) {
      const field = box.querySelector('input:not([disabled]):not([type="radio"]):not([type="checkbox"]), textarea:not([disabled])');
      (field || inside()[0])?.focus();
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (!busyRef.current) { e.preventDefault(); closeRef.current?.(); }
        return;
      }
      if (e.key !== "Tab") return;
      const list = inside();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (!box.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && typeof opener.focus === "function" && document.contains(opener)) opener.focus();
    };
  }, [open, ref]);
};

export const CrossCheckModal = ({
  matter, ccAddress, onConfirm, onCancel,
  // Defaulted rather than drilled, the same as the directory reads: it is a
  // plain staff GET behind the token every other read uses, and a test hands
  // its own in.
  onLoadHandlers = fetchFileHandlers,
}) => {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);
  useDialogKeys(dialogRef, { onClose: onCancel, busy });
  // Attach by default — a colleague reading a contract wants the file, and a
  // copy of their own outlives the link. The link is on offer at any size
  // because it is what gets past the email limit, and because the reader may
  // be on a phone.
  const [delivery, setDelivery] = useState("attach");
  // A refusal from the middleware, kept here rather than thrown at a toast:
  // "nothing compiled" and "too large" are both decisions to make right now.
  const [problem, setProblem] = useState(null);

  // The handlers, and whether the box is showing them. Loaded once on open —
  // the middleware caches for a minute anyway, and a firm does not gain a
  // conveyancer while a modal is up.
  const [handlers, setHandlers] = useState([]);
  const [showing, setShowing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    onLoadHandlers().then(list => { if (!cancelled) setHandlers(list || []); });
    return () => { cancelled = true; };
  }, [onLoadHandlers]);

  // **Filtered here rather than re-fetched per keystroke.** It is a list of a
  // dozen; asking the server on every letter would be a full read of `matters`
  // behind each one, which is what the middleware's own cache exists to avoid.
  //
  // The matter's own file handler is dropped: they are the person sending it,
  // and offering somebody their own address as a colleague to check their work
  // is the one suggestion that is never right.
  const mine = String(matter.fileHandlerEmail || matter.fileHandler || "").trim().toLowerCase();
  const q = to.trim().toLowerCase();
  // **Not capped.** It was `.slice(0, 6)` — borrowed from the public agent
  // typeahead, where a hard cap and no cursor are most of the security story
  // over thousands of published records. This is a dozen colleagues behind the
  // staff token, and cutting it at six meant the office's other conveyancers
  // simply were not there: the list looked complete and was not, which is
  // worse than a long list.
  //
  // The container scrolls, and the middleware has already dropped anybody with
  // no matter in six months — so what is left is the people who are actually
  // here, and a person can read a dozen names.
  const suggestions = handlers
    .filter(h => (h.email || "").toLowerCase() !== mine)
    .filter(h => !q || `${h.name || ""} ${h.email || ""}`.toLowerCase().includes(q));

  const [toLeft, setToLeft] = useState(false);
  const toOk = looksLikeEmail(to);
  const toBad = toLeft && !!to.trim() && !toOk;

  const send = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await onConfirm(to.trim(), note.trim(), delivery === "link");
    } catch (err) {
      // **Too large to attach switches the choice rather than only saying so.**
      // The link is the answer to that refusal, and leaving somebody to work
      // out which radio to press is how a refusal reads as a dead end — which
      // is exactly what "open it from the matter" used to be.
      if (err.code === "attachment_too_large") setDelivery("link");
      setProblem(err.message || "It was not sent");
      setBusy(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="Send for cross-check" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, width: "100%", maxWidth: 460, boxShadow: "0 12px 48px rgba(0,0,0,0.14)" }}>
        <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: "0 0 5px", letterSpacing: "-0.02em" }}>Send for cross-check</h2>
        {/* **Two facts, because both change what somebody does.** The version,
            since the attachment is a snapshot and the matter moves under it;
            and that this is not a release, because that button sits next to
            this one and pressing the wrong one is not recoverable from the
            timeline afterwards. The rest of what this used to say — internal
            read, the agent is not told — the title already says. */}
        <p style={{ fontSize: 12, color: T.textSoft, margin: "0 0 16px", lineHeight: 1.5 }}>
          Sends{matter.compileVersion ? ` v${matter.compileVersion}` : " the compiled contract"} to
          a colleague. Not a release.
        </p>

        <Label>To</Label>
        <input value={to} onChange={(e) => setTo(e.target.value)} disabled={busy}
          onFocus={() => setShowing(true)}
          onBlur={() => setToLeft(true)}
          placeholder="Another file handler"
          autoComplete="off"
          aria-invalid={toBad || undefined}
          style={{ ...inputStyle, marginBottom: suggestions.length && showing ? 0 : 4, ...badAddressBorder(toBad) }} />
        <AddressProblem show={toBad} />
        {/* The suggestions, under the box rather than in a `datalist`: that
            renders differently in every browser and cannot show the address
            beneath the name, which is the half that tells two colleagues
            apart. Hidden once the typed value is one of them, so a chosen
            address does not sit under a list of itself. */}
        {showing && suggestions.length > 0 && !suggestions.some(h => h.email === to.trim()) && (
          <div style={{ border: `1px solid ${T.border}`, borderTop: "none", borderRadius: "0 0 8px 8px",
                        marginBottom: 4, maxHeight: 210, overflowY: "auto" }}>
            {suggestions.map(h => (
              <button
                key={h.email}
                type="button"
                onClick={() => { setTo(h.email); setShowing(false); }}
                style={{ display: "block", width: "100%", textAlign: "left", font: "inherit",
                         background: T.white, border: "none", borderBottom: `1px solid ${T.border}`,
                         padding: "7px 10px", cursor: "pointer" }}
              >
                <span style={{ fontSize: 12, color: T.text }}>{h.name || h.email}</span>
                {h.name && (
                  <span style={{ fontSize: 11, color: T.textFaint, marginLeft: 6 }}>{h.email}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {/* Stated rather than controlled — see the note above the component.
            Written as the field it is rather than as a sentence about it: the
            caller cannot change it, so what somebody needs is the address. */}
        {ccAddress && (
          <p style={{ fontSize: 11, color: T.textFaint, margin: "0 0 14px", lineHeight: 1.5 }}>
            Cc {ccAddress}
          </p>
        )}

        <Label>Delivery</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "4px 0 14px" }}>
          {/* **A hint only where it changes the choice.** "the document
              itself, in the email" is the label again; "carries no portal
              access" is why the link was built rather than anything to decide;
              and the version is in the line above. What is left is the one
              fact somebody weighs — a link stops working. */}
          {[
            ["attach", "Attach the PDF", ""],
            ["link", "Send a download link", "expires in a week"],
          ].map(([value, label, hint]) => (
            <label key={value} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
              <input type="radio" name="cross-check-delivery" checked={delivery === value}
                disabled={busy} onChange={() => setDelivery(value)} />
              <span>{label}{hint && <span style={{ color: T.textFaint, fontSize: 11 }}> — {hint}</span>}</span>
            </label>
          ))}
        </div>

        <Label>Please look at <span style={{ color: T.textFaint, fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>optional</span></Label>
        {/* No placeholder: "anything you want them to check in particular" is
            the label again, in a sentence, inside the box it labels. */}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} disabled={busy}
          style={{ ...inputStyle, marginBottom: 4, resize: "vertical", fontFamily: "inherit" }} />

        {problem && (
          <p style={{ fontSize: 12, color: T.red, margin: "10px 0 0", lineHeight: 1.5 }}>{problem}</p>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <Btn variant="ghost" size="md" onClick={onCancel} disabled={busy}>Cancel</Btn>
          <Btn variant="primary" size="md" onClick={send} disabled={busy || !toOk}
            title={toOk ? to.trim() : undefined}>
            {busy ? "Sending…" : toOk
              ? sendLabel({ to, delivery, version: matter.compileVersion })
              : delivery === "link" ? "Send a link" : "Send"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// ── Who the contract goes to: one dialog, two tabs ─────────────────────────
//
// **Release and the vendor's copy are tabs of one dialog**, opening on the
// agent. Two dialogs reached from two buttons left staff choosing between
// *Release contract* and *Send to the vendor* before they could see what
// either did; one dialog with the recipient as its first question puts the
// choice where the forms are. Each tab keeps its own form, its own letter and
// its own send button naming who it sends to — the vendor's still says it
// does not release, and nothing on it marks the matter released. Both stay
// mounted while the dialog is open, so switching keeps what was typed.
export const SEND_TABS = [["agent", "Agent"], ["vendor", "Vendor"]];
export const SendTabs = ({ active, onChange }) => (onChange ? (
  <div role="tablist" aria-label="Send the contract to" style={{ display: "flex", gap: 20, borderBottom: `1px solid ${T.border}`, marginTop: 12 }}>
    {SEND_TABS.map(([key, label]) => (
      <button key={key} type="button" role="tab" aria-selected={active === key} onClick={() => onChange(key)}
        style={{ background: "none", border: "none", borderBottom: `2px solid ${active === key ? T.text : "transparent"}`, padding: "0 0 8px", marginBottom: -1, fontSize: 13.5, fontWeight: 500, fontFamily: "Inter, sans-serif", color: active === key ? T.text : T.textSoft, cursor: "pointer" }}>
        {label}
      </button>
    ))}
  </div>
) : null);

export const ReleaseModal = ({ matter, onConfirm, onCancel, hidden = false, tab, onTab }) => {
  const DIALOG_LABEL = matter.status === "released" ? "Resend contract" : "Release contract";
  // Seeded from the matter until the preview answers with the resolved
  // recipient, so the box is never empty while the letter loads.
  const [to, setTo] = useState(matter.agentEmail);
  const [cc, setCc] = useState("");
  // Whether each box has been left, so a half-typed address is not called a
  // mistake — see `AddressProblem`.
  const [left, setLeft] = useState({});
  const toOk = looksLikeEmail(to);
  const ccOk = looksLikeEmailList(cc);
  const toBad = !!String(to || "").trim() && !toOk && (left.to || !!matter.agentEmail);
  const ccBad = !ccOk && !!left.cc;
  const addressesOk = toOk && ccOk;
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);
  useDialogKeys(dialogRef, { open: !hidden, onClose: onCancel, busy });
  // **Whether every vendor has signed the version about to go out.** In
  // Victoria the vendor signs before a purchaser is given the statement, so a
  // contract released unsigned is one the agent will have to replace — and an
  // attachment cannot be replaced, where a link can.
  const sendingSigned = Boolean(matter.signatures?.complete)
    && matter.signedVersion === matter.compileVersion;

  // **The link is the default on an unsigned statement**, and attach on a
  // signed one. The link resolves the document on every open, so it starts
  // serving the executed copy the moment the vendors sign, with no second
  // email and no second URL; an attachment freezes the unsigned version into
  // the agent's inbox. Staff can still choose either — this decides which is
  // pre-selected, not what is allowed.
  const [delivery, setDelivery] = useState(sendingSigned ? "attach" : "link");
  // Released by link and re-compiled since: the existing link can be moved to
  // the new version without a second email — the correction case.
  const canQuietUpdate = matter.releasedVia === "link" && matter.releaseLinkToken
    && (matter.compileVersion || 0) !== (matter.releasedVersion || 0);
  // A refusal from the middleware, kept in the modal rather than thrown at a
  // toast. Two of them are actionable right here — a document too large to
  // email, and sending not being configured — and a toast that disappears is
  // no place for a decision.
  // Set by the middleware from MAIL_REDIRECT_TO. Empty is the ordinary
  // state, and an older middleware sends nothing, which reads the same.
  const [redirect, setRedirect] = useState("");
  const [problem, setProblem] = useState(null);
  // **Shown, never ticked.** There was a *Release anyway* checkbox holding the
  // button until somebody confirmed the warning, and the firm asked for it out:
  // what it mostly caught was an unsigned statement, which is how this office
  // ordinarily releases, so the gate stood between staff and the ordinary case.
  // A warning is worth reading; a tick in front of the press is a step.
  //
  // **A blocking warning still holds it**, and that is not a gate — there is
  // nothing to send, so the press could only ever end in a refusal from the
  // middleware.
  const warning = releaseWarning(matter);
  const held = Boolean(warning?.blocking);

  // `null` until the first preview answers, so the controls are never rendered
  // holding values the middleware has not suggested yet — an unticked 137B box
  // that then ticks itself is worse than a moment of nothing.
  const [note, setNote] = useState(null);
  const [factsText, setFactsText] = useState("");
  const [preview, setPreview] = useState({ loading: true, url: null, subject: "", error: null });
  const frameRef = useRef(null);
  const liveUrl = useRef(null);
  const factsRef = useRef(null);
  const customRef = useRef(null);
  // What the last request asked for. Seeding the controls from the first
  // answer changes `note`, which re-runs the effect — and without this the
  // modal would fetch the identical letter twice on open.
  const lastAsked = useRef(null);

  const inp = { width: "100%", height: 36, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "0 12px", fontSize: 13, color: T.text, fontFamily: "Inter, sans-serif", outline: "none" };
  const box = { ...inp, height: "auto", padding: "9px 12px", lineHeight: 1.5, resize: "vertical" };

  // The letter, rendered by the middleware with the same function the send
  // calls. Fetching it rather than composing it here is the whole point: a
  // preview built in the browser is a second copy of the wording, and the one
  // thing worse than an agent getting the wrong letter is staff being shown a
  // letter that is not the one that went out.
  //
  // Debounced, because it re-runs on every keystroke in the custom paragraph.
  useEffect(() => {
    let cancelled = false;
    // Nothing on the first pass, so the answer carries the suggestion rather
    // than an empty note overwriting it.
    const payload = {
      emailLink: delivery === "link",
      ...(note ? { note: { ...note, materialFacts: materialFactsLines(factsText) } } : {}),
    };
    const asked = JSON.stringify(payload);
    if (asked === lastAsked.current) return undefined;

    const run = () => {
      lastAsked.current = asked;
      apiFetch(`/release-preview/${encodeURIComponent(matter.ref)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.json().catch(() => null);
            throw new Error(body?.error || `Preview failed (${res.status})`);
          }
          return res.json();
        })
        .then((data) => {
          if (cancelled) return;
          if (!note) {
            const seeded = releaseNoteState(data.defaults);
            // The letter this answer rendered is the one the seeded controls
            // describe, so the run that follows the seeding has nothing to
            // ask for.
            lastAsked.current = JSON.stringify({ ...payload, note: seeded });
            setNote(seeded);
            setFactsText(materialFactsText(seeded.materialFacts));
            // **Where the contract goes is the middleware's answer, not the
            // raw Form 27 value.** This box was seeded from `matter.agentEmail`
            // — so a staff correction, an adopted directory record and the
            // agent's own contract-email preference all had no effect on where
            // a statutory disclosure was sent. Only ever a seed: it is a text
            // box, and staff see it before pressing Release.
            if (data.defaults?.to) setTo(data.defaults.to);
            // **And whoever was copied last time.** A matter is released more
            // than once — a correction, a late certificate, a signed copy going
            // to an agent who was sent an unsigned one — and `releasedCc` was
            // recorded on each and read back by nothing, so the second send
            // started with an empty box and the agency copy survived only if
            // somebody remembered it. A seed like the To, not a decision.
            if (data.defaults?.cc) setCc(data.defaults.cc);
          }
          // **Where this is actually going.** With test mode on, every
          // message goes to one mailbox whatever the To says — and that is
          // exactly the thing nobody should discover by checking their
          // inbox. The screen about to send is where it belongs.
          setRedirect(data.mail?.redirectTo || "");
          const next = URL.createObjectURL(new Blob([data.html], { type: "text/html" }));
          // Swap, then revoke — the old letter stays painted until the new one
          // has replaced it, so the pane never flashes empty on a keystroke.
          const previous = liveUrl.current;
          liveUrl.current = next;
          setPreview({ loading: false, url: next, subject: data.subject, error: null });
          if (previous) URL.revokeObjectURL(previous);
        })
        .catch((err) => {
          if (cancelled) return;
          setPreview(p => ({ ...p, loading: false, error: err.message }));
        });
    };
    const timer = setTimeout(run, note ? 400 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matter.ref, delivery, note, factsText]);

  // The blob outlives the effect that made it, so it is released when the
  // modal goes away rather than on every re-render.
  useEffect(() => () => { if (liveUrl.current) URL.revokeObjectURL(liveUrl.current); }, []);

  // Making the editable regions clickable inside the frame.
  //
  // The frame is a blob: URL the portal made, so it is same-origin and its DOM
  // can be reached — the same reason the contract preview fetches its HTML
  // rather than pointing an <iframe src> at the middleware.
  const wireUpLetter = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    noTextInflation(doc);
    doc.querySelectorAll("[data-release-field]").forEach((el) => {
      const field = el.dataset.releaseField;
      const target = field === "customParagraph" ? customRef : factsRef;
      el.style.cursor = "pointer";
      el.style.outline = "1px dashed rgba(156,142,120,0.5)";
      el.style.outlineOffset = "3px";
      el.style.borderRadius = "3px";
      el.title = RELEASE_FIELD_HINT[field] || "Click to edit";
      el.addEventListener("click", () => {
        target.current?.focus();
        target.current?.scrollIntoView({ block: "nearest" });
      });
    });
  };

  const set = (patch) => setNote(n => ({ ...n, ...patch }));

  const attempt = async (sendEmail, emailLink, updateOnly) => {
    setBusy(true);
    setProblem(null);
    try {
      await onConfirm(to, cc, sendEmail, emailLink, updateOnly,
        note ? { ...note, materialFacts: materialFactsLines(factsText) } : undefined);
    } catch (err) {
      setProblem({ message: err.message, code: err.code, canSkip: err.body?.canReleaseWithoutEmail === true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: hidden ? "none" : "flex", alignItems: "center", justifyContent: "center", padding: 24 }} aria-hidden={hidden || undefined}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={DIALOG_LABEL} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, width: "100%", maxWidth: 1080, maxHeight: "92vh", boxShadow: "0 12px 48px rgba(0,0,0,0.14)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "18px 24px 0" }}>
          <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: 0, letterSpacing: "-0.02em" }}>{onTab ? "Send contract" : DIALOG_LABEL}</h2>
          <SendTabs active={tab} onChange={onTab} />
        </div>

        <div style={{ display: "flex", gap: 24, padding: "14px 24px", flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* ── What is being sent, and to whom ───────────────────────── */}
          <div style={{ width: 340, flexShrink: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14, paddingRight: 4 }}>
            {warning && (
              <div style={{ background: "rgba(196,122,26,0.06)", border: "1px solid rgba(196,122,26,0.3)", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "#c47a1a", marginBottom: 4 }}>
                  {warning.title}
                </div>
                <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>{warning.body}</div>
              </div>
            )}
            {/* **What the agent will actually receive.** The middleware serves
                the executed copy for a version every vendor has signed — the
                vendor signs before the purchaser is given the statement — and
                the difference between that and the compiled version is
                invisible on this screen otherwise. A quiet substitution is
                exactly what somebody should not discover from the attachment. */}
            {sendsExecutedCopy(matter) && (
              <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5,
                            background: T.bg, borderRadius: 8, padding: "10px 12px" }}>
                This sends the <strong>executed</strong> statement — version {matter.signedVersion},
                carrying each vendor's signature in the form's own blocks.
              </div>
            )}
            {redirect && (
              <div style={{ border: `1px solid ${T.red}`, background: "#FFF4F2",
                            borderRadius: 8, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5 }}>
                <strong>Test mode.</strong> This will be sent to <code>{redirect}</code>, not to the
                addresses below — the real recipients are named on the subject.
              </div>
            )}
            <div>
              <Label>To</Label>
              <input value={to} onChange={e => setTo(e.target.value)} onBlur={() => setLeft(l => ({ ...l, to: true }))}
                aria-invalid={toBad || undefined} style={{ ...inp, ...badAddressBorder(toBad) }} />
              <AddressProblem show={toBad} />
            </div>
            <div>
              <Label>CC <span style={{ color: T.textFaint, fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>optional</span></Label>
              <input value={cc} onChange={e => setCc(e.target.value)} onBlur={() => setLeft(l => ({ ...l, cc: true }))}
                placeholder="email@example.com" aria-invalid={ccBad || undefined} style={{ ...inp, ...badAddressBorder(ccBad) }} />
              <AddressProblem show={ccBad} />
            </div>
            <div>
              <Label>Delivery</Label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                {[
                  ["attach", "Attach the PDF"],
                  ["link", "Send a download link"],
                ].map(([value, label]) => (
                  <label key={value} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                    <input type="radio" name="release-delivery" checked={delivery === value} onChange={() => setDelivery(value)} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* ── The three things the letter varies ─────────────────────
                Suggested by the middleware off this matter's own answers, and
                staff's to change. Everything else in the letter is the firm's
                standard wording and is not editable here — deliberately: it is
                advice about a legal instrument, and wording that varies by
                whoever pressed send is wording with no record behind it. */}
            {note && (
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
                <Label>The letter</Label>
                <div>
                  <Label>Greeting</Label>
                  <input
                    value={note.greetingName}
                    onChange={e => set({ greetingName: e.target.value })}
                    placeholder="Sir/Madam"
                    style={inp}
                  />
                </div>
                {/* **Above the material facts, because that is where it
                    prints.** The letter reads this matter's own note under an
                    *Important* heading before the standard advice, and a form
                    whose boxes run in a different order from the letter beside
                    it makes somebody hunt for the one they just typed into. */}
                <div>
                  <Label>Anything else <span style={{ color: T.textFaint, fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>optional</span></Label>
                  <textarea
                    ref={customRef}
                    rows={editorRows(note.customParagraph, 3)}
                    value={note.customParagraph}
                    onChange={e => set({ customParagraph: e.target.value })}
                    placeholder="A paragraph for this matter, printed under an Important heading"
                    style={box}
                  />
                </div>
                <div>
                  <Label>Material facts to disclose <span style={{ color: T.textFaint, fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>one per line</span></Label>
                  <textarea
                    ref={factsRef}
                    rows={editorRows(factsText, 2)}
                    value={factsText}
                    onChange={e => setFactsText(e.target.value)}
                    placeholder="Nothing to disclose"
                    style={box}
                  />
                </div>
                {/* **The two ticks share a row**, to keep the column short enough
                    that an ordinary screen does not have to scroll it. The
                    draft's condition is in its tooltip. */}
                <div style={{ display: "flex", flexWrap: "wrap", columnGap: 18, rowGap: 6 }}>
                  <label title="Subject to the vendor providing a section 137B condition report"
                    style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={note.section137bPending}
                      onChange={e => set({ section137bPending: e.target.checked })}
                    />
                    <span>Subject to s137B report provided later</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={note.includeMaterialFactsGuide}
                      onChange={e => set({ includeMaterialFactsGuide: e.target.checked })}
                    />
                    <span>Material facts guide</span>
                  </label>
                </div>
              </div>
            )}

            {problem && (
              <div style={{ background: "rgba(196,122,26,0.06)", border: "1px solid rgba(196,122,26,0.25)", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "#c47a1a", marginBottom: 4 }}>
                  {problem.code === "attachment_too_large" ? "Too large to email" : "Not sent"}
                </div>
                <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
                  {problem.message}
                  {problem.canSkip && problem.code === "attachment_too_large" && (
                    <>
                      {" "}Email a download link instead — unique to this recipient,
                      active for four weeks, and it always serves the latest released
                      version — or download the contract and send it yourself, then
                      record the release.
                      {/* The link is the middleware's own token URL for this one
                          matter's released contract — never the portal's
                          compiled-PDF URL, whose ?token= is the shared staff key
                          to every matter. */}
                    </>
                  )}
                  {problem.canSkip && problem.code !== "attachment_too_large" && (
                    <>{" "}Download the contract and send it yourself, then record the release below.</>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── The letter as it will be sent ─────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.bg }}>
            <div style={{ padding: "9px 12px", borderBottom: `1px solid ${T.border}`, background: T.white, display: "flex", alignItems: "baseline", gap: 8, minHeight: 20 }}>
              <span style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textFaint, flexShrink: 0 }}>Subject</span>
              <span style={{ fontSize: 12.5, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {preview.subject || "…"}
              </span>
            </div>
            <div style={{ flex: 1, minHeight: 300, position: "relative" }}>
              {preview.error
                ? <div style={{ padding: 18, fontSize: 12.5, color: T.textSoft, lineHeight: 1.5 }}>
                    The letter could not be previewed: {preview.error}
                    {/* The preview failing is not a reason to hold the release
                        — the send composes the letter itself. */}
                  </div>
                : preview.url
                  ? <iframe
                      ref={frameRef}
                      src={preview.url}
                      onLoad={wireUpLetter}
                      title="Release email preview"
                      style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                    />
                  : <div style={{ padding: 18, fontSize: 12.5, color: T.textFaint }}>Building the letter…</div>}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center", padding: "0 24px 18px", flexWrap: "wrap" }}>
          {/* **In the footer, where it is always in view.** It sat at the foot
              of the scrolling column, under the letter's boxes, and staff on
              an ordinary screen never scrolled far enough to find it — so a
              re-compiled matter went unupdated and the agent's link kept
              serving the old version. */}
          {canQuietUpdate && !problem?.canSkip && (
            <Btn variant="ghost" size="md" onClick={() => attempt(undefined, false, true)} disabled={busy || held}
              style={{ marginRight: "auto" }}>
              Update download link to v{matter.compileVersion} without emailing agent
            </Btn>
          )}
          <Btn variant="ghost" size="md" onClick={onCancel}>Cancel</Btn>
          {problem?.canSkip && (
            <>
              <Btn variant="ghost" size="md" onClick={() => window.open(compiledPdfSrc(matter), "_blank")}>
                Open contract
              </Btn>
              {problem.code === "attachment_too_large" && (
                <Btn variant="primary" size="md" onClick={() => attempt(undefined, true)} disabled={busy || !addressesOk || held}
                  title={toOk ? to.trim() : undefined}>
                  {busy ? "Sending…" : toOk ? sendLabel({ to, cc, delivery: "link" }) : "Email a download link"}
                </Btn>
              )}
              <Btn variant="warm" size="md" onClick={() => attempt(false)} disabled={busy || !toOk || held}>
                {busy ? "Recording…" : "Record release without emailing"}
              </Btn>
            </>
          )}
          {!problem?.canSkip && (
            <Btn variant="primary" size="md" onClick={() => attempt(undefined, delivery === "link")} disabled={busy || !addressesOk || held}
              title={toOk ? to.trim() : undefined}>
              {busy ? "Releasing…" : toOk
                ? sendLabel({ to, cc, delivery, version: matter.compileVersion, verb: "Release" })
                : delivery === "link" ? "Send download link" : "Confirm release"}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
};

// ── The client's own copy, which is not a release ───────────────────────────
//
// **Its own form, a tab of the send dialog** (`SendTabs`). The release modal
// collects a recipient, a CC, a delivery choice and three variable paragraphs
// because it is sending a legal instrument to somebody outside the office;
// this sends the same pack to the firm's own client, one letter each, with
// their signing link in it if staff ask for one. The two never share a form
// or a button, because one of them marks the matter released.
//
// **What varies is the middleware's, and so is the letter.** The private-sale
// toggle opens on what the matter says — `hasAnyAgentDetail` and
// `notRequired.agent` — and the preview is fetched rather than composed here,
// for the reason the release modal's is: a letter built in the browser is a
// second copy of the wording.
export const vendorNoteState = (defaults = {}) => ({
  ...defaults,
  privateSale: defaults.privateSale === true,
  customParagraph: defaults.customParagraph || "",
  section137bPending: defaults.section137bPending === true,
  ownersCorporation: defaults.ownersCorporation === true,
  includeMaterialFactsGuide: defaults.includeMaterialFactsGuide !== false,
});

export const VendorReleaseModal = ({ matter, onConfirm, onCancel, hidden = false, tab, onTab }) => {
  const DIALOG_LABEL = "Send to the vendor";
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);
  useDialogKeys(dialogRef, { open: !hidden, onClose: onCancel, busy });
  const [note, setNote] = useState(null);
  // Seeded from the preview, and staff's to correct: the office record
  // routinely carries one address for a matter with three vendors, which is
  // the whole reason the signing page asks a co-owner for the others.
  const [people, setPeople] = useState([]);
  // **Off by default.** Minting a signing token is a write grant sitting in an
  // inbox, so it is a tick somebody makes rather than a press that makes it —
  // the rule `send` on the signature request already follows.
  const [requestSignature, setRequestSignature] = useState(false);
  // Somebody else to copy on every vendor's letter — a power of attorney, the
  // vendor's accountant. Kept out of the preview payload like the addresses:
  // it changes nothing the letter says.
  const [cc, setCc] = useState("");
  const [redirect, setRedirect] = useState("");
  const [problem, setProblem] = useState(null);
  const [preview, setPreview] = useState({ loading: true, url: null, subject: "", error: null });
  const [previewFor, setPreviewFor] = useState("");
  const frameRef = useRef(null);
  const liveUrl = useRef(null);
  const customRef = useRef(null);
  const lastAsked = useRef(null);

  const inp = { width: "100%", height: 36, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "0 12px", fontSize: 13, color: T.text, fontFamily: "Inter, sans-serif", outline: "none" };
  const box = { ...inp, height: "auto", padding: "9px 12px", lineHeight: 1.5, resize: "vertical" };

  // **The addresses stay out of the payload.** They change nothing about the
  // letter — it greets the first vendor by name either way — so sending them
  // would re-fetch the identical letter on every keystroke in an address box.
  useEffect(() => {
    let cancelled = false;
    const payload = { requestSignature, ...(note ? { note } : {}) };
    const asked = JSON.stringify(payload);
    if (asked === lastAsked.current) return undefined;

    const run = () => {
      lastAsked.current = asked;
      apiFetch(`/vendor-release-preview/${encodeURIComponent(matter.ref)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.json().catch(() => null);
            throw new Error(body?.error || `Preview failed (${res.status})`);
          }
          return res.json();
        })
        .then((data) => {
          if (cancelled) return;
          if (!note) {
            const seeded = vendorNoteState(data.defaults);
            lastAsked.current = JSON.stringify({ ...payload, note: seeded });
            setNote(seeded);
            setPeople((data.recipients || []).map(one => ({ ...one, email: one.email || "" })));
          }
          setPreviewFor(data.previewFor || "");
          setRedirect(data.mail?.redirectTo || "");
          const next = URL.createObjectURL(new Blob([data.html], { type: "text/html" }));
          const previous = liveUrl.current;
          liveUrl.current = next;
          setPreview({ loading: false, url: next, subject: data.subject, error: null });
          if (previous) URL.revokeObjectURL(previous);
        })
        .catch((err) => {
          if (cancelled) return;
          setPreview(p => ({ ...p, loading: false, error: err.message }));
        });
    };
    const timer = setTimeout(run, note ? 400 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matter.ref, requestSignature, note]);

  useEffect(() => () => { if (liveUrl.current) URL.revokeObjectURL(liveUrl.current); }, []);

  const wireUpLetter = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    noTextInflation(doc);
    doc.querySelectorAll("[data-release-field]").forEach((el) => {
      el.style.cursor = "pointer";
      el.style.outline = "1px dashed rgba(156,142,120,0.5)";
      el.style.outlineOffset = "3px";
      el.style.borderRadius = "3px";
      el.title = "Your own paragraph — click to edit";
      el.addEventListener("click", () => {
        customRef.current?.focus();
        customRef.current?.scrollIntoView({ block: "nearest" });
      });
    });
  };

  const set = (patch) => setNote(n => ({ ...n, ...patch }));
  const setEmail = (position, email) =>
    setPeople(list => list.map(one => (one.position === position ? { ...one, email } : one)));

  const reachable = people.filter(one => looksLikeEmail(one.email)).length;
  // A box holding something that is not an address holds the send: that
  // vendor would otherwise be skipped quietly while the toast says sent.
  const [leftBox, setLeftBox] = useState({});
  const badBox = (one) => !!String(one.email || "").trim() && !looksLikeEmail(one.email);
  const ccBad = !looksLikeEmailList(cc) && !!leftBox.cc;
  const anyBad = people.some(badBox) || !looksLikeEmailList(cc);

  const attempt = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await onConfirm({
        requestSignature,
        note,
        cc: cc.trim(),
        emails: addressPayload(people.map(one => one.position),
          Object.fromEntries(people.map(one => [one.position, one.email]))),
      });
    } catch (err) {
      setProblem({ message: err.message, code: err.code,
        canRequestSignature: err.body?.canRequestSignature === true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: hidden ? "none" : "flex", alignItems: "center", justifyContent: "center", padding: 24 }} aria-hidden={hidden || undefined}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={DIALOG_LABEL} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, width: "100%", maxWidth: 1080, maxHeight: "92vh", boxShadow: "0 12px 48px rgba(0,0,0,0.14)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "18px 24px 0" }}>
          <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: "0 0 3px", letterSpacing: "-0.02em" }}>{onTab ? "Send contract" : DIALOG_LABEL}</h2>
          <SendTabs active={tab} onChange={onTab} />
          {/* **It does not release the matter**, and the one line under the
              heading is where that has to be said — the two presses sit in the
              same menu and only one of them puts the contract in front of the
              market. */}
          <p style={{ fontSize: 13, color: T.textFaint, margin: 0, lineHeight: 1.5 }}>
            This does not release the matter to the agent.
          </p>
        </div>

        <div style={{ display: "flex", gap: 24, padding: "14px 24px", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <div style={{ width: 340, flexShrink: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14, paddingRight: 4 }}>
            {redirect && (
              <div style={{ border: `1px solid ${T.red}`, background: "#FFF4F2",
                            borderRadius: 8, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5 }}>
                <strong>Test mode.</strong> These will be sent to <code>{redirect}</code>, not to the
                addresses below — the real recipients are named on the subject.
              </div>
            )}

            <div>
              <Label>The vendors</Label>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
                {people.map(one => (
                  <div key={one.position}>
                    <div style={{ fontSize: 12.5, color: T.text, marginBottom: 4 }}>
                      {one.name || `Signatory ${one.position}`}
                      {one.capacity && <span style={{ color: T.textFaint, fontSize: 11 }}> — {one.capacity}</span>}
                    </div>
                    {/* Named, because the visible label is a sibling div and
                        every one of these boxes otherwise carries the same
                        placeholder — which leaves the address for one vendor
                        indistinguishable from another's. */}
                    <input
                      value={one.email}
                      onChange={e => setEmail(one.position, e.target.value)}
                      onBlur={() => setLeftBox(b => ({ ...b, [one.position]: true }))}
                      aria-label={`Email for ${one.name || `signatory ${one.position}`}`}
                      aria-invalid={badBox(one) || undefined}
                      placeholder="No address on the matter"
                      style={{ ...inp, ...badAddressBorder(badBox(one) && leftBox[one.position]) }}
                    />
                    <AddressProblem show={badBox(one) && !!leftBox[one.position]} />
                    {/* Whose word the address is — the office's client record,
                        staff, or a co-owner who supplied it on the signing
                        page. A suspect one otherwise surfaces as nobody
                        signing. */}
                    {one.emailSaid && (
                      <p style={{ fontSize: 11, color: T.textFaint, margin: "4px 0 0" }}>{one.emailSaid}</p>
                    )}
                  </div>
                ))}
                {!people.length && (
                  <p style={{ fontSize: 12, color: T.textSoft, margin: 0 }}>
                    No vendor is named on this matter yet.
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label>CC <span style={{ color: T.textFaint, fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>optional</span></Label>
              <input value={cc} onChange={e => setCc(e.target.value)} onBlur={() => setLeftBox(b => ({ ...b, cc: true }))}
                aria-label="CC" placeholder="email@example.com" aria-invalid={ccBad || undefined}
                style={{ ...inp, ...badAddressBorder(ccBad) }} />
              <AddressProblem show={ccBad} />
              {/* Each vendor gets their own letter, so the CC is on each one. */}
              {people.length > 1 && cc.trim() && (
                <p style={{ fontSize: 11, color: T.textFaint, margin: "4px 0 0" }}>Copied on each vendor&rsquo;s letter.</p>
              )}
            </div>

            <label title="Each letter carries that vendor's own signing link. Without it they get a link to read the contract."
              style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={requestSignature}
                onChange={e => setRequestSignature(e.target.checked)}
              />
              <span>Ask them to sign</span>
            </label>

            {note && (
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
                <Label>The letter</Label>
                {/* **The one thing that changes what the letter says about who
                    does what.** With an agent they arrange the signing and
                    negotiate the terms; without one, both are the vendor's —
                    so telling a private seller their agent will arrange it
                    leaves them waiting for nobody. */}
                <div>
                  <Label>Anything else <span style={{ color: T.textFaint, fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>optional</span></Label>
                  <textarea
                    ref={customRef}
                    rows={editorRows(note.customParagraph, 3)}
                    value={note.customParagraph}
                    onChange={e => set({ customParagraph: e.target.value })}
                    placeholder="A paragraph for this matter, printed under an Important heading"
                    style={box}
                  />
                </div>
                {/* **The letter's ticks share a row**, as on the release screen,
                    with what each one changes in its tooltip. Selling
                    privately is the one that changes who the letter says does
                    what: with an agent they arrange the signing; without one,
                    telling a private seller their agent will arrange it leaves
                    them waiting for nobody. */}
                <div style={{ display: "flex", flexWrap: "wrap", columnGap: 18, rowGap: 6 }}>
                  <label title="No agent, so the letter does not promise one will arrange the signing"
                    style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={note.privateSale}
                      onChange={e => set({ privateSale: e.target.checked })}
                    />
                    <span>Selling without agent</span>
                  </label>
                  <label title="Subject to the vendor providing a section 137B condition report"
                    style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={note.section137bPending}
                      onChange={e => set({ section137bPending: e.target.checked })}
                    />
                    <span>Subject to s137B report provided later</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={note.includeMaterialFactsGuide}
                      onChange={e => set({ includeMaterialFactsGuide: e.target.checked })}
                    />
                    <span>Material facts guide</span>
                  </label>
                </div>
              </div>
            )}

            {problem && (
              <div style={{ background: "rgba(196,122,26,0.06)", border: "1px solid rgba(196,122,26,0.25)", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "#c47a1a", marginBottom: 4 }}>
                  {problem.code === "attachment_too_large" ? "Too large to email" : "Not sent"}
                </div>
                <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
                  {problem.message}
                  {problem.canRequestSignature && (
                    <>
                      {" "}
                      <button
                        type="button"
                        onClick={() => { setRequestSignature(true); setProblem(null); }}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", color: "#1a7ac4", textDecoration: "underline" }}
                      >Ask them to sign instead</button>
                      {" "}— the letter then carries a link to the whole pack.
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── The letter as it will be sent ─────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.bg }}>
            <div style={{ padding: "9px 12px", borderBottom: `1px solid ${T.border}`, background: T.white, display: "flex", alignItems: "baseline", gap: 8, minHeight: 20 }}>
              <span style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textFaint, flexShrink: 0 }}>Subject</span>
              <span style={{ fontSize: 12.5, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {preview.subject || "…"}
              </span>
              {/* The letter is composed once per vendor, so a preview is of one
                  of them — saying which stops a three-vendor matter reading as
                  though only the first is being written to. */}
              {previewFor && people.length > 1 && (
                <span style={{ fontSize: 11, color: T.textFaint, flexShrink: 0, marginLeft: "auto" }}>
                  {previewFor}&rsquo;s copy
                </span>
              )}
            </div>
            <div style={{ flex: 1, minHeight: 300, position: "relative" }}>
              {preview.error
                ? <div style={{ padding: 18, fontSize: 12.5, color: T.textSoft, lineHeight: 1.5 }}>
                    The letter could not be previewed: {preview.error}
                  </div>
                : preview.url
                  ? <iframe
                      ref={frameRef}
                      src={preview.url}
                      onLoad={wireUpLetter}
                      title="Vendor email preview"
                      style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                    />
                  : <div style={{ padding: 18, fontSize: 12.5, color: T.textFaint }}>Building the letter…</div>}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: "0 24px 18px", flexWrap: "wrap" }}>
          <Btn variant="ghost" size="md" onClick={onCancel}>Cancel</Btn>
          <Btn variant="primary" size="md" onClick={attempt} disabled={busy || !reachable || anyBad}>
            {busy
              ? "Sending…"
              : reachable === 1
                ? `Send to ${shortAddress(people.find(one => looksLikeEmail(one.email)).email)}`
                : `Send to ${reachable} vendors`}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// ── Search list ────────────────────────────────────────────────────
// Adding documents staff already hold — several at once, each with its own
// name.
//
// The name is what the merge order matches on — "scan_0001.pdf" lands in the
// catch-all and prints in the wrong part of the statement — so every file
// gets a name field, prefilled from its filename as a starting point and
// still staff's to correct. The queue uploads one at a time: each document is
// its own upload, so one that fails (not a PDF, say) costs itself and nothing
// else — its row keeps the reason and stays for correction while the rest go
// through.
export const suggestDocumentName = (filename) =>
  String(filename || "").replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim();

// **What one request to the middleware can carry.** Cloud Run refuses a body
// over 32 MiB before the middleware sees it, and the middleware's own limit
// (`MAX_UPLOAD_BYTES`) leaves room for the headers inside that. A larger file
// is sent straight to the bucket instead (`sendDocument`), up to the
// middleware's direct ceiling, which it enforces itself.
export const MAX_IN_REQUEST_BYTES = 31 * 1024 * 1024;
export const sendsDirect = (file) => Number(file?.size) > MAX_IN_REQUEST_BYTES;

export const formatFileSize = (bytes) => {
  const n = Number(bytes) || 0;
  return n >= 1024 * 1024
    ? `${(n / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;
};

// A pasted Drive link worth sending: the middleware fetches it, and refuses
// one Drive will not hand over, so this only asks that it is a link.
export const looksLikeDocumentLink = (url) => /^https:\/\/\S+$/i.test(String(url || "").trim());

const AddDocument = ({ onAdd, uploading, disabled }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const choose = (chosen) => {
    const picked = Array.from(chosen || []);
    if (!picked.length) return;
    setRows(prev => [...prev, ...picked.map(file => ({
      file, name: suggestDocumentName(file.name), state: "pending", error: "",
    }))]);
  };

  // **Or a link, for a file already in Drive.** The middleware fetches it and
  // keeps its own copy, so a link Drive will not hand over (not shared, not a
  // PDF) fails here, on its row, rather than at the compile.
  const addLink = () => setRows(prev => [...prev, { file: null, link: "", name: "", state: "pending", error: "" }]);

  const reset = () => { setOpen(false); setRows([]); };
  const setRow = (i, changes) => setRows(prev => prev.map((r, j) => (j === i ? { ...r, ...changes } : r)));
  const removeRow = (i) => setRows(prev => prev.filter((_, j) => j !== i));

  const ready = rows.length > 0
    && rows.every(r => r.name.trim() && (r.file || looksLikeDocumentLink(r.link)));
  const working = busy || uploading;

  const submit = async () => {
    if (!ready || working) return;
    setBusy(true);
    const queue = rows;
    let failures = 0;
    for (let i = 0; i < queue.length; i++) {
      if (queue[i].state === "done") continue;
      setRow(i, { state: "adding", error: "", pct: 0 });
      try {
        await onAdd(queue[i].file, queue[i].name.trim(), (pct) => setRow(i, { pct }),
          queue[i].file ? undefined : queue[i].link.trim());
        setRow(i, { state: "done", pct: 100 });
      } catch (err) {
        failures += 1;
        setRow(i, { state: "failed", error: err?.message || "Upload failed" });
      }
    }
    setBusy(false);
    if (failures === 0) reset();
    // What went through leaves the list; what failed stays, with its reason,
    // to be corrected and sent again.
    else setRows(prev => prev.filter(r => r.state !== "done"));
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} disabled={disabled} data-tour="add-document"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "transparent", border: `1.5px dashed ${T.border}`, borderRadius: 8, padding: "9px 12px", cursor: disabled ? "default" : "pointer", fontSize: 12, color: T.textFaint, fontFamily: "Inter, sans-serif", width: "100%", opacity: disabled ? 0.5 : 1 }}>
        <span style={{ fontSize: 15, lineHeight: 1 }}>+</span> Add document
      </button>
    );
  }

  return (
    <div style={{ background: "rgba(26,122,196,0.04)", border: "1px solid rgba(26,122,196,0.3)", borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, color: T.text, fontWeight: 500, marginBottom: 8 }}>
        {rows.length > 1 ? "Add documents" : "Add a document"}
      </div>

      <input ref={inputRef} type="file" accept="application/pdf,.pdf" multiple style={{ display: "none" }}
        onChange={e => { choose(e.target.files); e.target.value = ""; }} />

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <button onClick={() => inputRef.current?.click()} disabled={working}
          style={{ fontSize: 12, color: T.white, background: "#1a7ac4", border: "none", borderRadius: 999, padding: "6px 14px", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
          {rows.length ? "Add more PDFs" : "Choose PDFs"}
        </button>
        <Btn variant="ghost" size="sm" onClick={addLink} disabled={working}>Use a Drive link</Btn>
        {rows.length === 0 && (
          <span style={{ fontSize: 12, color: T.textFaint }}>No files chosen — you can pick several at once</span>
        )}
      </div>

      {rows.map((r, i) => (
        <div key={`${r.file ? r.file.name : "link"}_${i}`} style={{ border: `1px solid ${r.state === "failed" ? "rgba(254,84,66,0.5)" : T.border}`, borderRadius: 8, padding: "8px 10px", marginBottom: 6, background: T.white }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            {r.file
              ? <span style={{ fontSize: 11, color: T.textFaint, flex: 1, minWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.file.name} · {formatFileSize(r.file.size)}
                </span>
              : <input
                  value={r.link}
                  onChange={e => setRow(i, { link: e.target.value })}
                  disabled={working || r.state === "done"}
                  placeholder="https://drive.google.com/file/d/…"
                  aria-label="Drive link to the PDF"
                  style={{ ...inputStyle, flex: 1, minWidth: 120 }}
                />}
            {r.state === "adding" && (
              <span style={{ fontSize: 11, color: T.textSoft, whiteSpace: "nowrap" }}>
                {!r.file ? "Fetching from Drive…" : r.pct >= 100 ? "Filing…" : `Uploading ${r.pct || 0}%`}
              </span>
            )}
            {r.state === "done" && <span style={{ fontSize: 11, color: "#2e7d32" }}>Added</span>}
            {r.state !== "adding" && r.state !== "done" && (
              <button onClick={() => removeRow(i)} disabled={working} aria-label={`Remove ${r.file ? r.file.name : "link"}`}
                style={{ background: "transparent", border: "none", color: T.textFaint, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 2 }}>×</button>
            )}
          </div>
          <input
            value={r.name}
            onChange={e => setRow(i, { name: e.target.value })}
            onKeyDown={e => { if (e.key === "Enter") submit(); if (e.key === "Escape") reset(); }}
            disabled={working || r.state === "done"}
            placeholder="Document name, as it should read in the Section 32"
            style={{ ...inputStyle }}
          />
          {r.state === "adding" && r.file && (
            /* **A bar, because the number alone does not read as movement.**
               It is the file going up, not the work finishing — the label
               beside it turns to "Filing…" at 100%, where the bytes are sent
               and the middleware is still storing them and reading the
               matter. Indeterminate where the length is not computable:
               pretending to know is worse than plainly not knowing. */
            <div style={{ height: 3, background: T.border, borderRadius: 999, overflow: "hidden", marginTop: 6 }}>
              <div style={{
                height: "100%", width: `${Math.max(2, r.pct || 0)}%`, background: "#1a7ac4",
                borderRadius: 999, transition: "width 0.15s linear",
              }} />
            </div>
          )}
          {r.state === "failed" && r.error && (
            <div style={{ fontSize: 11, color: T.red, marginTop: 4 }}>{r.error}</div>
          )}
        </div>
      ))}

      <div style={{ fontSize: 11, color: T.textSoft, lineHeight: 1.5, margin: "4px 0 10px" }}>
        The name decides where each one sits in the statement — use the certificate&rsquo;s own wording (&ldquo;Copy of Title&rdquo;, &ldquo;Copy of Plan&rdquo;). Adding does not compile.
        {rows.some(r => !r.file) && <> A Drive link has to be shared as &ldquo;Anyone with the link&rdquo; — the file is copied from Drive when you add it.</>}
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="ghost" size="sm" onClick={reset} disabled={working}>Cancel</Btn>
        <Btn size="sm" onClick={submit} disabled={working || !ready}>
          {working ? "Adding…"
            : rows.length > 1 ? `Add ${rows.length} documents` : "Add document"}
        </Btn>
      </div>
    </div>
  );
};

// The certificates, in the order the compile merges them.
//
// **Read-only except for the status.** It used to offer drag-to-reorder, Add
// search and Replace PDF, and none of the three did anything: the drag
// reordered local state the next poll overwrote, Add search raised an alert,
// and Replace swapped in a blob URL that was gone on reload. Three affordances
// that looked like they worked, on the screen that decides what goes into a
// legal document — the worst place to be able to believe you had done
// something you hadn't.
//
// Reordering persists to the compile order (`onReorder`), the same store the
// compile workspace writes.
//
// Marking a search Not Required or Ordered *does* persist, through
// `POST /documents/:ref/:id/status`, and is how a matter gets completed by
// hand. That stays.
// Which status changes a search row offers.
//
// **Deliberately not a way to un-return a search.** Moving one back to
// `Ordered` was tried and reads wrong: the entry *did* come back, and when the
// problem is that the wrong PDF is against it, the operation staff actually
// want is to swap the file. That is Replace, below.
//
// So this stays what it always was — a search nobody is waiting on any more,
// and the way back from having said so.
//
// **The way back is named for the document, not for the status.** A settled
// row that still carries its file is not a search waiting to be ordered: the
// certificate is here, and *Mark as ordered* both reads wrong and says the
// wrong thing to the middleware. 1760533 is the case — staff un-settled an
// instrument precisely to get it back into the pack, and `Ordered` merges
// nothing and un-completes the matter, so the press took the document further
// out. The middleware settles what the status actually becomes
// (`buildSearchUpdate` answers `Returned` for a `Not Required` row carrying a
// file, whatever is posted), so this only has to stop promising otherwise;
// posting `Returned` from here is the two agreeing rather than a second rule.
//
// **A returned row with its document is not offered it at all.** *Remove*
// takes a document out of the Section 32 and *Add back* returns it, keeping the
// search received; two ways out of the pack on one row, one of which also
// changed whether it counts as back, was a distinction nobody could see from
// the menu. *Mark as not required* is for a search that is not back — stop
// waiting for it.
export const statusActionsFor = (search = {}) => {
  if (search.status === "Returned" && hasFile(search)) return [];
  if (search.status !== "Not Required") {
    return [{ label: "Mark as not required", status: "Not Required" }];
  }
  return hasFile(search)
    ? [{ label: "Mark as returned", status: "Returned" }]
    : [{ label: "Mark as ordered", status: "Ordered" }];
};

// A document already here, whose file can be swapped for the right one.
//
// Returned covers both cases the firm asked for: a certificate Zap 2 filed, and
// one staff uploaded (an upload is written `Returned`). A search still
// outstanding has nothing to replace — offering it there would make Replace a
// second way to record a return, which is exactly what `POST /documents` is
// carefully not called "add search" to avoid.
export const canReplaceFile = (search = {}) =>
  search.status === "Returned" && hasFile(search);

// A search still waiting on its document can be given one.
//
// **This is how a large certificate arrives.** Too big for the mailbox means
// Zap 2 never sees it, so nothing files it in Drive and nothing records it
// against the search it answers. Without this the only way in was Add
// document, which mints a second row and leaves the ordered one outstanding
// for ever — the duplication this screen keeps paying for.
//
// Not offered on a settled row: `Returned` already has a document (that is
// Replace), and `Not Required` is a search nobody is waiting on.
export const canAttachFile = (search = {}) =>
  search.status !== "Returned" && search.status !== "Not Required" && !hasFile(search);

// A PDF looked at in place, rather than downloaded and opened in a system
// viewer. Our own streamed documents embed directly; a Drive link embeds
// through Drive's /preview; a link nothing can embed opens in a tab from the
// same button, so View PDF never silently does less than it used to.
// What stands in for the frame where a browser will not paginate one.
//
// **One component, because the two places that embed a PDF must not say
// different things** — the panel's frame and the expanded modal are the same
// document, and a phone that got an honest answer from one and a broken frame
// from the other would read as the portal being unreliable rather than as the
// browser having a limit. Same reasoning as `displayName` being computed once
// on the middleware.
//
// It says what is actually true and offers the two things worth doing. Open
// hands the file to the device's viewer, which is the best way to read a
// contract on a phone anyway; Download saves it.
const PdfInDeviceViewer = ({ url }) => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 24, textAlign: "center" }}>
    <svg width="28" height="28" fill="none" stroke={T.textFaint} strokeWidth="1.6" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
    </svg>
    <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>
      This document opens in your phone&rsquo;s PDF viewer
    </div>
    <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.55, maxWidth: 340 }}>
      A phone browser shows only the first page of a document embedded in a
      page. Opening it gives you the whole thing, with search and zoom.
    </div>
    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
      <Btn size="md" onClick={() => window.open(url, "_blank")}>Open the document</Btn>
      <Btn size="md" variant="ghost" onClick={() => saveAs(downloadUrl(url))}>Download</Btn>
    </div>
  </div>
);

const PdfPreviewModal = ({ title, url, embed, view = "", onClose, wide = false, actions = [], embedPaginates = false, toolbar = null, panel = null }) => {
  // The portal has no router, so with the preview open the browser's Back
  // button had nothing in-app to return to — it left the portal entirely.
  // Opening pushes one history entry; Back pops it and closes the preview, and
  // closing any other way consumes the entry, so history ends where it began.
  const coarse = useCoarsePointer();
  // **The fallback is about a framed PDF, not about a phone.** `needsDeviceViewer`
  // answers "would embedding this show one page and stop", which is true of a
  // PDF on WebKit and false of a page that draws the PDF itself. A caller that
  // hands over such a page says so, or the modal would send somebody out to
  // their phone's viewer to read a document it can perfectly well show.
  // A document with a drawn view (`previewUrlFor`) frames that on a touch
  // screen instead of being sent out to the device's viewer.
  const drawn = coarse && view;
  const onPhone = needsDeviceViewer(url, coarse) && !embedPaginates && !drawn;
  const frameSrc = drawn ? view : embed;
  const paginates = embedPaginates || Boolean(drawn);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    let poppedClosed = false;
    window.history.pushState({ pdfPreview: true }, "");
    const onPop = () => { poppedClosed = true; closeRef.current(); };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (!poppedClosed) window.history.back();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="s32-sheet" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(54,47,42,0.55)", zIndex: 320, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      {/* **`maxHeight` is what keeps the header on the screen.** The card was
          sized at 88 or 94 `vh` inside a padded overlay, which already asks for
          more than the viewport — and on a phone `vh` is the viewport with the
          browser's toolbars *hidden*, so the card stood taller than anything
          visible and pushed its own header off the top. That header holds
          Close, so the effect was a full-screen document with no way out of it
          but the browser's Back button. The class carries what an inline style
          cannot: `dvh`, which is the height actually on screen, and the
          full-bleed sheet a phone should get. */}
      <div className="s32-sheet-card" onClick={e => e.stopPropagation()} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, width: "100%", maxWidth: wide ? 1180 : 860, height: wide ? "94vh" : "88vh", maxHeight: "100%", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 12px 48px rgba(0,0,0,0.2)" }}>
        {/* **It wraps, because the row overflowed and Close was the last thing
            in it.** Title, the Next actions, Open and Close is five or six
            items; a button will not shrink below its own label, so on a narrow
            screen the row ran off the right-hand edge and took Close with it.
            Wrapping costs nothing on a desktop, where it all fits on one line
            anyway, and guarantees nothing is pushed out of reach. */}
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text, flex: "1 1 140px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
          {/* **It said Download and opened a tab.** `window.open` on a route
              serving `inline` is what Open does, so the label was a small lie
              on a button people press when they want the file. A Drive-filed
              row keeps opening in Drive — that URL is not ours to add a
              parameter to — so it is labelled for what it actually does. */}
          {/* **What to do about what you are reading, beside the document
              rather than behind it.** A fault in a contract is found here and
              fixed somewhere else, and until these existed the way back was to
              close the modal and go looking. Each one closes the modal first —
              they all end it anyway, and a confirm dialog stacked on top of a
              full-screen document reads as two things having gone wrong. */}
          {actions.map(a => (
            <Btn key={a.label} variant="ghost" size="sm" disabled={a.disabled}
              onClick={() => { onClose(); a.onClick(); }}>{a.label}</Btn>
          ))}
          {/* **A control for the document rather than a way out of it.**
              Everything in `actions` closes the modal — they all end it anyway
              — and this does not: it reveals a panel over the same document,
              so closing would be the opposite of what it is for. */}
          {toolbar}
          {downloadUrl(url) === url
            ? <Btn variant="ghost" size="sm" onClick={() => window.open(url, "_blank")}>Open</Btn>
            : <Btn variant="ghost" size="sm" onClick={() => saveAs(downloadUrl(url))}>Download</Btn>}
          <Btn variant="ghost" size="sm" onClick={onClose}>Close</Btn>
        </div>
        {/* Between the header and the document, which is where the panel's own
            copy sits — the two views are the same thing in the same order. */}
        {panel && (
          <div style={{ padding: "0 14px 12px", borderBottom: `1px solid ${T.border}`, overflowY: "auto", maxHeight: "45%", flex: "0 0 auto" }}>
            {panel}
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, background: T.bgAlt }}>
          {onPhone ? (
            <PdfInDeviceViewer url={url} />
          ) : frameSrc ? (
            <iframe key={frameSrc} title={title} src={frameSrc}
              {...(paginates ? { "data-s32-view": true } : {})}
              style={{ width: "100%", height: "100%", border: 0 }} />
          ) : (
            <div style={{ padding: 24, fontSize: 13, color: T.textSoft, lineHeight: 1.6 }}>
              This document&rsquo;s link can&rsquo;t be shown in place — use Download to open it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// A search the matter header counts as not back: anything not yet settled.
// One test for the count, the strip and the rows View lands on.
export const isNotBack = (s) => s?.status !== "Returned" && s?.status !== "Not Required";

const SearchList = ({ searches, setSearches, onPersistStatus, matterRef, onDelete, onReorder, onReplace, onRename, onExclude, compileOrder = [], compileExcluded = [], flashing = false }) => {
  // **What Arrange documents did that this list did not**: leave a document
  // out of the pack and put it back, and say which rows came back after the
  // order was saved. The same `/compile-order` store, so the two screens agree.
  const leftOut = new Set(compileExcluded);
  const placed = new Set(compileOrder);
  // The document being looked at, or null. View PDF opens this rather than
  // handing the browser a download.
  const [preview, setPreview] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const dragItem = useRef(null);
  const dragOver = useRef(null);
  const replaceInputRef = useRef(null);
  const [replacing, setReplacing] = useState(null);
  // The row being given a document by link, rather than by file.
  const [linking, setLinking] = useState(null);
  // The row being renamed.
  const [renaming, setRenaming] = useState(null);
  // **The row being given a file, and how far it has got.** There was nothing
  // at all here: picking a file fired the request and the screen went on
  // showing `Ordered` with no date until the toast arrived, which on an 18MB
  // certificate is most of a minute of silence on the press that files it.
  // Held per row rather than as one flag, so the progress is on the document
  // it belongs to.
  const [upload, setUpload] = useState(null);

  const updateStatus = (id, status) => {
    setSearches(searches.map(s => s.id === id ? { ...s, status } : s));
    onPersistStatus?.(id, status);
  };

  // **Reordering here saves, which is the whole reason it is back.** This list
  // offered drag once and it did nothing: the rows moved, the next poll put
  // them back, and the compiled PDF came out in the old order. It goes through
  // `/compile-order` now, the same store Arrange documents writes — so the two
  // screens cannot disagree, and there is no Save button to forget.
  const reorder = (next) => {
    if (next === searches || !onReorder) return;
    onReorder(next.map(x => x.id));
  };
  const move = (i, by) => reorder(moveInList(searches, i, by));

  const handleDragEnd = () => {
    const from = dragItem.current;
    const to = dragOver.current;
    dragItem.current = null; dragOver.current = null;
    if (from === null || to === null || from === to) return;
    const arr = [...searches];
    arr.splice(to, 0, arr.splice(from, 1)[0]);
    reorder(arr);
  };

  const chooseReplacement = (search) => {
    setReplacing(search);
    setTimeout(() => replaceInputRef.current?.click(), 0);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {searches.map((s, i) => {
        const isUpload = Boolean(s.isUpload);
        const cfg = isUpload ? UPLOAD_CFG : (SEARCH_STATUS_CFG[s.status] || SEARCH_STATUS_CFG["Ordered"]);
        const isHovered = hoveredId === s.id;
        const pdfUrl = documentUrl(matterRef, s);
        // An upload is not an order, so it gets neither of the status actions:
        // marking one "not required" would leave a file staff deliberately put
        // in the matter sitting there doing nothing, when what they mean is
        // remove it. Deleting is offered instead, and only here — an ordered
        // search cannot be deleted at all, which the middleware enforces.
        // Replace is offered on anything already here — a certificate Zap 2
        // filed and a document staff uploaded alike. It is the answer to "the
        // right entry has the wrong PDF against it", which used to have none.
        const replaceAction = onReplace && canReplaceFile(s)
          ? [{ label: "Replace PDF", onClick: () => chooseReplacement(s) }]
          : [];
        // The same route, and a different sentence: nothing is being replaced,
        // the document is arriving. A link is offered beside the file because
        // the reason this is needed at all is a file too large to email — and
        // one too large to email is one you would rather not push through a
        // browser either.
        const attachActions = onReplace && canAttachFile(s)
          ? [
              { label: "Attach the document", onClick: () => chooseReplacement(s) },
              { label: "Attach by link…", onClick: () => setLinking(s) },
            ]
          : [];
        // **Offered on every row, an outstanding search included.** Renaming
        // changes only what is displayed — the stored name still places the
        // document in the merge order and is what the nightly import pairs on
        // — so unlike deleting there is nothing an ordered search could
        // misstate by being renamed.
        const renameAction = onRename
          ? [{ label: "Rename…", onClick: () => setRenaming(s) }]
          : [];
        // **Remove takes a document out of the Section 32 and Add back returns
        // it** — the compile order's exclusion, which keeps the file and keeps
        // the search received. Offered on any row the compile would merge,
        // certificates and uploads alike, and on one staff took out.
        const isLeftOut = leftOut.has(s.id);
        const excludeAction = onExclude && (isLeftOut || isCompilable(s))
          ? [{ label: isLeftOut ? "Add back" : "Remove", onClick: () => onExclude(s, !isLeftOut) }]
          : [];
        // **Deleting is for an upload already removed**, so it is never the
        // one word next to Remove: take it out first, and delete it from
        // there if it should not be on the matter at all. Without the remove
        // step (no `onExclude`), Delete stays where it was.
        const deleteAction = isUpload && (isLeftOut || !onExclude)
          ? ["divider", { label: onExclude ? "Delete permanently" : "Delete document", danger: true, onClick: () => onDelete?.(s) }]
          : [];
        const actions = isUpload
          ? [
              ...(pdfUrl ? [{ label: "View PDF", onClick: () => setPreview({ title: s.displayName || s.name, ...previewUrlFor(matterRef, s) }) }] : []),
              ...replaceAction,
              ...renameAction,
              ...excludeAction,
              ...deleteAction,
            ]
          : [
              // **Having a file is not the same question as being outstanding.**
              // This was gated on `Returned`, so marking a search Not Required
              // hid the document staff had already received — and the upload
              // branch above, which gates on the file alone, kept its preview.
              // Two rows with a PDF each, one viewable.
              //
              // It matters most where the status was set *for* us: the
              // transfer-of-land detection marks a document Not Required by
              // itself, and hiding the PDF makes that decision unreviewable —
              // the one document somebody needs to open is the one it took out.
              ...(pdfUrl ? [{ label: "View PDF", onClick: () => setPreview({ title: s.displayName || s.name, ...previewUrlFor(matterRef, s) }) }] : []),
              ...replaceAction,
              ...attachActions,
              ...renameAction,
              ...excludeAction,
              // The divider separates *what you can do with the document*
              // from *what the row's status is*, so it follows the same test
              // the entries above it do. Left on `Returned` it disappeared on
              // exactly the row that now has both halves.
              ...(pdfUrl && statusActionsFor(s).length ? ["divider"] : []),
              ...statusActionsFor(s).map(a => ({ label: a.label, onClick: () => updateStatus(s.id, a.status) })),
            ];

        // The rows the matter header counts as "not back" — the same test, so
        // its View lands on exactly what it named.
        const notBack = isNotBack(s);
        // **A row with a document opens it** (DA-03). View PDF was the
        // commonest press on this list and it sat in a bar that appeared only
        // under a mouse — so on a touchscreen it did not exist, and from the
        // keyboard neither did anything else. The rest is behind ⋯, which is
        // always there.
        const openDoc = pdfUrl
          ? () => setPreview({ title: s.displayName || s.name, ...previewUrlFor(matterRef, s) })
          : null;
        return (
          <div key={s.id}
            {...(notBack ? { "data-not-back": "" } : {})}
            data-search-row
            onClick={openDoc || undefined}
            tabIndex={openDoc ? 0 : undefined}
            aria-label={openDoc ? `Open ${s.displayName || s.name}` : undefined}
            onKeyDown={openDoc ? (e => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDoc(); }
            }) : undefined}
            draggable={Boolean(onReorder)}
            onDragStart={() => { dragItem.current = i; }}
            onDragEnter={() => { dragOver.current = i; }}
            onDragEnd={handleDragEnd}
            onDragOver={e => e.preventDefault()}
            onMouseEnter={() => setHoveredId(s.id)}
            onMouseLeave={() => setHoveredId(null)}
            style={{ display: "flex", alignItems: "center", gap: 10, background: isHovered ? T.bgAlt : T.white, border: `1px solid ${flashing && notBack ? ACCENT : isHovered ? T.borderMid : T.border}`, borderRadius: 8, padding: "9px 12px", transition: "all 0.12s, box-shadow 0.4s", boxShadow: flashing && notBack ? `0 0 0 3px ${ACCENT}40` : "none", position: "relative", cursor: openDoc ? "pointer" : onReorder ? "grab" : "default" }}>
            {onReorder && (
              /* draggable={false} so a press on the arrow is a click and not a
                 drag of the row underneath it. */
              <div className={`s32-move${isHovered ? " s32-move-on" : ""}`} style={MOVE_COLUMN}>
                <button draggable={false} aria-label={`Move ${s.name} up`} disabled={i === 0}
                  onClick={e => { e.stopPropagation(); move(i, -1); }} style={moveBtnStyle(i === 0)}>▲</button>
                <button draggable={false} aria-label={`Move ${s.name} down`} disabled={i === searches.length - 1}
                  onClick={e => { e.stopPropagation(); move(i, 1); }} style={moveBtnStyle(i === searches.length - 1)}>▼</button>
              </div>
            )}
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
            {/* **`minWidth: 0` is what lets this shrink at all.** A flex item
                defaults to `min-width: auto`, so it refuses to go narrower than
                its content — the name kept its full width, the row could not
                give the actions any, and a long one ran underneath them. */}
            <span style={{ fontSize: 12, color: T.text, flex: 1, minWidth: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
              {/* An upload carries a mark as well as its own pill: the pill is
                  at the far right and the eye reads the name column first. */}
              {isUpload && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={UPLOAD_CFG.dot} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              )}
              {/* The ellipsis goes on the text itself, not on the row: the
                  span around it is a flex container, and `text-overflow` does
                  nothing on one of those. `title` so a truncated name can
                  still be read. */}
              <span title={s.displayName || s.name}
                style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.displayName || s.name}
              </span>
              {/* The read runs for minutes with no request in flight, so the
                  row is the only place the work can show. `readState` is the
                  middleware's word for it — computed off the claim and the
                  answer, never narrated from a button press. */}
              {s.readState === "reading" && <span style={{ fontSize: 10, color: ACCENT, marginLeft: 6, fontWeight: 500 }}>being read…</span>}
              {s.readState === "failed" && <span style={{ fontSize: 10, color: "#c47a1a", marginLeft: 6, fontWeight: 500 }}>read failed</span>}
              {s.isReplaced && <span style={{ fontSize: 10, color: "#2a8a52", marginLeft: 6, fontWeight: 500 }}>replaced</span>}
              {/* An upload says it on its pill; a return that came by email
                  keeps *Returned* there, so it is said here. */}
              {s.emailedIn && !isUpload && <span title={emailedNote(s)} style={{ fontSize: 10, color: UPLOAD_CFG.color, marginLeft: 6, fontWeight: 500 }}>emailed</span>}
              {/* Returned but excluded merges nothing, so say it on the list
                  people actually read rather than only on Arrange documents. */}
              {s.excludedFromCompile && <span style={{ fontSize: 10, color: T.textFaint, marginLeft: 6 }}>not merged</span>}
              {isLeftOut && !s.excludedFromCompile && <span title="Removed from the files — Add back from ⋯" style={{ fontSize: 10, color: T.textFaint, marginLeft: 6 }}>removed</span>}
              {/* Returned since the order was saved: appended to the merge,
                  and flagged so somebody places it (a move here places it). */}
              {onExclude && compileOrder.length > 0 && !placed.has(s.id) && !isLeftOut && isCompilable(s) && <span title="Came back after the order was saved — it is added at the end; move it to place it" style={{ fontSize: 10, color: "#c47a1a", marginLeft: 6, fontWeight: 500 }}>new</span>}
              {/* An ordered row the compile is not waiting for. The matter can
                  read complete beside it, so the row is the only place that
                  can say why — and the *reason* is the middleware's, sent on
                  the row, because the completion applies that rule and a copy
                  of it here would drift into a badge on a matter that is
                  waiting after all. Sent only on a row that has not settled,
                  so this needs no status test of its own. */}
              {s.notWaitedFor && <span title={s.notWaitedFor} style={{ fontSize: 10, color: T.textFaint, marginLeft: 6 }}>not waiting</span>}
            </span>
            {/* **The actions take the date and the status's place rather than
                floating over it.** They were absolutely positioned, so they
                overlaid whatever was under them — and the two they cover fade
                but keep their width, so anything wider than those 178px landed
                on top of the name. Swapped in and out, the row is one flex
                line whose right-hand side is either the date and the status or
                the actions, and the name shrinks to whatever is left. */}
            {upload?.id === s.id ? (
              /* **The one thing worth seeing is what this row is doing**, so
                 it takes the date and the status's place rather than the
                 actions', and hover cannot pull it away mid-file. 100% is
                 *sent*: the bytes are up and the middleware is still storing
                 them, writing the row and re-reading the matter. */
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span style={{ width: 88, height: 3, background: T.border, borderRadius: 999, overflow: "hidden" }}>
                  <span style={{ display: "block", height: "100%", width: `${Math.max(2, upload.pct || 0)}%`, background: "#1a7ac4", borderRadius: 999, transition: "width 0.15s linear" }} />
                </span>
                <span style={{ fontSize: 10, fontWeight: 500, color: T.textSoft, width: 90, textAlign: "center", whiteSpace: "nowrap" }}>
                  {upload.pct >= 100 ? "Filing…" : `Uploading ${upload.pct || 0}%`}
                </span>
              </span>
            ) : (
              <>
                <span style={{ fontSize: 11, color: T.textFaint, flexShrink: 0, width: 88, textAlign: "right" }}>{s.date || ""}</span>
                <span title={isUpload ? emailedNote(s) || undefined : undefined} style={{ fontSize: 10, fontWeight: 500, color: cfg.color, padding: "2px 8px", borderRadius: 999, flexShrink: 0, border: `1px solid ${cfg.border}`, width: 90, textAlign: "center" }}>
                  {isUpload ? (s.emailedIn ? "Emailed" : UPLOAD_CFG.label) : s.status}
                </span>
              </>
            )}
            {/* Always there, so the date and the status no longer give way to
                a bar under the mouse. A leading divider would be a rule above
                nothing. */}
            <OverflowMenu
              items={actions[0] === "divider" ? actions.slice(1) : actions}
              label={`Actions for ${s.displayName || s.name}`}
              size={26}
              tour={false}
            />
          </div>
        );
      })}

      {/* One input for the whole list: which row it belongs to is `replacing`,
          set when the action was picked. */}
      <input ref={replaceInputRef} type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
        onChange={e => {
          const file = e.target.files?.[0];
          e.target.value = "";
          const target = replacing;
          setReplacing(null);
          if (!file || !target) return;
          setUpload({ id: target.id, pct: 0 });
          Promise.resolve(onReplace?.(target, file, null, (pct) => setUpload({ id: target.id, pct })))
            // The handler reports its own failure with a toast, so there is
            // nothing to say here — only the bar to take down, and it has to
            // come down whichever way the request ended.
            .catch(() => {})
            .finally(() => setUpload(null));
        }} />
      {linking && (
        <AttachByLink
          search={linking}
          onCancel={() => setLinking(null)}
          onAttach={(url) => { const target = linking; setLinking(null); onReplace?.(target, null, url); }}
        />
      )}
      {renaming && (
        <RenameDocument
          supplied={renaming.name}
          custom={renaming.customName || ""}
          onCancel={() => setRenaming(null)}
          onRename={(name) => { const target = renaming; setRenaming(null); onRename?.(target, name); }}
        />
      )}
      {preview && <PdfPreviewModal {...preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

// Giving a search the document by naming where it already is.
//
// **The size is why this exists.** A certificate too large for the mailbox
// never reaches Zap 2 — that is the whole reason it has to be placed by hand —
// and a file too large to email is one you would rather not push through a
// browser either. It is already in Drive; a link moves no bytes and has no
// ceiling.
const AttachByLink = ({ search, onCancel, onAttach }) => {
  const [url, setUrl] = useState("");
  const ok = /^https:\/\//i.test(url.trim());

  return (
    <div onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(54,47,42,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, width: "100%", maxWidth: 480 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 4 }}>
          Attach a document by link
        </div>
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 12, lineHeight: 1.5 }}>
          {search.displayName || search.name} — paste the Drive link to the PDF. The
          file is copied from Drive when you attach it, so it has to be shared as
          &ldquo;Anyone with the link&rdquo;.
        </div>
        <input
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && ok) onAttach(url.trim()); if (e.key === "Escape") onCancel(); }}
          placeholder="https://drive.google.com/file/d/…"
          autoFocus
          style={inputStyle}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <Btn variant="ghost" size="sm" onClick={onCancel}>Cancel</Btn>
          <Btn size="sm" onClick={() => onAttach(url.trim())} disabled={!ok}>Attach</Btn>
        </div>
      </div>
    </div>
  );
};

// Calling a document what it is.
//
// **The supplier's wording is kept, not overwritten.** Trisearch names every
// dealing "Instrument Search" and every OC report the same thing, so a matter
// can carry seven identical rows — and that name is what section 13 tells a
// purchaser is annexed. The middleware stores the typed name beside the
// stored one, which still places the document in the merge order and is what
// the nightly import pairs on, so renaming can only ever change what is
// displayed.
//
// That is also what makes it reversible: an empty box clears the correction
// and the supplier's wording comes straight back, which is why this offers a
// plain Save rather than asking anybody to confirm.
export const RenameDocument = ({ supplied = "", custom = "", onCancel, onRename }) => {
  const suppliedName = supplied.trim();
  // What the box opened holding — the typed name where there is one, and the
  // supplier's own wording otherwise. Compared against *that* rather than
  // against the stored correction, or pressing Save on an untouched box would
  // store the supplier's wording as a correction: a rename that renames
  // nothing, and one more row carrying a name it does not need.
  const opened = (custom || suppliedName).trim();
  const [name, setName] = useState(opened);
  const value = name.trim();
  const changed = value !== opened;

  return (
    <div onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(54,47,42,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, width: "100%", maxWidth: 480 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 4 }}>
          Rename this document
        </div>
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 12, lineHeight: 1.5 }}>
          Shown on screen and in the statement’s attachment list. Clear the box to put the supplier’s wording back.
        </div>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") onRename(value); if (e.key === "Escape") onCancel(); }}
          placeholder={suppliedName}
          maxLength={200}
          autoFocus
          aria-label="Document name"
          style={inputStyle}
        />
        {/* The supplier's own wording, so somebody renaming a row can see
            what they are replacing and what clearing the box brings back. */}
        {suppliedName && (
          <div style={{ fontSize: 11, color: T.textFaint, marginTop: 6 }}>
            Supplier’s name: {suppliedName}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <Btn variant="ghost" size="sm" onClick={onCancel}>Cancel</Btn>
          <Btn size="sm" onClick={() => onRename(value)} disabled={!changed}>
            {value ? "Save" : "Use the supplier’s name"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// Does this row have a file the merge can actually use?
//
// **Computed by the middleware** (`hasFile`), like `excludedFromCompile` and
// `compileOrderIndex`. This screen used to test `driveFileUrl`, which a
// document staff uploaded does not have — its file is in our bucket — so every
// upload merged into the PDF while being invisible on the screen that arranges
// the PDF. Falling back to the Drive link keeps a matter loaded before the
// middleware served the field from behaving worse than it did.
// The fallback covers `storagePath` too. An upload is exactly the row that has
// no Drive link, so a fallback testing only that would answer "no file" for the
// one kind of row this helper exists for.
export const hasFile = (s) =>
  s?.hasFile !== undefined
    ? Boolean(s.hasFile)
    : Boolean((s?.driveFileUrl || "").trim() || (s?.storagePath || "").trim());

// **What a row says about itself**, as one string, so the matter page can tell
// a reload that changed something from one that changed nothing.
//
// `loadMatter` builds a new array every time, so comparing identity would
// re-seed on every reload — including the ones that landed while a drag's save
// was still in flight. **The order is deliberately not in it**: display order
// comes from `matter.compileOrder`, so the array's own order says nothing, and
// the per-row entries are sorted so a reorder alone cannot read as a change.
//
// What is in it is everything the row renders and everything the screen was
// found to be missing — the status, the name as displayed, whether a document
// is attached, and the read the middleware claims is running.
export const searchesSignature = (searches = []) => (searches || [])
  .map(s => [s.id, s.status, s.displayName || s.name, hasFile(s) ? 1 : 0, s.readState || ""].join("\u0000"))
  .sort()
  .join("\u0001");

// **What the *document* is built from**, as one string — the merge list, which
// is what section 13 names and what section 2.2's "Refer to the attached"
// checks before it prints.
//
// Its own signature rather than `searchesSignature`, because the two answer
// different questions and one of them would be wrong here in both directions.
// `readState` is in that one and must not be in this: it changes every few
// seconds while the certificates are being read, and rebuilding the preview on
// it is exactly the churn `reloadKey` was serialised to stop — the document
// greying and swapping under whoever is reading it. And the compile
// *arrangement* is in this one and not in that: a drag on Arrange documents
// changes nothing a row says about itself and changes the order section 13
// prints them in.
//
// Order-blind for the same reason as its neighbour — display order comes from
// `matter.compileOrder`, which arrives as `compileOrderIndex` on each row, so
// a reorder registers as a changed entry rather than a changed array.
export const mergeListSignature = (searches = []) => (searches || [])
  .map(s => [
    s.id,
    s.status,
    s.displayName || s.name,
    hasFile(s) ? 1 : 0,
    s.excludedFromCompile ? 1 : 0,
    s.compileOrderIndex === undefined || s.compileOrderIndex === null ? "" : s.compileOrderIndex,
  ].join("\u0000"))
  .sort()
  .join("\u0001");

// A document the compile will actually merge: returned, with a file, and not
// one of the instruments the middleware drops by name.
//
// **One rule, three screens.** The matter screen counts these to decide whether
// Compile is worth offering, Arrange documents lists them, and saving that
// arrangement works out what was removed by subtracting the kept ones from
// them. All three had their own copy, spelled three different ways — and the
// one behind Save order tested `driveFileUrl`, so an upload removed on that
// screen never reached the excluded list. The middleware then did the right
// thing with a document it did not recognise in the saved order and appended
// it, so the file staff had just removed came back at the end of the Section 32
// with nothing on screen saying so.
//
// Fixing that one predicate would have left two more copies to drift. What
// differs between the three is only which set they subtract, so that is all
// they are allowed to differ by.
export const isCompilable = (s) =>
  s?.status === "Returned" && hasFile(s) && !s.excludedFromCompile;

// What a matter needs a person to look at, when the contract has already gone.
//
// **A released matter is never re-compiled automatically.** Its contract is in
// the agent's hands, so a new version built behind them makes the copy they
// hold quietly out of date, and whether to reissue is a decision about a
// document somebody has already sent. The middleware flags it and stops; this
// is the flag reaching a screen, which it never did — `needsRecompileReason`
// has been on the matter all along and nothing rendered it, so the only trace
// was a line in the History panel.
export const rebuildNotice = (matter = {}) => {
  const reason = (matter.needsRecompileReason || "").trim();
  if (!reason) return null;
  return {
    reason,
    // Released is the case that needs saying out loud. On any other matter the
    // hourly sweep rebuilds it by itself and the notice is transient.
    released: matter.status === "released",
    releasedTo: matter.releasedTo || "",
    // A matter flagged before it ever compiled — a completion the middleware
    // deferred while the import was still delivering rows. "Since this
    // version was built" would claim a version that does not exist.
    hasVersion: Boolean(matter.compileVersion),
  };
};

// **And a flag that says nothing is not shown at all.**
//
// The no-version wording — "everything is here, the contract will compile
// shortly" — is true of the case it was written for: a completion the
// middleware deferred while the import was still delivering rows. It is a
// plain falsehood on a matter that is nineteen searches of twenty-nine, which
// is what a flag raised before the middleware learned to withhold one leaves
// behind. Those clear themselves on the next hourly sweep; until they do,
// there is nothing useful to say, so nothing is said.
//
// The counts are the matter's own — the same pair the dashboard reads — and
// a released or compiled matter is unaffected, because it has a version.
export const rebuildNoticeWorthShowing = (matter = {}) => {
  const notice = rebuildNotice(matter);
  if (!notice) return null;
  // A contract exists, so the flag is about that contract and always means
  // something. `compiled` and `released` say one exists even where the version
  // number has not reached this screen.
  if (notice.hasVersion || matter.status === "compiled" || matter.status === "released") {
    return notice;
  }
  const total = Number(matter.searchCount) || 0;
  const done = Number(matter.returnedCount) || 0;
  return total > 0 && done >= total ? notice : null;
};

// Where to open a document from. A returned certificate lives in Drive; one
// staff added lives in our bucket and is streamed back through the middleware,
// which is why this needs the matter reference and the access token.
// A Drive link staff can *look at*. Zap 2 files returns in Drive, and the
// stored link is often the export/download form, which an iframe cannot show.
// Drive's own /preview endpoint is embeddable, and every common Drive URL
// shape carries the file id to build it from. Anything unrecognised returns
// null and the modal falls back to opening the original link — a preview that
// cannot be built must not become a broken grey box.
// The URL carries which matter is open — "#/matter/1760404" — which is what
// makes the browser's Back button return from a matter to the dashboard, an
// open matter survive a refresh, and a matter row a real destination a ctrl-
// or middle-click can open in its own tab. The hash rather than a path,
// because Hosting rewrites paths (/released/** already goes to the
// middleware) and the staff token rides the query string — a hash composes
// with both and asks nothing of the server.
export const matterHash = (ref) => `#/matter/${encodeURIComponent(ref)}`;

// Where a matter row actually points.
//
// **`matterHash` is for routing *this* document; this is for opening another
// one.** In-page navigation still sets the hash — nothing about that changes —
// but a ctrl-click, a middle-click and *Open in a new tab* all start a fresh
// browsing context, and that context has no token unless it goes through the
// embedder. See `readEmbed`.
//
// The embedder's own query is kept and `matter` set on top of it, so whatever
// a staff member arrived with — which is how WordPress decides whose token to
// inject — survives into the new tab. Unembedded, or with an `embed` that did
// not validate, this is exactly the link it always was.
export const matterHref = (ref, embed = EMBED_URL) => {
  if (!embed) return matterHash(ref);
  try {
    const url = new URL(embed);
    url.searchParams.set("matter", ref);
    return url.toString();
  } catch (e) { return matterHash(ref); }
};
// A tab opened through the embedder arrives with `?matter=1760519` rather than
// a hash: WordPress forwards the parameter it was handed, and asking it to
// build a fragment as well is a second thing to get wrong in a template nobody
// here can test. This turns that into the hash the router already understands,
// so there is one route source rather than two.
export const refFromQuery = (search) => {
  try {
    const ref = (new URLSearchParams(search || "").get("matter") || "").trim();
    return ref || null;
  } catch (e) { return null; }
};

export const refFromHash = (hash) => {
  const m = String(hash || "").match(/^#\/matter\/([^/]+)$/);
  return m ? decodeURIComponent(m[1]) : null;
};

export const drivePreviewUrl = (url) => {
  const said = String(url || "");
  if (!/drive\.google\.com|docs\.google\.com/.test(said)) return null;
  const m = said.match(/\/file\/d\/([\w-]+)/) || said.match(/[?&]id=([\w-]+)/);
  return m ? `https://drive.google.com/file/d/${m[1]}/preview` : null;
};

// What the preview modal shows for a row. Everything with a file embeds
// through the middleware's /documents route, which serves the PDF inline
// whether it holds the bytes itself or fetches them from the row's Drive
// link. Embedding Drive's own /preview was tried first and is not dependable:
// it wants the viewer signed into an account with access, and a browser that
// blocks third-party cookies — most, now — shows a blank frame even then.
// Download keeps the original URL, so a Drive-filed row still opens in Drive.
//
// **`view` is the same document drawn page by page** (`GET /document-view`,
// the compiled contract's viewer). A framed PDF shows its first page and stops
// on an iPhone or iPad, so on a touch screen that is what gets framed — which
// is what lets an upload be read in place there rather than only in the
// device's own viewer. `documentPreview` picks between the two.
export const previewUrlFor = (ref, s) => {
  const url = documentUrl(ref, s);
  if (!url) return { url: "", embed: "" };
  if (ref && s?.id && hasFile(s)) {
    const at = `${encodeURIComponent(ref)}/${encodeURIComponent(s.id)}`;
    return { url, embed: apiUrl(`/documents/${at}`), view: apiUrl(`/document-view/${at}`) };
  }
  return { url, embed: drivePreviewUrl(url) || "" };
};

// Which of the two to frame. The drawn view on a touch screen; the PDF itself
// elsewhere, where the browser's own viewer is better than ours. Answers
// `{ src, paginates }`, or a blank `src` where only the device's viewer will do.
export const documentPreview = ({ url = "", embed = "", view = "" } = {}, coarse = false) => {
  if (coarse && view) return { src: view, paginates: true };
  if (needsDeviceViewer(url, coarse)) return { src: "", paginates: false };
  return { src: embed, paginates: false };
};

export const documentUrl = (ref, s) => {
  // **Where the file is, not what kind of row this is.** Keying on `isUpload`
  // was right until a returned certificate could have a stored file too: after
  // its PDF is replaced, the row still carries the Drive link Zap 2 filed — the
  // old, wrong document — and View PDF would have kept opening it. The compile
  // and the per-document read both already prefer `storagePath`; this makes the
  // third reader agree with them.
  if ((s?.storagePath || "").trim() && ref) {
    return apiUrl(`/documents/${encodeURIComponent(ref)}/${encodeURIComponent(s.id)}`);
  }
  if (!s?.isUpload) return s?.driveFileUrl || s?.url || "";
  if (!ref) return "";
  return apiUrl(`/documents/${encodeURIComponent(ref)}/${encodeURIComponent(s.id)}`);
};

// Is *this* matter the one being compiled or read?
//
// The banner used to render off a bare boolean, so work started on one matter
// announced itself on every matter opened afterwards. The reference has to be
// part of the question, and both screens have to ask it the same way — a
// second copy of this comparison is how they would disagree about whose work
// it is.
export const compileWorkFor = (compileWork, ref) =>
  (compileWork && ref && compileWork.ref === ref) ? compileWork : null;

// The order the compile will actually merge them in.
//
// The API returns searches ordered by `updatedAt` — the order they were last
// written, so arrival order near enough. That is not the order they appear in
// the PDF, so the list on the matter screen disagreed with the list on Arrange
// documents and nothing said which was true. It looked right on matters whose
// certificates happened to come back in roughly Section 32 order, and random
// on the rest.
//
// Same rule the compile uses, and the same one buildRecompileDocs uses: a saved
// position wins, then the standard Section 32 category order, then the name.
// `compileOrderIndex` is computed by the middleware — this must not grow its
// own copy of the category list, or the screen and the PDF drift apart.
export const orderSearchesForDisplay = (searches = [], compileOrder = []) => {
  const position = new Map(compileOrder.map((id, i) => [id, i]));
  const at = (s) => (position.has(s.id) ? position.get(s.id) : Number.MAX_SAFE_INTEGER);
  // 0 is a real category index (Title goes first), so `??` rather than `||`.
  const category = (s) => s.compileOrderIndex ?? Number.MAX_SAFE_INTEGER;

  // **Not required sinks to the bottom, and it is not merely tidiness.** The
  // list is the merge order, and a `Not Required` row is not in the merge at
  // all — the compile loads `status == 'Returned'`, so an account line or a
  // transfer of land the read took out never reaches the pack. Sitting them
  // among the documents that *do* merge is the list claiming something about
  // the PDF that is not true, and on an import-heavy matter there are more of
  // them than certificates.
  //
  // **First key, ahead of the saved position**, or one carrying a
  // `compileOrder` slot from before it was settled would sort back up among
  // them.
  //
  // Safe against the other screen: *Arrange documents* filters to
  // `isCompilable`, so it never lists these — the two cannot disagree about an
  // order one of them does not show. And a drag saves the order as displayed,
  // which moves their ids to the end of `compileOrder`; harmless, since
  // nothing merges them, and it leaves the stored order agreeing with the
  // screen.
  const settledOut = (s) => (s.status === "Not Required" ? 1 : 0);

  return [...searches].sort((a, b) =>
    (settledOut(a) - settledOut(b)) ||
    (at(a) - at(b)) ||
    (category(a) - category(b)) ||
    (a.name || "").localeCompare(b.name || ""));
};

// ── What the matter's stage says is worth looking at ───────────────
//
// The matter screen carries six panels, and at any given stage only one or two
// of them are the work. While the searches are still coming back there is
// nothing to preview and no compiled document to read; once it is compiled the
// search list has done its job. Six collapsed headings is a worse starting
// point than one open panel and five closed ones — but so is six open panels,
// which is what "expand everything" would give.
//
// These are **defaults, not rules.** They apply when a matter is opened and
// again when its status actually changes. A panel someone opened or closed in
// between is their decision and is left alone until the matter moves on — a
// screen that re-collapsed a panel under someone's cursor because a poll came
// back would be worse than no defaults at all.
// **The searches are open at every stage**, on the firm's instruction, and
// that is the one thing here that changed.
//
// They used to close on a compiled matter for a reason that has since expired:
// the old two-column layout put them a long way down the page, so opening them
// on a matter whose contract already existed pushed the document off screen.
// The three-pane layout gives them their own pane, and a compiled matter is
// exactly when somebody reconciles the pack against the Trisearch account.
//
// **The contract data and the preview both close once there is a document**,
// which is what this always did. They are the two panels for reading a
// contract *before* one exists; past that the PDF is the thing to read, and
// opening either above it buries the document under a page of preview.
//
// **Only one stage differs at all**, which is why this is not a switch over
// four: before a document exists, the fields and the preview are what a
// contract gets checked against, and after one exists they are a click away.
export const sectionDefaults = (status) => {
  if (status === "compiled" || status === "released") {
    return { searches: true, contractData: false, preview: false, compiled: true };
  }

  // searches_complete, compile_failed, searches_pending, a stub, and anything
  // a newer deploy invents.
  return { searches: true, contractData: true, preview: true, compiled: false };
};

// Open state that follows the matter's stage without overruling the person
// looking at it. `resetKey` is the status: when it changes, the panel returns
// to what that stage wants; until then, whatever they last clicked stands.
// Open a collapsed section and scroll to it, when something elsewhere sends
// somebody here.
//
// **A link that only scrolled would look like it did nothing**, because every
// one of these sections can be shut — which is exactly what the compiled
// banner's "open the compiled document" link found. The signal is a counter
// rather than a boolean so pressing the same jump twice works: a boolean is
// already true the second time and the effect never runs again.
const useRevealSection = (revealSignal, setOpen, ref) => {
  useEffect(() => {
    if (!revealSignal) return;
    setOpen(true);
    // jsdom implements neither of these; guard on the method, not the node.
    if (typeof ref.current?.scrollIntoView === "function") {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealSignal]);
};

// ── Showing the document a compile just produced ────────────────────────────
//
// A re-compile is two minutes of watching a progress bar, and what it left
// behind was a toast in the corner and a panel still collapsed — so the result
// of the wait was something staff then had to go and find. Finishing the work
// includes showing it.
//
// **Scoped to the matter, and evaluated at render rather than in the poll.**
// The compile is watched from wherever it was started, so somebody can press
// Compile on one matter and open another while it runs — and a modal for A
// opening over B is worse than not opening at all. The poll records *which*
// matter finished; this asks at render time whether that is the one on screen,
// so there is no stale closure over `selected` to get wrong. Same reasoning as
// `compileWorkFor`, which scopes the banner the same way.
export const expandSignalFor = (signal, ref) =>
  signal && ref && signal.ref === ref ? (signal.n || 0) : 0;

const useStagedSection = (defaultOpen, resetKey) => {
  const [open, setOpen] = useState(defaultOpen);
  const lastKey = useRef(resetKey);

  useEffect(() => {
    if (lastKey.current !== resetKey) {
      lastKey.current = resetKey;
      setOpen(defaultOpen);
    }
  }, [resetKey, defaultOpen]);

  return [open, setOpen];
};

// A section heading that opens and closes. Extracted because there were four
// hand-rolled copies of the same chevron and click target, and the searches
// panel — the one people spend the most time in — had none.
const SectionHeader = ({ open, onToggle, title, meta, actions }) => (
  <div
    onClick={onToggle}
    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12, flexWrap: "wrap" }}
  >
    <span style={{ fontSize: 13, fontWeight: 500, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
      <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
           style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
        <path d="m9 18 6-6-6-6"/>
      </svg>
      {title}
      {meta}
    </span>
    {actions && (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={e => e.stopPropagation()}>
        {actions}
      </div>
    )}
  </div>
);

// ── The generated contract, at the head of the merge ───────────────
//
// The compile puts the generated Contract of Sale and Section 32 *first* in
// the merge list, ahead of every certificate. Nothing on this screen said so,
// so the document list read as "the searches are the Section 32" — which is
// exactly the misreading that matters, because the searches alone are not one.
//
// It is fixed rather than draggable on purpose: its position isn't a
// preference. The precedent contains the statement, and the certificates are
// annexed behind it.
//
// `state` is worked out from what the compile would actually use, not from
// whether a PDF happens to exist — a compiled matter can still be missing half
// its disclosures, and the PDF is not the thing to check.
// ── The contract that came out without page numbers ────────────────────────
//
// The middleware lays the contract out with Chromium, because that is the only
// renderer that can draw the running footer and the page numbers — and the
// contract **cross-references its own page numbers**, so a copy without them is
// a defect in a legal instrument rather than an untidy one.
//
// **It falls back rather than failing**, which is the right trade and the
// reason this is needed: a browser that timed out on an image, ran out of
// memory, or is missing from the image entirely still produces a contract, via
// pdf.co, at pdf.co's own margins and with no footer at all. A contract without
// page numbers is worse than one with them and far better than none.
//
// What the fallback does not do is *tell anybody*. It logs — loudly, and by
// name — and a log is behind an Actions workflow nobody opens unless they
// already suspect something, which is this service's standing failure mode one
// level up. `contractRenderedBy` was recorded on the matter precisely so the
// question could be answered without opening the PDF and counting page
// numbers, and until now no screen read it.
//
// **Three values, and the two that matter share a prefix**: `chromium` is the
// ordinary path, `pdfco` is a deployment that could not render at all, and
// `pdfco-after-render-failed` is one that tried and could not. The second and
// third produce the same document and want the same thing done about them, so
// the difference is for the log rather than for staff.
//
// **It cannot fire on a searches-only pack.** The field is written only where a
// contract was actually generated and deleted where one was not, so a matter
// carrying `documentWarning` says that and not this.
//
// And it stays quiet on the ordinary matter, which is what makes it worth
// reading: Chromium is in the image and is the normal path, so this is the
// exception rather than a row on every matter.
export const renderedWithoutPageNumbers = (matter = {}) =>
  String(matter?.contractRenderedBy || "").startsWith("pdfco");

export const generatedDocumentState = (matter) => {
  const missing = matter?.contractFields?.missing || [];
  // The certificates having been read is the difference between a contract
  // built from the vendor's own answers and one built from the title,
  // planning and rates certificates. Both compile; only one is a section 32.
  const read = Boolean(matter?.extractedFieldsAt || matter?.extractedFields);

  if (!read && missing.length > 0) {
    return {
      complete: false,
      label: "Incomplete",
      detail: `The certificates haven't been read yet. ${missing.length} required field${missing.length !== 1 ? "s are" : " is"} still blank.`,
    };
  }
  if (!read) {
    return {
      complete: false,
      label: "Incomplete",
      detail: "The certificates haven't been read yet.",
    };
  }
  if (missing.length > 0) {
    return {
      complete: false,
      label: "Incomplete",
      detail: `${missing.length} required field${missing.length !== 1 ? "s" : ""} still blank.`,
    };
  }
  return { complete: true, label: "Ready", detail: "Every required field is answered and the certificates have been read." };
};

const GeneratedDocumentRow = ({ matter }) => {
  const state = generatedDocumentState(matter);
  const accent = state.complete ? "#2a8a52" : "#c47a1a";
  const tint = state.complete ? "rgba(42,138,82,0.06)" : "rgba(196,122,26,0.06)";

  return (
    <div style={{
      background: tint, border: `1px solid ${state.complete ? "rgba(42,138,82,0.25)" : "rgba(196,122,26,0.3)"}`,
      borderRadius: 8, padding: "9px 12px", display: "flex", alignItems: "center", gap: 10,
    }}>
      {/* A pin rather than a drag handle: this one's position is not a choice. */}
      <svg width="11" height="11" fill="none" stroke={accent} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
        <line x1="12" y1="17" x2="12" y2="22"/><path d="M9 2h6l-1 8 4 3H6l4-3z"/>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: T.text }}>
          Contract of Sale &amp; Section 32
          <span style={{ fontSize: 10, color: T.textFaint, marginLeft: 6 }}>generated · always first</span>
        </div>
        <div style={{ fontSize: 11, color: T.textSoft, lineHeight: 1.45, marginTop: 2 }}>{state.detail}</div>
      </div>
      <span style={{
        fontSize: 10, fontWeight: 500, color: accent, padding: "2px 8px", borderRadius: 999,
        flexShrink: 0, border: `1px solid ${state.complete ? "rgba(42,138,82,0.25)" : "rgba(196,122,26,0.3)"}`,
        width: 90, textAlign: "center",
      }}>
        {state.label}
      </span>
    </div>
  );
};

// Move one row, without caring what kind of row it is.
//
// A document staff uploaded and a certificate Zap 2 filed are the same thing to
// the merge — a PDF at a position — so nothing here distinguishes them. That is
// the point of the request behind it: an upload has to be placeable *among* the
// searches, not appended after them.
//
// Out of range is a no-op rather than a wrap: the first row's ↑ is disabled,
// and a swap that silently sent it to the bottom would be worse than nothing on
// the screen that decides what order a legal document is bound in.
export const moveInList = (list = [], i, by) => {
  const to = i + by;
  if (i < 0 || i >= list.length || to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[i], next[to]] = [next[to], next[i]];
  return next.map((d, n) => ({ ...d, order: n + 1 }));
};

// Faint until the row is hovered, as before — but a device with no hover
// never gets one, and a keyboard focus is not a hover, so `.s32-move` in
// the global stylesheet keeps them at full ink on touch and while focused.
const MOVE_COLUMN = { display: "flex", flexDirection: "column", gap: 0, flexShrink: 0, margin: "-8px -4px" };
// **A target a finger can hit (DA-28).** These were 12×9 with the two arrows
// 1px apart — under half the 24px minimum, and on a tablet the only way to
// reorder, since touch browsers never fire HTML5 drag. The glyph is the same
// 8px; the hit area is 24×16, and the column takes a negative margin (see
// MOVE_COLUMN) so the row is no taller than it was.
const moveBtnStyle = (disabled) => ({
  background: "none", border: "none", padding: 0, lineHeight: 1,
  fontSize: 8, height: 16, width: 24, display: "flex", alignItems: "center", justifyContent: "center",
  color: disabled ? T.border : T.textFaint,
  cursor: disabled ? "default" : "pointer",
  fontFamily: "Inter, sans-serif",
});

// **Notes are a link in the rail, opening this.** A text box standing open in
// the rail was a panel among the panels it switches between, on every matter,
// for something most matters never use. It saves as it types, exactly as the
// box did — there is nothing to confirm, so the only button closes it.
//
// **Rendered into the body.** It is opened from the rail, which is sticky and so
// a stacking context of its own: drawn inside it, the box sat under the
// document and the matter bar while its dim covered them — a press that dimmed
// the page and showed nothing.
// **When the extension didn't answer**, what to do about it: install it, or
// (on a phone, a tablet or Safari) use a computer. The code is copied either
// way, to paste into the extension's panel.
export const DutiesHelpModal = ({ outcome, matterRef, onClose }) => createPortal((
  <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
    <div role="dialog" aria-label="Duties form extension" onClick={e => e.stopPropagation()}
      style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 520, boxShadow: "0 12px 48px rgba(0,0,0,0.14)", fontSize: 13, color: T.text, lineHeight: 1.55 }}>
      <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, margin: "0 0 8px", letterSpacing: "-0.02em" }}>
        {outcome === "unsupported" ? "The duties form fills on a computer" : "The duties form extension isn't in this browser"}
      </h2>
      {outcome === "unsupported" ? (
        <p style={{ margin: "0 0 10px" }}>It runs in Chrome or Edge on a computer. Phones and tablets (Chrome on an iPhone included) and Safari can't run extensions like it.</p>
      ) : (
        <>
          <p style={{ margin: "0 0 8px" }}>To open Duties Online and fill the form for you, the Provey government forms extension (0.3 or later) needs to be installed here:</p>
          <ol style={{ margin: "0 0 10px", paddingLeft: 20 }}>
            <li>Open <b>chrome://extensions</b> (in Edge, <b>edge://extensions</b>) and turn on <b>Developer mode</b>.</li>
            <li>Remove any older Provey government forms extension.</li>
            <li>Choose <b>Load unpacked</b>, and pick the <b>gov-forms</b> folder (unzipped) the office gave you.</li>
            <li>Press Duties form again.</li>
          </ol>
        </>
      )}
      <p style={{ margin: 0, color: T.textSoft }}>Matter {matterRef}'s code is copied. It works for four hours: paste it into the extension's panel, under <b>Add a matter by code</b>.</p>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
        <Btn variant="primary" size="md" onClick={onClose}>OK</Btn>
      </div>
    </div>
  </div>
), document.body);

// **The first few times Duties Online opens**, what happens next, in four
// lines, in the corner. Three times, or until "Don't show again"; it fades
// after a while unless the pointer is on it. Listens for the open itself
// (`openDutiesFormFor`), so the ribbon and the rail share it.
export const DUTIES_COACH_KEY = "duties-coach";
export const DUTIES_COACH_TIMES = 3;
const coachCount = () => { try { return Number(window.localStorage.getItem(DUTIES_COACH_KEY)) || 0; } catch { return DUTIES_COACH_TIMES; } };
const setCoachCount = (n) => { try { window.localStorage.setItem(DUTIES_COACH_KEY, String(n)); } catch { /* shown this session only */ } };
export const DutiesCoach = () => {
  const [shown, setShown] = useState(false);
  const [fading, setFading] = useState(false);
  const hover = useRef(false);
  useEffect(() => {
    const opened = () => {
      const n = coachCount();
      if (n >= DUTIES_COACH_TIMES) return;
      setCoachCount(n + 1);
      setFading(false);
      setShown(true);
    };
    window.addEventListener("provey:duties-opened", opened);
    return () => window.removeEventListener("provey:duties-opened", opened);
  }, []);
  useEffect(() => {
    if (!shown) return undefined;
    const t = setInterval(() => { if (!hover.current) setFading(true); }, 20000);
    return () => clearInterval(t);
  }, [shown]);
  useEffect(() => {
    if (!fading) return undefined;
    const t = setTimeout(() => setShown(false), 600);
    return () => clearTimeout(t);
  }, [fading]);
  if (!shown) return null;
  return createPortal((
    <div role="status" aria-label="How the duties form fills" onMouseEnter={() => { hover.current = true; setFading(false); }} onMouseLeave={() => { hover.current = false; }}
      style={{ position: "fixed", right: 20, bottom: 20, zIndex: 320, width: 340, maxWidth: "calc(100vw - 40px)", background: T.white, border: `1px solid ${T.border}`, borderRadius: 12,
        boxShadow: "0 12px 48px rgba(0,0,0,0.14)", padding: "16px 18px", fontFamily: "Inter, sans-serif", fontSize: 13, color: T.text, lineHeight: 1.5,
        opacity: fading ? 0 : 1, transition: "opacity 0.6s" }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Duties Online is opening in a new tab</div>
      <ol style={{ margin: "0 0 10px", paddingLeft: 18 }}>
        <li>Log in, then start (or open) this matter's duties form.</li>
        <li>Each page fills itself, and Add property, land identifiers and vendors are pressed for you.</li>
        <li>Check the outlined answers, pick each address suggestion, answer the rest.</li>
        <li>Press Save and continue yourself.</li>
      </ol>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="ghost" size="sm" onClick={() => { setCoachCount(DUTIES_COACH_TIMES); setShown(false); }}>Don't show again</Btn>
        <Btn variant="primary" size="sm" onClick={() => setShown(false)}>Got it</Btn>
      </div>
    </div>
  ), document.body);
};

export const NotesModal = ({ value, onChange, onClose }) => createPortal((
  <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
    <div role="dialog" aria-label="Internal notes" onClick={e => e.stopPropagation()}
      style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 24, width: "100%", maxWidth: 520, boxShadow: "0 12px 48px rgba(0,0,0,0.14)" }}>
      <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>Notes</h2>
      <p style={{ fontSize: 12.5, color: T.textSoft, margin: "0 0 14px" }}>Visible to staff only. Saved as you type.</p>
      <textarea autoFocus value={value || ""} onChange={e => onChange(e.target.value)} placeholder="Add notes visible to staff only…"
        style={{ width: "100%", minHeight: 180, background: T.white, border: `1px solid ${T.borderMid}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, color: T.text, fontFamily: "Inter, sans-serif", resize: "vertical", outline: "none", lineHeight: 1.55 }} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
        <Btn variant="primary" size="md" onClick={onClose}>Done</Btn>
      </div>
    </div>
  </div>
), document.body);

const underlineLink = (danger) => ({
  background: "none", border: "none", cursor: "pointer", fontSize: 12,
  color: danger ? T.red : T.textFaint, fontFamily: "Inter, sans-serif",
  textDecoration: "underline",
  textDecorationColor: danger ? "rgba(254,84,66,0.4)" : "rgba(158,143,132,0.4)",
  padding: 0,
});

// No permanent delete: archiving is the way a matter is put away.
const MatterActions = ({ archived, onArchive, onUnarchive }) => (
  <div style={{ display: "flex", gap: 18, justifyContent: "center", marginTop: 24 }}>
    {archived ? (
      <button onClick={onUnarchive} style={underlineLink(false)}>Restore matter</button>
    ) : (
      <button onClick={onArchive} style={underlineLink(false)}>Archive matter</button>
    )}
  </div>
);

// ── Dashboard ──────────────────────────────────────────────────────
// What the screen that orders the pack is called, in the one place that
// decides it.
//
// **The rail calls that section Searches and the button called it documents**,
// so the press and the place it lands read as two different screens. *Searches*
// is also the truer word here: an owners corporation certificate the office
// drafts itself is arranged on that screen too, and it is a search — it is
// recorded as one, it sorts by the same merge order, and section 13 names it
// with the rest.
//
// One constant rather than four strings that happen to agree, because the two
// that would drift are the button and the heading of the screen it opens —
// which is the pair somebody notices.
export const ARRANGE_LABEL = "Arrange searches";
// **Declared up here rather than beside the frame constants it used to sit
// with**, because `HELP_SECTIONS` below reads it at module level: a `const` is
// in its temporal dead zone until the line declaring it runs, so leaving it
// four thousand lines down threw at import and took every test suite with it.
// The same trap as `UNREGISTERED_PLAN_SECTION` in the middleware's registry.

// ── How this works ─────────────────────────────────────────────────────────
//
// **Four sections, and nothing that is only a label.** Every line here is
// something somebody has had to be told in person — which is the test for
// whether it belongs: a tour that names the buttons teaches nothing that
// looking at the screen does not, and then nobody opens it twice.
//
// **Written as data, so the modal has no prose in it.** What goes stale is the
// wording, and the wording is in one array.
//
// It is not versioned, has no "what's new", and does not track whether it has
// been read. Any of those turns a reference somebody opens when they are stuck
// into a thing that interrupts them.
// **It opens by itself the first time, and once.** A reference nobody knows is
// there is a reference nobody opens, and the thing a new starter most needs —
// the sample matter — is behind it. So the first visit is the one place this
// introduces itself; after that it is the menu item it always was.
//
// **Its own key, not the tour's.** The tour runs on whichever matter somebody
// happened to open and never mentions the sample, so having taken it is not
// having been shown this. Coupling them would mean the person who took the
// tour on a real matter is the one person never offered the way in.
//
// Remembered whether it was opened by itself or from the menu: the property is
// that it offers itself once, and a panel somebody has already read must not
// come back uninvited because they reached it the other way.
export const HELP_SEEN_KEY = "s32-help-seen";

const help = seenOnce(HELP_SEEN_KEY);
export const helpSeen = help.seen;
// **And in the office's own record beside it.** This frame's `localStorage` is
// partitioned and evicted within days, which is why the panel kept coming back
// — `onboarding.js` has the whole reasoning.
export const rememberHelp = () => { help.remember(); markSeen("help"); };
// Only for a test that needs a fresh browser's answer.
export const forgetHelp = help.forget;

export const HELP_SECTIONS = [
  {
    title: "The list",
    points: [
      "The tabs filter by stage and the number on each is how many. Search finds a matter by reference, client or address.",
      "Select ticks several rows at once, which is how a batch gets archived.",
    ],
  },
  {
    title: "Inside a matter",
    points: [
      "Contract is the document and what fills it. Searches is what was ordered and what is back. Activity is what has happened.",
      "A red count on either of the first two is how many things need you.",
    ],
  },
  {
    title: "Searches",
    points: [
      "Each certificate is read as it returns, and what it says fills the contract. Nothing needs pressing \u2014 the panel says which are read and which are still coming.",
      `${ARRANGE_LABEL} sets the order they bind in, or removes one. Add document is for anything that arrived outside Trisearch.`,
    ],
  },
  {
    title: "The contract",
    points: [
      "Compiled is the PDF that was built; Draft is the live version that moves as you correct things. The control above the document switches between them.",
      "Values come from the client\u2019s form and the Triconvey matter, then from the certificates as they are read.",
      "Click any highlighted value on the draft to correct it. A correction outranks all of them and survives the next re-compile.",
      "Contract data underneath lists what the statement still needs.",
    ],
  },
  {
    title: "Getting it out",
    points: [
      "Compile builds the pack: the statement first, then the certificates in order. It runs on its own once every search is back.",
      "Vendor signatures asks each vendor to sign the statement, and tracks who has.",
      "Release emails the agent. Send for cross-check sends the same document to a colleague and is not a release.",
    ],
  },
];

// The reference, not a tour.
//
// **Scrollable rather than paged**, because somebody opening this is looking
// for one answer and a wizard makes them step past three others to reach it.
const HelpModal = ({ onClose, onOpenSample }) => (
  <div
    onClick={onClose}
    style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, width: "100%", maxWidth: 560, maxHeight: "82vh", overflowY: "auto", boxShadow: "0 12px 48px rgba(0,0,0,0.14)" }}
    >
      <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
        How this works
      </h2>

      {/* **The way in leads, rather than sitting under five sections.** This
          opens by itself on a first visit, so the first thing a new starter
          meets has to be the thing to press — and a button five sections down
          is one they scroll past on the one visit it was put there for. The
          sections are what somebody comes back for; this is what they need the
          first time.

          **The tour runs on a matter, so this opens one.** It used to name the
          menu item and leave somebody to find a matter of their own — which is
          the version of this that reads as a sentence rather than a way in.
          The sample is the firm's own matter, it has a compiled contract and
          an owners corporation behind it so every step lands on something
          real, and nothing pressed on it can reach a client.

          Offered only where the deployment has one: a client instance has no
          sample and a button that answered *not configured* is worse than a
          sentence. */}
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 18, marginBottom: 22 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 6 }}>Start here</div>
        <p style={{ fontSize: 12.5, color: T.textSoft, margin: "0 0 14px", lineHeight: 1.6 }}>
          The sample matter is a real contract you can press every button on. Releasing it and asking for a signature both send the email to you rather than to anybody outside the office, and nothing you do there changes the matter.
        </p>
        {onOpenSample ? (
          <Btn variant="primary" size="md" onClick={onOpenSample} style={{ width: "100%", justifyContent: "center" }}>
            Open the sample matter
          </Btn>
        ) : (
          <p style={{ fontSize: 12, color: T.textFaint, margin: 0, lineHeight: 1.6 }}>
            Open any matter and choose <strong>Take the tour</strong> from the menu at the top right.
          </p>
        )}
      </div>

      {HELP_SECTIONS.map(section => (
        <div key={section.title} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 6 }}>{section.title}</div>
          {section.points.map(point => (
            <p key={point} style={{ fontSize: 12.5, color: T.textSoft, margin: "0 0 6px", lineHeight: 1.6 }}>{point}</p>
          ))}
        </div>
      ))}

      {/* Last, and its own line: it is the one thing on here that is about the
          build rather than about the work. */}
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 18px", lineHeight: 1.6, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
        Something wrong or unclear? Select the words on the screen and press <strong>Feedback on this</strong>.
      </p>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Btn variant="primary" size="md" onClick={onClose}>Close</Btn>
      </div>
    </div>
  </div>
);

// Which list, or lists, to ask for.
//
// **The endpoint splits on `trackedReason` exactly**, so one request can never
// return both kinds — `?tracking=true` is the complement of the default, not a
// superset. The whole book is therefore two requests, and `archived` is
// carried into both because it is a separate question: without it an archived
// purchase file would be in no list at all, which is 1760231, the matter the
// middleware's own note names as having been archived by hand off the
// dashboard.
//
// Exported so the pairing is pinned by a test. Built inline in the effect it
// could be quietly reduced to one request and nothing would fail — the list
// still fills, and half the book simply stops arriving.
export const mattersQueries = ({ showArchived = false, allMatters = false } = {}) => {
  const archived = showArchived ? "archived=true" : "";
  const ours = `/matters${archived ? `?${archived}` : ""}`;
  if (!allMatters) return [ours];
  return [ours, `/matters?${[archived, "tracking=true"].filter(Boolean).join("&")}`];
};

// Newest-updated first, which is the order each request already comes back in
// and the one staff are used to. Merging two of them is what makes this
// necessary: concatenating leaves every tracked matter below every ordinary
// one whatever its date, which reads as the list having a second half rather
// than as one list.
//
// A matter with no readable stamp sorts last rather than first, the rule
// `sortMatters` already follows about a missing value — an undated row at the
// top claims to be the most recent thing that happened.
export const byNewest = (rows = []) => [...rows].sort((a, b) => {
  const ta = timeOf(a?.updatedAt);
  const tb = timeOf(b?.updatedAt);
  if (ta === tb) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  return tb - ta;
});

// ── Which rows are not section 32 work ─────────────────────────────────────
//
// Only ever rendered on the whole book — `ALL_MATTERS` above is what puts
// those rows in the list at all, and the endpoint sends an empty reason for
// every matter that is ours, so on the section 32 portal this draws nothing on
// any row.
//
// **The wording is this side's and the rule is not.** `whyNotOurWork` decides
// and sends the key; these are the words. A key nobody has written words
// for **prints as itself** rather than as nothing, the rule the clearing
// workflow's own reason map already follows — a row with no chip in a list
// that mixes the two reads as a matter nobody classified, which is the one
// thing it is not.
export const TRACKED_REASONS = {
  purchase: "Purchase",
  transfer: "Transfer",
  past_exchange: "Past exchange",
};

export const trackedLabel = (reason = "") =>
  TRACKED_REASONS[reason] || String(reason || "");

// How wide a page of the portal may grow. It was 1100, which on an ordinary
// office monitor left a third of the screen as empty margin while the contract
// — the thing read most here — sat in an 850px letterbox. Capped rather than
// edge to edge: a banner's sentence across 2,500px is no longer readable.
export const PAGE_MAX = 1440;

// ── The portal's own navigation ────────────────────────────────────
// Every destination one press from anywhere. Agents and Settings used to be
// buttons on the dashboard alone, so reaching either from a matter meant Back
// first — and Archived was a toggle that read as a filter on one list rather
// than as a place.
//
// **Inside the WordPress page it is a row of tabs, never a second header.**
// The site's own bar is the app's header there, and a logo-and-menu bar under
// it reads as two apps stacked. Opened on its own — the tab a matter link
// opens, or a future standalone app — it is a white bar carrying the logo.
// `IN_FRAME` decides, read once: the frame cannot move between the two.
export const IN_FRAME = (() => {
  try { return window.self !== window.top; } catch { return true; }
})();

// Which tab a screen belongs to. A matter and Arrange documents are inside a
// list, so they light the list they came from.
export const navTabFor = (view, showArchived) => {
  if (view === "directory") return "agents";
  if (view === "sharing") return "sharing";
  if (view === "searchOrders") return "orders";
  if (view === "contacts") return "contacts";
  if (view === "settings") return "settings";
  return showArchived ? "archived" : "matters";
};

export const PortalNav = ({ active, onGo, admin = IS_ADMIN, framed = IN_FRAME, practice: practiceProp, search = null, mark = null }) => {
  const shell = useContext(PracticeLook);
  const practice = practiceProp ?? Boolean(shell);
  // A firm that is not Provey wears its own logo, or its name until it has
  // one. The mark can be passed in (tests); otherwise it comes from the
  // signed-in account. The default firm keeps the Provey wordmark.
  const { me } = useAccount(ACCESS_TOKEN);
  const [logos, setLogos] = useState({ logo: "", white: "" });
  const otherFirm = mark ? mark.isDefault === false : me?.firm?.isDefault === false;
  const firmName = String((mark ? mark.name : me?.firm?.name) || "").trim();
  const darkLogo = mark ? mark.logo : logos.logo;
  const whiteLogo = mark ? mark.white : logos.white;
  useEffect(() => {
    if (mark || !otherFirm) return undefined;
    let alive = true;
    apiFetch("/practice/appearance/logo")
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setLogos({ logo: d?.logo || "", white: d?.white || "" }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [mark, otherFirm]);
  const FirmMark = ({ onBrand = false }) => {
    // The coloured bar takes the white logo. A dark logo there disappears,
    // and a white logo on the white header does the same, so each ground
    // uses its own file and the name stands in until that file exists.
    const src = onBrand ? whiteLogo : darkLogo;
    if (otherFirm && src) {
      return <img src={src} alt={firmName || "Firm logo"} style={{ height: onBrand ? 22 : 24, width: "auto", maxWidth: 180, objectFit: "contain", display: "block", flexShrink: 0 }} />;
    }
    if (otherFirm && firmName) {
      return <span style={{ fontWeight: 600, fontSize: onBrand ? 15 : 16, letterSpacing: "-0.02em", color: onBrand ? T.onBrand : T.text, whiteSpace: "nowrap", flexShrink: 0 }}>{firmName}</span>;
    }
    return <img src={LOGO_SRC} alt="Provey" style={{ height: onBrand ? 18 : 20, width: "auto", display: "block", flexShrink: 0, ...(onBrand ? { filter: "brightness(0) invert(1)" } : {}) }} />;
  };
  const tabs = [
    ["matters", "Matters"],
    // In the practice look a matter's status is a filter on the list, so
    // there is no Archived destination.
    !practice && ["archived", "Archived"],
    // The main module's card file. Every file handler names parties, so it is
    // offered to staff in the embed as well as to an admin — in the practice
    // look, which is where the module is.
    practice && ["contacts", "Contacts"],
    admin && ["orders", "Order searches"],
    admin && ["agents", "Agents"],
    admin && ["sharing", "Purchaser sharing"],
    admin && ["settings", "Settings"],
  ].filter(Boolean);
  // **On its own the bar is the firm's colour, the way Outlook's is**, white
  // on brand; inside WordPress it stays a row of tabs on the site's page.
  const onBar = !framed && practice;
  const accent = practice ? T.brand : T.red;
  // On a phone the row scrolls sideways on one line (\`.s32-portalnav\`), so
  // the place you are is brought into view rather than left off the edge.
  const activeTabRef = useRef(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView?.({ inline: "nearest", block: "nearest" });
  }, [active]);
  const row = (
    <nav aria-label="Portal" className="s32-portalnav" style={{ display: "flex", gap: onBar ? 20 : 24, flexWrap: "wrap", alignSelf: "stretch" }}>
      {tabs.map(([key, label]) => (
        <button key={key} type="button" className="s32-tab" aria-current={active === key ? "page" : undefined}
          ref={active === key ? activeTabRef : undefined}
          onClick={() => onGo(key)}
          style={{
            background: "none", border: "none", borderRadius: 0,
            // The colour on its own property: it is a CSS variable (the firm's
            // brand), which a shorthand border does not reliably carry.
            borderBottom: active === key ? "2px solid" : "2px solid transparent",
            ...(active === key ? { borderBottomColor: onBar ? T.onBrand : accent } : {}),
            padding: framed ? "12px 0 10px" : "0", marginBottom: onBar ? 0 : -1,
            fontSize: 13.5, fontWeight: active === key ? 600 : 500, fontFamily: "Inter, sans-serif",
            color: onBar ? T.onBrand : (active === key ? T.text : T.textSoft),
            opacity: onBar && active !== key ? 0.85 : 1, cursor: "pointer",
            display: "flex", alignItems: "center", WebkitAppRegion: "no-drag",
          }}>
          {label}
        </button>
      ))}
    </nav>
  );
  if (framed) {
    return (
      <div style={{ maxWidth: PAGE_MAX, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ borderBottom: `1px solid ${T.border}` }}>{row}</div>
      </div>
    );
  }
  if (!practice) {
    return (
      <header style={{ background: T.white, boxShadow: "0 1px 0 rgba(0,0,0,0.06)" }}>
        <div style={{ maxWidth: PAGE_MAX, margin: "0 auto", padding: "0 24px", minHeight: 56, display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
          <FirmMark />
          {row}
          {/* Step 1 of moving across: the practice look's search, in the
              classic bar. Absent on classic itself. */}
          {search && <span style={{ marginLeft: "auto", display: "flex" }}><GlobalSearch {...search} onLight /></span>}
          <span style={{ marginLeft: search ? 0 : "auto" }}><AccountLink onGo={onGo} /></span>
        </div>
      </header>
    );
  }
  // In the installed app with its title bar hidden (Window Controls Overlay)
  // this bar *is* the title bar: it can be dragged, and it keeps clear of the
  // window's own buttons through the titlebar-area variables, which are unset
  // (so the fallbacks apply) everywhere else. The destinations move to the
  // left menu, and the middle of the bar is the search.
  const initials = String(PORTAL_USER || "").split(/\s+/).filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <>
      <header style={{ background: T.brand, color: T.onBrand, WebkitAppRegion: "drag", position: "sticky", top: 0, zIndex: 90,
        paddingLeft: "env(titlebar-area-x, 0px)", width: "env(titlebar-area-width, 100%)", boxSizing: "border-box" }}>
        <div style={{ padding: "0 16px 0 20px", height: TITLE_BAR_H, display: "flex", alignItems: "center", gap: 16 }}>
          <FirmMark onBrand />
          <div style={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
            {shell?.search && <GlobalSearch {...shell.search} />}
          </div>
          {shell?.onNewMatter && (
            <button type="button" onClick={shell.onNewMatter}
              style={{ WebkitAppRegion: "no-drag", display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px", borderRadius: 4,
                background: "transparent", color: T.onBrand, border: `1px solid ${T.onBrand}`, fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
              New matter
            </button>
          )}
          <AccountLink onBrand onGo={onGo} />
          {initials && (
            <span title={PORTAL_USER} aria-label={`Signed in as ${PORTAL_USER}`} style={{ width: 30, height: 30, borderRadius: "50%", background: T.onBrand, color: T.brand,
              fontSize: 11.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{initials}</span>
          )}
        </div>
      </header>
      <nav aria-label="Portal" style={{ position: "fixed", left: 0, top: shell?.railTop ?? TITLE_BAR_H, bottom: 0, width: RAIL_W, zIndex: 80, background: T.bg,
        borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, paddingTop: 8, overflowY: "auto" }}>
        {tabs.map(([key, label]) => {
          const on = active === key;
          return (
            <button key={key} type="button" aria-current={on ? "page" : undefined} aria-label={label} onClick={() => onGo(key)}
              style={{ width: 64, minHeight: 54, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
                borderRadius: 4, border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: 10.5, lineHeight: 1.15,
                position: "relative", padding: "6px 2px", textAlign: "center",
                background: on ? T.white : "transparent", color: on ? T.brand : T.textSoft, fontWeight: on ? 600 : 400,
                boxShadow: on ? "0 0 2px rgba(0,0,0,.08), 0 1px 2px rgba(0,0,0,.10)" : "none" }}>
              {on && <span aria-hidden="true" style={{ position: "absolute", left: -4, top: 16, height: 22, width: 3, borderRadius: 2, background: T.brand }} />}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {[].concat(RAIL_ICONS[key] || []).map(d => <path key={d} d={d} />)}
              </svg>
              <span aria-hidden="true">{RAIL_LABELS[label] || label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
};


// ── What a row says needs a person ────────────────────────────────────────
//
// **The list said nothing about the document.** Four things the matter page
// warns about — a searches-only pack, a certificate that arrived after the
// contract went out, a contract with no page numbers, a re-compile that failed
// on a matter whose status still reads as fine — never reached the row, so a
// released matter missing a certificate sat in the list as a calm green
// Released. Finding one meant opening every compiled matter and coming back.
//
// **The predicates are the matter page's own**, read off the stored fields
// every list row already carries; nothing here is a second rule. And it is
// exceptions only: read-since-compiled is deliberately absent, because on a
// list row it falls back to the two stamps and fires on matters that would
// re-compile identically (1760594) — a chip on a row nobody can clear is the
// furniture this codebase keeps refusing.
//
// **A failed compile is only said where the status does not already say it.**
// `compile_failed` wears its own red badge; the row this exists for is the
// released or compiled matter whose re-compile failed underneath an unchanged
// status.
// A matter opened on the new sale page that nobody has reviewed yet.
// An agent's proposal still waiting on the vendor sits under Proposals, not
// New sales: there is nothing for the office to do until the vendor accepts.
export const isProposalAwaiting = (m = {}) => m.newSale?.proposal?.status === "awaiting_vendor";
// A proposal that lapsed at 60 days; the middleware archives it.
export const isProposalExpired = (m = {}) => m.newSale?.proposal?.status === "expired";
// Needs a look is what is wrong; a proposal waiting on its vendor is not.
export const needsALook = (m = {}) => matterAttention(m).some(a => a.key !== "proposal");
export const isNewSaleWaiting = (m = {}) => m.newSale?.review === "pending" && !isProposalAwaiting(m);

export const matterAttention = (m = {}) => {
  const out = [];
  // Opened from the public new sale page and not yet checked by a person —
  // nothing has been ordered for it until somebody has. See the middleware's
  // docs/decisions/new-sale.md.
  // Unpaid, it says so instead: nothing can be ordered for it until the
  // client pays, or staff review it without payment.
  if (isProposalAwaiting(m)) {
    out.push({ key: "proposal", tone: "ink", label: "Proposal — awaiting vendor" });
  } else if (isProposalExpired(m)) {
    out.push({ key: "proposal", tone: "ink", label: "Proposal expired" });
  } else if (m.newSale?.review === "pending") {
    out.push(newSaleUnpaid(m.newSale)
      ? { key: "newSaleReview", tone: "warn", label: "New sale — awaiting payment" }
      : { key: "newSaleReview", tone: "warn", label: m.newSale.payment ? "New sale — card held, review" : "New sale — review" });
  }
  if (m.compileError && m.status !== "compile_failed") {
    out.push({ key: "compileError", tone: "red", label: "Re-compile failed" });
  }
  // Behind a failed compile, as on the matter page: that one is the bigger
  // problem and the warning is about the compile that just ran.
  if (m.documentWarning && !m.compileError) {
    out.push({ key: "documentWarning", tone: "warn", label: "Searches only" });
  }
  if (rebuildNoticeWorthShowing(m)?.released) {
    out.push({ key: "rebuildReleased", tone: "warn", label: "Certificate after release" });
  }
  if (renderedWithoutPageNumbers(m)) {
    out.push({ key: "contractRenderedBy", tone: "warn", label: "No page numbers" });
  }
  return out;
};

// ── What the search box looks in ───────────────────────────────────────────
//
// **It matched the client, the reference and the address**, and staff look a
// matter up by the agent or by who is running it — so typing *Ray White* said
// *No matters found*, which reads as the matter not existing. The agent is the
// matter's own field and any correction staff made to it, since a corrected
// agency is the one they will type.
export const MATTER_SEARCH_FIELDS = "client, address, reference, agent or file handler";
export const matterSearchText = (m = {}) => [
  m.client, m.ref, m.address, m.fileHandlerName, m.fileHandler,
  m.agent, m.agentEmail,
  m.manualFields?.["agent.name"], m.manualFields?.["agent.contactName"],
].filter(Boolean).join(" ").toLowerCase();

// `/` takes you to the search box, the convention of most list screens — but
// never while somebody is typing somewhere, or the character is theirs.
export const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || Boolean(el.isContentEditable);
};

// The four statuses the practice look's matter list filters on.
export const MATTER_STATUS_FILTERS = ["Open", "Closed", "Cancelled", "Deleted"];

export const Dashboard = ({ matters, onSelect, filter, setFilter, search, setSearch, showArchived, onBulkArchive, bulkBusy, onOpenSample, sampleRef, statusFilter = null, onStatusFilter, listLoading = false, onBulkStatus, handlerName = FH_FILTER, sort: sortProp, setSort: setSortProp, returnTo }) => {
  const fluent = useFluent();
  const [allHandlers, setAllHandlers] = useState(readAllHandlers);
  const chooseAllHandlers = (all) => { rememberAllHandlers(all); setAllHandlers(all); };
  const handlerFilter = handlerFilterFor(handlerName, allHandlers);
  const listMark = fluent ? T.brand : T.brown;
  // null key means the server's own order — newest-updated first, which is the
  // useful default and isn't reproducible from any column.
  // **Kept by the app when it is given one (DA-26)**, like the tab and the
  // search, so opening a matter and coming back does not put the list back in
  // the server's order. Local only for a list rendered on its own.
  const [localSort, setLocalSort] = useState({ key: null, dir: "asc" });
  const sort = sortProp || localSort;
  const setSort = setSortProp || setLocalSort;
  const [selectedRefs, setSelectedRefs] = useState(() => new Set());
  // Off until asked for. Bulk archiving is a housekeeping job somebody does
  // occasionally; a checkbox against every row all the time makes the list look
  // like a form to fill in rather than a list of matters to open.
  const [selecting, setSelecting] = useState(false);
  // **Open on a first visit, and only then.** Asked once, when the dashboard
  // mounts, so going back to the list from a matter does not reopen it — and
  // closing it, by either route, is what remembers.
  //
  // **Closed until the office has answered**, which is the one behaviour this
  // gave up when the record moved off the browser: the panel opens a beat into
  // the first visit rather than on the first render. That is the right way
  // round — showing it to somebody who dismissed it this morning is the fault
  // being fixed, and this frame's `localStorage` is evicted within days.
  const [helpOpen, setHelpOpen] = useState(false);
  useFirstVisit("help", helpSeen, () => setHelpOpen(true));
  const closeHelp = () => { rememberHelp(); setHelpOpen(false); };
  // Where a shift-click measures from. Held as a ref rather than an index, so
  // re-sorting the list can't move it onto a different matter.
  const [anchorRef, setAnchorRef] = useState(null);

  const stopSelecting = () => { setSelecting(false); setSelectedRefs(new Set()); setAnchorRef(null); };
  // **Leaving the archive with rows still ticked would carry a selection
  // across two lists that mean opposite things.** The toggle cleared it on its
  // own press; the list is now chosen in the portal's navigation, so the list
  // changing is what clears it.
  useEffect(() => { setSelecting(false); setSelectedRefs(new Set()); setAnchorRef(null); }, [showArchived]);
  // The status the bulk change sets: by default the first that is not the
  // list's own, so a press always changes something.
  const [bulkStatus, setBulkStatus] = useState(() => MATTER_STATUS_FILTERS.find(st => st !== (statusFilter || "Open")) || "Closed");
  useEffect(() => { setBulkStatus(MATTER_STATUS_FILTERS.find(st => st !== (statusFilter || "Open")) || "Closed"); }, [statusFilter]);

  const toggleSort = (key) => setSort(prev =>
    prev.key !== key
      // Dates and progress are most useful newest/highest first; names read
      // better A–Z. Nobody wants to click twice to get the obvious one.
      ? { key, dir: key === "opened" || key === "searches" ? "desc" : "asc" }
      : prev.dir === "asc"
        ? { key, dir: "desc" }
        // Third click returns to the server's order rather than cycling
        // between two sorts with no way back.
        : { key: null, dir: "asc" });
  // ?fh= narrows the list to one file handler so the portal can be embedded
  // per-staff-member, unless they switched to Everyone. Presentation only —
  // see CLAUDE.md, the API still returns every matter.
  const myMatters = matters.filter(m => (!handlerFilter || matchesHandler(m, handlerFilter)));
  const q = search.trim().toLowerCase();
  const filtered = sortMatters(myMatters.filter(m => {
    const ms = !q || matterSearchText(m).includes(q);
    const mf = filter === "all" || (filter === "attention" ? needsALook(m)
      : filter === "new_sale" ? isNewSaleWaiting(m) : filter === "proposal" ? isProposalAwaiting(m) : filter === "proposal_expired" ? isProposalExpired(m) : m.status === filter);
    // The status is the middleware's (`practiceStatus` on the row); a row
    // from an older one without it is let through.
    const st = !statusFilter || !m.practiceStatus || m.practiceStatus === statusFilter;
    return ms && mf && st;
  }), sort);

  // **Back to where you were (DA-26).** The app notes the scroll and the row
  // as a matter is opened from here; once the list is drawn again it goes
  // back to that place and marks the row for a moment, so the eye lands on
  // it. Consumed once, so a later visit to the list starts at the top.
  const [flashRef, setFlashRef] = useState(null);
  const shownCount = filtered.length;
  useEffect(() => {
    const at = returnTo?.current;
    if (!at || listLoading || shownCount === 0) return undefined;
    returnTo.current = null;
    const frame = window.requestAnimationFrame?.(() => window.scrollTo(0, at.y));
    setFlashRef(at.ref);
    const done = setTimeout(() => setFlashRef(null), 1500);
    return () => { clearTimeout(done); if (frame) window.cancelAnimationFrame?.(frame); };
  }, [returnTo, listLoading, shownCount]);

  const searchRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Only ever what is on screen. A select-all that quietly included matters
  // hidden by the filter or the search box would archive things nobody looked
  // at — and archiving is meant to be the safe, reversible action.
  const visibleRefs = filtered.map(m => m.ref);
  const selected = visibleRefs.filter(ref => selectedRefs.has(ref));
  const allVisibleSelected = visibleRefs.length > 0 && selected.length === visibleRefs.length;

  const toggleRef = (ref) => {
    setAnchorRef(ref);
    setSelectedRefs(prev => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      return next;
    });
  };

  // Shift extends from the last row picked; ctrl (or cmd) picks one without
  // disturbing the rest. Both **add** rather than replace — a bulk archive is
  // built up a few rows at a time, and a shift-click that silently dropped the
  // earlier picks would be discovered only after pressing Archive.
  const selectWithModifiers = (ref, e) => {
    if (e.shiftKey && anchorRef) {
      const range = rangeBetween(visibleRefs, anchorRef, ref);
      setSelectedRefs(prev => new Set([...prev, ...range]));
      // The anchor stays put, so a second shift-click extends the same range
      // rather than starting a new one from where the last one ended.
      return;
    }
    toggleRef(ref);
  };
  // **Needs a look sits beside All**, because it is the question of the
  // morning — what is wrong — rather than a stage in the pipeline. It is
  // offered only while it has something in it: an empty tab that is always
  // there is a control that teaches people it is always empty.
  const attentionCount = myMatters.filter(needsALook).length;
  // **New sales: the ones from the public page still waiting on the office**
  // (awaiting the client's card, or held and ready to review). Offered, like
  // Needs a look, only while it has something in it.
  const newSaleCount = myMatters.filter(isNewSaleWaiting).length;
  const proposalCount = myMatters.filter(isProposalAwaiting).length;
  // Lapsed proposals are archived, so they are counted in the archive.
  const expiredCount = myMatters.filter(isProposalExpired).length;
  const tabs = [
    { key: "all", label: "All" },
    ...(attentionCount > 0 || filter === "attention" ? [{ key: "attention", label: "Needs a look" }] : []),
    ...(newSaleCount > 0 || filter === "new_sale" ? [{ key: "new_sale", label: "New sales" }] : []),
    ...(proposalCount > 0 || filter === "proposal" ? [{ key: "proposal", label: "Proposals" }] : []),
    ...(expiredCount > 0 || filter === "proposal_expired" ? [{ key: "proposal_expired", label: "Expired proposals" }] : []),
    { key: "searches_pending", label: "Searches pending" },
    { key: "searches_complete", label: "Searches complete" },
    { key: "compiled", label: "Compiled" },
    { key: "compile_failed", label: "Compile failed" },
    { key: "released", label: "Released" },
  ];
  const counts = Object.fromEntries(tabs.map(t => [
    t.key,
    t.key === "all" ? myMatters.length
      : t.key === "attention" ? attentionCount
      : t.key === "new_sale" ? newSaleCount
      : t.key === "proposal" ? proposalCount
      : t.key === "proposal_expired" ? expiredCount
      : myMatters.filter(m => m.status === t.key).length,
  ]));

  return (
    <div>
      {helpOpen && <HelpModal onClose={closeHelp}
        onOpenSample={onOpenSample ? () => { closeHelp(); onOpenSample(); } : null} />}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 26, color: T.text, margin: "0 0 3px", letterSpacing: "-0.02em" }}>
            {/* **Named for what is in it, not for the parameter.** "All
                matters" already means the section 32 work to everybody using
                this screen, so reusing it for a list that also carries
                purchases would be the same words meaning two things. The
                firm's own phrase is the heading instead. */}
            {ALL_MATTERS
              ? (showArchived ? "Archived — sales, purchases and transfers" : "Sales, purchases and transfers")
              : statusFilter && statusFilter !== "Open" ? `${statusFilter} matters`
              : showArchived ? "Archived matters" : handlerFilter ? `${handlerFilter}'s matters` : "All matters"}
          </h1>
          <p style={{ fontSize: 13, color: T.textFaint, margin: 0 }}>
            {filtered.length} matter{filtered.length !== 1 ? "s" : ""}
            {/* **The order has a name when no column chose it.** The server's
                order — most recent change first — is reproducible from no
                column, so every header showing the same faint arrow said
                nothing about it. */}
            {sort.key === null && filtered.length > 1 && <span data-default-order> · most recently changed first</span>}
          </p>
          {/* **The title bar's search, said on the list.** In the practice
              look the list's own box is hidden, so a search sent from the
              title bar has to be visible here and clearable here, or the list
              stays narrowed with nothing on screen to undo it. */}
          {fluent && q && (
            <span data-list-search style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, padding: "2px 4px 2px 10px", border: `1px solid ${T.border}`, borderRadius: 4, background: T.white, fontSize: 12, color: T.text }}>
              Matching &ldquo;{search.trim()}&rdquo;
              <button type="button" aria-label="Clear the search" onClick={() => setSearch("")}
                style={{ display: "inline-flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", color: T.textFaint, padding: "2px 4px" }}>
                <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </span>
          )}
          {/* Said once, at the top, because it is true of the list rather
              than of a row — and the chips then say which individual rows are
              the ones with nothing to prepare. */}
          {ALL_MATTERS && (
            <p style={{ fontSize: 12, color: T.textFaint, margin: "4px 0 0", maxWidth: 560 }}>
              The office's whole book. Matters with no section 32 to prepare are marked,
              and are the ones the section 32 portal leaves out.
            </p>
          )}
        </div>
        <div className="s32-listtools" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {handlerName && <HandlerScopeSwitch name={handlerName} all={allHandlers} onChange={chooseAllHandlers} />}
          {/* **Status is a filter, in the practice look** — Open, Closed,
              Cancelled, Deleted — where the classic look has an Archived
              list. Anything but Open reads the archive. */}
          {onStatusFilter && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: T.textSoft }}>
              Status
              <select aria-label="Status" value={statusFilter || "Open"} onChange={e => onStatusFilter(e.target.value)}
                style={{ height: 32, border: `1px solid ${T.border}`, borderRadius: 4, background: T.white, fontSize: 12, color: T.text, fontFamily: "Inter, sans-serif", padding: "0 8px" }}>
                {MATTER_STATUS_FILTERS.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </label>
          )}
          {/* In the practice look the title bar's search is this box: its
              Enter fills `search` here. Two boxes one above the other would
              be two ways to ask one question. */}
          <div className="s32-listsearch" style={{ position: "relative", display: fluent ? "none" : undefined }}>
            <svg style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", opacity: 0.35 }} width="13" height="13" fill="none" stroke={T.text} strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            {/* Enter opens the top row, so *ray white ⏎* is a lookup rather
                than a scroll; Esc clears. `/` reaches the box from anywhere on
                the list (see `isTypingTarget`), and the faint key says so. */}
            <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Client, address, ref, agent…" aria-label={`Search by ${MATTER_SEARCH_FIELDS}`}
              onKeyDown={e => {
                if (e.key === "Escape") { setSearch(""); return; }
                if (e.key === "Enter" && q && filtered.length > 0) { e.preventDefault(); onSelect(filtered[0]); }
              }}
              style={{ paddingLeft: 32, paddingRight: 30, height: 34, background: T.white, border: `1px solid ${T.border}`, borderRadius: 999, fontSize: 12, color: T.text, outline: "none", width: 240, fontFamily: "Inter, sans-serif" }} />
            {!search && (
              <span aria-hidden="true" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: T.textFaint, border: `1px solid ${T.border}`, borderRadius: 4, padding: "0 5px", lineHeight: "16px", pointerEvents: "none" }}>/</span>
            )}
          </div>
          {/* **First, and with a surface under it** — which reverses how this
              shipped. It went in as a ghost link last of the plain ones, on
              the reasoning that it is the only control here that does nothing
              to the list and so should read as an aside. That is a fair
              description of what it *does* and the wrong weight for what it is
              *for*: a help panel nobody notices is a help panel nobody opens,
              and the firm asked for it to be seen.

              `raised` rather than `warm` or `primary`, so it is visible
              without reading as the action of the screen. Ahead of Select because it is what somebody
              who does not yet know this screen needs first, which is the only
              audience it has. */}
          <Btn variant="raised" size="sm" onClick={() => setHelpOpen(true)}>
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>
            </svg>
            How this works
          </Btn>
          <Btn variant="ghost" size="sm" onClick={() => (selecting ? stopSelecting() : setSelecting(true))}>
            {selecting ? "Done" : "Select"}
          </Btn>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", borderBottom: `1px solid ${T.border}`, marginBottom: 16 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            style={{ padding: "9px 16px", fontSize: 12, fontWeight: filter === t.key ? 500 : 400, color: filter === t.key ? T.text : T.textFaint, background: "transparent", border: "none", borderBottom: filter === t.key ? "2px solid" : "2px solid transparent", ...(filter === t.key ? { borderBottomColor: listMark } : {}), cursor: "pointer", fontFamily: "Inter, sans-serif", display: "flex", gap: 6, alignItems: "center", marginBottom: -1 }}>
            {t.label}
            <span style={{ fontSize: 10, fontWeight: 600, color: filter === t.key ? T.white : T.textFaint, background: filter === t.key ? listMark : "rgba(51,51,51,0.07)", padding: "1px 6px", borderRadius: 999 }}>
              {counts[t.key] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Appears only with something selected, so the ordinary list is
          unchanged for anyone not doing a bulk action. */}
      {selecting && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", background: T.white, border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: selected.length ? T.text : T.textFaint }}>
            {selected.length > 0
              ? <><strong>{selected.length}</strong> matter{selected.length !== 1 ? "s" : ""} selected</>
              : onBulkStatus ? "Tick the matters to change their status"
              : `Tick the matters to ${showArchived ? "restore" : "archive"}`}
            <span style={{ color: T.textFaint, marginLeft: 8 }}>
              · shift-click for a run, {IS_MAC ? "⌘" : "ctrl"}-click to add one
            </span>
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Btn variant="ghost" size="sm" onClick={stopSelecting} disabled={bulkBusy}>
              Cancel
            </Btn>
            {/* **A status, in the practice look**, where classic archives or
                restores: the same four words as Matter settings. */}
            {onBulkStatus && (
              <select aria-label="New status" value={bulkStatus} onChange={e => setBulkStatus(e.target.value)} disabled={bulkBusy}
                style={{ height: 30, border: `1px solid ${T.border}`, borderRadius: 4, background: T.white, fontSize: 12, color: T.text, fontFamily: "Inter, sans-serif", padding: "0 8px" }}>
                {MATTER_STATUS_FILTERS.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            )}
            {onBulkStatus ? (
              <Btn variant="warm" size="sm" disabled={bulkBusy || selected.length === 0}
                   onClick={async () => { await onBulkStatus(selected, bulkStatus); stopSelecting(); }}>
                {bulkBusy ? "Saving…" : `Set to ${bulkStatus}${selected.length ? ` (${selected.length})` : ""}`}
              </Btn>
            ) : (
            <Btn variant="warm" size="sm" disabled={bulkBusy || selected.length === 0}
                 onClick={async () => { await onBulkArchive(selected, !showArchived); stopSelecting(); }}>
              {bulkBusy
                ? (showArchived ? "Restoring…" : "Archiving…")
                : showArchived
                  ? `Restore${selected.length ? ` ${selected.length}` : ""}`
                  : `Archive${selected.length ? ` ${selected.length}` : ""}`}
            </Btn>
            )}
          </div>
        </div>
      )}

      {/* On a phone the column headings go (see .s32-mhead), and sorting
          is this one select instead — the same keys, the same sort. */}
      <label className="s32-msort" style={{ display: "none", alignItems: "center", gap: 6, fontSize: 12, color: T.textSoft, marginBottom: 8 }}>
        Sort
        <select aria-label="Sort matters by" value={sort.key || ""}
          onChange={e => { const key = e.target.value || null; setSort({ key, dir: key === "opened" || key === "searches" ? "desc" : "asc" }); }}
          style={{ height: 30, border: `1px solid ${T.border}`, borderRadius: 999, background: T.white, fontSize: 12, color: T.text, fontFamily: "Inter, sans-serif", padding: "0 10px" }}>
          <option value="">Most recently changed</option>
          {MATTER_SORT_KEYS.map(h => <option key={h.key} value={h.key}>{h.label}</option>)}
        </select>
      </label>

      <div className="s32-mhead" style={{ display: "grid", gridTemplateColumns: selecting ? GRID_COLS_SELECTING : GRID_COLS, gap: 16, padding: "0 14px", marginBottom: 6, alignItems: "center" }}>
        {selecting && (
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={() => setSelectedRefs(allVisibleSelected ? new Set() : new Set(visibleRefs))}
            disabled={visibleRefs.length === 0}
            aria-label="Select all matters shown"
            style={{ cursor: visibleRefs.length ? "pointer" : "default", accentColor: T.brown }}
          />
        )}
        {MATTER_SORT_KEYS.map(h => (
          <button key={h.key} onClick={() => toggleSort(h.key)}
            style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "Inter, sans-serif", fontSize: 10, fontWeight: 600, color: sort.key === h.key ? T.text : T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {h.label}
            <span style={{ fontSize: 9, opacity: sort.key === h.key ? 1 : 0.25 }}>
              {sort.key === h.key ? (sort.dir === "asc" ? "▲" : "▼") : "▲"}
            </span>
          </button>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }} aria-busy={listLoading || undefined}>
        {/* **Reloading the list spins in the list**, not over the page: a
            whole-page spinner on changing a filter read as the site going
            down. The old rows go while it loads, so none is opened by
            mistake from the list that is being replaced. */}
        {listLoading && (
          <div role="status" aria-label="Loading matters" style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <div style={{ width: 28, height: 28, border: `3px solid ${T.border}`, borderTopColor: T.brown, borderRadius: "50%", animation: "s32-spin 0.8s linear infinite" }} />
          </div>
        )}
        {!listLoading && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: T.textFaint, fontSize: 13 }}>
            {q
              ? <>No matter matches &ldquo;{search.trim()}&rdquo; in {MATTER_SEARCH_FIELDS}</>
              : "No matters found"}
            {q && fluent && (
              <div style={{ marginTop: 12 }}>
                <Btn variant="raised" size="sm" onClick={() => setSearch("")}>Clear search</Btn>
              </div>
            )}
            {handlerFilter && matters.length > 0 && (
              <div style={{ fontSize: 12, marginTop: 6 }}>
                Filtered to file handler &ldquo;{handlerFilter}&rdquo; — check the name matches the one on the matter, or switch to Everyone.
              </div>
            )}
          </div>
        )}
        {!listLoading && filtered.map(m => (
          <div key={m.ref}
            // **A row is reachable from the keyboard.** It was a div with a
            // click handler, so only the small reference chip took focus and
            // the list could not be worked without a mouse. Enter does what a
            // click does; while selecting it ticks the row.
            role="link" tabIndex={0} className={`s32-row s32-mrow${selecting ? " s32-mrow-sel" : ""}`}
            aria-label={`Open ${m.ref}${m.client ? ` — ${m.client}` : ""}`}
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || (selecting && e.key === " ")) {
                e.preventDefault();
                if (selecting) toggleRef(m.ref); else onSelect(m);
              }
            }}
            onClick={(e) => {
              // While selecting, a modifier means "pick rows", not "open this
              // one" — and shift-clicking a link normally selects text, so the
              // native behaviour is suppressed too.
              if (selecting && (e.shiftKey || e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                window.getSelection?.()?.removeAllRanges();
                selectWithModifiers(m.ref, e);
                return;
              }
              // Outside selecting, a modifier means what it means on any
              // link: open the matter in its own tab.
              if (e.ctrlKey || e.metaKey || e.shiftKey) {
                window.open(matterHref(m.ref), "_blank");
                return;
              }
              onSelect(m);
            }}
            style={{ display: "grid", gridTemplateColumns: selecting ? GRID_COLS_SELECTING : GRID_COLS, alignItems: "center", gap: 16, background: flashRef === m.ref ? T.bgAlt : selectedRefs.has(m.ref) ? "rgba(107,95,87,0.05)" : T.white, transitionProperty: "border-color, background", transitionDuration: "0.15s, 0.6s", border: `1px solid ${selectedRefs.has(m.ref) ? T.borderMid : T.border}`, borderRadius: 10, padding: "13px 14px", cursor: "pointer" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.borderMid}
            onMouseLeave={e => e.currentTarget.style.borderColor = selectedRefs.has(m.ref) ? T.borderMid : T.border}>
            {/* stopPropagation, or ticking a box opens the matter instead. */}
            {selecting && (
              <input
                type="checkbox"
                checked={selectedRefs.has(m.ref)}
                // onClick carries the modifier keys; onChange does not, which
                // is why the range is handled here and change is left inert.
                onClick={e => { e.stopPropagation(); selectWithModifiers(m.ref, e); }}
                onChange={() => {}}
                aria-label={`Select ${m.ref}`}
                className="s32-mc-check"
                style={{ cursor: "pointer", accentColor: T.brown }}
              />
            )}
            <div className="s32-mc-main" style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
                {/* A real link, so a middle-click opens the matter in its own
                    tab. A plain click behaves exactly like clicking the row;
                    in selecting mode it bubbles to the row, which knows about
                    range selection. */}
                <a
                  href={matterHref(m.ref)}
                  // The row is the keyboard target now; a second stop on the
                  // same matter would make every row two presses of Tab.
                  tabIndex={-1}
                  onClick={e => {
                    if (selecting) { e.preventDefault(); return; }
                    if (e.ctrlKey || e.metaKey || e.shiftKey) { e.stopPropagation(); return; }
                    e.preventDefault();
                    e.stopPropagation();
                    onSelect(m);
                  }}
                  style={{ fontSize: 11, fontFamily: "monospace", color: T.textFaint, background: T.bg, padding: "1px 6px", borderRadius: 4, textDecoration: "none" }}
                >{m.ref}</a>
                {/* **The training file, marked in the list.** Staff are told to
                    press Release and Request signature on it, so the one place
                    everybody needs to know which matter that is, is the place
                    they pick a matter from — and the matter screen's own banner
                    only says so once you are already inside.

                    Quiet rather than alarming: this is a label, not a problem,
                    so it takes the soft fill rather than 137b's outline or the
                    material-facts tint. It is the word the rest of the product
                    uses — the banner, the help link and the `[Sample]` on every
                    rehearsal subject — so a reader meets one name for it.

                    **From the tenant record, never a literal.** The middleware
                    is the only place that says which matter it is, and a
                    reference typed here would be a second source of truth that
                    goes stale the day the firm nominates another. */}
                {sampleRef && m.ref === sampleRef && <span style={{ fontSize: 10, color: T.textSoft, background: T.bgAlt, padding: "1px 6px", borderRadius: 999, fontWeight: 500 }}>Sample</span>}
                {saysYes(m.has137b) && <span style={{ fontSize: 10, color: T.text, background: T.white, border: `1px solid ${T.text}`, padding: "0 6px", borderRadius: 999, fontWeight: 500 }}>137b</span>}
                {saysYes(m.hasMaterialFacts) && <span style={{ fontSize: 10, color: T.red, background: "rgba(254,84,66,0.08)", padding: "1px 6px", borderRadius: 999, fontWeight: 500 }}>Material facts</span>}
                {/* Which of the two reasons this one is. Quiet, like Sample:
                    it is a classification rather than a problem — and on the
                    main list it never renders at all, because the endpoint
                    sends an empty reason for every matter that is ours. */}
                {m.trackedReason && <span style={{ fontSize: 10, color: T.textSoft, background: T.bgAlt, padding: "1px 6px", borderRadius: 999, fontWeight: 500 }}>{trackedLabel(m.trackedReason)}</span>}
                {/* What needs a person, in the matter page's own words — see
                    `matterAttention`. Outlined like 137b, red only for a
                    failed compile, so the row stays calm until one appears. */}
                {matterAttention(m).map(a => (
                  <span key={a.key} data-attention={a.key}
                    style={{ fontSize: 10, color: a.tone === "red" ? T.red : T.text, background: T.white, border: `1px solid ${a.tone === "red" ? "rgba(254,84,66,0.5)" : T.borderMid}`, padding: "0 6px", borderRadius: 999, fontWeight: 500 }}>
                    {a.label}
                  </span>
                ))}
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 2 }}>{m.client}</div>
              <div className="s32-mc-addr" style={{ fontSize: 11, color: T.textFaint }}>{m.address}</div>
            </div>
            <div className="s32-mc-handler" style={{ fontSize: 13, color: T.textSoft, minWidth: 0 }}>{m.fileHandlerName}</div>
            <div className="s32-mc-opened" style={{ fontSize: 12, color: T.textSoft }}>{formatOpenedDate(m.openedDate)}</div>
            <div className="s32-mc-search"><SearchProgress searches={m.searches} matter={m} /></div>
            <div className="s32-mc-status"><Badge status={m.status} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── The one next-step card ─────────────────────────────────────────
//
// The matter screen renders six banners on six independent conditions — the
// rebuild notice, nothing-to-compile, the compiling banner, the compiled
// banner, the document warning and the error banner — and on a bad day four of
// them stack above the fold in the same shape, all shouting equally.
//
// This resolves them into one headline and a list of quieter lines. **The
// danger of doing that is silence**: a precedence rule that drops a case does
// not throw, it just stops saying something. Two of these are the worst
// outcomes this screen can produce — `documentWarning` is a searches-only PDF
// read as a complete Section 32, and `rebuildNotice` on a released matter is a
// certificate the agent's copy does not contain — so the test asserts the
// invariant rather than the examples: **every condition that renders in the
// old layout renders here too**, as the headline or as a notice.
//
// Nothing is computed that the old layout did not already compute; the
// precedence is only which of them gets to be the headline.
export const FILL_IN_FROM_DRAFT = "Fill it in from Draft, then re-compile.";

// Whether the firm's contract template is missing its REIV/LIV licence
// number, read once per page and kept up to date by Settings (`noteTemplate`).
// A failed read, or a middleware without the route, is "not missing": the
// notice is a reminder, never a reason to show something false.
let templateRead = null;
const templateListeners = new Set();
export const noteTemplate = (t) => {
  templateRead = Promise.resolve(Boolean(t && t.licenceMissing));
  templateListeners.forEach(fn => fn(Boolean(t && t.licenceMissing)));
};
const useLicenceMissing = () => {
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!templateRead) {
      templateRead = apiFetch("/settings/contract-template")
        .then(r => (r.ok ? r.json() : null))
        .then(d => Boolean(d && d.contractTemplate && d.contractTemplate.licenceMissing))
        .catch(() => false);
    }
    templateRead.then(v => { if (alive) setMissing(v); });
    const listen = (v) => { if (alive) setMissing(v); };
    templateListeners.add(listen);
    return () => { alive = false; templateListeners.delete(listen); };
  }, []);
  return missing;
};

export const nextStep = (matter = {}, { compiling = false, reading = false, mergeableCount = 0, outstanding = 0, licenceMissing = false } = {}) => {
  const settled = matter.status === "compiled" || matter.status === "released";
  const rebuild = rebuildNoticeWorthShowing(matter);
  // **`canRelease` is the only thing that takes the send away.** That split
  // was already decided and written down: it decides whether the button
  // *exists*, because a press with no stored copy can only end in the
  // middleware's refusal, and `releaseWarning` carries everything else as a
  // confirmation staff tick — unsigned, signed against an older version, read
  // since compiled. Every one of those is "release this version knowing what
  // it is", which is staff's call.
  //
  // Three branches below had grown their own second refusal on top of that,
  // and it overruled the split rather than adding to it: a failed re-compile,
  // a rebuild flag on a matter not yet released, and a pack the import has not
  // listed each dropped the action with nothing on the card saying why. The
  // expanded document view never had the restriction, so the product read as
  // having a resend in one place and not the other.
  const canSend = canRelease(matter);
  const withSend = (...actions) => (canSend ? [...actions, "release"] : [...actions]);
  const generated = generatedDocumentState(matter);

  // Everything true of this matter, whichever one ends up leading. Order is
  // reading order, not precedence.
  const notices = [];
  if (matter.compileError) {
    notices.push({ key: "compileError", tone: "red", title: "Compile failed", detail: matter.compileError });
  } else if (matter.geminiError) {
    // One or the other, as before: a failed compile is the bigger problem and
    // the extraction failing underneath it is not the thing to act on.
    // `warn`, not `ink`: the contract printed without the answers the
    // certificates were going to supply, and nothing on the page says which
    // ones. That is the same class of fault as a searches-only PDF.
    notices.push({ key: "geminiError", tone: "warn", title: "Compiled, but the certificates could not be read", detail: matter.geminiError });
  }
  // Suppressed by a failed compile in the old layout too — the red banner
  // already covers it.
  if (matter.documentWarning && !matter.compileError) {
    notices.push({
      key: "documentWarning", tone: "warn",
      title: "Compiled without the vendor statement",
      detail: `${matter.documentWarning}. The PDF contains the searches only, so it is not yet a complete Section 32.`,
    });
  }
  // **Not suppressed by a failed compile**, unlike the warning above it. That
  // one is about the compile that just ran; this is about the PDF that is
  // *stored* — the last version that compiled, which is the one `canRelease`
  // would still send. It stays true, and worth saying, while the red banner
  // talks about the next one.
  // **The firm prints the standard template without its licence number.**
  // The contract still generates — the firm's choice, made in Settings — but
  // the form's footer has no licensee line until the number is entered.
  // Whether it is missing is the middleware's (`licenceMissing`).
  if (licenceMissing) {
    notices.push({
      key: "licenceMissing", tone: "warn",
      title: "Your REIV/LIV licence number has not been entered",
      detail: "Contracts are generated on the standard REIV/LIV template, but your licence number is not printed on them. "
        + "Enter it in Settings → Contract template.",
    });
  }
  if (renderedWithoutPageNumbers(matter)) {
    notices.push({
      key: "contractRenderedBy", tone: "warn",
      title: "This contract has no page numbers",
      detail: "The browser that lays the contract out could not run for this version, "
        + "so it was converted elsewhere — without the running footer or the page numbers "
        + "the contract's own clauses refer to. Re-compile to lay it out properly.",
    });
  }
  // **Why this matter is not on the list**, for somebody who reached it by
  // link. It is a classification rather than a fault, so `ink` and never an
  // action: a purchase file is doing exactly what it should.
  //
  // The sentence is the middleware's own — `trackingReport` composes it, and a
  // second copy here would drift from the rule that decides which list the
  // matter is on. A reason with no sentence is still said, in the words the
  // list uses, rather than being dropped.
  if (matter.tracking?.reason) {
    notices.push({
      key: "tracking", tone: "ink",
      title: "Not on the matters list",
      detail: matter.tracking.detail || trackedLabel(matter.tracking.reason),
    });
  }
  if (settled && readSinceCompiled(matter)) {
    notices.push({
      key: "readSinceCompiled", tone: "ink",
      title: "The certificates have been read since this version was built",
      detail: "The contract data is newer than the document. Re-compile to put it in the PDF.",
    });
  }

  const done = (head) => ({ ...head, notices: notices.filter((n) => n.key !== head.kind) });

  // Work in progress leads, because it is the answer to "why is nothing
  // happening". The elapsed count and the reading/merging wording stay in
  // CompilingBanner, which this does not reimplement.
  if (compiling || reading) return done({ kind: "busy", tone: "ink" });

  if (matter.compileError) {
    return done({
      kind: "compileError", tone: "red", title: "Compile failed",
      body: matter.compileError,
      // **A failed re-compile does not take the send with it.** The stored copy
      // is the last version that compiled and is still there; the failure is
      // about the *next* one. Sending it is exactly what somebody wants when
      // the agent has lost the email.
      //
      // Never the headline here — Re-compile is what the card is about.
      releaseReady: false,
      actions: withSend(...(mergeableCount > 0 ? ["compile"] : [])),
    });
  }

  // A certificate arrived after the version was built. On a released matter
  // this outranks "Released", because the copy the agent holds is the thing
  // that is now wrong.
  if (rebuild) {
    return rebuild.released
      ? done({
          // The one rebuild case that is a `warn`: a copy is already out
          // there and it does not contain what arrived.
          kind: "rebuildReleased", tone: "warn",
          title: "A certificate arrived after this contract was released",
          body: `The copy ${rebuild.releasedTo || "the agent"} holds does not contain it, and a released contract is never rebuilt automatically. Re-compile and release again when you're satisfied.`,
          actions: ["compile", "release", "crossCheck"],
        })
      : done({
          kind: "rebuild", tone: "ink",
          title: "Certificates have arrived since this version was built",
          body: `Flagged for the next automatic rebuild (${rebuild.reason}).`,
          // The version that is stored is still sendable, and
          // `releaseWarning`'s read-since-compiled confirmation is written for
          // exactly this — *"re-compile first, or release this version knowing
          // it does not carry what was read"*. Withholding the button made
          // that choice for somebody without saying so.
          releaseReady: false,
          actions: withSend("compile"),
        });
  }

  if (settled) {
    // **A compiled matter is not a finished one**, and this card is where that
    // gets decided by whoever is looking at it. Two things can be wrong with
    // it and both belong in the same sentence: the statement has holes, and
    // the pack is missing a certificate.
    //
    // `outstanding` is counted by the caller off the rows it already has, so
    // the card and the rail cannot disagree about how many are back.
    const shortPack = matter.status !== "released" && outstanding > 0;
    // **Nothing to send, so nothing to offer.** A compiled matter can still
    // have no stored copy — an older one whose pdf.co link expired, or a
    // compile that never got as far as storing — and the middleware refuses
    // a release without one. A button whose only outcome is a refusal does
    // not belong on the screen; Re-compile is the honest action.
    const sendable = canRelease(matter);
    // **And it is part of being ready**, which it was not: `ready` decided
    // whether the body prints `gaps` at all, so on an otherwise complete
    // matter with no stored copy the card read "The document is below" and the
    // sentence explaining the missing button was never reached. The button
    // vanishing with nothing saying why is what sent staff looking for a
    // resend that the expanded view was still offering.
    const ready = generated.complete && !shortPack && sendable;

    const gaps = [];
    // First, because it is the one that stops the matter going out at all.
    //
    // **Said on a released matter too.** It used to be withheld there, on the
    // reading that a released contract has already gone out so there is
    // nothing to explain — but resending one is ordinary (an address typed
    // wrong, an agent who lost it), and that is exactly the matter where the
    // button is missing and the reason is least guessable.
    if (!sendable) {
      gaps.push(matter.compileVersion
        ? `The stored copy of this contract has expired, so there is nothing to ${matter.status === "released" ? "send again" : "release"} — re-compile to produce one.`
        : "Nothing has been compiled yet, so there is nothing to release.");
    }
    if (!generated.complete) gaps.push(generated.detail);
    if (shortPack) {
      gaps.push(`${outstanding} search${outstanding === 1 ? " has" : "es have"} not come back, `
        + `so ${outstanding === 1 ? "that certificate is" : "those certificates are"} not in the PDF.`);
    }

    return done({
      // Green follows the badge: it means the contract has gone out, and
      // nothing else. A compiled matter is not finished — this card's own body
      // is usually a list of what is still blank in it — so a green panel over
      // that text said the opposite of the words inside it.
      kind: matter.status === "released" ? "released" : "compiled",
      tone: matter.status === "released" ? "green" : "ink",
      title: [
        // **The heading matches the badge.** A card headed "Compiled" over a
        // body listing what is still blank reads as finished and then argues
        // with itself; the badge has said "Compiled — review" all along.
        matter.status === "released" ? "Released" : ready ? "Compiled" : "Compiled — review",
        matter.compileVersion ? `v${matter.compileVersion}` : "",
        compiledAtText(matter),
      ].filter(Boolean).join(" · "),
      // **It names the control on screen, which is *Draft*.** This said "the
      // contract preview" — the name that panel had before staff started
      // reading the compiled PDF beside it, at which point *preview* named the
      // wrong half. The segmented control a few centimetres below this
      // sentence says Compiled and Draft, so a body sending somebody to a
      // third name is an instruction they have to translate.
      body: ready
        ? "The document is below. To change anything, edit it in Draft and re-compile."
        : `${gaps.join(" ")} ${FILL_IN_FROM_DRAFT}`,
      // **The same sentences as lines (DA-23)**, for the card to list when
      // there is more than one: four in a paragraph read as one block, and
      // the reader had to parse it to count what was wrong. `body` stays the
      // joined string for everything else that reads it.
      gaps: ready ? [] : gaps,
      // **Release is not the headline action on a statement with holes in it.**
      // It stays offered — the form is designed to issue with blanks completed
      // at sale, and going ahead is staff's call, the same reason the compile
      // gate reports rather than refuses — but a dark "Release to agent"
      // above a list of what is missing tells somebody the opposite of
      // what the words under it say.
      releaseReady: ready,
      // **A released contract can still be sent again, and this list was the
      // only thing saying otherwise.** The button, its label and the modal
      // were all built for it — `act.release` has said "Re-release" since it
      // was written, and `ReleaseModal` seeds its To box from `releasedTo`
      // and offers the quiet link update. Dropping `release` here took every
      // route to it off the screen but one: the expanded document modal,
      // whose own list never had the restriction. So staff read the product
      // as having no way to send a contract twice, which is a thing that
      // happens constantly — an address typed wrong, an agent who lost it, a
      // second agency on the listing.
      //
      // **It is never the primary button on a released matter**, because
      // there is nothing outstanding to press: `act.release` reads the status
      // for its variant, so this offers the action without inviting it.
      //
      // **The cross-check is not restored with it.** That one was withdrawn on
      // a released matter for its own reason — the contract has gone out, and
      // asking a colleague to check it afterwards is a different conversation
      // — and nothing about wanting to send the document again touches it.
      actions: !sendable ? ["compile"]
        : matter.status === "released" ? ["compile", "release"]
        : ready ? ["release", "compile", "crossCheck"] : ["compile", "release", "crossCheck"],
    });
  }

  // **Waiting on Trisearch to list the searches.**
  //
  // A pack that arrived entirely by email reads "9 of 9" and sits at
  // searches_pending: the completion claim will not call a matter complete on
  // a list the import has never confirmed. Nothing compiles, so no timeline
  // line is written either — and the banner led with whatever field happened
  // to be blank, which is a true sentence about the wrong thing. 1760442 and
  // 1760443 are both this.
  //
  // Above the incomplete-contract branch, because this is why nothing has
  // happened; the blank field is a separate fact and the panel below says it.
  if (matter.awaitingSearchList && mergeableCount > 0) {
    const { promoted } = matter.awaitingSearchList;
    return done({
      kind: "ready", tone: "ink",
      title: "Waiting for Trisearch to list the searches",
      body: `${promoted} document${promoted === 1 ? "" : "s"} arrived by email and ${promoted === 1 ? "is" : "are"} on the matter. `
        + "It won't compile by itself until the nightly import lists them.",
      // A matter here rarely has a stored copy; where it does, one exists and
      // is sendable, and this card is about why nothing compiled rather than
      // about what may be sent.
      releaseReady: false,
      actions: withSend("compile"),
    });
  }

  // Something the compile could actually merge. Offering Compile with nothing
  // to merge invites a press the middleware refuses.
  //
  // **"Ready" is a claim, and it was made without checking.** This branch is
  // the last fallthrough, so it fired on the strength of `mergeableCount`
  // alone — one mergeable document was enough to head the screen with *Ready
  // to compile* while the panel directly below said the contract was
  // incomplete and the Contract tab said a required field was blank. The
  // banner is the first thing read and it contradicted both.
  //
  // `generated` is already computed above and says exactly this, so the fix is
  // to use it rather than to add a second banner saying the same thing twice.
  // The action is unchanged either way: an incomplete contract is still the
  // normal thing to compile — the form is designed to issue with blanks — so
  // this reports, it does not gate.
  //
  // **And the pack is the other half of that claim, which was left out.** The
  // fix above checked whether the *contract* was complete and never asked
  // whether the *searches* were — so a matter at 10 of 11 read "Ready to
  // compile" over a rail saying 10 of 11, on nearly every matter still
  // gathering certificates. 1760566.
  //
  // The compiled branch has asked this all along (`shortPack`), in the same
  // words and the same order: the statement first, then the pack. Asking it
  // here is what stops the two cards disagreeing about one matter as it moves
  // from one to the other.
  if (mergeableCount > 0) {
    const merged = `${mergeableCount} document${mergeableCount === 1 ? "" : "s"}`;
    const one = outstanding === 1;
    const shortPack = outstanding > 0;
    const short = `${outstanding} search${one ? " has" : "es have"} not come back, `
      + `so ${one ? "that certificate would not be" : "those certificates would not be"} in it.`;

    if (!generated.complete) {
      return done({
        kind: "ready", tone: "ink",
        // **Still reports, still does not gate.** The form is designed to
        // issue with blanks completed at sale, so compiling a matter that is
        // short a certificate is a normal thing staff do deliberately — what
        // must not happen is the screen calling it ready. `compileReady` is
        // the same distinction `releaseReady` draws one branch up: offer the
        // action without inviting it.
        compileReady: false,
        title: "The contract is not complete yet",
        body: `${generated.detail} Compiling anyway merges ${merged} behind it.`
          + (shortPack ? ` ${short}` : ""),
        actions: ["compile"],
      });
    }

    if (shortPack) {
      return done({
        kind: "ready", tone: "ink",
        compileReady: false,
        title: `${outstanding} search${one ? " has" : "es have"} not come back yet`,
        body: `Compiling now merges ${merged} behind the generated contract; `
          + `${one ? "that certificate" : "those certificates"} would not be in it.`,
        actions: ["compile"],
      });
    }

    return done({
      kind: "ready", tone: "ink",
      title: "Ready to compile",
      body: `${merged} will be merged behind the generated contract.`,
      actions: ["compile"],
    });
  }

  // Said rather than left blank: an empty action area reads as a missing
  // feature, and this is the ordinary state of a matter for its first few days.
  return done({
    kind: "idle", tone: "",
    title: "Nothing to compile yet",
    body: (matter.searchCount || 0) === 0
      ? "No searches recorded yet. They arrive from Trisearch, or you can add a document you hold."
      : "No search has come back with a document yet. You can add one you hold.",
    actions: [],
  });
};

// ── What the searches don't cover ──────────────────────────────────
//
// The gap between "this field is blank" and "somebody has to do something".
// The middleware works out which, because the mapping from a blank field to
// the certificate that answers it is a conveyancing judgement, and a second
// copy here would drift from the one the statement is built against.
//
// Aimed at the file handler as much as at whoever is compiling: a missing
// planning certificate is an ordering job, not a typing job, and it has to be
// visible before the statement goes out rather than after.
// Which source an answer came from, in words staff use.
//
// The middleware's own names are `extraction`, `matter`, `firm`, `manual` and
// `standard` — accurate and not what anybody calls them. `matter` in
// particular covers the client's form, which is what somebody comparing two
// answers needs to be told.
export const sourceName = (source) => ({
  extraction: "the certificates",
  matter: "the client's form",
  office: "the matter in Triconvey",
  // The file handler's staff account (Settings → Users): their name and phone.
  account: "the file handler's account in Settings",
  firm: "firm settings",
  manual: "a staff correction",
  standard: "the form's own wording",
  // The state's own planning mapping. Named for what it is rather than for
  // the service: "Vicmap" means nothing to a file handler reading a
  // provenance tag beside a zone.
  vicmap: "the state planning map",
}[source] || source);

// What a Vicmap lookup actually answered, in one line.
//
// **The bushfire answer is named either way**, and it is why the sentence is
// not simply "the planning data was fetched". *Not* in a bushfire prone area is
// a disclosure a purchaser relies on, and it is the one required field this
// lookup exists to reach — reporting the zone and leaving it out would let
// somebody read success and never learn whether the thing they pressed the
// button for came back.
//
// **No overlays is stated, not omitted.** A line that simply leaves overlays
// out reads as nobody having looked, which is the same fault at a smaller
// scale, and it is what section 3.4 prints for the same reason.
export const vicmapSummary = (res = {}) => {
  const zones = res.zones || [];
  const overlays = res.overlays || [];
  return [
    res.planningScheme || "",
    zones.join(", "),
    overlays.length ? `${overlays.length} overlay${overlays.length > 1 ? "s" : ""}` : "no overlays",
    res.bushfireProneArea === true ? "bushfire prone area"
      : res.bushfireProneArea === false ? "not in a bushfire prone area"
        // Unreachable through the lookup, which reports an unresolved parcel
        // separately — but a summary that silently dropped the field would be
        // the failure this whole line is written against.
        : "bushfire prone area not answered",
    // **The press fetches the free state reports too**, so it has to say when
    // one landed: a document appearing in the pack with nothing on screen
    // saying why is the shape of surprise this codebase keeps removing.
    //
    // **Silent when nothing was filed, and only when nothing was wrong.** The
    // steady state is that the reports are already in the pack, and a line
    // reading "0 reports" on every press is noise that teaches people to stop
    // reading the toast. A fetch that *failed* is the opposite — nothing else
    // would say so until the hourly sweep tried again — so that one is named.
    reportsSaid(res.reports),
  ].filter(Boolean).join(" · ");
};

// What the lookup's report fetch is worth saying out loud.
const reportsSaid = (reports) => {
  if (!reports) return "";
  const filed = Number(reports.fetched) || 0;
  if (filed > 0) return `${filed} report${filed > 1 ? "s" : ""} filed`;
  // A skip is the ordinary case — already in the pack, or the fetch switched
  // off — and says nothing. Only a failure is reported.
  return reports.failedBy || reports.error ? "reports could not be fetched" : "";
};

// How many things in the review a person has to look at.
//
// Not everything in it counts. `pending` is the system working, and
// `couldFill` is an offer rather than a fault — saying either is outstanding
// is how a panel stops being read.
//
// **A conflict counts.** Both sources answered, they disagree, and the answer
// that lost is not visible anywhere on the page — so a green "Nothing
// outstanding" over one would hide the single thing the middleware went out of
// its way to report.
//
// **A check counts too, and it is not a fault.** "The property bought is the
// property sold" is the ordinary case, so a title the transfer names and
// nothing was ordered for is a question rather than an omission — but a
// question nobody is shown is not asked at all, and behind a green tick this
// one would never be seen. The badge says "to look at", which is exactly what
// it is. The middleware's own `ok` stays blind to it, so nothing gates a
// compile on a question.
export const reviewProblemCount = (review) => {
  if (!review) return 0;
  const { missing = [], unread = [], flags = [], conflicts = [], checks = [] } = review;
  return missing.length + unread.length + flags.length + conflicts.length + checks.length;
};

// Why a certificate is on the not-ordered list.
//
// Three different reasons, and only one of them is "every contract needs this".
//
// **`because` comes first, and without it this line was wrong.** A certificate
// the middleware chases conditionally — the building certificate when the
// vendor has said a permit was issued, the insurance certificate on a 137B
// matter — has no `answers`, because it is not being chased to fill a blank
// field. It fell through to "Required on every matter", which is false of both
// of them, and the `because` text explaining what the client actually
// instructed was dropped on the floor.
export const missingReason = (entry, labelFor = (n) => n) => {
  if (entry?.because) return entry.because;
  if (entry?.answers?.length) return `Would answer: ${entry.answers.map(labelFor).join(", ")}`;
  return "Required on every matter";
};

// One disagreement, and the certificate's own words behind a toggle.
//
// **A section 51 certificate's answer is a screenful.** 1760432's runs to four
// permits, two occupancy certificates and a demolition consent, every reference
// and surveyor spelled out — and printed whole in a review card it buries the
// one thing being asked, which is whether the vendor's answer and the
// certificate's agree. What a reader wants first is a sentence saying where it
// came from and a way to open the document.
//
// **Nothing is summarised, only shortened.** A synthesised summary of a
// statutory disclosure is a claim about a legal document that nobody wrote, and
// on this screen the two answers are being weighed against each other — so the
// full text stays one click away and the first line is the certificate's own.
const CONFLICT_PREVIEW_CHARS = 180;

// A small flat button, for an action that is not navigation.
const PillButton = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      font: "inherit", fontSize: 11, color: T.textSoft, background: T.white,
      border: `1px solid ${T.border}`, borderRadius: 999, padding: "3px 10px",
      cursor: "pointer", lineHeight: 1.4,
    }}
  >
    {children}
  </button>
);

const YesNo = ({ label, yes, detail }) => (
  <div style={{ display: "flex", gap: 6, fontSize: 12, lineHeight: 1.5 }}>
    <span style={{ color: T.textSoft }}>{label}:</span>
    <span style={{ color: T.text, fontWeight: 500 }}>{yes ? "Yes" : "No"}</span>
    {yes && detail ? <span style={{ color: T.textSoft }}>— {detail}</span> : null}
  </div>
);

// One disagreement, with the certificate reported rather than reproduced.
//
// **The wall of text was unusable and shortening it was worse.** Cutting at
// 180 characters landed on the council's *"No discoveries have been made."*
// and read as though no permit had been found, on a certificate where a
// private surveyor had issued two — a shortened legal disclosure is wrong
// rather than merely unread.
//
// So the middleware reads the certificate's own headings and reports which
// sections have content, and this shows those lines. Nothing interprets what
// the document means; the full text is a click away and the certificate itself
// another. A certificate whose headings the middleware could not recognise
// sends no summary at all, and the text is shown as before — saying "Permits
// found: No" about a document nobody could parse is the one answer that would
// matter here.
const ConflictCard = ({ c, first, onView, canView }) => {
  const [full, setFull] = useState(false);
  const value = String(c.value ?? "");
  const summary = c.summary && c.summary.parsed ? c.summary : null;
  // With a summary the text is supporting evidence, so it starts hidden
  // outright rather than truncated — a cut sentence is what caused this.
  const long = !summary && value.length > CONFLICT_PREVIEW_CHARS;
  const shown = full || !long ? value : `${value.slice(0, CONFLICT_PREVIEW_CHARS).trimEnd()}…`;

  return (
    <div style={{ padding: "10px 14px", borderTop: first ? "none" : `1px solid ${T.border}` }}>
      <div style={{ fontSize: 12, color: T.text, fontWeight: 500 }}>{c.title}</div>
      <div style={{ fontSize: 11, color: T.textFaint, marginTop: 4, lineHeight: 1.45 }}>{c.detail}</div>

      <div style={{ fontSize: 12, color: T.text, marginTop: 8, lineHeight: 1.5 }}>
        {/* The document, named. It is not "on the contract" — section 5 prints
            the firm's standard wording either way, so calling this the
            contract's answer was wrong about the page as well as wordy. */}
        <div style={{ fontWeight: 600 }}>
          From {c.documentName || sourceName(c.from)}
        </div>

        {summary ? (
          <div style={{ marginTop: 4 }}>
            <YesNo label="Permits found" yes={summary.permitsFound}
              detail={(summary.works || []).join("; ")} />
            <YesNo label="Final/occupancy permit issued" yes={summary.finalOrOccupancy} />
            <YesNo label="Notices/orders" yes={summary.noticesOrOrders} />
            <YesNo label="Demolition consent" yes={summary.demolitionConsent} />
          </div>
        ) : (
          <div style={{ marginTop: 4, whiteSpace: "pre-line" }}>{shown}</div>
        )}

        {summary && full ? (
          <div style={{ marginTop: 6, whiteSpace: "pre-line", color: T.textSoft }}>{value}</div>
        ) : null}

        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          {summary || long ? (
            <PillButton onClick={() => setFull(v => !v)}>
              {full ? "Hide the full text" : "Show the full text"}
            </PillButton>
          ) : null}
          {canView ? <PillButton onClick={onView}>View the certificate</PillButton> : null}
        </div>

        <div style={{ color: T.textSoft, marginTop: 10 }}>
          <strong>The vendor said</strong> — {String(c.alternative)}
        </div>
      </div>
    </div>
  );
};

export const SearchesReview = ({ review, registry, defaultOpen, stage, matterRef, searches = [] }) => {
  // Its own preview state rather than the searches list's. The two panels are
  // siblings, so hoisting one modal to serve both would mean threading it
  // through the matter view for the sake of one button — and it is the same
  // component either way.
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useStagedSection(defaultOpen, stage);
  const [copied, setCopied] = useState("");
  // `navigator.clipboard` throws on an insecure origin and where the browser
  // refuses permission. The SPI is on screen either way, so the failure is
  // recoverable by hand — but it has to *say* so: a Copy button that silently
  // does nothing gets pressed again, and then the wrong parcel gets pasted
  // from whatever was on the clipboard before.
  const [copyFailed, setCopyFailed] = useState("");

  const copySpi = async (spi) => {
    if (await copyText(spi)) {
      setCopyFailed("");
      setCopied(spi);
      setTimeout(() => setCopied(c => (c === spi ? "" : c)), 1500);
      return;
    }
    setCopied("");
    setCopyFailed(spi);
  };

  if (!review) return null;

  const { missing = [], pending = [], couldFill = [], unread = [], flags = [],
    conflicts = [], checks = [], hasExtraction, parcelReports = {} } = review;
  const parcels = parcelReports.parcels || [];
  const reportUnparsed = parcelReports.unparsed || [];
  // Whether the map already answered the scheme and the zone — the middleware's
  // call, since it is the one that resolved the fields.
  const planningAnswered = parcelReports.planningAnswered === true;
  const searchUrl = parcelReports.searchUrl || "";
  const labelFor = (name) => registry?.find(f => f.name === name)?.label || name;

  // Flags count as things to look at, but not as gaps in the statement — a
  // pool certificate is work on the file, and the contract is complete without
  // it. `ok` from the middleware is deliberately blind to them for that reason.
  const problems = reviewProblemCount(review);
  const clean = problems === 0;

  // **A section is the card; a finding is a line inside it.** It was the other
  // way round — every finding was its own little bordered box with nothing
  // containing it — so eight groups came out as a run of thirty loose boxes
  // with headings floating between them, and which finding belonged to which
  // heading had to be worked out from the gaps.
  //
  // **The headings carry no colour.** They were red, blue and amber by group,
  // which on a matter with several groups painted the panel in four accents
  // that meant nothing to each other: blue was doing duty for both "for the
  // file handler" and "worth confirming", and red for both a contradiction and
  // an absence. The order says what is urgent — conflicts first, then what was
  // never ordered — and the count says how much of it there is.
  /* The heading is ink, not the faint grey every other micro-label uses. These
     are what somebody scans the panel by — *Not ordered* is a certificate
     nobody has ordered — and at #8A8A8A they read as a caption on the note
     below them rather than as the thing the card is. The count stays faint:
     it qualifies the heading, it isn't what you are looking for. */
  //
  // **No note under the heading.** *"Not ordered / Nothing ordered, so nothing
  // is coming"* is the heading said twice, and a paragraph under every one of
  // six groups is a wall to scroll past on the panel whose whole job is to be
  // scanned. The headings carry it. Fetch by hand keeps its copy, and is not a
  // Group — it is the only one that is work to do rather than a finding.
  const Group = ({ title, items, render }) => items.length === 0 ? null : (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, marginTop: 10, overflow: "hidden", background: T.white }}>
      <div style={{ padding: "9px 14px 9px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.text, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {title}
          </span>
          <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 500 }}>{items.length}</span>
        </div>
      </div>
      <div>{items.map(render)}</div>
    </div>
  );

  // `i` is the item's index, so only the rows after the first draw a divider —
  // the card already has its own edge.
  const row = (key, main, sub, i = 0) => (
    <div key={key} style={{ padding: "10px 14px", borderTop: i > 0 ? `1px solid ${T.border}` : "none" }}>
      <div style={{ fontSize: 12, color: T.text }}>{main}</div>
      {sub && <div style={{ fontSize: 11, color: T.textFaint, marginTop: 2, lineHeight: 1.45 }}>{sub}</div>}
    </div>
  );

  // **"No gaps found", not "Nothing outstanding".** The search list directly
  // below uses *outstanding* to mean a search that has not come back, so this
  // badge saying "Nothing outstanding" beside a row reading *Ordered* — under a
  // header reading *10 of 11* — was one word carrying two meanings on one
  // screen, and the more visible one was wrong.
  //
  // **Rewording rather than recounting** is the deliberate half. An ordered
  // search is the system working, and `pending` is kept out of
  // `reviewProblemCount` for exactly that reason: counting it would light this
  // panel red on every matter for the first fortnight of its life, which is how
  // a review stops being read. What was wrong was the claim, not the
  // arithmetic — so the badge says what it actually measured, which is that the
  // review found no gaps in the statement.
  return (
    <div data-tour="searches-review" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <SectionHeader
        open={open}
        onToggle={() => setOpen(o => !o)}
        title="Searches review"
        meta={clean ? (
          <span style={{ fontSize: 11, color: "#2a8a52", background: "rgba(42,138,82,0.1)", padding: "2px 8px", borderRadius: 999, fontWeight: 500 }}>
            ✓ No gaps found
          </span>
        ) : (
          <span style={{ fontSize: 11, color: T.textSoft, background: T.bgAlt, padding: "2px 8px", borderRadius: 999, fontWeight: 500 }}>
            {problems} to look at
          </span>
        )}
      />

      {!open && !clean && (
        <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginTop: 10 }}>
          {conflicts.length > 0
            ? `${conflicts.length === 1 ? "A certificate contradicts" : `${conflicts.length} certificates contradict`} what the vendor said.`
            : missing.length > 0
            ? `${missing.length} certificate${missing.length !== 1 ? "s" : ""} every contract needs ${missing.length !== 1 ? "have" : "has"} not been ordered.`
            : unread.length > 0
              ? "Some certificates are in the pack but didn't answer what they should."
              : checks.length > 0 && flags.length === 0
                ? checks.map(c => c.title).join(", ")
                : flags.map(f => f.title).join(", ")}
        </div>
      )}

      {open && (
        <div>
          {/* First, because it is the only group where the statement is about
              to say something a document on the file contradicts. Everything
              else here is an absence; this is a disagreement, and the answer
              that lost is not visible anywhere on the page. */}
          <Group
            title="Two answers, and they disagree"
            items={conflicts}
            render={(c, i) => {
              // The middleware names the document; the row is what turns it
              // into a link, and there may not be one — a document deleted
              // since the read, or two of one name, which it refuses to guess
              // between.
              const row = searches.find(s => s.id === c.documentId);
              return (
                <ConflictCard
                  key={c.field}
                  c={c}
                  first={i === 0}
                  canView={Boolean(row && documentUrl(matterRef, row))}
                  onView={() => setPreview({
                    title: row.displayName || row.name,
                    ...previewUrlFor(matterRef, row),
                  })}
                />
              );
            }}
          />

          {/* Nothing else in the system will ever chase these — they answer no
              merge field, so no completeness check can miss them. */}
          <Group
            title="For the file handler"
            items={flags}
            render={(f, i) => (OWNER_BUILDER_FLAGS.includes(f.field)
              ? (
                <div key={f.field}>
                  {row(f.field, f.title, f.detail, i)}
                  <div style={{ padding: "0 14px 10px" }}>
                    <Btn variant="ghost" onClick={open137bPanel}>Ask the client for the 137B report</Btn>
                  </div>
                </div>
              )
              : row(f.field, f.title, f.detail, i))}
          />

          <Group
            title="Not ordered"
            items={missing}
            render={(m, i) => row(m.certificate, m.certificate, missingReason(m, labelFor), i)}
          />

          {/* A question, not a gap — hence its own group and its own wording.
              A vendor may have subdivided, or sold part of the land since, so
              reported as "you forgot to order this" the first legitimate
              part-sale teaches staff to ignore it. */}
          <Group
            title="Worth confirming"
            items={checks}
            render={(c, i) => row(c.kind, c.title, c.detail, i)}
          />

          <Group
            title="Read, but not found"
            items={unread}
            render={(u, i) => row(u.field, labelFor(u.field), `Expected from the ${u.certificates.join(" or ")}`, i)}
          />

          <Group
            title="On its way"
            items={pending}
            render={(p, i) => row(p.certificate, p.certificate,
              p.answers.length > 0 ? `Will answer: ${p.answers.map(labelFor).join(", ")}` : null, i)}
          />

          <Group
            title="Might be needed"
            items={couldFill}
            render={(c, i) => row(c.certificate, c.certificate,
              `Would answer: ${c.answers.map(labelFor).join(", ")}`, i)}
          />

          {/* ── The report nobody orders ───────────────────────────────────
              Every other line on this panel is a finding — something absent,
              waiting or contradicted. This is the opposite: the work itself,
              which staff do by hand because the planning property report is
              free and does not go through Trisearch.

              **There is no link to the report, and that was checked.** Its
              `property` parameter is the SPI *and* a mandatory opaque token, so
              a per-parcel link cannot be built — the SPI alone redirects to the
              search form. A link that resolved to a search form would be worse
              than none: it looks like it worked, and picking the parcel is the
              whole job.

              So this gives the thing staff were deriving by eye. They open the
              search and type a parcel; the title already says which parcel, and
              a matter of seven titles is seven of them behind one address.

              **Outside `reviewProblemCount`**, deliberately. Counting it would
              put a number on a panel that means "things are wrong" for
              something merely available, and a green "Nothing outstanding" has
              to be able to sit above it. */}
          {/* **Shown whether or not a parcel identifier could be derived.** It
              used to appear only once the title had been read and a lot on plan
              parsed out of it, which took the section off the screen on exactly
              the matters that need it most: no title read means nothing has
              answered the bushfire question either. 1760366 is the matter that
              made that visible. Searching by address is worse than searching by
              parcel and a great deal better than not knowing where to go. */}
          {searchUrl && (
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, marginTop: 10, overflow: "hidden", background: T.white }}>
              <div style={{ padding: "10px 14px 11px", borderBottom: (parcels.length + reportUnparsed.length) > 0 ? `1px solid ${T.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Fetch by hand
                </span>
                {(parcels.length + reportUnparsed.length) > 0 && (
                  <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 500 }}>{parcels.length + reportUnparsed.length}</span>
                )}
              </div>
              {/* **Two different jobs, so two different sentences.**
                  With the zone and the scheme already answered from the map,
                  the report is optional and the identifier is the good search
                  key. Without them it is the thing to go and get — and the
                  *address* is the better key then, because a lookup that
                  failed is often our own parcel identifier meeting a title
                  format it does not handle, so offering only the identifier
                  would send somebody back to the thing that just failed. */}
              <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginTop: 4 }}>
                {planningAnswered ? (<>
                  The zone, overlays and bushfire answer come from Vicmap and fill the section 32 — the planning property report is optional.
                </>) : (<>
                  Order the planning property report using the address
                  {parcels.length > 0 ? " or the parcel identifier below" : ""}.
                </>)}
              </div>
              </div>
              <div>
                {parcels.map((p, i) => (
                  <div
                    key={p.spi}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "10px 14px", borderTop: i > 0 ? `1px solid ${T.border}` : "none",
                    }}
                  >
                    <span style={{ fontSize: 12, color: T.text, fontFamily: "ui-monospace, Menlo, monospace" }}>
                      {p.spi}
                    </span>
                    <button
                      type="button"
                      onClick={() => copySpi(p.spi)}
                      style={{
                        background: "none", border: "none", cursor: "pointer", padding: 0,
                        fontSize: 11, color: copied === p.spi ? "#2a8a52" : T.textSoft, fontFamily: "inherit",
                        textDecoration: "underline", textUnderlineOffset: 2,
                      }}
                    >
                      {copied === p.spi ? "Copied" : copyFailed === p.spi ? "Select and copy" : "Copy"}
                    </button>
                  </div>
                ))}
                {/* Named rather than skipped. A Crown allotment writes its parcel
                    reference a different way, and showing three parcels for four
                    titles with nothing saying so reads as "this property is three
                    parcels" — a claim about the land, not about the parser. */}
                {reportUnparsed.map((u, i) => row(u, u,
                  "No parcel reference could be read from this one — search the address by hand.",
                  parcels.length + i))}
                {searchUrl && (
                  <a
                    href={searchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: "block", padding: "10px 14px", borderTop: `1px solid ${T.border}`, fontSize: 12, color: T.text, textDecoration: "underline", textUnderlineOffset: 2 }}
                  >
                    Open the property and parcel search &#8599;
                  </a>
                )}
              </div>
            </div>
          )}

          {/* No line for a matter with no parcels. It said they would appear once
              the title came back, which stopped being true when Fetch by hand
              began going away once the planning report is filed — and where the
              section is shown, it already says to search by address. */}

          {!hasExtraction && (
            <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
              The certificates haven&rsquo;t been read yet, so anything blank below is blank because nobody has looked.
            </div>
          )}

          {/* **Only once something has actually been read.** `unread` is empty
              before an extraction has run — that is deliberate, since every
              blank is then blank for the ordinary reason — but "nothing
              readable was left blank" reads it as a finding, so the panel
              stated the certificates had not been read and then, four lines
              later, that they had come back clean. An absence of evidence
              printed as evidence of absence. */}
          {clean && hasExtraction && missing.length === 0 && unread.length === 0 && (
            <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginTop: 12 }}>
              Searches appear in order.
            </div>
          )}
        </div>
      )}
      {preview && <PdfPreviewModal {...preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

// ── Received, but on no search ─────────────────────────────────────
//
// A return Zap 2 could not put against a search is banked, and a banked return
// **blocks the matter's completion and its compile**. Until now the only trace
// was a staff-only timeline line, so the matter simply stopped moving with
// nothing on screen saying why.
//
// Trisearch never lists the owners corporation basic and premium reports at
// all, so this is not an edge case — it is every matter with an owners
// corporation. The middleware places them by itself two hours after the import
// goes quiet; this is for staff who can see the document now and know where it
// belongs.
// **Filing a document is not reading one.** Placing a return marks its search
// returned, and that starts a per-document read — so the panel, disabled while
// anything was being read, disabled *itself* on the first placement and the
// remaining returns could not be filed until the read finished. A matter with
// six of them was six waits.
//
// Nothing about the middleware required that. Each read is claimed and
// fingerprinted on its own row, so placing the next document simply queues the
// next read beside it. Only a compile blocks: that one is reading the merge
// list, and adding to the pack underneath it is a genuine conflict.
//
// The row being filed disables itself, which is what the shared flag was
// really doing for the one case it was right about.
const UnmatchedReturns = ({ matter, returns = [], onPlace, onAdd, onDismiss, busy }) => {
  const [bankedPreview, setBankedPreview] = useState(null);
  const [choice, setChoice] = useState({});
  const [filing, setFiling] = useState([]);
  if (returns.length === 0) return null;

  const file = async (id, run) => {
    if (filing.includes(id)) return;
    setFiling(f => [...f, id]);
    try { await run(); } finally { setFiling(f => f.filter(x => x !== id)); }
  };

  // Anything still outstanding is a candidate. A settled search is not offered:
  // placing a document on one would overwrite what is already recorded there.
  const candidates = (matter.searches || []).filter(s => s.status === "Ordered");

  return (
    <div style={{ background: "rgba(196,122,26,0.06)", border: "1px solid rgba(196,122,26,0.3)", borderRadius: 8, padding: 12, marginTop: 8 }}>
      {bankedPreview && <PdfPreviewModal {...bankedPreview} onClose={() => setBankedPreview(null)} />}
      <div style={{ fontSize: 12, fontWeight: 500, color: "#c47a1a", marginBottom: 4 }}>
        {returns.length} document{returns.length !== 1 ? "s" : ""} received that {returns.length !== 1 ? "aren't" : "isn't"} against a search
      </div>
      <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginBottom: 10 }}>
        These came back by email and matched no search, so they are not in the statement and the matter is not complete. Place one against the search it belongs to, or add it as a document of its own.
      </div>

      {returns.map(r => (
        <div key={r.id} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: T.text, flex: 1, minWidth: 160 }}>{r.rawDescription || "(no description)"}</span>
            {r.driveFileUrl && (
              <button
                onClick={() => setBankedPreview({
                  title: r.rawDescription || "Banked return",
                  url: r.driveFileUrl,
                  embed: drivePreviewUrl(r.driveFileUrl) || "",
                })}
                style={underlineLink(false)}
              >View PDF</button>
            )}
          </div>
          {/* Named rather than left as a mystery: a refusal is a deliberate
              decision the middleware made, and staff are entitled to see it. */}
          {r.refusedAgainst && (
            <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 8, lineHeight: 1.5 }}>
              Not recorded against <strong>{r.refusedAgainst}</strong> — a report and a certificate are different documents.
            </div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={choice[r.id] || ""}
              onChange={e => setChoice(c => ({ ...c, [r.id]: e.target.value }))}
              disabled={busy || filing.includes(r.id) || candidates.length === 0}
              style={{ fontSize: 12, padding: "5px 8px", border: `1px solid ${T.border}`, borderRadius: 6, fontFamily: "inherit", color: T.text, background: T.white, maxWidth: 260 }}
            >
              <option value="">
                {candidates.length === 0 ? "No search is still outstanding" : "Place against a search…"}
              </option>
              {candidates.map(s => <option key={s.id} value={s.id}>{s.displayName || s.name}</option>)}
            </select>
            <Btn size="sm" variant="ghost" disabled={busy || filing.includes(r.id) || !choice[r.id]}
              onClick={() => file(r.id, () => onPlace(r.id, choice[r.id]))}>
              {filing.includes(r.id) ? "Placing…" : "Place"}
            </Btn>
            <span style={{ fontSize: 11, color: T.textFaint }}>or</span>
            <Btn size="sm" variant="ghost" disabled={busy || filing.includes(r.id) || !r.rawDescription}
              onClick={() => file(r.id, () => onAdd(r.id))}>
              {filing.includes(r.id) ? "Adding…" : "Add as a document"}
            </Btn>
            {/* **The third answer, and until now there was no way to give it.**
                Place needs a search still outstanding; Add binds the document
                into the statement, and an identity form must never be bound
                into a Section 32. A return that is neither held the matter
                open for ever. It is set aside, not deleted — the row is the
                record that the firm received it. */}
            <button
              type="button"
              disabled={busy || filing.includes(r.id)}
              onClick={() => {
                if (!window.confirm(`Set "${r.rawDescription || "this document"}" aside?\n\n`
                  + "It stops holding this matter and stays on the record. Use this for "
                  + "something that is not for this matter at all — an identity form, an "
                  + "account line — which must not go into the statement.")) return;
                file(r.id, () => onDismiss(r.id));
              }}
              style={{ ...underlineLink(false), fontSize: 11, color: T.textFaint, marginLeft: "auto" }}
            >Not for this matter</button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Searches & compilation ─────────────────────────────────────────
//
// "Searches" undersold this. The list is what the compile merges, in the order
// it merges them — the certificates *and* the generated contract that leads
// them. Naming it for the searches alone is what let the document at the head
// of the merge go unmentioned entirely.
//
// Collapsible like the rest, and closed by default once a matter is compiled:
// at that point the certificates are settled and the document is the thing.
// The summary stays in the heading, so a closed panel still answers the only
// question a closed panel gets asked — how many are back.
// The other way in: forward it to the search inbox.
//
// Staff forward a certificate, a plan or a letter to the inbox with the
// matter's reference in the subject, and the middleware's
// `POST /emailed-document` files it on this list — attached to the ordered
// search it plainly is, or added as a document of its own. A way in nobody
// knows about is one nobody uses, so it is said where the other way in is.
//
// **The address is the middleware's**, `tenant.searchInbox`, and absent on a
// client deployment: telling another firm's staff to email Provey would send
// their clients' documents to somebody else's inbox. Absent is silence, which
// is also what an older middleware gets.
//
// **The reference is printed rather than described**, because it is the one
// part people get wrong, and the link opens a message already carrying it.
export const SearchInboxNote = ({ inbox, matterRef }) => {
  if (!inbox) return null;
  const href = `mailto:${inbox}${matterRef ? `?subject=${encodeURIComponent(String(matterRef))}` : ""}`;
  return (
    <div style={{ fontSize: 11, color: T.textFaint, lineHeight: 1.5, margin: "6px 2px 0" }}>
      Or email documents to{" "}
      <a href={href} style={{ color: T.textSoft, fontWeight: 500 }}>{inbox}</a>
      {matterRef
        ? <> with <strong style={{ color: T.textSoft, fontWeight: 600 }}>{matterRef}</strong> in the subject</>
        : " with the matter reference in the subject"}
      {" "}— forward the email and its attachments are added to this list automatically.
    </div>
  );
};

const SearchesPanel = ({
  matter, searches, setSearches, searchesLoading, allDone, busy, busyLabel, merging,
  defaultOpen, stage, onArrangeDocuments, onSearchStatusChange,
  onAddDocument, onDeleteDocument, onReplaceDocument, onRenameDocument, onReorderSearches, uploading,
  onPlaceReturn, onAddReturn, onDismissReturn, searchInbox, revealSignal = 0, onOrderSearches, onExcludeSearch, orderSearchesSoon = false,
}) => {
  const [open, setOpen] = useStagedSection(defaultOpen, stage);
  const rootRef = useRef(null);
  // **View lands on the rows that are not back, and lights them.** Scrolling
  // to the panel alone did nothing visible when the panel was already on
  // screen — the ordinary case when Searches is the tab somebody is on — and a
  // press that changes nothing reads as broken. Two frames, because the panel
  // may have been shut and its rows do not exist until it has rendered open.
  const [flashing, setFlashing] = useState(false);
  useEffect(() => {
    if (!revealSignal) return;
    setOpen(true);
    setFlashing(true);
    let raf2;
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => {
      const root = rootRef.current;
      const target = root?.querySelector?.("[data-not-back]") || root;
      if (typeof target?.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: target === root ? "start" : "center" });
      }
    }); });
    const off = setTimeout(() => setFlashing(false), 1600);
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); clearTimeout(off); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealSignal]);

  const progress = allDone
    ? "✓ All complete"
    : searches.length > 0
      ? `${searches.filter(s => s.status === "Returned" || s.status === "Not Required").length} of ${searches.length} complete`
      : `${matter.returnedCount || 0} of ${matter.searchCount || 0} complete`;

  const generated = generatedDocumentState(matter);

  // Shown in merge order, not arrival order — this list is what the compile
  // produces, so it has to read like it.
  const ordered = useMemo(
    () => orderSearchesForDisplay(searches, matter.compileOrder),
    [searches, matter.compileOrder],
  );

  return (
    <div ref={rootRef} data-tour="searches" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12, scrollMarginTop: UNDER_BAR_CLEAR }}>
      <SectionHeader
        open={open}
        onToggle={() => setOpen(o => !o)}
        title="Searches & compilation"
        meta={
          <>
            {/* Belt and braces with the scroll: whatever is happening is
                readable from the panel that started it, without looking away. */}
            {busyLabel && (
              <span style={{ fontSize: 11, color: "#1a7ac4", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#1a7ac4", display: "inline-block", animation: "s32-pulse 2.4s ease-in-out infinite" }} />
                {busyLabel}
              </span>
            )}
            <span style={{ fontSize: 11, color: allDone ? "#2a8a52" : T.textFaint, fontWeight: 400 }}>{progress}</span>
            {/* Carried into the heading so a closed panel doesn't hide the one
                flag that says the statement isn't finished. */}
            {!generated.complete && (
              <span style={{ fontSize: 11, color: "#c47a1a", fontWeight: 400 }}>· contract incomplete</span>
            )}
          </>
        }
        actions={
          /* Reordering is the rare case, so it lives here with the documents
             rather than behind the button people press to rebuild. */
          /* **Ordering lives with the searches it adds to**, in the practice
             look: one Searches view, and its own Order searches button —
             greyed until the order form is ready. */
          orderSearchesSoon ? (
            <Btn variant="ghost" size="sm" disabled title="Coming soon">Order searches</Btn>
          ) : open && onArrangeDocuments ? (
            <Btn variant="ghost" size="sm" onClick={onArrangeDocuments} disabled={busy}>
              {ARRANGE_LABEL}
            </Btn>
          ) : null
        }
      />

      {open && (
        <div style={{ marginTop: 12 }}>
          {/* The head of the merge, shown because it is otherwise invisible:
              the contract is generated at compile time, so it is in no list
              until after the PDF exists. */}
          <div style={{ marginBottom: 4 }}>
            <GeneratedDocumentRow matter={matter} />
          </div>

          {searchesLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ height: 38, background: T.bgAlt, border: `1px solid ${T.border}`, borderRadius: 8, opacity: 0.7 - i * 0.18 }} />
              ))}
            </div>
          ) : (
            <SearchList
              searches={ordered}
              setSearches={setSearches}
              matterRef={matter.ref}
              onDelete={(doc) => onDeleteDocument?.(matter.ref, doc)}
              onPersistStatus={(searchId, status) => onSearchStatusChange?.(matter.ref, searchId, status)}
              onReplace={(doc, file, driveFileUrl, onProgress) => onReplaceDocument?.(matter.ref, doc, file, driveFileUrl, onProgress)}
              onRename={(doc, name) => onRenameDocument?.(matter.ref, doc, name)}
              onReorder={(order) => onReorderSearches?.(matter.ref, order)}
              onExclude={onExcludeSearch ? (doc, out) => onExcludeSearch(matter.ref, doc, out) : undefined}
              compileOrder={matter.compileOrder || []}
              compileExcluded={matter.compileExcluded || []}
              flashing={flashing}
            />
          )}

          <UnmatchedReturns
            matter={matter}
            returns={matter.unmatchedReturns || []}
            busy={merging}
            onDismiss={(returnId) => onDismissReturn?.(matter.ref, returnId)}
            onPlace={(returnId, searchId) => onPlaceReturn?.(matter.ref, returnId, searchId)}
            onAdd={(returnId) => onAddReturn?.(matter.ref, returnId)}
          />

          {!searchesLoading && searches.length > 1 && (
            <div style={{ fontSize: 11, color: T.textFaint, lineHeight: 1.5, margin: "6px 2px 0" }}>
              Drag a row, or use its arrows, to change the order they are bound in — saved as you go.
            </div>
          )}

          <div style={{ marginTop: 4 }}>
            <AddDocument
              onAdd={(file, name, onProgress, link) => onAddDocument(matter.ref, file, name, onProgress, link)}
              uploading={uploading}
              disabled={busy}
            />
            <SearchInboxNote inbox={searchInbox} matterRef={matter.ref ?? matter.id} />
            {/* Searches ordered from the order form are never in the nightly
                import, so they are ordered from here and land in this list. */}
            {onOrderSearches && (
              <MatterOrders api={apiFetch} matterRef={matter.ref ?? matter.id} onOrder={() => onOrderSearches(matter)}
                soon={!featureReady("orderSearches")} />
            )}
          </div>

        </div>
      )}
    </div>
  );
};

// ── Contract data ──────────────────────────────────────────────────
// What the section 32 and contract will be built from, where each value came
// from, and what's still blank.
//
// This panel is the data-entry form. Before it, the only way to fill the
// fields no certificate states was Form 27 → Smokeball → Form 265 → middleware
// — three hops whose only value-add was a screen to type into. This is that
// screen, and it works for a firm with no Smokeball at all.
//
// The registry (labels, types, grouping, what's required) is fetched from the
// middleware rather than duplicated here. Two copies of those rules would
// drift, and staff would be editing a form that doesn't match the document
// they get — the same reasoning as the compile order.

// Named and ordered the way Triconvey groups a matter, because that is the
// screen staff have open beside this one. The middleware decides which section
// a field is in (see GROUP_OF there) — this file only says what each is called
// and what order they read in.
const GROUP_LABELS = {
  // Triconvey calls this row Info and puts the matter's own details in it.
  // Ours holds the acting conveyancer, and the part of that worth reading at a
  // glance is who is handling the file — not the firm's name, which is the
  // same on every matter.
  firm: "Info",
  vendor: "Vendor",
  purchaser: "Purchaser",
  purchaserSolicitor: "Purchaser's solicitor",
  property: "Property and title",
  // **Its own section now.** It shared the property one on the reasoning that
  // the land and what must be disclosed about it are a single thing to check —
  // true of a handful of them and false of fifty-odd, since the GAIC
  // recordings, seven services and the owners corporation answers buried the
  // four fields that identify the property.
  s32: "Section 32 disclosures",
  conveyancing: "Conveyancing details",
  agent: "Vendor's agent",
  buyerAgent: "Buyer's agent",
};

// The order the panel reads in. It is a preference, not the list of groups —
// `sale` was missing from it for months and every field in that group, margin
// scheme and the GST treatment among them, was silently absent from the panel.
// A group the middleware sends and this file has never heard of now goes to
// the end rather than nowhere, which is the same rule as an unknown search
// landing at the end of a compile instead of being dropped.
const GROUP_ORDER = ["firm", "vendor", "property", "s32", "conveyancing", "agent",
                    "purchaser", "purchaserSolicitor", "buyerAgent"];

// Which sections start open.
//
// The ones that start closed are the ones nobody opens on an ordinary matter:
// the acting solicitor is the same firm every time, the conveyancing terms are
// agreed at sale rather than at issue, and the buyer's side is blank until
// there is a buyer. What is left open is the work — who is selling, what is
// being sold, and the agent the notices are served on.
//
// A section this file has never heard of starts open. An unknown group is
// unexpected content, and hiding it is how it would go unnoticed.
const GROUP_OPEN = {
  firm: false,
  vendor: true,
  property: true,
  // Open, like the property: the disclosures are the work on most matters, and
  // splitting them out only to hide them would be a worse screen than the one
  // this replaces.
  s32: true,
  conveyancing: false,
  agent: true,
  purchaser: false,
  purchaserSolicitor: false,
  buyerAgent: false,
};
export const groupStartsOpen = (group) => GROUP_OPEN[group] ?? true;

// What a collapsed section shows beside its name.
//
// Triconvey's matter card puts the answer next to the heading — "Vendor ·
// Roslyn Rae Crawley", "Property Details · 1307/231 Harbour Esplanade" — so a
// closed section still tells you what is in it. A heading that only said "20
// fields" makes you open it to learn anything.
//
// One or two fields each, and only the ones that identify the section. A
// summary long enough to wrap stops being a summary.
const SECTION_SUMMARY = {
  // The file handler, not "Provey Conveyancing" — the firm is the same on
  // every matter, so it says nothing at a glance.
  firm: ["firm.contactName"],
  vendor: ["vendor.legalNames"],
  property: [
    "property.address",
    // Labelled, because "Volume 11450 Folio 392" beside an address is only
    // obviously a title reference once it says so.
    { name: "property.titleParticulars", prefix: "Title Reference:" },
    "property.lotOnPlan",
  ],
  conveyancing: ["sale.price", "sale.settlementDate"],
  agent: ["agent.name", "agent.contactName"],
  purchaser: ["purchaser.legalNames"],
  purchaserSolicitor: ["purchaserSolicitor.name"],
  buyerAgent: ["buyerAgent.name"],
};

// A value short enough to sit on a heading.
//
// **A flag is deliberately not summarised**: a tick means nothing without its
// question, and the question is the part that does not fit. Anything unfilled
// is skipped rather than shown as a gap.
//
// **A list is summarised, joined.** It has to be: the title reference is
// `property.titleParticulars`, which the registry holds as a list because a
// property can be on more than one title — so refusing lists here meant the
// one value the property summary was asked for could never appear, while a
// test declaring it `text` passed. Two titles read as "Volume 1 Folio 2;
// Volume 3 Folio 4", which is longer than one and still the thing you wanted
// to see without opening the section.
export const summaryValue = (field, value) => {
  if (!field || value === null || value === undefined || value === "") return null;
  if (field.type === "flag") return null;
  const text = (Array.isArray(value) ? value : [value])
    .map(v => String(v ?? "").trim())
    .filter(Boolean)
    .join("; ");
  return text === "" ? null : text;
};

export const sectionSummary = (group, registry, values) => {
  const byName = new Map((registry || []).map(f => [f.name, f]));
  return (SECTION_SUMMARY[group] || [])
    .map((entry) => {
      const { name, prefix } = typeof entry === "string" ? { name: entry } : entry;
      const text = summaryValue(byName.get(name), (values || {})[name]);
      return text === null ? null : (prefix ? `${prefix} ${text}` : text);
    })
    .filter(Boolean);
};

// The sections the panel shows, in reading order.
//
// **Every section the registry has, always.** Building this from the visible
// fields and dropping the empty ones meant a section could only appear if it
// held a required field nobody had filled — so Conveyancing details and the
// vendor's agent, which have no required fields at all, could never appear,
// and the acting solicitor vanished as soon as its three were set.
//
// The sections are a structure staff navigate, not a list of outstanding work.
// What the filter decides is what is *inside* one.
// A section's fields, gathered under their subheadings.
//
// **Subheadings rather than a second level of collapsing.** The section 32
// group is fifty-odd fields and reads as one undifferentiated run without
// them — what somebody wants is to find the owners corporation answers among
// the GAIC recordings, not another thing to open.
//
// The labels are the middleware's (`section` on each field), like everything
// else this panel renders. A group whose fields carry none comes back as one
// unlabelled run, which is every group but the disclosures.
//
// **Gathered, not merely broken at each change** — and the first version did
// the latter, on the reasoning that the registry's order is the order the
// statement prints in, so a subheading would appear where its fields do. That
// is not what the registry's order is: it is declaration order, and the
// disclosures interleave. Twelve sections came out as **twenty-two
// headings** — Financial matters four times, GAIC twice — which is worse than
// no subheadings at all, because a repeated heading reads as a second, unequal
// thing of the same name.
//
// So each section appears once, in the position its first field holds. Fields
// move within the group to join their heading, which is the whole point of
// having one.
export const subheadedFields = (fields = []) => {
  const runs = [];
  const bySection = new Map();
  for (const field of fields) {
    const section = field.section || "";
    if (!bySection.has(section)) {
      const run = { section, fields: [] };
      bySection.set(section, run);
      runs.push(run);
    }
    bySection.get(section).fields.push(field);
  }
  return runs;
};

// ── What the worklist shows besides the blanks ──────────────────────────────
//
// The worklist is *what is missing and nothing else*, which is what keeps it
// the shortest list it can be. Two things had to join it anyway.
//
// **The fields that say which thing the blank belongs to.** An agent's address
// and the agency's phone were outstanding on 1760492 and the panel showed two
// empty boxes and nothing else — not the salesperson, not their email, not the
// agency. Somebody looking up an address needs to see *whose*, and the rest of
// the answer sat one click away behind "show all".
//
// **Named per group rather than "the whole group".** The section 32 group is
// dozens of fields, and one missing disclosure dragging all of them in is how
// a worklist stops being one. Which fields identify a section is a judgement,
// so it is written down — the same reasoning as `ANSWERED_BY` being named
// rather than derived.
export const WORKLIST_CONTEXT = {
  // The whole set an adoption writes, so what one filled is all on screen to
  // check — the address and phone had been left off, so an adoption replacing
  // an address that was already filled showed nothing of it.
  agent: ["agent.contactName", "agent.email", "agent.mobile", "agent.name", "agent.address", "agent.phone"],
};

// **And a field answered while this matter has been open stays put.**
//
// Adopting a directory record or a web result fills four or five fields at
// once, and each one vanished from the worklist the instant it landed — so
// what an adoption had actually written was invisible at exactly the moment
// somebody would want to check it. Staff asked to see what was pre-filled and
// have the chance to disagree, which cannot happen if the rows leave.
//
// `kept` is the names that were outstanding earlier in this matter's life on
// screen. It is deliberately not persisted: the point is to show the change
// somebody just made, and on the next visit the field is simply answered.
//
// **And the context stays with it.** `kept` held the answered field and lost
// everything around it: adopting a directory record filled the agency address
// and phone, those two stayed, and the salesperson's name, email and mobile —
// which the same press had just written — left the screen. So the one moment
// staff wanted to check what an adoption had done showed two of the five
// fields it wrote. A group is in play while anything in it is a gap **or** was
// one, which is what keeps the six agent rows together from the press until
// the panel is next opened fresh.
export const worklistFields = (registry = [], missing = [], kept = []) => {
  const gaps = new Set(missing);
  const keep = new Set(kept);
  const groupsInPlay = new Set(
    registry.filter(f => gaps.has(f.name) || keep.has(f.name)).map(f => f.group));
  const context = new Set(
    [...groupsInPlay].flatMap(group => WORKLIST_CONTEXT[group] || []));
  return registry.filter(f =>
    gaps.has(f.name) || keep.has(f.name) || context.has(f.name));
};

export const panelSections = (registry, visible) =>
  groupOrder(registry)
    .map(group => ({
      group,
      fields: (visible || []).filter(f => f.group === group),
      total: (registry || []).filter(f => f.group === group).length,
    }))
    .filter(section => section.total > 0);

// What clicking a tick box in the contract should save.
//
// A box carries the answer it stands for — `data-box-value` — because several
// of them are one value of a text field rather than a flag: deposit bond and
// bank guarantee are two boxes on the same field, as are farming business and
// going concern. So ticking one is "set the field to my answer", and clicking
// the one already ticked is "clear it".
//
// Clearing rather than setting false is deliberate. An unfilled flag is not the
// same statement as "no", and the contract's boxes mean "this does not apply"
// when empty — the registry is strict about that distinction and this must not
// quietly undo it.
//
// **Unless the form's own sentence names the other answer**, which is
// `data-box-off`. The Lease box reads *"the purchaser is entitled to vacant
// possession of the property unless the box is checked"* — so an unticked box
// there is not "this does not apply", it is vacant possession, stated by the
// form. Unticking says so.
//
// That is also the only thing that makes unticking it *work*.
// `property.transferredStatus` is filled by the client's form, so clearing the
// correction fell back to Form 27's *Subject to lease*, the box redrew ticked,
// and `boxClearSticks` sent the press to the editor's two radios instead —
// two clicks and a Save to untick a box whose own sentence has already
// answered the question. A named answer is a correction, and a correction
// outranks every source, so it always sticks.
//
// **The middleware decides which absence is an answer, and it is named per box
// rather than derived from the field having two options.** Deposit bond and
// bank guarantee are two answers on one field too, and there neither box being
// checked means the answer does not apply — so unticking one has to go on
// clearing. A box with no `data-box-off` behaves exactly as it did.
//
// **A yes box drawn ticked by something else unticks to No** (`shownTicked`).
// The building and pest report boxes tick from the inspection date while
// staff have answered neither, so the flag is still blank: setting it to
// "yes" redrew the same tick, and only a second click said No. A box the
// reader sees ticked is one they mean to untick, so the first click saves
// `false` — a correction, so it sticks.
export const boxToggleValue = (current, boxValue, offValue, shownTicked = false) => {
  if (shownTicked && boxValue === true && current !== true) return offValue === undefined ? false : offValue;
  if (current !== boxValue) return boxValue;
  return offValue === undefined ? null : offValue;
};

// Whether unticking a box would actually untick it.
//
// Clicking the ticked box clears the *correction*, and a value that came from
// somewhere else is still there afterwards — so the box redraws ticked and the
// press reads as a broken button. That is what staff reported on the Lease
// pair: `property.leaseType` is **derived** from the lease end date, so
// clearing changed nothing and the only way to take the tick off was to answer
// vacant possession, which is a different question.
//
// Only a correction can be cleared. Everything else — derived, standard, or a
// source that filled it — needs the thing underneath it changed, so the press
// opens the editor and says so instead of saving nothing.
export const boxClearSticks = (source) => source === "manual";

// The answer a box stands for, or undefined when it carries none — a box drawn
// by a date existing has nothing to toggle to, so clicking it opens the editor
// the way every other value does.
export const boxValueOf = (el) => {
  const raw = el?.dataset?.boxValue;
  if (raw === undefined) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    // Better to open the editor than to save something nobody wrote.
    return undefined;
  }
};

// The answer unticking a box means, where the form's own sentence names one.
// Undefined otherwise — including on a middleware that predates the attribute,
// which is what keeps unticking every other box a clear.
export const boxOffValueOf = (el) => {
  const raw = el?.dataset?.boxOff;
  if (raw === undefined) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

export const groupOrder = (registry) => {
  const seen = [...new Set((registry || []).map(f => f.group))];
  return [...GROUP_ORDER.filter(g => seen.includes(g)), ...seen.filter(g => !GROUP_ORDER.includes(g))];
};

// Where a value came from. Staff need this: without it a wrong extraction is
// indistinguishable from a typo, and nobody knows which values to double-check.
//
// **Every source the middleware can report needs an entry here.** The tag is
// the only thing on the row saying where a value came from, and an unknown
// source renders as *nothing* — which reads exactly like a field nobody
// filled. Two sources were missing and both were invisible for it:
//
// - **Vicmap.** The planning scheme, the responsible authority, the zone, the
//   overlays and the bushfire prone area come either from a planning
//   certificate in the pack or from the state's map, and those are not the
//   same evidence: a purchaser can turn to the certificate, and the map is the
//   answer standing in until one arrives. A file handler checking section 3.4
//   could not tell which they were looking at.
// - **The form's own wording.** `standard` is not an answer anybody gave, and
//   a clause printing the form's sentence looked identical to one somebody had
//   filled in.
//
// It is called Vicmap rather than "the state planning map" because that is
// what the firm calls it and the tag has room for one word. The longer prose
// name stays in `sourceName`, where there is room to explain.
// The fields the document's editor opens beside the one clicked.
//
// **Two declarations, and the editor opens both.** `data-also` is the slot's,
// stamped into the stored template; `opensWith` is the field's, sent on the
// registry, so it reaches every slot the field prints in without a template
// upload. The vendor's name is the case: on a company or trust sale what
// decides how it prints and who signs is whether the vendor is a company, the
// trust and the officers — and the name prints on the particulars and on every
// signing line, which the slot rule would have to name one by one.
export const editorAlso = (field, slotAlso) => {
  const out = [];
  for (const n of [...String(slotAlso || "").split(","), ...(field?.opensWith || [])]) {
    const name = String(n || "").trim();
    if (name && name !== field?.name && !out.includes(name)) out.push(name);
  }
  return out;
};

const PROVENANCE_CFG = {
  manual:     { label: "Edited",       color: "#1a7ac4", bg: "rgba(26,122,196,0.10)" },
  extraction: { label: "Certificate",  color: "#2a8a52", bg: "rgba(42,138,82,0.10)" },
  vicmap:     { label: "Vicmap",       color: "#7a4fa8", bg: "rgba(122,79,168,0.10)" },
  matter:     { label: "Client form",  color: T.textSoft, bg: "rgba(107,95,87,0.10)" },
  // **The office's own record of the matter, which is not the client's form.**
  // Form 265 out of Triconvey names the *client* — on a company or trust sale
  // the director the office deals with — and labelled "Client form" it read as
  // the vendor having named somebody they did not. The middleware tells the
  // two apart; this only names it.
  office:     { label: "Triconvey",    color: T.textSoft, bg: "rgba(107,95,87,0.10)" },
  firm:       { label: "Firm settings", color: T.textSoft, bg: "rgba(107,95,87,0.10)" },
  standard:   { label: "Form wording", color: T.textFaint, bg: "rgba(107,95,87,0.06)" },
  // Not a source — nothing supplies it. The middleware works it out from
  // another resolved field and sends the sentence saying so in `derivations`,
  // which reached no screen at all until the Lease box turned up ticked with
  // nothing on the page accounting for it.
  derived:    { label: "Worked out",   color: "#7a4fa8", bg: "rgba(122,79,168,0.08)" },
};

// **A standard is usually wording and on a flag it is an answer.**
// `sale.offThePlan` resolves to No when nobody has answered — the firm's
// instruction, safe because that flag prints nothing — and the chip then read
// "Form wording" beside the word No, which sends the reader looking for wording
// that is not there. The chip says "Form default" on a flag instead.
//
// This is a noun, not a rule: what `standard` *means* is the middleware's, and
// a second copy of that here is the drift this file keeps paying for. All the
// portal is choosing is which word fits a boolean.
export const provenanceLabel = (source, type) => {
  const cfg = PROVENANCE_CFG[source];
  if (!cfg) return null;
  if (source === "standard" && type === "flag") return "Form default";
  return cfg.label;
};

// What a derived answer was worked out from — the middleware's own sentence,
// never a second copy of the rule.
//
// **It was a panel of its own and is now the badge's tooltip**, on the firm's
// instruction. Three lines under the value on every derived field is furniture:
// the second sentence — that the value is not a correction, so there is nothing
// to clear — is standing behaviour, identical on every field forever, restated
// beside a value whose news is one word. The same reasoning that took four
// paragraphs off the signed-statement notice.
//
// The sentence itself stays, because it is the one thing the badge cannot say:
// "Worked out" is what happened and `because` is what from — "the lease term
// ended 5 January 2020" — and a computed answer presented with no account of
// itself reads as one somebody gave. A tooltip is one hover rather than no
// answer.
const ProvenanceTag = ({ source, because, type }) => {
  const cfg = PROVENANCE_CFG[source];
  if (!cfg) return null;
  const label = provenanceLabel(source, type);
  return (
    <span
      title={because ? `Worked out because ${because}` : undefined}
      style={{ fontSize: 10, fontWeight: 500, color: cfg.color, background: cfg.bg, padding: "1px 6px", borderRadius: 999, whiteSpace: "nowrap", cursor: because ? "help" : undefined }}
    >
      {label}
    </span>
  );
};

const inputStyle = {
  width: "100%", fontSize: 13, fontFamily: "Inter, sans-serif", color: T.text,
  background: T.white, border: `1px solid ${T.borderMid}`, borderRadius: 6,
  padding: "7px 9px", boxSizing: "border-box", outline: "none",
};

// The resolved value as it should appear in the input box.
//
// A flag has three states, not two: yes, no, and nobody-has-answered. Collapsing
// the third into "no" would turn an unanswered question into a definite
// statutory disclosure.
// Which fields are edited in a box rather than on a line.
//
// A `longtext` always is. A `list` usually is — one value per line is how you
// read and write one — **unless the field says otherwise**: the zone is a list
// a parcel nearly always has exactly one of, and a paragraph-sized box for it
// beside the overlays, which a parcel commonly carries three or four of, gets
// the emphasis backwards.
//
// `singleLine` is the middleware's, like `options` and `flagText`. It says how
// to edit the value and never what the value is — the zone is still a list,
// still filled by Vicmap as one, still printed a line each in section 3.4.
export const paragraphControl = (field = {}) =>
  field.type === "longtext" || (field.type === "list" && !field.singleLine);

// ── And which `<input type>` a field that is *not* a box gets ───────────────
//
// Three surfaces edit a registry field on a line — the contract data panel, the
// preview's click-to-edit popover, and `AlsoField` under both of them — and
// each carried its own answer to this. They disagreed:
//
//   panel      date → date    money → number, step 0.01
//   AlsoField  date → date    money → **text**
//   popover    date → **text**  money → **text**
//
// So `sale.settlementDate` and `property.leaseEndDate`, both `date` and both
// `editable()` merge slots, opened a **free text box** from the document — and
// `s32.outgoingsTotal`, which is `money`, is both an `editable()` slot and a
// `data-also` on `s32.outgoingsAnswer`, so it hit the two wrong ladders at
// once.
//
// **What that costs is worse than an untidy control.** Every one of the three
// commits through `contractInputToValue`, which coerces `money` only when the
// draft parses as a number and never touches a date at all — so `$1,200.00`
// stores as a string in a money field, and `next Friday` stores as a string in
// a date field, prints on the contract, and shows the panel's own
// `<input type="date">` **blank**, because a date input cannot display a value
// it cannot parse. A value that exists, prints, and is invisible where somebody
// would go to correct it.
//
// This is the fourth and fifth time the same fault has been found in these
// ladders, after `boxValue`, `options`, and `AlsoField` having no flag branch
// at all — each one a field that is one thing in the registry and another in
// the browser. One function, asked by all three, is what stops a sixth.
//
// Deliberately only the type-derived attributes. Width and placeholder differ
// between the three on purpose — the panel gives a `text` field the full row
// and the popover is a fixed-width card — and folding those in would be this
// deciding layout for three screens that legitimately differ.
// `fieldInputProps` lives in shared.js now — the main module's section dialogs
// ask the same ladder (see src/Practice.js) — and is re-exported from here.
export { fieldInputProps };

// ── How wide the click-to-edit box has to be ────────────────────────────────
//
// 320px is right for a line: an address, a name, a volume and folio. It is too
// narrow for the things written a value per line, and section 3.4 is where that
// showed — a planning overlay is `DCPO1 DEVELOPMENT CONTRIBUTIONS PLAN OVERLAY
// - SCHEDULE 1`, and a parcel commonly carries three or four. Every one of them
// wrapped mid-name inside a four-row box, so the control that exists to let
// somebody check the overlays against the certificate was the one place they
// could not be read. Section 13's attachment list is the same shape.
//
// **The predicate is the control, not the field.** `paragraphControl` already
// decides textarea against input, so a box you write lines into is exactly the
// box that wants the room — which is why this is a rule rather than two named
// fields. It widens every paragraph editor, and that is the same fix rather
// than a wider one: nothing edited a value per line reads better at 320.
//
// **And it reads the whole set the box opens, not the field it is bound to.**
// Section 3.4's popover is bound to the planning *scheme* — one line, and by
// itself no reason to widen anything — while the overlays arrive underneath it
// through `data-also`. Asked of the bound field alone this would have left the
// case it was written for exactly as it was.
export const EDITOR_WIDTH = 320;
export const WIDE_EDITOR_WIDTH = 500;

export const editorWidth = (field, also = [], viewport = 0) => {
  const wants = [field, ...(also || [])].some(f => paragraphControl(f || {}))
    ? WIDE_EDITOR_WIDTH : EDITOR_WIDTH;
  // A window narrower than the box is not a reason to hang off the edge of it.
  // The preview shares the screen with a data panel, so this is a real width on
  // a laptop rather than a defensive flourish.
  return viewport ? Math.min(wants, Math.max(240, viewport - 24)) : wants;
};

// A date box can only hold `YYYY-MM-DD`, and the value it is given may not be
// in it.
//
// `property.leaseEndDate` is read off the lease, and the middleware's own
// `asIsoDate` accepts Australia's day-first form as well as ISO — so a matter
// legitimately carries `19/03/2026`, which an `<input type="date">` cannot
// display at all. The panel has therefore been showing that field **blank** on
// those matters while the value was on the contract, and unifying the three
// ladders would have spread it to the popover and the also-field.
//
// **Only an unambiguous day-first date with a four-digit year**, because this
// is the control somebody corrects a legal date in: a two-digit year is a coin
// flip, which is exactly why the middleware refuses one too. Anything else is
// passed through untouched and the box stays blank, which is today's behaviour
// — this only ever rescues a value it is certain of, and never invents one.
const DAY_FIRST = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

export const dateForInput = (v) => {
  const raw = String(v ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const m = DAY_FIRST.exec(raw);
  if (!m) return raw;
  const [, d, mo, y] = m;
  if (Number(mo) < 1 || Number(mo) > 12 || Number(d) < 1 || Number(d) > 31) return raw;
  return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

export const contractFieldToInput = (type, v) => {
  if (type === "flag") return v === true ? "yes" : v === false ? "no" : "";
  if (type === "list") return Array.isArray(v) ? v.join("\n") : (v == null ? "" : String(v));
  if (type === "date") return dateForInput(v);
  return v == null ? "" : String(v);
};

// What to send back for a typed value. An empty box means "clear this
// correction" — the middleware then falls back to the certificate rather than
// storing a blank that masks it permanently.
// What the editor should start from when nothing has been typed.
//
// **Section 13 is the case this exists for.** The attachments box prints the
// merge list when `s32.attachments` is empty, so clicking the printed list
// opened an empty textarea — the value it showed was generated, and the only
// way to "edit" it was to retype forty certificate names by hand. Seeded, the
// editor opens on exactly what is on the page, which is what "edit this list"
// means.
//
// **Only when the stored value is empty**, so a typed correction is never
// silently replaced by the generated list — and clearing the box still clears
// the correction, because what is saved is what is in the box when it is
// committed, not this.
export const SEEDED_FROM_DOCUMENT = { 's32.attachments': 'generatedAttachments' };

export const seedDraft = (field, value, seeds = {}) => {
  const stored = contractFieldToInput(field.type, value);
  if (stored !== "") return stored;
  const from = SEEDED_FROM_DOCUMENT[field.name];
  const seed = from ? seeds[from] : null;
  if (!Array.isArray(seed) || seed.length === 0) return stored;
  return seed.join("\n");
};

// What an amount box holds, as a number: "$1,200", "1,200.50" and "1200" all
// read as amounts. Anything else answers null and is kept as typed — "see
// schedule" is something a parser cannot improve on.
export const parseAmount = (draft) => {
  const m = String(draft ?? "").trim().match(/^\$?\s*(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?$/);
  return m ? Number(m[1].replace(/,/g, "") + (m[2] || "")) : null;
};

export const contractInputToValue = (type, draft) => {
  if (type === "flag") return draft === "" ? "" : draft === "yes";
  if (type === "money") return parseAmount(draft) ?? draft;
  if (type === "list") {
    const items = draft.split("\n").map(s => s.trim()).filter(Boolean);
    return items.length === 0 ? "" : items;
  }
  return draft;
};

// What a correction is sitting on top of, in words.
//
// A manual value outranks the certificates and survives a re-extraction, which
// is what makes it safe to make one — but it also means the extracted value is
// no longer anywhere on screen. Without this, undoing an override looks exactly
// like deleting the value, so nobody does it.
//
// `fallback` comes from the middleware, which computes it while resolving the
// field. The portal deliberately doesn't work it out from the raw sources: the
// precedence order is the compile's, and a second copy of it here would drift.
export const describeFallback = (field, entry) => {
  if (!entry) return null;
  const label = provenanceLabel(entry.source, field.type);
  const text = field.type === "flag"
    ? (entry.value === true ? "Yes" : "No")
    : contractFieldToInput(field.type, entry.value).replace(/\n/g, ", ");
  return { label: label || entry.source, text };
};

// The offer to put a field back to what the certificate (or the client form, or
// the firm record) says. Rendered only when there is genuinely something
// underneath — a correction with nothing under it would be blanked rather than
// restored, and a button that empties a legal field while saying "revert" is
// the wrong label on a destructive action.
const RevertToSource = ({ field, entry, onRevert, saving }) => {
  const under = describeFallback(field, entry);
  if (!under) return null;
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
      <span style={{ fontSize: 11, color: T.textFaint }}>
        {under.label}:
      </span>
      <span style={{ fontSize: 11, color: T.textSoft, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={under.text}>
        {under.text || "\u2014"}
      </span>
      <button
        type="button"
        onClick={() => onRevert(field.name)}
        disabled={saving}
        style={{
          fontSize: 11, color: T.brown, background: "none", border: "none", padding: 0,
          cursor: saving ? "default" : "pointer", textDecoration: "underline", fontFamily: "inherit",
        }}
      >
        Revert to this
      </button>
    </div>
  );
};

// Every version the sources hold, offered as a choice.
//
// The address is the case that built it: Triconvey holds one form of it and
// the title states another, and staff should pick rather than retype. The
// middleware sends `candidates` only where the sources genuinely disagree,
// resolved and tidied — the portal never derives them from the raw sources,
// for the same reason as `fallback`. Choosing one saves it as a manual
// correction through the ordinary save, which is exactly what makes the
// choice revertable.
//
// The version equal to what the field currently shows is not offered — there
// is nothing to change to — and when a correction is active, the version the
// revert line already offers is left to that line rather than listed twice.
const OtherVersions = ({ field, value, source, fallback, candidates = [], onSave, saving }) => {
  const key = (v) => JSON.stringify(v ?? "");
  const rows = candidates
    .filter(c => key(c.value) !== key(value))
    .filter(c => !(source === "manual" && fallback && c.source === fallback.source
      && key(c.value) === key(fallback.value)));
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 5, display: "flex", flexDirection: "column", gap: 3 }}>
      {rows.map(c => {
        const under = describeFallback(field, c);
        return (
          <div key={c.source} style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: T.textFaint }}>{under.label}:</span>
            <span style={{ fontSize: 11, color: T.textSoft, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={under.text}>
              {under.text || "—"}
            </span>
            <button
              type="button"
              onClick={() => onSave(field.name, c.value)}
              disabled={saving}
              style={{
                fontSize: 11, color: T.brown, background: "none", border: "none", padding: 0,
                cursor: saving ? "default" : "pointer", textDecoration: "underline", fontFamily: "inherit",
              }}
            >
              Use this
            </button>
          </div>
        );
      })}
    </div>
  );
};

// Where the directory offer hangs off, and it is two answers rather than one.
//
// **In the panel it sits above the agent fields, not on one of them.** Adopting
// fills the whole set in a single write, so an offer under each row would read
// as six different choices — but hanging it under a single row read as an
// answer to *that* field, and staff met it under "agency address" while it
// fills the agency, the phone and the salesperson's details as well. It is a
// heading for the group now, with the sentence saying so.
//
// **In the popover it is every one of them**, because there you see one field
// and nothing else. Staff reported the fault exactly: the offer appeared on the
// agency name and not on the address, the email or the mobile — and the address
// is routinely *blank*, so clicking it to type one is precisely the moment
// somebody wants to be shown the agency the directory already holds. An
// affordance that is missing from the one field people open most is one that
// does not exist.
export const DIRECTORY_POPOVER_FIELDS = [
  "agent.name", "agent.address", "agent.contactName",
  "agent.email", "agent.mobile", "agent.phone",
];

// The row the directory offer is introduced above.
//
// **A heading for the group rather than a note on one field.** It was anchored
// to a single row, on the reasoning that adopting a record fills the whole set
// in one write and six offers would read as six choices. That half is still
// right; what was wrong is that a note under one row reads as an answer to
// *that* row. Staff met "Directory: David Macmillan — Jellis Craig" under
// **agency address**, while pressing it fills the agency, its phone and the
// salesperson's name, email and mobile too.
//
// So it goes above the first agent field on screen, whichever that is — the
// worklist shows what is missing and the full list shows everything, and in
// both the agent rows arrive together in the registry's own order. The lead is
// read off the list being rendered rather than off `missing`, which is what
// keeps it reachable from either view: an earlier version keyed on `missing`
// and vanished from the worklist the moment the last agent field was answered.
export const directoryLeadField = (visible = []) =>
  (visible || []).map(f => (typeof f === "string" ? f : f?.name))
    .find(name => DIRECTORY_POPOVER_FIELDS.includes(name)) || null;

// Which directory record this matter's agent is.
//
// **The office is most of what an existing matter needs.** `agent.name` prints
// the agency's contract name and `agent.address` is where notices are served,
// and both come off the *office* rather than the person — so a matter that
// knows its agency and not its salesperson still has most of the answer here.
//
// Two joins, and the difference between them is what this has to show. The
// **email** landing on one record is documentary: the middleware says so with
// `exact`, and that row leads and says why. Everything else is a **question**,
// which is why every candidate carries an address — 69 live trading names are
// shared between genuinely different agencies, and the address is what tells
// two `@realty` records apart in a second.
//
// It writes nothing itself. Adopting is `POST /matter-agent/:ref`, which saves
// the agency, the contact, the email, the address and the numbers as ordinary
// **manual corrections** — so it shows in the panel like any other correction
// and reverts like one. The rule that decides which field gets which value is
// the middleware's, deliberately: a second copy here would drift, the same
// reasoning as `fallback` and `excludedFromCompile`.
// One web result, with its fields picked rather than taken whole.
//
// **Two buttons could not both be pressed.** Adopting reloads the matter,
// which remounts this panel and takes the search results with it — so "use the
// agency" and "use the salesperson" were mutually destructive in practice,
// whatever the writes did. And they should not have been two presses anyway:
// what somebody wants is this address but not that phone, which is a choice
// per field.
//
// So it is one write over whatever is ticked. Everything found starts ticked —
// it was searched for on purpose — and unticking is how you decline a value.
const WebOffice = ({ office, onAdopt }) => {
  const contact = office.contact || {};
  const address = [office.address, office.suburb, office.state, office.postcode]
    .filter(Boolean).join(" ");

  // Only what the search actually found. A field with nothing behind it is not
  // offered at all, rather than offered as a blank that would clear what the
  // matter holds.
  const offered = [
    { name: "agent.name", label: "Agency", value: office.name },
    { name: "agent.address", label: "Address", value: address },
    { name: "agent.phone", label: "Phone", value: office.phone },
    { name: "agent.contactName", label: "Contact", value: contact.name },
    { name: "agent.email", label: "Email", value: contact.email || office.email },
    { name: "agent.mobile", label: "Mobile", value: contact.mobile },
  ].filter(f => String(f.value || "").trim());

  const [picked, setPicked] = useState(() => new Set(offered.map(f => f.name)));
  const toggle = (name) => setPicked(p => {
    const next = new Set(p);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  if (!offered.length) return null;

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: T.text }}>{office.name}</span>
        {/* **The page this one came from.** The middleware has captured it per
            office since the search was built and nothing rendered it — so the
            only sources on screen were the response's whole grounding list at
            the bottom, which does not say which result came from where. That
            is the link somebody actually wants: the page to check this address
            against before putting it on a contract. */}
        {office.source && (
          <a href={office.source} target="_blank" rel="noreferrer"
            style={{ fontSize: 11, color: T.textSoft }}>
            source
          </a>
        )}
      </div>
      {offered.map(f => (
        <label key={f.name}
          style={{ display: "flex", gap: 6, alignItems: "baseline", fontSize: 11,
            color: T.textSoft, cursor: "pointer", lineHeight: 1.6 }}>
          <input type="checkbox" checked={picked.has(f.name)} onChange={() => toggle(f.name)} />
          <span style={{ minWidth: 54 }}>{f.label}</span>
          <span style={{ color: T.text }}>{f.value}</span>
        </label>
      ))}
      {/* Nothing ticked is nothing to do — an empty set would post a write
          that changes nothing and toast as though it had. */}
      {picked.size > 0 && (
        <div style={{ marginTop: 4 }}>
          <PillButton onClick={() => onAdopt({
            fields: Object.fromEntries(
              offered.filter(f => picked.has(f.name)).map(f => [f.name, f.value]),
            ),
          })}>
            Use {picked.size === offered.length ? "these details" : `these ${picked.size}`}
          </PillButton>
        </div>
      )}
    </div>
  );
};

// A button that shows a model is thinking, by chasing its own border.
const ThinkingButton = ({ busy, disabled, onClick, children, busyLabel }) => {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      style={{
        font: "inherit", fontSize: 11, color: busy ? "#2f6fed" : T.text,
        background: T.white, border: `1px solid ${busy ? "transparent" : T.border}`,
        fontWeight: busy ? 500 : 400,
        borderRadius: 999, padding: "3px 10px",
        cursor: busy || disabled ? "default" : "pointer", lineHeight: 1.4,
      }}
    >
      {busy ? busyLabel : children}
    </button>
  );
  // The chase wraps the button only while it runs, so a resting button is an
  // ordinary one and nothing animates on a screen nobody has pressed.
  return busy ? <span className="s32-chase">{button}</span> : button;
};

// **Why the email's record was not selected by itself.** The middleware's
// automatic adoption stands down where the matter names a different agency
// from the record's — rightly, since the record's office is where notices get
// served — and it did so silently. On 1760653 the email landed on Greg Bowring,
// the matter said Jellis Craig Balwyn, his record says Hawthorn, and the panel
// showed him as the exact match with nothing saying why nobody was selected.
//
// The reason is the middleware's (`notAutoAdopted`, from the same rule the
// intake applies); only the words are here. A reason this build has not been
// taught still says something rather than going quiet.
export const notAutoAdoptedLine = (block) => {
  if (!block || !block.reason) return "";
  const said = String(block.matterAgency || "").trim();
  const theirs = String(block.recordOffice || "").trim();
  if (block.reason === "agency_differs" && said && theirs) {
    return `Not selected automatically: the directory has them at ${theirs}, `
      + `and this matter names ${said}. If they have moved, update their record under Agents first.`;
  }
  if (block.reason === "record_has_no_office") {
    return `Not selected automatically: their directory record names no agency`
      + (said ? ` to check against ${said}.` : ".");
  }
  return "Not selected automatically — check the agency before using this.";
};

export const DirectoryMatch = ({
  directory, onAdopt, adopting,
  onSearchOnline = searchAgentOnline,
  // **Reads, defaulted rather than drilled.** Both are plain staff GETs behind
  // the same token every other read uses, and threading them through four
  // components to reach a popover would be more machinery than the fetch. A
  // test hands its own in.
  onListOfficeAgents = fetchOfficeAgents, onSearchAgents = searchDirectory,
}) => {
  const [open, setOpen] = useState(false);
  // What the web search returned, or why it did not.
  const [online, setOnline] = useState({ busy: false });
  // Whether an already-answered matter is showing the list again. Staff picked
  // the wrong record and had no way back: the offer replaced itself with
  // "taken from the directory" and every candidate went with it.
  const [changing, setChanging] = useState(false);
  // The agency's own roster, fetched on a press. Keyed by office so opening a
  // second one does not blank the first.
  const [roster, setRoster] = useState({});
  const [loadingOffice, setLoadingOffice] = useState("");
  const [query, setQuery] = useState("");
  const [found, setFound] = useState(null);
  const [searching, setSearching] = useState(false);
  // **A new pick closes the chooser.** `changing` was set by *Pick a different
  // one* and by nothing else, so after the new record landed the panel went on
  // showing every candidate and both searches, with nothing saying which record
  // was now the matter's and no way to put the list away short of leaving.
  // Keyed on the link the middleware reports, so the answer arriving is what
  // closes it — a press that failed leaves the list open to try again.
  const linkKey = directory ? `${directory.agentId || ""}|${directory.officeId || ""}` : "";
  const linkSeen = useRef(linkKey);
  useEffect(() => {
    if (linkSeen.current === linkKey) return;
    linkSeen.current = linkKey;
    setChanging(false);
  }, [linkKey]);
  if (!directory) return null;
  // Carried on the payload rather than drilled through four components.
  const matterRef = directory.matterRef || "";

  const agents = directory.agents || [];
  const offices = directory.offices || [];
  // What the middleware matched on — the resolved agent details, not the raw
  // matter fields, so a correction somebody made is what it asked with.
  const asked = directory.asked;

  // **Nothing matched is the case the search exists for, and it used to render
  // nothing at all.** This returned null when the directory offered no agent
  // and no agency — so the "nothing matched" line below, the directory search
  // and the web search were all unreachable on exactly the matters where the
  // join had failed, which is the one situation somebody needs a way to look
  // further. 70 of 180 live matters name no agent and one names an agent the
  // directory has never heard of; all 71 got a blank space.
  //
  // So the offer is what is *known* plus the two ways of looking further, and
  // with nothing known it is the two ways on their own. `matterRef` is always
  // carried, so the web search is reachable either way.

  // What the middleware actually looked the agent up by. Empty means it had
  // nothing to look with, which is a different report from finding nobody.
  const askedFor = [asked?.email, asked?.contactName, asked?.agencyName]
    .map(v => String(v || "").trim()).filter(Boolean);

  const faint = { fontSize: 11, color: T.textFaint };
  const linkish = {
    fontSize: 11, color: T.brown, background: "none", border: "none", padding: 0,
    cursor: "pointer", textDecoration: "underline", fontFamily: "inherit",
  };

  // Already answered — said rather than hidden, since somebody looking at the
  // agency on a contract wants to know it was chosen rather than typed.
  //
  // **And changeable.** A wrong pick used to be the end of it: this replaced
  // the whole offer, so there was no way back to the list and no way to reach
  // the directory again from the matter at all. The middleware makes a second
  // adoption *replace* the first rather than blend with it, so the only thing
  // missing was a way to ask.
  if (directory.adopted && !changing) {
    return (
      <div style={{ marginTop: 6, display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
        {/* **Said in staff's words.** It read *"the values are ordinary
            corrections"* — the system's own term, which told nobody what
            changing the box would do. And *"Pick a different one"* left "one"
            to be guessed; it names the record the matter is linked to. */}
        <span style={faint}>
          From the agent directory. Changing it here changes this matter only, unless you also
          save it to the directory.
        </span>
        <button type="button" onClick={() => setChanging(true)} style={linkish}>
          {directory.agentId ? "Pick a different agent" : "Pick a different agency"}
        </button>
      </div>
    );
  }

  // **A press, not a link.** Six underlined phrases down a grey column read as
  // a paragraph that happened to be clickable; the thing somebody is doing
  // here is choosing one of several records, and a pill says so. The exact
  // email hit is the recommended one and is the only filled button — one
  // accent rather than a page of them.
  const Adopt = ({ choice, label, primary }) => (
    <button
      type="button"
      onClick={() => (adopting ? undefined : onAdopt(choice))}
      disabled={adopting}
      style={{
        font: "inherit", fontSize: 11, borderRadius: 999, padding: "3px 11px",
        whiteSpace: "nowrap", flexShrink: 0, lineHeight: 1.45,
        background: primary ? T.brown : T.white,
        color: primary ? T.warmGrey : T.text,
        border: `1px solid ${primary ? T.brown : T.borderMid}`,
        cursor: adopting ? "default" : "pointer", opacity: adopting ? 0.4 : 1,
      }}
    >
      {label}
    </button>
  );

  // One thing you can pick: what it is called, what tells it apart underneath,
  // and the press on the right. Each on its own row — the old block ran the
  // name, the email and the agency into a single line that wrapped, so two
  // records of the same agency read as the same suggestion twice.
  const Choice = ({ title, detail, note, action, extra }) => (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: T.text, lineHeight: 1.35 }}>{title}</div>
        {detail && <div style={{ fontSize: 11, color: T.textSoft, lineHeight: 1.45 }}>{detail}</div>}
        {note && <div style={{ fontSize: 11, color: T.textFaint, lineHeight: 1.45 }}>{note}</div>}
        {extra}
      </div>
      {action}
    </div>
  );

  const exact = agents.find(a => a.agentId === directory.exact);
  // Everything that is a question rather than an answer, capped on screen
  // until somebody asks for the rest — a list of five under a field is a
  // panel nobody reads.
  const rest = agents.filter(a => a.agentId !== directory.exact);
  const shown = open ? rest : rest.slice(0, 2);

  // The agency, offered whenever the person is not already settled. With an
  // exact email hit the salesperson is known and their office comes with them,
  // so a second way to answer the same question would only be a way to
  // disagree with it.
  //
  // **Unless somebody has said the exact match is wrong**, which is what
  // *Pick a different one* means. Withholding the agencies and the two
  // searches from that state left a matter with one candidate offering the
  // same record again and nothing else at all — the press was, as reported,
  // pointless. Disagreeing with the email is exactly when a second way to
  // answer is the point.
  const looking = !exact || changing;
  const showOffices = looking;
  const shownOffices = open ? offices : offices.slice(0, 2);

  const openOffice = async (officeId) => {
    if (roster[officeId]) return;
    setLoadingOffice(officeId);
    try {
      const list = await onListOfficeAgents(officeId);
      setRoster(r => ({ ...r, [officeId]: list }));
    } finally {
      setLoadingOffice("");
    }
  };

  const runSearch = async (e) => {
    e.preventDefault();
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      setFound(await onSearchAgents(query.trim()));
    } finally {
      setSearching(false);
    }
  };

  // Rows that hang off something above them — an agency's roster, a search
  // result. Ruled rather than bulleted, so a long name wrapping cannot leave a
  // marker stranded on its own line.
  const Nested = ({ children }) => (
    <div style={{ marginLeft: 8, paddingLeft: 10, borderLeft: `2px solid ${T.border}`,
                  display: "flex", flexDirection: "column", gap: 3 }}>
      {children}
    </div>
  );

  // **The typed name is what gets searched for, on the web as well.** The web
  // search sent `{}` and the route then fell back to the matter's own agency
  // and contact — which is right on a matter that names one, and on a matter
  // that names nothing it made the button a guaranteed dead press: 409, *"this
  // matter names no agency and no agent to search for"*, on exactly the 70 of
  // 180 matters where looking further is the whole point. Meanwhile the name
  // somebody had just typed into the box above was sitting right there.
  //
  // **It goes as `contactName`, which is what the box holds.** That box is the
  // directory's *agent* search — it queries agents and renders their name,
  // email and office — so what is typed into it is a person, not an agency.
  // Sending it as the agency would mislabel it in the prompt, on a panel whose
  // whole vocabulary keeps the two apart because notices are served on one of
  // them and not the other.
  //
  // **The typed text goes as a query, not as the salesperson's name**, and
  // that was the whole of the reported fault. This box is shared with the
  // directory search, which matches a name *and* an agency across fields — so
  // it teaches you to type "Daniel Cripps Barry plant", and that whole string
  // was being sent as `contactName`. The prompt then read `Salesperson: Daniel
  // Cripps Barry plant` with the matter's own agency underneath it, and a
  // model either untangled the contradiction or did not: the firm reported
  // three tries and one that worked.
  //
  // The matter's agency still goes with it — "type the person under the agency
  // we already hold" is the other real use of this box — and the middleware
  // ranks the typed text above it. `contactName` is still accepted there, so
  // the two repositories deploy in the usual order, middleware first.
  const webQuery = query.trim();
  // **The route's own refusal, mirrored rather than guessed at.** It answers
  // 409 when it has neither an agency nor a contact — and `asked.email` is
  // not one of them, so a matter carrying only an unmatched email would pass
  // an `askedFor.length` test and still be refused.
  const canSearchWeb = webQuery.length >= 2
    || Boolean(String(asked?.agencyName || "").trim() || String(asked?.contactName || "").trim());

  // **The web search, defined here and rendered inside the search form.** It
  // sits beside the directory search rather than under it: they are one
  // question — this is not it, where else should I look — reading the one box
  // between them.
  //
  // **Pressed, never automatic.** An outbound model call per matter read is
  // the shape that once turned six writes into thirty-six Gemini calls.
  const webSearch = matterRef ? (
    <ThinkingButton
      busy={online.busy}
      disabled={!canSearchWeb}
      busyLabel="Searching the web…"
      onClick={async () => {
        setOnline({ busy: true });
        try {
          const asking = webQuery.length >= 2 ? { query: webQuery } : {};
          setOnline({ busy: false, ...(await onSearchOnline(matterRef, asking)) });
        } catch (e) {
          setOnline({ busy: false, error: e.message });
        }
      }}
    >
      Search the web
    </ThinkingButton>
  ) : null;

  // **A card of its own, because it is a different kind of thing.** Sitting as
  // loose grey lines above the fields it read as more helper text over a form;
  // what it actually is is a short list of records to choose between. The
  // panel is white, so the card is the page's own grey.
  return (
    <div style={{
      marginTop: 8, marginBottom: 4,
      background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10,
      padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12,
    }}>
      {/* The way back out, while a matter that already has a record is being
          re-picked: keeping the current one is an answer too. */}
      {directory.adopted && changing && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
          <span style={faint}>Picking a different record — the current one stays until you choose.</span>
          <button type="button" onClick={() => setChanging(false)} style={linkish}>
            Keep the current one
          </button>
        </div>
      )}
      {/* **The person and the agency are two different choices, so they are
          two sections.** They ran together as one column with their labels
          inline — "Might be:" on a row, "Agencies of that name" on another —
          which made the one thing somebody has to understand, *am I picking a
          salesperson or an agency*, the hardest thing on the block to see.
          Adopting an agency deliberately leaves the salesperson's fields
          alone, so the difference is not cosmetic. */}
      {(exact || shown.length > 0) && (
        <div>
          <Label>Salesperson</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {exact && (
              <Choice
                title={exact.name}
                detail={[exact.email, exact.officeName].filter(Boolean).join(" \u00b7 ")}
                note="(same email address)"
                extra={notAutoAdoptedLine(directory.notAutoAdopted) && (
                  <div style={{ fontSize: 11, color: T.text, lineHeight: 1.45, marginTop: 3 }}>
                    {notAutoAdoptedLine(directory.notAutoAdopted)}
                  </div>
                )}
                action={<Adopt choice={{ agentId: exact.agentId, matchedBy: "email" }}
                  label="Use this" primary />}
              />
            )}
            {shown.map(a => (
              <Choice
                key={a.agentId}
                title={a.name}
                detail={[a.email, a.officeName].filter(Boolean).join(" \u00b7 ")}
                note={a.reason ? `(${a.reason})` : ""}
                action={<Adopt choice={{ agentId: a.agentId, matchedBy: a.matchedBy }} label="Use this" />}
              />
            ))}
          </div>
        </div>
      )}

      {/* Ruled off from the salesperson above it, like the searches below.
          Three bands rather than one column: who, which agency, and where else
          to look. */}
      {showOffices && shownOffices.length > 0 && (
        <div style={(exact || shown.length > 0)
          ? { borderTop: `1px solid ${T.borderMid}`, paddingTop: 10 } : undefined}>
          <Label>Agency</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {shownOffices.map(o => (
              <Choice
                key={o.officeId}
                title={o.contractName || o.tradingName}
                detail={o.address || "No address recorded"}
                action={<Adopt choice={{ officeId: o.officeId }} label="Use this agency" />}
                extra={<>
                  {/* The pick list that follows choosing an agency. The
                      directory will not match a salesperson by name — a wrong
                      match writes their email onto somebody else — but reading
                      an agency's own roster is a person answering. */}
                  {o.agentCount > 0 && !roster[o.officeId] && (
                    <button type="button" style={{ ...linkish, marginTop: 3 }}
                      onClick={() => openOffice(o.officeId)}>
                      {loadingOffice === o.officeId
                        ? "Loading\u2026"
                        : `Its ${o.agentCount} agent${o.agentCount === 1 ? "" : "s"}`}
                    </button>
                  )}
                  {(roster[o.officeId] || []).length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <Nested>
                        {roster[o.officeId].map(a => (
                          <Choice
                            key={a.agentId}
                            title={a.name}
                            detail={a.email || ""}
                            action={<Adopt choice={{ agentId: a.agentId, matchedBy: a.matchedBy }}
                              label="Use this" />}
                          />
                        ))}
                      </Nested>
                    </div>
                  )}
                  {roster[o.officeId] && !roster[o.officeId].length && (
                    <Nested><span style={faint}>No agents recorded at this agency.</span></Nested>
                  )}
                </>}
              />
            ))}
          </div>
        </div>
      )}

      {(rest.length > 2 || (showOffices && offices.length > 2)) && !open && (
        <button type="button" onClick={() => setOpen(true)}
          style={{ ...linkish, textAlign: "left", alignSelf: "flex-start" }}>
          Show the rest
        </button>
      )}

      {/* **What it looked for, when it found nobody.** A block that offers
          nothing and says nothing about why sends somebody to guess whether the
          directory is empty, the agent is missing, or the matter carries the
          wrong address — and the middleware has known the answer all along. */}
      {!agents.length && askedFor.length > 0 && (
        <div style={faint}>Nothing matched {askedFor.join(" \u00b7 ")}</div>
      )}
      {/* And when it looked for nothing: 70 of 180 live matters name no agent
          at all, which is a different report from finding nobody. */}
      {!agents.length && !offices.length && askedFor.length === 0 && (
        <div style={faint}>No agent details on this matter to look one up by.</div>
      )}

      {/* ── Looking further ───────────────────────────────────────────────
          **Two ways of asking one question, so they share the box and the
          row.** They were split apart once, and the reason was real at the
          time: *Search* consumed what you had typed and *Search the web*
          ignored it entirely, searching the matter's own agency instead. Two
          controls on one row implied both read the box, and only one did — so
          the web search was moved under its own lead-in to stop it reading as
          part of the form above it.

          **That stopped being true when the web search started reading the
          box.** Both now take the same text and differ only in where they
          look, which is exactly the case a row is for: one question, two
          destinations. Under a lead-in they read as unrelated, and the typed
          name — the thing they now share — was the one thing the layout hid.

          **The hierarchy survives, in weight rather than position.** A
          directory record is better evidence than a page somebody scraped,
          and it is free and instant where the web is a model call taking
          seconds. So the directory search comes first and carries the heavier
          border; the web sits beside it as a ghost. */}
      {looking && (
        <div style={{ borderTop: `1px solid ${T.borderMid}`, paddingTop: 10 }}>
          <Label>Not there?</Label>
          <form onSubmit={runSearch} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search the directory"
              style={{
                fontSize: 11.5, padding: "5px 10px", border: `1px solid ${T.borderMid}`,
                borderRadius: 999, fontFamily: "inherit", flex: 1, minWidth: 0,
                background: T.white,
              }}
            />
            <button type="submit" disabled={searching || query.trim().length < 2}
              style={{
                font: "inherit", fontSize: 11, borderRadius: 999, padding: "5px 13px",
                background: T.white, color: T.text, border: `1px solid ${T.borderMid}`,
                whiteSpace: "nowrap", flexShrink: 0,
                cursor: searching || query.trim().length < 2 ? "default" : "pointer",
                opacity: searching || query.trim().length < 2 ? 0.45 : 1,
              }}>
              {searching ? "Searching\u2026" : "Search"}
            </button>
            {/* Inside the form and `type="button"`, so Enter still runs the
                ordinary search rather than the model call. */}
            {webSearch}
          </form>

          {/* **Only when neither can run.** Two greyed buttons with no
              sentence is the dead end this replaced, wearing a different
              shape. It is not shown when the web button alone is live — the
              matter names an agency, the directory has *already* been searched
              on it automatically and that answer is on the screen above, so
              the asymmetry is the layout telling the truth rather than a state
              needing an apology. */}
          {!canSearchWeb && (
            <div style={{ ...faint, marginTop: 6 }}>
              Type a name to search the directory or the web.
            </div>
          )}

          {found && !found.length && (
            <div style={{ ...faint, marginTop: 6 }}>Nothing in the directory matches that.</div>
          )}
          {(found || []).length > 0 && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 9 }}>
              {found.slice(0, 8).map(a => (
                <Choice
                  key={a.agentId}
                  title={a.name}
                  detail={[a.email, a.officeName].filter(Boolean).join(" \u00b7 ")}
                  action={<Adopt choice={{ agentId: a.agentId, matchedBy: a.matchedBy }} label="Use this" />}
                />
              ))}
            </div>
          )}

          {online.error && <div style={{ ...faint, marginTop: 6 }}>{online.error}</div>}
          {/* Named rather than answered emptily: "nothing found" and "it never
              searched" need opposite things done about them. */}
          {online.refused && <div style={{ ...faint, marginTop: 6 }}>{online.message}</div>}
          {(online.offices || []).length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Nested>
                <div style={{ ...faint, marginBottom: 4 }}>
                  Found on the web &mdash; check it before using it.
                </div>
                {online.offices.map((o, i) => (
                  <WebOffice key={`${o.name}-${i}`} office={o} onAdopt={onAdopt} />
                ))}
                {(online.sources || []).length > 0 && (
                  <div style={faint}>
                    Searched{" "}
                    {online.sources.map((src, i) => (
                      <span key={src.uri}>
                        {i > 0 ? ", " : ""}
                        <a href={src.uri} target="_blank" rel="noreferrer"
                          style={{ color: T.textSoft }}>{src.title}</a>
                      </span>
                    ))}
                  </div>
                )}
              </Nested>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Spinner = ({ size = 18 }) => (
  <div style={{ width: size, height: size, border: `2px solid ${T.border}`, borderTopColor: T.brown, borderRadius: "50%", animation: "s32-spin 0.8s linear infinite" }} />
);

// A flag the section 32 prints as a ruled box.
//
// The form has one box per service and it is ticked when the service is NOT
// connected — so that is what staff get, rather than a yes/no they have to
// translate on every matter. `boxWhen` says which answer marks the box and
// `boxLabel` the words beside it; both come from the middleware's registry, so
// the polarity lives in exactly one place.
//
// Three states, not two. Unticked-and-answered means the opposite of the box;
// never-answered is neither, and stays that way until somebody says otherwise,
// because an untouched box would otherwise read as a disclosure nobody made.
// How many answers still read as a row of radios rather than a wall of them.
// Above this a dropdown is kinder — property type has twelve.
const RADIO_LIMIT = 4;

// A text field whose answers are a fixed set.
//
// It used to be a free-text box, which is how a contract came to say
// "vacant possession" in a field the clause tests for "With vacant
// possession" — stored, shown, and gating nothing. The set comes from the
// middleware's registry, so the strings offered here are the strings the
// conditions compare against.
//
// **Blank stays reachable.** Unanswered and "no" are different statements and
// only one belongs in a section 32; clicking the selected radio clears it,
// the same gesture the contract's own boxes use.
export const choiceToggleValue = (current, option) => (current === option ? "" : option);

const ChoiceInput = ({ field, draft, setDraft }) => {
  const options = field.options || [];
  // A value the client's form sent that is not on our list is still the
  // answer. Offered alongside the rest rather than silently dropped, or
  // opening the panel would look like nobody had answered.
  const shown = draft && !options.includes(draft) ? [...options, draft] : options;

  if (shown.length > RADIO_LIMIT) {
    return (
      <select
        value={draft}
        onChange={e => setDraft(e.target.value)}
        style={{ ...inputStyle, cursor: "pointer" }}
      >
        <option value="">— not answered —</option>
        {shown.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, minHeight: 34, flexWrap: "wrap" }}>
      {shown.map(o => (
        <label key={o} style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 13, color: T.text }}>
          <input
            type="radio"
            name={field.name}
            checked={draft === o}
            // Clicking the one already chosen clears it. A radio group with no
            // way back to blank makes "unanswered" unreachable the moment
            // somebody clicks by mistake.
            onClick={() => setDraft(choiceToggleValue(draft, o))}
            onChange={() => {}}
            style={{ width: 15, height: 15, cursor: "pointer", accentColor: T.brown, margin: 0 }}
          />
          {o}
        </label>
      ))}
      {draft === "" && <span style={{ fontSize: 11, color: "#c47a1a" }}>not answered yet</span>}
    </div>
  );
};

// A text field whose contract box stands for one answer — GST *price plus
// GST* is a single box, not a choice between two. Ticking sets the value,
// unticking clears it, which is what the box in the preview does.
const BoxValueInput = ({ field, draft, setDraft }) => {
  const checked = draft === field.boxValue;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 34, flexWrap: "wrap" }}>
      <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 13, color: T.text }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => setDraft(checked ? "" : field.boxValue)}
          style={{ width: 15, height: 15, cursor: "pointer", accentColor: T.brown, margin: 0 }}
        />
        {field.boxLabel || field.label}
      </label>
      {/* A value that is neither ticked nor blank came from somewhere else and
          must not be hidden behind an unticked box. */}
      {draft !== "" && !checked && (
        <span style={{ fontSize: 11, color: T.textFaint }}>currently: {draft}</span>
      )}
    </div>
  );
};

// **A checkbox has two states and this field has three**, which is the whole
// difficulty. Unticked has to mean two different things — "no" and "nobody has
// answered" — and on a required field those are the two the form cannot tell
// apart on paper, which is exactly why it is required.
//
// What that produced was a control where **the only click available from
// unanswered was *yes***. To record the answer nearly every matter needs — the
// land is not in a designated bushfire prone area — somebody had to tick the
// box, asserting it was, and then untick it. Answering a statutory disclosure
// by first stating its opposite.
//
// So the "no" is offered directly when nothing has been answered. The checkbox
// still means what it means, and the phrase beside it is the same one the
// answered-no state already shows, so the control reads the same before and
// after.
const BoxFlagInput = ({ field, draft, setDraft }) => {
  const ticked = field.boxWhen ? "yes" : "no";
  const unticked = field.boxWhen ? "no" : "yes";
  const checked = draft === ticked;
  const answered = draft !== "";
  // What "unticked" means on this field, in the form's own terms.
  const noPhrase = field.boxWhen === false ? "connected" : "does not apply";

  const linkStyle = {
    background: "none", border: "none", padding: 0, cursor: "pointer",
    fontSize: 11, textDecoration: "underline", fontFamily: "Inter, sans-serif",
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 34, flexWrap: "wrap" }}>
      <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 13, color: T.text }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => setDraft(checked ? unticked : ticked)}
          style={{ width: 15, height: 15, cursor: "pointer", accentColor: T.brown, margin: 0 }}
        />
        {field.boxLabel || field.label}
      </label>
      {!answered ? (
        <span style={{ fontSize: 11, color: "#c47a1a" }}>
          not answered
          {" · "}
          {/* The other answer, in one click. Without it the only way to reach
              "no" was to tick the box and untick it — stating the opposite of
              the disclosure on the way to making it. */}
          <button onClick={() => setDraft(unticked)} style={{ ...linkStyle, color: "#c47a1a" }}>
            {noPhrase}
          </button>
        </span>
      ) : !checked ? (
        <span style={{ fontSize: 11, color: T.textFaint }}>
          {noPhrase}
          {" · "}
          <button onClick={() => setDraft("")} style={{ ...linkStyle, color: T.textFaint }}>
            clear
          </button>
        </span>
      ) : (
        <button onClick={() => setDraft("")} style={{ ...linkStyle, color: T.textFaint }}>
          clear
        </button>
      )}
    </div>
  );
};

// One field. Saves on blur, and only when the value actually changed — a
// staff member tabbing through 40 fields shouldn't fire 40 writes.
// What the vendor said about a disclosure, beside the field it bears on.
//
// Form 27 asks about notices, restrictions and material facts, and for most of
// them the certificate is the document of record while the vendor is answering
// from memory. Their words are kept as a *helper* rather than written into the
// statement — a statutory disclosure nobody had checked reads, on the page,
// exactly like one that had been.
//
// It reached the searches review already. It did not reach this panel, which
// is the screen where somebody actually types the disclosure — so the one
// place the vendor's answer was worth having was the one place it was absent.
//
// A bubble rather than a row of its own: it belongs *to* the field, and giving
// it equal weight would read as a second answer.
// **And the bubble no longer claims the vendor said all of it.** The rows come
// from three different mouths — the vendor's own Form 27 answers, what a
// certificate stated, and what the transfer of land names — and every one of
// them was labelled "the vendor answered". On building permits that put a
// section 51 certificate's statement in the vendor's mouth, on the one screen
// where somebody weighs the two against each other.
//
// The attribution comes from the middleware, per row, for the same reason the
// provenance tags and the fallback do: a copy of it here would drift from the
// rows it describes. `helperTitle` reads the sources present and only says
// "The vendor said" when that is the whole of it.
export const helperTitle = (helpers = []) => {
  const sources = new Set(helpers.map(h => h?.source).filter(Boolean));
  // An older middleware sends no source at all. Everything it sends *is* the
  // vendor's, since that is all it had — so the old label is right, and
  // guessing anything else would be worse than the bug.
  if (sources.size === 0 || (sources.size === 1 && sources.has("vendor"))) {
    return "The vendor said";
  }
  if (sources.size === 1 && sources.has("certificate")) return "The certificate states";
  return "Answers to check";
};

const VendorSaidBubble = ({ helpers }) => {
  const [open, setOpen] = useState(false);
  if (!helpers?.length) return null;

  const title = helperTitle(helpers);

  return (
    <div style={{ marginTop: 5 }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="What has been said about this field, and by whom"
        style={{
          display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10,
          color: "#8a6d1f", background: "rgba(196,122,26,0.10)",
          border: "1px solid rgba(196,122,26,0.28)", borderRadius: 999,
          padding: "1px 8px", cursor: "pointer", fontFamily: "Inter, sans-serif",
        }}
      >
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {title}{helpers.length > 1 ? ` (${helpers.length})` : ""}
      </button>

      {open && (
        <div style={{
          marginTop: 5, background: "rgba(196,122,26,0.06)",
          border: "1px solid rgba(196,122,26,0.22)", borderRadius: 8,
          padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8,
        }}>
          {helpers.map((h, i) => (
            <div key={i}>
              <div style={{ fontSize: 11, color: T.text }}>
                {/* No attribution rather than a wrong one, where an older
                    middleware sent none: the label and the answer are still
                    true on their own. */}
                <strong>{h.label}</strong> — {h.said ? `${h.said} ` : ""}<strong>{h.answer}</strong>
              </div>
              {h.particulars && (
                <div style={{ fontSize: 11, color: T.textSoft, marginTop: 2, fontStyle: "italic", lineHeight: 1.45 }}>
                  “{h.particulars}”
                </div>
              )}
              {/* Said every time, not once at the top. This is the sentence
                  that stops the quote being pasted into the box. */}
              <div style={{ fontSize: 10, color: T.textFaint, marginTop: 3, lineHeight: 1.45 }}>
                {h.note}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Where a blank field's answer is coming from.
//
// The middleware decides which of these a field is (`expectedFrom` on the
// registry) — this only writes the sentence. Four different waits, and telling
// them apart is the point: "leave it to AI" is true of a certificate and false
// of everything else, and a field waiting on nobody is the one case where
// typing a value is the right thing to do.
const EXPECTED_COPY = {
  certificate: "Read from the certificates once they are back — leave it to AI rather than typing it",
  form: "Comes from the client's form",
  // The office's own record of the matter, not the vendor's answers. Saying
  // "the client's form" for the conveyancer on the file invites somebody to
  // type it, which is the one thing this line exists to prevent.
  office: "Comes from the matter in Triconvey",
  firm: "Comes from firm settings",
  staff: "Nothing else answers this — type it here",
};

export const expectedFromText = (expected) => {
  if (!expected?.kind) return null;
  if (expected.kind === "certificate" && expected.documents?.length > 0) {
    return `Expected from the ${expected.documents.join(" or ")}`
      + " — leave it to AI to fill once all searches have returned";
  }
  return EXPECTED_COPY[expected.kind] || null;
};

const ExpectedFrom = ({ expected }) => {
  const text = expectedFromText(expected);
  if (!text) return null;
  // Amber for the one that is waiting on the reader, grey for the rest: a
  // field somebody has to type is work, and a field waiting on a certificate
  // is not.
  const colour = expected.kind === "staff" ? T.textFaint : "#2a8a52";
  return (
    <div style={{ fontSize: 10, color: colour, marginTop: 4, lineHeight: 1.4 }}>
      {text}
    </div>
  );
};

// What a field does, in its own words.
//
// **For a field whose blank state is a decision rather than an absence.** The
// due diligence checklist is the case that prompted it: leaving it alone is
// what makes the property type decide whether the checklist is attached, and
// nothing on the page could say so. The sentence is the middleware's — a copy
// here would drift from the behaviour it describes.
export const FieldNote = ({ field }) => (field?.note ? (
  <div style={{ fontSize: 11, color: T.textSoft, lineHeight: 1.45, marginTop: 6 }}>
    {field.note}
  </div>
) : null);

// True for a moment after `saving` goes from true to false — the save landed.
export const SAVED_SHOWN_MS = 1500;
const useJustSaved = (saving) => {
  const [shown, setShown] = useState(false);
  const was = useRef(saving);
  useEffect(() => {
    if (was.current && !saving) {
      setShown(true);
      const t = setTimeout(() => setShown(false), SAVED_SHOWN_MS);
      was.current = saving;
      return () => clearTimeout(t);
    }
    was.current = saving;
    return undefined;
  }, [saving]);
  return shown;
};

const ContractFieldRow = ({ field, value, source, fallback, candidates, warning, expectedFrom, helpers, inapplicable, onRevert, onSave, saving, first,
  // Whether this matter has to answer it — not every required field is
  // required on every matter. See `requiredHere`.
  required = false, onNotRequired, markingNotRequired, derivation, directory, onSaveToDirectory }) => {
  const asInput = (v) => contractFieldToInput(field.type, v);

  const incoming = asInput(value);
  const [draft, setDraft] = useState(incoming);
  const [dirty, setDirty] = useState(false);
  const lastIncoming = useRef(incoming);

  // Follow the resolved value when it genuinely changes underneath us — a
  // re-extraction, or this field's own save landing — but never on an
  // unrelated repaint, which would wipe whatever is half-typed in the box.
  useEffect(() => {
    if (lastIncoming.current !== incoming) {
      lastIncoming.current = incoming;
      setDraft(incoming);
    }
  }, [incoming]);

  const commit = () => {
    if (!dirty) return;
    setDirty(false);
    onSave(field.name, contractInputToValue(field.type, draft));
  };

  const isBlank = draft === "" || (Array.isArray(value) && value.length === 0);
  const needed = required && isBlank;

  // **A row says it saved** (DA-10). `saving…` showed while the write was in
  // flight and then nothing, so a correction that landed and one that never
  // went looked the same. A failure already toasts, loudly, from the save
  // itself; this is the quiet half for the ordinary case.
  const justSaved = useJustSaved(saving);
  // The label names the control, so clicking the field's name lands in its box
  // and a screen reader announces what the box is for.
  const controlId = `cf-${field.name}`;

  return (
    // The section draws the line above its first row, so a border here as well
    // would double it.
    <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 220px) 1fr", gap: 12, alignItems: "start", padding: "8px 0", borderTop: first ? "none" : `1px solid ${T.border}` }}>
      <div style={{ paddingTop: 7 }}>
        <label htmlFor={controlId} style={{ display: "block", fontSize: 12, color: needed ? T.red : T.textSoft, lineHeight: 1.35 }}>
          {field.label}
          {required && <span style={{ color: T.red, marginLeft: 3 }}>*</span>}
        </label>
        <div style={{ marginTop: 3, display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
          <ProvenanceTag source={source} type={field.type} because={source === "derived" ? derivation : undefined} />
          {saving && <span style={{ fontSize: 10, color: T.textFaint }}>saving…</span>}
          {!saving && justSaved && <span role="status" style={{ fontSize: 10, color: T.textFaint }}>Saved</span>}
        </div>
        {/* Only against a blank. This is what stops staff typing a value the
            certificate is about to answer — and what the compile would then
            keep, because a manual correction outranks the extraction.

            Once there *is* a value, the provenance tag says where it came
            from, so between the two every field accounts for itself either
            way. */}
        {isBlank && <ExpectedFrom expected={expectedFrom} />}
        {/* Why a value nobody typed is sitting there. It is the middleware's
            own sentence, and until now it reached no screen at all. */}
        <VendorSaidBubble helpers={helpers} />

        {/* **One press takes the whole group out, not this row.** The agent's
            details are required because the matter has an agent, and a private
            sale — or an agency with nothing more to give — is a real matter.
            Five rows nobody can clear is how a completeness list stops being
            read.

            Offered only against a blank one that is actually being asked for,
            so it never appears on a matter that has answered. */}
        {needed && onNotRequired && (
          <button
            type="button"
            onClick={onNotRequired}
            disabled={markingNotRequired}
            style={{
              font: "inherit", fontSize: 10, color: T.textFaint, background: "none",
              border: "none", padding: "4px 0 0", cursor: markingNotRequired ? "default" : "pointer",
              textDecoration: "underline", textUnderlineOffset: 2,
            }}
          >
            {markingNotRequired ? "Marking…" : "Not needed on this matter"}
          </button>
        )}
      </div>

      <div>
        {/* A question the sale has answered for itself.
            The middleware decides which — a property sold with vacant
            possession has no tenancy, so its type and the lease's particulars
            are not questions about it any more. Rendering the control anyway
            showed "Fixed term" ticked beside "With vacant possession", which
            is a contract contradicting itself on the screen staff work in.
            Nothing is cleared: the answer is named here so it plainly still
            exists, and comes back the moment possession changes. */}
        {inapplicable ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 34, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: T.textFaint }}>Not applicable</span>
            {contractFieldToInput(field.type, value) !== "" && (
              <span style={{ fontSize: 11, color: T.textFaint }}>
                · kept: {contractFieldToInput(field.type, value).replace(/\n/g, ", ")}
              </span>
            )}
          </div>
        ) : /* A control that answers with a click saves on the click. A text box
            saves on blur, because a half-typed value is not an answer — but a
            radio or a checkbox has no half-way state to wait for. */
        field.type === "flag" && "boxWhen" in field ? (
          <BoxFlagInput
            field={field}
            draft={draft}
            setDraft={(v) => { setDraft(v); setDirty(true); onSave(field.name, contractInputToValue(field.type, v)); }}
          />
        ) : "boxValue" in field ? (
          <BoxValueInput
            field={field}
            draft={draft}
            setDraft={(v) => { setDraft(v); setDirty(true); onSave(field.name, contractInputToValue(field.type, v)); }}
          />
        ) : field.options ? (
          <ChoiceInput
            field={field}
            draft={draft}
            setDraft={(v) => { setDraft(v); setDirty(true); onSave(field.name, contractInputToValue(field.type, v)); }}
          />
        ) : field.type === "flag" ? (
          <select
            id={controlId}
            value={draft}
            onChange={e => { setDraft(e.target.value); setDirty(true); }}
            onBlur={commit}
            style={{ ...inputStyle, maxWidth: 160, cursor: "pointer" }}
          >
            {/* Blank is a real option: "nobody has answered" and "no" are
                different statements, and only one belongs in a section 32. */}
            <option value="">— not answered —</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        ) : paragraphControl(field) ? (
          <textarea
            id={controlId}
            value={draft}
            onChange={e => { setDraft(e.target.value); setDirty(true); }}
            onBlur={commit}
            rows={editorRows(draft, field.type === "list" ? 3 : 2)}
            placeholder={field.type === "list" ? "One per line" : ""}
            style={{ ...inputStyle, resize: "vertical", minHeight: 38, lineHeight: 1.5 }}
          />
        ) : (
          /* **The field's own control, from the one ladder** (DA-09). This
             was the last surface still drawing a bare text box, so on the
             panel staff edit in most a settlement date read `2026-03-19` in
             free text and took whatever was typed. Enter commits a line, as
             it does in the popover — by blurring, so there is one save path. */
          <input
            id={controlId}
            {...fieldInputProps(field)}
            value={draft}
            onChange={e => { setDraft(e.target.value); setDirty(true); }}
            onBlur={commit}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
            style={field.type === "date" ? { ...inputStyle, maxWidth: 180 } : inputStyle}
          />
        )}

        <FieldNote field={field} />

        {/* Only an edit can be reverted, and only to something that exists. */}
        {source === "manual" && (
          <RevertToSource field={field} entry={fallback} onRevert={onRevert} saving={saving} />
        )}
        {/* The panel's half of the popover's tick. This screen saves on blur,
            so there is no Save button to sit beside — the press is offered
            under a correction instead, and waits for that correction's own
            save before it reads anything. */}
        {source === "manual" && (
          <SaveToDirectoryLink field={field} directory={directory}
            onSave={onSaveToDirectory} saving={saving} />
        )}
        <OtherVersions field={field} value={value} source={source} fallback={fallback}
          candidates={candidates} onSave={onSave} saving={saving} />
        {/* A filled value worth a second look — the eight-digit agent phone.
            The middleware computes these; a copy of the rule here would
            drift. It warns, never gates. */}
        {warning && (
          <div style={{ fontSize: 11, color: "#c47a1a", marginTop: 5, fontWeight: 500 }}>
            {warning}
          </div>
        )}
      </div>
    </div>
  );
};

export const ContractDataPanel = ({ matterRef, registry, contractFields, documentReview, agentDirectory, onAdoptAgent, adoptingAgent, onSaveToDirectory, onSave, onRevert, onRevertAll, revertingAll, savingFields, onNotRequired, markingNotRequired, defaultOpen = false, stage, revealSignal = 0, reviewOnly = false }) => {
  // **`reviewOnly` is the practice look's contract page**: the matter's data is
  // entered on the Matter tab (every field has a row there), so this is the
  // review — what is missing or disputed, each answered in place, never a trip
  // to another tab. The full list of fields is Matter details'.
  const [open, setOpen] = useStagedSection(defaultOpen, stage);
  // *Fill in →* on the matter's issues strip opens this and brings it on
  // screen — a counter, like every other reveal here.
  const rootRef = useRef(null);
  useRevealSection(revealSignal, setOpen, rootRef);

  // ── A panel with something to answer opens itself ──────────────────────
  //
  // **On the firm's instruction, and it could not be done in
  // `sectionDefaults`.** That decides from the status alone, at mount — and at
  // mount this panel has no `contractFields` at all: the matter screen is
  // seeded from the dashboard row and the fields arrive with the matter read a
  // moment later. A default computed before they land can only ever say
  // *nothing is needed*.
  //
  // **It opens; it never closes.** A reset key over the count would collapse
  // the panel under somebody the instant they answered the last field, which
  // is the "worse than no defaults at all" failure `useStagedSection` is
  // written against. So this is one-way, and once: closing it is then a
  // decision that stands.
  //
  // **Except on a released matter**, where the contract has gone out and this
  // screen is read rather than filled — the one stage the firm named.
  const openedForMissing = useRef(false);
  const needed = contractFields?.missing?.length || 0;
  useEffect(() => {
    if (openedForMissing.current) return;
    if (stage === "released" || needed === 0) return;
    openedForMissing.current = true;
    setOpen(true);
  }, [needed, stage, setOpen]);
  const [showAllChosen, setShowAll] = useState(false);
  const showAll = showAllChosen && !reviewOnly;
  const [confirmRevertAll, setConfirmRevertAll] = useState(false);
  // **What has been answered while this matter has been on screen.**
  //
  // Adopting a directory record or a web result fills four or five fields in
  // one write, and every one of them left the worklist the instant it landed —
  // so what the adoption actually wrote was invisible at exactly the moment
  // somebody would check it. These stay until the matter changes, which is
  // what "until staff navigate away" means here.
  //
  // It catches a typed answer too, which is the same thing wanting the same
  // treatment: a row vanishing as you finish filling it in is disorienting,
  // and leaving it there is how you see what you just wrote.
  const [kept, setKept] = useState([]);
  // The names outstanding at the last render, and the matter they belonged to.
  // A ref rather than state: comparing two renders is not itself a render.
  const seen = useRef({ ref: null, missing: [] });
  // Only the browsing view has sections, so there is only one of these. The
  // worklist is a flat list of what is missing — a heading there would be a
  // menu over a list that is already the shortest it can be.
  const [closed, setClosed] = useState(() => new Set(GROUP_ORDER.filter(g => !groupStartsOpen(g))));
  const toggleGroup = (group) => setClosed(prev => {
    const next = new Set(prev);
    if (!next.delete(group)) next.add(group);
    return next;
  });

  // ── Jumping to a section ──────────────────────────────────────────────────
  //
  // There are ten sections and eighty-odd fields, so reaching the agent's
  // details from the top meant scrolling past the whole section 32 block. This
  // is the panel's own contents list.
  //
  // **A jump opens the section as well as scrolling to it.** Landing on a
  // collapsed heading is landing on the summary line, which is not what
  // somebody pressing *Agent* is asking for.
  //
  // Two renders are involved — opening the group, then scrolling to it — so
  // the target is held in state and the scroll happens in an effect once the
  // section is actually on the page. Doing it in the click handler scrolls to
  // where the heading was before the section above it grew.
  const groupRefs = useRef({});
  const [jumpTarget, setJumpTarget] = useState(null);

  const jumpTo = (group) => {
    setClosed(prev => {
      const next = new Set(prev);
      next.delete(group);
      return next;
    });
    setJumpTarget(group);
  };

  useEffect(() => {
    if (!jumpTarget) return;
    const el = groupRefs.current[jumpTarget];
    // Guarded on the method rather than the node: jsdom implements no
    // scrolling, and a nicety throwing out of an effect takes the panel with
    // it — the same reason the drafter's scroll is guarded.
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setJumpTarget(null);
  }, [jumpTarget]);

  // **Which fields have been answered since this matter came on screen.**
  //
  // Compared against the previous render rather than reported by whoever wrote
  // them: an adoption, a web result, a typed correction and a revert all reach
  // this the same way, and threading a callback through each would be four
  // places to forget. Keyed on the names rather than the array, which is new
  // on every render.
  //
  // Above the early returns because a hook has to be — so it reads
  // `contractFields` defensively rather than the destructured `missing` below.
  const outstanding = (contractFields?.missing || []).map(m => m.name);
  const outstandingKey = outstanding.join(",");
  // What each field holds now, read by the effect below through a ref: a field
  // leaves `missing` either because it was answered or because its group was
  // marked not required, and only the first is worth keeping on screen.
  // Marking the agent not required kept five blank rows on the worklist until
  // staff left the matter.
  const valuesNow = useRef({});
  valuesNow.current = contractFields?.values || {};
  useEffect(() => {
    const now = outstandingKey ? outstandingKey.split(",") : [];
    // A different matter starts again — this is what "until staff navigate
    // away" means, and without it one matter's answered rows would linger on
    // the next.
    if (seen.current.ref !== matterRef) {
      seen.current = { ref: matterRef, missing: now };
      setKept([]);
      return;
    }
    const filled = (v) => v != null && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
    const answered = seen.current.missing
      .filter(name => !now.includes(name) && filled(valuesNow.current[name]));
    seen.current = { ref: matterRef, missing: now };
    if (answered.length) setKept(k => [...new Set([...k, ...answered])]);
  }, [matterRef, outstandingKey]);

  // No contractFields means the matter hasn't loaded yet. Nothing to say.
  if (!contractFields) return null;

  const { values = {}, provenance = {}, missing = [], fallback = {}, candidates = {}, warnings = {}, derivations = {} } = contractFields;
  // Which certificate will answer each blank. From the middleware, because the
  // mapping is a conveyancing judgement and a copy here would drift from the
  // one the searches review uses.
  const hints = documentReview?.hints || {};
  // Questions the sale has answered for itself — a property sold with vacant
  // possession has no tenancy, so its type and the lease's particulars are not
  // questions about it. From the middleware, because the rule decides what the
  // *document* prints too and a second copy here would drift from it.
  const inapplicable = new Set(contractFields?.inapplicable || []);
  // What the vendor said, keyed by the field it bears on. Computed by the
  // middleware for the same reason as the hints: which Form 27 answer informs
  // which disclosure is a conveyancing judgement, and a second copy here would
  // drift from the one the searches review already uses.
  const helpers = contractFields?.helpers || {};

  // The registry is fetched, so it can fail. Returning null here made a failed
  // fetch look identical to the feature not being deployed — which is exactly
  // how a stale cached bundle got mistaken for a bug in the panel. `missing`
  // carries its own labels, so the part staff need still works.
  if (!registry?.length) {
    return (
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 6 }}>Contract data</div>
        <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
          Couldn&rsquo;t load the field list, so this can&rsquo;t be edited here right now — reload the page to try again.
          {missing.length > 0 && (
            <> The compile is still missing <strong>{missing.length}</strong> required field{missing.length !== 1 ? "s" : ""}: {missing.map(m => m.label).join(", ")}.</>
          )}
        </div>
      </div>
    );
  }
  const missingNames = new Set(missing.map(m => m.name));
  const filled = registry.filter(f => provenance[f.name]).length;

  // Only edits with something underneath. Reverting the rest would blank them,
  // so they aren't part of what "revert all" offers to undo — and the count has
  // to agree with what the middleware will actually do, or the button promises
  // more than it delivers.
  const revertable = registry.filter(f => provenance[f.name] === "manual" && fallback[f.name]);

  // Default to the fields that need attention, plus the two things that earn
  // their place beside them — see `worklistFields`. Seventy-one rows of mostly
  // filled data buries the handful someone actually has to act on.
  const visible = showAll ? registry : worklistFields(registry, [...missingNames], kept);

  // **The directory offer is a heading for the agent fields, not a note on
  // one.** Pressing it fills the agency, its address and phone and the
  // salesperson's details in a single write, and hung under a single row it
  // read as an answer to that row — staff met it under *agency address*.
  //
  // Read off the list being rendered rather than off `missing`, so it is in
  // the same place in the worklist and in the full list, and does not vanish
  // from the worklist the moment the last agent field is answered.
  const directoryLead = directoryLeadField(visible);
  // **An element, not a component defined in render.** A function declared
  // here is a new component *type* every render, so React would unmount and
  // remount what it holds — and `DirectoryMatch` keeps the search box, the
  // agency roster it has fetched and whether the list is being shown again in
  // state of its own. All of it would be thrown away the next time anything on
  // the panel changed.
  const directoryLeadBlock = agentDirectory ? (
    <div style={{ padding: "12px 0 2px" }}>
      <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
        Select an existing card from the directory — one choice fills these fields.
      </div>
      <DirectoryMatch directory={agentDirectory} onAdopt={onAdoptAgent} adopting={adoptingAgent} />
    </div>
  ) : null;
  const leadsHere = (name) => (name && name === directoryLead ? directoryLeadBlock : null);

  // **Every section, always.** Building the list from `visible` and then
  // dropping the empty ones meant a section could only appear if it held a
  // required field nobody had filled — so Conveyancing details and the
  // vendor's agent, which have no required fields at all, could never appear,
  // and the acting solicitor vanished as soon as its three were set. The
  // sections are a structure staff navigate, not a list of outstanding work;
  // what the filter decides is what is *inside* one.
  const groups = panelSections(registry, visible);

  const complete = missing.length === 0;

  return (
    <div ref={rootRef} data-tour="contract-data" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12, scrollMarginTop: UNDER_BAR_CLEAR }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{reviewOnly ? "Contract review" : "Contract data"}</span>
          {complete ? (
            <span style={{ fontSize: 11, color: "#2a8a52", background: "rgba(42,138,82,0.1)", padding: "2px 8px", borderRadius: 999, fontWeight: 500 }}>
              ✓ Complete
            </span>
          ) : (
            <span style={{ fontSize: 11, color: T.red, background: "rgba(254,84,66,0.08)", padding: "2px 8px", borderRadius: 999, fontWeight: 500 }}>
              {missing.length} still needed
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: T.textFaint }}>
          {filled} of {registry.length} filled
        </span>
      </div>

      {!open && !complete && (
        <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginTop: 10 }}>
          A section 32 compiled now would have blanks in it. Open this to fill them.
        </div>
      )}

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, maxWidth: 560 }}>
              {showAll
                ? "Every field the contract and section 32 draw on. Edits here override the certificates and survive a re-extraction."
                : reviewOnly
                  ? "What a valid section 32 still needs, and where the sources disagree — answer it here. Every other field is on the Matter tab, under Matter details."
                  : "The fields a valid section 32 needs that nothing has filled yet."}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {revertable.length > 0 && (
                confirmRevertAll ? (
                  <>
                    <span style={{ fontSize: 11, color: T.textSoft }}>
                      Undo {revertable.length} edit{revertable.length !== 1 ? "s" : ""}?
                    </span>
                    <Btn variant="ghost" size="sm" onClick={() => setConfirmRevertAll(false)}>Keep</Btn>
                    <Btn size="sm" disabled={revertingAll}
                         onClick={async () => { await onRevertAll(); setConfirmRevertAll(false); }}>
                      {revertingAll ? "Reverting…" : "Revert all"}
                    </Btn>
                  </>
                ) : (
                  <Btn variant="ghost" size="sm" onClick={() => setConfirmRevertAll(true)}>
                    Revert {revertable.length} edit{revertable.length !== 1 ? "s" : ""}
                  </Btn>
                )
              )}
              {!reviewOnly && (
                <Btn variant="ghost" size="sm" onClick={() => setShowAll(s => !s)}>
                  {showAll ? "Show only what's needed" : "Show all fields"}
                </Btn>
              )}
            </div>
          </div>

          {/* The worklist is what is missing, and nothing else. Grouping it
              puts a menu over a list that is already the shortest it can be —
              and every heading there would either be empty or hold one row. */}
          {!showAll ? (
            visible.length === 0 ? (
              <div style={{ fontSize: 12, color: T.textSoft, padding: "14px 0" }}>
                Nothing outstanding — every required field has a value.
              </div>
            ) : visible.map((f, i) => (
              <div key={f.name}>
              {leadsHere(f.name)}
              <ContractFieldRow
                first={i === 0}
                field={f}
                value={values[f.name]}
                source={provenance[f.name]}
                derivation={derivations[f.name]}
                fallback={fallback[f.name]}
                candidates={candidates[f.name]}
                warning={warnings[f.name]}
                expectedFrom={hints[f.name]?.length
                  ? { kind: "certificate", documents: hints[f.name] }
                  : f.expectedFrom}
                helpers={helpers[f.name]}
                inapplicable={inapplicable.has(f.name)}
                onRevert={onRevert}
                onSave={onSave}
                directory={agentDirectory}
                onSaveToDirectory={onSaveToDirectory}
                saving={savingFields.has(f.name)}
                required={requiredHere(f, contractFields)}
                onNotRequired={notRequiredGroupOf(f.name) && onNotRequired
                  ? () => onNotRequired(notRequiredGroupOf(f.name))
                  : null}
                markingNotRequired={markingNotRequired}
              />
              </div>
            ))
          ) : (
          <>
          {/* The panel's own contents list. Grouped mode only — the worklist
              is a flat list of what is missing, already the shortest it can
              be, and a menu over it would be a menu over four rows. */}
          {groups.length > 1 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "10px 0 2px" }}>
              <span style={{ fontSize: 11, color: T.textFaint, marginRight: 2 }}>Jump to</span>
              {groups.map(({ group, fields }) => {
                // A section with something outstanding is the reason to jump,
                // so it says so — that is the whole use of a contents list on
                // a panel whose job is to show what is still needed.
                const outstanding = fields.filter(f => missingNames.has(f.name)).length;
                return (
                  <button
                    key={group}
                    type="button"
                    onClick={() => jumpTo(group)}
                    style={{
                      font: "inherit", fontSize: 11, color: outstanding ? "#c47a1a" : T.textSoft,
                      background: T.white, border: `1px solid ${outstanding ? "rgba(196,122,26,0.35)" : T.border}`,
                      borderRadius: 999, padding: "3px 9px", cursor: "pointer", lineHeight: 1.4,
                    }}
                  >
                    {GROUP_LABELS[group] || group}
                    {outstanding > 0 && (
                      <span style={{ marginLeft: 5, fontWeight: 600 }}>{outstanding}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {groups.map(({ group, fields, total }) => {
            const shut = closed.has(group);
            // What a shut section is holding. A collapsed heading that said
            // nothing would hide the very fields this panel exists to surface
            // — so it says how many, and says it in amber when any of them are
            // a hole in the section 32 rather than a blank to fill at sale.
            const needed = fields.filter(f => missingNames.has(f.name)).length;
            // …and what is actually in it, which is what Triconvey's card puts
            // beside the heading. A count tells you to open it; an answer tells
            // you whether you need to.
            const summary = sectionSummary(group, registry, values);
            const runs = subheadedFields(fields);
            // Nothing to subhead — one unlabelled run, which is every group
            // but the disclosures.
            const plain = runs.length === 1 && !runs[0].section;
            return (
            <div
              key={group}
              ref={(el) => { groupRefs.current[group] = el; }}
              // Collapsed, the whole row is the target — the summary beside the
              // heading is the part you are looking at, and having to come back
              // to a ten-pixel chevron to open it is a worse click than none.
              // Expanded, only the heading toggles: the row is full of inputs.
              onClick={shut ? () => toggleGroup(group) : undefined}
              style={{ display: "grid", gridTemplateColumns: "minmax(140px, 190px) 1fr", gap: 12, alignItems: "start", borderTop: `1px solid ${T.border}`, cursor: shut ? "pointer" : "default", scrollMarginTop: 12 }}
            >
              {/* The heading column. Sticky so it stays beside its fields on a
                  long section rather than scrolling away from them. */}
              <button
                onClick={(e) => { e.stopPropagation(); toggleGroup(group); }}
                aria-expanded={!shut}
                style={{ display: "flex", alignItems: "flex-start", gap: 6, background: "none", border: "none", padding: "12px 0 10px", cursor: "pointer", fontFamily: "Inter, sans-serif", textAlign: "left", position: "sticky", top: 0, alignSelf: "start" }}
              >
                <svg width="10" height="10" fill="none" stroke={T.textFaint} strokeWidth="2.5" viewBox="0 0 24 24"
                     style={{ transform: shut ? "none" : "rotate(90deg)", transition: "transform 0.15s", flexShrink: 0, marginTop: 3 }}>
                  <path d="m9 18 6-6-6-6"/>
                </svg>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1.4 }}>
                  {GROUP_LABELS[group] || group}
                </span>
              </button>

              <div style={{ minWidth: 0 }}>
                {shut && (
                  <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", padding: "12px 0 10px" }}>
                    {summary.map((text, i) => (
                      <span key={i} style={{ fontSize: 12, color: T.text, lineHeight: 1.4 }}>{text}</span>
                    ))}
                    <span style={{ fontSize: 11, color: needed > 0 ? "#c47a1a" : T.textFaint }}>
                      {needed > 0
                        ? `${needed} still needed`
                        : `${total} field${total !== 1 ? "s" : ""}`}
                    </span>
                  </div>
                )}
                {/* An open section with nothing in it says so, rather than
                    reading as a heading somebody forgot to fill. Only reachable
                    for a namespace the registry declared and never filled. */}
                {!shut && fields.length === 0 && (
                  <div style={{ fontSize: 11, color: T.textFaint, padding: "12px 0 10px" }}>
                    No fields here.
                  </div>
                )}
                {/* A group with no subheadings keeps its fields here, beside
                    the heading, exactly as before. Every group but the
                    disclosures is this, and pushing them into grid rows of
                    their own would drop the heading onto a band of its own and
                    cost a line on each. */}
                {!shut && plain && runs[0].fields.map((f, i) => (
                  <div key={f.name}>
                  {leadsHere(f.name)}
                  <ContractFieldRow
                    first={i === 0}
                    field={f}
                    value={values[f.name]}
                    source={provenance[f.name]}
                    derivation={derivations[f.name]}
                    fallback={fallback[f.name]}
                    candidates={candidates[f.name]}
                        warning={warnings[f.name]}
                    expectedFrom={hints[f.name]?.length
                      ? { kind: "certificate", documents: hints[f.name] }
                      : f.expectedFrom}
                    helpers={helpers[f.name]}
                    inapplicable={inapplicable.has(f.name)}
                    onRevert={onRevert}
                    onSave={onSave}
                    directory={agentDirectory}
                    onSaveToDirectory={onSaveToDirectory}
                    saving={savingFields.has(f.name)}
                    required={requiredHere(f, contractFields)}
                    onNotRequired={notRequiredGroupOf(f.name) && onNotRequired
                      ? () => onNotRequired(notRequiredGroupOf(f.name))
                      : null}
                    markingNotRequired={markingNotRequired}
                  />
                  </div>
                ))}
              </div>

              {/* **The subheading sits in the heading column, not over the
                  fields.** Indented into the value column it read as a label
                  belonging to the field under it rather than to the run, and a
                  group of fifty is exactly where somebody scans the left edge
                  for the part they want. Lighter and smaller than the group
                  heading, so the hierarchy still reads at a glance.

                  **`display: contents` rather than a margin.** The heading
                  column is `minmax(140px, 190px)`, so its real width depends on
                  the container — a negative margin guessing at it drifts out of
                  alignment at some widths, which is worse than not aligning at
                  all. Dissolving the wrapper makes each run two cells of the
                  group's own grid, so the subheading is *in* that column by
                  construction. */}
              {!shut && !plain && runs.map((run, runIndex) => (
                <div key={run.section || `run-${runIndex}`} style={{ display: "contents" }}>
                  {/* **Indented onto the group heading's text edge**, which
                      is 16px in: that heading leads with a 10px chevron and a
                      6px gap, and the subheading has neither, so left-aligning
                      both to the column put them on two different edges 16px
                      apart. Aligned, the group and its sections read as one
                      ladder.

                      **Left, not right-justified.** Right-aligning would sit
                      each heading hard against its own fields, which reads
                      well for one label and badly for twelve: ragged-left text
                      gives the eye no common edge to run down, and scanning
                      for the part you want is the whole reason these exist.
                      The size and weight already separate the two levels. */}
                  <div style={{
                    fontSize: 10.5, fontWeight: 600, color: T.textFaint,
                    textTransform: "uppercase", letterSpacing: "0.07em",
                    lineHeight: 1.4, padding: "14px 0 10px 16px", minWidth: 0,
                  }}>
                    {run.section}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    {run.fields.map((f, i) => (
                  <div key={f.name}>
                  {leadsHere(f.name)}
                  <ContractFieldRow
                    first={i === 0}
                    field={f}
                    value={values[f.name]}
                    source={provenance[f.name]}
                    derivation={derivations[f.name]}
                    fallback={fallback[f.name]}
                candidates={candidates[f.name]}
                warning={warnings[f.name]}
                    expectedFrom={hints[f.name]?.length
                      ? { kind: "certificate", documents: hints[f.name] }
                      : f.expectedFrom}
                    helpers={helpers[f.name]}
                    inapplicable={inapplicable.has(f.name)}
                    onRevert={onRevert}
                    onSave={onSave}
                    directory={agentDirectory}
                    onSaveToDirectory={onSaveToDirectory}
                    saving={savingFields.has(f.name)}
                    required={requiredHere(f, contractFields)}
                    onNotRequired={notRequiredGroupOf(f.name) && onNotRequired
                      ? () => onNotRequired(notRequiredGroupOf(f.name))
                      : null}
                    markingNotRequired={markingNotRequired}
                  />
                  </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            );
          })}
          </>
          )}
        </div>
      )}
    </div>
  );
};

// ── The matter timeline ────────────────────────────────────────────
// What has actually happened on this matter, in order.
//
// Rendered entirely from GET /events/:ref. The lines and the audience
// filtering are computed by the middleware — nothing here decides what a step
// is called, so this screen and any future agent-facing view can't end up
// describing the same matter differently.
//
// **Who did it is the actor, and that is the only label.** A step a model
// performed reads *Automatic* like any other step nobody pressed: what staff
// need from the timeline is whether a person did it, and an AI badge beside an
// actor already saying so was a second label on one fact. The middleware still
// sends `ai`; nothing here renders it.
//
// It shows only what was recorded. A matter that predates the log has an empty
// timeline rather than a reconstructed one; events can't be backfilled, and
// guessing at them would defeat the point.

const ACTOR_LABELS = {
  system: "Automatic", staff: "Provey", client: "Client",
  agent: "Agent", service: "Trisearch", ai: "Automatic",
};

// The event's own time, as a short local stamp. `at` arrives as ISO 8601.
const formatEventTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-AU", {
    day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  });
};

// ── What this matter has sent ──────────────────────────────────────────────
//
// Every email that left the service about this matter, newest first, with the
// letter itself behind each row.
//
// **It is not a second History panel**, and the split is worth holding: the
// timeline says a release happened, this holds what the release actually said.
// The release letter varies per matter — the 137B draft sentence, the material
// facts the vendor disclosed, the custom paragraph — so it is the only record
// of what a particular agent was told. Six months on, *"what did we tell them
// on 3 March"* has no other answer, and the timeline deliberately does not
// carry one: its wording is computed, so quoting a message there would be a
// second copy of the email that carries it.
//
// **Nothing inbound yet.** This reads what the service sent and says so in as
// many words — a tab that looked like an inbox and held only half the
// conversation would read as a mailbox that had lost things.
const SentEmails = ({ matterRef, refreshKey }) => {
  const [state, setState] = useState({ loading: true, emails: [], error: null });
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/matters/${encodeURIComponent(matterRef)}/emails`)
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (!res.ok || !body?.ok) throw new Error(body?.error || `Could not load (${res.status})`);
        return body.emails || [];
      })
      .then((emails) => { if (!cancelled) setState({ loading: false, emails, error: null }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, emails: [], error: err.message }); });
    return () => { cancelled = true; };
    // Same key the History panel watches — a release or a compile has probably
    // sent something.
  }, [matterRef, refreshKey]);

  const { loading, emails, error } = state;

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Sent from this matter</span>
        <span style={{ fontSize: 11, color: T.textFaint, flexShrink: 0 }}>
          {loading ? "…" : error ? "unavailable" : `${emails.length} message${emails.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {/* Said plainly rather than implied by an empty half of the screen. */}
      <div style={{ fontSize: 12, color: T.textFaint, lineHeight: 1.5, marginBottom: 14 }}>
        What the service has emailed about this matter. Replies land in your own mailbox — nothing received is shown here yet.
      </div>

      {error && (
        <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
          Couldn&rsquo;t load what was sent — {error}
        </div>
      )}

      {!error && !loading && emails.length === 0 && (
        <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
          Nothing has gone out yet. Releases, signing links and the office&rsquo;s own notices appear here as they are sent.
        </div>
      )}

      {!error && emails.map((e) => {
        const open = openId === e.id;
        return (
          <div key={e.id} style={{ borderTop: `1px solid ${T.border}`, padding: "11px 0" }}>
            <div
              onClick={() => setOpenId(open ? null : e.id)}
              style={{ display: "flex", gap: 10, alignItems: "baseline", cursor: "pointer" }}
            >
              <svg width="11" height="11" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
                   style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s", flexShrink: 0, marginTop: 3 }}>
                <path d="m9 18 6-6-6-6"/>
              </svg>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12.5, color: T.text, fontWeight: 500 }}>{e.label}</span>
                  {/* A message the envelope moved is marked, or a test send
                      reads as a letter that reached the client. */}
                  {e.redirectedTo && (
                    <span style={{ fontSize: 10.5, color: T.red, border: `1px solid ${T.red}`, borderRadius: 999, padding: "1px 7px" }}>
                      redirected in test mode
                    </span>
                  )}
                  {e.attachment && (
                    <span style={{ fontSize: 11, color: T.textFaint }}>
                      {e.attachment.filename}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: T.textSoft, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {e.subject}
                </div>
                <div style={{ fontSize: 11.5, color: T.textFaint, marginTop: 2 }}>
                  to {e.to}{e.cc ? `, cc ${e.cc}` : ""}
                </div>
              </div>
              <span style={{ fontSize: 11, color: T.textFaint, flexShrink: 0 }}>{formatEventTime(e.sentAt)}</span>
            </div>

            {open && (
              <div style={{ marginTop: 10, marginLeft: 21 }}>
                {e.redirectedTo && (
                  <div style={{ fontSize: 11.5, color: T.red, marginBottom: 8, lineHeight: 1.5 }}>
                    Test mode was on — this was delivered to {e.redirectedTo}, not to {e.to}.
                  </div>
                )}
                {/* The letter as it was sent. `pre-wrap` because it is a plain
                    text body with its own line breaks, and re-flowing it would
                    show something other than what went out. */}
                <div style={{ fontSize: 12, color: T.text, lineHeight: 1.6, whiteSpace: "pre-wrap", background: T.bgAlt, borderRadius: 8, padding: "12px 13px", maxHeight: 420, overflow: "auto" }}>
                  {e.body}
                </div>
                {e.truncated && (
                  <div style={{ fontSize: 11, color: T.textFaint, marginTop: 6 }}>
                    This message was too long to keep whole — what is shown is the start of it.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// `defaultOpen` is for the redrawn layout, where the Activity pane holds this
// and nothing else: a closed panel there is a tab that opens onto one line and
// a chevron. The old layout keeps it closed — there it sits at the foot of a
// long page among other panels, and its header already carries the latest
// event.
const Timeline = ({ matterRef, refreshKey, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [state, setState] = useState({ loading: true, events: [], error: null });

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/events/${encodeURIComponent(matterRef)}`)
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (!res.ok || !body?.ok) throw new Error(body?.error || `Timeline failed (${res.status})`);
        return body.events || [];
      })
      .then((events) => { if (!cancelled) setState({ loading: false, events, error: null }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, events: [], error: err.message }); });
    return () => { cancelled = true; };
    // refreshKey changes when the matter does — a compile or a save has
    // probably added events.
  }, [matterRef, refreshKey]);

  const { loading, events, error } = state;
  const latest = events.length ? events[events.length - 1] : null;

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>History</span>
          {/* The most recent line, in the header — it's what someone wants at a
              glance, and it saves opening the panel to find out. */}
          {latest && !open && (
            <span style={{ fontSize: 12, color: T.textSoft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              — {latest.line}
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: T.textFaint, flexShrink: 0 }}>
          {loading ? "…" : error ? "unavailable" : events.length === 0 ? "nothing recorded" : `${events.length} event${events.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          {error && (
            <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
              Couldn&rsquo;t load the history — {error}
            </div>
          )}

          {!error && !loading && events.length === 0 && (
            <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
              Nothing recorded yet. History isn&rsquo;t reconstructed, so matters opened before it existed start empty.
            </div>
          )}

          {events.map((e, i) => (
            <div key={e.id || i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "7px 0" }}>
              {/* Dot and rail, so it reads as a sequence rather than a list. */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, alignSelf: "stretch" }}>
                <div style={{ width: 7, height: 7, borderRadius: 999, background: T.borderMid, marginTop: 5 }} />
                {i < events.length - 1 && <div style={{ flex: 1, width: 1, background: T.border, marginTop: 3 }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: T.text, lineHeight: 1.45 }}>
                  {e.line}
                </div>
                <div style={{ fontSize: 11, color: T.textFaint, marginTop: 1 }}>
                  {formatEventTime(e.at)}
                  {e.actorName ? ` · ${e.actorName}` : (ACTOR_LABELS[e.actor] ? ` · ${ACTOR_LABELS[e.actor]}` : "")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Additional Special Conditions ──────────────────────────────────
// The clauses staff add that the precedent doesn't cover. The contract renders
// them numbered, after its own, and prints no heading when there are none.
//
// A whole-list editor rather than a field-at-a-time one, because that is what
// the endpoint is: the list is ordered, and there is nothing stable to merge
// against, so it is saved and replaced entire. **The numbers are written by
// the box, never typed**: `NumberedClauseInput` (src/clauseNumbering.js)
// relabels every paragraph from its position as it is shown, and the
// middleware renumbers whatever it is sent — so a clause that moves up the
// list cannot print under the number it was typed with.

// Ids for React's benefit only. Reordering with array indices as keys moves the
// caret to a different row mid-sentence.
let conditionUid = 0;

// **The matter's own conditions are special condition 14 onwards.** The
// precedent's are 1 to 13; the saved precedents and then the matter's own
// follow on from them, and the precedent's 14 takes the number after the last
// (the middleware's `FIRST_MATTER_CONDITION`, which its template build checks
// against the precedent). Shown here so the box numbers a clause as the
// contract will.
export const FIRST_MATTER_CONDITION = 14;

const toConditionDraft = (list) => (list || []).map(c => ({
  uid: ++conditionUid,
  title: typeof c === "string" ? "" : (c?.title || ""),
  text: typeof c === "string" ? c : (c?.text || ""),
  ...(c?.precedentId ? { precedentId: c.precedentId } : {}),
}));

// The shape the middleware stores, so a draft can be compared against what came
// back from it without either side guessing at the other's format.
//
// Saved precedents first, as the middleware stores them — compared in any
// other order, a list read back would always look unsaved.
export const toConditionPayload = (draft) => [
  ...draft.filter(d => d.precedentId), ...draft.filter(d => !d.precedentId),
]
  .map(d => ({ title: d.title.trim(), text: d.text.trim(), precedentId: d.precedentId }))
  .filter(d => d.title !== "" || d.text !== "")
  .map(d => ({
    ...(d.title ? { title: d.title } : {}),
    text: d.text,
    ...(d.precedentId ? { precedentId: d.precedentId } : {}),
  }));

// ── Special conditions and other documents ─────────────────────────────────
//
// The two things that *customise* what goes out — clauses staff add to the
// contract, and the owners corporation certificate the office drafts itself —
// grouped rather than sitting loose between the contract data panel and the
// preview.
//
// **They are customisation, not the main path.** Most matters need neither:
// the contract prints from the registry and the pack is what came back from
// Trisearch. Two cards permanently open in the middle of the page said
// otherwise, and pushed the preview further down on every matter.
//
// **It is a container, and meant to grow.** Anything else that changes what
// the contract or the section 32 says, rather than what it says *about*, goes
// in here beside them — one line each, and the page keeps one place to look.
export const CustomisationSection = ({ conditionCount, revealSignal = 0, children }) => {
  // Open when the matter already has something in it. A matter carrying
  // clauses somebody wrote should not hide them behind two clicks, and one
  // carrying nothing should not spend the space.
  const [open, setOpen] = useState(conditionCount > 0);
  // **And openable from the contract.** Special condition 14.1 says the
  // contract is subject to conditions "appearing on the following pages", and
  // on a matter with none this container is shut — so the clause described an
  // attachment that was not there and the panel that would make it true was
  // two collapsed sections down the page.
  const sectionRef = useRef(null);
  useRevealSection(revealSignal, setOpen, sectionRef);

  // White like every other panel on the page. The raised grey said this was a
  // different *kind* of thing, when all it is is a collapsed panel holding two
  // more — and a container that looks unlike its neighbours reads as something
  // disabled rather than something closed.
  return (
    <div ref={sectionRef} data-tour="conditions" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>
            Special conditions and other documents
          </span>
        </div>
        <span style={{ fontSize: 11, color: T.textFaint }}>
          {conditionCount > 0
            ? `${conditionCount} clause${conditionCount === 1 ? "" : "s"}`
            : "Nothing added"}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginBottom: 12, maxWidth: 660 }}>
            Extra wording and documents. An ordinary sale needs none of it.
          </div>
          {children}
        </div>
      )}
    </div>
  );
};

// ── The owners corporation certificate the office drafts itself ────────────
//
// A section 151 certificate is normally bought from the corporation's
// professional manager. A three-or-more-lot subdivision with no manager has
// nobody to buy it from, so this office prepares it — and section 6 of the
// statement says a current certificate is attached, which without one is a
// claim about a document that is not in the pack.
//
// **Offered on any matter affected by an owners corporation**, and never on one
// that is not. Whether the office actually has to draft it depends on there
// being no manager, which nothing records — so staff decide, and the panel
// never hides the button on a matter that needs it.
//
// **It fetches its own data, lazily.** The vocabulary and the record are only
// needed once somebody opens this, on a small minority of matters, so loading
// them with every matter would be a round trip nobody uses. It also keeps this
// out of `MatterDetail`'s prop list, which is long enough.
const OC_GROUP_LABELS = {
  corporation: "The owners corporation",
  application: "The application",
  fees: "Fees and levies",
  insurance: "Insurance",
  funds: "Funds and liabilities",
  obligations: "Notices, proceedings and management",
  certificate: "Attachments and signing",
};

export const OcCertificatePanel = ({ matterRef, searchesKey = "", onGenerated }) => {
  // The firm's theme colour in the practice look, the classic red otherwise.
  const fluent = useFluent();
  const [open, setOpen] = useState(false);
  const [registry, setRegistry] = useState(null);
  const [record, setRecord] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState({});
  const [generating, setGenerating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  // Bumped after a save so the preview iframe re-fetches — it renders from the
  // stored record, so a stale frame would show the value before the edit.
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (!open || registry) return;
    let cancelled = false;
    apiFetch("/oc-certificate").then(r => r.json()).then(reg => {
      if (cancelled) return;
      if (!reg.ok) { setLoadError(reg.error || "Could not load"); return; }
      setRegistry(reg);
    }).catch(e => { if (!cancelled) setLoadError(e.message); });
    return () => { cancelled = true; };
  }, [open, registry]);

  // **The record is read again whenever the searches list changes.** The
  // draft is a document in that list, and deleting it there undrafts it on
  // the middleware — read once, this panel went on saying "Drafted" after
  // the certificate had left the pack.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    apiFetch(`/oc-certificate/${encodeURIComponent(matterRef)}`).then(r => r.json()).then(rec => {
      if (cancelled) return;
      if (!rec.ok) { setLoadError(rec.error || "Could not load"); return; }
      setRecord(rec);
    }).catch(e => { if (!cancelled) setLoadError(e.message); });
    return () => { cancelled = true; };
  }, [open, matterRef, searchesKey]);

  // Saved per field on blur, like the contract data panel — a half-typed value
  // is not an answer, and a single Save across nearly forty boxes is a lot to
  // lose to a mis-click.
  const save = async (name, value) => {
    setSaving(s => ({ ...s, [name]: true }));
    try {
      const res = await apiFetch(`/oc-certificate/${encodeURIComponent(matterRef)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [name]: value }),
      });
      const body = await res.json();
      if (body.ok) {
        setRecord(r => ({ ...r, ocCertificate: body.ocCertificate, missing: body.missing }));
        setPreviewKey(k => k + 1);
      }
    } finally {
      setSaving(s => { const next = { ...s }; delete next[name]; return next; });
    }
  };

  const generate = async () => {
    setGenerating(true);
    setGenerateError(null);
    try {
      const res = await apiFetch(`/oc-certificate/${encodeURIComponent(matterRef)}/generate`,
        { method: "POST" });
      const body = await res.json();
      if (!body.ok) { setGenerateError(body.error || "Could not draft the certificate"); return; }
      setRecord(r => ({ ...r,
        generated: { documentId: body.documentId, version: body.version, at: null } }));
      // The document lands in the searches list, so the matter has to be
      // re-read rather than patched — its position in the merge order is the
      // middleware's to decide.
      if (onGenerated) onGenerated();
    } catch (e) {
      setGenerateError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  // Undrafting is deleting the document: the same route as the searches
  // list's delete, which also clears the drafted state. What was typed stays,
  // so drafting again needs nothing re-entered.
  const removeDraft = async () => {
    const draft = record?.generated;
    if (!draft) return;
    if (!window.confirm("Remove the drafted certificate from the pack? "
      + "Your answers are kept, and you can draft it again.")) return;
    setRemoving(true);
    setGenerateError(null);
    try {
      const res = await apiFetch(
        `/documents/${encodeURIComponent(matterRef)}/${encodeURIComponent(draft.documentId)}`,
        { method: "DELETE" });
      const body = await res.json();
      if (!body.ok) { setGenerateError(body.error || "Could not remove the draft"); return; }
      setRecord(r => ({ ...r, generated: null }));
      if (onGenerated) onGenerated();
    } catch (e) {
      setGenerateError(e.message);
    } finally {
      setRemoving(false);
    }
  };

  const values = record?.ocCertificate || {};
  const missing = record?.missing || [];
  const generated = record?.generated;

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Owners corporation certificate</span>
        </div>
        <span style={{ fontSize: 11, color: T.textFaint }}>
          {generated ? `Drafted — v${generated.version}` : "Not drafted"}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginBottom: 14, maxWidth: 660 }}>
            For a subdivision of three or more lots with no manager, this office prepares the section 151 certificate itself. Nothing is produced until you press Draft. Insurance and minutes go in with <strong>Add document</strong>.
          </div>

          {loadError && (
            <div style={{ fontSize: 12, color: T.red, marginBottom: 12 }}>{loadError}</div>
          )}
          {!registry && !loadError && (
            <div style={{ fontSize: 12, color: T.textFaint }}>Loading&hellip;</div>
          )}

          {registry && (
            <>
              {registry.groups.map(group => {
                const fields = registry.fields.filter(f => f.group === group);
                if (fields.length === 0) return null;
                return (
                  <div key={group} style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: T.textSoft, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                      {OC_GROUP_LABELS[group] || group}
                    </div>
                    {fields.map(f => (
                      <OcField
                        key={f.name}
                        field={f}
                        value={values[f.name]}
                        missing={missing.includes(f.name)}
                        saving={Boolean(saving[f.name])}
                        onSave={save}
                      />
                    ))}
                  </div>
                );
              })}

              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <button
                  onClick={generate}
                  disabled={generating}
                  style={{ background: fluent ? T.brand : T.red, color: fluent ? T.onBrand : T.white, border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 12, fontWeight: 500, cursor: generating ? "default" : "pointer", opacity: generating ? 0.6 : 1, fontFamily: "inherit" }}
                >
                  {generating ? "Drafting…" : generated ? "Draft again" : "Draft the certificate"}
                </button>

                <button
                  onClick={() => setShowPreview(p => !p)}
                  style={{ background: "none", color: T.textSoft, border: `1px solid ${T.border}`, borderRadius: 6, padding: "8px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
                >
                  {showPreview ? "Hide preview" : "Preview"}
                </button>

                {generated && (
                  <button
                    onClick={removeDraft}
                    disabled={removing || generating}
                    style={{ background: "none", color: T.red, border: `1px solid ${T.border}`, borderRadius: 6, padding: "8px 14px", fontSize: 12, cursor: removing ? "default" : "pointer", opacity: removing ? 0.6 : 1, fontFamily: "inherit" }}
                  >
                    {removing ? "Removing…" : "Remove draft"}
                  </button>
                )}

                {/* Reported, never enforced: generating an incomplete
                    certificate to look at it is a normal thing to do halfway
                    through filling one in. */}
                {missing.length > 0 && (
                  <span style={{ fontSize: 11, color: "#c47a1a" }}>
                    {missing.length} required answer{missing.length === 1 ? "" : "s"} still blank
                  </span>
                )}
                {generated && (
                  <span style={{ fontSize: 11, color: T.textFaint }}>
                    Drafting again replaces version {generated.version} rather than adding a second
                    certificate.
                  </span>
                )}
              </div>

              {generateError && (
                <div style={{ fontSize: 12, color: T.red, marginTop: 10, lineHeight: 1.5 }}>
                  {generateError}
                </div>
              )}

              {showPreview && (
                <iframe
                  key={previewKey}
                  title="Owners corporation certificate"
                  src={apiUrl(`/oc-certificate/${encodeURIComponent(matterRef)}/preview`)}
                  style={{ width: "100%", height: 620, border: `1px solid ${T.border}`, borderRadius: 8, marginTop: 14, background: T.white }}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// One answer. The control follows the field's own type, so a date is a date
// box and an amount is a number — the same rule the contract data panel
// follows, and the same reason: a type declared here and a text box in the
// browser is a mismatch nothing reports.
const OcField = ({ field, value, missing, saving, onSave }) => {
  const [draft, setDraft] = useState(() => ocToInput(field.type, value));
  const stored = ocToInput(field.type, value);
  const seeded = useRef(stored);
  useEffect(() => {
    if (seeded.current === stored) return;
    seeded.current = stored;
    setDraft(stored);
  }, [stored]);

  const commit = () => {
    if (draft === stored) return;
    seeded.current = draft;
    onSave(field.name, ocFromInput(field.type, draft));
  };

  const inputStyle = {
    width: "100%", fontSize: 12, color: T.text, padding: "6px 8px",
    border: `1px solid ${missing ? "rgba(196,122,26,0.5)" : T.border}`,
    borderRadius: 6, boxSizing: "border-box", fontFamily: "inherit",
  };

  return (
    <div style={{ marginBottom: 10, maxWidth: 660 }}>
      <div style={{ fontSize: 12, color: T.text, fontWeight: 500, marginBottom: 4, display: "flex", gap: 6, alignItems: "center" }}>
        {field.label}
        {field.required && <span style={{ color: T.red }}>*</span>}
        {saving && <span style={{ fontSize: 10, color: T.textFaint }}>saving&hellip;</span>}
      </div>

      {field.type === "flag" ? (
        <select value={draft} onChange={(e) => { setDraft(e.target.value); }}
                onBlur={commit} style={{ ...inputStyle, maxWidth: 180, cursor: "pointer" }}>
          {/* Blank is a real option. "Nobody has answered" and "no" are
              different statements, and only one belongs on a certificate. */}
          <option value="">&mdash; not answered &mdash;</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      ) : field.options ? (
        <select value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
                style={{ ...inputStyle, maxWidth: 220, cursor: "pointer" }}>
          <option value="">&mdash;</option>
          {field.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : paragraphControl(field) ? (
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} rows={2}
                  style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} />
      ) : (
        <input
          {...fieldInputProps(field)}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          style={{ ...inputStyle, maxWidth: field.type === "text" ? "100%" : 220 }}
        />
      )}

      {field.note && (
        <div style={{ fontSize: 11, color: T.textFaint, marginTop: 3, lineHeight: 1.45 }}>
          {field.note}
        </div>
      )}
    </div>
  );
};

// A flag has three states, not two: yes, no, and nobody-has-answered.
// Collapsing the third into "no" would turn silence into an answer on a
// statutory certificate.
export const ocToInput = (type, value) => {
  if (value === undefined || value === null) return "";
  if (type === "flag") return value === true ? "yes" : value === false ? "no" : "";
  return String(value);
};

export const ocFromInput = (type, draft) => {
  if (type === "flag") return draft === "" ? "" : draft === "yes";
  // An amount is stored as a number so it can be formatted one way on the
  // page; anything that is not one is passed through as typed, because a staff
  // member writing "see schedule" into a box has said something a parser
  // cannot improve on.
  if (type === "money") return parseAmount(draft) ?? draft;
  return draft;
};

// ── Drafting a clause, in the shape people expect a chat to have ───────────
//
// **The reveal is a client-side typewriter over a complete answer, not a
// stream.** Worth saying plainly rather than describing it as streaming: the
// middleware answers once, and this pays the answer out. It reads identically
// to staff and cannot half-arrive — a real SSE stream through Cloud Run and
// Hosting can buffer, and a clause that stops mid-sentence with nothing saying
// why is worse than one that takes a moment to appear.
const useTypewriter = (text, animate) => {
  const [shown, setShown] = useState(animate ? 0 : text.length);
  useEffect(() => {
    if (!animate) { setShown(text.length); return undefined; }
    setShown(0);
    let at = 0;
    // Paced to the length rather than to a fixed rate, so a one-line clause
    // and a six-line one both land in about the same couple of seconds.
    const step = Math.max(2, Math.round(text.length / 160));
    const id = setInterval(() => {
      at += step;
      setShown(at);
      if (at >= text.length) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, [text, animate]);
  return Math.min(shown, text.length);
};

// Three dots, while nothing has come back yet.
const ThinkingDots = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <span style={{ display: "inline-flex", gap: 4 }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: 999, background: "#2f6fed",
          animation: "s32-pulse 1.1s ease-in-out infinite", animationDelay: `${i * 0.18}s`,
        }} />
      ))}
    </span>
    <span style={{ fontSize: 12, color: "#2f6fed", fontWeight: 500 }}>{label}</span>
  </div>
);

// One drafted clause, revealed and offered. **Offered, never applied** — it
// lands in the panel's editable list, where it is a clause somebody typed as
// far as everything downstream is concerned.
const DraftedClause = ({ clause, shownText, done, onUse, used }) => (
  <div style={{
    border: `1px solid ${used ? "#bcd3f8" : T.border}`, borderRadius: 8, padding: "10px 12px",
    background: used ? "rgba(47,111,237,0.04)" : T.white,
  }}>
    {clause.title && (
      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 4 }}>
        {clause.title}
      </div>
    )}
    <div style={{ fontSize: 13, color: T.text, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
      {shownText}
      {!done && <span style={{ opacity: 0.5 }}>▍</span>}
    </div>
    {done && (
      <div style={{ marginTop: 8 }}>
        {used ? (
          <span style={{ fontSize: 11, color: "#2f6fed", fontWeight: 500 }}>
            Added to the list below — save to keep it
          </span>
        ) : (
          <PillButton onClick={onUse}>Use this clause</PillButton>
        )}
      </div>
    )}
  </div>
);

export const SpecialConditionsDrafter = ({
  matterRef, onUse, onClose,
  onDraft = draftSpecialConditions,
  // Tests turn the reveal off; nothing else does.
  animate = true,
}) => {
  // Every exchange so far. The ask is sent back with each new one, which is
  // what makes "make it 14 days" mean anything.
  const [turns, setTurns] = useState([]);
  const [ask, setAsk] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [used, setUsed] = useState(() => new Set());
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  useEffect(() => { inputRef.current?.focus(); }, []);
  // Follow the conversation down. Guarded on the method rather than the node:
  // jsdom has no Element.scrollTo, and a nicety throwing out of an effect
  // takes the whole modal down with it.
  useEffect(() => {
    const box = boxRef.current;
    if (typeof box?.scrollTo === "function") {
      box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
    }
  }, [turns.length, busy]);

  const send = async () => {
    const said = ask.trim();
    if (!said || busy) return;
    setBusy(true);
    setError("");
    setAsk("");
    try {
      // Only the asks and what came back — the whole conversation, in the
      // shape the draft route reads it.
      const previous = turns.map(t => ({ ask: t.ask, clauses: t.clauses }));
      const data = await onDraft(matterRef, { ask: said, previous });
      setTurns(prev => [...prev, {
        ask: said,
        clauses: data.conditions || [],
        note: data.note || "",
        refused: data.refused || "",
      }]);
    } catch (e) {
      setError(e.message || "The drafting failed");
      setAsk(said);
    } finally {
      setBusy(false);
    }
  };

  const use = (turnIndex, i, clause) => {
    onUse([clause]);
    setUsed(prev => new Set(prev).add(`${turnIndex}:${i}`));
  };

  return (
    <div
      onClick={() => { if (!busy) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(54,47,42,0.55)", zIndex: 340, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, width: "100%", maxWidth: 720, height: "82vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 12px 48px rgba(0,0,0,0.2)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: "#2f6fed", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Draft a special condition</div>
            <div style={{ fontSize: 11, color: T.textFaint }}>
              Nothing is saved until you add a clause and press Save
            </div>
          </div>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={busy}>Close</Btn>
        </div>

        <div ref={boxRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16, background: T.bg }}>
          {turns.length === 0 && !busy && (
            <div style={{ maxWidth: 520, margin: "24px auto 0", textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 6 }}>
                What should the condition do?
              </div>
              <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.6, marginBottom: 14 }}>
                Describe it as you would to a colleague. This matter&rsquo;s details go with the request, so a clause can name the property and the vendor.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                {[
                  "Settlement subject to the vendor removing the shed",
                  "Purchaser takes the property subject to the existing tenancy",
                  "Vendor to repair the rear fence before settlement",
                ].map(s => (
                  <PillButton key={s} onClick={() => { setAsk(s); inputRef.current?.focus(); }}>
                    {s}
                  </PillButton>
                ))}
              </div>
            </div>
          )}

          {turns.map((turn, ti) => (
            <DraftedTurn
              key={ti}
              turn={turn}
              // Only the newest answer types itself out. Re-revealing an older
              // one on every render would look like it had been redrafted.
              animate={animate && ti === turns.length - 1}
              isUsed={i => used.has(`${ti}:${i}`)}
              onUse={(i, clause) => use(ti, i, clause)}
            />
          ))}

          {busy && (
            <div style={{ padding: "12px 0" }}>
              <ThinkingDots label="Drafting…" />
            </div>
          )}
        </div>

        <div style={{ borderTop: `1px solid ${T.border}`, padding: 12 }}>
          {error && (
            <div style={{ fontSize: 12, color: T.red, marginBottom: 8 }}>{error}</div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <textarea
              ref={inputRef}
              value={ask}
              onChange={e => setAsk(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder={turns.length ? "Ask for a change, or another clause…" : "Describe the condition…"}
              rows={2}
              disabled={busy}
              style={{
                flex: 1, fontSize: 13, color: T.text, padding: "8px 10px", borderRadius: 8,
                border: `1px solid ${T.border}`, boxSizing: "border-box", fontFamily: "inherit",
                lineHeight: 1.5, resize: "none", background: busy ? T.bg : T.white,
              }}
            />
            <ThinkingButton
              busy={busy}
              disabled={!ask.trim()}
              onClick={send}
              busyLabel="Drafting…"
            >
              {turns.length ? "Ask again" : "Draft"}
            </ThinkingButton>
          </div>
          <div style={{ fontSize: 10, color: T.textFaint, marginTop: 6 }}>
            Drafted wording is a suggestion. Read it before it goes on a contract.
          </div>
        </div>
      </div>
    </div>
  );
};

// One exchange: what was asked, and what came back.
const DraftedTurn = ({ turn, animate, isUsed, onUse }) => {
  // One reveal across the whole answer, so several clauses arrive in order
  // rather than all typing at once — which reads as noise rather than writing.
  const whole = turn.clauses.map(c => c.text).join("\n");
  const shown = useTypewriter(whole, animate);

  let spent = 0;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <div style={{ background: "#2f6fed", color: T.white, fontSize: 13, lineHeight: 1.5, padding: "8px 12px", borderRadius: "12px 12px 3px 12px", maxWidth: "80%", whiteSpace: "pre-wrap" }}>
          {turn.ask}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {turn.clauses.map((clause, i) => {
          const start = spent;
          spent += clause.text.length + 1;
          const chars = Math.max(0, Math.min(clause.text.length, shown - start));
          return (
            <DraftedClause
              key={i}
              clause={clause}
              shownText={clause.text.slice(0, chars)}
              done={shown >= whole.length}
              used={isUsed(i)}
              onUse={() => onUse(i, clause)}
            />
          );
        })}

        {/* **Nothing drafted, with the reason, is an answer.** The model
            saying "that belongs in the section 32, not a special condition"
            is exactly what somebody needs to read — swallowing it would
            leave the screen blank on a press that worked. */}
        {turn.note && (
          <div style={{ fontSize: 12, color: turn.refused ? T.text : T.textSoft, lineHeight: 1.55, padding: turn.refused ? "10px 12px" : "0 2px", background: turn.refused ? T.white : "transparent", border: turn.refused ? `1px solid ${T.border}` : "none", borderRadius: 8 }}>
            {turn.note}
          </div>
        )}
      </div>
    </div>
  );
};

// ── The precedent's own conditional blocks ─────────────────────────────────
//
// The contract carries two runs of conditions after clause 14 — the
// unregistered plan of subdivision, and the commercial lease — headed
// *Additional Special Condition* because 14.1 is what gives them precedence
// over the conditions above them. So they belong beside the clauses staff
// write, and until now there was nowhere on this screen that said they
// existed: turning one on meant knowing the precedent branches on
// `sale.offThePlan` or `sale.commercialLease` and then finding it among a
// hundred-odd fields in the data panel.
//
// **Everything it knows comes from the middleware.** Which blocks there are,
// whether the contract is printing each, and what answering yes or no writes
// are all on `contractFields.precedentBlocks` — for the reason every other
// rule is computed there: a copy here would drift, and the way it would drift
// is a row reading *off* over a block the contract is printing. The commercial
// lease block also prints where nobody has answered and the property type and
// the possession answer together say so, so a row keyed on the stored flag
// alone is wrong on exactly the matters that inference exists for.
//
// **A toggle writes ordinary manual corrections**, through the same save the
// data panel uses. No new state and no second way to answer — so the row and
// the document cannot disagree, and every value stays revertable one at a time
// afterwards.
//
// **Nothing is shown where the middleware sent nothing.** An older deploy has
// never heard of this key, and a panel that invented two rows from its own
// list would offer a toggle that saved a field that answered nothing.
// The answers a block needs, offered from the row that turns it on.
//
// An off-the-plan sale needs three no other sale has — the proposed lot, the
// unregistered plan's reference and the sunset date — and until now they were
// three fields to find among a hundred-odd in the data panel a tab away.
// Turning the block on is exactly when somebody has them to hand.
//
// **Which fields, and what the group is called, are the middleware's.** They
// are on the block itself, for the reason every other rule there is: a list
// here would drift, and the way it would drift is this offering a field the
// contract does not ask for. What the *button* says stays this side.
//
// **`AlsoField` rather than a control of its own**, so these read and behave
// exactly as they do in the preview's popover — the right control for the
// type, where the value came from, the other versions, and the revert. A
// second implementation is how the same field comes to be a dropdown on one
// screen and a text box on another, which has been found here three times.
//
// Each field saves itself on blur, so there is nothing to commit: Done closes.
export const BlockFieldsModal = ({ block, registry, contractFields, savingFields, onSave, onRevert, onClose }) => {
  const fields = (block?.fields || [])
    .map(name => (registry || []).find(f => f.name === name))
    .filter(Boolean);

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(54,47,42,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto" }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 4 }}>
          {block.fieldsLabel}
        </div>
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 4, lineHeight: 1.5 }}>
          {block.note}
        </div>
        {/* A field the registry does not have draws nothing rather than an
            empty box: the middleware is a deploy ahead of this, so a block
            naming a field this build has never heard of is a real state. */}
        {fields.map(f => (
          <AlsoField
            key={f.name}
            field={f}
            value={contractFields?.values?.[f.name]}
            source={contractFields?.provenance?.[f.name]}
            fallback={contractFields?.fallback?.[f.name]}
            candidates={contractFields?.candidates?.[f.name]}
            saving={savingFields?.has?.(f.name)}
            onSave={onSave}
            onRevert={onRevert}
          />
        ))}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Btn size="sm" onClick={onClose}>Done</Btn>
        </div>
      </div>
    </div>
  );
};

// ── Clauses a matter adds to one of the precedent's blocks ─────────────────
//
// The unregistered plan and lease blocks are the precedent's wording, and each
// clause can be reworded or taken out from the preview. This is the other
// half: clauses added after the block's last, cited on from it — B.10 after
// B.9, C.7 after C.6 — and printed only where the block is. Where they start
// is the middleware's (`prefix` and `lastClause` on the block), so an older
// deploy that sends neither offers nothing here.
//
// A whole list per block, saved and replaced entire, like the special
// conditions. Each clause's wording is typed in the numbered box, which leaves
// a lone paragraph bare and numbers two or more as C.7.1, C.7.2, the way the
// block numbers its own.
export const BlockClausesModal = ({ block, clauses, saving, onSave, onClose }) => {
  const [draft, setDraft] = useState(() => toConditionDraft(clauses));
  const payload = toConditionPayload(draft);
  const dirty = JSON.stringify(payload) !== JSON.stringify(toConditionPayload(toConditionDraft(clauses)));
  const headingWithNoText = draft.some(d => d.title.trim() !== "" && d.text.trim() === "");
  const cite = (i) => `${block.prefix}.${block.lastClause + 1 + i}`;
  const update = (uid, patch) => setDraft(prev => prev.map(d => (d.uid === uid ? { ...d, ...patch } : d)));
  const move = (i, by) => setDraft(prev => {
    const to = i + by;
    if (to < 0 || to >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[to]] = [next[to], next[i]];
    return next;
  });

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(54,47,42,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, width: "100%", maxWidth: 620, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 4 }}>
          {block.label} — added clauses
        </div>
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 12, lineHeight: 1.5 }}>
          Printed after {block.prefix}.{block.lastClause}, and only while the block is on the contract.
          Enter starts the next paragraph; Tab indents it, Shift+Tab brings it back.
        </div>
        {draft.length === 0 && (
          <div style={{ fontSize: 12, color: T.textFaint, padding: "4px 0 10px" }}>No clauses added to this block.</div>
        )}
        {draft.map((d, i) => (
          <div key={d.uid} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderTop: i === 0 ? "none" : `1px solid ${T.border}` }}>
            <div style={{ fontSize: 12, color: T.textFaint, width: 34, paddingTop: 9, textAlign: "right", flexShrink: 0 }}>{cite(i)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                value={d.title}
                onChange={(e) => update(d.uid, { title: e.target.value })}
                placeholder="Heading (optional)"
                style={{ width: "100%", fontSize: 12, fontWeight: 500, color: T.text, padding: "6px 8px", border: `1px solid ${T.border}`, borderRadius: 6, marginBottom: 6, boxSizing: "border-box", fontFamily: "inherit" }}
              />
              <NumberedClauseInput
                aria-label={`Clause ${cite(i)}`}
                value={d.text}
                onChange={(text) => update(d.uid, { text })}
                cite={cite(i)}
                always={false}
                placeholder="The clause, as it should read in the contract"
                rows={3}
                style={{
                  width: "100%", fontSize: 13, color: T.text, padding: "8px", borderRadius: 6,
                  border: `1px solid ${d.title.trim() !== "" && d.text.trim() === "" ? T.red : T.border}`,
                  boxSizing: "border-box", fontFamily: "inherit", lineHeight: 1.5, resize: "vertical",
                }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0, paddingTop: 2 }}>
              <Btn variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0}>↑</Btn>
              <Btn variant="ghost" size="sm" onClick={() => move(i, 1)} disabled={i === draft.length - 1}>↓</Btn>
              <Btn variant="ghost" size="sm" onClick={() => setDraft(prev => prev.filter(x => x.uid !== d.uid))}>✕</Btn>
            </div>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
          <Btn variant="ghost" size="sm" onClick={() => setDraft(prev => [...prev, { uid: ++conditionUid, title: "", text: "" }])}>
            + Add a clause
          </Btn>
        </div>
        {headingWithNoText && (
          <div style={{ fontSize: 11, color: T.red, marginTop: 8 }}>
            A heading on its own doesn&rsquo;t print — give it a clause or clear the heading.
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 16 }}>
          <Btn size="sm" onClick={onClose}>{dirty ? "Cancel" : "Done"}</Btn>
          <Btn variant="primary" size="sm" disabled={!dirty || saving || headingWithNoText}
            onClick={async () => { if (await onSave(block.key, payload)) onClose(); }}>
            {saving ? "Saving…" : "Save clauses"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// The precedent's runs, one row each with Add / Take off — drawn on their own
// panel, or inside the one special conditions section.
const PrecedentBlockRows = ({ rows, savingFields, onSave, onEdit, blockClauses, onEditClauses }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {rows.map(b => {
              const answers = b.printing ? b.no : b.yes;
              const busy = Object.keys(answers || {}).some(n => savingFields?.has?.(n));
              return (
                <div key={b.key} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginBottom: 3 }}>
                      {b.label}
                      {b.printing && (
                        <span style={{ marginLeft: 8, fontSize: 10, color: T.textFaint, fontWeight: 400 }}>
                          on the contract
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: T.textSoft, lineHeight: 1.5 }}>{b.note}</div>
                  </div>
                  <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 10 }}>
                    {b.prefix && onEditClauses && (
                      <button
                        type="button"
                        onClick={() => onEditClauses(b.key)}
                        style={{
                          fontSize: 11, color: T.brown, background: "none", border: "none",
                          padding: 0, cursor: "pointer", textDecoration: "underline",
                          fontFamily: "inherit", whiteSpace: "nowrap",
                        }}
                      >
                        {(blockClauses?.[b.key] || []).length
                          ? `Added clauses (${blockClauses[b.key].length})`
                          : "Add clauses"}
                      </button>
                    )}
                    {/* **Offered whether or not the block is on.** Somebody
                        filling the answers in before turning it on is the
                        ordinary order, and a link that appeared only once the
                        contract was already printing the conditions would be
                        available exactly too late. Shown only where the
                        middleware named a set — an older one sends none. */}
                    {b.fields?.length > 0 && onEdit && (
                      <button
                        type="button"
                        onClick={() => onEdit(b.key)}
                        style={{
                          fontSize: 11, color: T.brown, background: "none", border: "none",
                          padding: 0, cursor: "pointer", textDecoration: "underline",
                          fontFamily: "inherit", whiteSpace: "nowrap",
                        }}
                      >
                        {b.fieldsLabel}
                      </button>
                    )}
                    <button
                      onClick={() => onSave(answers)}
                      disabled={busy}
                      style={{
                        padding: "6px 12px", borderRadius: 6, fontSize: 12,
                        border: `1px solid ${T.border}`, cursor: busy ? "default" : "pointer",
                        background: b.printing ? T.white : T.text,
                        color: b.printing ? T.text : T.white,
                        opacity: busy ? 0.5 : 1,
                      }}
                    >
                      {busy ? "Saving…" : b.printing ? "Take off" : "Add"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
);

export const PrecedentConditionsPanel = ({ blocks, registry, contractFields, onSave, onSaveField, onRevertField, savingFields, embedded = false, blockClauses, onSaveBlockClauses, savingBlockClauses }) => {
  const rows = blocks || [];
  // Open when the matter already prints one. A contract carrying a run of
  // conditions should not hide it behind a click; one carrying none should not
  // spend the space — the same rule `SpecialConditionsPanel` applies beside it.
  //
  // **Above the early return**, or an older middleware that sends no blocks
  // would take a different number of hooks through this component than one
  // that does, which React refuses on the render after the payload arrives.
  const [open, setOpen] = useState(() => rows.some(b => b.printing));
  // Which block's fields are being filled in, by key. Above the early return
  // for the same reason `open` is.
  const [editing, setEditing] = useState(null);
  // Which block's added clauses are open, by key — above the return too.
  const [clausesOf, setClausesOf] = useState(null);
  if (!rows.length) return null;
  const clausesBlock = rows.find(b => b.key === clausesOf) || null;
  const clausesModal = clausesBlock && (
    <BlockClausesModal
      block={clausesBlock}
      clauses={blockClauses?.[clausesBlock.key]}
      saving={savingBlockClauses}
      onSave={onSaveBlockClauses}
      onClose={() => setClausesOf(null)}
    />
  );
  const on = rows.filter(b => b.printing).length;
  const editingBlock = rows.find(b => b.key === editing) || null;

  // Inside the one special conditions section, the rows alone: that section
  // has the heading, the open state and the explanation.
  if (embedded) {
    return (
      <>
        <PrecedentBlockRows rows={rows} savingFields={savingFields} onSave={onSave}
          onEdit={onSaveField ? setEditing : null}
          blockClauses={blockClauses} onEditClauses={onSaveBlockClauses ? setClausesOf : null} />
        {clausesModal}
        {editingBlock && (
          <BlockFieldsModal
            block={editingBlock}
            registry={registry}
            contractFields={contractFields}
            savingFields={savingFields}
            onSave={onSaveField}
            onRevert={onRevertField}
            onClose={() => setEditing(null)}
          />
        )}
      </>
    );
  }

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Precedent special conditions</span>
        </div>
        <span style={{ fontSize: 11, color: T.textFaint }}>
          {on === 0 ? "None" : `${on} on`}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginBottom: 12, maxWidth: 620 }}>
            Runs of conditions the contract already carries, printed only where the
            answers below say they apply. Turning one on saves those answers, and
            each stays correctable in the contract data.
          </div>

          <PrecedentBlockRows rows={rows} savingFields={savingFields} onSave={onSave}
            onEdit={onSaveField ? setEditing : null}
            blockClauses={blockClauses} onEditClauses={onSaveBlockClauses ? setClausesOf : null} />
        </div>
      )}
      {clausesModal}

      {editingBlock && (
        <BlockFieldsModal
          block={editingBlock}
          registry={registry}
          contractFields={contractFields}
          savingFields={savingFields}
          onSave={onSaveField}
          onRevert={onRevertField}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

// ── The firm's saved special conditions ────────────────────────────────────
//
// A library of clauses to copy onto a matter (the middleware's
// lib/precedent-conditions.js). An admin saves straight to it; anybody else
// suggests, and the clause joins only when the approver presses Approve on the
// emailed page. *Admin* here is `IS_ADMIN`, which is presentation and not
// access control — the emailed approval is the control that holds.
const precedentCall = async (path, { method = "GET", body } = {}) => {
  const res = await apiFetch(path, {
    method,
    ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) throw new Error(data.error || `The request failed (${res.status})`);
  return data;
};

// What a press of Save / Suggest says afterwards, beside the clause it was for.
export const precedentOutcome = (data, admin) => {
  if (data?.duplicate) return "Already a saved precedent";
  return admin ? "Saved to precedents" : `Sent to ${data?.to || "the approver"} for approval`;
};

// **Copied, never linked.** Adding one copies its words onto the matter as an
// ordinary clause, which reaches the contract through the panel's Save like any
// other — and a later change to the library reaches no contract already
// worded. The copy remembers which saved clause it is (`precedentId`), which
// is what lets this list say it is added and the contract print it with the
// others from the library.

// A sub-heading inside the one special conditions section.
const ConditionsGroup = ({ title, note, children }) => (
  <div style={{ marginTop: 16 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: T.textSoft, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
      {title}
    </div>
    {note && (
      <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginBottom: 10, maxWidth: 620 }}>{note}</div>
    )}
    {children}
  </div>
);

// ── Special conditions, as one section ─────────────────────────────────────
//
// **Three groups, in the order the contract prints them**, on the firm's
// instruction: they are all special conditions, and two panels side by side
// read as two different things.
//
//   1. The precedent's own conditions — runs the template carries, switched
//      on by the matter's answers (`PrecedentConditionsPanel`, embedded).
//   2. The firm's saved precedents — clauses kept for reuse, added or taken
//      off here.
//   3. The matter's own — typed, or drafted with AI.
//
// Groups 2 and 3 are one stored list, `matter.specialConditions`, saved
// together by the one Save. The middleware stores the saved precedents first
// whatever order it is sent, so the contract cannot print them out of turn.
export const SpecialConditionsPanel = ({
  conditions, onSave, saving, matterRef, revealSignal = 0,
  blocks, registry, contractFields, onSaveFields, onSaveField, onRevertField, savingFields,
  blockClauses, onSaveBlockClauses, savingBlockClauses,
}) => {
  // **Open when the matter already carries a clause** — its own, or one of
  // the precedent's. Read once, at mount: following the count afterwards
  // would re-open the panel under somebody who had just closed it, every time
  // the compile poll returned an equal-but-new array.
  const [open, setOpen] = useState(() => (conditions || []).length > 0 || (blocks || []).some(b => b.printing));
  // **Both open, or the jump lands on a heading.** On a matter with no clauses
  // this panel and the container around it are both shut, so opening only the
  // outer one would scroll somebody to a closed row.
  useRevealSection(revealSignal, setOpen, useRef(null));
  const [draft, setDraft] = useState(() => toConditionDraft(conditions));

  // Re-seed from the server only when the *stored* list actually changed.
  // Re-fetching the matter — which the compile poll does every three seconds —
  // hands back an equal-but-new array, and re-seeding on identity would wipe a
  // half-typed clause out from under whoever was writing it.
  const savedKey = JSON.stringify(toConditionPayload(toConditionDraft(conditions)));
  const seeded = useRef(savedKey);
  useEffect(() => {
    if (seeded.current === savedKey) return;
    seeded.current = savedKey;
    setDraft(toConditionDraft(conditions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const payload = toConditionPayload(draft);
  const dirty = JSON.stringify(payload) !== savedKey;

  const fromLibrary = draft.filter(d => d.precedentId);
  const own = draft.filter(d => !d.precedentId);

  // The same rule the middleware enforces, said before the round trip rather
  // than after it. A heading with no clause under it would never print, so
  // saving it would look like it worked and quietly change nothing.
  const headingWithNoText = draft.some(d => d.title.trim() !== "" && d.text.trim() === "");

  const update = (uid, patch) =>
    setDraft(prev => prev.map(d => (d.uid === uid ? { ...d, ...patch } : d)));
  const remove = (uid) => setDraft(prev => prev.filter(d => d.uid !== uid));
  // Within the matter's own clauses: the saved precedents print ahead of them
  // whatever happens here.
  const move = (i, by) => setDraft(prev => {
    const lib = prev.filter(d => d.precedentId);
    const mine = prev.filter(d => !d.precedentId);
    const to = i + by;
    if (to < 0 || to >= mine.length) return prev;
    [mine[i], mine[to]] = [mine[to], mine[i]];
    return [...lib, ...mine];
  });
  const add = () => setDraft(prev => [...prev, { uid: ++conditionUid, title: "", text: "" }]);

  // A drafted clause arrives as an ordinary unsaved row. Nothing about it is
  // marked as a model's: once it is in the list it is a clause staff are
  // editing, and it reaches the contract only through the same Save below.
  const [drafting, setDrafting] = useState(false);
  const useDrafted = (clauses) => setDraft(prev => [
    ...prev,
    ...clauses.map(c => ({ uid: ++conditionUid, title: c.title || "", text: c.text || "" })),
  ]);

  const count = savedKey === "[]" ? 0 : JSON.parse(savedKey).length;
  const blocksOn = (blocks || []).filter(b => b.printing).length;

  // ── The library ──
  // Read when the section opens; a failed read says so in its own group
  // rather than taking the matter's clauses with it.
  const [library, setLibrary] = useState(null);
  const [libraryError, setLibraryError] = useState("");
  const [removing, setRemoving] = useState(null);
  useEffect(() => {
    if (!open || library !== null) return undefined;
    let live = true;
    precedentCall("/precedent-conditions")
      .then(d => { if (live) setLibrary(d.precedents || []); })
      .catch(e => { if (live) { setLibrary([]); setLibraryError(e.message); } });
    return () => { live = false; };
  }, [open, library]);

  // **Add and Take off save there and then**, the way the precedent's own runs
  // above them do. They used to change only the unsaved list and wait for
  // Save conditions — while the row already said *on the contract* — so a
  // clause somebody had plainly added never reached the contract.
  //
  // The whole list goes, so anything half-typed below is saved with it rather
  // than wiped when the stored list comes back. A heading left with no clause
  // would refuse the save, so that is said instead of failing quietly.
  const [savedError, setSavedError] = useState("");
  const saveWith = (next) => {
    if (next.some(d => d.title.trim() !== "" && d.text.trim() === "")) {
      setSavedError("A clause below has a heading but no text — finish or clear it first.");
      return;
    }
    setSavedError("");
    setDraft(next);
    onSave(toConditionPayload(next));
  };
  const addSaved = (p) => {
    if (draft.some(d => d.precedentId === p.id)) return;
    saveWith([
      ...draft.filter(d => d.precedentId),
      { uid: ++conditionUid, title: p.title || "", text: p.text || "", precedentId: p.id },
      ...draft.filter(d => !d.precedentId),
    ]);
  };
  const takeOffSaved = (id) => saveWith(draft.filter(d => d.precedentId !== id));
  // What the matter actually stores, not the draft: "on the contract" is a
  // claim about the contract.
  const storedIds = new Set((conditions || []).map(c => c?.precedentId).filter(Boolean));
  const removeFromLibrary = async (p) => {
    if (!window.confirm("Remove this clause from the saved precedents? Matters already carrying it keep their copy.")) return;
    setRemoving(p.id);
    try {
      await precedentCall(`/precedent-conditions/${encodeURIComponent(p.id)}`, { method: "DELETE" });
      setLibrary(prev => (prev || []).filter(x => x.id !== p.id));
    } catch (e) {
      setLibraryError(e.message);
    } finally {
      setRemoving(null);
    }
  };

  // Every saved clause, and any on this matter the library no longer has —
  // removed since it was added — which is still on the contract and still has
  // to be reachable to take off.
  const libraryRows = [
    ...(library || []),
    ...fromLibrary
      .filter(d => !(library || []).some(p => p.id === d.precedentId))
      .map(d => ({ id: d.precedentId, title: d.title, text: d.text, gone: true })),
  ];

  // Saving a matter's own clause to the library, or suggesting it — said
  // beside the clause it was for. **An admin's save makes it one of the
  // saved precedents on this matter too**, so it moves up into that group
  // (unsaved until Save, like any other change here).
  const [precedentState, setPrecedentState] = useState({});
  const toPrecedents = async (d) => {
    setPrecedentState(prev => ({ ...prev, [d.uid]: { busy: true } }));
    try {
      const data = await precedentCall(IS_ADMIN ? "/precedent-conditions" : "/precedent-conditions/suggest", {
        method: "POST",
        body: {
          ...(d.title.trim() ? { title: d.title.trim() } : {}),
          text: d.text.trim(),
          ...(PORTAL_USER ? { who: PORTAL_USER } : {}),
          ...(matterRef ? { matterRef: String(matterRef) } : {}),
        },
      });
      setPrecedentState(prev => ({ ...prev, [d.uid]: { done: precedentOutcome(data, IS_ADMIN) } }));
      const saved = data.precedent;
      if (IS_ADMIN && saved?.id) {
        setLibrary(prev => (prev && !prev.some(p => p.id === saved.id) ? [...prev, saved] : prev));
        setDraft(prev => {
          const lib = prev.filter(x => x.precedentId);
          const mine = prev.filter(x => !x.precedentId && x.uid !== d.uid);
          return lib.some(x => x.precedentId === saved.id) ? [...lib, ...mine]
            : [...lib, { ...d, precedentId: saved.id }, ...mine];
        });
      }
    } catch (e) {
      setPrecedentState(prev => ({ ...prev, [d.uid]: { error: e.message } }));
    }
  };

  const toggleStyle = (on) => ({
    padding: "6px 12px", borderRadius: 6, fontSize: 12, border: `1px solid ${T.border}`, cursor: "pointer",
    background: on ? T.white : T.text, color: on ? T.text : T.white, fontFamily: "inherit",
  });

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Special conditions</span>
          {dirty && (
            <span style={{ fontSize: 11, color: "#c47a1a", background: "rgba(196,122,26,0.1)", padding: "2px 8px", borderRadius: 999, fontWeight: 500 }}>
              Unsaved
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: T.textFaint }}>
          {count + blocksOn === 0 ? "None" : `${count + blocksOn} on the contract`}
        </span>
      </div>

      {open && (
        <div>
          {(blocks || []).length > 0 && (
            <ConditionsGroup
              title="From the precedent"
              note="Runs of conditions the contract already carries, printed where the matter's answers say they apply."
            >
              <PrecedentConditionsPanel
                embedded
                blocks={blocks}
                blockClauses={blockClauses}
                onSaveBlockClauses={onSaveBlockClauses}
                savingBlockClauses={savingBlockClauses}
                registry={registry}
                contractFields={contractFields}
                onSave={onSaveFields}
                onSaveField={onSaveField}
                onRevertField={onRevertField}
                savingFields={savingFields}
              />
            </ConditionsGroup>
          )}

          <ConditionsGroup title="Saved precedents" note="Clauses the firm keeps for reuse. Adding one copies its wording onto this contract.">
            {libraryError && <div style={{ fontSize: 11, color: T.red, marginBottom: 8 }}>{libraryError}</div>}
            {savedError && <div style={{ fontSize: 11, color: T.red, marginBottom: 8 }}>{savedError}</div>}
            {library === null && <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>}
            {library !== null && libraryRows.length === 0 && !libraryError && (
              <div style={{ fontSize: 12, color: T.textFaint, lineHeight: 1.5 }}>
                Nothing saved yet. Use &ldquo;{IS_ADMIN ? "Save as a precedent" : "Suggest as a precedent"}&rdquo; under a clause below to start the list.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {libraryRows.map(p => {
                const on = fromLibrary.some(d => d.precedentId === p.id);
                return (
                  <div key={p.id} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginBottom: 3 }}>
                        {p.title || "Untitled clause"}
                        {storedIds.has(p.id) && (
                          <span style={{ marginLeft: 8, fontSize: 10, color: T.textFaint, fontWeight: 400 }}>
                            on the contract{p.gone ? " · no longer in the saved list" : ""}
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: 11, color: T.textSoft, lineHeight: 1.5, whiteSpace: "pre-wrap",
                        display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>{p.text}</div>
                    </div>
                    <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 10 }}>
                      {IS_ADMIN && !p.gone && (
                        <button
                          type="button"
                          onClick={() => removeFromLibrary(p)}
                          disabled={removing === p.id}
                          style={{ fontSize: 11, color: T.textSoft, background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", whiteSpace: "nowrap" }}
                        >
                          {removing === p.id ? "Removing…" : "Remove from list"}
                        </button>
                      )}
                      <button type="button" disabled={saving} onClick={() => (on ? takeOffSaved(p.id) : addSaved(p))}
                        style={{ ...toggleStyle(on), opacity: saving ? 0.5 : 1, cursor: saving ? "default" : "pointer" }}>
                        {saving ? "Saving…" : on ? "Take off" : "Add"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ConditionsGroup>

          <ConditionsGroup title="Additional conditions" note="Written for this matter, typed or drafted with AI. Printed after the saved precedents, in the order below, numbered on from special condition 13. A heading is optional.">
            {own.length === 0 && (
              <div style={{ fontSize: 12, color: T.textFaint, padding: "4px 0 10px" }}>
                No extra clauses on this matter.
              </div>
            )}

            {own.map((d, i) => (
              <div key={d.uid} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderTop: i === 0 ? "none" : `1px solid ${T.border}` }}>
                <div style={{ fontSize: 12, color: T.textFaint, width: 24, paddingTop: 9, textAlign: "right", flexShrink: 0 }}>
                  {FIRST_MATTER_CONDITION + fromLibrary.length + i}.
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input
                    value={d.title}
                    onChange={(e) => update(d.uid, { title: e.target.value })}
                    placeholder="Heading (optional)"
                    style={{ width: "100%", fontSize: 12, fontWeight: 500, color: T.text, padding: "6px 8px", border: `1px solid ${T.border}`, borderRadius: 6, marginBottom: 6, boxSizing: "border-box", fontFamily: "inherit" }}
                  />
                  <NumberedClauseInput
                    aria-label={`Special condition ${FIRST_MATTER_CONDITION + fromLibrary.length + i}`}
                    value={d.text}
                    onChange={(text) => update(d.uid, { text })}
                    cite={String(FIRST_MATTER_CONDITION + fromLibrary.length + i)}
                    placeholder="The clause, as it should read in the contract. Enter starts the next paragraph; Tab indents it, Shift+Tab brings it back."
                    rows={3}
                    style={{
                      width: "100%", fontSize: 13, color: T.text, padding: "8px", borderRadius: 6,
                      border: `1px solid ${d.title.trim() !== "" && d.text.trim() === "" ? T.red : T.border}`,
                      boxSizing: "border-box", fontFamily: "inherit", lineHeight: 1.5, resize: "vertical",
                    }}
                  />
                  {d.title.trim() !== "" && d.text.trim() === "" && (
                    <div style={{ fontSize: 11, color: T.red, marginTop: 4 }}>
                      A heading on its own doesn&rsquo;t print — give it a clause or clear the heading.
                    </div>
                  )}
                  {d.text.trim() !== "" && (() => {
                    const st = precedentState[d.uid] || {};
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 11 }}>
                        <button
                          type="button"
                          onClick={() => toPrecedents(d)}
                          disabled={st.busy}
                          title={IS_ADMIN
                            ? "Add this clause to the saved precedents, ready to add to any matter"
                            : "Email this clause for approval as a saved precedent"}
                          style={{
                            font: "inherit", fontSize: 11, color: T.textSoft, background: "none", border: "none",
                            padding: 0, cursor: st.busy ? "default" : "pointer", textDecoration: "underline", textUnderlineOffset: 2,
                          }}
                        >
                          {st.busy ? (IS_ADMIN ? "Saving…" : "Sending…") : IS_ADMIN ? "Save as a precedent" : "Suggest as a precedent"}
                        </button>
                        {st.done && <span style={{ color: T.textFaint }}>{st.done}</span>}
                        {st.error && <span style={{ color: T.red }}>{st.error}</span>}
                      </div>
                    );
                  })()}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0, paddingTop: 2 }}>
                  <Btn variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0}>↑</Btn>
                  <Btn variant="ghost" size="sm" onClick={() => move(i, 1)} disabled={i === own.length - 1}>↓</Btn>
                  <Btn variant="ghost" size="sm" onClick={() => remove(d.uid)}>✕</Btn>
                </div>
              </div>
            ))}

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <Btn variant="ghost" size="sm" onClick={add}>+ Add a clause</Btn>
              {matterRef && (
                <button
                  type="button"
                  onClick={() => setDrafting(true)}
                  style={{
                    font: "inherit", fontSize: 11, fontWeight: 500, color: T.white,
                    background: "#2f6fed", border: "1px solid #2f6fed", borderRadius: 999,
                    padding: "4px 12px", cursor: "pointer", lineHeight: 1.4,
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2l1.9 5.6L19.5 9.5 13.9 11.4 12 17l-1.9-5.6L4.5 9.5l5.6-1.9L12 2zM19 14l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9L19 14z"/>
                  </svg>
                  Draft with AI
                </button>
              )}
            </div>
          </ConditionsGroup>

          {/* One Save for both lists it stores — the saved precedents added
              here and the matter's own clauses. The precedent's runs above
              save as they are switched, as they always have. */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 16 }}>
            {dirty && !saving && (
              <span style={{ fontSize: 11, color: T.textFaint }}>Not saved yet</span>
            )}
            <Btn
              variant="primary"
              size="sm"
              onClick={() => onSave(payload)}
              disabled={!dirty || saving || headingWithNoText}
            >
              {saving ? "Saving…" : "Save conditions"}
            </Btn>
          </div>
        </div>
      )}

      {drafting && (
        <SpecialConditionsDrafter
          matterRef={matterRef}
          onUse={useDrafted}
          onClose={() => setDrafting(false)}
        />
      )}
    </div>
  );
};

// ── One document, two states ───────────────────────────────────────────────
//
// The draft and the compiled PDF are not two documents. They are one contract
// in two states — the one being worked on, and the one that was issued — and
// the screen modelled them as peers: two white cards, stacked, each with a
// 13px heading over a frame of the same document. Once you had scrolled into
// either frame the heading was off screen and nothing on it answered *which am
// I in*. Staff reported exactly that.
//
// **Two axes, and the rail already carries the other one.** The rail answers
// *what am I working on* — the contract, the searches, the activity. Which
// state of the contract is a property of the contract, so the control for it
// belongs on the document rather than in the navigation. It is also where
// every other tool with a draft and a published state puts it.
//
// **Sticky, because the failure was a label that scrolled away.** It sits at
// `top: UNDER_BAR` under the matter bar, so the answer is on screen for as long as
// the document is — which the two headings never were.
//
// **Both are rendered and one is hidden.** Unmounting the draft revokes its
// blob URL and throws away the fetch, the pagination and the click-to-edit
// wiring; unmounting the compiled one reboots the viewer and loses the reader's
// place. Switching between them is the whole point of the control, so neither
// may cost either. The enclosing `Pane` already hides this same subtree the
// same way when the rail moves, so nothing new is being asked of either frame.
//
// **And it stops short of the bottom of the window, deliberately.** The frame
// filled the viewport, so the contract data panel directly beneath it began
// exactly at the fold — and a card that begins at the fold is a card nobody
// knows is there. What sits in that panel's collapsed header is the one thing
// staff need off this screen without scrolling for it: the completeness badge,
// *3 still needed* against *✓ Complete*. A matter with no agent picked yet
// reads as finished until somebody scrolls past a full-height document.
//
// `CONTRACT_PEEK` is that sliver, measured against what it has to reveal
// rather than chosen for the look of it: 12px of gap, the card's own 16px of
// padding, and the ~17px badge row, which lands the badge 18px clear of the
// window's edge — enough that it reads as the top of something rather than as
// a clipped one.
//
// **The floor is derived from it, or the whole change is off on a short
// window.** `CONTRACT_FRAME_MIN` is a floor *under* this calc, so a peek added
// to the calc alone would be silently cancelled on any viewport short enough
// to hit the floor — under about 750px, which is most laptops, and invisible
// because the frame looks exactly as it always did. Subtracting the peek from
// the floor as well keeps the crossover where it was and makes the frame 64px
// shorter at every height. Written as arithmetic rather than as two numbers
// that happen to agree, so changing one cannot leave the other behind.
export const CONTRACT_PEEK = 64;

// **A matter with holes in it needs more than the badge.** The peek above is
// sized to reveal *that* something is outstanding; on such a matter the panel
// opens itself, and what it opens to was still below the fold — so the reader
// was told there were three fields to fill and had to scroll past a
// full-height document to learn which. The firm asked for the frame to give up
// a little more room on exactly those matters.
//
// Measured the same way, against what it has to reveal: the 64 above, then the
// open body's 14px top margin, its ~26px line of instruction and that row's
// 4px below it, and 31px into the first outstanding row — 8px of its padding,
// 7px above its label and the ~16px label itself. So the first thing still
// needed is named on screen rather than merely counted.
//
// **It is only ever the missing case that pays.** A complete matter keeps the
// frame it has, because the panel below it has nothing to show.
export const CONTRACT_PEEK_MISSING = 140;

// Which of the two this matter gets. One rule, read by both panels, because
// the compiled document and the draft sit in the same column above the same
// data panel and a frame that shortened for one and not the other would read
// as the layout jumping when you switch between them.
export const contractPeek = (missing) => (missing > 0 ? CONTRACT_PEEK_MISSING : CONTRACT_PEEK);

export const contractFrameHeight = (peek = CONTRACT_PEEK) => `calc(100vh - ${230 + peek}px)`;
export const contractFrameMin = (peek = CONTRACT_PEEK) => 460 - peek;
export const CONTRACT_FRAME_HEIGHT = contractFrameHeight();
export const CONTRACT_FRAME_MIN = contractFrameMin();

// Where the frame should actually end, measured rather than assumed.
//
// **A constant subtraction cannot be right for two panels.** The calc above
// guesses at how much chrome sits above the frame, and the two panels carry
// different amounts: the compiled one has the header buttons, the expiry
// warning, the *Next* row and the lot strip; the draft one has the jump-to
// marks and its own line of instructions. So the peek landed on one and not
// the other — the firm's report was that the *Contract data* flag still needs
// scrolling to, which is the whole of what the peek exists to prevent.
//
// The honest number is the element's own distance from the top of the page,
// which the element knows and nothing else does.
//
// **The page offset, not the viewport offset.** `rect.top` moves as the reader
// scrolls; `rect.top + scrollY` does not, and it is the one that answers *will
// this be on screen when the page is at the top*, which is the question. The
// frame is sized once for that position and stays there — scrolled down there
// is more room than it takes, which costs nothing, because the document
// scrolls inside the frame rather than the page scrolling past it.
// **The floor defaults from the peek it was handed, not from the constant.**
// A caller passing a larger peek and taking the default floor would have the
// extra silently cancelled on any window short enough to hit it — which is the
// trap `CONTRACT_FRAME_MIN` is derived to avoid, one argument along.
export function frameHeightFor(pageOffset, viewport, { peek = CONTRACT_PEEK, min = contractFrameMin(peek) } = {}) {
  if (!Number.isFinite(pageOffset) || !Number.isFinite(viewport) || viewport <= 0) return null;
  return Math.max(min, Math.round(viewport - pageOffset - peek));
}

// The measured height for one frame, or null until it can be measured.
//
// **A hidden panel measures zero and must not be believed.** Both panels are
// mounted and one is `display: none`, which is what makes moving between them
// free — and a zero rect read literally would size that frame to the whole
// window, so it takes the window's height the moment the reader switches to
// it. The last good answer is kept instead.
//
// Watching the document rather than the element is what catches the chrome
// above changing height: the stale chip appearing, the marks row arriving
// after the fetch, a warning being dismissed. Watching the element as well is
// what catches it becoming visible, which changes its own size from nothing.
export function useContractFrameHeight(ref, peek = CONTRACT_PEEK) {
  const [height, setHeight] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      setHeight(frameHeightFor(rect.top + window.scrollY, window.innerHeight, { peek }));
    };
    measure();
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      if (document.documentElement) ro.observe(document.documentElement);
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [ref, peek]);
  return height;
}

// How tall the sticky matter bar actually is, published for whatever has to
// sit under it.
//
// **84 was a guess and it is wrong in both directions.** The bar is two lines
// of content in 12px of padding — about 65px — so the segment bar stuck 19px
// below it and the document scrolled up through the band between them. The
// bar's background is `rgba(239,239,239,0.93)`, so what shows in that band is
// a strip of contract with a pale wash over it, which reads as the document
// painting over the thing that is meant to be covering it. That is the firm's
// report.
//
// And the bar carries `flexWrap`, so on a narrow window it becomes two rows
// and grows *past* 84 — at which point the same constant hides the segment
// control underneath it instead. One number cannot be right for both.
//
// A custom property rather than a prop, because the two are in different
// components with the whole matter screen between them: the bar is the only
// thing that knows its height, and everything that has to clear it reads one
// value it cannot get wrong.
export const BAR_HEIGHT_VAR = "--s32-bar";
export const BAR_HEIGHT_FALLBACK = "84px";
// `top` for anything sticking directly under the bar, and the scroll offset
// that puts a heading below it rather than behind it.
export const UNDER_BAR = `var(${BAR_HEIGHT_VAR}, ${BAR_HEIGHT_FALLBACK})`;
export const UNDER_BAR_CLEAR = `calc(var(${BAR_HEIGHT_VAR}, ${BAR_HEIGHT_FALLBACK}) + 12px)`;

export function useStickyBarHeight(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof document === "undefined") return undefined;
    const publish = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) document.documentElement.style.setProperty(BAR_HEIGHT_VAR, `${Math.round(h)}px`);
    };
    publish();
    window.addEventListener("resize", publish);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener("resize", publish);
      if (ro) ro.disconnect();
      // Left set rather than cleared: the bar is going away because the screen
      // is, and a fallback flashing in behind it moves everything that clears
      // it for one frame.
    };
  }, [ref]);
}

// Which state a matter opens on.
//
// **The stage rule is `sectionDefaults`', not a second one.** It already
// decides which of the two a stage is about — `compiled` on a compiled or
// released matter, `preview` before that — and a copy of that judgement here
// is the drift this codebase keeps paying for.
//
// Guarded on there actually being a compiled document, or a matter whose
// status says compiled and whose PDF never stored would open on a segment with
// nothing in it.
// **Why the Compiled half is off, in the step card's words (DA-24).** A
// compiled matter whose stored copy is gone said "Not compiled yet" beside a
// "Compiled v2" tab, while the card above said the copy had expired — three
// claims about one document. Only a matter that has never been compiled is
// "not compiled yet".
export const compiledUnavailableText = (matter = {}) =>
  ((matter.status === "compiled" || matter.status === "released") && Number(matter.compileVersion) > 0
    ? "The stored copy has expired — re-compile to see it"
    : "Not compiled yet");

export const documentSegment = (matter = {}, hasCompiled = false) =>
  (hasCompiled && sectionDefaults(matter.status).compiled ? "compiled" : "draft");

// Whether the compiled document is behind what the matter now holds. **A
// property of the version you are looking at**, so it reads on that version's
// own label rather than as a second button. The step card says the same thing
// at the top of the page and scrolls away with it; this does not.
export const documentStale = (matter = {}) =>
  Boolean(rebuildNoticeWorthShowing(matter)) || readSinceCompiled(matter);

// **One toolbar row for the document, whichever state is showing.** Each state
// carried its own toolbar under the Compiled/Draft header, so two bars stood
// between the switch and the first line of the contract. The header now holds
// a slot, and the state on screen puts its controls there; the hidden one puts
// nothing. Outside `ContractDocument` there is no slot and a panel draws its
// toolbar where it always did.
const DocToolbar = createContext(null);
// A frame inside the document card: no border of its own, and the card's lower
// corners, since it is the last thing in it.
const EDGE_FRAME = { borderRadius: "0 0 9px 9px" };
export const InDocToolbar = ({ children, bypass = false }) => {
  const ctx = useContext(DocToolbar);
  if (!ctx || bypass) return children;
  if (!ctx.active || !ctx.slot) return null;
  return createPortal(children, ctx.slot);
};

export const ContractDocument = ({
  matter, hasCompiled, segment, onSegment, draft, compiled, innerRef,
}) => {
  const stale = hasCompiled && documentStale(matter);
  const on = hasCompiled ? segment : "draft";

  // **Mounted on first sight, and never unmounted after.** The guarantee that
  // matters is switching *back*: unmounting the draft revokes its blob URL and
  // throws away the fetch, the pagination and the click-to-edit wiring, and
  // unmounting the compiled one reboots the viewer and loses the reader's
  // place — so a control whose whole purpose is moving between them must not
  // charge for the move.
  //
  // What it deliberately does not do is mount them both up front. The
  // commonest visit to a compiled matter is to read the PDF, and the draft is
  // a third of a megabyte fetched and paginated for a press that may not come
  // — so the requests this layout makes are the ones the stacked panels made,
  // which is what `MatterDetailV2` claims of the panes above it.
  const [seen, setSeen] = useState(() => ({ [on]: true }));
  useEffect(() => {
    setSeen(s => (s[on] ? s : { ...s, [on]: true }));
  }, [on]);
  const [slot, setSlot] = useState(null);

  const tab = (key, label, { disabled = false, title } = {}) => (
    <button
      key={key}
      type="button"
      disabled={disabled}
      title={title}
      aria-current={on === key}
      onClick={() => onSegment(key)}
      style={{
        background: on === key ? T.white : "none",
        border: on === key ? `1px solid ${T.borderMid}` : "1px solid transparent",
        boxShadow: on === key ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
        borderRadius: 999, padding: "5px 12px", fontSize: 12.5,
        fontFamily: "Inter, sans-serif",
        fontWeight: on === key ? 500 : 400,
        color: disabled ? T.textFaint : on === key ? T.text : T.textSoft,
        cursor: disabled ? "default" : "pointer",
        display: "flex", alignItems: "center", gap: 6,
      }}
    >
      {label}
    </button>
  );

  return (
    <div ref={innerRef} data-tour="document" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 12, scrollMarginTop: UNDER_BAR_CLEAR }}>
      <div style={{
        position: "sticky", top: UNDER_BAR, zIndex: 40,
        background: T.white, borderBottom: `1px solid ${T.border}`,
        borderRadius: "10px 10px 0 0",
        padding: "8px 12px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      }}>
        <div data-tour="segment" style={{ display: "flex", gap: 2, background: T.bg, borderRadius: 999, padding: 3 }}>
          {tab("compiled",
            <>
              Compiled
              {matter.compileVersion > 0 && (
                <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 400 }}>v{matter.compileVersion}</span>
              )}
            </>,
            // **Present and disabled rather than absent**, so the shape of the
            // control does not change under somebody between one matter and
            // the next — and so the reason is on the thing they pressed.
            hasCompiled ? {} : { disabled: true, title: compiledUnavailableText(matter) })}
          {tab("draft", "Draft")}
        </div>

        {!hasCompiled && (
          <span style={{ fontSize: 11.5, color: T.textFaint }}>{compiledUnavailableText(matter)}</span>
        )}
        {stale && on === "compiled" && (
          // Grey words and a red dot: the page has no amber, and the red is
          // the same *look here* the rail's pills use.
          <span data-stale-chip style={{ fontSize: 11.5, color: T.textSoft, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: T.red, display: "inline-block" }} />
            Behind the matter — re-compile to bring it up to date
          </span>
        )}
        {/* A basis, so on a narrow screen the toolbar takes a line of its own
            under the segment rather than a sliver beside it. */}
        <div ref={setSlot} data-doc-toolbar style={{ flex: "1 1 280px", minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} />
      </div>

      {/* **No padding: the document runs to the card's edges**, on its own
          darker ground, rather than sitting in a framed box inside a box. */}
      <div>
        <div style={{ display: on === "compiled" ? "block" : "none" }} aria-hidden={on === "compiled" ? undefined : true}>
          <DocToolbar.Provider value={{ slot, active: on === "compiled" }}>
            {seen.compiled ? compiled : null}
          </DocToolbar.Provider>
        </div>
        <div style={{ display: on === "draft" ? "block" : "none" }} aria-hidden={on === "draft" ? undefined : true}>
          <DocToolbar.Provider value={{ slot, active: on === "draft" }}>
            {seen.draft ? draft : null}
          </DocToolbar.Provider>
        </div>
      </div>
    </div>
  );
};

// The channel to the compiled viewer.
//
// **Module level, because two things talk to it.** The panel sends the lot
// marks and the place to travel to; the document shell asks where the reader
// is when they press Draft. A second copy of the selector is how one of them
// comes to miss the frame the modal mounts.
export const postToViewers = (message) => {
  document.querySelectorAll("iframe[data-s32-view]").forEach((frame) => {
    try { frame.contentWindow?.postMessage(message, "*"); } catch { /* not booted; the ready call brings it back */ }
  });
};

// ── The compiled document ──────────────────────────────────────────
// The finished merge: the contract, then every returned search, as one PDF.
//
// Collapsed until asked for. It is the *output*, not the thing being worked
// on, and rendering a multi-megabyte PDF in a frame on every matter view cost
// a wait nobody asked for — the header still carries what staff read at a
// glance (the version, and whether the vendor statement made it in), and the
// Open button doesn't need the frame at all.
export const CompiledDocumentPanel = ({ matter, compiling, defaultOpen = false, stage, revealSignal = 0, expandSignal = 0, actions = [], modalActions = null, onChanged = () => {}, lotDefaultOpen = false, embedded = false, peek = CONTRACT_PEEK }) => {
  // **Embedded is the document surface, so it is always open.** The fold
  // belonged to a card stacked among others; inside `ContractDocument` the
  // segment decides whether this is on screen at all, and a chevron there
  // would be a second way to hide the thing you just asked for.
  const [ownOpen, setOpen] = useStagedSection(defaultOpen, stage);
  const open = embedded || ownOpen;
  const [expanded, setExpanded] = useState(false);
  const contractSrc = compiledPdfSrc(matter);
  // **One viewer, for the document and for the lot alike.** The frame used to
  // hold the raw PDF and swap to the marked view on a press, which meant two
  // sources, a mode to be in, and a phone getting neither — a framed PDF
  // renders its first page and stops on WebKit, so the compiled preview showed
  // one page of thirty-five on every iPhone and the portal's answer was to
  // hand the file to the device's own viewer and let the reader leave.
  //
  // The middleware's page draws the pack properly and draws the halos on the
  // pages that carry one, so there is nothing to swap: the marks are simply
  // part of the document view. What is left is *travel*, which is what
  // `showLot` does now.
  const src = compiledViewSrc(matter);
  const lotRecord = matter.lotSearch?.record || null;
  // **One button reveals the lot's whole section**, rather than a strip that
  // is always there with its own fold. Offered wherever the middleware offers
  // the search at all — including its refusals, which name the missing
  // condition and are the answer to *why is there no lot here*.
  const canLot = Boolean(src && matter.lotSearch && !compiling);

  // **Whether the halos are on.** The viewer draws none until asked, so this
  // is a mode rather than a place in a document — and closing the section
  // turns it off, which is what makes one button enough.
  // **Open on arrival where somebody owes it a verdict**, which is the one
  // state the button alone could not announce: an agent ran the search and the
  // office has not looked. Every other matter starts closed.
  const [lotOpen, setLotOpen] = useState(lotDefaultOpen);
  const [showing, setShowing] = useState(false);
  // **Which place, held here rather than counted in the frame.** The viewer
  // used to be asked for *the next one*, which is not one answer when two
  // frames are mounted — and a press made in the second before a frame booted
  // reached nothing at all, so the re-ask below could only turn the halos
  // back on and the document sat where it was. That is what staff saw as
  // *Next place does nothing* in the panel, where the press is offered the
  // moment the frame is created and is therefore usually pressed too early.
  const [place, setPlace] = useState(null);
  // The panel's own frame, so a travel press can bring it on screen.
  const frameRef = useRef(null);
  // **Whose lot, not merely that there was one.** The panel stays mounted
  // across a matter change and a re-compile, so without this the marks stay
  // "on" over a document they were never about.
  const lotKey = `${matter.ref}#${matter.compileVersion}#${(lotRecord?.places || []).length}`;
  const firstKey = useRef(lotKey);
  useEffect(() => {
    if (lotKey === firstKey.current) return;
    setLotOpen(lotDefaultOpen); setShowing(false); setPlace(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotKey]);

  // **Posted to every frame of the viewer on the page, rather than threaded
  // through a ref.** The modal mounts a second one, so a ref would have to be
  // handed out of it and the two kept straight; both frames hold the same
  // document, and telling the hidden one to scroll costs nothing.
  const tellViewers = useCallback((message) => postToViewers(message), []);

  // **One page or two, the same choice the draft offers.** The viewer is the
  // middleware's page, so this asks it — and only once it has said it can:
  // `viewer-ready` carries `can`, and a viewer deployed before the feature
  // says nothing, so the switch is simply absent rather than one that does
  // nothing. `fits` is the viewer's own answer about whether two fit the frame.
  const [pages, setPages] = usePagesPreference();
  const pagesNowRef = useRef(pages);
  pagesNowRef.current = pages;
  const [viewerPages, setViewerPages] = useState(false);
  const [pagesFit, setPagesFit] = useState(true);
  useEffect(() => {
    const onMessage = (event) => {
      const said = event.data;
      if (said?.s32 === "viewer-ready" && Array.isArray(said.can) && said.can.includes("pages")) {
        setViewerPages(true);
        if (pagesNowRef.current === 2) tellViewers({ s32: "pages", n: 2 });
      } else if (said?.s32 === "pages-now") {
        setPagesFit(said.fits !== false);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [tellViewers]);
  useEffect(() => {
    if (viewerPages) tellViewers({ s32: "pages", n: pages });
  }, [pages, viewerPages, tellViewers]);

  // **The frame says when it is ready rather than this guessing.** Expanding
  // mounts a second frame, and marks asked for before it booted would reach
  // nothing — so it re-asks on the viewer's own call instead of on a load
  // event that would have to be hooked per frame.
  const showingRef = useRef(showing);
  showingRef.current = showing;
  const placeRef = useRef(place);
  placeRef.current = place;
  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.s32 !== "viewer-ready" || !showingRef.current) return;
      tellViewers({ s32: "lot", on: true });
      // **And it goes to the place, not merely to the marks.** Sending the
      // index rather than *step on* is what makes this safe to re-send: a
      // frame already there scrolls to where it is, and one that missed the
      // press catches up instead of showing halos on page 1.
      if (placeRef.current !== null) {
        tellViewers({ s32: "lot-at", index: placeRef.current });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [tellViewers]);

  // The main button. Closing takes the marks off with it: a section closed
  // over a document still carrying halos is a mode with nothing saying it is
  // on.
  const toggleLot = () => {
    if (lotOpen) { setLotOpen(false); setShowing(false); setPlace(null); tellViewers({ s32: "lot", on: false }); }
    else { setOpen(true); setLotOpen(true); }
  };
  const places = (lotRecord?.places || []).length;
  const showLot = () => {
    const next = place === null || !places ? 0 : (place + 1) % places;
    setShowing(true); setPlace(next);
    tellViewers({ s32: "lot-at", index: next });
    // **The document is 520px below the press.** In the modal the sheet fills
    // the screen, so travelling is its own evidence; in the panel the frame
    // can be off the bottom entirely, and a jump nobody can see is
    // indistinguishable from a jump that did not happen.
    if (!expanded) frameRef.current?.scrollIntoView?.({ block: "nearest" });
  };

  // **The two views render the same two things**, rather than each building
  // its own: the panel's frame and the expanded modal are the same document
  // and the same job, and a second copy of either is the one that goes stale.
  // Wrapped rather than marked on the `Btn`, which forwards no unknown props.
  // It exists only where the press does, which is what lets the tour say "not
  // on this matter" rather than pointing at the document and hoping.
  const lotButton = canLot ? (
    <span data-tour="find-lot" title={LOT_BETA_HINT} style={{ display: "inline-flex" }}>
    <Btn variant={lotOpen ? "warm" : "ghost"} size="sm" onClick={toggleLot}>
      <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="10" r="3"/><path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z"/></svg>
      <span>{lotOpen ? "Hide lot tools" : "Find lot on plan"}</span>
      <BetaTag />
    </Btn>
    </span>
  ) : null;

  const lotPanel = canLot && lotOpen ? (
    <LotOnPlanPanel matter={matter} onChanged={onChanged}
      showing={showing} onShowLot={lotRecord?.found ? showLot : null} />
  ) : null;

  // The compiled banner's "open the compiled document" link lands here, and so
  // does *Back to the contract* on the way home from Arrange documents.
  const containerRef = useRef(null);
  const frameBoxRef = useRef(null);
  const frameHeight = useContractFrameHeight(frameBoxRef, peek);
  useRevealSection(revealSignal, setOpen, containerRef);

  // **A finished compile opens the document it produced.**
  //
  // Deliberately a second signal rather than a louder `revealSignal`: that one
  // opens the *section*, and it is also what *Back to the contract* fires — so
  // sharing it would pop a full-screen modal on somebody simply walking back
  // from Arrange documents.
  //
  // The section is opened too, so closing the modal leaves the document where
  // they can see it rather than collapsing it away again.
  useEffect(() => {
    if (!expandSignal) return;
    setOpen(true);
    setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandSignal]);

  return (
    <div ref={containerRef} style={embedded
      ? { opacity: compiling ? 0.55 : 1, transition: "opacity 0.2s" }
      : { background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12, opacity: compiling ? 0.55 : 1, transition: "opacity 0.2s", scrollMarginTop: 12 }}>
      <InDocToolbar>
      <div
        onClick={embedded ? undefined : () => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: embedded ? "default" : "pointer", gap: 12, flex: embedded ? 1 : undefined, flexWrap: "wrap", minWidth: 0 }}
      >
        <span style={{ fontSize: 13, fontWeight: 500, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
          {/* **The segment names it and carries the version**, so repeating
              both here would be the document titled twice on one screen — the
              fault the rail's own tab labels were fixed for. What is kept is
              everything that is *not* the name: which version this is standing
              in for while a compile runs, and whether the statement made it in. */}
          {!embedded && (
            <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
                 style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
              <path d="m9 18 6-6-6-6"/>
            </svg>
          )}
          {compiling ? "Previous version" : (embedded ? null : "Compiled contract")}
          {!embedded && matter.compileVersion > 1 && (
            <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 400 }}>v{matter.compileVersion}</span>
          )}
          {compiling && (
            <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 400 }}>
              — will be replaced when the new one lands
            </span>
          )}
          {!compiling && matter.includesVendorStatement === false && (
            <span style={{ fontSize: 11, color: "#c47a1a", fontWeight: 400 }}>
              — searches only
            </span>
          )}
        </span>
        {/* **Both live in the header, and both stay visible when the frame is
            collapsed.** They are the two things you can do *to* the document —
            the preview below is reading it in place — and the common case is
            not reading it at all but taking the file: releasing by hand, or
            attaching it to an email. Open keeps its position so nobody's
            muscle memory moves; Download is the new one and reads second. */}
        {/* **It wraps.** On a phone the cluster — pages, lot, Expand,
            Download, Arrange — is wider than the card, and a row that will not
            wrap runs off the right-hand edge with the last buttons in it. */}
        <span style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
          {/* **Open was the dead end.** It put the contract in a new browser
              tab, where there is no portal at all — so the one screen where a
              fault gets noticed was the one screen with nowhere to go. Expand
              is the same document in the frame the returned certificates
              already use, with the portal still around it. Download is what
              Open was mostly being pressed for and keeps doing that job. */}
          {viewerPages && open && !expanded && (
            <PagesSwitch pages={pages} fits={pages === 1 || pagesFit} onChange={setPages} />
          )}
          {lotButton}
          <Btn variant="ghost" size="sm" onClick={() => setExpanded(true)}>
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            Expand
          </Btn>
          {/* **The contract, never whatever the frame is drawing.** With the
              lot shown the frame is a page of ours, and a Download that saved
              that would hand somebody an HTML file under a button labelled
              with the document's name. */}
          <Btn variant="ghost" size="sm" onClick={() => saveAs(downloadUrl(contractSrc))}>
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download
          </Btn>

          {/* **Where you go next, in the same row rather than under it** —
              and without the word.

              It was its own line labelled *Next*, on the reasoning that the
              header's buttons are things you do *to the file* and this is a
              different question. Two things were wrong with that. The label
              made a claim that is usually false: on a compiled contract the
              next step is almost always Release or nothing, and arranging the
              pack is what you do *if* it is wrong — so *Next* pointed at the
              uncommon case. And a single ghost button on a row of its own,
              under a right-aligned cluster, reads as something that has come
              adrift however it is aligned.

              What the old paragraph was right about is that this one **leaves
              the screen** where the others stay on it. That is worth showing,
              and position says it better than a label: last, after a rule.
              Kept on `open` so a collapsed panel still offers nothing. */}
          {open && actions.length > 0 && (
            <>
              <span aria-hidden style={{ width: 1, alignSelf: "stretch", background: T.border, margin: "2px 4px" }} />
              {actions.map(a => (
                <Btn key={a.label} variant="ghost" size="sm" disabled={a.disabled} onClick={a.onClick}>{a.label}</Btn>
              ))}
            </>
          )}
        </span>
      </div>
      </InDocToolbar>

      {/* Embedded, the frame runs to the card's edges, so what sits above it
          is given its own inset rather than inheriting a padding that is no
          longer there. */}
      <div style={embedded && (compiledPdfIsStale(matter) || (open && !expanded && lotPanel)) ? { padding: "0 14px 12px" } : undefined}>
      {/* The expiry warning stays visible whether or not the frame is open —
          it is the reason the frame would show an AccessDenied page. */}
      {compiledPdfIsStale(matter) && (
        <div style={{ background: "rgba(196,122,26,0.06)", border: "1px solid rgba(196,122,26,0.25)", borderRadius: 8, padding: "10px 12px", marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "#c47a1a", marginBottom: 3 }}>
            This copy has expired
          </div>
          <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
            Compiled before documents were stored permanently, so its link has expired. Re-compile for one that keeps working.
          </div>
        </div>
      )}


      {/* **Revealed by the button, not sitting there folded.** The sheets it
          names are pages of the document below it, so a separate card meant
          reading a page number on one panel and finding it on another — and a
          strip that is always present with its own chevron is a second thing
          to open inside a section that is already open.

          Above the frame rather than below it: the frame is 520px, and a strip
          under it is one nobody scrolls to. */}
      {/* **Not while the modal is open**, and that is memory rather than tidiness.
          Both were rendered at once, so expanding mounted a *second* full
          viewer of the same pack behind the one on screen — two documents,
          two sets of canvases, and `tellViewers` posting to both so every
          travel press drew in both. On a wide column that is fifteen megabytes
          a page each, and cycling the lot press walked Safari into its ceiling
          and killed the tab. The frame underneath is invisible while the modal
          is up; nothing is lost by not drawing it.

          Closing remounts it, which is the path the protocol already has: the
          fresh frame posts `viewer-ready` and the framer re-sends the marks
          and the place it is on, so it comes back where the modal left off. */}
      {open && !expanded && lotPanel}
      </div>

      {/* **Reading the contract is the commonest thing anybody does on this
          screen**, and it was happening in a 520px letterbox four panels down.
          With the document leading the pane there is nothing above it but the
          matter bar, so it gets the window. */}
      {open && !expanded && (
        <div ref={frameBoxRef} style={{ ...(embedded ? EDGE_FRAME : { borderRadius: 8, border: `1px solid ${T.border}`, marginTop: 12 }), overflow: "hidden", background: T.bg, height: embedded ? (frameHeight ?? contractFrameHeight(peek)) : 520, minHeight: embedded ? contractFrameMin(peek) : undefined, position: "relative" }}>
          {/* **No device-viewer fallback here any more.** That existed because
              a framed PDF shows its first page and stops on WebKit; this frame
              holds the middleware's own viewer, which paginates, zooms and
              draws the halos — so a phone reads the contract in the portal
              rather than being handed the file and sent away.

              **Keyed on the version, because a version is a different
              document.** A re-compile used to change `src` from v3 to v4 with
              the frame still displaying v3, so closing the modal that had just
              shown the new contract revealed the old one underneath it — and
              which of the two somebody was reading was decided by how they got
              there. The URL no longer carries the version, so the key does.
              The same reason `previewKey` exists on the owners corporation
              preview. */}
          <iframe
            data-s32-view
            ref={frameRef}
            key={`${src}#v${matter.compileVersion}`}
            src={src}
            style={{ width: "100%", height: "100%", border: "none", display: "block" }}
            title="Compiled contract"
          />
        </div>
      )}

      {expanded && (
        <PdfPreviewModal
          wide
          title={`Compiled contract${matter.compileVersion > 1 ? ` v${matter.compileVersion}` : ""}`}
          // **`url` is the document and `embed` is the page that draws it.**
          // The modal reads `url` for what a download would save, which is
          // never the viewer page. `embedPaginates` is what stops it falling
          // back to the device viewer on a phone: that fallback is about a
          // framed *PDF*, and this frame holds a page that paginates properly
          // — which is the whole reason the expanded contract was unreadable
          // on an iPad.
          //
          // Travelling to the lot is the viewer's own bar button while the
          // modal is open; the header's press is behind it.
          url={contractSrc}
          embed={src}
          embedPaginates
          toolbar={lotButton}
          panel={lotPanel}
          // **The modal carries more than the frame, and that is the point.**
          // It is drawn over the matter bar, so Release and cross-check are
          // unreachable while it is open; beneath the frame they are on the
          // screen already. Defaulted to `actions` so a caller that passes
          // only the one set gets what it always got.
          actions={modalActions || actions}
          onClose={() => setExpanded(false)}
        />
      )}
    </div>
  );
};

// A merged value in the document carries the *printable* name — the registry
// holds `property.titleParticulars` and the template binds
// `property.titleParticularsText`, because a template slot needs a string and
// the registry holds lists and flags. Saving has to go back under the real
// name, or the correction lands in a field nothing reads.
const registryFieldFor = (registry, dataField) => {
  if (!dataField) return null;
  return registry.find(f => f.name === dataField)
    || registry.find(f => f.name === dataField.replace(/Text$/, ""))
    || null;
};

// The editor that opens over a clicked value.
//
// A popover rather than editing the document text directly: a flag has three
// states and prints as a tick box, a list is one item per line, and neither can
// be typed into the page. One mechanism covers every type, and it stays over
// the document instead of a modal that hides it.
// One of the extra fields a box opens alongside its own — the loan's lender
// and amount beside its approval date. A plain labelled input: it saves on
// blur like the panel does, so Save on the popover commits the field the box
// is actually bound to and these look after themselves.
//
// **It says where its value came from, and offers the other versions.** This
// was a bare label and a control, which is the third time the same fault has
// been found here: a field that is one thing in the registry and another in
// the browser, with nothing anywhere saying so. Section 3.4's overlays are an
// `ALSO_FIELD`, so on 1760545 the preview showed a value read from the
// certificate — a single overlay on land carrying four — with no provenance
// beside it and no way to take the map's fuller answer, while the data panel a
// tab away offered exactly that. Staff reported it as the option not existing
// in the preview.
//
// The revert comes with the offer rather than after it: a way to override with
// no way back inside the same box is the one-way door `fallback` exists to
// remove, and pressing "Use this" writes a manual correction.
export const AlsoField = ({ field, value, source, fallback, candidates, saving, onSave, onRevert, onDraft }) => {
  const incoming = contractFieldToInput(field.type, value);
  const [draft, setDraft] = useState(incoming);
  const last = useRef(incoming);

  // A value corrected elsewhere while this is open should not be overwritten
  // by a stale draft nobody touched.
  useEffect(() => {
    if (incoming !== last.current) { last.current = incoming; setDraft(incoming); }
  }, [incoming]);

  // **A field with a fixed set of answers is a fixed set of answers here too.**
  // This rendered a bare text box whatever the field declared, so the due
  // diligence override — whose whole point is that only its exact wording turns
  // the checklist on — would have arrived as something to type into. That is
  // the same fault as a property type that was a dropdown in the registry and a
  // text box in the browser.
  //
  // **Where a Save button owns the box, an also-field defers to it.** This
  // wrote every edit straight through, which is right in `BlockFieldsModal` —
  // that has only a Done button, so a field saving itself is the whole of how
  // anything is kept. In the preview's popover it is wrong twice over: the
  // contract re-renders on every keystroke's blur and every tick, and Cancel
  // abandons the field the box is bound to while everything under it has
  // already been written. Staff reported the first half; the second is what
  // makes a Cancel button a lie.
  //
  // `onDraft` present means somebody above holds the value and will commit it.
  // Absent, this saves itself exactly as it always did.
  const held = (next) => {
    setDraft(next);
    last.current = next;
    const value = contractInputToValue(field.type, next);
    if (onDraft) onDraft(field.name, { value });
    else onSave(field.name, value);
  };

  // A choice commits as it changes rather than on blur: there is no half-typed
  // state to protect, and a radio nobody blurs would otherwise never save.
  const choose = held;

  // **Deferred, a keystroke is recorded rather than a blur.** Pressing Save
  // does blur the box first in a browser, so hanging the record off blur
  // would *usually* work — and "usually" is how a correction comes to be
  // dropped on the one path that does not blur, which is Enter and every test
  // that clicks a button. Nothing is sent either way, so a keystroke costs
  // what a blur costs.
  const type = (next) => { if (onDraft) held(next); else setDraft(next); };

  // Shared by the box and the line, so the two cannot drift into different
  // ideas of when a draft is worth saving.
  const commit = () => {
    if (onDraft) return;
    if (draft === incoming) return;
    last.current = draft;
    onSave(field.name, contractInputToValue(field.type, draft));
  };

  // **"Use this" is a value, so deferred it belongs to the box's Save**, like
  // anything typed into the control above it. A revert is not a value — it
  // clears a correction and there is nothing to hold — so it acts at once and
  // drops whatever draft was pending, or Save would put the reverted value
  // straight back.
  const take = (name, v) => held(contractFieldToInput(field.type, v));
  const forget = () => { if (onDraft) onDraft(field.name, null); };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
        <span style={{ fontSize: 11, color: T.textSoft }}>{field.label}</span>
        <ProvenanceTag source={source} type={field.type} />
      </div>
      {/* **A flag is a flag here too, and it was not.** This branch ladder had
          no flag case at all, so `sale.offThePlan` and the two subdivision
          questions — the very fields the popover opens for an off-the-plan
          matter — drew a text box with the word "no" typed in it. Third time
          this exact fault has been found in this function: a field that is one
          thing in the registry and another in the browser.

          The special controls are shared components taking {field, draft,
          setDraft}, so these branches are the same ones the popover's own
          control and the data panel use. The plain text and textarea branches
          below still differ, because the three surfaces commit differently —
          on Save, on blur, and on change — and unifying that is a refactor of
          a screen staff use all day rather than a fix for this. What `onDraft`
          settles is narrower: *who* commits, not how the control is drawn. */}
      {field.type === "flag" && "boxWhen" in field ? (
        <BoxFlagInput field={field} draft={draft} setDraft={choose} />
      ) : field.type === "flag" ? (
        <select
          value={draft}
          onChange={e => choose(e.target.value)}
          style={{ ...inputStyle, cursor: "pointer" }}
        >
          {/* "Not answered" is a real answer, and the only honest one until
              somebody has checked. */}
          <option value="">— not answered —</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      ) : field.options ? (
        <ChoiceInput field={field} draft={draft} setDraft={choose} />
      // **A field that wants a box gets one here too, and it never did.**
      // This rendered a bare `<input>` whatever the field declared, so section
      // 3.4's overlays — a list a parcel commonly carries three or four of —
      // were edited on one line that the first one overflowed. Same fault as
      // the options above: a field that is one thing in the registry and
      // another in the browser, with nothing anywhere saying so.
      ) : paragraphControl(field) ? (
        <textarea
          value={draft}
          rows={field.type === "list" ? listRows(draft) : 3}
          placeholder={field.type === "list" ? "One per line" : ""}
          onChange={e => type(e.target.value)}
          onBlur={commit}
          style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5, fontFamily: "inherit" }}
        />
      ) : (
        <input
          {...fieldInputProps(field)}
          value={draft}
          onChange={e => type(e.target.value)}
          onBlur={commit}
          style={inputStyle}
        />
      )}
      <FieldNote field={field} />
      <OtherVersions
        field={field}
        value={value}
        source={source}
        fallback={fallback}
        candidates={candidates}
        saving={saving}
        onSave={onDraft ? take : onSave}
      />
      {source === "manual" && onRevert && (
        <RevertToSource field={field} entry={fallback} saving={saving}
                        onRevert={(name) => { forget(); onRevert(name); }} />
      )}
    </div>
  );
};

// Which vendor a signing slot stands for, or null. "vendor.name2" and
// "vendor.authority2" are both the second vendor's line — the position is the
// meaning, and the template stamps it on the span as data-slot precisely so a
// click can know which vendor it was.
export const vendorPositionOfSlot = (slot) => {
  const m = String(slot || "").match(/^vendor\.(?:name|authority)([1-4])$/);
  return m ? Number(m[1]) : null;
};

// The attorneys list with one position changed. Position is the pairing —
// attorneys[1] is the second vendor's — so blanks in the middle are kept and
// only trailing blanks are trimmed.
export const withAttorneyAt = (list, position, name) => {
  const arr = Array.isArray(list) ? list : [];
  const next = Array.from({ length: Math.max(position, arr.length) },
    (_, i) => String(arr[i] || "").trim());
  next[position - 1] = String(name || "").trim();
  while (next.length && !next[next.length - 1]) next.pop();
  return next;
};

// ── Saving a correction back to the record it came from ────────────────────
//
// **The firm's report:** *"from the contract preview, when amending the sales
// person's details, an address of an office or phone number, there should be
// an option to update the directory record so the change is saved for future
// matters."* Without it a correction is per-matter for ever — the next matter
// at that agency prints the same wrong address.
//
// **A tick beside Save rather than a second press**, because the moment
// somebody wants it is the moment they are correcting the value, and a button
// that appears afterwards is one they have already closed the box on.
//
// **Which fields it is offered for is the middleware's answer.** `saveable`
// comes off `agentDirectory`, derived there from the maps that do the writing
// — so a press this screen offers is one that actually saves something, and a
// field added to the write-back reaches this without a portal deploy.
//
// **And only where the matter is linked to a record.** `adopted` is what says
// so; a matter whose agent was typed into Form 27 has nothing to correct, and
// offering the tick there would be a promise nothing could keep.
// ── One agency detail, looked up on the web for this box ──────────────────
//
// 1760832: Woodards Sunbury & Macedon Ranges was matched and adopted, but its
// record had no phone. The web search staff ran found it — and the only press
// it offered was *Use this agency*, which writes every detail it found and
// **ends the directory link**, so the number could reach neither the record nor
// the matter without undoing the match.
//
// This asks the same search for the one value the box is bound to and offers
// *Use this* per result: it fills the box and ticks *Also save this to the
// directory* where that tick is offered, so one Save puts the number on the
// matter and on the agency's record. Nothing is written until Save — the
// number is the staff member's to check against the source first.
export const WEB_FIELD = {
  "agent.phone": { label: "agency phone", pick: o => o.phone },
  "agent.officeEmail": { label: "agency email", pick: o => o.email },
  "agent.address": {
    label: "agency address",
    pick: o => [o.address, o.suburb, o.state, o.postcode].filter(Boolean).join(" "),
  },
};

export const WebFieldLookup = ({ field, directory, onUse, onSearchOnline = searchAgentOnline }) => {
  const [state, setState] = useState({ busy: false });
  const spec = WEB_FIELD[field?.name];
  const matterRef = directory?.matterRef || "";
  if (!spec || !matterRef) return null;

  const found = (state.offices || [])
    .map(o => ({ office: o, value: String(spec.pick(o) || "").trim() }))
    .filter(r => r.value);
  const faint = { fontSize: 11, color: T.textFaint };

  return (
    <div style={{ marginTop: 10 }}>
      {/* **A link, and it says when it is working.** Underlined at rest; once
          pressed it is a spinner and grey words that cannot be pressed again,
          which is what the button's busy state did. */}
      {state.busy ? (
        <span role="status" style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: T.textFaint }}>
          <Spinner size={12} />
          Searching the web…
        </span>
      ) : (
        <button type="button"
          onClick={async () => {
            setState({ busy: true });
            try {
              setState({ busy: false, ...(await onSearchOnline(matterRef, {})) });
            } catch (e) {
              setState({ busy: false, error: e.message });
            }
          }}
          style={{
            font: "inherit", fontSize: 12, color: T.text, background: "none", border: "none",
            padding: 0, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2,
          }}>
          Search the web for the {spec.label}
        </button>
      )}
      {state.error && <div style={{ ...faint, marginTop: 6 }}>{state.error}</div>}
      {state.refused && <div style={{ ...faint, marginTop: 6 }}>{state.message}</div>}
      {state.offices && !found.length && !state.refused && (
        <div style={{ ...faint, marginTop: 6 }}>The web search found no {spec.label}.</div>
      )}
      {found.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={faint}>Found on the web — check it against the source before using it.</div>
          {found.map(({ office, value }, i) => (
            <div key={`${office.name}-${i}`} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ flex: "1 1 160px", minWidth: 0 }}>
                <span style={{ fontSize: 12.5, color: T.text, display: "block" }}>{value}</span>
                <span style={faint}>
                  {office.name}
                  {office.source && (
                    <>{" \u00b7 "}<a href={office.source} target="_blank" rel="noreferrer" style={{ color: T.textSoft }}>source</a></>
                  )}
                </span>
              </span>
              <PillButton onClick={() => onUse(value)}>Use this</PillButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// The fields that save to the agency's record rather than the salesperson's —
// the middleware's `TO_OFFICE` keys plus the address. Wording only: whether the
// tick is offered at all stays `saveable`'s answer.
const AGENCY_RECORD_FIELDS = new Set(["agent.name", "agent.address", "agent.phone", "agent.officeEmail"]);

export const SaveToDirectory = ({ field, directory, checked, onChange }) => {
  if (!directory?.adopted) return null;
  if (!(directory.saveable || []).includes(field?.name)) return null;

  return (
    <label style={{
      display: "flex", gap: 6, alignItems: "flex-start", marginTop: 10,
      fontSize: 11.5, color: T.textSoft, cursor: "pointer",
    }}>
      <input type="checkbox" checked={checked}
             onChange={e => onChange(e.target.checked)}
             style={{ accentColor: T.brown, marginTop: 1 }} />
      {/* **Names the record it writes.** The agency's details save to the
          office, which every salesperson there shares — the one thing a
          screen offering this has to say out loud; the salesperson's own
          details save to their record alone. */}
      {AGENCY_RECORD_FIELDS.has(field.name) ? (
        <span>
          Also save to this agency&rsquo;s directory record, so future matters use it.
          <span style={{ color: T.textFaint, display: "block" }}>
            Every salesperson at this agency shares it.
          </span>
        </span>
      ) : (
        <span>Also save to the salesperson&rsquo;s directory record, so future matters use it.</span>
      )}
    </label>
  );
};

// **The contract data panel's way to the same write.** Staff reported
// *"save to directory from contract data doesn't work"* — and on this screen
// it did not exist at all: the tick lived in the preview's popover only.
//
// A press rather than a tick, because the panel saves each field on blur and
// has no Save to put a tick beside. Offered only under a correction — a value
// taken from a source is not somebody's answer to push onto a shared record —
// and only on a field the write-back handles, on a matter linked to a record:
// the same two conditions `SaveToDirectory` applies, read off the same
// middleware answer.
// Why a save to the directory reached no record, in the middleware's terms.
// It used to say *"not linked to a record yet"* whatever the reason, which was
// false of an agency-only matter and of an agent who has since changed agency
// — both are linked, and the press needs a different answer in each case.
const DIRECTORY_SKIP_WORDING = {
  agent_moved_agency: "the salesperson has moved agency since this matter was adopted, so the agency this matter prints is no longer theirs. Correct that agency on the Agents screen instead.",
  no_agent_record: "this matter is linked to an agency, not to a salesperson.",
  no_office_record: "this matter is not linked to a record yet.",
  office_not_found: "the agency this matter was linked to is no longer in the directory.",
  agent_not_found: "the salesperson this matter was linked to is no longer in the directory.",
};
export const directorySkipWording = (skipped = []) => {
  const reason = (skipped || []).map(s => s?.reason).find(r => DIRECTORY_SKIP_WORDING[r]);
  return DIRECTORY_SKIP_WORDING[reason] || "this matter is not linked to a record yet.";
};

export const SaveToDirectoryLink = ({ field, directory, onSave, saving }) => {
  const [busy, setBusy] = useState(false);
  if (!onSave || !directory?.adopted) return null;
  if (!(directory.saveable || []).includes(field?.name)) return null;
  const run = async () => {
    setBusy(true);
    try { await onSave(field.name); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy || saving}
      title="The agency's own details are shared by every salesperson at it."
      style={{
        font: "inherit", fontSize: 11, color: T.textSoft, background: "none",
        border: "none", padding: "5px 0 0", cursor: busy || saving ? "default" : "pointer",
        textDecoration: "underline", textUnderlineOffset: 2,
      }}>
      {busy ? "Saving to the directory…" : "Save this to the directory for future matters"}
    </button>
  );
};

// The executor box and the deceased it names, drawn together in the popover.
const EXECUTORS_FIELD = "vendor.asExecutors";
const DECEASED_FIELD = "vendor.deceasedName";
const EXECUTOR_PAIR = new Set([EXECUTORS_FIELD, DECEASED_FIELD]);

export const FieldPopover = ({ field, also = [], contractFields, helpers, value, source, fallback, seeds, at, slot, saving, onSave, onSaveFields, onRevert, onClose, agentDirectory, onAdoptAgent, adoptingAgent, onSaveToDirectory }) => {
  const coarse = useCoarsePointer();
  const [draft, setDraft] = useState(seedDraft(field, value, seeds));
  const boxRef = useRef(null);

  // **The box commits on Save, and it did not.** Everything under the field
  // this opened on — the proposed lot, the plan's reference, the sunset date,
  // the question that turns the block on — wrote itself through the moment it
  // was touched, so editing 10.1 re-rendered the contract four times and
  // Cancel undid only the top control. Staff reported it as the fields
  // updating straight away.
  //
  // The drafts live in a ref because nothing on screen reads them: they are
  // the box's own state until Save, and holding them in `useState` would
  // re-render the popover on every keystroke to show exactly the same thing.
  const pending = useRef(new Map());
  // What *is* read is whether anything has been touched at all — see `moot`.
  const [touched, setTouched] = useState(false);
  const hold = (name, entry) => {
    if (entry) { pending.current.set(name, entry.value); setTouched(true); }
    else pending.current.delete(name);
  };

  // **A question this matter is not being asked gets no control.** On a sale
  // that is not off the plan there is no proposed lot and no sunset date, and
  // 10.2's clause is *Not applicable* until its own question says otherwise —
  // so drawing a box to type into is offering to answer something nobody
  // asked. Tick the question and the field it settles appears.
  //
  // **The middleware decides**, through the same `inapplicable` the data panel
  // already reads: a copy of the rule here would drift, and the way it would
  // drift is this hiding a field the document is printing an answer for.
  //
  // The panel keeps showing them, named as kept — that screen is the record of
  // what the matter holds, and this one is a quick edit over the document.
  //
  // **And it is only applied while the box has changed nothing.** That list is
  // derived from answers this box may have just changed — ticking *the lot is
  // on an unregistered plan* is exactly what makes the proposed lot and the
  // plan's reference applicable — and nothing has been sent yet, so the list
  // in hand describes the matter as it was when the box opened. Read against a
  // draft it is stale, and the failure is the one that matters: the question
  // is ticked, the three fields it settles stay hidden, and the only way to
  // reach them is to press Save and click the same clause again. So once
  // anything is touched the box shows everything the click opened — which errs
  // towards a control too many rather than one missing, the direction
  // `hideOwn` already refuses to go the other way on.
  const moot = touched ? new Set() : new Set(contractFields?.inapplicable || []);
  // Never to nothing: a popover with no controls at all reads as broken, and
  // the field the click came from is the one thing it can always offer.
  const hideOwn = moot.has(field.name)
    && also.some(f => !moot.has(f.name));

  // A vendor signing under a power of attorney, offered on the signing line
  // and the nature-of-authority line — the places somebody looks when the
  // wrong name is about to print. The middleware pairs the attorney to this
  // vendor by position and composes the authority wording; this only says
  // which position and who the attorney is.
  const attorneyPos = vendorPositionOfSlot(slot);
  const attorneysList = contractFields?.values?.["vendor.attorneys"];
  const attorneyNow = attorneyPos
    ? String((Array.isArray(attorneysList) ? attorneysList[attorneyPos - 1] : "") || "").trim()
    : "";
  const [underPoA, setUnderPoA] = useState(Boolean(attorneyNow));

  // **Executors, drawn the way the attorney is.** The two answers are the same
  // kind of question — in what capacity the people on this line sign — so they
  // read as one control: a box to tick, and the name it needs inside it once
  // ticked. Drawn as two ordinary also-fields, the tick repeated its own label
  // above itself and the deceased's name sat loose under the officers. Which
  // two fields, and their wording, are still the registry's.
  const executorsField = also.find(f => f.name === EXECUTORS_FIELD);
  const deceasedField = also.find(f => f.name === DECEASED_FIELD);
  const executorsNow = contractFields?.values?.[EXECUTORS_FIELD] === true;
  const deceasedNow = String(contractFields?.values?.[DECEASED_FIELD] ?? "");
  const [asExecutors, setAsExecutors] = useState(executorsNow);
  const [deceased, setDeceased] = useState(deceasedNow);
  const [attorney, setAttorney] = useState(attorneyNow);
  // Unticked every time the box opens, deliberately: pushing a correction onto
  // a record every other agent at that agency shares is a decision per edit,
  // not a preference that sticks.
  const [toDirectory, setToDirectory] = useState(false);

  useEffect(() => {
    const el = boxRef.current?.querySelector("input, textarea, select");
    if (el) { el.focus(); if (el.select) el.select(); }
  }, []);

  // **One write, and only what changed.**
  //
  // One, because the route has always taken a map and two sequential saves
  // repaint twice — the reasoning `saveContractFields` already carries. This
  // made two of its own before the also-fields are counted, and each of those
  // was a third.
  //
  // Only what changed, because the draft is seeded from the resolved value —
  // so pressing Save on a box nobody typed in would store section 10.2's
  // derived *Not applicable*, or the form's own standard wording, as a manual
  // correction that outranks every source and survives a re-extraction. The
  // seeded case is the exception that proves it: section 13's editor opens on
  // the generated attachment list against a stored blank, and saving that is
  // the override it exists to offer.
  const commitWith = (chosen) => {
    const fields = {};
    if (draft !== contractFieldToInput(field.type, value)) {
      fields[field.name] = contractInputToValue(field.type, draft);
    }
    for (const [name, v] of pending.current) fields[name] = v;
    if (executorsField) {
      if (asExecutors !== executorsNow) fields[EXECUTORS_FIELD] = asExecutors;
      if (asExecutors && deceasedField && deceased.trim() !== deceasedNow.trim()) {
        fields[DECEASED_FIELD] = deceased.trim();
      }
    }
    if (attorneyPos) {
      const next = underPoA ? attorney.trim() : "";
      if (next !== attorneyNow) fields["vendor.attorneys"] = withAttorneyAt(attorneysList, attorneyPos, next);
    }
    // A version taken from a source is the answer for that field whatever is
    // in the control above it — it is the later press of the two.
    Object.assign(fields, chosen || {});
    if (Object.keys(fields).length) onSaveFields(fields);
    // **After the field, and only for the field this box is bound to.** The
    // correction on the matter is what was asked for and has to land whatever
    // happens to the directory; and the also-fields are a different record's
    // business, so pushing them on a tick beside *this* value would save more
    // than the tick said.
    //
    // **And only a value.** Emptying the box clears the correction, so the
    // field falls back to Form 27 or the certificate — and the directory route
    // reads the resolved value, so a tick left on would have pushed that
    // fallback onto a shared record as though somebody had typed it.
    const sent = fields[field.name];
    const blank = sent == null || (Array.isArray(sent) ? sent.length === 0 : String(sent).trim() === "");
    if (toDirectory && field.name in fields && !blank) onSaveToDirectory?.(field.name);
    onClose();
  };

  const commit = () => commitWith(null);

  // Enter commits a single-line value; a textarea needs its newlines, so there
  // it takes ⌘/Ctrl+Enter. Escape always abandons.
  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    // Follows the control rather than the type, or a zone edited on one line
    // would want ⌘Enter to commit while looking exactly like every other box
    // that takes a plain one.
    if (e.key === "Enter" && (paragraphControl(field) ? (e.metaKey || e.ctrlKey) : !e.shiftKey)) {
      e.preventDefault(); commit();
    }
  };

  const common = { value: draft, onChange: e => setDraft(e.target.value), onKeyDown, style: inputStyle };

  // Read once per render rather than in the style object, so the width and the
  // edge it is clamped against cannot be computed from two different values.
  const boxWidth = editorWidth(field, also, typeof window === "undefined" ? 0 : window.innerWidth);

  return (
    <div
      ref={boxRef}
      data-field-popover=""
      style={{
        position: "fixed",
        // The clamp is derived from the width rather than written beside it as
        // a second number — they were 320 and 340, and a box that grew without
        // its margin would sit half off the right edge.
        left: Math.max(8, Math.min(at.left, window.innerWidth - (boxWidth + 20))),
        top: at.top + 6,
        visibility: at.hidden ? "hidden" : undefined,
        width: boxWidth, zIndex: 60, background: T.white, border: `1px solid ${T.borderMid}`,
        borderRadius: 8, padding: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: T.text, fontWeight: 500 }}>
          {field.label}
          {field.required && <span style={{ color: T.red, marginLeft: 3 }}>*</span>}
        </div>
        <ProvenanceTag source={source} type={field.type} because={source === "derived" ? contractFields?.derivations?.[field.name] : undefined} />
      </div>

      {/* **A paragraph box opens at two lines**, on the firm's instruction:
          section 10.1 composes a sentence and three rows left a band of empty
          box under it. `editorRows` is a floor, so it still grows.

          **And the whole control goes where this matter is not being asked
          the question** — see `hideOwn`. What is left is the question that
          settles it, one line down. */}
      {hideOwn ? null : field.type === "flag" && "boxWhen" in field ? (
        <BoxFlagInput field={field} draft={draft} setDraft={setDraft} />
      ) : "boxValue" in field ? (
        <BoxValueInput field={field} draft={draft} setDraft={setDraft} />
      ) : field.options ? (
        <ChoiceInput field={field} draft={draft} setDraft={setDraft} />
      ) : field.type === "flag" ? (
        <select {...common} style={{ ...inputStyle, cursor: "pointer" }}>
          {/* "Not answered" is a real answer, and the only honest one until
              somebody has checked. */}
          <option value="">— not answered —</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      ) : paragraphControl(field) ? (
        <textarea {...common} rows={field.type === "list" ? listRows(common.value) : editorRows(common.value, 2)}
                  placeholder={field.type === "list" ? "One per line" : ""}
                  style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} />
      ) : (
        <input {...fieldInputProps(field)} {...common} />
      )}

      {!hideOwn && <FieldNote field={field} />}

      {attorneyPos && (
        <div style={{ marginTop: 10, background: T.bg, borderRadius: 6, padding: "8px 10px" }}>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: T.text, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={underPoA}
              onChange={e => setUnderPoA(e.target.checked)}
              style={{ accentColor: T.brown }}
            />
            Vendor {attorneyPos} signs under power of attorney
          </label>
          {underPoA && (
            <>
              <input
                value={attorney}
                onChange={e => setAttorney(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") onClose(); }}
                placeholder="Attorney's full name"
                style={{ ...inputStyle, marginTop: 6 }}
              />
              <div style={{ fontSize: 10, color: T.textFaint, marginTop: 4, lineHeight: 1.5 }}>
                Puts the attorney on this signing line, with &ldquo;As attorney for&rdquo; the vendor as the nature of authority. Untick to have the vendor sign personally.
              </div>
            </>
          )}
        </div>
      )}

      {executorsField && (
        <div style={{ marginTop: 10, background: T.bg, borderRadius: 6, padding: "8px 10px" }}>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: T.text, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={asExecutors}
              onChange={e => setAsExecutors(e.target.checked)}
              style={{ accentColor: T.brown }}
            />
            {executorsField.boxLabel || executorsField.label}
          </label>
          {asExecutors && deceasedField && (
            <>
              <input
                value={deceased}
                onChange={e => setDeceased(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") onClose(); }}
                placeholder={deceasedField.label}
                aria-label={deceasedField.label}
                style={{ ...inputStyle, marginTop: 6 }}
              />
              <div style={{ fontSize: 10, color: T.textFaint, marginTop: 4, lineHeight: 1.5 }}>
                Filled from the title. Prints the vendors as executors of the estate of this person, deceased. Untick to print them as owners.
              </div>
            </>
          )}
        </div>
      )}

      {/* What the vendor said about this on Form 27, beside the disclosure it
          bears on rather than two screens away in the data panel. It is a
          helper, never the answer — see HELPER_FOR in the middleware. */}
      <VendorSaidBubble helpers={helpers} />

      {/* The rest of the set this box opens. Rendered under the field the box
          is bound to, in the order the document prints them — less the ones
          this matter's own answers have settled. See `moot`. */}
      {also.filter(f => !moot.has(f.name) && !EXECUTOR_PAIR.has(f.name)).map(f => (
        <AlsoField
          key={f.name}
          field={f}
          value={contractFields?.values?.[f.name]}
          source={contractFields?.provenance?.[f.name]}
          fallback={contractFields?.fallback?.[f.name]}
          candidates={contractFields?.candidates?.[f.name]}
          saving={saving}
          onSave={onSave}
          onRevert={onRevert}
          onDraft={hold}
        />
      ))}

      {source === "manual" && (
        <RevertToSource
          field={field}
          entry={fallback}
          saving={saving}
          onRevert={(name) => { onRevert(name); onClose(); }}
        />
      )}
      {/* The other versions the sources hold — the Triconvey address beside
          the title's — pick one instead of retyping it. Saving closes the
          popover the way the Save button does. */}
      <OtherVersions
        field={field}
        value={value}
        source={source}
        fallback={fallback}
        candidates={contractFields?.candidates?.[field.name]}
        saving={saving}
        onSave={(name, v) => commitWith({ [name]: v })}
      />
      {/* And the directory's own answer, offered where the agency actually
          prints. Clicking the agent's name on the particulars of sale is the
          natural place to ask "is this the right agency?", and sending
          somebody to a panel on another tab to answer it is how an affordance
          goes unused. Adopting closes the popover the way saving does. */}
      {DIRECTORY_POPOVER_FIELDS.includes(field.name) && (
        <DirectoryMatch
          directory={agentDirectory}
          adopting={adoptingAgent}
          onAdopt={(choice) => { onAdoptAgent(choice); onClose(); }}
        />
      )}

      {/* One agency detail from the web, into this box — never the whole
          agency, which would end the directory link. */}
      <WebFieldLookup
        field={field}
        directory={agentDirectory}
        onUse={(found) => {
          setDraft(found);
          // Ticked only where the tick is offered: a matter linked to a record,
          // on a field the write-back handles.
          if (agentDirectory?.adopted && (agentDirectory.saveable || []).includes(field.name)) {
            setToDirectory(true);
          }
        }}
      />

      {/* **Only once there is something to save.** Ticked over an unchanged
          box it pushed nothing — the save sends only what changed — so it
          was a promise the press could not keep, and on a phone a long one. */}
      {draft !== contractFieldToInput(field.type, value) && (
        <SaveToDirectory
          field={field}
          directory={agentDirectory}
          checked={toDirectory}
          onChange={setToDirectory}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, gap: 8 }}>
        <span style={{ fontSize: 10, color: T.textFaint }}>
          {/* No Esc key on a phone, so no hint about one. */}
          {saving ? "saving…" : coarse ? "" : "Esc to cancel"}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn>
          <Btn size="sm" onClick={commit}>Save</Btn>
        </div>
      </div>
    </div>
  );
};

// ── Rewording a clause of the precedent, on this matter ────────────────────
//
// The numbered special conditions and the two Additional Special Condition
// blocks are the precedent's own wording — markup in the stored template, one
// copy shared by every matter — so a clause holds no merge value and has no
// registry field behind it. `FieldPopover` is built around one: provenance,
// candidates, also-fields, the attorney row. Threading a made-up field through
// it to reach a textarea is how that editor acquires a second meaning nobody
// can hold in their head, so this is its own small popover instead.
//
// **Blank means the precedent's own wording.** Clearing an override restores
// the clause rather than blanking it — the rule `s32.attachments` follows over
// the generated attachment list — which is what makes offering one safe, and
// why *Restore* saves an empty value rather than being a separate operation.
export const ClausePopover = ({
  clause, text, printed, family, removed, at, saving, onSave, onRemove, onClose,
}) => {
  const coarse = useCoarsePointer();
  // **Seeded with the wording the page is printing**, which is the override
  // where there is one and the precedent where there is not. An empty box was
  // the thing staff reported first: it forced a copy and paste to change a
  // word, and it made "delete the wording" impossible to express, because
  // blank already means *use the precedent*.
  const [draft, setDraft] = useState(text || printed || "");
  const [confirming, setConfirming] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (!boxRef.current?.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  // Only what changed, the rule the field editor follows: pressing Save on a
  // clause nobody typed into would store the precedent's own wording as this
  // matter's override, which then survives every future precedent correction.
  const commit = () => {
    const next = draft.trim();
    if (next !== (text || "").trim()) onSave(clause, next);
    onClose();
  };

  // A clause is a paragraph, so Enter takes a newline and ⌘/Ctrl+Enter
  // commits. Escape always abandons.
  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
  };

  return (
    <div
      ref={boxRef}
      data-field-popover=""
      style={{
        position: "fixed",
        left: Math.max(8, Math.min(at.left, window.innerWidth - 420)),
        top: at.top + 6, width: 400, zIndex: 60,
        visibility: at.hidden ? "hidden" : undefined,
        background: T.white, border: `1px solid ${T.borderMid}`, borderRadius: 8,
        padding: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: T.text, fontWeight: 500 }}>
          {/* The middleware spells a section out — "SC9" is its shorthand, not
              what a conveyancer says — so this reads its word rather than
              composing one and getting "Special condition SC9". */}
          {family?.label ? (/^\d/.test(family.label)
            ? `Special condition ${family.label}` : family.label)
            : `Special condition ${clause}`}
        </div>
        <span style={{ fontSize: 11, color: T.textFaint }}>
          {removed ? "Not used on this matter"
            : (text ? "Reworded on this matter" : "The office's precedent")}
        </span>
      </div>

      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        rows={Math.min(14, Math.max(4, String(draft || "").split("\n").length + 2))}
        placeholder="The precedent's own wording"
        style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
      />

      {/* What the precedent says, where this matter has replaced it — so
          nobody has to close the box to read what they are overriding. Shown
          only while the two differ; with no override the box already holds it. */}
      {text && printed && text.trim() !== printed.trim() && (
        <div style={{ marginTop: 8, fontSize: 11, color: T.textSoft, lineHeight: 1.5,
                      maxHeight: 96, overflow: "auto" }}>
          <span style={{ fontWeight: 500 }}>The precedent says:</span> {printed}
        </div>
      )}

      {/* Taking the clause out. **Its number stays and its wording becomes
          "Not used"** — a contract of sale cross-references its own clauses, so
          closing the gap would silently change what every reference above it
          means. What goes is everything filed underneath it, and both halves
          are named here rather than left to be discovered after the press. */}
      {confirming && (
        <div style={{ marginTop: 8, padding: 8, borderRadius: 6,
                      background: T.surfaceSoft || "#faf7f2",
                      border: `1px solid ${T.borderMid}`, fontSize: 11,
                      color: T.textSoft, lineHeight: 1.6 }}>
          <div style={{ color: T.text }}>
            <span style={{ fontWeight: 500 }}>{family?.label || clause}</span>{" "}
            will keep its number and print <span style={{ fontWeight: 500 }}>Not used</span>.
          </div>
          {family?.removes?.length > 0 && (
            <div style={{ marginTop: 4 }}>
              These stop printing altogether: {family.removes.join(", ")}
            </div>
          )}
          {/* The one thing nothing else on the screen could tell them. */}
          {family?.citedBy?.length > 0 && (
            <div style={{ marginTop: 4, color: T.warnText || T.text }}>
              <span style={{ fontWeight: 500 }}>Check {family.citedBy.join(", ")}</span>
              {" "}— {family.citedBy.length === 1 ? "it refers" : "they refer"} to this clause
              by number.
            </div>
          )}
          <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
            <Btn variant="ghost" size="sm" onClick={() => setConfirming(false)}>Keep it</Btn>
            <Btn size="sm" onClick={() => { onRemove(clause, false); onClose(); }}>
              {deleteClauseLabel(clause, family)}
            </Btn>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, gap: 8 }}>
        <span style={{ fontSize: 10, color: T.textFaint }}>
          {/* No Esc key on a phone, so no hint about one. */}
          {saving ? "saving…" : coarse ? "" : "Esc to cancel"}
        </span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* Putting a clause back means the precedent's own wording: the
              removal goes, and so does anything that stood in for "Not used". */}
          {removed ? (
            <Btn variant="ghost" size="sm" onClick={() => { onRemove(clause, true); onClose(); }}>
              Put it back
            </Btn>
          ) : (
            onRemove && !confirming && (
              <Btn variant="ghost" size="sm" onClick={() => setConfirming(true)}>
                {deleteClauseLabel(clause, family)}
              </Btn>
            )
          )}
          {/* Clearing an override is a revert, not a delete — the clause comes
              back as the precedent wrote it. Offered only where there is one
              to clear, so it never reads as a way to empty a clause. */}
          {text && (
            <Btn variant="ghost" size="sm" onClick={() => { onSave(clause, ""); onClose(); }}>
              Restore the precedent
            </Btn>
          )}
          <Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn>
          <Btn size="sm" onClick={commit}>Save</Btn>
        </div>
      </div>
    </div>
  );
};

// The frames the portal writes its own documents into (the contract preview,
// the letters) are documents of their own, so 'index.css' does not reach them,
// and a phone's text autosizing enlarges some paragraphs and not others.
const noTextInflation = (doc) => {
  doc.documentElement.style.setProperty("-webkit-text-size-adjust", "100%");
  doc.documentElement.style.setProperty("text-size-adjust", "100%");
};

// Laying the preview out as pages.
//
// The rendered contract is one continuous column banked against the left, which
// is what HTML does and not what a contract looks like. This cuts it at the
// page breaks the document itself declares and gives each piece an A4 sheet,
// centred on a grey ground.
//
// **These are the document's declared breaks, not its printed pagination.** A
// sheet holding four pages' worth of clauses is one sheet here and four pages
// in the PDF — real pagination needs a layout engine, and the compiled PDF is
// the authority. What this buys is a document you can read as pages and
// navigate, which the single column was not.
//
// Injected into the frame rather than added to the template, deliberately:
// pdf.co converts the same markup, and a screen-only rule that leaked into the
// converter would repaginate the actual contract.
export const PAGED_CSS = `
/* Phones and tablets inflate what they judge to be body text, and only that:
 * the 9pt clauses rendered bigger than the 10pt headings above them. The
 * portal's own 'index.css' turns this off, but the preview is a frame with a
 * document of its own and inherits none of it. */

/* ── The affordances that must never reach a PDF ────────────────────────────
 *
 * These live here, in the stylesheet the *preview* injects, rather than in
 * 'contract.css', which is inlined into the stored template and so reaches
 * every renderer — Chromium, and pdf.co when the render falls back.
 *
 * '@media print' would be enough for Chromium, whose 'page.pdf()' renders
 * print media. It is **not known** to be enough for pdf.co: the middleware
 * sends that conversion '{html, name, margins}' and no media parameter at all,
 * so which media their engine emulates is theirs to decide and nothing on our
 * side can say. A tint printing would be untidy; *words* printing would be
 * wording nobody chose in a contract of sale.
 *
 * So the template ships the attributes and this file supplies the text. A
 * renderer cannot print a rule it was never given, whatever it does with
 * media queries.
 *
 * 'data-hint' — an editable slot that prints nothing. The lease's options
 * sentence is the case: with neither option recorded it prints nothing at all,
 * and an empty slot is a non-breaking space nobody can see, let alone click.
 */
.f[data-hint]:empty::after {
  content: attr(data-hint);
  color: #8a8378;
  font-style: italic;
  cursor: pointer;
}

/* 'data-jump' — a clause that points somewhere the reader can go. Special
 * condition 14.1 says the contract is subject to conditions "appearing on the
 * following pages", and on a matter with none there are no such pages and the
 * panel that writes them is two collapsed sections down the screen.
 *
 * Its own class rather than '.f': nothing there is a merge value, nothing is
 * saved, and the marks and popover '.f' carries would both be wrong on it.
 */
.jump { cursor: pointer; box-shadow: inset 0 -1px 0 #b9c8dc; }
.jump::after {
  content: " " attr(data-hint);
  color: #8a8378;
  font-style: italic;
}

/* An additional special condition is a way back to the drafter too.
 *
 * 14.1 points at "the following pages", so it is the route in for somebody who
 * has written nothing — and it was the only one. Once the pages exist they are
 * what a reader is looking at, and the holiday closure clause prints on every
 * contract issued inside the window, so correcting it meant knowing to scroll
 * back to a sentence about pages that are now there.
 *
 * A tint rather than the underline '.jump' draws: the whole paragraph is the
 * target, and a rule under a six-line clause reads as an underlined clause.
 * The words stay on the heading — the emitter gives the clauses 'data-jump'
 * and no 'data-hint', so nothing is appended after each one. */
[data-jump] { cursor: pointer; }
p[data-jump] { transition: background 0.1s; }
p[data-jump]:hover { background: #eef4fb; }

/* 'data-clause' — a clause of the precedent, editable on this matter.
 *
 * The numbered special conditions and the two Additional Special Condition
 * blocks are the precedent's own wording, shared by every matter, so a clause
 * carries no merge value and cannot be a '.f'. The emitter stamps each one
 * with its citation and the middleware stores what was typed against it; this
 * is only the affordance saying the words can be clicked.
 *
 * A tint like the additional conditions above, and deliberately fainter than
 * '.f': most of a contract is precedent wording, and marking all of it as
 * loudly as a merge value would read as a document full of holes. Nothing is
 * appended after it — the words that turn a mark into text stay out of
 * contract.css, and out of here, for the reason that file explains.
 */
[data-clause] { cursor: pointer; transition: background 0.1s; }
[data-clause]:hover { background: #f2f7f2; }
/* A clause this matter has already reworded. '.rw' is the middleware's, in
 * contract.css, and is stripped in print — this only says it is clickable. */
.rw { cursor: pointer; }

/* A value the vendor said something about on Form 27. A quiet mark rather than
 * a tint: this sits on a statutory disclosure, and it must read as a note to
 * the person checking rather than as anything about the clause. Stripped in
 * print with every other affordance — see .f in contract.css. */
.f.helped { border-bottom: 1px dotted #c47a1a; position: relative; }
.f.helped::after {
  content: "\\2022";
  color: #c47a1a;
  font-size: 0.7em;
  vertical-align: super;
  margin-left: 1px;
}

/* On hover, not on two clicks. white-space:pre-line is what lets two answers
 * about one field sit on their own lines — helperTooltip joins them with a
 * newline, and without it they run together into one sentence.
 *
 * left:0 with a max-width rather than centring: a value near the right margin
 * would otherwise push the box off the sheet, and there is no positioning code
 * inside the frame to pull it back. */
.f.helped:hover::before {
  content: attr(data-helper);
  position: absolute;
  left: 0;
  bottom: calc(100% + 6px);
  z-index: 40;
  width: max-content;
  max-width: 320px;
  white-space: pre-line;
  text-align: left;
  font-family: Inter, -apple-system, sans-serif;
  font-size: 10.5px;
  font-weight: 400;
  font-style: normal;
  line-height: 1.45;
  color: #f6f2ea;
  background: #3d3226;
  border-radius: 6px;
  padding: 6px 9px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.22);
  pointer-events: none;
}

/* A statutory disclosure sent to a purchaser must not be annotated, and the
 * tooltip sets a background at two-class specificity like .f.notable — so it
 * is named explicitly here rather than left to the bare .f reset. */
@media print {
  .f.helped { border-bottom: 0; }
  .f.helped::after { content: ""; }
  .f.helped:hover::before { content: none; display: none; }
  /* Somebody printing the preview from the browser. Not the compiled PDF —
   * that renderer never receives this file at all, which is the whole point of
   * the rules being here — but the same rule applies to anything that comes
   * off a printer with the form's words on it. */
  .f[data-hint]:empty::after { content: none; }
  .jump { box-shadow: none; }
  .jump::after { content: none; }
  /* Paper is one page wide whatever the screen was showing. */
  body.s32-two-up .contract { display: block; zoom: 1 !important; }
  body.s32-two-up .sheet { margin: 0 auto 14pt; }
}

${SHEET_CSS}  /* Two pages side by side. The sheets are siblings in .contract once
   * paginate has run, so a wrapping row is the whole of it; the scale that
   * makes two fit is set on .contract by applyPageLayout, after pagination,
   * because pagination only reads the breaks the document declares and never
   * measures — so a scale applied afterwards moves no page break. */
  body.s32-two-up .contract { display: flex; flex-wrap: wrap; justify-content: center; align-items: flex-start; column-gap: 14pt; }
  body.s32-two-up .sheet { margin: 0 0 14pt; }
  .f { cursor: pointer; transition: background 0.1s; }
  .f:hover { background: #d6e6f8; outline: 1px solid #1a7ac4; outline-offset: 1px; }
`;

// ── One page or two ───────────────────────────────────────────────
// A sheet is A4 at 595.3pt, about 794px, so two side by side need about
// 1,640px with their gaps — wider than the frame on any ordinary screen. They
// are scaled to fit, and below `TWO_UP_MIN_ZOOM` the text is too small to
// click a value in with any confidence, so the draft stays on one page there
// whatever was chosen. `null` is that answer.
export const SHEET_PX = 595.3 * 4 / 3;
export const TWO_UP_WIDTH = (2 * SHEET_PX) + (3 * 14 * 4 / 3);
export const TWO_UP_MIN_ZOOM = 0.6;
export const twoUpZoom = (width) => {
  const z = Math.min(1, (Number(width) || 0) / TWO_UP_WIDTH);
  return z >= TWO_UP_MIN_ZOOM ? z : null;
};

// Applied inside the frame, after `paginate`. Answers whether two pages are
// showing, so the toolbar can say when the frame is too narrow for them.
//
// **`zoom`, not a transform.** A transform scales the picture and leaves the
// layout box full size, so the row would still wrap one sheet per line and
// the frame would scroll through empty space. `zoom` changes the layout.
export const applyPageLayout = (doc, pages) => {
  const contract = doc?.querySelector?.(".contract");
  if (!contract) return false;
  const z = pages === 2 ? twoUpZoom(doc.documentElement.clientWidth) : null;
  doc.body.classList.toggle("s32-two-up", z != null);
  contract.style.zoom = z != null && z < 1 ? String(Math.round(z * 1000) / 1000) : "";
  return z != null;
};

// The reader's choice, per browser. A convenience rather than a record, so
// storage that is blocked or evicted only costs the default.
export const DRAFT_PAGES_KEY = "s32_draft_pages";
// **One page until somebody asks for two.** Inside the office's WordPress page
// the frame is about 1,100px wide, so two pages land at two-thirds size — fine
// for finding your place in the document, small for reading a clause. Reading
// is the commoner job, so it is the default, and the switch remembers whoever
// prefers the spread.
export const readDraftPages = () => {
  try { return window.localStorage.getItem(DRAFT_PAGES_KEY) === "2" ? 2 : 1; } catch { return 1; }
};
const rememberDraftPages = (n) => {
  try { window.localStorage.setItem(DRAFT_PAGES_KEY, String(n)); } catch { /* a preference is never worth an error */ }
};

// **One choice for both states of the contract.** The draft and the compiled
// contract sit behind one segment, so a reader who asked for two pages on one
// and pressed across to the other would otherwise meet a single column and a
// switch to find again. Both mount once and stay mounted, so a change made on
// one is announced to the other rather than read again on mount.
const PAGES_EVENT = "s32-pages";
export const usePagesPreference = () => {
  const [pages, setPagesState] = useState(readDraftPages);
  useEffect(() => {
    const onChange = (e) => setPagesState(e.detail === 2 ? 2 : 1);
    window.addEventListener(PAGES_EVENT, onChange);
    return () => window.removeEventListener(PAGES_EVENT, onChange);
  }, []);
  const setPages = useCallback((n) => {
    rememberDraftPages(n);
    setPagesState(n);
    window.dispatchEvent(new CustomEvent(PAGES_EVENT, { detail: n }));
  }, []);
  return [pages, setPages];
};

// Where to jump to. Matched on the document's own headings rather than on
// anchors we would have to add to the template — the wording is the precedent's
// and changes only when REIV republishes the form.
// Where the vendor signs.
//
// One bookmark covers both kinds of vendor. The heading sits *outside* the
// person/company branch, so it renders either way — and a company's
// "EXECUTED by … in accordance with s.127" block is inside the section it
// heads, immediately below it. Falling back to that block anyway, in case the
// heading is ever reworded: landing on the execution panel is still landing on
// the right page.
//
// It is early in the document, not at the end: the REIV form puts the signing
// panel in the front matter, before the particulars of sale.
const vendorSigning = (d) =>
  byText(d, /^signed by the vendor:?$/i)
  || [...d.querySelectorAll("p, div, h1, h2, h3, h4")]
    .filter(el => /^EXECUTED by/i.test((el.textContent || "").trim()))
    .pop()
  || null;

const BOOKMARKS = [
  { label: "Cover", find: (d) => d.querySelector(".cover") },
  { label: "Vendor signing", find: vendorSigning },
  { label: "Particulars", find: (d) => byText(d, /^particulars of sale$/i) },
  { label: "Special conditions", find: (d) => byText(d, /^special conditions$/i) },
  { label: "Additional", find: (d) => d.querySelector(".special-conditions") },
  { label: "General conditions", find: (d) => byText(d, /^general conditions$/i) },
  { label: "Vendor statement", find: (d) => byText(d, /^vendor statement$/i) },
];

// The heading a bookmark should land on.
//
// Two things were wrong with taking the first match.
//
// **The contract opens with its own contents list** — "Particulars of sale",
// "Special conditions", "General conditions", each its own paragraph — and the
// real headings come pages later in title case. The match is case-insensitive,
// so every jump landed on the contents page rather than the section.
//
// **And the real headings were unreachable anyway.** They are styled —
// `<p><span><strong>General conditions</strong></span></p>` — so requiring no
// children skipped them, which is why General conditions could *only* ever
// find the contents line.
//
// So: allow children, and take the **last** match. A contents entry always
// precedes the section it points at, which is what makes last the right one.
// The whole trimmed text must match, so a mention inside a sentence is not a
// candidate and cannot become the last.
// Where inside the frame a bookmark's target sits.
//
// **Not `scrollIntoView`.** That walks up every scrollable ancestor of the
// element — and an element inside an iframe has the *host page* among its
// ancestors, so scrolling the document to a heading also scrolled the portal
// underneath it. The reader asked to move within the contract and the whole
// screen jumped.
//
// Scrolling the frame's own window instead touches nothing outside it. The
// element's viewport position plus the frame's current scroll is its offset in
// the frame document, which is what `scrollTo` wants.
//
// A little air above it, so the heading is not flush against the top edge.
export const JUMP_MARGIN = 12;

export const frameScrollTop = (el, win) => {
  const top = el.getBoundingClientRect().top + (win.scrollY || 0) - JUMP_MARGIN;
  return Math.max(0, top);
};

// ── Where the reader was, across a rebuild ──────────────────────────────────
//
// **The restore was a pixel offset applied before pagination, and that is the
// jump.** `paginate` rebuilds the whole document into `.sheet` divs, each with
// a `min-height` of a full A4 page and a margin under it — so the paginated
// document is far taller than the raw one the frame has just loaded. Restoring
// `scrollY` at that moment measures against the short layout:
//
// - deep in the document the saved offset is past the raw document's own
//   maximum scroll, so the browser **clamps** it — which is why every edit in
//   the vendor's statement landed in the same place, whichever field it was;
// - nearer the top it is not clamped, and pagination still inflates everything
//   above the reader, so they land earlier than they were. The particulars
//   have few sheets above them, which is why they looked fine.
//
// So the restore happens **after** pagination, and it anchors on an element
// rather than on a number. A pixel restore after pagination would still drift:
// a flag does not merely print, it gates whole clauses, so the edit that
// caused the rebuild is exactly what changes the heights above the reader.
//
// The anchor is the topmost editable value still on screen, put back at the
// same viewport offset — so the page does not appear to move at all.
//
// **Identified by field, slot and ordinal.** A field binds several slots
// (`vendor.legalNames` prints in four signing blocks), so neither alone is an
// identity; the ordinal among its own matches settles the rest.
export const anchorSelector = ({ field, slot }) =>
  `.f[data-field="${CSS.escape(field)}"]`
  + (slot ? `[data-slot="${CSS.escape(slot)}"]` : ":not([data-slot])");

export const readScrollAnchor = (doc, win) => {
  const scrollY = win?.scrollY || 0;
  const fallback = { scrollY };
  if (!doc?.querySelectorAll) return fallback;

  // The first value at or below the top edge. Something scrolled half off the
  // top is not what the reader is looking at, and restoring to it would put
  // the line they were reading above the fold.
  for (const el of doc.querySelectorAll(".f[data-field]")) {
    const top = el.getBoundingClientRect?.().top;
    if (typeof top !== "number" || top < 0) continue;
    const field = el.dataset.field;
    const slot = el.dataset.slot || "";
    const peers = [...doc.querySelectorAll(anchorSelector({ field, slot }))];
    return { scrollY, field, slot, index: Math.max(0, peers.indexOf(el)), top };
  }
  return fallback;
};

export const applyScrollAnchor = (doc, win, anchor) => {
  if (!win?.scrollTo) return;
  const { field, slot, index = 0, top = 0, scrollY = 0 } = anchor || {};
  if (field && doc?.querySelectorAll) {
    // The same value in the rebuilt document. A slot that is gone — a clause
    // the edit turned off — falls through to the offset, which is no worse
    // than what this did before and is still measured after pagination.
    const el = [...doc.querySelectorAll(anchorSelector({ field, slot }))][index];
    const now = el?.getBoundingClientRect?.().top;
    if (typeof now === "number") {
      win.scrollTo(0, Math.max(0, now + (win.scrollY || 0) - top));
      return;
    }
  }
  if (scrollY) win.scrollTo(0, scrollY);
};

export const byText = (doc, re) => {
  const all = [...doc.querySelectorAll("h1, h2, h3, h4, p, div, span, strong")]
    .filter((el) => re.test((el.textContent || "").trim()));
  // A styled heading matches at every level of its own nesting. The outermost
  // is the line to scroll to; the inner ones are parts of it.
  const outermost = all.filter((el) => !all.includes(el.parentElement));
  return outermost[outermost.length - 1] || null;
};

// ── Landing the draft where the compiled document was ──────────────────────
//
// Read the compiled PDF, spot a problem at 9.1, press Draft, arrive at 9.1.
//
// **The key is the wording, not a page number.** The two are the same contract
// and they paginate differently — the draft breaks where the document declares
// a break and Chromium breaks wherever the content runs out of page — and the
// compiled pack puts every returned certificate behind the contract, so most
// of it has no counterpart here at all. The viewer answers with the text at
// the top of its view (`lib/pdf-where.js` in the middleware) and this finds it.
//
// **Nor the clause id**, which is worth saying because the case that prompted
// this is literally a citation. `data-clause` covers the 162 special
// conditions and nothing else, so a reader in the particulars of sale or at
// section 3.4 has no clause above them at all. Text covers the whole document
// — and on a clause it lands on the clause anyway, because the phrase starts
// with the marker the draft prints inside that very element.

// Whitespace is not part of the answer, and neither is the shape of a quote.
//
// A PDF's text layer is a list of positioned runs rather than a sentence:
// pdf.js routinely splits a phrase across items with no space between them, so
// a literal comparison fails on wording that is plainly there. The phrases are
// long enough that stripping it cannot produce a false match.
export const normPhrase = (value) => String(value || "")
  .replace(/[\u2018\u2019]/g, "'")
  .replace(/[\u201c\u201d]/g, '"')
  .replace(/\s+/g, "")
  .toLowerCase();

// Below this a run of the form's own wording stops identifying anywhere in
// particular. It is the middleware's `WHERE_FLOOR`, and the two are allowed to
// differ: that one bounds what is *sent*, this one what is accepted.
export const PHRASE_FLOOR = 24;

// **Trimmed from the end, never the start.** The reader is at the top of the
// view, so the phrase begins where they are looking — a prefix keeps the place
// and drops whatever came after it, which is where a value corrected since the
// compile most often sits. The draft is live data and the PDF is not, so the
// whole phrase failing is ordinary rather than exceptional.
export const phraseTries = (phrase, floor = PHRASE_FLOOR) => {
  const whole = normPhrase(phrase);
  if (whole.length < floor) return [];
  const out = [];
  for (let n = whole.length; n >= floor; n = Math.floor(n * 0.6)) out.push(whole.slice(0, n));
  out.push(whole.slice(0, floor));
  // **And then from the other end, which is the half a prefix cannot do.** A
  // value that has changed since the compile sitting at the *very* top of the
  // view is inside every prefix, however short — so the tail is tried too, and
  // it lands a line or two below where the reader was rather than nowhere at
  // all. Second, because keeping the exact place is worth more than keeping
  // the exact wording.
  for (let n = whole.length; n >= floor; n = Math.floor(n * 0.6)) out.push(whole.slice(-n));
  out.push(whole.slice(-floor));
  return [...new Set(out)];
};

// The element carrying that wording, or null.
//
// **Null is a real answer and the caller leaves the reader alone.** A phrase
// that cannot be found is a page of the pack the draft does not contain, or a
// value that has changed past every prefix — and landing somewhere arbitrary
// is worse than not moving, which is what this did before it existed.
export const findPhrase = (doc, phrase) => {
  const tries = phraseTries(phrase);
  if (!tries.length || !doc?.body || !doc.createTreeWalker) return null;

  // Built once over the whole document, so the ladder costs one walk rather
  // than one per attempt. 4 is `NodeFilter.SHOW_TEXT` — a number rather than
  // the constant, which lives on the *frame's* window and not on this one.
  const walker = doc.createTreeWalker(doc.body, 4);
  const spans = [];
  let hay = "";
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = normPhrase(node.nodeValue);
    if (!text) continue;
    spans.push({ node, at: hay.length });
    hay += text;
  }

  for (const want of tries) {
    const at = hay.indexOf(want);
    if (at === -1) continue;
    // The node the phrase starts in: the last one beginning at or before it.
    let hit = null;
    for (const span of spans) {
      if (span.at > at) break;
      hit = span;
    }
    if (hit?.node?.parentElement) return hit.node.parentElement;
  }
  return null;
};

// ── Edit contract and section 32 ───────────────────────────────────────────────
//
// **It was called the contract preview, and that stopped being true.** Staff
// now read the compiled PDF on the same screen, so *preview* named the wrong
// half: what they are previewing is the finished document, and this is the one
// they edit. Named for what it is *for* rather than what it renders, and the
// compiled panel keeps *Compiled contract*, which is what it is.
//
// **The section key does not move.** `sectionDefaults` stores `preview`, the
// same rule the rail's `documents` pane already follows — the key is what open
// and closed are remembered against, and only the label is anybody's to read.
// The contract and section 32 as they will print, rendered from the same
// template and the same data the compile uses.
//
// It sits under the contract data panel so an edit and its effect are on one
// screen: correct a value, watch it change in the document below. Compiling to
// find that out is the slow way round — that merges every search, converts the
// lot through pdf.co and bumps the version.
//
// Fetched rather than pointed at with an <iframe src>, so a failure can be read
// and explained. A cross-origin frame cannot be inspected, so a middleware
// answering "no template configured" would render as a blank rectangle with
// nothing to distinguish it from a bug. It also leaves the frame same-origin
// with the portal, which is what editing values in place will need.
// What the document should show about one of its own values.
//
// Three marks, and they answer three different questions: is this a hole, has
// somebody already corrected it, and did the vendor say something about it on
// Form 27. All three are affordances for whoever is checking the statement,
// and all three are stripped in print — a document sent to a purchaser must
// not be annotated.
// What the hover tooltip says about a value.
//
// **A footnote dot was two clicks from the answer** — click the value, open the
// popover, expand "The vendor said" — which on the one screen where somebody
// is typing a disclosure is well past the point of being read. The same words
// now appear on hover, where a note beside a value belongs.
//
// Plain text in an attribute rather than markup: it is rendered by CSS
// `content: attr(...)` inside the preview frame, which cannot carry elements.
// Kept compact for the same reason — a tooltip the width of the page is a
// second document.
export const helperTooltip = (helpers = []) => helpers.map((h) => {
  const said = [h.label, h.answer].filter(Boolean).join(': ');
  return [said, h.particulars && `“${h.particulars}”`].filter(Boolean).join(' — ');
}).join('\n');

// ── Whether a field has to be answered on *this* matter ─────────────────────
//
// `field.required` comes off the registry, which is one payload for every
// matter — so it can only carry the unconditional ones. The agent's details
// are required once a matter has an agent at all, and that is a fact about
// the matter, so the middleware sends the answer as `contractFields.required`.
//
// **Read from what was sent, never re-derived.** It is the same list `missing`
// is built against, and a second copy of the rule here would drift — the way
// it would drift is a field marked required that never appears as missing.
export const requiredHere = (field, contractFields = {}) =>
  field?.required === true
  || (contractFields.required || []).includes(field?.name);

// The group whose "not needed" decision covers a field, or "" where there is
// none. Only the agent's details today. The middleware refuses any group it
// has not named, so this is an affordance rather than the rule.
export const NOT_REQUIRED_GROUPS = ["agent"];
export const notRequiredGroupOf = (name = "") => {
  const group = String(name).split(".")[0];
  return NOT_REQUIRED_GROUPS.includes(group) ? group : "";
};

export const fieldMarks = (name, { missing = [], provenance = {}, helpers = {} } = {}) => {
  const marks = [];
  if (missing.some(m => m.name === name)) marks.push("missing");
  if (provenance[name] === "manual") marks.push("edited");
  // The vendor's answer was reaching the data panel and the searches review,
  // two screens from the disclosure it bears on — so somebody typing section 5
  // on the document had no sign the client had answered the same question.
  if (helpers[name]?.length) marks.push("helped");
  return marks;
};

// One page or two. Says so when two were asked for and the frame cannot fit
// them, rather than looking like a switch that did nothing.
// The draft's parts, one hover away.
//
// **Not in the toolbar row, and not behind a select.** A select was a press,
// a read of a list and a second press for every jump; seven words laid along
// the toolbar fixed that and read as a sentence wedged between the
// Compiled/Draft switch and the instruction — out of place, the firm said.
// So the row carries one quiet *Skip to*, and the parts are a second bar
// that drops beneath the toolbar while somebody is reaching for them.
//
// **Hover opens it, and nothing has to close it.** Leaving the press or the
// bar hides it after `SKIP_LEAVE_MS`, so crossing the gap between them does
// not drop it; opened and then ignored, it hides itself after `SKIP_IDLE_MS`,
// because a bar left over the top of the contract is a bar covering the first
// line somebody reads. A jump closes it too: the place has been chosen.
//
// **A press still opens it**, because a touchscreen has no hover and a
// keyboard has no pointer. Focus inside the bar holds it open the same way a
// pointer over it does, and Escape puts it away.
//
// It is positioned against the sticky document header rather than its own
// wrapper, so the bar runs the header's full width and travels with it.
export const SKIP_LEAVE_MS = 400;
export const SKIP_IDLE_MS = 4000;
export const SkipTo = ({ marks, onJump }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const holding = useRef({ pointer: false, focus: false });
  const clear = () => { clearTimeout(timer.current); timer.current = null; };
  const later = (ms) => {
    clear();
    timer.current = setTimeout(() => {
      if (holding.current.pointer || holding.current.focus) return;
      setOpen(false);
    }, ms);
  };
  useEffect(() => () => clear(), []);
  const show = () => { setOpen(true); later(SKIP_IDLE_MS); };
  const enter = () => { holding.current.pointer = true; clear(); setOpen(true); };
  const leave = () => { holding.current.pointer = false; later(SKIP_LEAVE_MS); };
  const close = () => { clear(); holding.current = { pointer: false, focus: false }; setOpen(false); };
  if (!marks.length) return null;
  return (
    <span
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={() => { holding.current.focus = true; clear(); }}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        holding.current.focus = false;
        later(SKIP_LEAVE_MS);
      }}
      onKeyDown={(e) => { if (e.key === "Escape") close(); }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls="s32-skip-to"
        onClick={() => (open ? close() : show())}
        style={{
          background: "none", border: 0, padding: "4px 2px", font: "inherit", fontSize: 12,
          fontFamily: "Inter, sans-serif", color: open ? T.text : T.textSoft, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 4,
        }}
      >
        Skip to
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <nav
          id="s32-skip-to"
          aria-label="Jump to a part of the contract"
          style={{
            // **Under the row it drops from**, which is the nearest positioned
            // box: the document header on the matter page, and the toolbar
            // itself in the compile workspace and the expanded draft — before
            // that, those had none and it opened off the bottom of the window.
            // Above the frame, which comes after it and is positioned too.
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5,
            background: T.white, borderBottom: `1px solid ${T.border}`,
            boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
            padding: "8px 16px", display: "flex", flexWrap: "wrap", columnGap: 16, rowGap: 4,
          }}
        >
          {marks.map((m) => (
            <button
              key={m.label}
              type="button"
              className="s32-link"
              onClick={() => { onJump(m); close(); }}
              style={{
                background: "none", border: 0, padding: "2px 0", font: "inherit", fontSize: 12,
                fontFamily: "Inter, sans-serif", color: T.text, cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {m.label}
            </button>
          ))}
        </nav>
      )}
    </span>
  );
};

export const PagesSwitch = ({ pages, fits = true, onChange }) => {
  const opt = (n, label, icon) => (
    <button type="button" aria-pressed={pages === n} title={label} aria-label={label}
      onClick={() => onChange(n)}
      style={{
        border: "none", borderRadius: 999, padding: "4px 8px", display: "flex", cursor: "pointer",
        background: pages === n ? T.white : "transparent",
        boxShadow: pages === n ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
        color: pages === n ? T.text : T.textSoft,
      }}>
      {icon}
    </button>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {!fits && <span style={{ fontSize: 11.5, color: T.textFaint, whiteSpace: "nowrap" }}>Too narrow for two pages</span>}
      <div role="group" aria-label="Pages side by side" style={{ display: "flex", background: T.bg, borderRadius: 999, padding: 2 }}>
        {opt(1, "One page", <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="7" y="3" width="10" height="18" rx="1.5"/></svg>)}
        {opt(2, "Two pages", <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2.5" y="4" width="8.5" height="16" rx="1.5"/><rect x="13" y="4" width="8.5" height="16" rx="1.5"/></svg>)}
      </div>
    </div>
  );
};

// ── The draft, expanded ────────────────────────────────────────────────────
//
// The compiled contract had Expand and the draft did not, so the one document
// staff edit was the one they could only read in the card — one page at a time
// beside the classic screen's navigation, too narrow for two.
//
// **The same frame, restyled, never a second one.** The draft is a blob the
// panel fetched, paginated and wired for click-to-edit; a modal holding its own
// iframe would be a copy where editing does nothing, and moving the iframe to a
// portal reloads it. So the layer around the toolbar and frame turns into a
// fixed layer over the window and back: nothing remounts, the reader keeps
// their place, and the ResizeObserver relays the pages, so two side by side
// fit where the card was too narrow. The popovers rise above it with it.
export const DRAFT_EXPANDED_Z = 320;
export const draftExpandedStyle = {
  position: "fixed", inset: 0, zIndex: DRAFT_EXPANDED_Z, background: "rgba(54,47,42,0.55)",
  padding: 20, display: "flex", flexDirection: "column",
};

const ContractPreview = ({ matterRef, registry, contractFields, specialConditions, clauseOverrides, clausesRemoved, onRemoveClause, generatedAttachments, searches, agentDirectory, onAdoptAgent, adoptingAgent, onSaveToDirectory, onSave, onSaveFields, onSaveClause, onRevert, onJump, savingFields, savingClause, defaultOpen = false, stage, revealSignal = 0, embedded = false, goTo = null, peek = CONTRACT_PEEK, fill = false }) => {
  // `fill`: the frame takes the height its parent gives it rather than measuring
  // its distance down the page — inside a fixed layer (`CompileWorkspace`) the
  // page's scroll says nothing about where the frame is.
  // See `CompiledDocumentPanel`: embedded is the document surface, and the
  // segment above it decides whether this is on screen at all.
  const [ownOpen, setOpen] = useStagedSection(defaultOpen, stage);
  const open = embedded || ownOpen;
  // Where *Edit contract and section 32* lands, from the compiled contract.
  // On a compiled matter this section starts shut, so the jump has to open it.
  const sectionRef = useRef(null);
  useRevealSection(revealSignal, setOpen, sectionRef);
  const [state, setState] = useState({ loading: true, url: null, error: null });
  const [editing, setEditing] = useState(null);
  const [marks, setMarks] = useState([]);
  const [pages, setPages] = usePagesPreference();
  // Whether two actually fit — the choice can be two and the frame too narrow.
  const [twoUp, setTwoUp] = useState(false);
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  // **Expand is the same frame, not a second one.** See `draftExpandedStyle`.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  // Rebuilding, with the previous document still on screen. Blanking the frame
  // and putting it back read as a flicker on every edit — the document is the
  // thing being worked on, so it stays up and greys out instead.
  const [busy, setBusy] = useState(false);
  const frameRef = useRef(null);
  const frameBoxRef = useRef(null);
  const frameHeight = useContractFrameHeight(frameBoxRef, peek);
  // The layout follows the choice and the frame's width. Kept on the value the
  // reader was looking at, since rows of two are a different height from a
  // column of one and a raw scroll position would land elsewhere.
  useEffect(() => {
    const frame = frameRef.current;
    const box = frameBoxRef.current;
    const relayout = () => {
      const doc = frame?.contentDocument;
      const contract = doc?.querySelector?.(".contract");
      // **A hidden segment measures nothing.** The other state of the contract
      // is `display: none`, so its frame reads zero wide — laying it out then
      // would drop to one page and lose the reader's place, which the move
      // back to it is meant never to cost.
      if (!contract || !box?.clientWidth) return;
      const was = `${doc.body.className}|${contract.style.zoom}`;
      const anchor = readScrollAnchor(doc, frame.contentWindow);
      // A draft loaded while hidden could not be measured; it is filled the
      // first time it is on screen.
      const filledNow = fillPages(doc);
      setTwoUp(applyPageLayout(doc, pagesRef.current));
      if (filledNow || `${doc.body.className}|${contract.style.zoom}` !== was) {
        applyScrollAnchor(doc, frame.contentWindow, anchor);
      }
    };
    relayout();
    if (!box || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(relayout);
    ro.observe(box);
    return () => ro.disconnect();
  }, [pages, state.url]);
  // Where the reader was before an edit re-rendered the document under them —
  // the value they were looking at and its offset up the frame, not a raw
  // scroll position. See readScrollAnchor.
  const scrollRef = useRef(null);
  // ── Landing where the compiled document was ─────────────────────────────
  //
  // **Held rather than applied, because the frame may not exist yet.** The
  // segment mounts this on first sight, so pressing Draft from the compiled
  // document starts a fetch, a parse and a pagination — and the answer to
  // *where were you* usually arrives before any of it. `wireUp` applies it at
  // the end of the load; the effect applies it when the frame is already
  // there. Whichever is second finds nothing left to do.
  const goToRef = useRef(null);
  const applyGoTo = useCallback(() => {
    const target = goToRef.current;
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    const win = frame?.contentWindow;
    if (!target?.text || !doc || !win?.scrollTo) return;
    const el = findPhrase(doc, target.text);
    // **Not found is a real answer: leave the reader alone.** The page they
    // were on may be a certificate this document does not contain, and
    // landing somewhere arbitrary is worse than not moving.
    if (!el) { goToRef.current = null; return; }
    // Instant, never smooth. The document is still settling — sheets are
    // sized as they render — so an animation travelling towards a number
    // worked out at the start arrives somewhere else. The lot search paid for
    // that lesson already.
    win.scrollTo(0, frameScrollTop(el, win));
    goToRef.current = null;
  }, []);
  useEffect(() => {
    if (!goTo?.text) return;
    goToRef.current = goTo;
    applyGoTo();
  }, [goTo, applyGoTo]);
  // The blob currently on screen, so the new one can replace it before the old
  // one is revoked. Revoking first is what blanked the frame.
  const liveUrl = useRef(null);

  // A save replaces contractFields, and the document has to be rebuilt rather
  // than patched: a flag does not merely print, it gates whole clauses through
  // the precedent's 39 conditions, so changing one adds or removes text.
  //
  // **Serialised, not compared by identity.** This was the object itself, which
  // is a new one on every poll even when nothing in it changed — so while the
  // certificates were being read the preview rebuilt every ten seconds, greying
  // and swapping the document under whoever was reading it. Exactly the trap
  // the conditions key below already names; it just wasn't applied here.
  const reloadKey = JSON.stringify(contractFields || null);

  // Saved conditions are their own trigger — they aren't part of contractFields,
  // and a clause that was just saved has to appear in the document below it.
  // Serialised rather than compared by identity, because re-fetching the matter
  // hands back an equal-but-new array and the compile poll does that every three
  // seconds — a rebuild each time would fight whoever is reading.
  const conditionsKey = JSON.stringify(specialConditions || []);

  // And a clause of the precedent this matter reworded — its own trigger for
  // the same reason, and serialised for the same reason: the matter poll hands
  // back an equal-but-new object every few seconds, and rebuilding on identity
  // would swap the document under whoever is reading it.
  // The preview rebuilds on either, or a deleted clause stays on screen until
  // something else happens to repaint — which reads as the press doing nothing.
  const clausesKey = JSON.stringify([clauseOverrides || {}, clausesRemoved || []]);

  // What each clause is called, what removing it takes with it, and what cites
  // it. One fetch for the whole session — it is the same for every matter.
  const [clauseTree, setClauseTree] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetchClauseTree().then(list => { if (!cancelled) setClauseTree(list); });
    return () => { cancelled = true; };
  }, []);

  // **And the pack, which nothing here was watching.** Section 13 names the
  // merge list, so a certificate returning, a returned one marked Not
  // Required, a document added, removed or renamed, and a drag on Arrange
  // documents all change what the statement prints — and none of them touches
  // `contractFields` or `specialConditions`. The list went stale until
  // somebody closed the panel and opened it again, which reads as a section
  // that does not update itself.
  //
  // **`matter.searches`, never the row the matter screen holds locally.** That
  // one flips before its save lands, and this fetches a document the server
  // renders from the server's own data — so rebuilding on an optimistic flip
  // would swap in a document that does not carry it yet.
  const packKey = mergeListSignature(searches);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    // Captured against the document on screen — which is the *paginated* one,
    // so the anchor's offset is measured in the layout the reader is actually
    // looking at. See readScrollAnchor.
    try {
      const win = frameRef.current?.contentWindow;
      scrollRef.current = readScrollAnchor(win?.document, win);
    } catch (e) { scrollRef.current = null; }
    setBusy(true);
    setMarks([]);
    if (!liveUrl.current) setState({ loading: true, url: null, error: null });

    apiFetch(`/contract-preview/${encodeURIComponent(matterRef)}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error || `Preview failed (${res.status})`);
        }
        return res.text();
      })
      .then((html) => {
        if (cancelled) return;
        const next = URL.createObjectURL(new Blob([html], { type: "text/html" }));
        // Swap, then revoke — the old document stays painted until the new one
        // has replaced it.
        const previous = liveUrl.current;
        liveUrl.current = next;
        setState({ loading: false, url: next, error: null });
        setBusy(false);
        if (previous) URL.revokeObjectURL(previous);
      })
      .catch((err) => {
        if (cancelled) return;
        setBusy(false);
        setState({ loading: false, url: null, error: err.message });
      });

    return () => { cancelled = true; };
  }, [matterRef, open, reloadKey, conditionsKey, clausesKey, packKey]);

  // The blob outlives the effect that made it, so it is released when the panel
  // goes away rather than on every re-fetch. The rendered contract is a third
  // of a megabyte.
  useEffect(() => () => { if (liveUrl.current) URL.revokeObjectURL(liveUrl.current); }, []);

  // Dismiss on a click outside — via a listener rather than a covering overlay,
  // because an overlay sits over the frame too, and the click that should have
  // opened the next value would be spent closing this one.
  const isEditing = Boolean(editing);
  useEffect(() => {
    if (!isEditing) return undefined;
    const onDown = (e) => { if (!e.target.closest?.("[data-field-popover]")) setEditing(null); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isEditing]);

  // **The popover follows the value it opened on.** It is drawn in viewport
  // coordinates, and it used to close the moment the document scrolled — so a
  // box taller than the space under its value could not be finished, because
  // scrolling to reach its Save took it away. Scrolling the portal instead
  // left it pinned while the value moved off without it.
  //
  // So every scroll — the document's, the portal's, or any panel's (capture
  // catches the ones that do not bubble) — re-measures the value and moves
  // the box with it. Scrolled out of the preview it is hidden rather than
  // closed, so what was typed survives scrolling back; a value the preview
  // rebuilt away has nothing to follow, and the box closes.
  const anchor = editing?.anchor;
  useEffect(() => {
    if (!anchor) return undefined;
    const win = frameRef.current?.contentWindow;
    let raf = 0;
    const follow = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const frame = frameRef.current;
        if (!frame || !anchor.isConnected) { setEditing(null); return; }
        const r = anchor.getBoundingClientRect();
        const f = frame.getBoundingClientRect();
        const hidden = r.bottom < 0 || r.top > f.height;
        setEditing(e => (e && e.anchor === anchor
          ? { ...e, left: f.left + r.left, top: f.top + r.bottom, hidden } : e));
      });
    };
    win?.addEventListener("scroll", follow, { passive: true });
    window.addEventListener("scroll", follow, { passive: true, capture: true });
    window.addEventListener("resize", follow);
    return () => {
      cancelAnimationFrame(raf);
      win?.removeEventListener("scroll", follow);
      window.removeEventListener("scroll", follow, { capture: true });
      window.removeEventListener("resize", follow);
    };
  }, [anchor]);

  // Wiring the document up once it has loaded.
  //
  // The frame is a blob: URL the portal made, so it is same-origin and its DOM
  // can be reached. That was the reason for fetching the HTML rather than
  // pointing an <iframe src> at the middleware — a cross-origin frame is a
  // sealed box, and none of this would be possible through one.
  const wireUp = () => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!doc) return;

    const style = doc.createElement("style");
    style.textContent = PAGED_CSS;
    doc.head.appendChild(style);
    paginate(doc);
    fillPages(doc);
    // Before the scroll anchor, which measures where the reader was against
    // the layout it lands in.
    setTwoUp(applyPageLayout(doc, pagesRef.current));

    // **After pagination, never before.** Sheets are a full page tall each, so
    // the paginated document is far taller than the one the frame just loaded
    // — restoring here measured against the short layout and the browser
    // clamped it, which is why every edit in the statement landed in the same
    // place. See readScrollAnchor.
    applyScrollAnchor(doc, frame.contentWindow, scrollRef.current);

    // Built from what the document actually contains: the additional special
    // conditions only exist when staff have added some, and a bookmark to a
    // heading that isn't there would scroll nowhere.
    setMarks(BOOKMARKS.map(b => ({ label: b.label, el: b.find(doc) })).filter(b => b.el));

    // The stylesheet already carries these; nothing was applying them. Amber
    // for a required field nothing has filled, a blue rule for a value someone
    // corrected by hand — so the document itself shows what still needs doing.
    doc.querySelectorAll(".f[data-field]").forEach((el) => {
      const field = registryFieldFor(registry || [], el.dataset.field);
      if (!field) return;
      // The mark is on the value itself; the words are on hover, and the
      // popover on click still carries them in full.
      for (const mark of fieldMarks(field.name, contractFields)) el.classList.add(mark);

      const said = contractFields?.helpers?.[field.name];
      if (said?.length) el.setAttribute("data-helper", helperTooltip(said));
    });

    doc.addEventListener("click", (e) => {
      // **A drag that selected text is not a click on a value.** Releasing
      // inside an element still fires a click, so selecting a sentence that
      // runs across a tick box would tick it — and now that selecting text in
      // here is how feedback is reported, that is a correction nobody made.
      if (!doc.getSelection?.()?.isCollapsed) return;
      // **A clause that points somewhere, before a field that holds a value.**
      // Special condition 14.1 says the contract is subject to conditions on
      // the following pages; on a matter with none there are no such pages and
      // the panel that writes them is two collapsed sections down the screen.
      // It is not a merge field — nothing is saved and no editor opens — so it
      // is answered here rather than by giving it a `data-field` the compile
      // never reads.
      const jump = e.target.closest?.("[data-jump]");
      if (jump) {
        setEditing(null);
        // **And where the following pages are in the document, go to them.**
        // 14.1 points at "any additional special conditions appearing on the
        // following pages", and on an off-the-plan sale or a tenanted one
        // those pages are the precedent's own blocks — printed a few pages
        // down, marked `data-block` by the emitter. Opening the editor for the
        // conditions staff write instead sent the reader to a panel that has
        // nothing to do with the clauses the sentence is about.
        //
        // The blocks are the first destination and the editor is the fallback,
        // because only where neither prints are the following pages the ones
        // staff write. The custom conditions carry the editor's own jump value
        // and land below, so clicking one of those still opens the panel that
        // wrote it — the standing rule that the thing in front of you should
        // be the thing you can click.
        if (jump.dataset.jump === "additionalConditions") {
          // `scrollIntoView` walks up every scrollable ancestor, and an
          // element inside an iframe has the host page among them — the
          // reader asked to move within the contract and the whole screen
          // jumped. `frameScrollTop` is the same fix the bookmarks use.
          const block = doc.querySelector("[data-block]");
          const win = frame?.contentWindow;
          if (block && win) {
            win.scrollTo({ top: frameScrollTop(block, win), behavior: "smooth" });
            return;
          }
          onJump?.("specialConditions");
          return;
        }
        onJump?.(jump.dataset.jump);
        return;
      }
      const el = e.target.closest?.(".f[data-field]");

      // **A clause of the precedent, where the click was not on a value.**
      //
      // The numbered special conditions and the two Additional Special
      // Condition blocks are the precedent's own wording, stamped with their
      // own citation by the emitter — there is no merge value and no registry
      // field, so this cannot go through the editor above it.
      //
      // **The field wins**, and the order is what decides it: a merge slot
      // sits *inside* a clause — the sunset date inside B.1's definition — and
      // `closest` from the click finds the nearer of the two. So clicking the
      // value edits the value and clicking the words around it rewords the
      // clause, which is what either gesture plainly means.
      if (!el) {
        const clause = e.target.closest?.("[data-clause]");
        if (clause && onSaveClause) {
          const r = clause.getBoundingClientRect();
          const f = frame.getBoundingClientRect();
          const id = clause.dataset.clause;
          setEditing({
            clause: id,
            // The wording the page is printing, read off the document itself
            // rather than carried in a second copy — resolved, and this
            // matter's own override where there is one, which is exactly what
            // somebody clicking means to edit.
            printed: clauseWording(clause),
            anchor: clause,
            left: f.left + r.left, top: f.top + r.bottom,
          });
          return;
        }
      }

      if (!el) { setEditing(null); return; }
      const field = registryFieldFor(registry || [], el.dataset.field);
      // A merged value with no registry field behind it is precedent wording,
      // not data. Better to do nothing than to open an editor that saves into
      // a name the compile never reads.
      if (!field) return;

      // A tick box ticks. Opening a popover to choose between two states the
      // box already shows is a step for nothing, and the boxes are most of
      // what a reader corrects on the particulars of sale.
      const answer = boxValueOf(el);
      if (answer !== undefined) {
        const now = contractFields?.values?.[field.name];
        const next = boxToggleValue(now, answer, boxOffValueOf(el), /[\u2611\u2612]/.test(el.textContent || ""));
        // Ticking always sticks, and so does unticking into an answer the form
        // itself names — that is a correction, and a correction outranks every
        // source. Unticking into *nothing* only clears a correction: against
        // anything else the box would redraw ticked and the press would read
        // as a broken button, so the editor opens and accounts for the value
        // instead of saving nothing.
        if (next !== null || boxClearSticks(contractFields?.provenance?.[field.name])) {
          setEditing(null);
          onSave(field.name, next);
          return;
        }
      }

      const r = el.getBoundingClientRect();
      const f = frame.getBoundingClientRect();
      // The other fields this one opens with. The loan box is drawn by its
      // approval date existing, so it opened the approval date alone — while a
      // contract subject to a loan has three particulars, all blank at issue
      // and only one of them reachable from the page they are printed on.
      const also = editorAlso(field, el.dataset.also);
      setEditing({ name: field.name, also, slot: el.dataset.slot || "", anchor: el, left: f.left + r.left, top: f.top + r.bottom });
    });

    // **Last, after pagination.** The sheets are what the reader is landing
    // among, and measuring before they exist is the clamp that used to throw
    // every statement edit back to section 1. Held since the press if the
    // frame was still loading; a no-op if the effect already applied it.
    applyGoTo();
  };

  return (
    <div ref={sectionRef} style={fill ? { height: "100%", display: "flex", flexDirection: "column", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" } : embedded ? {} : { background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      {/* **The subtitle was this bug's first fix and it did not take.** *live —
          not the compiled copy* is the right thing to say and it was said in
          the one place that scrolls off the moment anybody reads the document.
          The segment says it permanently, so the heading goes with the card. */}
      {!embedded && (
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Edit contract and section 32</span>
          <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 400 }}>live — not the compiled copy</span>
        </div>
        {/* No Open button. The preview is a live render with click-to-edit
            wired into the frame; opening it in a tab gives a detached copy of
            a blob URL where editing does nothing and the values go stale the
            moment anything is corrected — a page that looks like the contract
            and quietly is not. The compiled PDF is the thing worth opening on
            its own, and it has its own button. */}
      </div>
      )}

      {/* A clause of the precedent, not a merge value — its own editor, for
          the reason ClausePopover explains. Checked first, because a clause
          edit carries no `name` and the field lookup below would answer null
          and draw nothing at all. */}
      <div data-draft-popovers style={expanded ? { position: "relative", zIndex: DRAFT_EXPANDED_Z + 1 } : undefined}>
      {editing?.clause && (
        <ClausePopover
          // Keyed on the clause, so opening a second one starts from its own
          // wording rather than keeping the first one's draft.
          key={editing.clause}
          clause={editing.clause}
          text={clauseOverrides?.[editing.clause] || ""}
          printed={editing.printed}
          family={clauseTree.find(c => c.id === editing.clause)}
          removed={(clausesRemoved || []).includes(editing.clause)}
          at={editing}
          saving={savingClause === editing.clause}
          onSave={onSaveClause}
          onRemove={onRemoveClause}
          onClose={() => setEditing(null)}
        />
      )}

      {editing && !editing.clause && (() => {
        const field = (registry || []).find(f => f.name === editing.name);
        if (!field) return null;
        return (
          <FieldPopover
            field={field}
            also={(editing.also || [])
              .map(n => (registry || []).find(f => f.name === n))
              .filter(Boolean)}
            contractFields={contractFields}
            helpers={contractFields?.helpers?.[field.name]}
            value={contractFields?.values?.[field.name]}
            source={contractFields?.provenance?.[field.name]}
            fallback={contractFields?.fallback?.[field.name]}
            seeds={{ generatedAttachments }}
            at={editing}
            slot={editing.slot}
            saving={savingFields?.has(field.name)}
            agentDirectory={agentDirectory}
            onAdoptAgent={onAdoptAgent}
            adoptingAgent={adoptingAgent}
            onSaveToDirectory={onSaveToDirectory}
            onSave={onSave}
            onSaveFields={onSaveFields}
            onRevert={onRevert}
            onClose={() => setEditing(null)}
          />
        );
      })()}
      </div>

      {open && (
        <div style={fill ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : { marginTop: embedded ? 0 : 12 }}>
          {state.error ? (
            <div style={{ background: "rgba(196,122,26,0.06)", border: "1px solid rgba(196,122,26,0.25)", borderRadius: 8, padding: "10px 12px", margin: embedded ? 14 : 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#c47a1a", marginBottom: 3 }}>
                Nothing to preview
              </div>
              <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5 }}>
                {state.error}.
                {/^no template/i.test(state.error) && " The contract template hasn’t been stored yet — it goes up from the middleware repo, under Actions → Upload contract template."}
              </div>
            </div>
          ) : state.loading ? (
            <div style={{ height: embedded ? "70vh" : 760, background: T.bgAlt, ...(embedded ? EDGE_FRAME : { border: `1px solid ${T.border}`, borderRadius: 8 }) }} />
          ) : (
            <div data-draft-layer style={expanded ? draftExpandedStyle : fill ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : undefined} onClick={expanded ? () => setExpanded(false) : undefined}>
              {/* **One row, and one press for the parts.** `SkipTo` says why
                  they are a bar that drops on hover rather than words laid
                  along this row or a select. Expanded, the row is the
                  layer's own header: the document card's slot is behind it. */}
              <InDocToolbar bypass={expanded}>
              <div onClick={expanded ? (e) => e.stopPropagation() : undefined} style={expanded
                ? { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", minWidth: 0, background: T.white, borderRadius: "12px 12px 0 0", border: `1px solid ${T.border}`, borderBottom: "none", padding: "10px 14px", position: "relative" }
                : { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: embedded ? 0 : 10, alignItems: "center", flex: embedded && !fill ? 1 : undefined, minWidth: 0, ...(fill ? { padding: "8px 12px", position: "relative" } : {}) }}>
                {expanded && <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Draft — edit contract and section 32</span>}
                <SkipTo
                  marks={marks}
                  onJump={(m) => {
                    const win = frameRef.current?.contentWindow;
                    if (!win) return;
                    win.scrollTo({ top: frameScrollTop(m.el, win), behavior: "smooth" });
                  }}
                />
                <span title="Changes reach the PDF on re-compile" style={{ fontSize: 12, color: T.textFaint }}>Click any value to edit</span>
                <span style={{ flex: 1 }} />
                <PagesSwitch pages={pages} fits={pages === 1 || twoUp} onChange={setPages} />
                {expanded ? (
                  <Btn variant="ghost" size="sm" onClick={() => setExpanded(false)}>Close</Btn>
                ) : (
                  <Btn variant="ghost" size="sm" onClick={() => setExpanded(true)}>
                    <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                    Expand
                  </Btn>
                )}
              </div>
              </InDocToolbar>
              {/* The same window the compiled state gets, so switching between
                  them does not resize the page under the reader. */}
              <div ref={frameBoxRef} onClick={expanded ? (e) => e.stopPropagation() : undefined} style={expanded
                ? { overflow: "hidden", background: T.bg, flex: 1, minHeight: 0, position: "relative", border: `1px solid ${T.border}`, borderRadius: "0 0 12px 12px" }
                : fill
                ? { borderTop: `1px solid ${T.border}`, overflow: "hidden", background: T.bg, flex: 1, minHeight: 0, position: "relative" }
                : { ...(embedded ? EDGE_FRAME : { borderRadius: 8, border: `1px solid ${T.border}` }), overflow: "hidden", background: T.bg, height: embedded ? (frameHeight ?? contractFrameHeight(peek)) : 760, minHeight: embedded ? contractFrameMin(peek) : undefined, position: "relative" }}>
                <iframe
                  ref={frameRef}
                  onLoad={wireUp}
                  src={state.url}
                  style={{ width: "100%", height: "100%", border: "none", display: "block", background: "#fff" }}
                  title="Edit contract and section 32"
                />
                {busy && (
                  <div style={{
                    position: "absolute", inset: 0, background: "rgba(250,249,247,0.72)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    backdropFilter: "blur(1px)", transition: "opacity 0.15s",
                  }}>
                    <Spinner />
                    <span style={{ fontSize: 12, color: T.textSoft }}>Redrawing the contract…</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Automatic compiling ────────────────────────────────────────────
// One switch, service-wide: does a matter compile by itself the moment its
// searches are all accounted for, or does it wait to be told.
//
// **Off does not mean stuck.** The matter still reaches "searches complete"
// and still offers Compile — only the automatic start is withheld. The copy
// says so, because a switch that looked like it might strand a matter is one
// nobody would dare turn off.
//
// Saved on the flip rather than behind a Save button: it is a single boolean,
// and a dirty-state form around one switch is friction with nothing to show
// for it. A failed save puts the switch back where it was, so the screen never
// claims a setting the service does not have.
// ── The matter page, redrawn ───────────────────────────────────────
//
// Opt-in through `?ui=new`, and **nothing about the old one changes**: both
// render from the same state in `App` — the same poll, the same handlers, the
// same middleware calls — and both mount the same panels with the same props.
// What differs is the arrangement.
//
// Four things it does differently:
//   1. One sticky bar carries the matter's identity, its status and the one
//      action this stage calls for. Marking the last search Not Required
//      happens at the bottom of the page, several screens from the buttons.
//   2. One next-step card instead of six banners — see `nextStep`.
//   3. A rail holds the facts and the outstanding counts, so "is anything
//      wrong with this matter" is answerable without opening a panel. Today
//      that answer is spread over a badge, three cards, a progress string in
//      one heading and counts inside two others.
//   4. Three panes instead of eight stacked panels.
//
// **The panes are hidden, never unmounted**, which is the whole reason this
// can be a layout change rather than a rewrite. `ContractPreview` fetches a
// third of a megabyte, builds a blob URL, paginates it and wires click-to-edit
// into the frame's own DOM; unmounting revokes the blob and throws all of that
// away, so a tab that unmounted it would re-fetch and drop the reader back to
// page one of thirty-five every time they looked at the searches. Mounting all
// three from the start also means this makes exactly the same requests as the
// old layout, in the same order — the panels keep their own open/closed
// defaults from `sectionDefaults`, so nothing new is fetched and nothing that
// used to be fetched isn't.
// **The matter's status card belongs to the work it describes.** It names
// the document's state and what is missing from it — fields still needed and
// searches not back — and both of those are answered on Contract or Searches.
// On Email, Activity, signing and the rest it was the same card above work it
// says nothing about, so it stays with those two panes. The compile's own
// progress banner and the last action's outcome are not this card, and still
// show on every pane.
export const statusCardShown = (pane) => pane === "contract" || pane === "documents";

export const paneFor = (status) => {
  switch (status) {
    // The searches are in and the document exists or is about to: the work is
    // reading what the contract will print. Matches what `sectionDefaults`
    // opens for these stages.
    case "searches_complete":
    case "compiled":
    case "released":
      return "contract";
    // searches_pending, compile_failed, a stub, and anything a newer deploy
    // invents. The work is the documents — and a failed compile is more often
    // a missing document than a missing field.
    default:
      return "documents";
  }
};

// A pane that is put away rather than taken down. See the note above.
const Pane = ({ active, children }) => (
  <div style={{ display: active ? "block" : "none" }} aria-hidden={active ? undefined : true}>
    {children}
  </div>
);

// The actions that are not this stage's action. They were three buttons
// competing with Compile at the top and two underlined links at the very
// bottom of the page.
// `label`, `size` and `tour` let a row carry one too (DA-03): the searches list
// puts one on every row, and only the matter's own menu is the tour's mark.
const OverflowMenu = ({ items, label = "More actions", size = 34, tour = true }) => {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  // A listener rather than a covering overlay: an overlay eats the click that
  // should have opened the next thing. Same reasoning as the field popover.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const usable = items.filter(Boolean);
  if (usable.length === 0) return null;

  return (
    <div ref={box} {...(tour ? { "data-tour": "tour-menu" } : {})} style={{ position: "relative", flexShrink: 0 }}
      onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
      <button onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open} aria-label={label}
        style={{ width: size, height: size, borderRadius: 999, border: `1px solid ${size < 34 ? "transparent" : T.border}`, background: "transparent", cursor: "pointer", color: T.textSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="5" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="12" cy="19" r="1.6"/>
        </svg>
      </button>
      {open && (
        <div role="menu" style={{ position: "absolute", right: 0, top: size + 6, background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 5, minWidth: 230, boxShadow: "0 12px 32px rgba(51,51,51,0.16)", zIndex: 90 }}>
          {usable.map((item, i) => item === "divider" ? (
            <div key={i} style={{ height: 1, background: T.border, margin: "5px 8px" }} />
          ) : (
            <button key={i} role="menuitem" disabled={item.disabled}
              onClick={() => { setOpen(false); item.onClick(); }}
              style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", cursor: item.disabled ? "not-allowed" : "pointer", fontSize: 12.5, color: item.danger ? T.red : T.text, fontFamily: "Inter, sans-serif", padding: "8px 10px", borderRadius: 7, opacity: item.disabled ? 0.4 : 1 }}
              onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background = T.bgAlt; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RailPill = ({ tone, children }) => {
  const cfg = {
    red:   { color: T.red, bg: "rgba(254,84,66,0.08)" },
    green: { color: "#2a8a52", bg: "rgba(42,138,82,0.1)" },
    grey:  { color: T.textFaint, bg: "rgba(158,143,132,0.12)" },
  }[tone] || { color: T.textFaint, bg: "rgba(158,143,132,0.12)" };
  return <span style={{ fontSize: 11, fontWeight: 500, color: cfg.color, background: cfg.bg, padding: "1px 7px", borderRadius: 999, whiteSpace: "nowrap" }}>{children}</span>;
};

// Four tones, and which one a banner gets is a claim about the document.
//
//   `warn`  the document is not what it looks like — somebody could send it,
//           or already has, believing something that is not true. White with a
//           foot, so it differs from an ordinary panel at a glance.
//   `red`   the compile failed. There is no document.
//   `green` released, and nothing else. Same rule as the badges.
//   `ink`   everything else: work to do, work in progress, work finished but
//           not sent. A neutral raised panel.
//
// **There is no amber and no blue left here.** They were carrying five states
// between them that differ in what they ask of you, not in how alarming they
// are — so the colour said "be concerned" on all of them and distinguished
// none. The card's own title is what says which it is; there is only ever one.
// The outcome of the last action, in the same two tones the banners use.
// `amber` is what the toast layer still calls "part of this failed" — it
// reads as neutral here, because an outcome is not a warning about the
// document, and the only thing that gets the warning treatment is a document
// that is not what it looks like.
// **A named tone has to resolve to that tone.** This mapped two ways only —
// `amber` to ink, and *everything else* to green — so the planning lookup's
// "Looking up the planning map…", which asks for `ink`, printed green while it
// was still running. Green says finished and fine about work in flight, which
// is the same class of fault as a progress bar that vanishes early.
//
// A known name now wins; the old two-way mapping stays as the fallback, so
// `amber` is still ink and an unnamed tone is still green. Nothing else moves.
const actionNoticeStyle = (tone) => {
  const t = NOTICE_TONE[tone] || NOTICE_TONE[tone === "amber" ? "ink" : "green"];
  return { background: t.bg, border: `1px solid ${t.border}`, color: t.color };
};

const NOTICE_TONE = {
  ink:   { color: T.text, bg: T.bgAlt, border: T.borderMid },
  warn:  { color: T.text, bg: T.white, border: "#ddd", foot: "2px solid #bbb" },
  red:   { color: T.red, bg: "rgba(254,84,66,0.06)", border: "rgba(254,84,66,0.3)" },
  green: { color: OK, bg: "rgba(42,138,82,0.05)", border: "rgba(42,138,82,0.25)" },
  // Work in flight, and the same blue the search rows use for *being read…* —
  // so a lookup, a read and a compile all say "this is happening" the same way
  // wherever somebody meets one.
  blue:  { color: ACCENT, bg: "rgba(26,122,196,0.06)", border: "rgba(26,122,196,0.3)" },
};

// Where *Back* should land after Arrange documents, given how it was reached.
//
// **A click event is not a return ticket.** The overflow menu hands this
// straight to `onClick`, so what arrives is a MouseEvent — which has no
// `from`, and must not be read as one. Only a caller that asked by name gets
// a ticket; everything else returns where it always did.
// A badge on the matter header states a fact about the property, so it shows
// on a definite yes and on nothing else.
//
// **Rendered on truthiness, these read a string as a yes.** Form 27 offers
// "Not sure" on the material facts question, and it reached the matter as the
// string `"Not sure"` — truthy. On 1760502 a vendor who simply did not know
// raised the same red *Material facts* badge as one with a death in the
// property to disclose, which is the badge losing its meaning rather than
// merely being wrong once.
//
// The middleware no longer stores a non-boolean in a flag, and reads one as
// unanswered — but these two badges read the **raw matter field** rather than
// the resolved one, so neither fix reaches them. This is the third place, and
// the one staff were actually looking at.
//
// `has137b` is guarded the same way. Nothing sends a string into it today; it
// is the same kind of field beside the same kind of badge, and guarding one and
// not the other is how the next one of these gets missed.
export const saysYes = (value) => value === true || /^(yes|true)$/i.test(String(value ?? "").trim());

// A refused press that means "this is already happening", not "this failed".
//
// The middleware answers 409 `compile_running` when a compile is already going
// on the matter. Rendering that as an error would be worse than the race it
// replaced: it says nothing is happening when something is, which is exactly
// what invites the next press. `runningForMs` is how long it has been going, so
// the banner can start from the compile rather than from this tab noticing.
export const runningCompile = (err, now = Date.now()) =>
  (err?.code === "compile_running"
    ? { startedAt: now - (Number(err.body?.runningForMs) || 0) }
    : null);

// ── Assembling a matter out of the response ────────────────────────────────
//
// **Half of what this screen shows is a sibling of `matter`, not a field on
// it.** The middleware computes `contractFields`, `documentReview`,
// `signatories` and the rest at read time and sends them beside the matter
// rather than inside it — so anything not named here is simply never on
// screen, with no error anywhere to say why. That has now happened six times:
// the searches review rendered nothing, the certificate hints were missing,
// the banked returns silently held a compile, the attachment editor opened
// empty, the agent suggestions never appeared, and the signing panel said
// "nobody to ask" on every matter.
//
// **It happened a seventh time, so the list stopped being the mechanism.**
// `lotSearch` and `vicmap` were both sent for weeks and folded by nothing, and
// the guard could not see it: it walked `SIBLING_KEYS` against this function,
// which only ever proved the list agreed with itself. The list was still
// hand-kept against a middleware this repository cannot read — the same shape
// as a hand-kept list of extraction keys, failing the same silent way.
//
// So **everything beside the matter is folded**, and a key added there arrives
// here on its own. `NOT_SIBLINGS` is the three that are not siblings: the
// envelope's own `ok`, the matter itself, and `searches`, which is assembled
// with its own default below.
//
// The hazard this takes on, named rather than discovered: a sibling sharing a
// name with a real matter field would now win over it. None does today, and
// the named defaults below re-assert the nine the screen actually depends on.
//
// `SIBLING_KEYS` survives as what the screen *expects*, and the walk still
// asserts each one arrives — but it is documentation now, and the folding no
// longer depends on it being complete.
export const SIBLING_KEYS = [
  "contractFields", "documentReview", "awaitingSearchList", "compileOutstanding",
  "generatedAttachments", "unmatchedReturns", "agentDirectory",
  "signatories", "signatures", "lotSearch", "vicmap",
];

const NOT_SIBLINGS = new Set(["ok", "matter", "searches"]);

// **A read of the matter replaces it; it does not paint over it.**
//
// Both reads spread the answer over what was on screen, and a field the
// middleware *deletes* is absent from its answer rather than null — so the
// spread kept the old value. `needsRecompileReason` is the one that showed:
// the dashboard's list row carried the flag, a compile in the background
// cleared it, and opening 1760698 afterwards painted the fresh read over that
// row and kept *"Certificates have arrived since this version was built"*
// above a History panel reading *Draft contract prepared (v4)* — until a
// reload fetched the list again. Every field the compile clears
// (`compileError`, `documentWarning`, `contractRenderedBy`…) had the same hole.
//
// The detail read carries the whole stored document, so it is a superset of
// the list row. What survives a read is only what the server never sends on
// it: the browser's own `lastActionNotice`, and the list's `trackedReason` and
// `id`.
const KEPT_ACROSS_READS = ["lastActionNotice", "trackedReason", "id"];
export const withServerMatter = (prev, next) => {
  const kept = {};
  for (const key of KEPT_ACROSS_READS) {
    if (prev && key in prev && !(key in next)) kept[key] = prev[key];
  }
  return { ...next, ...kept };
};

export const matterFromResponse = (data = {}) => {
  const matter = data.matter || {};
  const siblings = {};
  for (const key of Object.keys(data)) {
    if (!NOT_SIBLINGS.has(key)) siblings[key] = data[key];
  }
  return {
    ...matter,
    ...siblings,
    ref: matter.matterRef ?? matter.id,
    searches: data.searches || [],
    contractFields: data.contractFields ?? null,
    documentReview: data.documentReview ?? null,
    awaitingSearchList: data.awaitingSearchList || null,
    compileOutstanding: data.compileOutstanding || null,
    generatedAttachments: data.generatedAttachments || [],
    unmatchedReturns: data.unmatchedReturns || [],
    agentDirectory: data.agentDirectory || null,
    signatories: data.signatories || null,
    signatures: data.signatures || null,
    lotSearch: data.lotSearch || null,
    vicmap: data.vicmap || null,
  };
};

// ── Who has to sign the statement, and asking them ─────────────────────────
//
// In Victoria the vendor must have signed the vendor statement **before** the
// purchaser is given it, so this sits between compiling and releasing — which
// is where the panel sits on the screen.
//
// Everything shown here is the middleware's: `signatories` is derived from the
// document's own signing blocks (so it cannot ask somebody to sign a line the
// contract does not have) and `signatures` is the state of the request. A
// second copy of either rule in the browser would drift.

const SIGN_STATE = {
  signed: { label: "Signed", tone: "good" },
  open: { label: "Waiting", tone: "soft" },
  expired: { label: "Link expired", tone: "warn" },
  cancelled: { label: "Withdrawn", tone: "faint" },
  none: { label: "Not asked", tone: "faint" },
};

const signStateColour = (tone) => (
  tone === "good" ? "#1F7A44" : tone === "warn" ? T.red : tone === "soft" ? T.textSoft : T.textFaint
);

const signedAtText = (raw) => {
  const ms = timeOf(raw);
  if (!ms) return "";
  return new Date(ms).toLocaleString("en-AU", {
    day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  });
};

// **When the link went and when it stops working** (DA-07). A Waiting row said
// nobody had signed and not since when — so a link that lapsed after its
// fortnight read the same as one sent this morning, and the expiry was found
// by the vendor. `sentAt` and `expiresAt` are the middleware's, per vendor,
// because one vendor can be re-sent alone; an older middleware sends neither
// and the row reads as it did.
export const LINK_EXPIRES_SOON_DAYS = 3;
const DAY_MS = 24 * 3600 * 1000;
const shortDate = (ms) => new Date(ms).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
const daysWord = (n) => `${n} ${n === 1 ? "day" : "days"}`;

export const linkTiming = (one, now = Date.now()) => {
  if (!one || (one.state !== "open" && one.state !== "expired")) return null;
  const until = timeOf(one.expiresAt);
  if (!until) return null;
  const sent = timeOf(one.sentAt);
  const sentDays = sent ? Math.floor((now - sent) / DAY_MS) : null;
  const sentText = sentDays === null ? ""
    : sentDays <= 0 ? "Sent today"
    : sentDays === 1 ? "Sent yesterday"
    : `Sent ${daysWord(sentDays)} ago`;
  if (one.state === "expired" || until <= now) {
    return { text: [sentText, `expired ${shortDate(until)}`].filter(Boolean).join(" · "), soon: false, expired: true };
  }
  const left = Math.ceil((until - now) / DAY_MS);
  const soon = left <= LINK_EXPIRES_SOON_DAYS;
  const tail = soon
    ? `link expires in ${daysWord(left)}`
    : `link works until ${shortDate(until)}`;
  return { text: [sentText, tail].filter(Boolean).join(" · "), soon, expired: false };
};

// **A link that is really a press.** Underlined text for an action that is
// worth offering and not worth a button — deliberately a `button` rather than
// an anchor, since an `<a>` with no `href` is unreachable from the keyboard and
// tells a screen reader it navigates somewhere.
const LinkAction = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: "none", border: 0, padding: 0, font: "inherit", color: T.textSoft,
      textDecoration: "underline", cursor: "pointer",
    }}
  >
    {children}
  </button>
);

// One row, whether it is being asked for or reported on.
//
// **`last` drops the closing rule, because the block after a list owns the
// separator.** The panel follows its rows with the executed statement or the
// action row, each of which draws its own `borderTop` — so the final row's
// `borderBottom` came out as a second line with the row gap between the two.
// The modals do not: what follows them is plain text, so there the trailing
// rule is the list closing itself and stays.
const SignatoryRow = ({ position, name, capacity, right, children, last = false }) => (
  <div style={{ padding: "9px 0", borderBottom: last ? "none" : `1px solid ${T.border}` }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <span style={{ fontSize: 12.5, color: T.text }}>
          {name || <span style={{ color: T.textFaint, fontStyle: "italic" }}>Not named — signatory {position}</span>}
        </span>
        {capacity && <span style={{ fontSize: 11, color: T.textFaint, marginLeft: 6 }}>{capacity}</span>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

// **The links exist once, so this does not close on success.** The record keeps
// only hashes: a link not handed on at that moment cannot be recovered, and
// asking again mints a fresh one. So a successful request switches the modal to
// showing them with a copy button each, and closing is a deliberate press.
export const SignatureRequestModal = ({
  matter, onDone, onCancel,
  // Defaulted rather than drilled, like the cross-check modal's handler list:
  // it is a plain staff POST behind the token every other write uses, and a
  // test hands its own in.
  onRequest = requestSignatures,
}) => {
  const known = matter.signatories?.signatories || [];
  const report = matter.signatures || {};
  // Asking again at the same version keeps what has already been signed, so
  // those rows are shown as settled rather than offered a fresh link.
  const signedAt = new Map(
    (report.version === matter.compileVersion ? report.signatories || [] : [])
      .filter(s => s.state === "signed").map(s => [s.position, s]),
  );

  const [emails, setEmails] = useState(() => Object.fromEntries(
    known.map(s => [s.position, s.email || ""]),
  ));
  const [send, setSend] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const [copyFailed, setCopyFailed] = useState(null);

  const toAsk = known.filter(s => !signedAt.has(s.position));
  // Sending needs somewhere to send to. Handing the links over does not, which
  // is what makes a matter with no addresses workable at all.
  const missingAddress = toAsk.filter(s => addressState(emails[s.position]) === "blank");
  const malformed = toAsk.filter(s => addressState(emails[s.position]) === "malformed");

  const ask = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const answer = await onRequest(matter.ref, {
        send,
        emails: addressPayload(toAsk.map(s => s.position), emails),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setResult(answer);
    } catch (err) {
      setProblem(err.message || "The request was not made");
    } finally {
      setBusy(false);
    }
  };

  // `navigator.clipboard` throws on an insecure origin and where the browser
  // refuses permission — and here the link is the one thing that cannot be
  // fetched again, so a Copy that silently did nothing would lose it.
  const copy = async (url) => {
    if (await copyText(url)) {
      setCopyFailed(null);
      setCopied(url);
      setTimeout(() => setCopied(c => (c === url ? null : c)), 1500);
      return;
    }
    setCopied(null);
    setCopyFailed(url);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 28, width: "100%", maxWidth: 520, maxHeight: "84vh", overflowY: "auto", boxShadow: "0 12px 48px rgba(0,0,0,0.14)" }}>
        <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: "0 0 5px", letterSpacing: "-0.02em" }}>
          {!result ? "Ask the vendors to sign" : result.sample ? "The sample was sent" : "The signing links"}
        </h2>

        {!result && (
          <>
            {/* The version, because the signature is pinned to it: a re-compile
                past this one makes what they signed stale. */}
            <p style={{ fontSize: 12, color: T.textSoft, margin: "0 0 16px", lineHeight: 1.5 }}>
              Each signatory gets their own link to
              {matter.compileVersion ? ` v${matter.compileVersion}` : " the compiled statement"},
              good for a fortnight. A re-compile after this makes what they signed stale.
            </p>

            {known.map(one => {
              const done = signedAt.get(one.position);
              return (
                <SignatoryRow
                  key={one.position}
                  position={one.position}
                  name={one.name}
                  capacity={one.capacity}
                  right={done && (
                    <span style={{ fontSize: 11, color: signStateColour("good"), whiteSpace: "nowrap" }}>
                      Already signed
                    </span>
                  )}
                >
                  {!done && (
                    <input
                      value={emails[one.position] || ""}
                      onChange={(e) => setEmails(m => ({ ...m, [one.position]: e.target.value }))}
                      disabled={busy}
                      placeholder="No address on the matter"
                      autoComplete="off"
                      style={{ ...inputStyle, marginTop: 6, fontSize: 12 }}
                    />
                  )}
                  {/* Only while the box still holds the address we have: once
                      somebody types over it the sentence is about a value that
                      is no longer there, which is worse than saying nothing. */}
                  {!done && one.emailSaid && emails[one.position] === one.email && (
                    <div style={{ fontSize: 11, color: T.textFaint, marginTop: 4 }}>
                      {one.emailSaid}
                    </div>
                  )}
                </SignatoryRow>
              );
            })}

            <div style={{ marginTop: 16 }}>
              <label style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, color: T.text, cursor: "pointer" }}>
                <input type="checkbox" checked={send} disabled={busy}
                  onChange={(e) => setSend(e.target.checked)} />
                <span>
                  Email each of them their link
                  {/* Named rather than hidden: a matter with no addresses is
                      workable by handing the links over, and somebody has to be
                      told which of the two is about to happen. */}
                  {send && missingAddress.length > 0 && (
                    <span style={{ color: T.red, fontSize: 11 }}>
                      {" "}— {missingAddress.length} {missingAddress.length === 1 ? "has" : "have"} no address
                      and will not be sent
                    </span>
                  )}
                </span>
              </label>
            </div>

            {send && (
              <div style={{ marginTop: 14 }}>
                <Label>Anything to add <span style={{ color: T.textFaint, fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>optional</span></Label>
                {/* It adds under its own heading and never replaces the standard
                    wording — what has to happen, and that nothing can be
                    exchanged until they have signed, is the firm's and is
                    composed rather than typed. */}
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} disabled={busy}
                  style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit", fontSize: 12 }} />
              </div>
            )}

            {malformed.length > 0 && (
              <p style={{ fontSize: 12, color: T.red, margin: "12px 0 0", lineHeight: 1.5 }}>
                {malformedSentence(malformed)}
              </p>
            )}

            {problem && (
              <p style={{ fontSize: 12, color: T.red, margin: "12px 0 0", lineHeight: 1.5 }}>{problem}</p>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
              <Btn variant="ghost" size="md" onClick={onCancel} disabled={busy}>Cancel</Btn>
              <Btn variant="primary" size="md" onClick={ask} disabled={busy || toAsk.length === 0 || malformed.length > 0}>
                {busy ? "Asking…" : send ? "Send the links" : "Create the links"}
              </Btn>
            </div>
          </>
        )}

        {/* **A rehearsal mints nothing, so there is nothing to copy.** The
            sample matter's press sends the letter and writes no token, and
            this modal read "copy anything you need now" over an empty list —
            which on a real client's file reads as links that failed to
            arrive. The middleware's own sentence says what happened, and the
            fallback here only matters against an older one — so it says the
            same thing rather than a second version of it. */}
        {result && result.sample && (
          <>
            <p style={{ fontSize: 12, color: T.textSoft, margin: "0 0 14px", lineHeight: 1.5 }}>
              {result.message
                || `Sample sent to ${result.sentTo}. Real email, real link — nothing went to the vendors, and nothing on the matter changed.`}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <Btn variant="primary" size="md" onClick={onDone}>Done</Btn>
            </div>
          </>
        )}

        {result && !result.sample && (
          <>
            <p style={{ fontSize: 12, color: T.textSoft, margin: "0 0 14px", lineHeight: 1.5 }}>
              <strong style={{ color: T.text }}>Copy anything you need now.</strong> These are
              stored hashed, so a link not handed on cannot be shown again — asking
              again mints fresh ones for whoever has not signed.
            </p>

            {(result.links || []).map(one => (
              <SignatoryRow
                key={one.position}
                position={one.position}
                name={one.name}
                capacity={one.capacity}
                right={
                  <span style={{ fontSize: 11, whiteSpace: "nowrap",
                                 color: one.sent?.ok ? signStateColour("good") : T.textFaint }}>
                    {one.sent?.ok ? `Emailed ${one.sent.to}` : one.sent ? sendFailureText(one.sent.reason) : ""}
                  </span>
                }
              >
                <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center" }}>
                  <input
                    readOnly
                    value={one.url}
                    onFocus={(e) => e.target.select()}
                    style={{ ...inputStyle, fontSize: 11, color: T.textSoft }}
                  />
                  <Btn variant="ghost" size="sm" onClick={() => copy(one.url)}>
                    {copied === one.url ? "Copied" : "Copy"}
                  </Btn>
                </div>
                {copyFailed === one.url && (
                  <div style={{ fontSize: 11, color: T.red, marginTop: 4 }}>
                    Could not reach the clipboard — select the link and copy it by hand.
                  </div>
                )}
              </SignatoryRow>
            ))}

            {(result.notEmailed || []).length > 0 && (
              <p style={{ fontSize: 11.5, color: T.textSoft, margin: "12px 0 0", lineHeight: 1.5 }}>
                Not emailed: {result.notEmailed.map(n => `${n.name || `signatory ${n.position}`} (${sendFailureText(n.reason)})`).join(", ")}.
                Their links are above and still work.
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <Btn variant="primary" size="md" onClick={onDone}>Done</Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// The middleware's own reasons, said in words. Named rather than passed
// through, because "no_email" in front of somebody is not a sentence.
const sendFailureText = (reason) => ({
  no_email: "no address",
  recipient_not_allowed: "address not allowed while sending is restricted",
  send_failed: "the send failed",
  no_provider: "email is not configured",
  no_sender: "email is not configured",
}[reason] || (reason ? String(reason).replace(/_/g, " ") : "not emailed"));

// ── Which lot on the plan is theirs ────────────────────────────────────────
//
// The press began as the agent's alone, and the firm moved it here. **A model
// pointing at a drawing is a suggestion, and a suggestion an external party
// acts on unread is the shape of mistake nothing downstream catches** — so the
// office runs it, looks at the plan, and approves what is right. An approved
// record is served to the agent as a record, with no model asked at all.
//
// **The agent's press is not withdrawn.** The alternative to an unchecked
// answer is not a better one, it is a phone call to the office. A matter nobody
// has looked at still answers.
//
// **Offered by the middleware, not decided here.** `lotSearch.offered` is
// `canFindLot` — an owners corporation document in the pack, a plan to look at,
// exactly one lot — and a copy of that rule in the browser would drift from the
// route that refuses.
const findLotForStaff = async (ref, again = false) => {
  const res = await apiFetch(`/lot-location/${ref}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(again ? { again: true } : {}),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) throw new Error(body.reason || "The search did not run");
  return body;
};

const judgeLot = async (ref, approved, pages) => {
  const res = await apiFetch(`/lot-location/${ref}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ approved, ...(pages ? { pages } : {}) }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) throw new Error(body.error || "That was not saved");
  return body;
};

// Why the plan search is not offered, in the words somebody can act on. The
// middleware decides which one; a copy of the *rule* here would drift from the
// route that refuses, so only the wording lives in the browser.
const LOT_NOT_OFFERED = {
  no_owners_corporation_document:
    "no owners corporation certificate or basic report is in the pack, so nothing "
    + "says this is a lot in a subdivision.",
  no_plan: "no copy of plan is in the pack to look at.",
  no_lot: "the title has not been read, so there is no lot to look for.",
  not_compiled: "nothing has been compiled yet, so there is no plan to look in.",
};

// Why the search could not be *made*, as opposed to what it found. The route
// answers 409 with a code, and `findLotForStaff` throws it — so a refusal read
// as a bare `no_document` in red, which names our own vocabulary at somebody
// rather than what to do about it. Anything unnamed keeps its own message.
const LOT_SEARCH_PROBLEM = {
  no_document: "There is no compiled document to look in. Compile the contract "
    + "and try again.",
  not_offered: "The search is no longer offered on this matter — the pack or "
    + "the title has changed since this was drawn. Reload the matter.",
};

// Why the search found nothing, which is three different things needing three
// different answers — and the panel said one sentence for all of them.
//
// **The one worth separating is `no_plan_pages`**: the copy of plan could not
// be located in the compiled pack at all, so nothing was read and no model was
// asked. Reported as *"nothing could be matched"* that reads as a fact about
// the drawing, when the drawing was never opened. A pack carries a page map
// only where this service merged it itself, and the printed-heading fallback
// needs a text layer — which a scanned plan, the exact plan this feature is
// for, does not have. Re-compiling records the map, so that is the thing to
// say.
//
// A reason nobody has named here keeps the general sentence rather than
// printing a code at somebody: the wording is this side's, the rule is not.
export const lotMissWording = (why, lotWords) => {
  if (why === "no_plan_pages") {
    return "The copy of plan could not be located in the compiled pack, so "
      + "nothing was read — this is not a finding about " + lotWords + ". "
      + "Re-compiling records where each document landed, which is what the "
      + "search needs.";
  }
  if (why && why !== "not_on_plan") {
    return "The plan could not be read this time, so nothing has been ruled "
      + "out about " + lotWords + ". The agent is not told it was not found. "
      + "Look again.";
  }
  return "Nothing on the plan could be matched to " + lotWords + ". The agent "
    + "is told the same rather than being sent to a sheet.";
};

export const LotOnPlanPanel = ({
  matter, onChanged,
  onFind = findLotForStaff, onJudge = judgeLot,
  // **Showing the lot is the frame's business, not this panel's.** It is a
  // mode the compiled contract panel holds, because that is what owns the
  // frame and the main button — this reports it and asks for it.
  showing = false, onShowLot = null,
}) => {
  // Which of the two writes is running, or null. See `looking` below.
  const [act, setAct] = useState(null);
  const [problem, setProblem] = useState(null);
  // **What this press answered, kept, because the matter may not have kept
  // it.** A miss the middleware judges transient — the model answered 503, the
  // plan was only partly read, a cap of ours refused it — is deliberately *not
  // stored*, so that the next press tries again rather than the agent being
  // told the lot is not on a plan nobody managed to read. The consequence on
  // this screen is that `onChanged()` refetches a matter that has not moved:
  // the panel renders exactly as it did, and the press reads as a button that
  // does nothing. That is what the firm reported on 1760462 — a spinner for
  // three seconds and then nothing.
  //
  // The route says why every time. It was simply being thrown away.
  const [answer, setAnswer] = useState(null);
  // A new compiled version is a different question about a different document,
  // so an older press's answer is not about the pack in front of us.
  useEffect(() => { setAnswer(null); }, [matter.compileVersion]);
  const search = matter.lotSearch || {};
  const record = search.record || null;
  // Which sheets staff are keeping. Seeded from what was found, so approving
  // without touching anything keeps all of them — the ordinary case.
  const [keep, setKeep] = useState(null);
  // **Sheets, not marks.** A lot's car space and its storage cage are drawn on
  // the same sheet as often as not, and a matter of several titles has several
  // parcels on one — so the same page arrives more than once, and a list that
  // repeated it would offer staff the same checkbox twice with the two copies
  // able to disagree. The verdict is per page, which is what the route keeps.
  const pages = [...new Set((record?.places || []).map(one => one.page))]
    .sort((a, b) => a - b);
  const kept = keep === null ? pages : keep;
  // **Every lot the title carries.** A matter is as many parcels as it has
  // titles and all of them are the land being sold, so naming only the first
  // would leave the rest silently unaccounted for on the one screen where
  // somebody checks them against the plan.
  const lots = search.lots || (search.lot ? [search.lot] : []);
  const lotWords = lots.length === 0 ? "the lot"
    : lots.length === 1 ? `lot ${lots[0]}`
    : `lots ${lots.slice(0, -1).join(", ")} and ${lots[lots.length - 1]}`;
  // Which lots are drawn on a given sheet, so a row says what is on it.
  const lotsOnPage = (page) => [...new Set((record?.places || [])
    .filter(one => one.page === page).map(one => one.lot).filter(Boolean))];

  // How it was read, where that is worth saying. **The model's own line is
  // deliberately absent** — the mark now carries its own disclaimer on the page
  // an agent reads, so repeating *worth checking against the plan* here said
  // the same thing to the one reader who is already being asked to check it.
  // Blank rather than empty, so the whole row is withheld: a faint empty line
  // under the sheets reads as a sentence that failed to render.
  const howRead = record?.by === "ocr"
    ? "Read off the scanned drawing — an exact box, and worth a glance at the sheet."
    : record?.by === "text" ? "Read from the plan's own text layer."
    : "";

  // **A panel that draws nothing reads as one that is broken.** It used to
  // return null wherever the search was not offered, which is right on a house
  // sale and wrong on the apartment matter with two section 151 certificates in
  // the pack: staff had no way to tell which of the four conditions was
  // missing, and the answer existed only in a predicate nothing reported. The
  // middleware names it, and this says it in one line.
  //
  // **Only on a compiled matter**, so the ordinary house sale still shows
  // nothing at all: before a compile there is no plan to look in, which is
  // waiting rather than a fault.
  if (!search.offered) {
    if (!matter.compileVersion) return null;
    // The pack listing that used to sit under this is gone — it was a
    // debugging aid, it found the naming gap it was built for, and on the
    // ordinary house sale it was eleven rows under a one-line answer. See
    // `considered` in the middleware's own matter read.
    return (
      <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 12, paddingTop: 12, fontSize: 12.5, color: T.textSoft, lineHeight: 1.55 }}>
        <b style={{ color: T.text, fontWeight: 600 }}>Lot on the plan</b>
        {" — "}{LOT_NOT_OFFERED[search.why] || "not available on this matter."}
      </div>
    );
  }

  // **Looking is not the same busy as deciding.** One `busy` covered both, so
  // pressing Confirm put *"Looking…"* on the search button beside it — a
  // sentence about work nobody had started, and the more visible for wearing
  // the chase. `looking` is what the search sets; `busy` is still what stands
  // every control down, since either one is a write the rest must not race.
  const looking = act === "look";
  const busy = act !== null;

  const run = async (again) => {
    setAct("look"); setProblem(null);
    try {
      const said = await onFind(matter.ref, again);
      // Only a miss is kept. A press that found something is answered by the
      // record itself, and a stale sentence beside it would contradict it.
      setAnswer(said && !said.found ? said : null);
      setKeep(null);
      onChanged();
    } catch (err) { setProblem(LOT_SEARCH_PROBLEM[err.message] || err.message); }
    finally { setAct(null); }
  };

  const judge = async (approved) => {
    setAct("judge"); setProblem(null);
    try {
      // The whole list needs no `pages` at all — sending it would store the
      // same answer under a shape that reads as a correction.
      await onJudge(matter.ref, approved,
        approved && kept.length !== pages.length ? kept : null);
      onChanged();
    } catch (err) { setProblem(err.message); }
    finally { setAct(null); }
  };

  // The last press's own answer outranks the record, because it is the more
  // recent — a re-run that could not be completed leaves the old found record
  // standing, and nothing else would say the re-run failed.
  const missWhy = answer && !answer.found ? (answer.why || "not_on_plan")
    : (record && !record.found ? (record.why || "not_on_plan") : null);

  const state = !record ? "Not looked for yet"
    : !record.found ? "Not found on the plan"
    : record.approved ? `Confirmed on ${pages.length === 1 ? "1 sheet" : `${pages.length} sheets`}`
    : record.rejected ? "Marked not right"
    : `Found on ${pages.length === 1 ? "1 sheet" : `${pages.length} sheets`} — check it`;

  // **No fold of its own.** The section is revealed by *Find lot on plan* in
  // the panel above, so a chevron here would be a second thing to open inside
  // the thing that opened it — and the whole point of the button is that every
  // tool arrives together.
  return (
    <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 12, paddingTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
          {lotWords.charAt(0).toUpperCase() + lotWords.slice(1)} on the plan
        </span>
        <span style={{ fontSize: 11.5, color: record?.approved ? "#2a8a52" : T.textFaint }}>{state}</span>
      </div>

      {true && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 12.5, color: T.textSoft, lineHeight: 1.55, maxWidth: "70ch" }}>
            Find lot on plan for difficult to locate plans. The location markers
            will be shown to agents on their contract download page.{" "}
            <strong style={{ fontWeight: 600, color: T.text }}>Beta:</strong>{" "}
            plans vary in quality, so a marker is not always accurate, and the
            agent's page says so too.
          </div>

          {problem && (
            <div style={{ fontSize: 12, color: T.red }}>{problem}</div>
          )}

          {record?.found && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {pages.map((page) => {
                const on = lotsOnPage(page);
                return (
                  <label key={page} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, color: T.text, cursor: record.approved ? "default" : "pointer" }}>
                    <input type="checkbox" disabled={busy || record.approved}
                      checked={kept.includes(page)}
                      onChange={() => setKeep(kept.includes(page)
                        ? kept.filter(n => n !== page)
                        : [...kept, page].sort((a, b) => a - b))} />
                    <span>
                      Page {page} of the compiled contract
                      {on.length && lots.length > 1
                        ? ` — lot${on.length > 1 ? "s" : ""} ${on.join(", ")}` : ""}
                    </span>
                  </label>
                );
              })}
              {(howRead || record.askedBy === "agent") && (
                <div style={{ fontSize: 11.5, color: T.textFaint }}>
                  {howRead}
                  {record.askedBy === "agent" && `${howRead ? " " : ""}The agent ran this.`}
                </div>
              )}
            </div>
          )}

          {/* **This press first, the record second.** They say the same thing
              on a durable miss, since that one is stored — and on a transient
              one there is no record at all, which is precisely the case that
              was silent. One line rather than two blocks, or the more recent
              answer could sit under the older one. */}
          {missWhy && (
            <div style={{ fontSize: 12.5, color: T.textSoft }}>
              {lotMissWording(missWhy, lotWords)}
            </div>
          )}

          {/* **Every tool for the lot, in the section the button reveals.**
              They were spread over the panel's header, the strip's own header
              row and this list, so pressing one meant knowing which of three
              places it was in. Finding, looking and judging are one job. */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!record && (
              <Btn size="sm" onClick={() => run(false)} busy={looking} disabled={busy}>
                {looking ? "Looking…" : "Find it on the plan"}
              </Btn>
            )}
            {/* **Before Confirm, because looking is what you do first.** A
                verdict on a mark nobody has seen is the thing this closes. It
                marks the frame the panel above owns rather than drawing a
                second one, and steps through the places on each press. */}
            {/* **Stood down while a search runs**, which it was not. The
                places it travels to belong to the record that press is about
                to replace, so a halo reached that way is an answer on its way
                out — and every other button in the row greys, so the one that
                stayed live read as the thing to press while waiting. */}
            {record?.found && onShowLot && (
              <Btn variant={showing ? "ghost" : "warm"} size="sm" onClick={onShowLot} disabled={busy}>
                {showing && pages.length > 1 ? "Next place"
                  : showing ? "Show me again" : "Show the lot"}
              </Btn>
            )}
            {record?.found && !record.approved && (
              <Btn size="sm" onClick={() => judge(true)} disabled={busy || kept.length === 0}>
                {kept.length && kept.length !== pages.length
                  ? `Confirm ${kept.length} of ${pages.length}` : "Confirm"}
              </Btn>
            )}
            {record?.found && !record.rejected && (
              <Btn variant="ghost" size="sm" onClick={() => judge(false)} disabled={busy}>
                Not right
              </Btn>
            )}
            {record && (
              <Btn variant="ghost" size="sm" onClick={() => run(true)} busy={looking} disabled={busy}>
                {looking ? "Looking…" : "Look again"}
              </Btn>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Purchasers: who has the contract, and sending it for the agent ──────────
//
// The agent shares a released contract from their own download page — sends it
// to purchasers by email, or pastes the property's share link. This is the
// office's view of the same list: who has it, how they got it, whether they
// opened and downloaded it, and a way to switch somebody off. Staff can also
// send it **on the agent's behalf**, which the middleware records as exactly
// that, so the agent's list and the timeline both say who pressed it.
//
// **Everything shown is the middleware's.** The list, how each person came to
// have it, and the share link itself are read from `GET /matters/:ref/purchasers`
// — the share link is derived there from a key this bundle never sees, so a
// copy worked out here could not even be made.
//
// **Only on a released matter.** A purchaser is given what the agent was given,
// and before a release there is nothing to give them.
//
// **A middleware without the route hides the panel rather than erroring.**
// This bundle has to merge before the middleware deploys — the `/p/**` rewrite
// must exist before links are built on the domain — so for a while it meets a
// middleware that answers 404 here. A missing *route* is not tolerated by
// falling back on a field, so the panel is simply absent until it exists.
const loadPurchasers = async (matterRef) => {
  const r = await apiFetch(`/matters/${encodeURIComponent(matterRef)}/purchasers`);
  if (r.status === 404) return { ok: true, absent: true };
  const data = await r.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || `Could not load (${r.status})`);
  return data;
};

const sendToPurchasers = async (matterRef, purchasers) => {
  const r = await apiFetch(`/matters/${encodeURIComponent(matterRef)}/purchasers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ purchasers, ...(PORTAL_USER ? { viewer: PORTAL_USER } : {}) }),
  });
  const data = await r.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || `It was not sent (${r.status})`);
  return data;
};

const revokePurchaser = async (matterRef, id) => {
  const r = await apiFetch(`/matters/${encodeURIComponent(matterRef)}/purchasers/${encodeURIComponent(id)}/revoke`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(PORTAL_USER ? { viewer: PORTAL_USER } : {}),
  });
  const data = await r.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || `Access was not switched off (${r.status})`);
  return data;
};

const replaceShareLink = async (matterRef) => {
  const r = await apiFetch(`/matters/${encodeURIComponent(matterRef)}/share-link/regenerate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(PORTAL_USER ? { viewer: PORTAL_USER } : {}),
  });
  const data = await r.json().catch(() => null);
  if (!data?.ok) throw new Error(data?.error || `The link was not replaced (${r.status})`);
  return data;
};

// One line per purchaser of what they did. The times are the middleware's
// stamps, shown the way the timeline shows its own.
export const purchaserActivityLine = (one = {}) => {
  if (one.revoked) return "Access switched off";
  const bits = [one.firstOpenedAt
    ? `Opened ${formatEventTime(one.lastOpenedAt || one.firstOpenedAt)}${one.openCount > 1 ? ` (${one.openCount}×)` : ""}`
    : "Not opened yet"];
  if (one.firstDownloadedAt) {
    bits.push(`downloaded ${formatEventTime(one.lastDownloadedAt || one.firstDownloadedAt)}`
      + `${one.downloadCount > 1 ? ` (${one.downloadCount}×)` : ""}`);
  }
  return bits.join(" · ");
};

const BLANK_ROW = { name: "", email: "", mobile: "" };

export const PurchasersPanel = ({
  matter, stage, defaultOpen = false,
  onLoad = loadPurchasers, onSend = sendToPurchasers, onRevoke = revokePurchaser,
  onReplaceLink = replaceShareLink, onCopy = copyText,
}) => {
  const [open, setOpen] = useStagedSection(defaultOpen, stage);
  const [state, setState] = useState({ loading: true, error: null, purchasers: [], shareUrl: "", absent: false });
  const [rows, setRows] = useState([BLANK_ROW]);
  const [sending, setSending] = useState(false);
  const [said, setSaid] = useState(null);
  const [copied, setCopied] = useState(null);
  const [reload, setReload] = useState(0);
  const ref = matter.ref ?? matter.id;

  useEffect(() => {
    let cancelled = false;
    onLoad(ref)
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, purchasers: data.purchasers || [], shareUrl: data.shareUrl || "", absent: Boolean(data.absent) });
      })
      .catch((err) => { if (!cancelled) setState(s => ({ ...s, loading: false, error: err.message })); });
    return () => { cancelled = true; };
  }, [ref, reload, onLoad]);

  const { loading, error, purchasers, shareUrl, absent } = state;
  const filled = rows.filter(r => r.name.trim() || r.email.trim() || r.mobile.trim());
  const setRow = (i, key) => (e) => {
    const value = e.target.value;
    setRows(rs => rs.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  };

  const send = async () => {
    setSending(true);
    setSaid(null);
    try {
      const data = await onSend(ref, filled);
      const n = (data.sent || []).length;
      const missed = (data.notSent || []).map(x => x.email).filter(Boolean);
      setSaid({
        bad: missed.length > 0,
        text: missed.length
          ? `Sent to ${n}. Not sent to ${missed.join(", ")}.`
          : `Sent to ${n === 1 ? "the purchaser" : `${n} purchasers`} on the agent’s behalf.`,
      });
      setRows([BLANK_ROW]);
      setReload(k => k + 1);
    } catch (err) {
      setSaid({ bad: true, text: err.message });
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    // A Copy that silently did nothing would be pasted as whatever was on the
    // clipboard before, so a refusal is said.
    setCopied(await onCopy(shareUrl) ? "Copied" : "Select the link and copy it by hand");
  };

  const revoke = async (one) => {
    if (!window.confirm(`Switch off ${one.name}’s access to this contract?`)) return;
    try { await onRevoke(ref, one.id); setReload(k => k + 1); } catch (err) { setSaid({ bad: true, text: err.message }); }
  };

  const replace = async () => {
    if (!window.confirm("Replace the share link? The old link stops letting new people in. "
      + "Purchasers who already opened it keep their access.")) return;
    try {
      const data = await onReplaceLink(ref);
      setState(s => ({ ...s, shareUrl: data.shareUrl || s.shareUrl }));
      setCopied(null);
    } catch (err) { setSaid({ bad: true, text: err.message }); }
  };

  if (absent) return null;
  const inp = { width: "100%", height: 34, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "0 10px", fontSize: 12.5, color: T.text, fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box" };
  const summary = loading ? "…" : error ? "unavailable"
    : `${purchasers.length} ${purchasers.length === 1 ? "purchaser" : "purchasers"}`;

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Purchasers</span>
        </div>
        <span style={{ fontSize: 11, color: T.textFaint }}>{summary}</span>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginBottom: 14, maxWidth: 660 }}>
            The agent shares the contract from their download page. Everybody who has it is
            listed here with what they have opened and downloaded.
          </div>

          {error && <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 12 }}>Couldn&rsquo;t load the purchasers — {error}</div>}

          {shareUrl && (
            <div style={{ marginBottom: 16 }}>
              <Label>Share link</Label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input readOnly value={shareUrl} aria-label="Share link" onFocus={(e) => e.target.select()} style={{ ...inp, flex: 1 }} />
                <Btn variant="raised" onClick={copy}>Copy</Btn>
              </div>
              <div style={{ display: "flex", gap: 14, marginTop: 6, fontSize: 11.5, color: T.textFaint, flexWrap: "wrap" }}>
                {copied && <span>{copied}</span>}
                <LinkAction onClick={() => window.open(apiUrl(`/purchaser-preview/${ref}`), "_blank", "noopener")}>
                  Preview the purchaser page
                </LinkAction>
                <LinkAction onClick={replace}>Replace the link</LinkAction>
              </div>
            </div>
          )}

          {!loading && !error && (
            purchasers.length === 0
              ? <div style={{ fontSize: 12, color: T.textFaint, marginBottom: 14 }}>Nobody yet.</div>
              : <div style={{ marginBottom: 16 }}>
                {purchasers.map((one) => (
                  <div key={one.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", borderTop: `1px solid ${T.border}`, padding: "10px 0" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, color: T.text, fontWeight: 500 }}>
                        {one.name} <span style={{ fontWeight: 400, color: T.textFaint }}>· {one.email}{one.mobile ? ` · ${one.mobile}` : ""}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: T.textFaint, marginTop: 2 }}>{one.access}</div>
                      <div style={{ fontSize: 11.5, color: one.revoked ? T.red : T.textSoft, marginTop: 2 }}>{purchaserActivityLine(one)}</div>
                    </div>
                    {!one.revoked && <Btn variant="ghost" onClick={() => revoke(one)}>Remove access</Btn>}
                  </div>
                ))}
              </div>
          )}

          <Label>Send on the agent’s behalf</Label>
          {rows.map((r, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr 1fr", gap: 6, marginBottom: 6 }}>
              <input aria-label={`Purchaser ${i + 1} name`} placeholder="Name" value={r.name} onChange={setRow(i, "name")} style={inp} />
              <input aria-label={`Purchaser ${i + 1} email`} placeholder="Email" type="email" value={r.email} onChange={setRow(i, "email")} style={inp} />
              <input aria-label={`Purchaser ${i + 1} mobile`} placeholder="Mobile (optional)" value={r.mobile} onChange={setRow(i, "mobile")} style={inp} />
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
            {rows.length < 10 && <LinkAction onClick={() => setRows(rs => [...rs, BLANK_ROW])}>Add another</LinkAction>}
            <span style={{ flex: 1 }} />
            <Btn variant="primary" onClick={send} disabled={!filled.length} busy={sending}>
              {sending ? "Sending…" : "Send"}
            </Btn>
          </div>
          <div style={{ fontSize: 11.5, color: T.textFaint, marginTop: 6 }}>
            Each purchaser is emailed their own link in the agent’s name, and the timeline records it as sent on the agent’s behalf.
          </div>
          {said && (
            <div style={{ fontSize: 12, color: said.bad ? T.red : T.textSoft, marginTop: 8 }}>{said.text}</div>
          )}
        </div>
      )}
    </div>
  );
};

// The searches review raises the owner-builder flag; its button opens the
// request panel below it. An event rather than lifted state, because the two
// panels are siblings several components apart and neither owns the other.
const OPEN_137B_EVENT = "s32:open-137b";
const OWNER_BUILDER_FLAGS = ["ownerBuilderWorks", "ownerBuilderInsurance"];
const open137bPanel = () => window.dispatchEvent(new Event(OPEN_137B_EVENT));

// Where a request is up to, in the words the panel's header shows.
const REPORT_137B_STATE = {
  sent: { label: "Sent — waiting on the client", tone: "warn" },
  booking_requested: { label: "Inspection requested", tone: "ink" },
  waived: { label: "Declined — release signed", tone: "red" },
  not_required: { label: "Client’s answers say not required — check", tone: "warn" },
  cancelled: { label: "Withdrawn", tone: "ink" },
  expired: { label: "Link expired", tone: "warn" },
};

const ANSWER_137B = { yes: "Yes", no: "No", unsure: "Not sure", na: "Not applicable" };
const PERMIT_COPY_137B = { uploaded: "permit uploaded", ready: "permit copy ready for the inspector", none: "no copy of the permit" };

const DECISION_137B = {
  book: "Book the firm’s inspector",
  other: "Arranging their own inspector (sent the works summary)",
  waive: "No report — release signed",
  confirm: "No report needed, on their answers — to check",
};

// The calculator's verdict on the client's answers (middleware's words).
const VERDICT_137B = { required: "a report is required", maybe: "a report may be required", not_required: "no report is required" };

// Does this matter have anything to do with section 137B? Owner-builder works
// on the client form, the client saying so, or a request already made.
export const mightNeed137b = (matter = {}) => Boolean(
  matter.report137b
  || saysYes(matter.has137b)
  || (Array.isArray(matter.buildingWorkRows) && matter.buildingWorkRows.length)
  || saysYes(matter.worksNotByPractitioner),
);

export const Report137bPanel = ({
  matter, defaultOpen = false, stage,
  onLoad = report137bStatus, onRequest = requestReport137b, onWithdraw = cancelReport137b,
  onReset = resetReport137b, admin = IS_ADMIN,
}) => {
  const [open, setOpen] = useStagedSection(defaultOpen, stage);
  const [status, setStatus] = useState(null);
  const [problem, setProblem] = useState(null);
  const [classification, setClassification] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState(null);
  const [copied, setCopied] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    // **Scrolled once the pane is showing.** The event usually comes from
    // another pane (the practice look keeps the searches review with the
    // contract), and the matter page switches to this one on the same event —
    // so scrolling straight away would scroll a hidden box and do nothing.
    const show = () => {
      setOpen(true);
      setTimeout(() => box.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }), 60);
    };
    window.addEventListener(OPEN_137B_EVENT, show);
    return () => window.removeEventListener(OPEN_137B_EVENT, show);
  }, [setOpen]);

  const load = useCallback(async () => {
    try {
      const data = await onLoad(matter.ref);
      setStatus(data);
      setClassification(c => c || "auto");
      setTo(t => t || data.clientEmail || "");
      setProblem(null);
    } catch (err) {
      setProblem(err.message || "Could not load the section 137B request");
    }
  }, [matter.ref, onLoad]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const request = status?.request || null;
  // **The loaded status wins once it is in.** The matter's own copy is only
  // for the header before the panel has loaded: after a reset (or any change
  // made elsewhere) it can still carry the old request, and reading it then
  // hid the form behind an "answered" request that no longer exists.
  const state = status ? (request?.state || null) : (matter.report137b ? matter.report137b.state : null);
  const shown = REPORT_137B_STATE[state];
  const answered = state === "booking_requested" || state === "waived" || state === "not_required";
  const response = request?.response;

  const ask = async (send) => {
    setBusy(true);
    setProblem(null);
    try {
      const data = await onRequest(matter.ref, { classification, note, send, ...(send ? { to } : {}) });
      setMinted({ url: data.url, emailed: data.emailed, reason: data.reason });
      await load();
    } catch (err) {
      setProblem(err.message || "It was not sent");
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await onWithdraw(matter.ref);
      setMinted(null);
      await load();
    } catch (err) {
      setProblem(err.message || "It was not withdrawn");
    } finally {
      setBusy(false);
    }
  };

  // **No edit, only an admin's reset.** What a client signed is a record; this
  // deletes it, so the reference is typed to confirm and the timeline says so.
  const reset = async () => {
    const typed = window.prompt(`Reset the section 137B request on ${matter.ref}? This deletes the client's answers, their uploads and the signed PDF. Type ${matter.ref} to confirm.`);
    if (typed === null) return;
    setBusy(true);
    setProblem(null);
    try {
      await onReset(matter.ref, typed.trim());
      setMinted(null);
      setStatus(null);
      await load();
    } catch (err) {
      setProblem(err.message || "It was not reset");
    } finally {
      setBusy(false);
    }
  };

  const tone = shown ? NOTICE_TONE[shown.tone] : null;
  const fieldStyle = { width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 6, fontFamily: "Inter, sans-serif" };

  return (
    <div ref={box} data-report-137b style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Section 137B report</span>
          {status?.reportOnFile && (
            // Names the document the middleware matched, so a chip nobody
            // expected can be traced to the search list rather than the form.
            <span title={status.reportOnFileName || ""} data-report-on-file
              style={{ fontSize: 10.5, color: T.text, border: `1px solid ${T.text}`, borderRadius: 999, padding: "1px 7px", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {status.reportOnFileName ? `Report on file: ${status.reportOnFileName}` : "Report on file"}
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: tone ? tone.color : T.textFaint }}>
          {shown ? shown.label : mightNeed137b(matter) ? "Not requested" : "Not requested · no works on the client form"}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 14, fontSize: 12.5, color: T.text }}>
          <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginBottom: 12, maxWidth: 660 }}>
            Emails the client a page that explains section 137B, prefills the works from the client form, and asks the
            office’s calculator questions about each job to work out whether a report is needed. The client then books
            {status?.inspector ? ` ${status.inspector.name}${status.inspector.fee ? ` ($${status.inspector.fee})` : ""}` : " the firm’s inspector"} or
            arranges their own (we email them a works summary). Where a report may be needed, they may instead decline it by signing a release.
          </div>

          {!status && !problem && <div style={{ color: T.textFaint }}>Loading…</div>}

          {status && !answered && (status.prefill || []).length === 0 && (
            <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 12 }}>
              The client form lists no owner-builder works{matter.form27EntryId ? "" : " — or there is no client form on this matter"}.
              You can still ask: the client lists the works on the page themselves.
            </div>
          )}

          {status?.assessment?.report?.reasons?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Label>What the client form says</Label>
              <ul style={{ margin: 0, paddingLeft: 18, color: T.textSoft }}>
                {status.assessment.report.reasons.map((r, i) => <li key={i}>{r}</li>)}
                {(status.assessment.insurance?.reasons || []).map((r, i) => <li key={`i${i}`}>{r}</li>)}
              </ul>
            </div>
          )}

          {answered && response && (
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <Label>The client’s answer</Label>
              <div><strong>{DECISION_137B[response.decision] || response.decision}</strong>
                {response.decision === "other" && response.otherInspector?.name ? ` — ${response.otherInspector.name}` : ""}
                {response.decision === "book" && request.inspector?.name ? ` — ${request.inspector.name}` : ""}</div>
              {response.assessment?.verdict && (
                <div style={{ marginTop: 4 }}>
                  {`Calculator: ${VERDICT_137B[response.assessment.verdict] || response.assessment.verdict}`}
                  {(response.assessment.reasons || []).filter(Boolean).length ? ` (${response.assessment.reasons.filter(Boolean).join("; ")})` : ""}
                </div>
              )}
              <div style={{ color: T.textSoft, marginTop: 4 }}>
                Signed by {response.signedName}{response.signedAt ? ` on ${new Date(response.signedAt).toLocaleString("en-AU")}` : ""}
                {response.agentFor?.length > 0 ? `, also as agent for ${response.agentFor.join(", ")}` : ""}
              </div>
              {/* The release binds the signer, and the others only where the
                  signer said they were acting as their agent. */}
              {(() => {
                const others = (request.vendors || []).filter(n => n !== response.signedName && !(response.agentFor || []).includes(n));
                return others.length > 0 && !response.agentFor?.length ? (
                  <div style={{ color: T.red, marginTop: 4 }}>
                    {`Signed for themselves only — ${others.join(", ")} ${others.length === 1 ? "has" : "have"} not signed`
                      + `${response.decision === "waive" ? ", and the release does not bind them" : ""}.`}
                  </div>
                ) : null;
              })()}
              {response.contact?.who && (
                <div style={{ marginTop: 6 }}>
                  Access through the {response.contact.who === "agent" ? "agent" : "client"}
                  {response.contact.availability ? ` — prefers ${response.contact.availability}` : ""}
                </div>
              )}
              <Label>Their answers</Label>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(status.questions || []).map(q => {
                  const a = response.answers?.[q.id] || {};
                  return <li key={q.id}>{q.short}: {ANSWER_137B[a.answer] || "not answered"}{a.detail ? ` — ${a.detail}` : ""}</li>;
                })}
              </ul>
              {response.documents?.length > 0 && (
                <div style={{ marginTop: 6 }}>Documents they have: {response.documents.join(", ")}</div>
              )}
              {response.waiverReason && <div style={{ marginTop: 6 }}>Their reason: {response.waiverReason}</div>}
              {(request.signedFile || request.uploads?.length > 0) && (
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {[...(request.signedFile ? [request.signedFile] : []), ...(request.uploads || [])].map(f => (
                    <a key={f.fileId} href={apiUrl(`/matters/${encodeURIComponent(matter.ref)}/files/${encodeURIComponent(f.fileId)}/content`)}
                      target="_blank" rel="noopener noreferrer" style={{ color: T.text, fontSize: 12 }}>
                      {f.name}
                    </a>
                  ))}
                </div>
              )}
              <Label>Works declared</Label>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(response.works || []).map((w, i) => (
                  <li key={i}>{w.what}{[w.byWhom, w.completed, w.value, w.permit, w.finalCert, w.permitCopy].filter(Boolean).length ? ` (${[w.byWhom, w.completed && `finished ${w.completed}`, w.value, w.permit && `permit: ${w.permit}`, w.finalCert && `final inspection certificate: ${w.finalCert}`, w.permitCopy && PERMIT_COPY_137B[w.permitCopy]].filter(Boolean).join(", ")})` : ""}</li>
                ))}
              </ul>
            </div>
          )}

          {status && !answered && (status.prefill || []).some(w => w.permitFrom) && (
            <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 10 }}>
              The client’s permit answers from the client form are carried over, not asked again.
            </div>
          )}

          {status && !answered && (
            <div>
              <Label>How clear is it?</Label>
              {[
                ["auto", "Let the client’s answers decide", "The calculator works it out from their answers: required, may be required (release offered), or not required."],
                ["required", "Clearly requires a report", "Required whatever they answer. No option to decline."],
                ["grey", "Grey area (maintenance, minor or single-trade)", "At least “may be required”: the client may decline by signing a release, unless their answers say it is required."],
              ].map(([value, label, help]) => (
                <label key={value} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6, cursor: "pointer" }}>
                  <input type="radio" name={`c137b-${matter.ref}`} checked={classification === value}
                    onChange={() => setClassification(value)} style={{ marginTop: 3 }} />
                  <span>{label}{status.suggestion === value ? " (suggested)" : ""}
                    <span style={{ display: "block", fontSize: 11.5, color: T.textFaint }}>{help}</span></span>
                </label>
              ))}
              <Label>Client email</Label>
              <input type="email" value={to} onChange={e => setTo(e.target.value)} style={{ ...fieldStyle, marginBottom: 8 }} />
              <Label>A note for the client (optional)</Label>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={{ ...fieldStyle, marginBottom: 10 }} />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn onClick={() => ask(true)} disabled={!classification || !to.trim()} busy={busy}>
                  {state === "sent" ? "Send a fresh link" : "Email the client"}
                </Btn>
                <Btn variant="ghost" onClick={() => ask(false)} disabled={!classification} busy={busy}>Create link only</Btn>
                {state === "sent" && <Btn variant="ghost" onClick={withdraw} busy={busy}>Withdraw</Btn>}
              </div>
            </div>
          )}

          {minted && (
            <div style={{ marginTop: 12, fontSize: 12, color: T.textSoft }}>
              {minted.emailed ? "Emailed. " : `Not emailed${minted.reason ? ` (${minted.reason})` : ""} — hand this link over yourself. `}
              It is shown once:
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                <code style={{ wordBreak: "break-all", fontSize: 11 }}>{minted.url}</code>
                <Btn variant="ghost" onClick={async () => setCopied(await copyText(minted.url))}>{copied ? "Copied" : "Copy"}</Btn>
              </div>
            </div>
          )}

          {admin && request && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Btn variant="ghost" size="sm" onClick={reset} busy={busy}>Reset (admin)</Btn>
              <span style={{ fontSize: 11, color: T.textFaint }}>Deletes the answers, uploads and signed PDF so the form can be sent again.</span>
            </div>
          )}

          {problem && <div style={{ marginTop: 10, fontSize: 12, color: T.red }}>{problem}</div>}
        </div>
      )}
    </div>
  );
};

export const SigningPanel = ({
  matter, onChanged, defaultOpen = false, stage,
  onRequest = requestSignatures, onWithdraw = cancelSignatures,
}) => {
  const [open, setOpen] = useStagedSection(defaultOpen, stage);
  const [asking, setAsking] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [problem, setProblem] = useState(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [showSigned, setShowSigned] = useState(false);
  // **Opened in the frame, not a new tab** — the same rule the statement above
  // it and the compiled contract both follow. Being the lesser document is a
  // reason to say it quietly, not a reason to hand the reader off to the
  // browser's own viewer and take them off the matter.
  const [showCertificate, setShowCertificate] = useState(false);

  const report = matter.signatures || {};
  const derived = matter.signatories || {};
  const rows = report.asked ? (report.signatories || []) : (derived.signatories || []);
  const expected = report.asked ? report.expected : derived.expected || 0;

  const expiringSoon = report.asked
    ? rows.filter(one => linkTiming(one)?.soon).length : 0;
  const summary = !report.asked
    ? (expected ? `${expected} ${expected === 1 ? "signature" : "signatures"} needed` : "Nobody to ask")
    : report.complete ? "Signed"
    : `${report.signed} of ${report.expected} signed`
      + (expiringSoon ? ` · ${expiringSoon} ${expiringSoon === 1 ? "link expires" : "links expire"} soon` : "");

  const withdraw = async () => {
    setWithdrawing(true);
    setProblem(null);
    try {
      await onWithdraw(matter.ref);
      setConfirmWithdraw(false);
      onChanged();
    } catch (err) {
      setProblem(err.message || "It was not withdrawn");
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <div data-tour="signing" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" fill="none" stroke={T.textFaint} strokeWidth="2" viewBox="0 0 24 24"
               style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Vendor signatures</span>
          {/* **Stale is the one that has to show closed.** The signatures are
              against a version this matter has compiled past, so what is on the
              document panel is not what anybody signed — and that is a decision
              somebody has to take before releasing. */}
          {report.stale && (
            <span style={{ fontSize: 10.5, color: T.red, border: `1px solid ${T.red}`, borderRadius: 4, padding: "1px 5px" }}>
              Signed v{report.version} — v{matter.compileVersion} compiled since
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: report.complete && !report.stale ? signStateColour("good") : T.textFaint }}>
          {summary}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginBottom: 12, maxWidth: 660 }}>
            The vendor must have signed the statement before the purchaser is given it,
            and every vendor signs. Each gets their own link to the version they were shown.
          </div>

          {rows.length === 0 && (
            <div style={{ fontSize: 12, color: T.textFaint }}>
              Nobody to ask — the contract names no vendor yet.
            </div>
          )}

          {rows.map((one, i) => {
            const state = SIGN_STATE[one.state] || SIGN_STATE.none;
            return (
              <SignatoryRow
                key={one.position}
                position={one.position}
                name={one.name}
                capacity={one.capacity}
                last={i === rows.length - 1}
                right={
                  <span style={{ fontSize: 11, color: signStateColour(state.tone), whiteSpace: "nowrap" }}>
                    {state.label}
                    {one.signedAt && ` · ${signedAtText(one.signedAt)}`}
                  </span>
                }
              >
                <div style={{ fontSize: 11, color: T.textFaint, marginTop: 3 }}>
                  {one.email || "No address on the matter"}
                  {/* **Whose word the address is.** Three things reach this
                      field — the office's own client record, staff typing one
                      here, and a co-owner supplying another vendor's on the
                      signing page — and only the first is a record anybody has
                      checked. Undistinguished, a suspect address surfaced as
                      nobody signing. The sentence is the middleware's, like
                      every other provenance tag. */}
                  {one.emailSaid && one.emailSaid !== "from the client record" && (
                    <span style={{ marginLeft: 8, color: T.textFaint }}>· {one.emailSaid}</span>
                  )}
                  {/* What their pen said, beside what we asked for. The two
                      disagreeing is a thing to look at rather than a refusal —
                      a vendor writing "Bob" for "Robert" has still signed. */}
                  {one.signedName && one.signedName !== one.name && (
                    <span style={{ marginLeft: 8 }}>signed as “{one.signedName}”</span>
                  )}
                </div>
                {(() => {
                  const timing = linkTiming(one);
                  return timing && (
                    <div data-link-timing style={{ fontSize: 11, marginTop: 2,
                      color: timing.soon ? T.red : T.textFaint }}>
                      {timing.text}
                    </div>
                  );
                })()}
              </SignatoryRow>
            );
          })}

          {/* **The two documents a completed signing produces.** The executed
              statement is the version they signed with each mark and date in
              the form's own signature blocks — a different document from the
              compiled one, so it is offered as one rather than quietly
              replacing it on the panel above. The signing certificate is the
              office's own record of the act and is never sent to the agent.

              **They fail apart**: stamping the marks needs no browser and the
              certificate does, so one can be here without the other, and the
              warning says which. Where nothing could be produced the reason is
              still said — the signatures are real either way, and somebody
              hunting for a file that was never made is the worse outcome. */}
          {report.complete && !report.stale && (
            <>
              {/* **The document that goes out, opened the way every other one
                  here is opened.** A new tab hands the reader the browser's own
                  PDF viewer and takes them off the matter; the modal keeps them
                  on it, which is what the compiled contract and the searches
                  already do. Same component, so the three cannot drift into
                  three different ways of looking at a PDF. */}
              {signedStatementSrc(matter) && (
                <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 14, paddingTop: 14,
                              display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: T.text, fontWeight: 500 }}>
                    Signed vendor's statement
                  </span>
                  <span style={{ fontSize: 11, color: T.textFaint }}>
                    version {matter.signedVersion}, signed by every vendor
                  </span>
                  <span style={{ flex: 1 }} />
                  <Btn variant="ghost" size="sm" onClick={() => setShowSigned(true)}>
                    Open
                  </Btn>
                  <Btn variant="ghost" size="sm"
                       onClick={() => saveAs(downloadUrl(signedStatementSrc(matter)))}>
                    Download
                  </Btn>
                </div>
              )}

              {/* **The office's own record, said rather than offered.** It is
                  evidence about the act for the file — never sent to the agent,
                  and not the document anybody on this screen is looking for. As
                  a second row of buttons it read as the equal of the statement
                  above it, so it is a sentence with the two links in it. */}
              {signingCertificateSrc(matter) && (
                <p style={{ fontSize: 11.5, color: T.textFaint, margin: "8px 0 0", lineHeight: 1.55 }}>
                  A signing certificate records who signed, when, and from where — for
                  the file, not the agent.{" "}
                  <LinkAction onClick={() => setShowCertificate(true)}>
                    Open
                  </LinkAction>{" or "}
                  <LinkAction onClick={() => saveAs(downloadUrl(signingCertificateSrc(matter)))}>
                    download
                  </LinkAction>{" it."}
                </p>
              )}

              {matter.signedPdfWarning && (
                <p style={{ fontSize: 12, color: T.red, margin: "12px 0 0", lineHeight: 1.5 }}>
                  {SIGNED_PDF_TROUBLE[matter.signedPdfWarning]
                    || "The signatures are recorded, but no executed PDF was produced."}
                </p>
              )}
            </>
          )}

          {problem && (
            <p style={{ fontSize: 12, color: T.red, margin: "12px 0 0", lineHeight: 1.5 }}>{problem}</p>
          )}

          {/* **Nothing is offered once every vendor has signed this version.**
              Asking again at the same version mints no link — the request
              carries signed entries over — so the button did nothing but leave
              a rule and a control on a panel with no action left in it. A new
              version is a new document, and `stale` is what says so. */}
          {!(report.complete && !report.stale) && (
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {/* Refused before it is offered: asking somebody to sign a document
                that has not been built is asking them to sign nothing. */}
            {!matter.compileVersion ? (
              <span style={{ fontSize: 12, color: T.textFaint }}>
                Compile the contract before asking anyone to sign it.
              </span>
            ) : (
              <>
                <Btn variant="primary" size="md" onClick={() => setAsking(true)} disabled={rows.length === 0}>
                  {report.asked ? "Ask again" : "Request signatures"}
                </Btn>
                {report.asked && (report.open > 0 || report.expected > report.signed) && (
                  confirmWithdraw ? (
                    <>
                      <span style={{ fontSize: 12, color: T.textSoft }}>
                        Withdraw the links nobody has used?
                      </span>
                      <Btn variant="ghost" size="md" onClick={withdraw} disabled={withdrawing}>
                        {withdrawing ? "Withdrawing…" : "Yes, withdraw"}
                      </Btn>
                      <Btn variant="ghost" size="md" onClick={() => setConfirmWithdraw(false)} disabled={withdrawing}>
                        Keep them
                      </Btn>
                    </>
                  ) : (
                    <Btn variant="ghost" size="md" onClick={() => setConfirmWithdraw(true)}>
                      Withdraw
                    </Btn>
                  )
                )}
              </>
            )}
          </div>
          )}

          {report.asked && report.cancelled > 0 && (
            <p style={{ fontSize: 11, color: T.textFaint, margin: "10px 0 0" }}>
              {report.cancelled} withdrawn. Signatures already given are kept.
            </p>
          )}
        </div>
      )}

      {asking && (
        <SignatureRequestModal
          matter={matter}
          onRequest={onRequest}
          onCancel={() => setAsking(false)}
          onDone={() => { setAsking(false); onChanged(); }}
        />
      )}

      {/* The same frame the compiled contract and the searches open in — a
          reader stays on the matter rather than being handed to the browser's
          own viewer in another tab. */}
      {showSigned && signedStatementSrc(matter) && (
        <PdfPreviewModal
          wide
          title={`Signed vendor's statement v${matter.signedVersion}`}
          url={signedStatementSrc(matter)}
          embed={signedStatementSrc(matter)}
          onClose={() => setShowSigned(false)}
        />
      )}

      {showCertificate && signingCertificateSrc(matter) && (
        <PdfPreviewModal
          wide
          title={`Signing certificate v${matter.signedVersion}`}
          url={signingCertificateSrc(matter)}
          embed={signingCertificateSrc(matter)}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </div>
  );
};

// ── A matter opened from the new sale page, waiting for a person ───────────
//
// The vendor or agent typed an address on `/sell` and the middleware opened
// this matter with its searches saved as an order, **not yet on the matter and
// not ordered**. This panel is the review: who started it, how to reach them,
// what the owners corporation looked like and what was left for a person to
// decide. *Reviewed* puts the order's lines on the matter as searches to place.
// Everything shown is the matter's own `newSale` record. See the middleware's
// docs/decisions/new-sale.md.
const NEW_SALE_LEFT_OUT = {
  two_lot_exempt: "left out — two-lot owners corporation",
  choose_authority: "choose the water authority",
};
export const newSaleLeftOut = (sale = {}) => (sale.leftOut || []).map(l => `${l.name}: ${NEW_SALE_LEFT_OUT[l.reason] || l.reason}`);

// Form 27's questions, asked on the page. Answered in full, the form is not
// needed; otherwise it can still be sent from Formidable.
export const newSaleAnswersLine = (sale = {}) => {
  if (sale.answersComplete === true) return "All answered on the page — Form 27 is not needed.";
  const vendorLink = sale.startedBy === "agent" && sale.vendorEmail ? " The vendor was emailed a link to answer them." : "";
  if (!sale.answers || !Object.keys(sale.answers).length) return `Not answered yet.${vendorLink} Send Form 27 from Formidable if they are not.`;
  const left = sale.answersMissing ? `${sale.answersMissing} still to answer.` : "Some still to answer.";
  return `${left}${vendorLink} Send Form 27 from Formidable if they are not.`;
};

// The client's answers to the new sale page's questions, in words, fetched
// when opened (`GET /sale/matters/:ref/answers` — the middleware words them).
const NewSaleAnswers = ({ matterRef }) => {
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [error, setError] = useState("");
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || sheet) return;
    setError("");
    try {
      const res = await apiFetch(`/sale/matters/${encodeURIComponent(matterRef)}/answers`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setSheet(body);
    } catch (e) {
      setError(`Could not load the answers: ${e.message}`);
    }
  };
  return (
    <div style={{ marginTop: 8 }}>
      <button type="button" onClick={toggle} data-new-sale-answers-toggle
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: T.text, fontWeight: 500, fontSize: 12, textDecoration: "underline" }}>
        {open ? "Hide answers" : "View answers"}
      </button>
      {open && error && <div style={{ marginTop: 6, color: T.red }}>{error}</div>}
      {open && !error && !sheet && <div style={{ marginTop: 6, color: T.textSoft }}>Loading…</div>}
      {open && sheet && (
        <div data-new-sale-answers style={{ marginTop: 8, display: "grid", gap: 10 }}>
          {sheet.groups.length === 0 && <div style={{ color: T.textSoft }}>Nothing answered yet.</div>}
          {sheet.groups.map(g => (
            <div key={g.title}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{g.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 2fr) 3fr", gap: "2px 12px" }}>
                {g.items.map(i => (
                  <div key={i.key} style={{ display: "contents" }}>
                    <span style={{ color: T.textSoft }}>{i.label}</span>
                    <span>{i.answer == null ? <span style={{ color: T.textSoft, fontStyle: "italic" }}>Not answered</span> : i.answer}
                      {i.by && <span style={{ color: T.textSoft }}> · {i.by === "agent" ? "the agent" : i.by === "manager" ? "the property manager" : "the vendor"}</span>}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// The card payment on a new sale, as the review panel says it. Amounts are
// the middleware's, in cents.
const centsAud = (c) => `$${(Math.round(c || 0) / 100).toFixed(2)}`;
// Still to pay: the middleware's statuses that hold the searches back
// (anything but held, charged, nothing owed, or reviewed without payment).
export const newSaleUnpaid = (sale = {}) => Boolean(sale.payment && !["authorised", "captured", "not_required", "waived"].includes(sale.payment.status));
export const newSalePaymentLine = (p = {}) => {
  if (p.status === "not_required") return `Nothing to pay${p.promoCode ? ` — promotion ${p.promoCode}` : ""}. Terms accepted.`;
  if (p.status === "authorised") return `${centsAud(p.authorisedCents || p.holdCents)} held on the card — Reviewed charges the fee and the searches as ordered.`;
  if (p.status === "captured") return `Charged ${centsAud(p.capturedCents)}${p.shortfallCents ? ` (${centsAud(p.shortfallCents)} more than was held, not charged)` : ""}.`;
  if (p.status === "waived") return "Reviewed without card payment.";
  if (p.status === "expired") return "The hold on the card lapsed — the client can pay again from their link.";
  return `Not paid yet${p.holdCents ? ` (${centsAud(p.holdCents)})` : ""} — no card is held. The client pays at the end of the page or from their link; nothing can be ordered until then.`;
};

// **A property the map could not find** (`newSale.unlisted`): staff confirm
// the land from the rates notice — a lot and plan, or one of the properties it
// has — and the middleware rebuilds the order from it. Reviewed waits for it.
const ConfirmSaleProperty = ({ matter, onConfirmed }) => {
  const sale = matter.newSale || {};
  const typed = sale.answers && typeof sale.answers.lotPlan === "string" ? sale.answers.lotPlan : "";
  const [spi, setSpi] = useState(typed);
  const [choices, setChoices] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const confirm = async (payload) => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`/sale/matters/${encodeURIComponent(matter.ref)}/property`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (body.needsChoice) { setChoices(body.candidates || []); return; }
      if (!res.ok || !body.ok) {
        throw new Error(body.samePlan && body.samePlan.length ? `${body.error} That plan has ${body.samePlan.slice(0, 6).join(", ")}${body.samePlan.length > 6 ? "…" : ""}.` : (body.error || `HTTP ${res.status}`));
      }
      if (onConfirmed) await onConfirmed();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const ratesNotices = sale.ratesNotices || [];
  return (
    <div data-new-sale-property style={{ marginTop: 10, padding: "10px 12px", background: T.white, border: `1px solid ${T.borderMid}`, borderRadius: 8 }}>
      <div style={{ fontWeight: 600 }}>Not found on the map — confirm the property</div>
      <div style={{ color: T.textSoft, margin: "2px 0 8px" }}>
        The client typed <strong style={{ color: T.text }}>{matter.address || sale.address || "an address"}</strong>.
        {" "}{ratesNotices.length ? `Their rates notice is in the documents (${ratesNotices.length === 1 ? "one file" : `${ratesNotices.length} files`}).` : "No rates notice yet."}
        {" "}The order is an estimate until the property is confirmed; Reviewed charges what the confirmed order comes to.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={spi} onChange={e => setSpi(e.target.value)} placeholder="Lot and plan, e.g. 3\PS123456" aria-label="Lot and plan"
          style={{ flex: "1 1 220px", font: "inherit", fontSize: 12, padding: "6px 10px", border: `1px solid ${T.borderMid}`, borderRadius: 6 }} />
        <Btn variant="raised" onClick={() => confirm({ spi })} disabled={busy || !spi.trim()} busy={busy}>Confirm property</Btn>
      </div>
      {choices && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ color: T.textSoft }}>{choices.length ? "That lot and plan has more than one property — which is it?" : "That lot and plan has no addressed property."}</span>
          {choices.map(c => (
            <Btn key={c.propertyPfi} variant="raised" onClick={() => confirm({ address: c.address, propertyPfi: c.propertyPfi })} disabled={busy}>{c.address}</Btn>
          ))}
        </div>
      )}
      {error && <div style={{ marginTop: 6, color: T.red }}>{error}</div>}
    </div>
  );
};

// **The lease, on a property sold subject to one**: where it is up to, in
// the middleware's words (`GET /sale/matters/:ref/lease`), and asking for it
// again — to whoever manages the tenancy, or an address staff type. The
// middleware decides who is asked and sends it; see its new-sale.md, "The lease".
export const newSaleLeaseLine = (lease, recipient) => {
  if (!lease) return null;
  const who = lease.role === "agent" ? "the selling agent's office" : "the property manager";
  const when = lease.at ? new Date(lease.at).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "";
  if (lease.state === "received") return `In the documents (${lease.files === 1 ? "one file" : `${lease.files} files`}) — it goes in the Section 32.`;
  if (lease.state === "requested") {
    return lease.emailed
      ? `Asked for from ${who}, ${lease.to}${when ? `, on ${when}` : ""}${lease.times > 1 ? ` (${lease.times} times)` : ""}. Not here yet.`
      : `A link for ${who} (${lease.to}) was made but not emailed — mail is not set up. Not here yet.`;
  }
  if (lease.state === "not_requested") return `Not asked for — ${who} manages it${recipient && recipient.email ? ` (${recipient.email})` : ""}. Not here yet.`;
  if (lease.state === "no_address") return `Not asked for — no address for ${who}. Not here yet.`;
  if (lease.state === "to_upload") return "The client has it and is to upload it from their link. Not here yet.";
  return "The vendor manages the tenancy — they can upload it from their link. Not here yet.";
};
const NewSaleLease = ({ matter }) => {
  const sale = matter.newSale || {};
  const leaseFiles = (sale.leases || []).length;
  const leased = (sale.answers && sale.answers.transferredStatus === "Subject to lease") || leaseFiles > 0;
  const [state, setState] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`/sale/matters/${encodeURIComponent(matter.ref)}/lease`);
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.ok) setState(body);
    } catch (e) { /* the line stays off */ }
  }, [matter.ref]);
  useEffect(() => { if (leased) load(); }, [leased, load, leaseFiles]);
  if (!leased || !state || !state.lease) return null;
  const ask = async () => {
    setBusy(true); setNote("");
    try {
      const res = await apiFetch(`/sale/matters/${encodeURIComponent(matter.ref)}/lease-request`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(email.trim() ? { email: email.trim() } : {}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setNote(body.emailed ? `Emailed ${body.to}.` : `Not emailed — send them this link: ${body.link || "(no sale page address set)"}`);
      setEmail("");
      await load();
    } catch (e) {
      setNote(`Not sent: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  const { lease, recipient } = state;
  const canAsk = lease.state !== "received";
  return (
    <>
      <span>Lease</span>
      <span data-new-sale-lease={lease.state}>
        {newSaleLeaseLine(lease, recipient)}
        {canAsk && (
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginLeft: 6 }}>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder={recipient ? recipient.email : "Property manager's email"} aria-label="Who to ask for the lease"
              style={{ font: "inherit", fontSize: 12, padding: "3px 8px", border: `1px solid ${T.borderMid}`, borderRadius: 6, width: 200 }} />
            <Btn variant="raised" onClick={ask} disabled={busy || (!email.trim() && !recipient)} busy={busy}>{lease.state === "requested" ? "Ask again" : "Ask for it"}</Btn>
          </span>
        )}
        {note && <span style={{ display: "block", color: T.textSoft }}>{note}</span>}
      </span>
    </>
  );
};

const NewSaleReview = ({ matter, onReviewed }) => {
  const sale = matter.newSale || {};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [unpaidRefused, setUnpaid] = useState(false);
  // Reviewed: only the answers stay, for as long as the matter is open.
  if (matter.newSale && sale.review === "done") {
    return (
      <div data-new-sale-answers-panel style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: T.text, lineHeight: 1.55 }}>
        <span style={{ fontWeight: 600 }}>New sale — the client's answers</span>
        <span style={{ color: T.textSoft }}> · {newSaleAnswersLine(sale)}</span>
        <div style={{ marginTop: 6, color: T.textSoft, display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px" }}><NewSaleLease matter={matter} /></div>
        <NewSaleAnswers matterRef={matter.ref} />
      </div>
    );
  }
  if (sale.review !== "pending") return null;
  // Reviewed charges the held card; unpaid, it waits unless staff go on
  // without payment (the middleware records that on the timeline).
  const unpaid = newSaleUnpaid(sale) || unpaidRefused;
  const markReviewed = async (withoutPayment = false) => {
    // Going on without a card is a decision, so it is asked, not clicked past.
    if (withoutPayment && typeof window !== "undefined" && !window.confirm("The client has not paid and no card is held. Review it anyway? The searches can then be ordered and the office pays for them.")) return;
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch(`/sale/matters/${encodeURIComponent(matter.ref)}/reviewed`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withoutPayment ? { withoutPayment: true } : {}),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409 && body.code === "payment_pending") { setUnpaid(true); throw new Error(body.error); }
      if (res.status === 409 && body.code === "property") throw new Error(body.error);
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      if (onReviewed) await onReviewed();
    } catch (e) {
      setError(`Could not mark it reviewed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  const who = sale.startedBy === "agent"
    ? `the agent, ${sale.name}${sale.agency ? ` (${sale.agency})` : ""}`
    : `the vendor, ${sale.name}`;
  const leftOut = newSaleLeftOut(sale);
  const t = NOTICE_TONE.warn;
  return (
    <div data-new-sale-review style={{ background: t.bg, border: `1px solid ${t.border}`, borderBottom: t.foot, borderRadius: 10, padding: "12px 14px", marginBottom: 12, fontSize: 12, color: T.text, lineHeight: 1.55 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600 }}>New sale — waiting for review</span>
        <span style={{ color: T.textSoft, flex: 1, minWidth: 240 }}>
          Opened on the new sale page by {who}. Nothing has been ordered: <strong>Reviewed</strong> {sale.payment && !unpaid ? "charges the card and " : ""}adds the queued searches to this matter to place.
        </span>
        {sale.payment && (
          <span data-new-sale-card={unpaid ? "unpaid" : "held"} style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 999, background: T.white, border: `1px solid ${unpaid ? T.borderMid : T.text}`, color: unpaid ? T.textSoft : T.text }}>
            {unpaid ? "Awaiting card payment" : sale.payment.status === "not_required" ? "Nothing to pay" : `Card held · ${centsAud(sale.payment.authorisedCents || sale.payment.holdCents)}`}
          </span>
        )}
        <Btn variant="primary" onClick={() => markReviewed(false)} disabled={busy || unpaid || Boolean(sale.unlisted && !sale.propertyConfirmedAt)} busy={busy}>Reviewed</Btn>
      </div>
      <div style={{ marginTop: 8, color: T.textSoft, display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px" }}>
        <span>Contact</span><span>{[sale.email, sale.phone].filter(Boolean).join(" · ")}</span>
        {sale.startedBy === "agent" && <><span>Vendor</span><span>{[sale.vendorName, sale.vendorEmail].filter(Boolean).join(" · ") || "Not given"}</span></>}
        {sale.startedBy !== "agent" && <><span>Agent</span><span>{sale.agentEmail ? [sale.agentFound, sale.agentEmail].filter(Boolean).join(" · ") : "Not given"}</span></>}
        {(sale.cc || []).length > 0 && <><span>Copy to</span><span>{sale.cc.join(", ")}</span></>}
        {sale.ownersCorporation && <><span>Owners corp.</span><span>{(sale.ownersCorporation.plans || []).map(p => `${p.plan}: ${p.status === "exempt_two_lot" ? "two lots, exempt" : `likely ${p.likelyCount}`}${p.lots == null ? " (lot count unknown)" : ""}`).join("; ") || "None found"}</span></>}
        {leftOut.length > 0 && <><span>To decide</span><span>{leftOut.join("; ")}</span></>}
        <span>Questions</span><span>{newSaleAnswersLine(sale)}</span>
        {sale.payment && <><span>Payment</span><span>{newSalePaymentLine(sale.payment)}</span></>}
        {(sale.sellerNotes || []).length > 0 && <><span>Sellers</span><span>{sale.sellerNotes.join(" ")}</span></>}
        <NewSaleLease matter={matter} />
      </div>
      {sale.unlisted && !sale.propertyConfirmedAt && <ConfirmSaleProperty matter={matter} onConfirmed={onReviewed} />}
      <NewSaleAnswers matterRef={matter.ref} />
      {error && <div style={{ marginTop: 6, color: T.red }}>{error}</div>}
      {unpaid && (
        <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: T.textSoft }}>Reviewed waits for the client's card. Only if the office has agreed to go on without it:</span>
          <Btn variant="raised" onClick={() => markReviewed(true)} disabled={busy}>Review without payment…</Btn>
        </div>
      )}
    </div>
  );
};

export const MatterDetailV2 = ({ embedded = false, matter, onSaleReviewed, onOrderSearches, onExcludeSearch, searchesLoading, compiling, compileStartedAt, onStop, stopping, stopRequested, fieldRegistry, tenant, sampleRef, savingFields, onAdoptAgent, onRevokeAgentLink, adoptingAgent, onSaveToDirectory, onSaveField, onSaveFields, onSaveClause, onRemoveClause, savingClause, onRevertField, onRevertAllFields, revertingAll, onNotRequired, markingNotRequired, savingConditions, onSaveSpecialConditions, savingBlockClauses, onSaveBlockClauses, onBack, onRelease, onCrossCheck, onManualCompile, onReread, onArrangeDocuments, onAddDocument, onDeleteDocument, onReplaceDocument, onRenameDocument, onReorderSearches, onOcCertificateDrafted, onPlaceReturn, onAddReturn, onDismissReturn, uploading, onNotesChange, onArchive, onUnarchive, onUndoToast, onSearchStatusChange, onSignaturesChanged, compiledReveal = 0, compiledExpand = 0 }) => {
  // Where you are among the panes: red in the classic look, the firm's colour
  // in the practice look (see `useFluent`).
  const paneMark = useFluent() ? T.brand : T.red;
  const [searches, setSearches] = useState(matter.searches);

  // **The list on screen is local state, and this is what re-seeds it.**
  // A row is held locally because a status flip shows before its save lands,
  // so the array has to be writable — but nothing ever wrote the *server's*
  // answer back into it. What stood in for that was the parent's `key`, which
  // carried `searches.length`: a row being added or removed remounted the
  // whole page and picked up fresh data, and a row merely *changing* did not.
  //
  // So a document attached to a search that was already ordered — the
  // ordinary "it came by post rather than through Zap 2" case, and the one
  // write on this screen that cannot change the count — reached the server and
  // never the screen. The row went on saying `Ordered`, with no file and no
  // *being read…*, until somebody left the matter and came back. Renaming had
  // it too, and so did every `readState` the re-check went looking for.
  //
  // **Signed on what a row says about itself, not on the array's identity** —
  // `searchesSignature`, which is where that reasoning lives.
  const signature = useMemo(() => searchesSignature(matter.searches), [matter.searches]);
  const lastSignature = useRef(signature);
  useEffect(() => {
    if (lastSignature.current === signature) return;
    lastSignature.current = signature;
    setSearches(matter.searches || []);
  }, [signature, matter.searches]);
  const allDone = !searchesLoading && searches.length > 0 && searches.every(s => s.status === "Returned" || s.status === "Not Required");
  const reading = Boolean(matter.awaitingExtractionSince) || (matter.documentsBeingRead || 0) > 0;
  const busy = compiling || reading;
  // **`compiling` means work is being watched, and the watch is armed for a
  // read too** — so it is not the thing that blocks filing a document. Only a
  // merge is: it has the pack in hand, and adding to it underneath changes
  // what gets bound.
  const merging = compiling && !reading;

  // ── What to do about the contract you are reading ────────────────────────
  //
  // A fault in the compiled PDF is found there and fixed somewhere else, and
  // the way back used to be closing it and going looking. These are the three
  // things anybody does next, and they are handed to the panel so the inline
  // frame and the expanded modal cannot offer different ones.
  //
  // **The preview is where a fault gets fixed, not the data panel.** It
  // renders the same contract and edits in place, so it is one screen rather
  // than a translation from a clause back to a field name.
  const [previewReveal, setPreviewReveal] = useState(0);
  // Sent from the contract itself: special condition 14.1 points at conditions
  // "appearing on the following pages", and pressing it opens the panel that
  // writes them. A counter rather than a boolean so pressing it twice works.
  const [conditionsReveal, setConditionsReveal] = useState(0);
  //
  // **Re-compile is not one of them, in either view.** On the matter screen it
  // is already in the step card and in the sticky bar, so offering it under
  // the frame was a duplicate a hundred pixels away. In the modal it is worse
  // than a duplicate: every action here closes the modal before it runs, so it
  // would shut the document and then replace it with a different version. You
  // press it after changing something, and changing something is what the two
  // below are for. What that costs is the matter whose data is newer than its
  // PDF — which has its own banner on the screen behind, with Re-compile in it.
  //
  // **Edit is no longer one of them under the frame.** It was the read → edit
  // move — the commonest thing anybody does after looking at the PDF — and it
  // opened a different panel and scrolled you to it, which is the trip past
  // two identical frames that made *which one am I in* a question at all. The
  // segment directly above the document is that move now, in one click and
  // without moving the page.
  //
  // It stays in the modal, where the segment is covered: every action there
  // closes the modal before it runs, so pressing it lands on the draft with
  // the document still on screen.
  // Read through a ref so the memo need not change with every render; see
  // `arrangeHere` below for what it does in the practice look.
  const arrangeRef = useRef(onArrangeDocuments);
  const compiledActions = useMemo(() => [
    { label: ARRANGE_LABEL, onClick: () => arrangeRef.current() },
  ], []);

  // ── And two the expanded view alone offers ───────────────────────────────
  //
  // **The modal covers the buttons; the frame does not.** It is `fixed` over
  // the whole viewport at a z-index above the sticky matter bar, so while it
  // is open Release and Send for cross-check are not merely a scroll away —
  // they are unreachable. That is the dead end Open in a new tab had, and the
  // reason Expand replaced it. Beneath the frame both are on the screen twice
  // over, in the step card and in the bar.
  //
  // So the two views deliberately offer different sets, which reverses the
  // rule that used to sit here — one array, so they "cannot offer different
  // ones". That rule was right about *where you go to fix a fault*: as likely
  // to be spotted in the frame as in the modal, so Edit and Arrange are shared
  // and stay shared. It is the same principle producing a different answer for
  // these two, because one view hides the toolbar and the other does not.
  //
  // **Neither sends anything on the press.** Release opens the covering-letter
  // modal and cross-check opens its own, so the deliberate step this was
  // always meant to have is in the flow rather than in the button's absence —
  // which is what makes offering them beside a glance at the document safe.
  //
  // Cross-check is absent entirely where the deployment has no address for it,
  // the same rule the bar's button follows: the middleware withholds it from a
  // client instance, and a button that answers 404 is worse than no button.
  // **And it asks `canRelease` like everything else.** This offered Release
  // unconditionally while the step card applied the rule, so the two disagreed
  // by construction — on a matter with no stored copy the modal was the only
  // thing offering a send, and the only outcome of pressing it is the
  // middleware's refusal. Same rule, both places.
  const modalActions = useMemo(() => [
    { label: "Edit contract and section 32", onClick: () => setPreviewReveal(n => n + 1) },
    ...compiledActions,
    ...(canRelease(matter)
      ? [{ label: sendContractLabel(matter), onClick: onRelease, disabled: busy }]
      : []),
    // **Beside Release rather than instead of it.** The vendor's copy and the
    // agent's release are two different sends — one of them puts the contract
    // in front of the market — and the same rule decides whether either is
    // offered, since both need a stored document to send.

    ...(tenant?.crossCheck
      ? [{ label: "Send for cross-check", onClick: onCrossCheck, disabled: busy }]
      : []),
    // **`matter`, not two of its fields.** The body asks `canRelease(matter)`
    // and `releaseLabel(matter)`, which read more than the two named here — so
    // the list was a claim about what this depends on that was already false,
    // and the memo would go stale on any field those rules grow. It is an array
    // of three entries; re-building it on any change to the matter costs
    // nothing worth reasoning about.
  ], [compiledActions, matter, onRelease, onCrossCheck, tenant, busy]);

  // **The Section 32 tab's ribbon, in the main module.** The tab draws an
  // empty ribbon and this page fills it (`useRibbonSlot`), so every button
  // asks the rules the page's own buttons ask: Release and the vendor's copy
  // only where `canRelease`, cross-check only where the deployment has an
  // address, nothing that writes while a compile or a read is running. None
  // of them is primary — the step card above the page keeps that job — and
  // none sends on the press: each opens the page's own modal.
  //
  // **The ribbon is the page's navigation as well**, so the rail goes: its
  // panes are the first group of buttons (pressed while on screen), sent
  // letters are the matter's Emails tab, notes are its Memos, and archiving
  // is in the matter's settings. Signing — the vendor's and the purchasers' —
  // is a view of its own rather than two panels at the foot of the contract.
  const ribbonSlot = useRibbonSlot();
  const ribbonNav = Boolean(embedded && ribbonSlot);
  const view = (key, label, icon) => ({ label, icon, pressed: pane === key, onClick: () => setPane(key) });
  const hasNotes = Boolean(String(matter.notes || "").trim());
  // **In the practice look the searches list is the arrange screen**: it
  // reorders, renames, replaces, adds, and leaves a document out of the pack.
  // Every Arrange press goes to it; the classic look keeps its own page.
  const arrangeHere = (opts) => {
    if (!ribbonNav) return onArrangeDocuments(opts);
    setPane("documents");
    setSearchesReveal(n => n + 1);
    return undefined;
  };
  arrangeRef.current = arrangeHere;
  // Drawn at render, after the panes' state below is declared.
  const drawRibbon = () => (ribbonNav ? createPortal(
    <>
    <span data-tour="rail" style={{ display: "inline-flex", gap: 2 }}>
      <RibbonItems items={[
        view("contract", "Contract", "file"),
        view("documents", "Searches", "list"),
        view("signing", "Signing", "sign"),
        view("activity", "Activity", "activity"),
      ]} />
    </span>
    <RibbonDivider />
    <RibbonItems items={[
      busy && onStop
        ? { label: stopRequested ? "Stopping…" : "Stop", icon: "stop", onClick: () => onStop(reading ? "read" : "compile"), disabled: stopping }
        : { label: matter.status === "compile_failed" ? "Retry compile" : matter.status === "compiled" || matter.status === "released" || matter.compileVersion ? "Re-compile" : "Compile", icon: "compile", onClick: onManualCompile, disabled: busy },
      { label: "Edit contract", icon: "edit", onClick: () => { setPane("contract"); setPreviewReveal(n => n + 1); } },
      canRelease(matter) && { label: sendContractLabel(matter), icon: "send", onClick: onRelease, disabled: busy },
      tenant?.crossCheck && { label: "Cross-check", icon: "check", onClick: onCrossCheck, disabled: busy },
      // Notes predate the Memos tab and are kept for the matters that have
      // some; a matter without any has nowhere new to start them but Memos.
      hasNotes && { label: "Notes", icon: "memo", onClick: () => setNotesOpen(true) },
    ]} />
    <RibbonMenu items={[
      (matter.returnedCount || 0) > 0 && { label: "Re-read certificates", onClick: onReread, disabled: busy },
      matter.compileVersion && { label: "Preview the agent's page", onClick: () => window.open(apiUrl(`/agent-preview/${matter.ref}`), "_blank", "noopener") },
      matter.compileVersion && { label: "Preview the purchaser page", onClick: () => window.open(apiUrl(`/purchaser-preview/${matter.ref}`), "_blank", "noopener") },
      onRevokeAgentLink && agentLinkLive(matter) && "divider",
      onRevokeAgentLink && agentLinkLive(matter) && { label: "Switch off the agent's download link", onClick: () => onRevokeAgentLink(matter.ref) },
      "divider",
      { label: "Take the tour", onClick: startTour },
    ]} />
    </>,
    ribbonSlot,
  ) : null);

  const defaults = useMemo(() => sectionDefaults(matter.status), [matter.status]);
  const mergeableCount = searches.filter(isCompilable).length;
  const isCompiledOrReleased = matter.status === "compiled" || matter.status === "released";
  const hasCompiled = Boolean(isCompiledOrReleased && compiledPdfSrc(matter));

  // ── Which state of the document is on screen ──────────────────────────────
  //
  // **Staged like every other open state**: it follows the matter's stage and
  // a click overrules it until the stage changes. So a compiled matter opens
  // on the PDF the autocompile produced, and one whose compile never fired
  // opens on the draft — which is the case staff described as the one where
  // they look at the template first.
  const documentRef = useRef(null);
  const [segment, setSegment] = useStagedSection(documentSegment(matter, hasCompiled), matter.status);

  // **The reveal signals switch the segment rather than opening a section.**
  // They kept their meaning — *take me to the draft*, *take me to the
  // compiled document* — and what that means is now one control rather than
  // two folds. Done here rather than inside the panels because the hidden one
  // has no layout: a child scrolling itself into view before the parent has
  // swapped the segment would scroll nothing at all.
  // ── Landing the draft where the compiled document was ────────────────────
  //
  // Read the PDF, spot a problem at 9.1, press Draft, arrive at 9.1.
  //
  // **Asked on the press, answered whenever it is answered.** Reading a page's
  // text layer is async and the draft may be mounting for the first time, so
  // the answer is held and the preview consumes it once it has a paginated
  // document — which is why this is a value handed down rather than a call.
  //
  // **The ask is one-way and the answer may never come**: an older viewer does
  // not know the message, and a scanned certificate has no text to read. Both
  // leave the draft where it was, which is what it did before this existed.
  const [draftTarget, setDraftTarget] = useState(null);
  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.s32 !== "at" || !event.data.text) return;
      // A counter rather than the text alone: pressing Draft twice from the
      // same place has to move the reader twice.
      setDraftTarget(prev => ({ text: event.data.text, n: (prev?.n || 0) + 1 }));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // **Only leaving the compiled document**, so a press of Draft while already
  // on the draft does not move the reader off what they were editing.
  const chooseSegment = (key) => {
    if (key === "draft" && segment === "compiled" && hasCompiled) {
      postToViewers({ s32: "where" });
    }
    setSegment(key);
  };

  const goToDocument = () => {
    if (typeof documentRef.current?.scrollIntoView === "function") {
      documentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  useEffect(() => {
    if (!previewReveal) return;
    setSegment("draft");
    goToDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewReveal]);
  useEffect(() => {
    if (!compiledReveal) return;
    setSegment("compiled");
    goToDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compiledReveal]);
  // The panel opens its own modal off this one; the segment is set so closing
  // it leaves the document they were shown on screen rather than the other.
  useEffect(() => {
    if (!compiledExpand) return;
    setSegment("compiled");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compiledExpand]);

  // Follows the stage without overruling the person looking at it — the same
  // rule the panels use for their own open state.
  const [pane, setPane] = useStagedSection(paneFor(matter.status), matter.status);

  // **"Ask the client for the 137B report" works from wherever it is
  // pressed.** The panel sits in the Searches pane, while the button is also
  // in the searches review (the Contract pane, in the practice look) and in
  // the release and compile reminders — so the press brings this pane up, and
  // the panel opens and scrolls itself into view.
  useEffect(() => {
    const toSearches = () => setPane("documents");
    window.addEventListener(OPEN_137B_EVENT, toSearches);
    return () => window.removeEventListener(OPEN_137B_EVENT, toSearches);
  }, [setPane]);

  // **The tour drives the pane, which is why it is held here rather than in
  // `App`.** Its steps move between Contract and Searches, and an inactive pane
  // is `display: none` with no rectangle to point at — so a tour mounted above
  // this component could describe the searches list and never be able to show
  // it. Everything it needs is one setter away from where it is now.
  //
  // The nudge is read once, on mount, rather than on every render: `tourSeen`
  // touches `localStorage`, and this screen re-renders on every poll.
  // **The sample matter opens the tour by itself, every time.** It is the
  // matter staff are sent to in order to learn the screen, so waiting to be
  // asked would make the one page built for that purpose behave like every
  // other one. This component is keyed on the matter reference, so leaving and
  // coming back starts it again — and skipping it leaves the rest of the visit
  // alone, which is what makes starting it automatically bearable.
  const isSample = Boolean(sampleRef) && matter.ref === sampleRef;
  // A banner above the matter bar — the sample's, or a new sale waiting for
  // review — and the bar's top pull would paint over it (see the bar below).
  const bannerAbove = isSample || matter.newSale?.review === "pending";
  // The Section 32 tab on the portal's own site, in the practice look: the
  // header is a toolbar under the matter's (see the bar below). Inside the
  // WordPress frame the embedded page stays as it has been.
  const tidy = embedded && !IN_FRAME;
  const [tourOpen, setTourOpen] = useState(isSample);
  // No nudge on the sample: the tour is already open, and a card offering what
  // is on screen is the kind of thing people stop reading.
  //
  // Closed until the office has answered, like the help panel above and for
  // the same reason.
  const [nudge, setNudge] = useState(false);
  useFirstVisit("tour", tourSeen, () => setNudge(true), { skip: isSample });
  const startTour = () => { setNudge(false); setTourOpen(true); };

  const done = searches.length > 0
    ? searches.filter(s => s.status === "Returned" || s.status === "Not Required").length
    : (matter.returnedCount || 0);
  const total = searches.length > 0 ? searches.length : (matter.searchCount || 0);

  const licenceMissing = useLicenceMissing();
  const step = nextStep(matter, {
    compiling, reading, mergeableCount, outstanding: Math.max(0, total - done), licenceMissing,
  });

  // The counts are the panels' own — `contractFields.missing` is the
  // middleware's answer and `reviewProblemCount` is the one the searches
  // review shows. A second way of counting either is how the rail and the
  // panel come to disagree.
  const missingCount = matter.contractFields?.missing?.length;
  const reviewCount = matter.documentReview ? reviewProblemCount(matter.documentReview) : null;

  const act = {
    compile: () => (
      <Btn
        key="compile"
        // **A dark button over a list of what is missing says the opposite of
        // the words under it** — the reasoning `releaseReady` already carries,
        // applied to the press this card is usually about. `compileReady` is
        // only ever set false, so every branch that does not mention it reads
        // exactly as it did.
        variant={step.compileReady === false ? "raised"
          : step.kind === "ready" || step.kind === "compileError" ? "primary"
          : step.tone === "ink" ? "raised" : "warm"}
        size="md" onClick={onManualCompile} disabled={busy}>
        {matter.status === "compile_failed" ? "Retry compile" : isCompiledOrReleased || matter.compileVersion ? "Re-compile" : "Compile the contract"}
      </Btn>
    ),
    // `raised`, not `ghost`. Ghost is transparent and this card has a tint, so
    // demoting the release made it read as *absent* rather than as secondary —
    // which answers a different question entirely.
    release: () => (
      <Btn key="release"
        variant={matter.status === "released" || step.releaseReady === false ? "raised" : "primary"}
        size="md" onClick={onRelease} disabled={busy}>
        {sendContractLabel(matter)}
      </Btn>
    ),
    // **Sending it to a colleague is not releasing it**, so it is never the
    // primary button: a dark action beside "Release to agent" is how
    // somebody presses the wrong one on a contract that then reads as sent.
    //
    // Absent entirely where the deployment has no cross-check address — the
    // middleware withholds it from a client instance, and a button that
    // answers 404 is worse than no button. `.filter(Boolean)` drops the null.
    crossCheck: () => (tenant?.crossCheck ? (
      <Btn key="crossCheck" variant="raised" size="md" onClick={onCrossCheck} disabled={busy}>
        Send for cross-check
      </Btn>
    ) : null),
  };
  // **One send button in both looks**: the dialog it opens has the Agent and
  // Vendor tabs, so there is no second button for the vendor.
  const buttons = (step.actions || []).map(a => act[a]?.()).filter(Boolean);

  const tone = NOTICE_TONE[step.tone];
  // The step card alone reads `ink` as plain white: it is the one card at the
  // top of every matter, and a grey slab there was the heaviest thing on it.
  const cardTone = step.tone === "ink" ? null : tone;
  // **A plain notice is a line in the headline card, not a card of its own.**
  // An ink notice was a grey slab under the white headline — the last grey
  // block on the page, and a second card saying much what the first one said.
  // A tone that means something (warn, red, green) keeps its own card, and
  // while work is running there is no headline card to join, so they stay
  // where they were.
  const cardNotices = step.kind === "busy" ? [] : step.notices.filter(n => (n.tone || "ink") === "ink");
  const ownNotices = step.notices.filter(n => !cardNotices.includes(n));

  // The card the headline lives in. It no longer carries the actions — those
  // sit beside the matter's name and are always there — so nothing watches
  // whether it is on screen any more.
  const cardRef = useRef(null);
  const [dataReveal, setDataReveal] = useState(0);
  const [searchesReveal, setSearchesReveal] = useState(0);
  const [notesOpen, setNotesOpen] = useState(false);
  // The duties form for this matter: a four-hour code for this one matter,
  // handed straight to the extension, which opens Duties Online and fills
  // the form as each page opens. Without the extension the code is copied,
  // to paste into its panel. Never put on a link
  // (ProveyAustralia/s32-middleware#383).
  const [dutiesCode, setDutiesCode] = useState("");
  const [dutiesHelp, setDutiesHelp] = useState("");
  const dutiesStatus = useDutiesExtension();
  const openDutiesForm = async () => {
    const ref = matter.ref ?? matter.id;
    setDutiesCode("busy");
    const outcome = await openDutiesFormFor(ref);
    setDutiesCode(outcome === "unsupported" ? "copied" : outcome);
    if (outcome === "copied" || outcome === "unsupported") setDutiesHelp(outcome);
    setTimeout(() => setDutiesCode(""), 6000);
  };

  // ── What the matter is waiting on, as presses ──────────────────────────
  // The fields still blank and the searches not back, each a line that goes
  // where it is answered. Counted off what the screen already holds — the
  // middleware's own missing list and the rows — so the strip and the rail
  // cannot disagree. A released matter's pack is the record, so a search
  // arriving after it is the rebuild notice's to report, not this.
  const missingFields = matter.contractFields?.missing || [];
  // **Every row not settled, the same count the headline sentence states** —
  // it excluded the rows the matter is not waiting for, so one card read
  // *4 searches have not come back* over *3 searches not back*.
  const notBack = matter.status === "released" ? []
    : searches.filter(isNotBack);
  const listNames = (names) => names.slice(0, 2).join(", ") + (names.length > 2 ? ` and ${names.length - 2} more` : "");
  const issues = [
    missingFields.length > 0 && {
      key: "fields", count: missingFields.length, tone: "red",
      label: `Field${missingFields.length === 1 ? "" : "s"} still needed`,
      detail: listNames(missingFields.map(f => String(f.label || f.name || "").toLowerCase())),
      press: "Fill in", go: () => { setPane("contract"); setDataReveal(n => n + 1); },
    },
    notBack.length > 0 && {
      key: "searches", count: notBack.length, tone: "ink",
      label: `Search${notBack.length === 1 ? "" : "es"} not back`,
      detail: listNames(notBack.map(s => s.displayName || s.name || "")),
      // **It moves even when Searches is already the tab on screen**: it opens
      // the list and scrolls to it, past the review above. Switching a pane
      // that is already showing was a press that did nothing.
      press: "View", go: () => { setPane("documents"); setSearchesReveal(n => n + 1); },
    },
  ].filter(Boolean);

  const matterBarRef = useRef(null);
  useStickyBarHeight(matterBarRef);

  return (
    <div>
      {/* **The tour is a spotlight over the real screen, not a picture of
          one**, so it sits above the sticky bars and moves the panes under
          itself. The nudge is a card in the corner and blocks nothing. */}
      {tourOpen && <Tour onPane={setPane} onClose={() => setTourOpen(false)} />}
      {nudge && !tourOpen && (
        <TourNudge
          onStart={startTour}
          // Dismissing counts as having been offered it. Asking again on the
          // next matter is how a card gets ignored and then resented, and the
          // menu keeps the tour reachable for ever.
          onDismiss={() => { rememberTour(); setNudge(false); }}
        />
      )}

      {/* **The sample says what it is, above everything.** It is a real
          matter with a real address on it, so somebody who arrived without
          reading the help panel has no other way to know that the presses here
          go nowhere — and a person who thinks they have released a contract
          when they have not is the worst outcome this page can produce.

          Under the bar rather than over it, so it scrolls away once read: it
          is a standing fact about the matter, not the next thing to do. */}
      {isSample && (
        <div style={{
          background: T.bgAlt, border: `1px solid ${T.borderMid}`, borderRadius: 10,
          padding: "11px 14px", marginBottom: 12, display: "flex", gap: 10,
          alignItems: "baseline", flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Sample matter</span>
          <span style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, flex: 1, minWidth: 260 }}>
            Press anything. Release and the signature request email <strong>you</strong> rather than
            the agent or the vendors, and nothing here changes the matter.
          </span>
          <button onClick={startTour}
            style={{ background: "none", border: "none", cursor: "pointer", color: T.text, fontSize: 12, fontWeight: 500, fontFamily: "Inter, sans-serif", padding: 0, textDecoration: "underline" }}>
            Take the tour again
          </button>
        </div>
      )}

      <NewSaleReview matter={matter} onReviewed={onSaleReviewed} />

      {drawRibbon()}
      {/* ── 1. The matter bar ─────────────────────────────────────── */}
      {/* It publishes its own height — see `useStickyBarHeight`. Everything
          that has to clear it reads that rather than repeating a guess.

          **The top pull is there to cancel the pane's own padding**, so the
          bar sits flush when it is the first thing in the pane. With the
          sample banner above it there is nothing to cancel, and the -24 ate
          the banner instead: the bar is 93% opaque and sits at `zIndex: 60`,
          so it painted over the banner's last 12px and squared off its bottom
          corners. The banner looked like a box with two rounded corners.

          Left and right keep the pull either way — that is the full-bleed,
          and it has nothing to do with what is above. */}
      {/* **In the practice look the bar goes**: its status is on the tab, its
          facts and links are in the matter header, and its actions are the
          ribbon's (More holds the overflow). */}
      {!ribbonNav && (
      <div ref={matterBarRef} style={{ position: "sticky", top: 0, zIndex: 60, background: "rgba(239,239,239,0.93)", backdropFilter: "blur(8px)", margin: bannerAbove ? "0 -24px 16px" : "-24px -24px 16px", padding: "20px 24px 14px", ...(embedded ? { margin: "0 -24px 16px", padding: "8px 24px 14px" } : {}),
        // As a tab of the matter on its own site, a toolbar: no frosted band
        // of the classic page's grey, which read as a second header under the
        // matter's own. In the WordPress frame it stays as it has been.
        ...(tidy ? { position: "static", background: "transparent", backdropFilter: "none", margin: "0 0 12px", padding: "0 0 12px" } : {}),
        display: "flex", alignItems: tidy ? "center" : "flex-end", gap: 16, flexWrap: "wrap" }}>
        {/* **The rule is inset to the content, not the bar.** The bar bleeds
            to the page edge so its frosted ground covers what scrolls under it,
            and a border on it ran 24px past the tabs' own line at each end —
            two rules of different lengths one above the other. */}
        <div aria-hidden="true" data-bar-rule style={{ position: "absolute", left: tidy ? 0 : 24, right: tidy ? 0 : 24, bottom: 0, height: 1, background: T.border }} />
        {/* **A breadcrumb, the address, and who is on it.** The way back is the
            first thing on the bar and says where it goes, as a trail rather
            than a button in front of the name. The address leads because it
            is how the office names a matter; the client, the file handler, the
            agent and the date are the line under it, where the facts card in
            the left column used to keep them a glance away from the name. */}
        <div style={{ flex: 1, minWidth: 260, ...(tidy ? { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" } : {}) }}>
          {/* **As the main module's Section 32 tab, the matter is already
              named** in the header above it — the way back is the matter tabs,
              and the address is the header's. What stays is what this page
              adds: its status, its facts and its actions. */}
          {!embedded && <nav aria-label="Breadcrumb" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: T.textFaint, marginBottom: 6 }}>
            {/* **The first crumb is the way back, and looks it.** A chevron and
                ink rather than grey, so it reads as the press it is rather than
                as a label beside the reference. */}
            <button type="button" onClick={onBack} className="s32-link"
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: T.text, fontSize: 12.5, fontWeight: 500, fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
              {matter.archived ? "Archived" : "Matters"}
            </button>
            <span aria-hidden="true" style={{ color: T.borderMid }}>/</span>
            <span style={{ color: T.textSoft }}>{matter.ref}</span>
          </nav>}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {tidy ? null : embedded
              ? <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: T.text, lineHeight: 1.2 }}>{CONTRACT_TAB_LABEL}</h2>
              : <h1 style={{ margin: 0, fontSize: 24, fontWeight: 300, letterSpacing: "-0.02em", color: T.text, lineHeight: 1.2 }}>{matter.address || matter.client || matter.ref}</h1>}
            <Badge status={matter.status} />
            {matter.archived && <span style={{ fontSize: 11, color: T.textFaint, background: "rgba(158,143,132,0.14)", padding: "2px 8px", borderRadius: 999, fontWeight: 500 }}>Archived</span>}
            {saysYes(matter.has137b) && <span style={{ fontSize: 11, color: T.text, background: T.white, border: `1px solid ${T.text}`, padding: "2px 8px", borderRadius: 999, fontWeight: 500 }}>137b report</span>}
            {saysYes(matter.hasMaterialFacts) && <span style={{ fontSize: 11, color: T.red, background: "rgba(254,84,66,0.08)", padding: "2px 8px", borderRadius: 999, fontWeight: 500 }}>Material facts</span>}
          </div>
          <div data-tour="matter-facts" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: tidy ? 0 : 4, fontSize: 12.5, color: T.textSoft }}>
            {/* **The agent says it is the agent.** Two names and then a third
                read as client, file handler and *somebody* — the third could be
                a co-owner as easily as the agency. A faint word is enough; the
                agency's full name would not fit, so it is the hover. */}
            {[
              // The matter's header already names the client and the file
              // handler; on the tidy tab only what this page adds stays.
              { text: matter.address && !tidy ? matter.client : null },
              { text: tidy ? null : matter.fileHandlerName },
              { text: agentDisplay(matter).name, label: "Agent", title: agentDisplay(matter).office || undefined },
              { text: matter.openedDate ? `opened ${formatOpenedDate(matter.openedDate)}` : null },
            ]
              .filter(fact => typeof fact.text === "string" && fact.text.trim())
              .map((fact, i) => (
                <span key={i} style={{ display: "inline-flex", gap: 12 }}>
                  {i > 0 && <span aria-hidden="true" style={{ color: T.borderMid, marginLeft: -6 }}>·</span>}
                  <span title={fact.title}>
                    {fact.label && <span style={{ color: T.textFaint, marginRight: 5 }}>{fact.label}</span>}
                    {fact.text}
                  </span>
                </span>
              ))}
            <MatterLinks matter={matter} tenant={tenant} inline />
          </div>
        </div>

        {/* **The actions are always here, beside the name.** They were in the
            headline card and copied into this bar once the card scrolled away
            — two copies of one array, one of them invisible. One copy, always
            on screen, is simpler and is where a press is looked for.

            **Marked for the tour only where Release is one of them**, so a
            row carrying Compile alone never puts the spotlight on the wrong
            button under wording about releasing. */}
        <div {...((step.actions || []).includes("release") ? { "data-tour": "release" } : {})}
          style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {buttons}
          <OverflowMenu items={[
            // **First, and never disabled.** It is the one item here for
            // somebody who does not yet know what the others do, and it is
            // where the nudge sends a person who said *not now* — so the offer
            // being one-shot costs nothing.
            { label: "Take the tour", onClick: startTour },
            "divider",
            // Only once there is something to read. Before the searches are
            // back this would merge whatever had arrived and call that the
            // certificates.
            (matter.returnedCount || 0) > 0 && { label: "Re-read certificates", onClick: onReread, disabled: busy },
            { label: ARRANGE_LABEL, onClick: () => arrangeHere(), disabled: busy },
            // **The one screen in the product nobody in the office ever sees.**
            // The agent's page is reachable only through their own link, minted
            // at the moment of release — so its wording shipped unlooked-at and
            // its first reader was a client's estate agent. A new tab rather
            // than a modal: it is a page, and it is worth looking at at the
            // size an agent will.
            matter.compileVersion && {
              label: "Preview the agent's page",
              onClick: () => window.open(apiUrl(`/agent-preview/${matter.ref}`), "_blank", "noopener"),
            },
            // And the page the agent's purchasers get, which is the next thing
            // nobody in the office would otherwise ever see.
            matter.compileVersion && {
              label: "Preview the purchaser page",
              onClick: () => window.open(apiUrl(`/purchaser-preview/${matter.ref}`), "_blank", "noopener"),
            },
            // **Only while the link still works.** An agent who left the
            // agency, a link forwarded, a release to the wrong address. Moving
            // the matter to another agent does this by itself; this is for
            // everything else.
            onRevokeAgentLink && agentLinkLive(matter) && "divider",
            onRevokeAgentLink && agentLinkLive(matter) && {
              label: "Switch off the agent's download link",
              onClick: () => onRevokeAgentLink(matter.ref),
            },
          ]} />
        </div>
      </div>
      )}

      {/* ── 2. One next step ──────────────────────────────────────── */}
      <div ref={cardRef} data-tour="next-step">
      {/* The last action's outcome, where its progress banner was — a corner
          toast is gone in seconds. Cleared when the next action starts. */}
      {step.kind !== "busy" && matter.lastActionNotice && (
        <div style={{
          ...actionNoticeStyle(matter.lastActionNotice.tone),
          borderRadius: 10, padding: "10px 14px", marginBottom: 12,
          fontSize: 12, fontWeight: 500,
          display: "flex", alignItems: "center", gap: 9,
        }}>
          {matter.lastActionNotice.busy && <NoticeSpinner />}
          {matter.lastActionNotice.text}
        </div>
      )}
      {step.kind === "busy" ? (
        <CompilingBanner
          startedAt={reading ? readingStartedAt(matter) || compileStartedAt : compileStartedAt}
          isFirst={!matter.compileVersion}
          phase={reading ? "reading" : "merging"}
          // **Only the bundle path rebuilds on its own.** This read
          // `awaitingExtractionTrigger`, which only the whole-pack pass ever
          // sets — so a per-document read, which sets no matter-level flag at
          // all, came out `undefined !== "reread"` and the banner promised a
          // contract that was never going to be built. It has to be a wait
          // this matter is actually in, not merely one it is not.
          rebuilds={Boolean(matter.awaitingExtractionSince)
            && matter.awaitingExtractionTrigger !== "reread"}
          onStop={onStop ? () => onStop(reading ? "read" : "compile") : null}
          stopping={stopping}
          // **Either half is enough.** The local one is set the moment the
          // stop route answers, so the button acknowledges the press before
          // the next poll; the matter's own is the middleware applying
          // `isStopRequested`, which is what covers a stop asked from another
          // tab and what stops a stale stamp reading as a pending stop.
          stopRequested={Boolean(stopRequested || matter.compile?.stopRequested)}
        />
      ) : statusCardShown(pane) && (
        // **One white card, not a grey panel with white cards in it.** The
        // neutral headline is white like everything else on the page, and the
        // strip is the bottom of the same card behind a hairline, its presses
        // divided by rules rather than boxed. A tone that means something —
        // the document warning, a failure, released — keeps its own colour.
        <div style={{ background: cardTone ? cardTone.bg : T.white, border: `1px solid ${cardTone ? cardTone.border : T.border}`, borderBottom: cardTone?.foot || `1px solid ${cardTone ? cardTone.border : T.border}`, borderRadius: 12, marginBottom: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px" }}>
          {/* **Compact, because it is read every visit.** It stood a hundred
              pixels tall over the document on every matter, the heaviest thing
              on the page whatever it said. The words are unchanged — only the
              padding, and the buttons centred on the text rather than hanging
              from its top. */}
            <div style={{ fontSize: 13.5, color: cardTone ? cardTone.color : T.text, fontWeight: cardTone?.foot ? 600 : 500, marginBottom: 2 }}>{step.title}</div>
            {(step.gaps?.length || 0) > 1 ? (
              <div style={{ fontSize: 12.5, color: T.textSoft, lineHeight: 1.5, maxWidth: "96ch" }}>
                <ul data-step-gaps style={{ listStyle: "none", margin: "2px 0 4px", padding: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  {step.gaps.map(g => (
                    <li key={g} style={{ display: "flex", gap: 8, alignItems: "baseline", color: T.text }}>
                      <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: "50%", background: T.textSoft, flex: "none", transform: "translateY(-2px)" }} />
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
                {FILL_IN_FROM_DRAFT}
              </div>
            ) : (
              <div style={{ fontSize: 12.5, color: T.textSoft, lineHeight: 1.5, maxWidth: "96ch" }}>{step.body}</div>
            )}
          </div>
          {cardNotices.map(n => (
            <div key={n.key} data-card-notice={n.key} style={{ borderTop: `1px solid ${cardTone ? cardTone.border : T.border}`, padding: "10px 16px" }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: T.text, marginBottom: 2 }}>{n.title}</div>
              <div style={{ fontSize: 12.5, color: T.textSoft, lineHeight: 1.5, maxWidth: "96ch", wordBreak: "break-word" }}>{n.detail}</div>
            </div>
          ))}
          {/* What the matter is waiting on, each a press to where it is
              answered. Under the headline rather than instead of it: the
              headline is the one sentence about the document, and these are
              the work. */}
          {issues.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", borderTop: `1px solid ${cardTone ? cardTone.border : T.border}` }}>
              {/* **One press is half the card, not all of it.** Two sit side
                  by side at half each; one alone stretched across the whole
                  card and put its View a screen's width from the words it
                  belongs to. So a lone one keeps the width it would have beside
                  the other, closed off by a hairline — and on a phone, where
                  half is too narrow to read, it takes the row. */}
              {issues.map((it, i) => (
                <button key={it.key} type="button" onClick={it.go} className="s32-issue"
                  data-issue={it.key}
                  style={{ flex: "1 1 300px", maxWidth: issues.length === 1 ? "max(50%, min(100%, 420px))" : undefined, display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", borderLeft: i > 0 ? `1px solid ${cardTone ? cardTone.border : T.border}` : "none", borderRight: issues.length === 1 ? `1px solid ${cardTone ? cardTone.border : T.border}` : "none", borderRadius: 0, padding: "11px 16px", cursor: "pointer", fontFamily: "Inter, sans-serif", textAlign: "left", minWidth: 0 }}>
                  <span style={{ flexShrink: 0, minWidth: 20, height: 20, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, background: it.tone === "red" ? "rgba(254,84,66,0.12)" : T.bgAlt, color: it.tone === "red" ? T.red : T.text }}>{it.count}</span>
                  <span style={{ fontSize: 12.5, color: T.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {it.label}{it.detail && <span style={{ color: T.textSoft }}> — {it.detail}</span>}
                  </span>
                  <span style={{ marginLeft: "auto", flexShrink: 0, fontSize: 12.5, fontWeight: 500, color: T.text }}>{it.press} →</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      </div>

      {/* Everything else that is true of this matter. Quieter than the
          headline, and never dropped — see `nextStep`. */}
      {ownNotices.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          {ownNotices.map(n => {
            const nt = NOTICE_TONE[n.tone] || NOTICE_TONE.ink;
            return (
              <div key={n.key} style={{ background: nt.bg, border: `1px solid ${nt.border}`, borderBottom: nt.foot || `1px solid ${nt.border}`, borderRadius: 10, padding: "11px 14px" }}>
                <div style={{ fontSize: 12, fontWeight: nt.foot ? 600 : 500, color: nt.color, marginBottom: 3 }}>{n.title}</div>
                <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, wordBreak: "break-word" }}>{n.detail}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── 3 & 4. The rail, and the panes ────────────────────────── */}
      <div className={ribbonNav ? undefined : "s32-cols"}>
        {/* **Under the bar's measured height, never a constant.** It was
            `top: 84` — the number the segment bar was moved off for the same
            reason — and the bar is taller than that once the facts line sits
            under the address (and taller again when it wraps), so the rail
            stuck behind it and *Contract* scrolled out from under a
            translucent wash. UNDER_BAR also lines the rail up with the
            document's own sticky toolbar beside it. */}
        {ribbonNav ? (notesOpen && <NotesModal value={matter.notes} onChange={onNotesChange} onClose={() => setNotesOpen(false)} />) : (
        <div className="s32-rail" style={{ position: "sticky", top: UNDER_BAR, display: "flex", flexDirection: "column", gap: 10 }}>
          {/* **On the page, not in a card.** The rail is navigation, and a white
              card made it read as one more panel of content beside the panels
              it switches between. The current pane is the white row. */}
          <div data-tour="rail" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* ── A pill means look here, and nothing else ───────────────
                **It read `Complete` on the Contract tab**, once nothing
                required was blank — which is not what that word says beside a
                label reading *Contract*. The contract is very often not
                complete at that moment: it may be uncompiled, short a
                certificate, or unsigned. What was actually true is far
                narrower, and the panel still says it under its own title —
                *Contract data · ✓ Complete* — where the word is qualified by
                the thing it is about. The rail had no such qualifier and could
                not have one in two words.

                **And a green pill on the healthy matter is furniture**, the
                standing rule here about a row nobody can clear — with the
                extra cost that it sat beside the red one and competed with it
                for the eye. So the all-clear goes from *both* tabs rather than
                only the one that was wrong: one green and one absent would
                make the absence read as *not checked yet*.

                What is given up is the rail distinguishing *nothing to do*
                from *not computed yet*. That second state is the moment before
                the matter read lands, and the panel one click away says which
                check actually ran. */}
            {[
              { key: "contract", label: "Contract", pill: missingCount > 0 ? { tone: "red", text: `${missingCount} needed` } : null },
              // The pane holds the searches review and the search list — the
              // contract and its compiled PDF live under Contract. The key is
              // the stored pane name and does not move; only the label does.
              { key: "documents", label: "Searches", pill: reviewCount > 0 ? { tone: "red", text: `${reviewCount} to look at` } : null, count: total > 0 ? `${done}/${total}` : "" },
              // **No pill, ever.** A rail pill means *look here*, and a count
              // of letters already sent is not something to act on — the
              // standing rule about a row nobody can clear, and a number
              // beside "Email" would compete with the red one for the eye.
              { key: "email", label: "Email", pill: null },
              { key: "activity", label: "Activity", pill: null },
            ].map(item => (
              <button key={item.key} onClick={() => setPane(item.key)} aria-current={pane === item.key}
                className={pane === item.key ? undefined : "s32-railitem"}
                style={{ display: "flex", width: "100%", gap: 8, alignItems: "center", background: "none", boxShadow: pane === item.key ? `inset 2px 0 0 ${paneMark}` : "none", border: "none", cursor: "pointer", padding: "9px 10px 9px 12px", borderRadius: 0, fontSize: 13, color: pane === item.key ? T.text : T.textSoft, fontWeight: pane === item.key ? 500 : 400, fontFamily: "Inter, sans-serif", textAlign: "left" }}>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.pill ? <RailPill tone={item.pill.tone}>{item.pill.text}</RailPill>
                  : item.count ? <span style={{ fontSize: 12, color: T.textFaint }}>{item.count}</span> : null}
              </button>
            ))}
          </div>

          {/* **Things to open, under the panes.** Notes and Arrange documents
              are not panes — one opens a box, the other a screen of its own —
              so they sit under a rule, styled like the rows above so the rail
              reads as one list. Notes says when there are some, since a link
              cannot show its text the way the open box did. */}
          <div style={{ height: 1, background: T.border, margin: "4px 10px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[
              { key: "notes", label: "Notes", onClick: () => setNotesOpen(true),
                aside: String(matter.notes || "").trim()
                  ? <span aria-label="Has notes" title={String(matter.notes).trim().slice(0, 140)} style={{ width: 6, height: 6, borderRadius: 999, background: T.textFaint }} />
                  : null },
              { key: "arrange", label: ARRANGE_LABEL, onClick: () => onArrangeDocuments(), disabled: busy },
              // The sample matter reaches nobody outside the office, the SRO
              // included.
              ...((matter.ref ?? matter.id) === sampleRef ? [] : [{ key: "duties", label: "Duties form", onClick: openDutiesForm, disabled: dutiesCode === "busy",
                aside: dutiesCode === "opened" ? <span style={{ fontSize: 12, color: T.textFaint }}>Opened</span>
                  : dutiesCode === "copied" ? <span title="The extension isn't installed here: paste the code into its panel" style={{ fontSize: 12, color: T.textFaint }}>Code copied</span>
                  : dutiesCode === "failed" ? <span style={{ fontSize: 12, color: T.red }}>Couldn't open</span>
                  : dutiesStatus?.state === "missing"
                    // An extension's puzzle piece, struck through: the row's
                    // own words stay short, and the tooltip says the rest.
                    ? <svg role="img" aria-label="Extension not installed" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.textFaint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <title>Extension not installed</title>
                        <path d="M10 4a2 2 0 1 1 4 0v2h4v4h2a2 2 0 1 1 0 4h-2v4h-4v-2a2 2 0 1 0-4 0v2H6v-4h2a2 2 0 1 0 0-4H6V6h4z" />
                        <line x1="3" y1="3" x2="21" y2="21" />
                      </svg>
                    : null,
                title: dutiesTooltip(dutiesStatus) }]),
            ].map(item => (
              <button key={item.key} type="button" onClick={item.onClick} disabled={item.disabled} title={item.title} className="s32-railitem"
                style={{ display: "flex", width: "100%", gap: 8, alignItems: "center", background: "none", border: "none", cursor: item.disabled ? "default" : "pointer", opacity: item.disabled ? 0.5 : 1, padding: "9px 10px 9px 12px", borderRadius: 0, fontSize: 13, color: T.textSoft, fontFamily: "Inter, sans-serif", textAlign: "left" }}>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.aside}
              </button>
            ))}
          </div>
          {notesOpen && <NotesModal value={matter.notes} onChange={onNotesChange} onClose={() => setNotesOpen(false)} />}
          {dutiesHelp && <DutiesHelpModal outcome={dutiesHelp} matterRef={matter.ref ?? matter.id} onClose={() => setDutiesHelp("")} />}

          {/* Archiving stays a deliberate trip to the bottom of the rail
              rather than moving into the menu at the top. */}
          <MatterActions archived={matter.archived} onArchive={onArchive} onUnarchive={onUnarchive} />
        </div>
        )}

        <div>
          <Pane active={pane === "contract"}>
            {/* ── The document leads ─────────────────────────────────────
                **Ordered by the job rather than by the pipeline.** The
                commonest visit to a compiled matter is to read the PDF the
                autocompile produced — and it was fourth down the pane, behind
                three panels, reached by scrolling past a second frame of the
                same document. That scroll is where *which one am I in* came
                from, so the order is most of the fix. What follows it is what
                you reach for once you have read it: the data, the wording,
                the signing. */}
            <ContractDocument
              matter={matter}
              hasCompiled={hasCompiled}
              segment={segment}
              onSegment={chooseSegment}
              innerRef={documentRef}
              compiled={hasCompiled ? (
                <CompiledDocumentPanel embedded matter={matter} compiling={compiling} defaultOpen={defaults.compiled} stage={matter.status} expandSignal={compiledExpand} actions={compiledActions} modalActions={modalActions}
                  peek={contractPeek(missingCount)}
                  onChanged={onSignaturesChanged}
                  lotDefaultOpen={Boolean(matter.lotSearch?.record
                    && matter.lotSearch.record.found
                    && !matter.lotSearch.record.approved
                    && !matter.lotSearch.record.rejected)} />
              ) : null}
              draft={(
                <ContractPreview
                  embedded
                  peek={contractPeek(missingCount)}
                  defaultOpen={defaults.preview}
                  stage={matter.status}
                  matterRef={matter.ref}
                  registry={fieldRegistry}
                  contractFields={matter.contractFields}
                  specialConditions={matter.specialConditions}
                  clauseOverrides={matter.clauseOverrides}
                  clausesRemoved={matter.clausesRemoved}
                  onRemoveClause={onRemoveClause}
                  generatedAttachments={matter.generatedAttachments}
                  searches={matter.searches}
                  agentDirectory={matter.agentDirectory}
                  onAdoptAgent={(choice) => onAdoptAgent(matter.ref, choice)}
                  adoptingAgent={adoptingAgent}
                  onSaveToDirectory={(name) => onSaveToDirectory(matter.ref, name)}
                  onSave={onSaveField}
                  onSaveFields={onSaveFields}
                  onSaveClause={onSaveClause}
                  onRevert={onRevertField}
                  savingFields={savingFields}
                  savingClause={savingClause}
                  goTo={draftTarget}
                  onJump={(where) => {
                    // One target today. Named rather than a bare callback so a
                    // second pointer — the guarantee, the due diligence checklist —
                    // is a case here rather than a second prop.
                    // A clause added to a block is edited from the same
                    // section, beside the block's own row.
                    if (where === "specialConditions" || where === "blockClauses") setConditionsReveal(n => n + 1);
                  }}
                />
              )}
            />
            <ContractDataPanel
              reviewOnly={ribbonNav}
              matterRef={matter.ref}
              revealSignal={dataReveal}
              documentReview={matter.documentReview}
              defaultOpen={defaults.contractData}
              stage={matter.status}
              registry={fieldRegistry}
              contractFields={matter.contractFields}
              agentDirectory={matter.agentDirectory}
              onAdoptAgent={(choice) => onAdoptAgent(matter.ref, choice)}
              onSaveToDirectory={(name) => onSaveToDirectory(matter.ref, name)}
              adoptingAgent={adoptingAgent}
              onSave={onSaveField}
              onRevert={onRevertField}
              onRevertAll={() => onRevertAllFields(matter.ref)}
              onNotRequired={(group) => onNotRequired(matter.ref, group)}
              markingNotRequired={markingNotRequired}
              revertingAll={revertingAll}
              savingFields={savingFields}
            />
            {/* **The searches review is the contract's, in the practice look**:
                it says why a field the contract prints is blank or disputed,
                so it sits with the contract data rather than the search list. */}
            {ribbonNav && (
              <SearchesReview
                review={matter.documentReview}
                registry={fieldRegistry}
                defaultOpen
                stage={matter.status}
                matterRef={matter.ref ?? matter.id}
                searches={searches}
              />
            )}
            {/* Grouped the same way as the old layout, and for the same
                reason — see `CustomisationSection`. **A panel added to one
                layout has to be added to the other**, and so does the section
                around them: git conflicts on a changed signature and says
                nothing about a grouping that only happened once. Asserted in
                both. */}
            <CustomisationSection
              conditionCount={(matter.specialConditions || []).length}
              revealSignal={conditionsReveal}
            >
              <SpecialConditionsPanel
                conditions={matter.specialConditions}
                matterRef={matter.ref}
                onSave={(conditions) => onSaveSpecialConditions(matter.ref, conditions)}
                saving={savingConditions}
                revealSignal={conditionsReveal}
                blocks={matter.contractFields?.precedentBlocks}
                blockClauses={matter.blockClauses}
                onSaveBlockClauses={(block, clauses) => onSaveBlockClauses(matter.ref, block, clauses)}
                savingBlockClauses={savingBlockClauses}
                registry={fieldRegistry}
                contractFields={matter.contractFields}
                onSaveFields={onSaveFields}
                onSaveField={onSaveField}
                onRevertField={onRevertField}
                savingFields={savingFields}
              />
              {matter.contractFields?.values?.["s32.hasOwnersCorporation"] === true && (
                <OcCertificatePanel
                  matterRef={matter.ref}
                  searchesKey={(matter.searches || []).map(s => s.id).join(",")}
                  onGenerated={onOcCertificateDrafted}
                />
              )}
            </CustomisationSection>
            {/* **Signing sits between compiling and releasing**, which is where
                it reads: in Victoria the purchaser must be given a statement the
                vendor has already signed. Shown once there is something to sign
                or somebody has been asked — before a compile there is no
                document and the panel could only refuse. */}
            {/* **The lot moved into the compiled contract panel**, where the
                sheets it names are pages of the document in the frame. It was a
                section of its own for a few days, which meant reading a page
                number on one card and finding it on another — and one more
                section on a screen that already has plenty. */}
            {!ribbonNav && (matter.compileVersion || matter.signatures?.asked) && (
              <SigningPanel matter={matter} stage={matter.status}
                defaultOpen={Boolean(matter.signatures?.stale)}
                onChanged={onSignaturesChanged} />
            )}
            {/* **Who the agent has shared it with**, once it has gone out — a
                purchaser is given what the agent was given, so before a release
                there is nothing to list. See `PurchasersPanel`. */}
            {!ribbonNav && matter.status === "released" && (
              <PurchasersPanel matter={matter} stage={matter.status} />
            )}
          </Pane>

          {/* The ribbon's Signing: the same two panels, on their own and open. */}
          {ribbonNav && (
            <Pane active={pane === "signing"}>
              {(matter.compileVersion || matter.signatures?.asked) ? (
                <SigningPanel matter={matter} stage={matter.status} defaultOpen onChanged={onSignaturesChanged} />
              ) : (
                <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 12, fontSize: 13, color: T.textSoft }}>
                  The vendor signs the compiled statement — compile the contract first, then send it for signing from here.
                </div>
              )}
              {matter.status === "released" && (
                <PurchasersPanel matter={matter} stage={matter.status} defaultOpen />
              )}
            </Pane>
          )}

          <Pane active={pane === "documents"}>
            {!ribbonNav && (
              <SearchesReview
                review={matter.documentReview}
                registry={fieldRegistry}
                defaultOpen={defaults.searches}
                stage={matter.status}
                matterRef={matter.ref ?? matter.id}
                searches={searches}
              />
            )}
            {/* **With the documents, in both layouts.** The report is a
                document the pack is waiting on, and the searches review just
                above is where the owner-builder works are flagged — whose
                button opens this. */}
            {/* **On every matter**, not only those whose client form
                declared works: a matter with no client form at all, or works
                the vendor mentioned only on the phone, is asked the same way. */}
            <Report137bPanel matter={matter} stage={matter.status} />
            <SearchesPanel
              matter={matter}
              onOrderSearches={ribbonNav ? undefined : onOrderSearches}
              orderSearchesSoon={ribbonNav}
              searches={searches}
              setSearches={setSearches}
              searchesLoading={searchesLoading}
              allDone={allDone}
              busy={busy}
              busyLabel={reading ? "Reading the certificates…" : compiling ? "Compiling…" : null}
              merging={merging}
              onAddDocument={onAddDocument}
              onDeleteDocument={onDeleteDocument}
              onReplaceDocument={onReplaceDocument}
              onRenameDocument={onRenameDocument}
              onReorderSearches={onReorderSearches}
              onPlaceReturn={onPlaceReturn}
              onAddReturn={onAddReturn} onDismissReturn={onDismissReturn}
              searchInbox={tenant?.searchInbox || ""}
              uploading={uploading}
              defaultOpen={defaults.searches}
              stage={matter.status}
              onArrangeDocuments={ribbonNav ? null : onArrangeDocuments}
              onExcludeSearch={onExcludeSearch}
              onSearchStatusChange={onSearchStatusChange}
              revealSignal={searchesReveal}
            />
          </Pane>

          {!ribbonNav && <Pane active={pane === "email"}>
            <SentEmails
              matterRef={matter.ref}
              refreshKey={`${matter.status || ""}_${matter.compileVersion || 0}_${matter.releasedVersion || 0}`}
            />
          </Pane>}

          <Pane active={pane === "activity"}>
            <Timeline
              matterRef={matter.ref}
              refreshKey={`${matter.status || ""}_${matter.compileVersion || 0}_${matter.returnedCount || 0}`}
              defaultOpen
            />
          </Pane>
        </div>
      </div>
    </div>
  );
};

// What the service does on its own once a compile finishes with nobody
// watching. Two switches, one settings document, one endpoint.
//
// **Each reads its absence the way the middleware does**, and the two are
// opposite on purpose: introducing `autoCompile` must not change what a matter
// *does*, so unset is on; introducing the notice must not start sending mail to
// people who never asked, so unset is off. `describeAutomation` already answers
// real booleans, so these coercions only ever matter against an older
// middleware — and each one then lands on the same answer that one would have
// given, which is the point of mirroring them rather than picking one.
export const AUTOMATION_SWITCHES = [
  {
    key: "autoCompile",
    read: (a) => a?.autoCompile !== false,
    label: "Compile automatically when all searches are accounted for",
    on: "A matter compiles as soon as the last certificate returns, or as soon as you mark the last outstanding search not required.",
    off: "Matters still complete and wait for you — open one and press Compile. Re-compile and re-read are unaffected.",
    toast: { on: "Automatic compiling on", off: "Automatic compiling off" },
  },
  // **One switch for both halves of one question**, which is how the firm asked
  // for it: tell me when a matter needs me. The name is the middleware's key
  // and understates what it governs — renaming a key Firestore holds is a
  // migration for a word — so the label is what says so.
  // **Off unless turned on**, like the notice: it emails clients.
  {
    key: "autoReport137b",
    read: (a) => a?.autoReport137b === true,
    label: "Email the client the section 137B questionnaire when their client form shows owner-builder work",
    on: "When a client first submits their client form (Form 27) and it lists work they did or arranged themselves that needs, or may need, a report, they are emailed the questionnaire straight away, with the file handler copied. Once per matter, and never after the contract is released. Opening a matter or editing a form never sends it.",
    off: "Never sent automatically. Staff send it from the Section 137B panel on the matter’s Documents tab.",
    toast: { on: "137B questionnaire sends automatically", off: "137B questionnaire left to staff" },
  },
  {
    key: "notifyContractReady",
    read: (a) => a?.notifyContractReady === true,
    label: "Email the file handler when a matter needs them",
    on: "The conveyancer on the matter is emailed once, with a link to it, when a compile nobody was watching produces a contract — and again when every search is accounted for and nothing is going to compile it. A pack that came out searches-only says so rather than saying ready. The office is copied on both.",
    off: "Nothing is emailed. A matter waits on the dashboard until somebody opens it.",
    toast: { on: "Matter notices on", off: "Matter notices off" },
  },
];

export const automationToast = (key, on) => {
  const sw = AUTOMATION_SWITCHES.find(s => s.key === key);
  return (on ? sw?.toast?.on : sw?.toast?.off) || "Settings saved";
};

const AutomationSettings = ({ onSaved, onError }) => {
  const [automation, setAutomation] = useState(null);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiFetch("/settings/automation")
      .then(r => r.json())
      .then(data => { if (!cancelled && data?.ok) setAutomation(data.automation || {}); })
      .catch(err => console.error("Failed to load automation settings", err));
    return () => { cancelled = true; };
  }, []);

  // **Only the switch that was flipped is sent.** The endpoint merges, so a
  // POST of both would have one screen answering for a setting the other half
  // of this card is showing — and the value sent would be whatever this
  // component last read rather than what is stored.
  const flip = async (key, next) => {
    const previous = automation;
    setAutomation({ ...automation, [key]: next });
    setSaving(key);
    try {
      const res = await apiFetch("/settings/automation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: next }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Save returned ${res.status}`);
      setAutomation(data.automation || {});
      onSaved?.(key, next);
    } catch (err) {
      setAutomation(previous);
      onError?.(err.message);
    } finally {
      setSaving("");
    }
  };

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>Automatic work</div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 12px" }}>
        What happens on its own once every search on a matter is accounted for.
      </p>

      {automation === null ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>
      ) : AUTOMATION_SWITCHES.map((sw, i) => {
        const on = sw.read(automation);
        const busy = saving === sw.key;
        return (
          <div key={sw.key} style={i ? { marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` } : undefined}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: busy ? "wait" : "pointer", fontSize: 13, color: T.text }}>
              <input
                type="checkbox"
                checked={on}
                disabled={Boolean(saving)}
                onChange={e => flip(sw.key, e.target.checked)}
                style={{ width: 15, height: 15, cursor: busy ? "wait" : "pointer", accentColor: T.brown, margin: 0 }}
              />
              {sw.label}
            </label>
            <p style={{ fontSize: 11, color: T.textFaint, margin: "8px 0 0", paddingLeft: 23 }}>
              {on ? sw.on : sw.off}
            </p>
          </div>
        );
      })}
    </div>
  );
};

// ── Firm settings ──────────────────────────────────────────────────
// The solicitor acting for the vendor. One record for the whole service, not
// ten merge fields repeated on every matter — and until it's filled in, every
// firm.* field shows as missing on every matter.

const FIRM_FIELDS = [
  { key: "name", label: "Firm name", required: true },
  { key: "contactName", label: "Contact name" },
  { key: "streetAddress", label: "Street address", required: true, multiline: true,
    hint: "Two lines if you write it that way — the contract cover keeps the break." },
  { key: "postalAddress", label: "DX or PO address" },
  { key: "email", label: "Email", required: true,
    hint: "The fallback for a matter with no file handler appointed. The handler's own address wins where there is one." },
  { key: "phone", label: "Phone" },
  { key: "mobile", label: "Mobile" },
  // **The letterhead, and it is the office's rather than the matter's.** These
  // print on the contract's cover page and nowhere else. `email` above is the
  // *contract's* address and resolves to the file handler on the matter, which
  // is right there and wrong on a letterhead — so the cover has its own, and
  // the hint says so rather than leaving somebody to wonder why there are two.
  { key: "abn", label: "ABN", hint: "Prints on the contract cover." },
  { key: "licenceNumber", label: "Conveyancing licence number", hint: "Prints on the contract cover." },
  { key: "officeEmail", label: "Office email", hint: "The address on the contract cover — the office's, not the file handler's." },
  { key: "website", label: "Website", hint: "Prints on the contract cover." },
];

// How a window's two ends are written, and what that means.
//
// `MM-DD` recurs every year; `YYYY-MM-DD` happens once. The distinction is
// worth showing rather than hiding, because a closure written as fixed dates
// has to be rewritten every December — and the December somebody forgets is
// the December the clause silently stops printing.
export const describeSeasonalWindow = ({ from, to } = {}) => {
  const full = /^\d{4}-\d{2}-\d{2}$/;
  const day = /^\d{2}-\d{2}$/;
  const ok = v => full.test(v || "") || day.test(v || "");

  if (!ok(from) || !ok(to)) return "Set both dates for the clause to appear.";
  if (full.test(from) !== full.test(to)) {
    return "Both dates must be written the same way — either MM-DD or YYYY-MM-DD.";
  }
  // A date the calendar does not have — 31 June, or 29 February in a year
  // without one. Recurring dates may be 29 February: it simply opens on the
  // 1st of March in the other three years.
  const real = (v) => {
    const [y, m, d] = (full.test(v) ? v : `2024-${v}`).split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.getMonth() === m - 1 && dt.getDate() === d;
  };
  if (!real(from) || !real(to)) return "One of those dates does not exist.";
  if (full.test(from)) {
    return to < from ? "The end date is before the start date." : "Applies once, between these dates.";
  }
  // A recurring window reading "backwards" is the ordinary closure, not a typo.
  return from <= to
    ? "Repeats every year, between these dates."
    : "Repeats every year, running from December into the new year.";
};

// **Whether a window can be saved** (DA-12). The middleware refuses a malformed
// date and a one-off ending before it starts, but it takes a window with only
// one end set — which is stored and then never prints, the silent December
// this section exists to prevent. So a window is saveable when both ends make
// sense, or when both are cleared, which is how the clause is switched off.
export const seasonalWindowOk = ({ from, to } = {}) => {
  if (!(from || "").trim() && !(to || "").trim()) return true;
  return /^(Repeats every year|Applies once)/.test(describeSeasonalWindow({ from, to }));
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// The window as dates somebody reads, not two codes to work out: the one
// running now, or the next one. A one-off that has already ended says so.
// `today` is a parameter so the test can stand on a fixed day.
export const nextSeasonalWindow = ({ from, to } = {}, today = new Date()) => {
  if (!(from || "").trim() || !seasonalWindowOk({ from, to })) return "";
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const at = (y, mmdd) => {
    const [m, d] = mmdd.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  const fmt = (dt) => `${dt.getDate()} ${MONTHS_SHORT[dt.getMonth()]} ${dt.getFullYear()}`;
  let start; let end;
  if (/^\d{4}-/.test(from)) {
    start = at(+from.slice(0, 4), from.slice(5));
    end = at(+to.slice(0, 4), to.slice(5));
    if (end < day) return `Ended ${fmt(end)}.`;
  } else {
    const y = day.getFullYear();
    const wraps = from > to;
    start = at(wraps && day <= at(y, to) ? y - 1 : y, from);
    end = at(start.getFullYear() + (wraps ? 1 : 0), to);
    if (end < day) {
      start = at(start.getFullYear() + 1, from);
      end = at(end.getFullYear() + 1, to);
    }
  }
  return `${start <= day ? "Now" : "Next"}: ${fmt(start)} – ${fmt(end)}.`;
};

// One end of the window, as pickers rather than a code to type (DA-12). It
// writes exactly what the middleware stores — MM-DD, or YYYY-MM-DD when
// `once` — so the format and `describeSeasonalWindow` are unchanged. A value
// stored in neither shape is shown as it was typed, in a text box, with the
// warning underneath, rather than being quietly reinterpreted.
const SEASON_YEARS = (() => { const y = new Date().getFullYear(); return [y - 1, y, y + 1, y + 2]; })();
const WindowEnd = ({ label, value = "", once, onChange }) => {
  const parsed = /^(?:(\d{4})-)?(\d{2})-(\d{2})$/.exec(value);
  // A half-made pick lives here until both halves are chosen; the draft only
  // ever holds a whole date or nothing.
  const [part, setPart] = useState({ d: "", m: "", y: "" });
  const cur = parsed ? { y: parsed[1] || "", m: parsed[2], d: parsed[3] } : part;
  const set = (k, v) => {
    const next = { ...cur, [k]: v };
    if (once && !next.y) next.y = String(new Date().getFullYear());
    setPart(next);
    const whole = next.d && next.m && (!once || next.y);
    onChange(whole ? `${once ? next.y + "-" : ""}${next.m}-${next.d}` : "");
  };
  const sel = { ...inputStyle, width: "auto", paddingRight: 6 };
  if (value && !parsed) {
    return (
      <div style={{ flex: "1 1 160px" }}>
        <div style={{ fontSize: 11, color: T.textSoft, marginBottom: 3 }}>{label}</div>
        <input aria-label={label} value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />
      </div>
    );
  }
  return (
    <div style={{ flex: "0 1 auto" }}>
      <div style={{ fontSize: 11, color: T.textSoft, marginBottom: 3 }}>{label}</div>
      <div style={{ display: "flex", gap: 6 }}>
        <select aria-label={`${label} day`} value={cur.d} onChange={e => set("d", e.target.value)} style={sel}>
          <option value="">Day</option>
          {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map(d => <option key={d} value={d}>{+d}</option>)}
        </select>
        <select aria-label={`${label} month`} value={cur.m} onChange={e => set("m", e.target.value)} style={sel}>
          <option value="">Month</option>
          {MONTHS_SHORT.map((n, i) => <option key={n} value={String(i + 1).padStart(2, "0")}>{n}</option>)}
        </select>
        {once && (
          <select aria-label={`${label} year`} value={cur.y} onChange={e => set("y", e.target.value)} style={sel}>
            {SEASON_YEARS.map(y => <option key={y} value={String(y)}>{y}</option>)}
          </select>
        )}
      </div>
    </div>
  );
};

// The seasonal special condition — the office's holiday closure.
//
// A paragraph staff edit each year, and two dates that turn it on and off by
// themselves. **The dates decide, not a switch**: a switch is something to
// remember to turn off, and a closure clause on a contract issued in March is
// the kind of thing nobody notices until a purchaser asks.
const SeasonalSettings = ({ onSaved, onError, onDirty }) => {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState({});
  const [activeToday, setActiveToday] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = (data) => {
    const s = data.seasonal || {};
    setSaved(s);
    setDraft(s);
    setActiveToday(data.activeToday === true);
  };

  useEffect(() => {
    let cancelled = false;
    apiFetch("/settings/seasonal")
      .then(r => r.json())
      .then(data => { if (!cancelled && data?.ok) load(data); })
      .catch(err => console.error("Failed to load seasonal settings", err));
    return () => { cancelled = true; };
  }, []);

  const KEYS = ["from", "to", "title", "text"];
  const dirty = saved && KEYS.some(k => (draft[k] || "") !== (saved[k] || ""));
  useEffect(() => { onDirty?.(Boolean(dirty)); }, [dirty, onDirty]);
  // A window that cannot print cannot be saved (DA-12). Cleared is fine — that
  // is how the clause is switched off.
  const windowOk = seasonalWindowOk(draft);
  // "Just once" is a property of the stored dates, so it opens on whatever was
  // saved and only the tick changes it.
  const [once, setOnce] = useState(null);
  const isOnce = once ?? /^\d{4}-/.test(draft.from || draft.to || "");
  const switchOnce = (next) => {
    setOnce(next);
    const y = new Date().getFullYear();
    // A recurring window that wraps the new year ends in the following year
    // once it is written as one-off dates.
    const wraps = /^\d{2}-\d{2}$/.test(draft.from || "") && /^\d{2}-\d{2}$/.test(draft.to || "") && draft.from > draft.to;
    const conv = (v, later) => {
      if (!v) return v;
      if (next && /^\d{2}-\d{2}$/.test(v)) return `${later ? y + 1 : y}-${v}`;
      if (!next && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v.slice(5);
      return v;
    };
    setDraft(d => ({ ...d, from: conv(d.from, false), to: conv(d.to, wraps) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Every key, including the emptied ones — this is a settings screen
      // showing current values, so a cleared box is an instruction.
      const body = {};
      for (const k of KEYS) body[k] = draft[k] || "";
      const res = await apiFetch("/settings/seasonal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Save returned ${res.status}`);
      load(data);
      setOnce(null);
      onSaved?.("Seasonal clause saved");
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };


  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>Seasonal special condition</span>
        {/* Whether it is applying *today* is the question somebody opening this
            screen actually has — two dates leave them working it out, and
            getting it wrong either way is a paragraph on a contract of sale. */}
        {saved !== null && (
          <span style={{ fontSize: 11, color: activeToday ? "#1a7a4a" : T.textFaint }}>
            {activeToday ? "· appearing on contracts now" : "· not appearing today"}
          </span>
        )}
      </div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 12px" }}>
        A clause added to every contract issued between these dates — the office
        closure, usually. Write the dates as <strong>MM-DD</strong> to repeat every
        year, or <strong>YYYY-MM-DD</strong> for a one-off.
      </p>

      {saved === null ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 4 }}>
            <WindowEnd label="From" value={draft.from || ""} once={isOnce} onChange={v => setDraft(d => ({ ...d, from: v }))} />
            <WindowEnd label="To" value={draft.to || ""} once={isOnce} onChange={v => setDraft(d => ({ ...d, to: v }))} />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.textSoft, paddingBottom: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={isOnce} onChange={e => switchOnce(e.target.checked)} style={{ margin: 0, accentColor: T.brown }} />
              Just once, not every year
            </label>
          </div>
          <p style={{ fontSize: 11, color: windowOk ? T.textFaint : T.red, margin: "0 0 12px" }}>
            {describeSeasonalWindow(draft)}
            {nextSeasonalWindow(draft) && <> {nextSeasonalWindow(draft)}</>}
          </p>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: T.textSoft, marginBottom: 3 }}>Heading (optional)</div>
            <input
              value={draft.title || ""}
              onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
              placeholder="Office closure"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: T.textSoft, marginBottom: 3 }}>The clause</div>
            <textarea
              value={draft.text || ""}
              onChange={e => setDraft(d => ({ ...d, text: e.target.value }))}
              rows={5}
              placeholder="The wording that prints on the contract."
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
            />
            {/* Dates set with nothing written add nothing, rather than an empty
                numbered item on a contract of sale. Said here so an empty box
                does not read as something that failed to save. */}
            <p style={{ fontSize: 11, color: T.textFaint, margin: "6px 0 0" }}>
              With no wording there is no clause, whatever the dates say.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Btn size="sm" onClick={save} disabled={!dirty || saving || !windowOk}>
              {saving ? "Saving…" : "Save"}
            </Btn>
            {dirty && !saving && (
              <span style={{ fontSize: 11, color: T.textFaint }}>
                {windowOk ? "Unsaved changes" : "Fix the dates to save"}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};


// ── The section 137B inspector ─────────────────────────────────────────────
//
// Who a client's section 137B inspection is booked with, and the fee they are
// quoted. Each firm sets its own; the boxes show what applies now when the
// firm has not, so an empty box reads as "the default" rather than "nobody".
// The quote is frozen on each request when it is made, so a change here never
// alters what a client has already been told.
const INSPECTOR_KEYS = ["name", "email", "phone", "fee"];

export const InspectionSettings = ({ onSaved, onError, onDirty }) => {
  const [saved, setSaved] = useState(null);
  const [current, setCurrent] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  // Test mode: every 137B email goes to one address ("" is live).
  const [testTo, setTestTo] = useState(null);
  const [testDraft, setTestDraft] = useState("");

  const load = (data) => {
    const s = data.settings || {};
    const asText = Object.fromEntries(INSPECTOR_KEYS.map(k => [k, s[k] === undefined || s[k] === null ? "" : String(s[k])]));
    setSaved(asText);
    setDraft(asText);
    setCurrent(data.inspector || null);
    if (data.testTo !== undefined) { setTestTo(data.testTo || ""); setTestDraft(data.testTo || ""); }
  };

  const saveTest = async (value) => {
    setSaving(true);
    try {
      const res = await apiFetch("/settings/inspection", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ testTo: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Save returned ${res.status}`);
      load(data);
      onSaved?.(value ? `137B emails now go only to ${value}` : "137B emails now go to clients, inspectors and the office");
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    apiFetch("/settings/inspection")
      .then(r => r.json())
      .then(data => { if (!cancelled && data?.ok) load(data); })
      .catch(err => console.error("Failed to load inspection settings", err));
    return () => { cancelled = true; };
  }, []);

  const dirty = saved && INSPECTOR_KEYS.some(k => (draft[k] || "") !== (saved[k] || ""));
  useEffect(() => { onDirty?.(Boolean(dirty)); }, [dirty, onDirty]);

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(INSPECTOR_KEYS.map(k => [k, draft[k] || ""]));
      const res = await apiFetch("/settings/inspection", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Save returned ${res.status}`);
      load(data);
      onSaved?.("Inspection company saved");
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, placeholder, type = "text") => (
    <div style={{ flex: "1 1 220px" }}>
      <div style={{ fontSize: 11, color: T.textSoft, marginBottom: 3 }}>{label}</div>
      <input type={type} value={draft[key] || ""} placeholder={placeholder || ""}
        onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))} style={inputStyle} />
    </div>
  );

  return (
    <div data-inspection-settings style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>Section 137B inspection company</div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 12px" }}>
        Who a client's section 137B inspection is booked with, and the fee they are quoted.
        The booking request is emailed here with the office copied. With no email address,
        bookings come to the file handler to pass on.
      </p>
      {saved === null ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            {field("name", "Company", current?.name)}
            {field("email", "Booking email", current?.email, "email")}
            {field("phone", "Phone", current?.phone, "tel")}
            {field("fee", "Fee quoted to clients ($)", current?.fee ? String(current.fee) : "")}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Btn size="sm" onClick={save} disabled={!dirty || saving}>{saving ? "Saving…" : "Save"}</Btn>
            {dirty && !saving && <span style={{ fontSize: 11, color: T.textFaint }}>Unsaved changes</span>}
            {!dirty && current && (
              <span style={{ fontSize: 11, color: T.textFaint }}>
                Now: {current.name}{current.fee ? ` · $${current.fee}` : ""}{current.email ? ` · ${current.email}` : " · no booking email"}
              </span>
            )}
          </div>
          {testTo !== null && (
            <div data-137b-test style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: testTo ? T.red : T.text, marginBottom: 3 }}>
                {testTo ? `Test mode is on: every 137B email goes only to ${testTo}` : "Live: 137B emails go to clients, inspectors and the office"}
              </div>
              <p style={{ fontSize: 11.5, color: T.textFaint, margin: "0 0 8px" }}>
                In test mode the client’s request, the inspector’s booking, the office notice, the client’s copy and the
                reminders all go to the address below instead, each saying who it would have gone to.
              </p>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input type="email" aria-label="Test mode address" value={testDraft} placeholder="name@example.com"
                  onChange={e => setTestDraft(e.target.value)} style={{ ...inputStyle, flex: "1 1 220px" }} />
                <Btn size="sm" variant="ghost" onClick={() => saveTest(testDraft.trim())} disabled={saving || !testDraft.trim() || testDraft.trim() === testTo}>
                  {testTo ? "Change address" : "Turn test mode on"}
                </Btn>
                {testTo && (
                  <Btn size="sm" onClick={() => { if (window.confirm("Go live? 137B emails will go to clients, inspectors and the office.")) saveTest(""); }} disabled={saving}>
                    Go live
                  </Btn>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};


// ── Feedback mode ──────────────────────────────────────────────────────────
//
// **On while the build is being refined, and meant to be switched off.**
// Selecting a run of text anywhere — the dashboard, a matter, the contract
// preview — offers a note about it, and the note is emailed. The whole point
// is that the problem is reported from the place it was noticed rather than
// remembered later, which is why the trigger is a selection rather than a
// button in a corner.
//
// The threshold comes from the middleware, so it can be tuned without a portal
// deploy, and so can the switch.

// Where the selection is, in page coordinates, so a chip can sit under it.
// Returns null when there is nothing worth offering: too short, or inside
// something the person is editing.
export const selectionFeedback = (sel, { minSelection = 8 } = {}) => {
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;
  const text = String(sel.toString() || "").trim();
  if (text.length < minSelection) return null;

  // **Not while somebody is typing.** Selecting in a field you are editing —
  // to replace a word, or with ⌘A — is not pointing at a problem, and a chip
  // appearing over the box would be in the way of the work.
  const node = sel.anchorNode;
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  if (el?.closest?.("input, textarea, select, [contenteditable='true']")) return null;

  return { text };
};

// Where the note came from, said in the words the person would use.
export const feedbackWhere = ({ view, matterRef, inPreview }) => {
  if (view === "dashboard") return "Dashboard";
  if (view === "settings") return "Settings";
  if (inPreview) return "Edit contract and section 32";
  return matterRef ? "Matter" : "Portal";
};

// Whether a mouseup is a new answer about the selection, or part of reporting
// on the one already found.
//
// **The form must not be torn down by the click that opened it.** Pressing the
// chip is a mouseup in the parent document, and the selection it is about
// usually is not — it is in the preview frame, or the click itself collapsed
// it. Read as "nothing selected", that cleared the finding, unmounted the form
// and lost whatever had been typed into it: the button did nothing.
export const ignoreFeedbackMouseUp = ({ target, formOpen } = {}) =>
  Boolean(formOpen) || Boolean(target?.closest?.("[data-feedback-ui]"));

// The chip, and the form it opens.
//
// Rendered at the document level rather than inside whatever was selected —
// the selection can be in an iframe, and a chip inside it would be clipped by
// the frame and scroll with the document under it.
const FeedbackPrompt = ({ at, selection, where, matterRef, open, onOpen, onClose, onSent }) => {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    if (!note.trim() || sending) return;
    setSending(true);
    setError("");
    try {
      const res = await apiFetch("/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          note: note.trim(), selection, where, matterRef, who: PORTAL_USER,
          url: window.location.href,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Send returned ${res.status}`);
      onSent?.();
    } catch (err) {
      // Kept open with what they typed still in it. Losing a paragraph of
      // explanation to a failed send is how somebody stops bothering.
      setError(err.message);
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        data-feedback-ui
        onMouseDown={e => e.preventDefault()}
        onClick={onOpen}
        style={{ position: "fixed", left: Math.min(at.left, window.innerWidth - 150), top: at.top + 8, zIndex: 400, background: T.text, color: T.white, border: "none", borderRadius: 999, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontFamily: "Inter, sans-serif", boxShadow: "0 4px 14px rgba(0,0,0,0.18)" }}
      >
        Feedback on this
      </button>
    );
  }

  return (
    <div onClick={onClose} data-feedback-ui
      style={{ position: "fixed", inset: 0, background: "rgba(54,47,42,0.45)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, width: "100%", maxWidth: 520 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 8 }}>
          Feedback — {where}
        </div>
        {selection && (
          <div style={{ fontSize: 12, color: T.textSoft, background: T.bg, borderRadius: 6, padding: "8px 10px", marginBottom: 10, maxHeight: 110, overflow: "auto", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
            {selection}
          </div>
        )}
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Escape") onClose();
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
          }}
          rows={4}
          autoFocus
          placeholder="What's wrong with this, or what would you change?"
          style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
        />
        {error && <div style={{ fontSize: 11, color: T.red, marginTop: 6 }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, gap: 8 }}>
          <span style={{ fontSize: 10, color: T.textFaint }}>
            {sending ? "sending…" : "Sent to dev team for review"}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <Btn variant="ghost" size="sm" onClick={onClose} disabled={sending}>Cancel</Btn>
            <Btn size="sm" onClick={send} disabled={sending || !note.trim()}>Send</Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

// Watches for a selection worth offering feedback on — in this document and in
// any same-origin iframe, which is what reaches the contract preview.
//
// **The compiled PDF is out of reach**, and that is not a bug to chase: it is
// the browser's own PDF viewer in a plugin document, and nothing in the page
// can see a selection inside it.
const FeedbackWatcher = ({ enabled, minSelection, view, matterRef, onSent }) => {
  const [found, setFound] = useState(null);
  const [open, setOpen] = useState(false);

  // Read by the listeners, which are bound once — the form opening must not
  // re-bind them, or the mouseup that opened it arrives at a fresh listener.
  const openRef = useRef(false);
  openRef.current = open;

  useEffect(() => {
    if (!enabled) { setFound(null); setOpen(false); return undefined; }

    // A selection is reported while the drag is still happening, so the chip
    // would appear and move under the cursor. Reading it on release is what
    // makes it feel like an offer rather than an interruption.
    const check = (doc, frame) => {
      const sel = doc.getSelection?.();
      const hit = selectionFeedback(sel, { minSelection });
      if (!hit) { setFound(null); return; }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      const frameRect = frame?.getBoundingClientRect?.();
      setFound({
        text: hit.text,
        inPreview: Boolean(frame),
        left: (frameRect?.left || 0) + rect.left,
        top: (frameRect?.top || 0) + rect.bottom,
      });
    };

    const ignore = (event) =>
      ignoreFeedbackMouseUp({ target: event?.target, formOpen: openRef.current });

    const onUp = (event) => {
      if (ignore(event)) return;
      setTimeout(() => check(document), 0);
    };
    document.addEventListener("mouseup", onUp);

    // The preview renders in a same-origin blob: frame, so its selection is
    // reachable — and its coordinates need the frame's own offset added, or
    // the chip lands at the top of the page. The rect is read at mouseup
    // rather than at bind time, since the panel scrolls.
    const bound = new WeakSet();
    const cleanups = [];

    const attach = (doc, frame) => {
      if (!doc || bound.has(doc)) return;
      bound.add(doc);
      const handler = (event) => {
        if (ignore(event)) return;
        setTimeout(() => check(doc, frame), 0);
      };
      doc.addEventListener("mouseup", handler);
      cleanups.push(() => { try { doc.removeEventListener("mouseup", handler); } catch { /* gone */ } });
    };

    // **Bound on load as well as now.** The preview lives in a collapsible
    // panel, so its frame usually does not exist yet when this runs — and a
    // frame replaces its document every time the contract is re-rendered, so
    // a listener attached once is lost on the next rebuild.
    const watched = new WeakSet();
    const scan = () => {
      for (const frame of document.querySelectorAll("iframe")) {
        if (!watched.has(frame)) {
          watched.add(frame);
          const onLoad = () => {
            let doc = null;
            try { doc = frame.contentDocument; } catch { doc = null; }
            attach(doc, frame);
          };
          frame.addEventListener("load", onLoad);
          cleanups.push(() => { try { frame.removeEventListener("load", onLoad); } catch { /* gone */ } });
        }
        let doc = null;
        try { doc = frame.contentDocument; } catch { doc = null; }
        // A frame that has not navigated yet reports an empty about:blank
        // document; binding to that one would use up its turn in `bound` and
        // the real document would never be reached.
        if (doc?.body?.firstChild) attach(doc, frame);
      }
    };

    scan();
    const observer = typeof MutationObserver === "function"
      ? new MutationObserver(() => scan())
      : null;
    observer?.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("mouseup", onUp);
      observer?.disconnect();
      for (const undo of cleanups) undo();
    };
  }, [enabled, minSelection]);

  if (!enabled || !found) return null;
  return (
    <FeedbackPrompt
      at={found}
      selection={found.text}
      where={feedbackWhere({ view, matterRef, inPreview: found.inPreview })}
      matterRef={matterRef}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => { setOpen(false); setFound(null); }}
      onSent={() => { setOpen(false); setFound(null); onSent?.(); }}
    />
  );
};

// The switch that turns feedback mode off once the build settles.
//
// It exists because this is a temporary affordance, and a temporary
// affordance with no off switch is a permanent one. Absent means on, so the
// only state worth storing here is off.
const FeedbackSettings = ({ onError }) => {
  const [enabled, setEnabled] = useState(null);
  const [canSend, setCanSend] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/settings/feedback")
      .then(r => r.json())
      .then(data => {
        if (cancelled || !data?.ok) return;
        setEnabled(data.feedback?.enabled !== false);
        setCanSend(data.feedback?.canSend !== false);
      })
      .catch(err => console.error("Failed to load feedback settings", err));
    return () => { cancelled = true; };
  }, []);

  const flip = async (next) => {
    const previous = enabled;
    setEnabled(next);
    setSaving(true);
    try {
      const res = await apiFetch("/settings/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Save returned ${res.status}`);
      setEnabled(data.feedback?.enabled !== false);
    } catch (err) {
      setEnabled(previous);
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>Feedback mode</div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 12px" }}>
        Select a run of text anywhere and say what is wrong with it. The note is emailed with the selection. Turn this off once the build settles.
      </p>

      {enabled === null ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>
      ) : (
        <>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: saving ? "wait" : "pointer", fontSize: 13, color: T.text }}>
            <input
              type="checkbox"
              checked={enabled}
              disabled={saving}
              onChange={e => flip(e.target.checked)}
              style={{ width: 15, height: 15, cursor: saving ? "wait" : "pointer", accentColor: T.brown, margin: 0 }}
            />
            Offer feedback on selected text
          </label>
          {enabled && !canSend && (
            <p style={{ fontSize: 11, color: "#c47a1a", margin: "8px 0 0", paddingLeft: 23 }}>
              Nothing can be sent — mail is not configured.
            </p>
          )}
        </>
      )}
    </div>
  );
};

// ── The agent directory, on a screen ───────────────────────────────────────
//
// Every staff route the directory has ever had was API-only. There are 2,785
// agent records and 2,016 offices in this deployment, seeded from the firm's
// own workbook — and a typo in one of them, an office admin to appoint, 107
// duplicate pairs and 216 records nobody has published were all reachable only
// from an Actions workflow or a curl with the staff token in it. This is that
// work on a page.
//
// **The list is the middleware's answer, not a filter over one.**
// `GET /agent-accounts` searches, filters, sorts and pages 2,785 rows off its
// own cached index — one collection read a minute however many people are
// looking — so nothing here holds the collection and nothing re-derives who
// can sign in. `counts` comes back beside the page for the same reason: a
// screen narrowed to five rows out of 2,785 has to be able to say so, and
// counting the page would say five.
//
// **`signIn.ok` is the middleware's verdict and this only names it.** The rule
// is `canSignIn` in `lib/agent-auth.js` — active, not merged, has an email —
// and a copy of it here would drift from the gate that actually refuses, which
// is the one kind of drift that ends with a screen saying somebody can sign in
// when they cannot.
export const SIGN_IN_PROBLEMS = {
  no_record: "There is no record to sign in with.",
  merged: "This record was merged into another — the survivor is the account.",
  status_unverified: "Unverified. Nobody has checked this record, so it cannot sign in yet.",
  status_archived: "Archived.",
  status_merged: "Merged into another record.",
  status_unset: "This record has no status.",
  no_email: "No email address, so there is nowhere to send a sign-in link.",
  no_id: "This record has no id.",
};

// Named, and an unrecognised reason still says *something*. A middleware that
// grows a reason this bundle has not been taught would otherwise print a blank
// line where the one useful sentence goes — and a screen that goes quiet about
// a refusal reads as a screen that has not loaded.
export const signInProblem = (reason = "") =>
  SIGN_IN_PROBLEMS[reason] || (reason ? `Cannot sign in (${reason}).` : "Cannot sign in.");

// The tabs, and the key each one sends. `all` is the count's own name for the
// unfiltered list and the route wants no `filter` at all for it, so the two are
// kept apart here rather than by the caller remembering.
export const ACCOUNT_TABS = [
  { key: "all", label: "All" },
  { key: "cannotSignIn", label: "Cannot sign in" },
  { key: "neverSignedIn", label: "Never signed in" },
  { key: "awaitingReview", label: "Awaiting review" },
  { key: "admins", label: "Office admins" },
  { key: "staleGrant", label: "Lapsed grant" },
  // Agents who saved what buyers see themselves. A draft made for them when a
  // contract was released is not one — nothing a buyer sees has changed until
  // they adopt it — and the middleware's flag already draws that line.
  { key: "customisedPage", label: "Customised page" },
];

// What a row says about itself at a glance. Only ever states, never the name or
// the agency, which have their own columns — and **nothing is shown for the
// ordinary record**, so a list of chips is a list of things to look at.
export const accountChips = (row = {}) => {
  const chips = [];
  // Archived says it all; "cannot sign in" beside it would be the same fact twice.
  if (row.status === "archived") return [{ label: "Archived", tone: "faint" }];
  if (!row.signIn?.ok) chips.push({ label: "Cannot sign in", tone: "red" });
  if (row.awaitingReview) chips.push({ label: "Awaiting review", tone: "ink" });
  if (row.officeAdmin) chips.push({ label: "Office admin", tone: "ink" });
  // Granted at an office the agent has since left. The middleware sends this
  // only when it is *not* the live grant, so its presence is the whole finding.
  if (row.adminGrantedAt) chips.push({ label: "Lapsed admin grant", tone: "red" });
  if (!row.published) chips.push({ label: "Not published", tone: "faint" });
  // A fact rather than a problem, but one staff go looking for: these are the
  // agents whose photo and logo buyers see, and the ones worth a glance.
  if (row.customisedPage) chips.push({ label: "Customised buyer page", tone: "ink" });
  return chips;
};

const CHIP_TONE = {
  red: { color: T.red, border: "rgba(254,84,66,0.4)" },
  ink: { color: T.text, border: T.borderMid },
  faint: { color: T.textFaint, border: T.border },
};

const Chip = ({ label, tone }) => {
  const c = CHIP_TONE[tone] || CHIP_TONE.faint;
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 500, color: c.color, background: T.white,
      border: `1px solid ${c.border}`, padding: "1px 7px", borderRadius: 999, whiteSpace: "nowrap",
    }}>{label}</span>
  );
};

const directoryTime = (iso) => {
  if (!iso) return "";
  const t = Date.parse(iso);
  return Number.isFinite(t)
    ? new Date(t).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
    : "";
};

// ── What a record may be edited to say ─────────────────────────────────────
//
// **The form is generated from `GET /directory/fields`, never from a list
// here.** `AGENT_FIELDS` and `OFFICE_FIELDS` are the whitelists every write
// goes through and `FIELD_LABELS` sits beside them in the middleware, for the
// reason that file gives: a map kept in the browser drifts from the list the
// moment a field is added, and what that looks like is a raw key on a form in
// front of staff. So declaring a field there is the whole change, and a field
// this screen cannot save is impossible rather than merely unlikely.
//
// **Two lists of fields, or nothing at all.** The portal deploys on merge and
// the middleware only when somebody triggers it, so this bundle meets an older
// one routinely — and there the route 404s. A half-answer is the same case:
// `ok` with no lists on it. Either way `fields` stays null and every screen
// that reads it falls back to what it showed before this existed, which is the
// deploy-ordering rule this repository runs on. Without the check the account
// panel threw on `undefined.map` and took the whole record with it.
export const useDirectoryFields = (onError) => {
  const [fields, setFields] = useState(null);
  useEffect(() => {
    let cancelled = false;
    apiFetch("/directory/fields")
      .then(r => r.json())
      .then(d => {
        if (cancelled || !d?.ok) return;
        if (!Array.isArray(d.agent) || !Array.isArray(d.office)) return;
        setFields({ agent: d.agent, office: d.office });
      })
      .catch(err => onError?.(err.message));
    return () => { cancelled = true; };
  }, [onError]);
  return fields;
};

// An office, as one line somebody can pick out of a list. The contract name
// leads because that is what prints; the address is what tells two records of
// one agency apart, and it is the whole reason the seed has six Shape
// Property Group rows.
export const officeLine = (o = {}) =>
  [o.contractName || o.tradingName || "(unnamed)", o.address, o.suburb]
    .map(v => String(v || "").trim()).filter(Boolean).join(" · ");

// ── Which agency a salesperson is at ───────────────────────────────────────
//
// A text box would be a staff member typing `off_a65e5286…`, so it is a search
// over the offices with the answer picked from the results.
//
// **An office with no address is offered here and nowhere else.** The matter
// panel's picker refuses one outright — adopting it writes `agent.address`,
// which is where notices are served, so a record with no street line looks
// like it answered and did not. This screen is where that record is *fixed*,
// so hiding it would make it unreachable; it is marked instead, which is the
// report-rather-than-gate rule the compile gate already follows.
//
// **What was picked stays on screen.** Picking clears the box and the list, so
// without this the only sign anything happened was a button appearing further
// down — which on linking an office read as the pick having been lost. The
// picked office is held here, named under the box, and can be cleared.
//
// **`chosenLabel` says what the pick is for.** Linking an office and folding
// one in are not moving the agent, so they pass no `current` and a label of
// their own rather than reading *"At no agency"* about the agent in front of
// them.
export const OfficePicker = ({ value, current, onPick, onClear, onError, chosenLabel = "Moving to" }) => {
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState(null);
  const [chosen, setChosen] = useState(null);
  const shown = chosen && value && chosen.officeId === value ? chosen : null;
  const standalone = current === undefined;

  useEffect(() => {
    const id = setTimeout(() => setQ(typed.trim()), 250);
    return () => clearTimeout(id);
  }, [typed]);

  useEffect(() => {
    if (!q) { setHits(null); return undefined; }
    let cancelled = false;
    apiFetch(`/directory/offices?q=${encodeURIComponent(q)}&limit=8`)
      .then(r => r.json())
      .then(d => { if (!cancelled && d?.ok) setHits(d.offices || []); })
      .catch(err => onError?.(err.message));
    return () => { cancelled = true; };
  }, [q, onError]);

  return (
    <div>
      {!standalone && (
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 4 }}>
          {current
            ? <>Currently at <span style={{ color: T.text }}>{officeLine(current)}</span></>
            : "At no agency"}
        </div>
      )}
      {shown && (!current || shown.officeId !== current.officeId) && (
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 6, display: "flex", gap: 8,
                      alignItems: "center", flexWrap: "wrap" }}>
          <span>
            {chosenLabel}{" "}
            <span style={{ color: T.text, fontWeight: 500 }}>{officeLine(shown)}</span>
          </span>
          {onClear && (
            <button type="button" onClick={() => { setChosen(null); onClear(); }}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit",
                       fontSize: 11.5, color: T.textSoft, textDecoration: "underline" }}>
              Clear
            </button>
          )}
        </div>
      )}
      <input
        value={typed}
        onChange={e => setTyped(e.target.value)}
        placeholder="Search agencies by name, suburb or address"
        style={inputStyle}
      />
      {hits && (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 6, marginTop: 6, maxHeight: 220, overflowY: "auto" }}>
          {hits.length === 0 ? (
            <div style={{ fontSize: 12, color: T.textFaint, padding: "8px 10px" }}>
              Nothing matched “{q}”.
            </div>
          ) : hits.map((o, i) => (
            <button key={o.officeId} type="button"
              onClick={() => { setChosen(o); onPick(o.officeId); setTyped(""); setHits(null); }}
              style={{
                display: "block", width: "100%", textAlign: "left", cursor: "pointer",
                fontFamily: "inherit", background: o.officeId === value ? T.bgAlt : "transparent",
                border: "none", borderTop: i ? `1px solid ${T.border}` : "none", padding: "7px 10px",
              }}>
              <span style={{ fontSize: 12.5, color: T.text }}>{officeLine(o)}</span>
              <span style={{ fontSize: 11, color: T.textFaint, display: "block" }}>
                {o.agentCount} agent{o.agentCount === 1 ? "" : "s"}
                {!o.hasAddress && (
                  <span style={{ color: T.red }}> · no address, so a matter will not be offered it</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// The edit form itself. One component for both records, because the only thing
// that differs between them is the list the middleware served.
export const RecordForm = ({ fields, record, current, saving, onSave, onError }) => {
  const [draft, setDraft] = useState({});
  useEffect(() => { setDraft({}); }, [record?.agentId, record?.officeId]);

  const valueOf = (name) => (name in draft ? draft[name] : (record?.[name] ?? ""));
  const dirty = Object.keys(draft).some(k => String(draft[k] ?? "") !== String(record?.[k] ?? ""));

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
        {fields.map(f => (
          <label key={f.name} style={{ fontSize: 11.5, color: T.textFaint, display: "block" }}>
            {f.label}
            {f.control === "record" ? (
              <div style={{ marginTop: 4 }}>
                <OfficePicker
                  value={valueOf(f.name)}
                  current={current || null}
                  onPick={id => setDraft(d => ({ ...d, [f.name]: id }))}
                  onClear={() => setDraft(d => { const { [f.name]: _drop, ...rest } = d; return rest; })}
                  onError={onError}
                />
              </div>
            ) : f.control === "choice" ? (
              <select value={valueOf(f.name)}
                onChange={e => setDraft(d => ({ ...d, [f.name]: e.target.value }))}
                style={{ ...inputStyle, marginTop: 4, cursor: "pointer" }}>
                <option value="">— not set —</option>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input value={valueOf(f.name)}
                onChange={e => setDraft(d => ({ ...d, [f.name]: e.target.value }))}
                style={{ ...inputStyle, marginTop: 4 }} />
            )}
          </label>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
        <Btn size="sm" disabled={!dirty} busy={saving}
             onClick={() => onSave(draft).then(ok => { if (ok) setDraft({}); })}>
          Save changes
        </Btn>
        {dirty && <Btn variant="ghost" size="sm" onClick={() => setDraft({})}>Discard</Btn>}
        {/* A blank clears the field, which is the route's own rule — said out
            loud because on a contract that is a value disappearing rather than
            a box left empty. */}
        <span style={{ fontSize: 10.5, color: T.textFaint }}>
          Emptying a box clears that field on the record.
        </span>
      </div>
    </div>
  );
};

// ── Checking a record against the web ──────────────────────────────────────
//
// Two questions somebody used to settle by opening a browser: where a
// salesperson works **now** — nearly every review pair is an agent who moved
// agency and left a card behind, Oskar Forsyth at Harcourts rather than
// Neilson — and what an office's address **is**, where two records at one
// building disagree about the address notices are served at.
//
// **It reports and writes nothing, and every answer carries its sources as
// links.** The point is that the reader opens one and checks it; a model's
// answer taken on trust is exactly what the grounding rule in the middleware
// exists to stop. What they then change is an ordinary edit on the record.
//
// **Remembered for the session, per record.** The office check runs by itself
// when a group is opened, and closing and reopening it — or StrictMode running
// the effect twice — must not spend a second model call on a question already
// answered. A press of *Search again* is how somebody asks anew.
const webChecks = new Map();

export const forgetWebChecks = () => webChecks.clear();

// Loose on purpose: this only decides whether to say *matches our record* or
// *differs*, beside an answer the reader is going to look at anyway. Case,
// punctuation and the street type are how one address is written two ways.
const STREET_SHORT = { street: "st", road: "rd", avenue: "ave", drive: "dr", highway: "hwy", place: "pl", parade: "pde", court: "ct" };
export const looseKey = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()
  .split(" ").filter(Boolean).map(w => STREET_SHORT[w] || w).join(" ");

export const webCheckAgrees = (kind, found = {}, record = {}) => {
  if (kind === "office") {
    const theirs = looseKey(found.address);
    const ours = looseKey(record.address);
    if (!theirs || !ours) return null;
    return ours.includes(theirs) || theirs.includes(ours);
  }
  const theirs = looseKey(found.name);
  const ours = looseKey(record.officeName);
  if (!theirs || !ours) return null;
  const head = (k) => k.split(" ").slice(0, 2).join(" ");
  return ours.includes(head(theirs)) || theirs.includes(head(ours));
};

export const runWebCheck = (kind, id, { again = false } = {}) => {
  const key = `${kind}:${id}`;
  if (!again && webChecks.has(key)) return webChecks.get(key);
  const pending = apiFetch(`/directory-check/${kind === "office" ? "offices" : "agents"}/${encodeURIComponent(id)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  })
    .then(r => r.json().then(d => ({ r, d })))
    .then(({ r, d }) => {
      if (!r.ok || !d?.ok) throw new Error(d?.error || `The search did not work (${r.status})`);
      return d;
    });
  webChecks.set(key, pending);
  // A failure is not remembered: the next press should ask again.
  pending.catch(() => { if (webChecks.get(key) === pending) webChecks.delete(key); });
  return pending;
};

export const WebCheck = ({ kind, id, record = {}, auto = false }) => {
  const [state, setState] = useState(auto ? "loading" : "idle");
  const [out, setOut] = useState(null);

  const run = useCallback((again = false) => {
    setState("loading");
    runWebCheck(kind, id, { again })
      .then(d => { setOut(d); setState("done"); })
      .catch(err => { setOut({ error: err.message }); setState("error"); });
  }, [kind, id]);

  useEffect(() => { if (auto) run(false); }, [auto, run]);

  const label = kind === "office" ? "Check the address on the web" : "Where do they work now?";

  // The same press as the matter panel's *Search the web*, so a model call
  // looks like one wherever it is offered — the rim chases while it thinks.
  if (state === "idle" || state === "loading") {
    return (
      <ThinkingButton busy={state === "loading"} busyLabel="Searching the web…" onClick={() => run(false)}>
        {label}
      </ThinkingButton>
    );
  }
  if (state === "error") {
    return (
      <div style={{ fontSize: 11.5, color: T.red }}>
        {out?.error}{" "}
        <button type="button" onClick={() => run(true)}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit",
                   fontSize: 11.5, color: T.textSoft, textDecoration: "underline" }}>Try again</button>
      </div>
    );
  }

  const offices = out?.offices || [];
  const sources = out?.sources || [];
  return (
    <div style={{ fontSize: 12, background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px" }}>
      <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 6 }}>
        {kind === "office" ? "What the web says this office's address is" : "Where the web says they work now"}
        {" — found automatically, so open a source before changing the record."}
      </div>
      {offices.length === 0 ? (
        <div style={{ color: T.textSoft }}>{out?.message || "Nothing was found."}</div>
      ) : offices.map((o, i) => {
        const agrees = webCheckAgrees(kind, o, record);
        return (
          <div key={i} style={{ padding: "4px 0", borderTop: i ? `1px solid ${T.border}` : "none" }}>
            <span style={{ color: T.text }}>{o.name}</span>
            <span style={{ color: T.textSoft }}>
              {" — "}{[o.address, o.suburb, o.state, o.postcode].filter(Boolean).join(" ")}
              {o.phone ? ` · ${o.phone}` : ""}
            </span>
            {agrees !== null && (
              <span style={{ marginLeft: 6 }}>
                <Chip label={agrees ? "Matches our record" : "Differs from our record"} tone={agrees ? "faint" : "red"} />
              </span>
            )}
            {/^https?:\/\//.test(o.source || "") && (
              <a href={o.source} target="_blank" rel="noopener noreferrer"
                 style={{ marginLeft: 6, fontSize: 11.5, color: T.textSoft }}>source</a>
            )}
          </div>
        );
      })}
      {sources.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11.5 }}>
          <span style={{ color: T.textFaint }}>Sources: </span>
          {sources.map((s, i) => (
            <span key={s.uri || i}>
              {i ? " · " : ""}
              <a href={s.uri} target="_blank" rel="noopener noreferrer" style={{ color: T.textSoft }}>
                {s.title || s.uri}
              </a>
            </span>
          ))}
        </div>
      )}
      <button type="button" onClick={() => run(true)}
        style={{ marginTop: 6, background: "none", border: "none", padding: 0, cursor: "pointer",
                 fontFamily: "inherit", fontSize: 11, color: T.textFaint, textDecoration: "underline" }}>
        Search again
      </button>
    </div>
  );
};

// ── The offices an agent also sells for ─────────────────────────────────────
//
// A franchise agent based at one office routinely sells in another region
// under that office's banner. The **home** office is the Agency field on the
// record — what a contract prints — and this is the list beside it. The
// middleware keeps the home office off it and replaces the list whole, so this
// sends the list it shows.
//
// **It does not change what they see in the agent portal**, which is scoped on
// the home office; widening that is a decision about who reads client matters,
// not about a contact card, and it is said here so nobody assumes otherwise.
export const LinkedOffices = ({ linked = [], busy, onSave, onError }) => {
  const [picking, setPicking] = useState("");
  const ids = linked.map(o => o.officeId);
  return (
    <div>
      {linked.length === 0 ? (
        <div style={{ fontSize: 12, color: T.textFaint, marginBottom: 6 }}>No other offices.</div>
      ) : (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          {linked.map(o => (
            <span key={o.officeId} style={{ fontSize: 12, color: T.text, border: `1px solid ${T.border}`,
                                             borderRadius: 999, padding: "2px 4px 2px 10px", display: "inline-flex",
                                             alignItems: "center", gap: 4 }}>
              <RecordLink kind="office" id={o.officeId}>
                {[o.name, o.suburb].filter(Boolean).join(" — ") || o.officeId}
              </RecordLink>
              <button type="button" aria-label={`Unlink ${o.name || o.officeId}`} disabled={busy}
                onClick={() => onSave(ids.filter(id => id !== o.officeId))}
                style={{ background: "none", border: "none", cursor: "pointer", color: T.textFaint, fontSize: 13, padding: "0 4px" }}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <OfficePicker value={picking} onPick={setPicking} onClear={() => setPicking("")}
                    chosenLabel="To link:" onError={onError} />
      {picking && !ids.includes(picking) && (
        <div style={{ marginTop: 6 }}>
          <Btn size="sm" busy={busy}
               onClick={() => onSave([...ids, picking]).then(ok => { if (ok) setPicking(""); })}>
            Link this office
          </Btn>
        </div>
      )}
      <div style={{ fontSize: 10.5, color: T.textFaint, marginTop: 6 }}>
        The agency field above stays their home office — what a contract prints and what their agent
        portal is scoped on. A linked office is for finding them, not for access.
      </div>
    </div>
  );
};

// ── Any agent or office named on this screen opens as a modal ───────────────
//
// The directory names records in four places — an account's agency, an
// agency's staff, a duplicate pair's agents and agencies, an agent's linked
// offices — and until this each was plain text. Checking the agency an agent
// is at meant leaving the account, going to the Agencies tab, searching for it
// and finding the row: the firm's own words were *"there is no way of clicking
// on the office to edit the office's details"*.
//
// **One modal, opened from anywhere, and it stacks.** Clicking the agency
// inside an agent's modal opens that agency over it with a *Back* to the agent,
// because that is the ordinary route through a correction — an agent at the
// wrong-looking agency, whose record is the one that needs fixing.
//
// **The panels are the ones the lists already use** — `AccountDetail` and
// `OfficeDetail` through `OfficeEditor` — so what can be done to a record is the
// same wherever it was opened, and nothing here is a second copy of an edit.
//
// **A change in the modal re-reads the list behind it.** `refresh` is a counter
// every tab's loader depends on, so a name corrected in a modal is not left
// standing wrong on the row it was opened from.
const DirectoryRecords = createContext(null);

export const RecordLink = ({ kind, id, children, style, faint = false }) => {
  const records = useContext(DirectoryRecords);
  if (!records || !id) return <span style={style}>{children}</span>;
  return (
    <button type="button"
      onClick={(e) => { e.stopPropagation(); records.open(kind, id); }}
      onKeyDown={(e) => e.stopPropagation()}
      title={kind === "office" ? "Open this agency" : "Open this agent"}
      style={{
        background: "none", border: "none", padding: 0, margin: 0, cursor: "pointer",
        font: "inherit", textAlign: "left", color: faint ? "inherit" : T.text,
        textDecoration: "underline", textDecorationColor: T.borderMid || T.border,
        textUnderlineOffset: 2, ...style,
      }}>
      {children}
    </button>
  );
};

export const DirectoryRecordModal = ({ stack, fields, onBack, onClose, onChanged, onToast, onError }) => {
  const top = stack[stack.length - 1];
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!top) return null;
  return (
    <div role="dialog" aria-modal="true"
         onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)", zIndex: 300,
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                  padding: "40px 16px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 14, width: "100%",
                    maxWidth: 760, boxShadow: "0 12px 48px rgba(0,0,0,0.14)", padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          {stack.length > 1 && (
            <Btn variant="ghost" size="sm" onClick={onBack}>← Back</Btn>
          )}
          <span style={{ fontSize: 12, color: T.textFaint }}>
            {top.kind === "office" ? "Agency" : "Agent"}
          </span>
          <Btn variant="ghost" size="sm" onClick={onClose} style={{ marginLeft: "auto" }}>Close</Btn>
        </div>
        {top.kind === "office"
          ? <OfficeEditor key={`o:${top.id}`} officeId={top.id} fields={fields} onChanged={onChanged}
                          onToast={onToast} onError={onError} />
          : <AccountDetail key={`a:${top.id}`} agentId={top.id} fields={fields} onChanged={onChanged}
                           onToast={onToast} onError={onError} />}
      </div>
    </div>
  );
};

// ── One account ─────────────────────────────────────────────────────────────
//
// The list deliberately leaves out the sessions actually open — that is a query
// per agent, which is exactly why — and it is the only way to answer *is this
// person signed in right now*, which is what somebody asks before revoking.
// ── What the agent set up for their purchaser page ──────────────────────────
//
// *Customise what buyers see*, from the agent's download page, puts their photo,
// details, logo and colours on every purchaser page. It is theirs to set and
// it is authorised by the release link alone, so this is where the office sees
// it — and clears it, where a photo is of the wrong person or a logo is one
// somebody objects to. **Clearing keeps it** as the agent's previous version,
// so the agent can put it back with Undo.
//
// **Absent on an older middleware**: the route answers 404 and nothing is drawn,
// the rule the purchasers panel follows.
export const AgentPageDetails = ({ agentId, onToast, onError }) => {
  const [state, setState] = useState({ loading: true, branding: null, missing: false });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    let cancelled = false;
    apiFetch(`/agent-branding/${encodeURIComponent(agentId)}`)
      .then(async (res) => {
        if (res.status === 404) return { missing: true };
        const data = await res.json().catch(() => ({}));
        return { branding: data?.ok ? data.branding : null };
      })
      .then(out => { if (!cancelled) setState({ loading: false, branding: null, missing: false, ...out }); })
      .catch(() => { if (!cancelled) setState({ loading: false, branding: null, missing: true }); });
    return () => { cancelled = true; };
  }, [agentId]);
  useEffect(() => load(), [load]);

  if (state.loading || state.missing) return null;
  const b = state.branding;
  const clear = async () => {
    if (!window.confirm("Clear this agent's page details? Their purchaser pages go back to the standard look. They can undo it from their own page.")) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/agent-branding/${encodeURIComponent(agentId)}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(PORTAL_USER ? { viewer: PORTAL_USER } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || `Clearing returned ${res.status}`);
      onToast?.("Their page details are cleared");
      load();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy(false);
    }
  };
  const img = (url) => (url ? `${MIDDLEWARE_URL}${url}` : "");
  return (
    <div style={{ marginBottom: 14 }} data-agent-page-details>
      <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginBottom: 6 }}>Their purchaser page</div>
      {!b ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>
          The standard page. They can set up their own photo and details from <em>Customise what buyers see</em> on any contract we send them.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          {b.photoUrl
            ? <img src={img(b.photoUrl)} alt="" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: `1px solid ${T.border}` }} />
            : null}
          <div style={{ flex: "1 1 200px", minWidth: 0, fontSize: 12, color: T.textSoft, lineHeight: 1.6 }}>
            <div style={{ color: T.text, fontWeight: 500 }}>{b.name || "(name from the matter)"}{b.title ? ` · ${b.title}` : ""}</div>
            {[b.mobile, b.email, b.office].filter(Boolean).join(" · ")}
            {b.logoUrl && <div><img src={img(b.logoUrl)} alt="" style={{ maxHeight: 28, maxWidth: 160, marginTop: 6 }} /></div>}
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              {[b.primary, b.secondary, b.accent].filter(Boolean).map(hex => (
                <span key={hex} title={hex} style={{ width: 16, height: 16, borderRadius: "50%", background: hex, border: `1px solid ${T.border}` }} />
              ))}
            </div>
            <div style={{ color: T.textFaint, marginTop: 4 }}>
              {b.updatedAt ? `Updated ${directoryTime(b.updatedAt)}` : ""}
              {b.updatedFrom?.matterRef ? ` from matter ${b.updatedFrom.matterRef}` : ""}
            </div>
          </div>
          <Btn variant="ghost" busy={busy} onClick={clear}>Clear their page details</Btn>
        </div>
      )}
    </div>
  );
};

const AccountDetail = ({ agentId, fields, onChanged, onToast, onError }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [link, setLink] = useState(null);

  const load = useCallback(() => {
    let cancelled = false;
    apiFetch(`/agent-accounts/${encodeURIComponent(agentId)}`)
      .then(r => r.json())
      .then(d => { if (!cancelled) setData(d?.ok ? d : { error: d?.error || "Could not load it" }); })
      .catch(err => { if (!cancelled) setData({ error: err.message }); });
    return () => { cancelled = true; };
  }, [agentId]);

  useEffect(load, [load]);

  // One door for every press on this panel. Each of them is a POST that either
  // changes the record or ends a session, so each re-reads afterwards — the
  // panel showing a stale verdict beside a button somebody just pressed is the
  // shape that gets pressed twice.
  const act = async (what, path, body, describe) => {
    setBusy(what);
    try {
      const res = await apiFetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {}),
      });
      const out = await res.json();
      // **The middleware's own sentence, never one invented here.** Its
      // refusals are specific — an agent at no office has nothing to
      // administer, a merged record is a tombstone — and replacing them with
      // "That did not work" is how somebody comes to press a button twice.
      if (!res.ok || !out.ok) throw new Error(out.error || `That did not work (${res.status})`);
      onToast?.(describe(out));
      load();
      onChanged?.();
      return out;
    } catch (err) {
      onError?.(err.message);
      return null;
    } finally {
      setBusy("");
    }
  };

  if (!data) return <div style={{ fontSize: 12, color: T.textFaint, padding: 16 }}>Loading…</div>;
  if (data.error) return <div style={{ fontSize: 12, color: T.red, padding: 16 }}>{data.error}</div>;

  const a = data.account || {};
  const office = a.office;

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 15, color: T.text, marginBottom: 2 }}>{a.name || "(no name recorded)"}</div>
          <div style={{ fontSize: 12, color: T.textSoft }}>{a.email || "no email address"}</div>
          {office && (
            <div style={{ fontSize: 12, color: T.textFaint, marginTop: 2 }}>
              <RecordLink kind="office" id={office.officeId} faint>
                {office.name}{office.suburb ? ` — ${office.suburb}` : ""}
              </RecordLink>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "flex-start" }}>
          {accountChips(a).map(c => <Chip key={c.label} {...c} />)}
        </div>
      </div>

      {/* The one sentence that says why this record cannot be used. */}
      {!a.signIn?.ok && (
        <div style={{ fontSize: 12, color: T.red, marginBottom: 10 }}>{signInProblem(a.signIn?.reason)}</div>
      )}
      {a.adminGrantedAt && (
        <div style={{ fontSize: 12, color: T.red, marginBottom: 10 }}>
          Made an office admin at an agency this record is no longer at, so the grant does nothing.
          Removing it clears the record.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, fontSize: 12, marginBottom: 14 }}>
        <div><span style={{ color: T.textFaint }}>Signed in</span><br />
          {data.sessions?.live
            ? `${data.sessions.live} session${data.sessions.live === 1 ? "" : "s"} open`
            : "not signed in now"}</div>
        <div><span style={{ color: T.textFaint }}>First sign-in</span><br />
          {directoryTime(a.firstSignInAt) || "never"}</div>
        <div><span style={{ color: T.textFaint }}>Last sign-in</span><br />
          {directoryTime(a.lastSignInAt) || "never"}</div>
        <div><span style={{ color: T.textFaint }}>Verified</span><br />
          {a.verifiedAt ? `${directoryTime(a.verifiedAt)}${a.verifiedBy ? ` (${a.verifiedBy})` : ""}` : "not verified"}</div>
      </div>

      {!a.published && (
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 8 }}>
          Approving adds them to the directory: the client form&rsquo;s agent search offers them on
          future matters, and {a.email ? "they can sign in to the agent portal" : "once they have an email address they can sign in to the agent portal"}.
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {/* **Named for what it does, which is approve.** It read *Publish to
            the client form*, which hid the half staff care about: the same
            press makes the record active, and an active record with an email
            is one that can sign in to the agent portal. Taking it out of the
            client form leaves it active, so the agent keeps their sign-in. */}
        <Btn variant={a.published ? "ghost" : "primary"} busy={busy === "publish"}
             onClick={() => act("publish", `/directory/agents/${encodeURIComponent(a.agentId)}/publish`,
               { published: !a.published },
               () => a.published
                 ? "Taken out of the client form's agent search. They can still sign in."
                 : "Approved — added to the directory, offered on the client form, and able to sign in.")}>
          {a.published ? "Take out of the client form" : "Approve and add to directory"}
        </Btn>

        {/* **An office admin sees the agency's whole book**, so it is offered
            as what it is rather than as a tick. The middleware refuses one at
            no office, and that refusal is worth reading rather than hiding the
            button: *this agent is at no office* is the thing to fix. */}
        <Btn variant="ghost" busy={busy === "admin"}
             onClick={() => act("admin", `/directory/agents/${encodeURIComponent(a.agentId)}/office-admin`,
               { officeAdmin: !a.officeAdmin },
               () => a.officeAdmin ? "No longer an office admin" : "Made an office admin")}>
          {a.officeAdmin ? "Remove office admin" : "Make an office admin"}
        </Btn>

        {/* **Revoked, never forgotten.** A token may still be in a browser, so
            a session that merely stopped being listed is one that still works.
            A press that revoked nothing says so rather than claiming an action
            nobody took. */}
        <Btn variant="ghost" disabled={!data.sessions?.live} busy={busy === "signout"}
             onClick={() => act("signout", `/agent-accounts/${encodeURIComponent(a.agentId)}/sign-out`, {},
               out => out.revoked
                 ? `Signed out of ${out.revoked} session${out.revoked === 1 ? "" : "s"}`
                 : "There were no sessions to end")}>
          Sign out everywhere
        </Btn>

        {/* **Returned, not sent.** A verification link is a write grant sitting
            in an inbox, so who receives one is a decision worth a person
            making — which is the middleware's own rule, and this is where that
            person is. */}
        <Btn variant="ghost" busy={busy === "verify"}
             onClick={async () => {
               const out = await act("verify", `/directory/agents/${encodeURIComponent(a.agentId)}/verification-link`, {},
                 () => "Verification link minted — it is on screen, and it is not emailed");
               if (out?.url) setLink(out);
             }}>
          Verification link
        </Btn>

        {/* **Archived, never deleted.** A matter that adopted this agent keeps
            what it printed — an adoption writes corrections rather than reading
            the record live — and the record is out of every suggestion, the
            client form and the agent portal. Their sessions end with it. It is
            found again under *Show archived*. */}
        <Btn variant="ghost" busy={busy === "archive"}
             onClick={() => act("archive", `/directory/agents/${encodeURIComponent(a.agentId)}/archive`,
               { archived: a.status !== "archived" },
               out => (a.status === "archived"
                 ? "Taken out of the archive"
                 : `Archived${out.signedOut ? ` — ${out.signedOut} session${out.signedOut === 1 ? "" : "s"} ended` : ""}`))}>
          {a.status === "archived" ? "Take out of the archive" : "Archive this agent"}
        </Btn>
      </div>

      {link && (
        <div style={{ background: T.bgAlt, borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 4 }}>
            Send this to the agent yourself. It expires {directoryTime(link.expiresAt) || "in a fortnight"} and is spent once used.
          </div>
          <code style={{ fontSize: 11.5, color: T.text, wordBreak: "break-all" }}>{link.url}</code>
        </div>
      )}

      {/* ── Correcting the record ───────────────────────────────────────────
          The firm's report: *"we have sales person and agent information
          filling in the contract of sale template, and sometimes there are
          mistakes or errors here"*. `POST /directory/agents/:agentId` has
          taken these since the directory shipped and there was no screen for
          it, so a typo in a seed of 2,785 records was reachable only from a
          curl with the staff token in it.

          **The agency is one of the fields**, which is the half actually
          asked for: it is `officeId` on the record, so relinking a
          salesperson is a staff edit like any other rather than a mechanism
          of its own. The middleware says it is a record reference and the
          form draws a picker. */}
      {fields && (
        <>
          <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginBottom: 6 }}>
            The record
          </div>
          <div style={{ marginBottom: 14 }}>
            <RecordForm
              fields={fields.agent}
              record={a}
              current={office}
              saving={busy === "save"}
              onError={onError}
              onSave={async (draft) => Boolean(await act("save",
                `/directory/agents/${encodeURIComponent(a.agentId)}`, draft,
                out => (out.signedOut
                  ? `Saved — the email moved, so ${out.signedOut} session${out.signedOut === 1 ? " was" : "s were"} ended`
                  : "Saved to the directory")))}
            />
          </div>
        </>
      )}

      <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginBottom: 6 }}>
        Other offices they sell for
      </div>
      <div style={{ marginBottom: 14 }}>
        <LinkedOffices
          linked={a.linkedOffices || []}
          busy={busy === "linked"}
          onError={onError}
          onSave={async (officeIds) => Boolean(await act("linked",
            `/directory/agents/${encodeURIComponent(a.agentId)}/linked-offices`, { officeIds },
            () => "Linked offices saved"))}
        />
      </div>

      <AgentPageDetails agentId={a.agentId} onToast={onToast} onError={onError} />

      <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginBottom: 6 }}>History</div>
      {(data.history || []).length === 0 ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>Nothing has been recorded against this record.</div>
      ) : (
        <div>
          {data.history.map((h, i) => (
            <div key={i} style={{ display: "flex", gap: 10, fontSize: 12, padding: "5px 0", borderTop: i ? `1px solid ${T.border}` : "none" }}>
              <span style={{ color: T.textFaint, minWidth: 92 }}>{directoryTime(h.at)}</span>
              {/* The wording is computed from the kind by the middleware, never
                  stored — `line` is what it renders, and a kind it has not
                  been taught falls back to the kind itself rather than to a
                  blank row that would read as nothing having happened. */}
              <span style={{ color: T.textSoft }}>{h.line || h.kind || "(no description)"}</span>
              <span style={{ color: T.textFaint, marginLeft: "auto" }}>{h.by}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// **The directory remembers where staff left it**, for the tab's life. Opening
// a matter from *Awaiting review* and coming back used to land on Accounts with
// the search gone. One key, per tab (sessionStorage, like the portal's other
// UI state), and every read and write survives storage being unavailable.
export const DIRECTORY_STATE_KEY = "s32.directory";
export const readDirectoryState = () => {
  try { return JSON.parse(sessionStorage.getItem(DIRECTORY_STATE_KEY) || "{}") || {}; }
  catch { return {}; }
};
export const writeDirectoryState = (patch) => {
  try {
    sessionStorage.setItem(DIRECTORY_STATE_KEY, JSON.stringify({ ...readDirectoryState(), ...patch }));
  } catch { /* storage unavailable: the page still works, it just forgets */ }
};

// `only` pins the list to one of the middleware's filters and drops the tab
// row, so *Awaiting review* can list the records that cannot sign in with the
// same rows, presses and paging rather than a count and a pointer here.
const DirectoryAccounts = ({ fields, refresh, onToast, onError, only = "" }) => {
  // The full list remembers where staff left it (see `readDirectoryState`); the
  // pinned copy on *Awaiting review* always starts clean.
  const remembered = only ? {} : readDirectoryState();
  const [q, setQ] = useState(() => (remembered.typed || "").trim());
  const [typed, setTyped] = useState(() => remembered.typed || "");
  const [picked, setTab] = useState(() => remembered.filter || "all");
  const tab = only || picked;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState("");
  // **The archive is its own list**, like the matter dashboard's: an archived
  // record is out of everything that offers or matches, and this is where it
  // is found again to be brought back.
  const [archived, setArchived] = useState(() => !!remembered.archived);
  useEffect(() => {
    if (!only) writeDirectoryState({ typed, filter: picked, archived });
  }, [only, typed, picked, archived]);

  // Typing is debounced rather than sent per keystroke. The route answers off a
  // cached index so a keystroke is cheap — what it is not is *free*, and a list
  // that reflows under somebody halfway through a surname is harder to use than
  // one that waits a moment.
  useEffect(() => {
    const id = setTimeout(() => setQ(typed.trim()), 300);
    return () => clearTimeout(id);
  }, [typed]);

  const load = useCallback((cursor) => {
    let cancelled = false;
    if (!cursor) setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (tab !== "all") params.set("filter", tab);
    if (archived) params.set("archived", "true");
    if (cursor) params.set("cursor", String(cursor));
    apiFetch(`/agent-accounts?${params.toString()}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled || !d?.ok) return;
        // A further page is appended; a new search replaces. Appending a first
        // page would leave the old search's rows above the new one's.
        setPage(prev => (cursor && prev
          ? { ...d, accounts: [...prev.accounts, ...d.accounts] }
          : d));
      })
      .catch(err => onError?.(err.message))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q, tab, archived, onError]);

  useEffect(() => load(), [load]);
  useRefreshed(refresh, load);

  const rows = page?.accounts || [];

  return (
    <div>
      <input
        value={typed}
        onChange={e => setTyped(e.target.value)}
        placeholder="Name, email, mobile or agency"
        style={{ ...inputStyle, maxWidth: 360, marginBottom: 12 }}
      />
      <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12, color: T.textSoft,
                      cursor: "pointer", marginLeft: 12 }}>
        <input type="checkbox" checked={archived} onChange={e => setArchived(e.target.checked)}
               style={{ accentColor: T.brown }} />
        Show archived{page?.counts?.archivedTotal != null ? ` (${page.counts.archivedTotal})` : ""}
      </label>

      {!only && <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
        {ACCOUNT_TABS.map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            style={{
              fontSize: 11.5, fontFamily: "inherit", padding: "4px 11px", borderRadius: 999,
              cursor: "pointer",
              background: tab === t.key ? T.text : T.white,
              color: tab === t.key ? T.white : T.textSoft,
              border: `1px solid ${tab === t.key ? T.text : T.border}`,
            }}>
            {t.label}
            {/* **The whole collection's count, not the page's.** A list showing
                five of 2,785 has to be able to say which, and counting what is
                on screen would say five. */}
            <span style={{ marginLeft: 6, opacity: 0.65 }}>{page?.counts?.[t.key] ?? "—"}</span>
          </button>
        ))}
      </div>}

      {loading ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>
          {q ? `Nothing matched “${q}”.` : "No records in this list."}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 8 }}>
            Showing {rows.length} of {page.total}
          </div>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
            {rows.map((row, i) => (
              <div key={row.agentId}>
                {/* A row, not a <button>: the agency inside it is a press of
                    its own, and a button cannot hold another. */}
                <div role="button" tabIndex={0}
                  onClick={() => setOpenId(openId === row.agentId ? "" : row.agentId)}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(openId === row.agentId ? "" : row.agentId); } }}
                  style={{
                    width: "100%", textAlign: "left", background: openId === row.agentId ? T.bgAlt : "transparent",
                    border: "none", borderTop: i ? `1px solid ${T.border}` : "none",
                    padding: "10px 14px", cursor: "pointer", fontFamily: "inherit",
                    display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
                  }}>
                  <span style={{ flex: "1 1 200px", minWidth: 0 }}>
                    <span style={{ fontSize: 13, color: T.text }}>{row.name || "(no name)"}</span>
                    <span style={{ fontSize: 11.5, color: T.textFaint, display: "block" }}>
                      {row.email || "no email"}
                    </span>
                    {/* **The agency on its own line.** It trailed the email
                        behind a interpunct, which is where a reader stops —
                        and it is the half that decides what a contract prints
                        and where notices are served, so it reads as a column
                        rather than as a suffix. A record at no agency says so,
                        because that is the one worth acting on. */}
                    <span style={{ fontSize: 11.5, display: "block",
                                   color: row.office ? T.textSoft : T.red }}>
                      {row.office
                        ? (
                          <RecordLink kind="office" id={row.office.officeId} faint>
                            {[row.office.name, row.office.suburb].filter(Boolean).join(" — ")}
                          </RecordLink>
                        )
                        : "at no agency"}
                    </span>
                    {/* Pinned to the cannot-sign-in list, the reason is what
                        varies between rows, so each says its own. */}
                    {only === "cannotSignIn" && !row.signIn?.ok && (
                      <span style={{ fontSize: 11.5, display: "block", color: T.textFaint }}>
                        {signInProblem(row.signIn?.reason)}
                      </span>
                    )}
                  </span>
                  <span style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {accountChips(row).map(c => <Chip key={c.label} {...c} />)}
                  </span>
                </div>
                {openId === row.agentId && (
                  <div style={{ padding: "0 14px 14px" }}>
                    <AccountDetail agentId={row.agentId} fields={fields} onChanged={() => load()}
                                   onToast={onToast} onError={onError} />
                  </div>
                )}
              </div>
            ))}
          </div>
          {page.nextCursor != null && (
            <div style={{ marginTop: 12 }}>
              <Btn variant="ghost" onClick={() => load(page.nextCursor)}>Show more</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Matters whose agent the directory does not hold ─────────────────────────
//
// The agent on these was typed — on Form 27, or by staff from a web search —
// rather than picked from a record, so nothing links them to the matter: they
// cannot see it in the agent portal, and the next client naming them gets no
// typeahead row either (1760712).
//
// **The queue is the middleware's.** `GET /matter-agents/unmatched` decides
// which matters belong on it, newest first, and why — `lib/agent-queue.js` is
// the rule — so this lists what it is sent and filters nothing.
//
// **Add to directory** asks the middleware to make the record from what the
// matter prints, and the firm's rule is that staff adding one approves it. The
// directory is checked first: the same email is the same person and that
// record is used; a name match comes back as `possible_duplicates`, which is
// shown here for somebody to pick from or to add a new record anyway. Either
// way the answer is then adopted through `POST /matter-agent/:ref`, the one
// route that writes a matter's agent.
//
// **And the agency, in the same press.** An agent at no agency prints nothing
// the next time they are picked, so the middleware settles it: where the
// directory has agencies of the matter's agency name it answers
// `choose_office`, and the row asks which — opening with what the matter says
// the agent was associated with, since that is what staff compare against. A
// pick goes back as `officeId`; *New agency from this matter* as `newOffice`.
// With none of that name the middleware makes one without asking.
export const UNMATCHED_REASONS = {
  no_record: "Not in the directory.",
  not_adopted: "The directory has records that may be them — check before adding.",
  picked_unresolved: "The client picked a record that no longer exists.",
  agent_office_differs: "Picked at a different agency from the one the vendor named.",
};

// The words for what an add did, off the middleware's own answer.
export const addedWording = (out = {}) => (out.created
  ? `Added to the directory and approved — they can sign in, and the client form offers them.${
    out.officeCreated ? " A new agency record was made from the matter." : ""}`
  : `Already in the directory under that email — ${out.approved ? "approved and " : ""}linked to this matter.${
    out.officeSet ? (out.officeCreated ? " A new agency record was made and set on theirs." : " Their record now names that agency.") : ""}`);

const postDirectoryJson = async (path, body) => {
  const r = await apiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  const out = await r.json().catch(() => ({}));
  return { status: r.status, out };
};

// The directory record a *link* press would put on the matter: who, how to
// reach them, and the agency it would print — or that it names none, which
// leaves the matter's typed agency standing and is worth fixing first.
const ExactRecordCard = ({ id, record }) => {
  const faint = { fontSize: 11.5, color: T.textFaint, display: "block" };
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", margin: "2px 0 8px" }}>
      <span style={{ ...faint, marginBottom: 2 }}>Their directory record</span>
      <RecordLink kind="agent" id={id}>
        <span style={{ fontSize: 12.5, color: T.text }}>{record?.name || "Open the record"}</span>
      </RecordLink>
      {record && (
        <>
          <span style={faint}>{[record.email || "no email", record.mobile].filter(Boolean).join(" \u00b7 ")}</span>
          {record.officeId ? (
            <span style={{ ...faint, color: T.textSoft }}>
              <RecordLink kind="office" id={record.officeId} faint>
                {[record.officeName, record.officeAddress].filter(Boolean).join(" \u2014 ")}
              </RecordLink>
            </span>
          ) : (
            <span style={{ ...faint, color: T.red }}>
              At no agency. Adding their agency asks which, from what this matter says — or open the
              record to set one.
            </span>
          )}
          {record.status && record.status !== "active" && (
            <span style={faint}>Not approved yet — linking approves it.</span>
          )}
        </>
      )}
    </div>
  );
};

const UnlinkedAgentMatters = ({ onOpenMatter, onToast, onError }) => {
  const [page, setPage] = useState(null);
  const [busy, setBusy] = useState("");
  // The records a name matched, per matter, once an add came back asking.
  const [dupes, setDupes] = useState({});
  // The agencies of that name, per matter, once an add came back asking which —
  // with what the matter says and the answers already given, sent again with
  // the pick.
  const [officeAsk, setOfficeAsk] = useState({});

  const load = useCallback((cursor) => {
    let cancelled = false;
    apiFetch(`/matter-agents/unmatched?limit=25${cursor ? `&cursor=${cursor}` : ""}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        const next = d?.ok ? d : { matters: [], total: 0, nextCursor: null };
        setPage(prev => (cursor && prev ? { ...next, matters: [...prev.matters, ...next.matters] } : next));
      })
      .catch(err => { if (!cancelled) { setPage({ matters: [], total: 0, nextCursor: null }); onError?.(err.message); } });
    return () => { cancelled = true; };
  }, [onError]);
  useEffect(() => load(), [load]);

  // Linked, so it is no longer this list's — taken off rather than re-read,
  // since a re-read would page the list back to the top.
  const done = (ref) => {
    setPage(p => ({ ...p, matters: p.matters.filter(m => m.matterRef !== ref), total: Math.max(0, (p.total || 1) - 1) }));
    setDupes(d => { const { [ref]: _gone, ...rest } = d; return rest; });
    setOfficeAsk(d => { const { [ref]: _gone, ...rest } = d; return rest; });
  };

  const link = async (ref, agentId) => {
    const { status, out } = await postDirectoryJson(`/matter-agent/${encodeURIComponent(ref)}`, { agentId });
    if (!out.ok) throw new Error(out.error || `Could not link the record (${status})`);
  };

  // `answers` is what a person has already said — `confirm`, `officeId`,
  // `newOffice` — carried forward so the second question does not undo the first.
  const add = async (ref, answers = {}) => {
    setBusy(ref);
    try {
      const { status, out } = await postDirectoryJson(
        `/matter-agent/${encodeURIComponent(ref)}/add-to-directory`, answers);
      if (out.code === "possible_duplicates") {
        setDupes(d => ({ ...d, [ref]: out.candidates || [] }));
        return;
      }
      if (out.code === "choose_office") {
        setDupes(d => { const { [ref]: _gone, ...rest } = d; return rest; });
        setOfficeAsk(d => ({ ...d, [ref]: { offices: out.offices || [], typed: out.typed || {}, answers } }));
        return;
      }
      if (!out.ok) throw new Error(out.error || `Could not add them (${status})`);
      await link(ref, out.agentId);
      onToast?.(addedWording(out));
      done(ref);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy("");
    }
  };

  // A record somebody recognised. Approved as it is linked, for the same rule
  // an add follows: staff choosing it is staff vouching for it.
  const pickRecord = async (ref, candidate) => {
    setBusy(ref);
    try {
      await link(ref, candidate.agentId);
      if (candidate.status !== "active") {
        await postDirectoryJson(`/directory/agents/${encodeURIComponent(candidate.agentId)}/publish`, { published: true });
      }
      onToast?.(`${candidate.name || "That record"} is linked to this matter and approved.`);
      done(ref);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy("");
    }
  };

  if (!page) return <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>;
  const rows = page.matters || [];
  if (!rows.length) {
    return <div style={{ fontSize: 12, color: T.textFaint }}>Every live matter naming an agent is linked to a record.</div>;
  }
  const faint = { fontSize: 11.5, color: T.textFaint, display: "block" };
  return (
    <>
      <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 8 }}>
        Showing {rows.length} of {page.total}
      </div>
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
        {rows.map((m, i) => {
          const said = m.said || {};
          const canAdd = Boolean(said.contactName && said.email);
          const found = dupes[m.matterRef];
          const asking = officeAsk[m.matterRef];
          return (
            <div key={m.matterRef} style={{ padding: "10px 14px", borderTop: i ? `1px solid ${T.border}` : "none" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                <span style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <span style={{ fontSize: 13, color: T.text }}>
                    {said.contactName || said.email || said.agencyName || "(no agent named)"}
                  </span>
                  <span style={faint}>
                    {[said.contactName ? said.email || "no email" : "", said.agencyName].filter(Boolean).join(" · ")}
                  </span>
                  {/* An exact row says its own, fuller reason below; the
                      generic "may be them" line beside it contradicted it. */}
                  {!m.exact && <span style={faint}>{UNMATCHED_REASONS[m.reason] || ""}</span>}
                </span>
                <a href={matterHref(m.matterRef)}
                  onClick={e => {
                    // A plain click opens it here; ctrl, middle and *Open in a
                    // new tab* keep the real link, as a dashboard row does.
                    if (!onOpenMatter || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    onOpenMatter(m.matterRef);
                  }}
                  style={{ fontSize: 11.5, color: T.textSoft, textAlign: "right" }}>
                  {m.matterRef}{m.address ? ` — ${m.address}` : ""}
                </a>
              </div>
              {/* **Already a record under this email** — the queue's `exact`.
                  Offering *Add to directory* here read as a duplicate about to
                  be made (1760787: Michael Pastrikos, whose client typed "Ray
                  and White"). What it needs is the link the intake held back,
                  and the reason it held back, which is the panel's own words. */}
              {!found && !asking && m.exact && (
                <div style={{ marginTop: 8 }}>
                  <span style={{ ...faint, marginBottom: 6 }}>
                    Already in the directory under this email.{" "}
                    {notAutoAdoptedLine(m.notAutoAdopted) || "Not linked to this matter yet."}
                  </span>
                  {/* **The record the press links, shown before it is
                      pressed.** It named no record, so staff were asked to
                      vouch for one they could not see (1760758: Eric Kuan,
                      whose record has no agency — exactly what to know
                      first). `exactRecord` is the middleware's; an older one
                      sends only the id, and the name opens the record. */}
                  <ExactRecordCard id={m.exact} record={m.exactRecord} />
                  {/* **A record at no agency gets the agency step.** The add
                      route finds it by email, asks which agency (or makes one
                      from what the matter says — where a web search staff ran
                      on the matter lands), sets it on the record, and the
                      link follows (1760824, Danny See). One that names an
                      agency is linked as it is. */}
                  {m.exactRecord && !m.exactRecord.officeId ? (
                    <Btn variant="ghost" busy={busy === m.matterRef} onClick={() => add(m.matterRef)}>
                      Add their agency and link to {m.matterRef}
                    </Btn>
                  ) : (
                    <Btn variant="ghost" busy={busy === m.matterRef}
                         onClick={() => pickRecord(m.matterRef, {
                           agentId: m.exact,
                           name: m.exactRecord?.name || said.contactName,
                           status: m.exactRecord?.status,
                         })}>
                      Link this record to {m.matterRef}
                    </Btn>
                  )}
                </div>
              )}
              {!found && !asking && !m.exact && (
                <div style={{ marginTop: 8 }}>
                  {canAdd ? (
                    <Btn variant="ghost" busy={busy === m.matterRef} onClick={() => add(m.matterRef)}>
                      Add to directory
                    </Btn>
                  ) : (
                    <span style={faint}>
                      Needs the salesperson&rsquo;s name and email before they can be added — open the
                      matter to fill them in, or pick their record there.
                    </span>
                  )}
                </div>
              )}
              {found && (
                <div style={{ marginTop: 10, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 12, color: T.text, marginBottom: 8 }}>
                    These records may be the same agent. Use one, or add a new record if none is them.
                  </div>
                  {found.map(c => (
                    <div key={c.agentId} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                      <span style={{ flex: "1 1 200px", minWidth: 0 }}>
                        <span style={{ fontSize: 12.5, color: T.text }}>{c.name || "(no name)"}</span>
                        <span style={faint}>{[c.email || "no email", c.officeName].filter(Boolean).join(" · ")}</span>
                      </span>
                      <Btn variant="ghost" busy={busy === m.matterRef} onClick={() => pickRecord(m.matterRef, c)}>Use this one</Btn>
                    </div>
                  ))}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Btn variant="ghost" busy={busy === m.matterRef} onClick={() => add(m.matterRef, { confirm: true })}>Add as new anyway</Btn>
                    <Btn variant="ghost" onClick={() => setDupes(d => { const { [m.matterRef]: _x, ...rest } = d; return rest; })}>Cancel</Btn>
                  </div>
                </div>
              )}
              {asking && (
                <div style={{ marginTop: 10, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px" }}>
                  {/* **What the matter says first**, because it is what each
                      record below is compared against — the firm asked for it
                      in these words. */}
                  <div style={{ fontSize: 12, color: T.text, marginBottom: 4 }}>
                    For {m.matterRef}, the agent was associated with this office:
                  </div>
                  <div style={{ fontSize: 12.5, color: T.text, marginBottom: 2 }}>{asking.typed.name || said.agencyName}</div>
                  <span style={{ ...faint, marginBottom: 10 }}>
                    {[asking.typed.address || "no address on the matter", asking.typed.phone, asking.typed.email].filter(Boolean).join(" \u00b7 ")}
                  </span>
                  <div style={{ fontSize: 12, color: T.text, margin: "10px 0 8px" }}>
                    Which agency record is it? These are in the directory under that name.
                  </div>
                  {asking.offices.map(o => (
                    <div key={o.officeId} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                      <span style={{ flex: "1 1 200px", minWidth: 0 }}>
                        <span style={{ fontSize: 12.5, color: T.text }}>{o.contractName || o.tradingName}</span>
                        <span style={faint}>
                          {[o.address, o.agentCount != null ? `${o.agentCount} agent${o.agentCount === 1 ? "" : "s"}` : ""].filter(Boolean).join(" \u00b7 ")}
                        </span>
                      </span>
                      <Btn variant="ghost" busy={busy === m.matterRef}
                           onClick={() => add(m.matterRef, { ...asking.answers, officeId: o.officeId })}>This one</Btn>
                    </div>
                  ))}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Btn variant="ghost" busy={busy === m.matterRef}
                         onClick={() => add(m.matterRef, { ...asking.answers, newOffice: true })}>
                      New agency from {m.matterRef}&rsquo;s details
                    </Btn>
                    <Btn variant="ghost" onClick={() => setOfficeAsk(d => { const { [m.matterRef]: _x, ...rest } = d; return rest; })}>Cancel</Btn>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {page.nextCursor != null && (
        <div style={{ marginTop: 10 }}>
          <Btn variant="ghost" onClick={() => load(page.nextCursor)}>Show more</Btn>
        </div>
      )}
    </>
  );
};

// ── What is waiting for somebody ────────────────────────────────────────────
//
// Two lists with one thing in common: a record nobody has answered for.
// `pendingChanges` is an agent's own verification of the fields that cannot
// land on their say-so — their name, their email and their office, each of
// which decides something a contract states. `unverified` is a record the
// import created that nobody has looked at, which is most of what a seed
// produces and the reason 216 of them are not published.

// **What the record says now, and what the agent asked for.** The middleware
// sends `changes` — labelled rows with both values, and an office named rather
// than its id. An older middleware sends only `pendingChanges`, so that is
// shown as before: the field and the asked value, no "from" (`from: null`).
export const reviewChangeRows = (row = {}) => {
  if (Array.isArray(row.changes)) {
    return row.changes.map(c => ({
      field: c.field,
      label: c.label || c.field,
      from: String(c.currentDisplay || c.current || ""),
      to: String(c.askedDisplay || c.asked || ""),
    }));
  }
  return Object.entries(row.pendingChanges || {}).map(([field, value]) => ({
    field, label: field, from: null, to: value == null ? "" : String(value),
  }));
};

// `data` and `reload` come from `DirectoryPage`, which fetches the queue once so
// the tab can carry its count.
const DirectoryReview = ({ data, reload, fields, refresh, onToast, onError, onOpenMatter }) => {
  const [busy, setBusy] = useState("");
  const load = reload;

  const answer = async (agentId, approve) => {
    setBusy(agentId);
    try {
      const res = await apiFetch(`/directory/agents/${encodeURIComponent(agentId)}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ approve }),
      });
      const out = await res.json();
      if (!res.ok || !out.ok) throw new Error(out.error || `That did not work (${res.status})`);
      onToast?.(approve ? "Applied to the record" : "Discarded");
      load();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy("");
    }
  };

  if (!data) return <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>;

  return (
    <div>
      {/* **First, because it is the newest work.** These are live matters
          where somebody is waiting on the agent; the lists below are the
          directory's backlog. */}
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>
        Recent matters with an agent who is not in the directory
      </div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 10px" }}>
        The agent was typed in — on the client form or from a web search — rather than picked from
        a record, so they cannot see the matter in the agent portal and the client form cannot
        find them. <strong style={{ fontWeight: 500 }}>Add to directory</strong> makes them a record,
        approved and linked to the matter, after checking the directory for the same person.
      </p>
      <div style={{ marginBottom: 22 }}>
        <UnlinkedAgentMatters onOpenMatter={onOpenMatter} onToast={onToast} onError={onError} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>
        Changes an agent asked for
      </div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 10px" }}>
        The name, the email and the office wait for staff — each decides something a contract
        states, and an office is shared, so one agent's answer would move every other agent at
        that agency.
      </p>
      {data.pending.length === 0 ? (
        <div style={{ fontSize: 12, color: T.textFaint, marginBottom: 22 }}>Nothing is waiting.</div>
      ) : (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 22 }}>
          {data.pending.map((row, i) => (
            <div key={row.agentId} style={{ padding: "10px 14px", borderTop: i ? `1px solid ${T.border}` : "none" }}>
              <div style={{ fontSize: 13, color: T.text, marginBottom: 4 }}>
                <RecordLink kind="agent" id={row.agentId}>{row.name || "(no name)"}</RecordLink>
              </div>
              {reviewChangeRows(row).map(c => (
                <div key={c.field} style={{ fontSize: 12, color: T.textSoft, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "baseline" }}>
                  <span style={{ minWidth: 70 }}>{c.label}</span>
                  {c.from !== null && (
                    <>
                      <span style={{ color: T.textFaint, textDecoration: c.from ? "line-through" : "none" }}>{c.from || "(blank)"}</span>
                      <span aria-hidden="true" style={{ color: T.textFaint }}>→</span>
                    </>
                  )}
                  <span style={{ color: T.text }}>{c.to || "(blank)"}</span>
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <Btn variant="raised" busy={busy === row.agentId} onClick={() => answer(row.agentId, true)}>Apply</Btn>
                <Btn variant="ghost" busy={busy === row.agentId} onClick={() => answer(row.agentId, false)}>Discard</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>
        Records nobody has checked
      </div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 10px" }}>
        Every agent who cannot sign in to the agent portal, with the reason on each row. Open one
        and press <strong style={{ fontWeight: 500 }}>Approve and add to directory</strong>: they
        can then sign in, and the client form&rsquo;s agent search offers them on future matters.
      </p>
      {/* **The list itself, not a count and a pointer.** It said how many and
          sent staff to find each one by name on the Accounts tab; the same
          rows and presses are here instead, pinned to the middleware's own
          cannot-sign-in filter. */}
      <DirectoryAccounts only="cannotSignIn" fields={fields} refresh={refresh}
                         onToast={onToast} onError={onError} />

    </div>
  );
};

// ── Which record and which fields survive ───────────────────────────────────
//
// The press used to be a name, a reason and the word **Merge**, and what came
// out the other side was decided by two rules nobody could see: the middleware
// ranking the survivor by evidence, and *fill a blank, never overwrite an
// answer*. Both are right; neither was visible. And a merge is the one thing on
// this screen a person cannot undo, so *press it and look* was the wrong way to
// find out what it did.
//
// **The plan comes from the middleware and the chooser only renders it.** Which
// record survives, whose value applies per field and what is discarded are all
// its answers — computed by the same function the merge itself applies, so this
// cannot show one thing while the press does another. The labels travel with
// the rows for the same reason: a map kept here would drift from the field list
// the moment somebody added one, and what that looks like is a raw key in front
// of staff.

// The rows worth a line. A field nothing answers is not a decision, and a row
// per field whatever it says is how a list stops being read — the same reason
// a blank is left off the agent's summary.
export const chooserRows = (plan) => (plan?.rows || []).filter(r => r.values?.length);

// What one record holds for one row — its own value, never the merge's answer.
//
// **The cards are a transpose of the plan's rows**, and that is the whole of
// what this screen decides for itself. Which record survives, what applies and
// what is discarded all stay the middleware's, computed by the same function
// the merge runs.
export const heldOn = (row, id) => (row?.values || []).find(v => v.id === id) || null;

// The asides worth a line under the cards, and only these two.
//
// **What the losing record's values do is visible now rather than narrated.**
// The old chooser said *"jane@bp.com.au will not apply"* per field because the
// layout offered no other way to see it; two whole records side by side say it
// by standing there, and a line per differing field underneath is the noise
// that stops a list being read.
//
// What is left is the pair a reader cannot see by looking: a discard that is
// **kept** rather than lost, and a value the merge **refused to carry**. Both
// are the middleware's answers — `keptAside` and `heldBack` — not guesses here.
export const mergeNotes = (plan) => {
  const out = [];
  for (const row of chooserRows(plan)) {
    const kept = row.keptAside ? (row.discarded || []).map(d => d.value) : [];
    if (kept.length) out.push(`${kept.join(", ")} is kept on the record`);

    const held = (row.heldBack || []).map(h => h.display || h.value);
    // One tie exists — an agent's phone to their agency — and the sentence says
    // so plainly rather than in the general terms a second one would need.
    if (held.length) out.push(`${held.join(", ")} is not carried over — it is the other agency's number`);
  }
  return out;
};

// A merge with nothing chosen writes exactly what it always wrote, so the
// button says which of the two it is about to be.
export const mergeSummary = (plan) => {
  if (!plan) return "";
  const over = (plan.overwritten || []).length;
  const changed = Object.keys(plan.patch || {}).length;
  if (over) return `${over} answer${over === 1 ? "" : "s"} will be replaced`;
  if (changed) return `${changed} blank${changed === 1 ? "" : "s"} will be filled`;
  return "The surviving record does not change";
};

const MERGE_PATHS = {
  agent: (id, others, rest) => [`/directory/agents/${encodeURIComponent(id)}/merge`,
    { agentIds: others, ...rest }],
  office: (id, others, rest) => [`/directory/offices/${encodeURIComponent(id)}/merge`,
    { merge: others, ...rest }],
};

// ── The record first, and then the values the other record gets to keep ──────
//
// This began as a radio per *field*, then became one radio per *record* on the
// firm's own note — **you should not mix and match agency details**: an agent's
// agency, their phone and where the contract is sent all describe one place,
// and taking one record's office with another's number produces a record true
// of nobody.
//
// **The firm has now asked for the per-field choice back**, for a different
// case: two records of one agency where the trading name, the address and the
// suburb are right on one and the contract email is right on the other. Both
// asks are honoured by the order they are offered in. The record is still the
// decision — one radio keeps it whole, and with nothing else ticked the merge
// writes exactly what it always wrote — and a value from the record being
// merged away is an **exception somebody ticks**, row by row, on the card that
// holds it. Nothing is mixed unless somebody says so, which is the half of the
// earlier note that still stands.
//
// **The tie is still the middleware's.** An agent's phone follows their agency,
// and where a tick would carry one agency's number across, the plan's
// `heldBack` still says so under the cards. A tick can never *clear* a field:
// the middleware refuses a choice naming a blank, since clearing is a decision
// taken on the record itself.
//
// **And a record can be left out of the merge**, for a group of three or more
// where one of them is not the same thing at all — Marshall White Projects in a
// building of Marshall Whites. Leaving it out is for this merge only; *Not the
// same* on the review screen is what stops it being raised again.
export const MERGE_PROBLEMS = {
  marked_distinct: "These were marked as not the same. Take that marker off first if they are.",
  no_value: "A ticked value is blank on that record.",
  unknown_record: "A ticked value belongs to a record that is not in this merge.",
};

export const MergeChooser = ({ kind, members, survivorId: initial, onDone, onClose, onError }) => {
  const [survivorId, setSurvivorId] = useState(initial || members[0]?.id);
  const [choices, setChoices] = useState({});
  const [left, setLeft] = useState([]);
  // The plan is kept **with the question it answers**. Switching the kept
  // record re-asks the middleware, and until that answer lands the plan in hand
  // is the one for the *other* survivor — rendered against the new radio it
  // struck through every value on the record being kept and labelled every one
  // of the other's as filling its blanks, with no tick boxes, because every
  // row's `from` still named the old survivor. The firm met exactly that on an
  // Andrew Vandermeer pair.
  const [held, setHeld] = useState(null);
  const [busy, setBusy] = useState(false);

  const included = members.filter(m => !left.includes(m.id));
  const others = included.filter(m => m.id !== survivorId).map(m => m.id);
  const labelOf = (id) => members.find(m => m.id === id)?.label || id;
  const choiceKey = JSON.stringify(choices);
  const othersKey = others.join(",");

  const call = useCallback(async (rest) => {
    const [path, body] = MERGE_PATHS[kind](survivorId, othersKey.split(",").filter(Boolean), {
      ...rest,
      // Sent only where somebody ticked one: with none, the merge is exactly
      // the record-level one it always was.
      ...(choiceKey !== "{}" ? { choices: JSON.parse(choiceKey) } : {}),
    });
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const out = await res.json();
    if (!res.ok || !out.ok) throw new Error(out.error || `That did not work (${res.status})`);
    return out;
  }, [kind, survivorId, othersKey, choiceKey]);

  // Re-asked whenever the survivor, a tick or who is in the merge moves,
  // because each of those changes the answer. Nothing is written by it.
  useEffect(() => {
    if (!othersKey) { setHeld(null); return undefined; }
    let cancelled = false;
    const asked = { survivorId, othersKey, choiceKey };
    call({ preview: true })
      .then(out => { if (!cancelled) setHeld({ ...asked, plan: out.plan }); })
      .catch(err => { if (!cancelled) onError?.(err.message); });
    return () => { cancelled = true; };
  }, [call, othersKey, onError, survivorId, choiceKey]);

  // **Drawn only against the records it was asked about.** A plan for another
  // survivor, or another set of records, is not shown at all — the cards say
  // it is being worked out instead. A tick alone keeps the table up while the
  // answer refreshes, since only that one row moves, but the press waits for
  // the fresh answer either way: what it merges must be what the screen shows.
  const plan = held && held.survivorId === survivorId && held.othersKey === othersKey
    ? held.plan : null;
  const fresh = Boolean(plan) && held.choiceKey === choiceKey;

  const keep = (id) => {
    setSurvivorId(id);
    // A tick names a record whose value applies *instead of* the kept one's,
    // so a tick on the record now being kept means nothing.
    setChoices({});
  };
  const toggleChoice = (field, id) => setChoices(c => {
    const next = { ...c };
    if (next[field] === id) delete next[field]; else next[field] = id;
    return next;
  });
  const toggleLeft = (id) => {
    setLeft(l => (l.includes(id) ? l.filter(x => x !== id) : [...l, id]));
    setChoices(c => Object.fromEntries(Object.entries(c).filter(([, rid]) => rid !== id)));
  };

  const merge = async () => {
    setBusy(true);
    try {
      await call({});
      onDone?.(labelOf(survivorId), othersKey.split(",").filter(Boolean));
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy(false);
    }
  };

  const rows = chooserRows(plan);
  const notes = mergeNotes(plan);
  const problems = (plan?.problems || []).map(p => MERGE_PROBLEMS[p.reason] || p.reason);
  const canLeaveOut = members.length > 2;

  return (
    <div style={{ borderTop: `1px solid ${T.border}`, padding: "12px 14px", background: T.bg }}>
      <div style={{ fontSize: 11.5, color: T.textSoft, marginBottom: 10 }}>
        Pick the record to keep. Where the other one has a value you want instead, tick it on that
        card — anything not ticked stays as the kept record has it.
      </div>

      {!othersKey ? (
        <div style={{ fontSize: 12, color: T.textFaint, marginBottom: 10 }}>
          Nothing left to merge into it.
        </div>
      ) : !plan ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>Working out what this would do…</div>
      ) : (
        typeof plan.movedAgents === "number" && (
          <div style={{ fontSize: 11.5, color: T.textSoft, marginBottom: 8 }}>
            {plan.movedAgents} agent{plan.movedAgents === 1 ? "" : "s"} move to this record, and
            their contracts print its name and address.
          </div>
        )
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "stretch" }}>
        {members.map(m => {
          const kept = survivorId === m.id;
          const out = left.includes(m.id);
          return (
            <div key={m.id} style={{
              flex: "1 1 260px", borderRadius: 10, padding: "10px 12px",
              border: `1px solid ${kept ? T.text : T.border}`,
              background: kept ? T.white : T.bgAlt, opacity: out ? 0.55 : 1,
            }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: out ? "default" : "pointer" }}>
                  <input type="radio" name={`survivor-${members.map(x => x.id).join("")}`}
                         checked={kept} disabled={out} onChange={() => keep(m.id)}
                         style={{ width: 14, height: 14, accentColor: T.text }} />
                  <span style={{ fontSize: 12, color: kept ? T.text : T.textSoft, fontWeight: kept ? 600 : 400 }}>
                    {kept ? "Kept" : out ? "Left out of this merge" : "Merged away"}
                  </span>
                </label>
                <span style={{ fontSize: 12, color: T.textSoft }}>{m.label}</span>
                {canLeaveOut && !kept && (
                  <button type="button" onClick={() => toggleLeft(m.id)}
                    style={{ marginLeft: "auto", background: "none", border: "none", padding: 0, cursor: "pointer",
                             fontFamily: "inherit", fontSize: 11, color: T.textFaint, textDecoration: "underline" }}>
                    {out ? "Put back in" : "Leave out"}
                  </button>
                )}
              </div>
              {plan && !out && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <tbody>
                    {rows.map(row => {
                      const held = heldOn(row, m.id);
                      const applies = held && row.from === m.id;
                      const replaced = kept && held && row.from && row.from !== m.id;
                      // Only a value that would change something is tickable:
                      // one the kept record already agrees with is not a choice.
                      const tickable = !kept && held && held.value !== row.value;
                      return (
                        <tr key={row.field} style={{ borderTop: `1px solid ${T.border}` }}>
                          <td style={{ padding: "6px 8px 6px 0", color: T.textSoft, width: 96, verticalAlign: "top" }}>
                            {row.label}
                          </td>
                          <td style={{ padding: "6px 0", verticalAlign: "top", color: held ? T.text : T.textFaint }}>
                            <span style={{ textDecoration: replaced ? "line-through" : "none",
                                           color: replaced ? T.textFaint : undefined }}>
                              {held ? (held.display || held.value) : "—"}
                            </span>
                            {held?.note && (
                              <span style={{ display: "block", fontSize: 11, color: T.textFaint }}>{held.note}</span>
                            )}
                            {tickable && (
                              <label style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 11,
                                              color: T.textSoft, marginTop: 3, cursor: "pointer" }}>
                                <input type="checkbox" checked={choices[row.field] === m.id}
                                       onChange={() => toggleChoice(row.field, m.id)}
                                       style={{ accentColor: T.text }} />
                                Use this instead
                              </label>
                            )}
                            {!kept && applies && !tickable && (
                              <span style={{ display: "block", fontSize: 11, color: T.textSoft }}>
                                Fills the kept record's blank
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      {notes.map(note => (
        <div key={note} style={{ fontSize: 11.5, color: T.textSoft, marginTop: 8 }}>{note}</div>
      ))}
      {problems.map(p => (
        <div key={p} style={{ fontSize: 11.5, color: T.red, marginTop: 8 }}>{p}</div>
      ))}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
        <Btn busy={busy} disabled={!fresh || !othersKey || problems.length > 0 || busy} onClick={merge}>
          {busy ? "Merging…" : `Merge into ${labelOf(survivorId)}`}
        </Btn>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <span style={{ fontSize: 11.5, color: T.textFaint }}>
          {busy ? "Moving the records across — this takes a few seconds"
            : fresh ? mergeSummary(plan) : othersKey ? "Working out what this would do…" : ""}
        </span>
      </div>
    </div>
  );
};

// ── The same person, twice ──────────────────────────────────────────────────
//
// Three lists, and the difference between them is who decides.
//
// **`auto` needs nobody.** Those groups share an email address exactly, which
// is the one signal strong enough to merge on — so the press is a bulk one and
// the survivor is chosen by evidence: verified first, then an agent's own
// confirmation above staff's above an import's.
//
// **`review` is a pair somebody answers.** The same mobile, or the same name at
// the same office — real most of the time and a guess, so an unmerged duplicate
// stays visible where a wrong merge would not.
//
// **`offices` has no auto tier at all, and that is the rule the whole import is
// built around.** 69 live trading names in the firm's own sheet belong to
// genuinely different offices, and a wrong office merge points one agency's
// agents at another agency's address. So the survivor is *named* rather than
// chosen — its contract name is what prints on the particulars of sale, which
// makes picking it the decision rather than a detail of it.
// An office opened for editing from the review list, read whole first. The
// duplicates route sends a composed line for each record rather than its
// fields, and `OfficeDetail` edits the fields — so it is fetched, the way the
// account panel fetches its own record.
const OfficeEditor = ({ officeId, fields, onChanged, onToast, onError }) => {
  const [office, setOffice] = useState(null);
  const load = useCallback(() => {
    let cancelled = false;
    apiFetch(`/directory/offices/${encodeURIComponent(officeId)}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled || !d?.ok) return;
        setOffice({ ...d.office, hasAddress: Boolean(String(d.office?.address || "").trim()) });
      })
      .catch(err => onError?.(err.message));
    return () => { cancelled = true; };
  }, [officeId, onError]);
  useEffect(load, [load]);
  if (!office || !fields) return <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>;
  return (
    <OfficeDetail office={office} fields={fields}
                  onChanged={() => { load(); onChanged?.(); }}
                  onToast={onToast} onError={onError} />
  );
};

const linkStyle = {
  background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit",
  fontSize: 11.5, color: T.textSoft, textDecoration: "underline",
};

// One agent in a review pair: who they are, **which agency each record is at**
// — that is nearly always the whole question, since a pair is usually one
// person who moved — and the three things to do about a single record: check
// the web, edit it (the agency is a field on it), or archive it.
const ReviewAgent = ({ member }) => (
  <div style={{ padding: "6px 0" }}>
    <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
      <RecordLink kind="agent" id={member.agentId} style={{ fontSize: 13 }}>
        {member.name || "(no name)"}
      </RecordLink>
      <span style={{ fontSize: 11.5, color: T.textFaint }}>
        {[member.email, member.mobile].filter(Boolean).join(" · ") || "no email or mobile"}
      </span>
    </div>
    <div style={{ fontSize: 11.5, color: member.officeName ? T.textSoft : T.red }}>
      {member.officeName
        ? <RecordLink kind="office" id={member.officeId} faint>{member.officeName}</RecordLink>
        : "at no agency"}
      {(member.linkedOffices || []).length > 0 && (
        <span style={{ color: T.textFaint }}>
          {" · also "}
          {member.linkedOffices.map((o, i) => (
            <span key={o.officeId}>
              {i ? ", " : ""}
              <RecordLink kind="office" id={o.officeId} faint>{o.name || o.officeId}</RecordLink>
            </span>
          ))}
        </span>
      )}
    </div>
    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
      <WebCheck kind="agent" id={member.agentId} record={member} />
      <OpenRecord kind="agent" id={member.agentId}>Edit, change agency or archive</OpenRecord>
    </div>
  </div>
);

// The underlined press that opens a record's modal, where a sentence names the
// action rather than the record.
const OpenRecord = ({ kind, id, children }) => {
  const records = useContext(DirectoryRecords);
  if (!records) return null;
  return <button type="button" style={linkStyle} onClick={() => records.open(kind, id)}>{children}</button>;
};

// ── Duplicates, and whether each record is right ────────────────────────────
//
// The tab was *Duplicates*, and what the firm does on it is wider: which of two
// records is the agent, whether either is at the agency it says, whether an
// office's address is the one notices should go to. So it is named for that,
// and each record carries the presses to settle it without leaving the list —
// edit, change agency, archive, check the web, and **say the two are not the
// same**, which is stored on both and stops the pair being raised again.
const DirectoryDuplicates = ({ fields, refresh, onToast, onError }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  // One chooser open at a time. Two would each be previewing a different merge
  // of possibly the same record, and the second answer would land on the first.
  const [open, setOpen] = useState("");
  // Records merged away, taken off the list the moment the merge answers. The
  // reload behind it reads the whole directory — nearly five thousand records —
  // so a pair left on screen for those seconds read as a merge that had not
  // happened, and invited a second press.
  const [gone, setGone] = useState([]);

  const load = useCallback(() => {
    let cancelled = false;
    apiFetch("/directory/duplicates")
      .then(r => r.json())
      .then(d => { if (!cancelled && d?.ok) { setData(d); setGone([]); } })
      .catch(err => onError?.(err.message));
    return () => { cancelled = true; };
  }, [onError]);

  useEffect(load, [load]);
  useRefreshed(refresh, load);

  const post = async (key, path, body, describe) => {
    setBusy(key);
    try {
      const res = await apiFetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const out = await res.json();
      if (!res.ok || !out.ok) throw new Error(out.error || `That did not work (${res.status})`);
      onToast?.(describe(out));
      load();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy("");
    }
  };

  if (!data) return <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>;

  const merged = (into, ids) => {
    setOpen("");
    setGone(g => [...g, ...(ids || [])]);
    onToast?.(`Merged into ${into}`);
    load();
  };
  const settled = (ids) => ids.some(id => gone.includes(id));
  const review = data.review.filter(p => !settled(p.members.map(m => m.agentId)));
  const officeGroups = data.offices.filter(g => !settled(g.members.map(m => m.officeId)));
  const names = (members) => members.map(m => m.name || m.email || m.agentId).join(", ");

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>
        The same email address
      </div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 10px" }}>
        The one signal strong enough to merge without a person. Nothing is deleted — a merged
        record keeps its fields and points at the survivor, so a dead id still resolves. A group
        where somebody marked two of them as not the same is left to the pairs below instead.
      </p>
      {data.auto.length === 0 ? (
        <div style={{ fontSize: 12, color: T.textFaint, marginBottom: 22 }}>None.</div>
      ) : (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 8 }}>
            {data.auto.length} group{data.auto.length === 1 ? "" : "s"}: {data.auto.map(g => names(g.members)).slice(0, 5).join(" · ")}
            {data.auto.length > 5 ? " · …" : ""}
          </div>
          <Btn variant="ghost" busy={busy === "auto"}
               onClick={() => post("auto", "/directory/duplicates/merge", { confirm: true },
                 out => `Merged ${out.merged} group${out.merged === 1 ? "" : "s"}`)}>
            Merge them
          </Btn>
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>
        Agents who might be the same person
      </div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 10px" }}>
        Nearly always one agent who has moved agency and left a card behind. Check the web for
        where they work now, fix either record in place, then merge — or say they are not the
        same, and the pair is not raised again.
      </p>
      {review.length === 0 ? (
        <div style={{ fontSize: 12, color: T.textFaint, marginBottom: 22 }}>None.</div>
      ) : (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 22 }}>
          {review.map((pair, i) => {
            const key = pair.members.map(m => m.agentId).join("+");
            const [a, b] = pair.members;
            return (
              <div key={key} style={{ padding: "10px 14px", borderTop: i ? `1px solid ${T.border}` : "none" }}>
                <div style={{ fontSize: 11.5, color: T.textFaint, marginBottom: 4 }}>{pair.reason}</div>
                {pair.members.map(m => (
                  <ReviewAgent key={m.agentId} member={m} />
                ))}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
                  <Btn variant="ghost" size="sm" onClick={() => setOpen(open === key ? "" : key)}>
                    {open === key ? "Close" : "Review and merge"}
                  </Btn>
                  <Btn variant="ghost" size="sm" busy={busy === `distinct:${key}`}
                       onClick={() => post(`distinct:${key}`,
                         `/directory/agents/${encodeURIComponent(a.agentId)}/distinct`,
                         { from: [b.agentId] },
                         () => "Marked as two different people — this pair will not be raised again")}>
                    Not the same person
                  </Btn>
                </div>
                {open === key && (
                  <div style={{ margin: "8px -14px -10px" }}>
                    <MergeChooser
                      kind="agent"
                      survivorId={pair.survivorId}
                      members={pair.members.map(m => ({
                        id: m.agentId,
                        label: m.name || m.email || m.agentId,
                        sub: m.email || "",
                      }))}
                      onClose={() => setOpen("")}
                      onDone={merged}
                      onError={onError}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>
        Agencies recorded twice
      </div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 10px" }}>
        Never merged automatically: a wrong one points an agency's agents at another agency's
        address. Opening a group checks each record's address against the web. Merging moves every
        agent at the merged-away office to the one you keep — the agents themselves are not merged
        with each other, and their old office stays on their history. An agency that only shares
        the building is marked as not the same, and leaves the group.
      </p>
      {officeGroups.length === 0 ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>None.</div>
      ) : (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10 }}>
          {officeGroups.map((group, i) => {
            const key = group.members.map(o => o.officeId).join("+");
            const isOpen = open === key;
            return (
            <div key={key}
                 style={{ padding: "10px 14px", borderTop: i ? `1px solid ${T.border}` : "none" }}>
              <div style={{ fontSize: 11.5, color: T.textFaint, marginBottom: 6 }}>{group.reason}</div>
              {group.members.map(office => {
                const others = group.members.filter(o => o.officeId !== office.officeId).map(o => o.officeId);
                return (
                  <div key={office.officeId} style={{ padding: "4px 0" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                      <RecordLink kind="office" id={office.officeId} style={{ fontSize: 13 }}>
                        {office.contractName || office.tradingName}
                      </RecordLink>
                      {/* **How many agents are at it.** They are what an office
                          merge moves, so this is what the survivor is picked
                          from — and a record with none is usually the stray. */}
                      <Chip label={`${office.agentCount ?? 0} agent${office.agentCount === 1 ? "" : "s"}`}
                            tone={office.agentCount ? "ink" : "faint"} />
                    </div>
                    <div style={{ fontSize: 11.5, color: T.textFaint }}>{office.address || "no address recorded"}</div>
                    {isOpen && (
                      <div style={{ marginTop: 6 }}>
                        <WebCheck kind="office" id={office.officeId} record={office} auto />
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                      <OpenRecord kind="office" id={office.officeId}>Edit or archive</OpenRecord>
                      <button type="button" style={linkStyle} disabled={busy === `distinct:${office.officeId}`}
                        onClick={() => post(`distinct:${office.officeId}`,
                          `/directory/offices/${encodeURIComponent(office.officeId)}/distinct`,
                          { from: others },
                          () => `${office.contractName || office.tradingName} marked as a different agency`)}>
                        Not the same agency as the others
                      </button>
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop: 8 }}>
                <Btn variant="ghost" size="sm" onClick={() => setOpen(isOpen ? "" : key)}>
                  {isOpen ? "Close" : "Check and merge"}
                </Btn>
              </div>
              {isOpen && (
                <div style={{ margin: "8px -14px -10px" }}>
                  <MergeChooser
                    kind="office"
                    members={group.members.map(o => ({
                      id: o.officeId,
                      label: o.contractName || o.tradingName,
                      sub: o.address || "",
                    }))}
                    onClose={() => setOpen("")}
                    onDone={merged}
                    onError={onError}
                  />
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── The agencies themselves ────────────────────────────────────────────────
//
// **Every staff office route had been API-only.** `POST /directory/offices/:id`
// has existed since the directory shipped and there was no screen for it, so a
// wrong trading name or a missing address in a seed of 2,016 records could be
// corrected only from a curl with the staff token in it. That is the half the
// firm reported: *"there is no list of agents offices for me to amend details
// for"*.
//
// It is also where the Shape Property Group case is actually settled. The
// duplicates list groups on the address alone, on the firm's instruction — so
// six records at four addresses surface as one pair, and folding the other
// four is a merge somebody makes here by naming the survivor.
const OfficeDetail = ({ office, fields, onChanged, onToast, onError }) => {
  const [busy, setBusy] = useState("");
  const [agents, setAgents] = useState(null);
  const [folding, setFolding] = useState("");

  useEffect(() => {
    let cancelled = false;
    setAgents(null);
    apiFetch(`/directory/offices/${encodeURIComponent(office.officeId)}/agents`)
      .then(r => r.json())
      .then(d => { if (!cancelled && d?.ok) setAgents(d.agents || []); })
      .catch(err => onError?.(err.message));
    return () => { cancelled = true; };
  }, [office.officeId, onError]);

  // One door for every press here, like the account panel's: each is a POST
  // that changes the record, so each re-reads afterwards — a panel showing a
  // stale row beside a button somebody just pressed is what gets pressed twice.
  const act = async (what, path, body, describe) => {
    setBusy(what);
    try {
      const res = await apiFetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {}),
      });
      const out = await res.json();
      // **The middleware's own sentence, never one invented here.** Its
      // refusals are specific — *six agents are still at this office, move
      // them or merge it* — and replacing that with "That did not work" is how
      // somebody comes to press a button twice.
      if (!res.ok || !out.ok) throw new Error(out.error || `That did not work (${res.status})`);
      onToast?.(describe(out));
      onChanged?.();
      return out;
    } catch (err) {
      onError?.(err.message);
      return null;
    } finally {
      setBusy("");
    }
  };

  const archived = office.status === "archived";

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16 }}>
      {!office.hasAddress && (
        <div style={{ fontSize: 12, color: T.red, marginBottom: 10 }}>
          No street address, so this office is not offered on a matter — adopting it would put the
          agency's name on a contract with nowhere to serve a notice. Give it an address, or merge
          it into the record that has one.
        </div>
      )}

      <RecordForm
        fields={fields.office}
        record={office}
        saving={busy === "save"}
        onError={onError}
        onSave={async (draft) => Boolean(await act("save",
          `/directory/offices/${encodeURIComponent(office.officeId)}`, draft,
          () => "Saved to the directory"))}
      />

      <div style={{ borderTop: `1px solid ${T.border}`, margin: "14px 0 12px" }} />

      {/* **Who would move if this record were folded into another.** The
          agents are the substance of an office merge — left pointing at a
          tombstone they resolve to nothing at print time — so the count is
          what somebody decides the survivor from. */}
      <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginBottom: 6 }}>
        {agents === null ? "Agents…" : `${agents.length} agent${agents.length === 1 ? "" : "s"} at this office`}
      </div>
      {agents && agents.length > 0 && (
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 12, display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
          {agents.map(a => (
            <RecordLink key={a.agentId} kind="agent" id={a.agentId}>{a.name || "(no name)"}</RecordLink>
          ))}
        </div>
      )}

      <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginBottom: 4 }}>
        Fold another office into this one
      </div>
      <p style={{ fontSize: 11.5, color: T.textFaint, margin: "0 0 8px", maxWidth: "62ch" }}>
        The one you pick stops being offered and its agents move here, so this record's name and
        address are what their contracts will print. Nothing is deleted — the old id still
        resolves to this one.
      </p>
      <OfficePicker value={folding} onPick={setFolding} onClear={() => setFolding("")}
                    chosenLabel="To fold in:" onError={onError} />
      {folding && folding !== office.officeId && (
        <div style={{ marginTop: 8 }}>
          <Btn size="sm" busy={busy === "merge"}
               onClick={() => act("merge",
                 `/directory/offices/${encodeURIComponent(office.officeId)}/merge`,
                 { merge: [folding] },
                 out => `Folded in — ${out.movedAgents || 0} agent${out.movedAgents === 1 ? "" : "s"} moved`)
                 .then(ok => { if (ok) setFolding(""); })}>
            Fold it in
          </Btn>
        </div>
      )}

      <div style={{ borderTop: `1px solid ${T.border}`, margin: "14px 0 12px" }} />

      {/* **Archived rather than deleted, and that is not caution.** A matter's
          adoption reads the office live on every render, so a deleted record
          blanks the agency's name and the address notices are served on, on
          every matter that ever adopted it. The middleware refuses while
          agents are on it and says how many. */}
      <Btn variant="ghost" size="sm" busy={busy === "archive"}
           onClick={() => act("archive",
             `/directory/offices/${encodeURIComponent(office.officeId)}/archive`,
             { archived: !archived },
             () => archived ? "Taken out of the archive" : "Archived")}>
        {archived ? "Take out of the archive" : "Archive this office"}
      </Btn>
      <span style={{ fontSize: 10.5, color: T.textFaint, marginLeft: 8 }}>
        {archived
          ? "Archived, so nothing is offered it. Anything already pointing at it still prints."
          : "Stops it being offered anywhere. Reversible, and refused while agents are on it."}
      </span>
    </div>
  );
};

export const DirectoryOffices = ({ fields, refresh, onToast, onError }) => {
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [withArchived, setWithArchived] = useState(false);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    const id = setTimeout(() => setQ(typed.trim()), 300);
    return () => clearTimeout(id);
  }, [typed]);

  const load = useCallback((cursor) => {
    let cancelled = false;
    if (!cursor) setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (withArchived) params.set("archived", "true");
    if (cursor) params.set("cursor", String(cursor));
    apiFetch(`/directory/offices?${params.toString()}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled || !d?.ok) return;
        // A further page is appended; a new search replaces, or the old
        // search's rows would sit above the new one's.
        setPage(prev => (cursor && prev
          ? { ...d, offices: [...prev.offices, ...d.offices] }
          : d));
      })
      .catch(err => onError?.(err.message))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q, withArchived, onError]);

  useEffect(() => load(), [load]);
  useRefreshed(refresh, load);

  const create = async () => {
    const tradingName = newName.trim();
    if (!tradingName) return;
    setCreating(true);
    try {
      const res = await apiFetch("/directory/offices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tradingName }),
      });
      const out = await res.json();
      if (!res.ok || !out.ok) throw new Error(out.error || `That did not work (${res.status})`);
      onToast?.("Created — give it an address before a matter can be offered it");
      setNewName("");
      setOpenId(out.officeId);
      load();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setCreating(false);
    }
  };

  const rows = page?.offices || [];

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <input
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder="Agency name, suburb or address"
          style={{ ...inputStyle, maxWidth: 360 }}
        />
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: T.textSoft, cursor: "pointer" }}>
          <input type="checkbox" checked={withArchived}
                 onChange={e => setWithArchived(e.target.checked)}
                 style={{ accentColor: T.brown }} />
          Include archived
        </label>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") create(); }}
          placeholder="New agency's trading name"
          style={{ ...inputStyle, maxWidth: 260 }}
        />
        <Btn variant="ghost" size="sm" disabled={!newName.trim()} busy={creating} onClick={create}>
          Add an agency
        </Btn>
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: T.textFaint }}>
          {q ? `Nothing matched “${q}”.` : "No agencies recorded."}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 8 }}>
            Showing {rows.length} of {page.total}
            {page.all != null && page.all !== page.total && ` · ${page.all} in the directory`}
          </div>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
            {rows.map((row, i) => (
              <div key={row.officeId}>
                <button type="button" onClick={() => setOpenId(openId === row.officeId ? "" : row.officeId)}
                  style={{
                    width: "100%", textAlign: "left", background: openId === row.officeId ? T.bgAlt : "transparent",
                    border: "none", borderTop: i ? `1px solid ${T.border}` : "none",
                    padding: "10px 14px", cursor: "pointer", fontFamily: "inherit",
                    display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
                  }}>
                  <span style={{ flex: "1 1 220px", minWidth: 0 }}>
                    <span style={{ fontSize: 13, color: T.text }}>
                      {row.contractName || row.tradingName || "(unnamed)"}
                    </span>
                    <span style={{ fontSize: 11.5, color: T.textFaint, display: "block" }}>
                      {[row.address, row.suburb, row.postcode].filter(Boolean).join(" ") || "no address recorded"}
                      {` · ${row.agentCount} agent${row.agentCount === 1 ? "" : "s"}`}
                    </span>
                  </span>
                  <span style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {!row.hasAddress && <Chip label="No address" tone="red" />}
                    {row.status === "archived" && <Chip label="Archived" tone="faint" />}
                    {row.status === "unverified" && <Chip label="Unverified" tone="faint" />}
                  </span>
                </button>
                {openId === row.officeId && (
                  <div style={{ padding: "0 14px 14px" }}>
                    <OfficeDetail office={row} fields={fields} onChanged={() => load()}
                                  onToast={onToast} onError={onError} />
                  </div>
                )}
              </div>
            ))}
          </div>
          {page.nextCursor != null && (
            <div style={{ marginTop: 12 }}>
              <Btn variant="ghost" onClick={() => load(page.nextCursor)}>Show more</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const DIRECTORY_TABS = [
  { key: "accounts", label: "Accounts" },
  { key: "offices", label: "Agencies" },
  { key: "review", label: "Awaiting review" },
  { key: "duplicates", label: "Duplicates & data review" },
];

export const DirectoryPage = ({ onToast, onError, onOpenMatter }) => {
  const [tab, setTabState] = useState(() => {
    const t = readDirectoryState().tab;
    return DIRECTORY_TABS.some(d => d.key === t) ? t : "accounts";
  });
  const setTab = (t) => { setTabState(t); writeDirectoryState({ tab: t }); };
  // The review queue, fetched here rather than in its tab so the tab can say
  // how many are waiting without being opened.
  const [review, setReview] = useState(null);
  const loadReview = useCallback(() => {
    let cancelled = false;
    apiFetch("/directory/review")
      .then(r => r.json())
      .then(d => { if (!cancelled && d?.ok) setReview(d); })
      .catch(err => onError?.(err.message));
    return () => { cancelled = true; };
  }, [onError]);
  useEffect(loadReview, [loadReview]);
  const pendingCount = review?.pending?.length || 0;
  // Fetched once for the page rather than per panel: both edit forms are
  // generated from it, and a list that arrived twice is a list that can be
  // half-loaded on one of them.
  const fields = useDirectoryFields(onError);
  // The records opened from a name, newest last. A stack rather than one slot,
  // so the agency opened from inside an agent's modal has a way back to them.
  const [stack, setStack] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const records = useMemo(() => ({
    open: (kind, id) => setStack(s => {
      const top = s[s.length - 1];
      return top && top.kind === kind && top.id === id ? s : [...s, { kind, id }];
    }),
  }), []);
  const closeRecords = useCallback(() => setStack([]), []);

  return (
    <DirectoryRecords.Provider value={records}>
    <div>
      <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 24, color: T.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>Agent directory</h1>
      <p style={{ fontSize: 12.5, color: T.textFaint, margin: "0 0 20px", maxWidth: "70ch" }}>
        The agents and agencies this office records, and who among them can sign in to the agent
        portal. A record here is what a contract names and where notices are served, so it is
        edited on this screen rather than typed onto a matter.
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", borderBottom: `1px solid ${T.border}`, marginBottom: 16 }}>
        {DIRECTORY_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: "9px 16px", fontSize: 12, fontWeight: tab === t.key ? 500 : 400, color: tab === t.key ? T.text : T.textFaint, background: "transparent", border: "none", borderBottom: tab === t.key ? `2px solid ${T.brand}` : "2px solid transparent", cursor: "pointer", fontFamily: "Inter, sans-serif", marginBottom: -1 }}>
            {t.label}
            {t.key === "review" && pendingCount > 0 && (
              <span data-review-count style={{ marginLeft: 6, padding: "1px 7px", borderRadius: 999, fontSize: 10.5, fontWeight: 500, color: T.red, border: `1px solid ${T.red}` }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "accounts" && <DirectoryAccounts fields={fields} refresh={refresh} onToast={onToast} onError={onError} />}
      {tab === "offices" && (fields
        ? <DirectoryOffices fields={fields} refresh={refresh} onToast={onToast} onError={onError} />
        : <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>)}
      {tab === "review" && <DirectoryReview data={review} reload={loadReview} fields={fields} refresh={refresh} onToast={onToast} onError={onError} onOpenMatter={onOpenMatter} />}
      {tab === "duplicates" && <DirectoryDuplicates fields={fields} refresh={refresh} onToast={onToast} onError={onError} />}
      {stack.length > 0 && (
        <DirectoryRecordModal stack={stack} fields={fields}
          onBack={() => setStack(s => s.slice(0, -1))}
          onClose={closeRecords}
          onChanged={() => setRefresh(n => n + 1)}
          onToast={onToast} onError={onError} />
      )}
    </div>
    </DirectoryRecords.Provider>
  );
};

// Re-runs a list's loader when a record was changed in the modal. Its own
// effect rather than a dependency of the loader, so the first render loads
// once and a search does not load twice.
const useRefreshed = (refresh, load) => {
  const seen = useRef(refresh);
  useEffect(() => {
    if (refresh === seen.current) return;
    seen.current = refresh;
    load();
  }, [refresh, load]);
};

// ── How much purchaser sharing is used ──────────────────────────────────────
//
// `GET /reports/purchaser-sharing`, on its own admin tab. The office wants to
// know whether agents use the sharing at all, which no single matter can say:
// the Purchasers panel on a matter shows that matter, and this is every
// released matter at once.
//
// **Everything counted is the middleware's** (`lib/purchaser-report.js`): what
// counts as the agent using it, how a purchaser came to have the contract, and
// the per-agent lines. This screen chooses a period and lays the answer out; a
// second copy of the rules here would be a report that disagrees with itself.
//
// **A staff send is shown, never counted as the agent using it.** It is the
// office doing the agent's job, which is exactly the thing the question is
// trying to see past.
//
// **The copied share link is the only trace of the agent taking it** — the
// link is derived, not stored — so its column says *copied*, not *shared*: a
// copy nobody opens is still a copy, and selecting the text by hand is not
// seen at all. The intro says so, so a low number is read for what it is.
export const SHARING_PERIODS = [
  ["all", "All time"],
  ["30", "Last 30 days"],
  ["90", "Last 90 days"],
  ["365", "Last 12 months"],
];

// The `since` a period asks for, as a date. Whole days in Melbourne are not
// worth the arithmetic here: a release on the boundary is a day either way.
export const sharingSince = (period, now = Date.now()) => {
  const days = Number(period);
  if (!days) return "";
  return new Date(now - days * 864e5).toISOString().slice(0, 10);
};

const sharingDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", timeZone: "Australia/Melbourne" });
};

const SharingStat = ({ label, value, note }) => (
  <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 16px", minWidth: 0 }}>
    <Label>{label}</Label>
    <div style={{ fontSize: 24, fontWeight: 300, color: T.text, letterSpacing: "-0.02em" }}>{value}</div>
    {note && <div style={{ fontSize: 11.5, color: T.textSoft, marginTop: 2 }}>{note}</div>}
  </div>
);

const th = { textAlign: "left", fontSize: 10, fontWeight: 600, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", padding: "8px 10px", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
const td = { fontSize: 12.5, color: T.text, padding: "9px 10px", borderBottom: `1px solid ${T.border}`, verticalAlign: "top" };
const num = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const dash = (n) => (n ? n : <span style={{ color: T.textFaint }}>–</span>);

export const PurchaserSharingPage = ({ onBack, onOpenMatter }) => {
  const [period, setPeriod] = useState("all");
  const [mode, setMode] = useState("matters");
  const [onlyUsed, setOnlyUsed] = useState(false);
  const [state, setState] = useState({ loading: true, error: "", report: null });

  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: "" }));
    const since = sharingSince(period);
    apiFetch(`/reports/purchaser-sharing${since ? `?since=${since}` : ""}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || !data.ok) throw new Error(data.error || (r.status === 404 ? "The report is not on the service yet." : `The report returned ${r.status}`));
        if (!cancelled) setState({ loading: false, error: "", report: data });
      })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message, report: null }); });
    return () => { cancelled = true; };
  }, [period]);

  const { loading, error, report } = state;
  const t = report?.totals;
  const rows = (report?.matters || []).filter(r => !onlyUsed || r.agentUsed);
  const agents = report?.byAgent || [];
  const pctOf = (n) => (t?.released ? ` · ${Math.round((n / t.released) * 100)}%` : "");

  return (
    <div>
      <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: 13, color: T.textFaint, fontFamily: "Inter, sans-serif", marginBottom: 20, padding: 0 }}>
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="m15 18-6-6 6-6"/></svg>
        Back to dashboard
      </button>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 6 }}>
        <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 24, color: T.text, margin: 0, letterSpacing: "-0.02em" }}>Purchaser sharing</h1>
        <select aria-label="Released in" value={period} onChange={(e) => setPeriod(e.target.value)}
          style={{ fontFamily: "Inter, sans-serif", fontSize: 13, padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.borderMid}`, background: T.white, color: T.text }}>
          {SHARING_PERIODS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </div>
      <p style={{ fontSize: 13, color: T.textSoft, margin: "0 0 18px", maxWidth: 760, lineHeight: 1.5 }}>
        Released matters, and whether the agent passed the contract on to purchasers from their download page — by
        emailing a purchaser, or by copying the property&rsquo;s share link. A link copied by selecting the text
        rather than pressing Copy is not seen. Sends staff made on the agent&rsquo;s behalf are shown, but not counted
        as the agent using it.
      </p>

      {loading && !report && <LoadingSpinner />}
      {error && <div style={{ fontSize: 13, color: T.red, marginBottom: 16 }}>Couldn&rsquo;t load the report — {error}</div>}

      {t && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 22, opacity: loading ? 0.5 : 1 }}>
            <SharingStat label="Released matters" value={t.released} note={`${t.agentOpened} opened by the agent`} />
            <SharingStat label="Agent used sharing" value={t.agentUsed} note={`of ${t.released}${pctOf(t.agentUsed)}`} />
            <SharingStat label="Share link copied" value={t.linkCopied} note={`matters${pctOf(t.linkCopied)}`} />
            <SharingStat label="Agent emailed purchasers" value={t.agentSent} note={`matters${pctOf(t.agentSent)}`} />
            <SharingStat label="Came in by share link" value={t.purchasersByRoute.shareLink} note={`purchasers, on ${t.viaShareLink} matters`} />
            <SharingStat label="Staff sent for the agent" value={t.staffSent} note="matters" />
            <SharingStat label="Purchasers" value={t.purchasers} note={`${t.purchasersOpened} opened · ${t.purchasersDownloaded} downloaded`} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 10 }}>
            <div role="tablist" aria-label="Report view" style={{ display: "inline-flex", background: T.bgAlt, borderRadius: 999, padding: 3 }}>
              {[["matters", "By matter"], ["agents", "By agent"]].map(([key, label]) => (
                <button key={key} role="tab" type="button" aria-selected={mode === key} onClick={() => setMode(key)}
                  style={{ border: "none", borderRadius: 999, padding: "5px 14px", fontSize: 12.5, fontFamily: "Inter, sans-serif", cursor: "pointer",
                    background: mode === key ? T.white : "transparent", color: mode === key ? T.text : T.textSoft, fontWeight: mode === key ? 500 : 400 }}>
                  {label}
                </button>
              ))}
            </div>
            {mode === "matters" && (
              <label style={{ fontSize: 12.5, color: T.textSoft, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={onlyUsed} onChange={(e) => setOnlyUsed(e.target.checked)} />
                Only where the agent used it
              </label>
            )}
          </div>

          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflowX: "auto" }}>
            {mode === "matters" ? (
              rows.length === 0
                ? <div style={{ padding: 20, fontSize: 13, color: T.textSoft }}>{onlyUsed ? "No agent has used sharing on a matter released in this period." : "No matters were released in this period."}</div>
                : (
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                    <thead><tr>
                      <th style={th}>Matter</th><th style={th}>Agent</th><th style={th}>Released</th>
                      <th style={{ ...th, textAlign: "right" }}>Link copied</th>
                      <th style={{ ...th, textAlign: "right" }}>Agent emailed</th>
                      <th style={{ ...th, textAlign: "right" }}>Via share link</th>
                      <th style={{ ...th, textAlign: "right" }}>Staff sent</th>
                      <th style={{ ...th, textAlign: "right" }}>Opened</th>
                      <th style={{ ...th, textAlign: "right" }}>Downloaded</th>
                    </tr></thead>
                    <tbody>
                      {rows.map(r => (
                        <tr key={r.ref} data-used={r.agentUsed ? "yes" : "no"}>
                          <td style={td}>
                            <button type="button" onClick={() => onOpenMatter?.(r.ref)}
                              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: 12.5, color: T.text, textAlign: "left" }}>
                              <span style={{ fontWeight: 500 }}>{r.ref}</span>
                              {r.address && <span style={{ display: "block", color: T.textSoft, fontSize: 11.5 }}>{r.address}</span>}
                            </button>
                          </td>
                          <td style={td}>
                            {r.agent.name || r.agent.agency || <span style={{ color: T.textFaint }}>No agent</span>}
                            {r.agent.name && r.agent.agency && <span style={{ display: "block", color: T.textSoft, fontSize: 11.5 }}>{r.agent.agency}</span>}
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>{sharingDate(r.releasedAt)}</td>
                          <td style={num} title={r.shareLinkFirstCopiedAt ? `First copied ${sharingDate(r.shareLinkFirstCopiedAt)}` : undefined}>{dash(r.shareLinkCopies)}</td>
                          <td style={num}>{dash(r.agentSent)}</td>
                          <td style={num}>{dash(r.shareLink)}</td>
                          <td style={num}>{dash(r.staffSent)}</td>
                          <td style={num}>{dash(r.opened)}</td>
                          <td style={num}>{dash(r.downloaded)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
            ) : (
              agents.length === 0
                ? <div style={{ padding: 20, fontSize: 13, color: T.textSoft }}>No matters were released in this period.</div>
                : (
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                    <thead><tr>
                      <th style={th}>Agent</th>
                      <th style={{ ...th, textAlign: "right" }}>Released</th>
                      <th style={{ ...th, textAlign: "right" }}>Used sharing</th>
                      <th style={{ ...th, textAlign: "right" }}>Link copied</th>
                      <th style={{ ...th, textAlign: "right" }}>Emailed purchasers</th>
                      <th style={{ ...th, textAlign: "right" }}>Via share link</th>
                      <th style={{ ...th, textAlign: "right" }}>Purchasers</th>
                    </tr></thead>
                    <tbody>
                      {agents.map(a => (
                        <tr key={a.key}>
                          <td style={td}>
                            {a.name || a.agency || a.email}
                            <span style={{ display: "block", color: T.textSoft, fontSize: 11.5 }}>{[a.name && a.agency, a.email].filter(Boolean).join(" · ")}</span>
                          </td>
                          <td style={num}>{a.released}</td>
                          <td style={num}>{dash(a.agentUsed)}</td>
                          <td style={num}>{dash(a.linkCopied)}</td>
                          <td style={num}>{dash(a.agentSent)}</td>
                          <td style={num}>{dash(a.viaShareLink)}</td>
                          <td style={num}>{dash(a.purchasers)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
            )}
          </div>
          <p style={{ fontSize: 11.5, color: T.textFaint, marginTop: 10 }}>
            Numbers per matter count purchasers; the cards and the agent view count matters. The sample matter is left out.
          </p>
        </>
      )}
    </div>
  );
};

// **What leaving with unsaved edits asks** (DA-11). The two forms on this
// screen — the firm record and the seasonal clause — save behind a button, so
// a correction typed and then navigated away from used to vanish with nothing
// said. One sentence, asked by App's navigation and by the browser on reload.
export const LEAVE_SETTINGS_PROMPT = "You have unsaved changes in Settings. Leave without saving them?";

// **In the practice look, Settings is one column of like cards.** Classic lays
// its cards out at their own widths (620 here, 900 for Users) from the left of
// a 1440 page, which on the practice look's grey ground read as a ragged strip
// down one side. One centred column, every card its full width and drawn as a
// Fluent card (no border, the card shadow). Done as one rule over the cards
// rather than a second copy of each — the classic look is untouched.
const FLUENT_SETTINGS_W = 880;
const FLUENT_SETTINGS_CSS = `[data-fluent-settings] > :not(h1):not(style) {
  max-width: none !important; width: 100%; box-sizing: border-box;
  border: none !important; border-radius: 8px !important; box-shadow: ${T.shadowCard};
  background: ${T.card} !important; padding: 20px !important; margin-bottom: 16px !important;
}`;

// Smokeball on the office settings. Status is an ordinary staff call, so
// `apiFetch` sends the dashboard token in `x-portal-token`.
//
// Connect is a browser navigation, and it has to stay on this page's host.
// Hosting proxies `/smokeball/**` to the middleware, so the path is
// `/smokeball/connect` on the current origin — contracts.provey.com.au, or
// whatever host this tab is already on. The Cloud Run hostname is a different
// origin; opening it is what comes back Access denied.
//
// The token is the one the dashboard already holds (`ACCESS_TOKEN`, from
// `s32_portal_token`). A navigation cannot send `x-portal-token`, so it
// travels as `?token=`, the same way other staff URLs the browser loads
// already pass that token. Not a new store.
export const smokeballConnectUrl = (token) => {
  const base = "/smokeball/connect";
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
};

// The status route says whether this office is connected. `connected` is the
// flag; `status: "connected"` is the same answer when that is how it is sent.
export const smokeballConnected = (data) => {
  if (!data || typeof data !== "object") return false;
  if (typeof data.connected === "boolean") return data.connected;
  return data.status === "connected";
};

const SmokeballSettings = () => {
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/smokeball/status")
      .then(async (r) => {
        const data = await r.json().catch(() => null);
        if (cancelled) return;
        if (!r.ok || !data || data.ok === false) {
          setState("unknown");
          return;
        }
        setState(smokeballConnected(data) ? "connected" : "off");
      })
      .catch((err) => {
        console.error("Failed to load Smokeball status", err);
        if (!cancelled) setState("unknown");
      });
    return () => { cancelled = true; };
  }, []);

  const connect = () => {
    setBusy(true);
    window.location.assign(smokeballConnectUrl(ACCESS_TOKEN));
  };

  const statusText = state === "loading" ? "Loading…"
    : state === "connected" ? "Connected"
    : state === "off" ? "Not connected"
    : "Could not check whether Smokeball is connected";

  return (
    <div data-smokeball-settings style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>Smokeball</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 8 }}>
        <span style={{ fontSize: 12, color: state === "unknown" ? T.textFaint : T.textSoft }}>{statusText}</span>
        <Btn variant="primary" size="md" onClick={connect} disabled={busy}>
          {busy ? "Connecting…" : "Connect"}
        </Btn>
      </div>
    </div>
  );
};

// **Searches: this firm's Trisearch / InfoTrack account.** The key is sent
// once; the middleware tries it against Trisearch, seals it and never sends it
// back — the screen shows the account it opens, its last four and the mode.
// With a key saved, the middleware reads the firm's orders every hour and
// (live) puts the searches and their returned documents on each matter. The
// default firm also has a Shadow/Live switch, because its nightly import and
// Zap 2 still run; going live means turning those off. See the middleware's
// docs/decisions/trisearch-sync.md.
export const trisearchStatusLine = (t) => {
  if (!t) return "Loading…";
  if (!t.configured) return "Not connected — searches arrive the old way, if at all.";
  const who = t.account && (t.account.name || t.account.email) ? ` · ${[t.account.name, t.account.email].filter(Boolean).join(", ")}` : "";
  const from = t.source === "environment" ? " · key from the server's configuration" : ` · key ending ${t.keyLast4 || "····"}`;
  const mode = t.mode === "live" ? "Live: searches and returns are put on each matter" : "Shadow: compared hourly, nothing written to matters";
  return `${mode}${from}${who}`;
};
export const TrisearchSettings = ({ api = apiFetch, onSaved, onError }) => {
  const [t, setT] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState("");
  const [problem, setProblem] = useState("");
  useEffect(() => {
    let cancelled = false;
    api("/trisearch/settings").then(r => r.json()).then(d => { if (!cancelled && d?.ok) setT(d); })
      .catch(() => { if (!cancelled) setProblem("Could not read the Trisearch settings."); });
    return () => { cancelled = true; };
  }, [api]);
  const save = async (body, label) => {
    setBusy(label);
    setProblem("");
    try {
      const res = await api("/trisearch/settings", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) throw new Error(d.error || `HTTP ${res.status}`);
      setT(d);
      setKey("");
      if (onSaved) onSaved("Trisearch settings saved");
    } catch (e) {
      setProblem(e.message);
      if (onError) onError(e.message);
    } finally {
      setBusy("");
    }
  };
  const goLive = () => {
    if (typeof window !== "undefined" && !window.confirm("Go live? Trisearch will put searches and returned documents on every open matter each hour. Turn off Zap 2 and the nightly search import first, or the same certificate will be listed twice.")) return;
    save({ mode: "live" }, "mode");
  };
  const remove = () => {
    if (typeof window !== "undefined" && !window.confirm("Remove the Trisearch key? Searches stop arriving from Trisearch.")) return;
    save({ clearKey: true }, "clear");
  };
  const fieldStyle = { flex: 1, minWidth: 220, padding: "7px 10px", fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 6, fontFamily: "Inter, sans-serif" };
  const lastRun = t?.lastRun;
  return (
    <div data-trisearch-settings style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>Searches: Trisearch / InfoTrack</div>
      <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 10px", lineHeight: 1.5 }}>
        Your firm's API key (Trisearch → Manage API keys). Each hour the portal reads your orders by matter number and puts the searches, and the documents as they come back — large ones included — on the matter.
      </p>
      <div data-trisearch-status style={{ fontSize: 12, color: T.textSoft, marginBottom: 10 }}>{trisearchStatusLine(t)}</div>
      {lastRun && (
        <div style={{ fontSize: 11.5, color: T.textFaint, marginBottom: 10 }}>
          Last run {new Date(lastRun.at).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}
          {lastRun.live ? ` · ${lastRun.live.created} searches added, ${lastRun.live.returned} documents filed${lastRun.live.failed?.length ? `, ${lastRun.live.failed.length} to attach by hand` : ""}` : lastRun.matters != null ? ` · ${lastRun.matters} matters compared` : ""}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input type="password" autoComplete="off" value={key} onChange={e => setKey(e.target.value)} placeholder={t?.configured ? "Replace the key" : "Paste the API key"} aria-label="Trisearch API key" style={fieldStyle} />
        <Btn variant="primary" size="md" onClick={() => save({ key: key.trim() }, "key")} disabled={!!busy || !key.trim()} busy={busy === "key"}>Save key</Btn>
      </div>
      {t?.configured && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
          {t.canSwitch && t.mode === "shadow" && <Btn variant="raised" onClick={goLive} disabled={!!busy} busy={busy === "mode"}>Go live…</Btn>}
          {t.canSwitch && t.mode === "live" && <Btn variant="ghost" onClick={() => save({ mode: "shadow" }, "mode")} disabled={!!busy} busy={busy === "mode"}>Back to shadow</Btn>}
          {t.source === "settings" && <Btn variant="ghost" onClick={remove} disabled={!!busy} busy={busy === "clear"}>Remove key</Btn>}
        </div>
      )}
      {problem && <div style={{ marginTop: 8, fontSize: 12, color: T.red }}>{problem}</div>}
    </div>
  );
};

// **Payments: this firm's own Stripe account and fee** for the new sale page.
// Keys are sent once and sealed by the middleware; nothing sends them back —
// the screen shows whether each is set, test or live, the key's last four,
// and the webhook address to give Stripe. A firm with no key takes no payment.
export const paymentStatusLine = (p) => {
  if (!p) return "Loading…";
  if (!p.configured) return "Not set up — the new sale page takes no payment.";
  const mode = p.mode === "live" ? "Live" : "Test mode";
  const hook = p.webhookConfigured ? "" : " · webhook secret not set yet, so no payment is confirmed";
  return `${mode} · key ending ${p.keyLast4 || "····"}${p.enabled === false ? " · switched off" : ""}${hook}`;
};
const PaymentSettings = ({ onSaved, onError }) => {
  const [p, setP] = useState(null);
  const [draft, setDraft] = useState({ testSecretKey: "", testWebhookSecret: "", liveSecretKey: "", liveWebhookSecret: "", mode: "test", feeExGst: "", listPriceIncGst: "", enabled: true, terms: "", promos: [] });
  const [busy, setBusy] = useState(false);
  const load = (data) => {
    setP(data);
    setDraft({ testSecretKey: "", testWebhookSecret: "", liveSecretKey: "", liveWebhookSecret: "", mode: data.mode || "test", feeExGst: data.feeExGst ?? "", listPriceIncGst: data.listPriceIncGst ?? "", enabled: data.enabled !== false, terms: (data.terms || []).join("\n"),
      promos: (data.promos || []).map(x => ({ ...x, maxUses: x.maxUses ?? "" })) });
  };
  const setPromo = (i, patch) => setDraft(d => ({ ...d, promos: d.promos.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  useEffect(() => {
    let cancelled = false;
    apiFetch("/payments/settings").then(r => r.json()).then(d => { if (!cancelled && d?.ok) load(d); })
      .catch(err => console.error("Failed to load payment settings", err));
    return () => { cancelled = true; };
  }, []);
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const body = { feeExGst: draft.feeExGst, listPriceIncGst: draft.listPriceIncGst, enabled: draft.enabled, terms: draft.terms,
        promos: draft.promos.filter(x => String(x.code || "").trim()).map(({ code, feeExGst, label, active, maxUses }) => ({ code, feeExGst, label, active, maxUses })), ...extra };
      // Each mode's keys only when typed; empty keeps what is stored.
      for (const k of ["testSecretKey", "testWebhookSecret", "liveSecretKey", "liveWebhookSecret"]) if (draft[k].trim()) body[k] = draft[k].trim();
      body.mode = draft.mode;
      const res = await apiFetch("/payments/settings", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Save returned ${res.status}`);
      load(data);
      onSaved?.();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy(false);
    }
  };
  const input = { fontFamily: "Inter, sans-serif", fontSize: 13, padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 6, width: "100%", boxSizing: "border-box" };
  const label = { fontSize: 12, color: T.textSoft, display: "block", marginBottom: 4 };
  return (
    <div data-payment-settings style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>Payments (Stripe)</div>
      <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 12 }}>{paymentStatusLine(p)}</div>
      {p && p.webhookUrl && (
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 12 }}>
          In Stripe, add a webhook to <code style={{ userSelect: "all" }}>{p.webhookUrl}</code> for <code>checkout.session.completed</code> and <code>payment_intent.canceled</code>, then paste its signing secret below.
        </div>
      )}
      {/* **Test and live side by side**, each with its own webhook signing
          secret (Stripe's modes have separate endpoints); the switch says
          which the new sale page uses. */}
      <div role="radiogroup" aria-label="Stripe mode" style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {["test", "live"].map(m => (
          <button key={m} type="button" role="radio" aria-checked={draft.mode === m} onClick={() => setDraft(d => ({ ...d, mode: m }))}
            style={{ fontFamily: "Inter, sans-serif", fontSize: 12, padding: "5px 14px", borderRadius: 999, cursor: "pointer", border: `1px solid ${draft.mode === m ? T.text : T.border}`, background: draft.mode === m ? T.text : T.white, color: draft.mode === m ? T.white : T.text }}>
            {m === "test" ? "Test mode" : "Live"}
          </button>
        ))}
        <span style={{ fontSize: 12, color: T.textSoft, alignSelf: "center" }}>{draft.mode === "test" ? "Cards are not charged — use Stripe's test card 4242 4242 4242 4242." : "Real cards are charged."}</span>
      </div>
      {["test", "live"].map(m => {
        const slot = (p?.modes || {})[m] || {};
        return (
          <div key={m} data-stripe-slot={m} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12, opacity: draft.mode === m ? 1 : 0.7 }}>
            <label><span style={label}>{m === "test" ? "Test" : "Live"} secret key {slot.configured ? `(ending ${slot.keyLast4 || "····"} — blank keeps it)` : ""}</span>
              <input type="password" autoComplete="off" placeholder={m === "test" ? "sk_test_…" : "sk_live_…"} style={input} value={draft[`${m}SecretKey`]} onChange={e => setDraft(d => ({ ...d, [`${m}SecretKey`]: e.target.value }))} /></label>
            <label><span style={label}>{m === "test" ? "Test" : "Live"} webhook signing secret {slot.webhookConfigured ? "(set — blank keeps it)" : ""}</span>
              <input type="password" autoComplete="off" placeholder="whsec_…" style={input} value={draft[`${m}WebhookSecret`]} onChange={e => setDraft(d => ({ ...d, [`${m}WebhookSecret`]: e.target.value }))} /></label>
          </div>
        );
      })}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label><span style={label}>Section 32 fee, ex GST ($)</span>
          <input inputMode="decimal" style={input} value={draft.feeExGst} onChange={e => setDraft(d => ({ ...d, feeExGst: e.target.value }))} /></label>
        <label><span style={label}>Usual price, inc GST ($) — shown struck through</span>
          <input inputMode="decimal" style={input} value={draft.listPriceIncGst} onChange={e => setDraft(d => ({ ...d, listPriceIncGst: e.target.value }))} /></label>
      </div>
      <label style={{ display: "block", marginTop: 12 }}>
        <span style={label}>Quote terms — one per line; {"{fee}"}, {"{feeIncGst}"} and {"{listPrice}"} are filled in{p && !p.termsCustom ? " (showing the default)" : ""}</span>
        <textarea rows={5} style={{ ...input, resize: "vertical", lineHeight: 1.5 }} value={draft.terms} onChange={e => setDraft(d => ({ ...d, terms: e.target.value }))} />
      </label>
      <div data-promo-codes style={{ marginTop: 12 }}>
        <span style={label}>Promotion codes — the fee a code gives, ex GST ($0 for free); searches stay at cost</span>
        {draft.promos.map((x, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr 1.6fr 0.8fr auto auto", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <input aria-label="Code" placeholder="EMAIL-FREE" style={{ ...input, textTransform: "uppercase" }} value={x.code} onChange={e => setPromo(i, { code: e.target.value.toUpperCase() })} />
            <input aria-label="Fee ex GST" inputMode="decimal" placeholder="0" style={input} value={x.feeExGst} onChange={e => setPromo(i, { feeExGst: e.target.value })} />
            <input aria-label="Label" placeholder="Shown to the client" style={input} value={x.label || ""} onChange={e => setPromo(i, { label: e.target.value })} />
            <input aria-label="Most uses" inputMode="numeric" placeholder="No limit" style={input} value={x.maxUses} onChange={e => setPromo(i, { maxUses: e.target.value })} />
            <label style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12, color: T.textSoft, whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={x.active !== false} onChange={e => setPromo(i, { active: e.target.checked })} />On{x.uses ? ` · used ${x.uses}` : ""}
            </label>
            <Btn variant="ghost" size="sm" onClick={() => setDraft(d => ({ ...d, promos: d.promos.filter((_, j) => j !== i) }))}>Remove</Btn>
          </div>
        ))}
        <Btn variant="ghost" size="sm" onClick={() => setDraft(d => ({ ...d, promos: [...d.promos, { code: "", feeExGst: 0, label: "", active: true, maxUses: "" }] }))}>Add a code</Btn>
      </div>
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: T.text, marginTop: 12 }}>
        <input type="checkbox" checked={draft.enabled} onChange={e => setDraft(d => ({ ...d, enabled: e.target.checked }))} />
        Take card payment on the new sale page
      </label>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        {(p?.modes || {})[draft.mode]?.configured && <Btn variant="ghost" size="md" onClick={() => save({ clearMode: draft.mode })} disabled={busy}>Remove {draft.mode} keys</Btn>}
        <Btn variant="primary" size="md" onClick={() => save()} disabled={busy}>{busy ? "Saving…" : "Save payments"}</Btn>
      </div>
    </div>
  );
};

export const SettingsPage = ({ onSaved, onAutomationSaved, onError, onDirtyChange }) => {
  // Matter numbers are the main module's, so only its look offers them.
  const fluent = useFluent();

  const [firm, setFirm] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/settings/firm")
      .then(r => r.json())
      .then(data => { if (!cancelled && data?.ok) { setFirm(data.firm || {}); setDraft(data.firm || {}); } })
      .catch(err => console.error("Failed to load firm settings", err));
    return () => { cancelled = true; };
  }, []);

  const dirty = firm && FIRM_FIELDS.some(f => (draft[f.key] || "") !== (firm[f.key] || ""));

  // Each form reports its own dirtiness and the page is dirty if either is.
  // The effect below is what tells App and the browser.
  const dirtyParts = useRef({});
  const [anyDirty, setAnyDirty] = useState(false);
  const markDirty = useCallback((part, isDirty) => {
    dirtyParts.current[part] = isDirty;
    setAnyDirty(Object.values(dirtyParts.current).some(Boolean));
  }, []);
  const markSeasonal = useCallback((d) => markDirty("seasonal", d), [markDirty]);
  const markInspection = useCallback((d) => markDirty("inspection", d), [markDirty]);
  useEffect(() => { markDirty("firm", Boolean(dirty)); }, [dirty, markDirty]);
  useEffect(() => {
    onDirtyChange?.(anyDirty);
    if (!anyDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = LEAVE_SETTINGS_PROMPT; return LEAVE_SETTINGS_PROMPT; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [anyDirty, onDirtyChange]);
  // Leaving the screen any way at all clears what App holds.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      // Send every key, including the emptied ones — this is a settings screen
      // showing current values, so a cleared box is an instruction.
      for (const f of FIRM_FIELDS) body[f.key] = draft[f.key] || "";
      const res = await apiFetch("/settings/firm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Save returned ${res.status}`);
      setFirm(data.firm || {});
      setDraft(data.firm || {});
      onSaved?.();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  // **The firm record leads** (DA-11): it is the one card here with required
  // fields, and until it is filled in every matter shows its firm.* fields as
  // missing. What prints on every contract first, the clause next, the
  // switches after. No "Back to dashboard" — the portal's own navigation is
  // on this screen, and two ways to one place is what that nav replaced.
  return (
    <div {...(fluent ? { "data-fluent-settings": "" } : {})} style={fluent ? { maxWidth: FLUENT_SETTINGS_W, margin: "0 auto" } : undefined}>
      {fluent && <style>{FLUENT_SETTINGS_CSS}</style>}
      <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 24, color: T.text, margin: "0 0 20px", letterSpacing: "-0.02em" }}>Settings</h1>

      <BrandSettings api={apiFetch} onSaved={onSaved} onError={onError} practice={fluent} />
      <ContractTemplateSettings api={apiFetch} onSaved={onSaved} onError={onError} onChange={noteTemplate} />
      {fluent && <NumberingSettings api={apiFetch} onSaved={onSaved} onError={onError} />}
      {fluent && <StageSettings api={apiFetch} onSaved={onSaved} onError={onError} />}
      {fluent && <FolderSettings api={apiFetch} onSaved={onSaved} onError={onError} />}

      {/* Your own sign-in and two-step for anybody signed in; the firm's
          people and sign-in rules for an owner or admin. Nothing at all on
          the shared key. */}
      <AccountSettings token={ACCESS_TOKEN} />

      {/* With the office record, at the top of it. Everyone who can open
          Settings sees it — this page has no permission split of its own. */}
      <SmokeballSettings />
      <TrisearchSettings onSaved={onSaved} onError={onError} />
      <PaymentSettings onSaved={onSaved} onError={onError} />

      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 }}>Firm</div>
        <p style={{ fontSize: 12, color: T.textFaint, margin: "0 0 12px" }}>
          Appears on every contract and section 32.
        </p>
        {firm === null ? (
          <div style={{ fontSize: 12, color: T.textFaint }}>Loading…</div>
        ) : (
          <>
            {FIRM_FIELDS.map(f => (
              <div key={f.key} style={{ display: "grid", gridTemplateColumns: "minmax(140px, 180px) 1fr", gap: 12, alignItems: f.multiline ? "start" : "center", padding: "8px 0", borderTop: `1px solid ${T.border}` }}>
                <label htmlFor={`firm-${f.key}`} style={{ fontSize: 12, color: f.required && !(draft[f.key] || "").trim() ? T.red : T.textSoft, paddingTop: f.multiline ? 7 : 0 }}>
                  {f.label}{f.required && <span style={{ color: T.red, marginLeft: 3 }}>*</span>}
                </label>
                <div>
                  {/* **A textarea where the value carries its own line breaks.**
                      The office writes its floor and its suburb on two lines and
                      the contract's cover keeps the break — which an
                      `input type="text"` cannot hold at all, so the box has to
                      admit the shape the page renders. */}
                  {f.multiline ? (
                    <textarea
                      id={`firm-${f.key}`}
                      rows={2}
                      value={draft[f.key] || ""}
                      onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                      style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit", lineHeight: 1.45 }}
                    />
                  ) : (
                    <input
                      id={`firm-${f.key}`}
                      type="text"
                      value={draft[f.key] || ""}
                      onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                      style={inputStyle}
                    />
                  )}
                  {/* What a box is for, where two of them look alike. The office
                      email and the contract email are the case that needs it. */}
                  {f.hint && (
                    <div style={{ fontSize: 11, color: T.textFaint, marginTop: 4, lineHeight: 1.45 }}>{f.hint}</div>
                  )}
                </div>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 16 }}>
              {dirty && !saving && (
                <span style={{ fontSize: 11, color: T.textFaint }}>Unsaved changes</span>
              )}
              <Btn variant="primary" size="md" onClick={save} disabled={!dirty || saving}>
                {saving ? "Saving…" : "Save"}
              </Btn>
            </div>
          </>
        )}
      </div>

      <SeasonalSettings onSaved={onSaved} onError={onError} onDirty={markSeasonal} />
      <InspectionSettings onSaved={onSaved} onError={onError} onDirty={markInspection} />
      <AutomationSettings onSaved={onAutomationSaved} onError={onError} />
      <FeedbackSettings onError={onError} />
    </div>
  );
};

// ── Recompile page ─────────────────────────────────────────────────
// Builds the re-compile list from the matter's returned searches, arranged by
// whatever order was last saved. Anything that returned since then has no
// saved position, so it lands at the end and is flagged rather than dropped —
// the middleware does the same thing when it merges.
// Has the work this poll is watching actually finished?
//
// Pulled out because the version of this that lived inline was wrong in two
// ways that both showed as a progress bar disappearing while the work carried
// on — and a bar that vanishes early is worse than no bar, because it says
// "done" about something that isn't.
export const compileFinished = ({
  matter, fromVersion = 0, expectsVersion = true, startSeen = true, stopAsked = false,
}) => {
  // Both kinds of reading: the bundle wait, and the per-document reads. The
  // per-document path never sets awaitingExtractionSince, so without
  // documentsBeingRead this declared a re-read finished three seconds in and
  // toasted "certificates read" about a read that had not happened.
  const reading = Boolean(matter?.awaitingExtractionSince)
    || (matter?.documentsBeingRead || 0) > 0;
  if (reading) return false;
  // A re-read produces no version — it deliberately does not rebuild the
  // contract — so it is finished when the matter stops saying it is reading.
  //
  // **Not yet started is not finished**, and telling those two apart is the
  // whole of `startSeen`. A matter that is not reading says nothing about
  // whether the read is over or has not begun, and the same tick was being
  // read as the first.
  //
  // The per-document path stamps its rows *before* answering, so its poll sees
  // the work from the first tick. The whole-bundle path does not: it merges
  // the pack at pdf.co first and only stamps `awaitingExtractionSince` when it
  // hands the pack over — eight seconds later on 1760339, five seconds after
  // the poll had already declared the read complete. The bar vanished, the
  // toast said the certificates had been read, and the read had not started.
  // Leaving the matter and coming back armed the watch again from the stamp
  // that had since landed, which is exactly what it looked like from a desk.
  //
  // So the poll has to have *seen* it running before an idle matter counts as
  // finished. Defaults true, because a watch that armed itself off a matter
  // already reading has seen it by definition.
  if (!expectsVersion) return startSeen;
  // And a compile is finished when the version goes up. `status === "compiled"`
  // was already true three seconds into a *re*-compile, which is why the bar
  // vanished before anything had happened.
  if ((matter?.compileVersion || 0) > fromVersion) return true;

  // **Or when a compile somebody stopped actually stops.** A stopped compile
  // deliberately writes no version and does not fail, so it is none of the
  // three things this poll watches for — the bar ran its whole three-minute
  // budget and then said *"Still compiling — reopen the matter to check"*
  // about work that had ended two and a half minutes earlier. Which is how
  // Stop came to be reported as doing nothing.
  //
  // **`stopAsked` is what the server told us**, not a guess: the stop route
  // answers `running`, so `true` means a run was genuinely going when the
  // press landed and `compile.running` turning false is that run ending.
  // Without it this would have to decide whether the run had *started* yet —
  // the compile is fire-and-forget, so `running` is honestly false between the
  // press and the claim — and the only way to answer that from here is to
  // compare a server timestamp against this browser's clock, which is exactly
  // what the middleware's own stop refuses to do.
  //
  // A compile stopped from another tab is not covered and falls through to the
  // budget, which is today's behaviour rather than a new fault.
  if (stopAsked && matter?.compile && matter.compile.running === false) return true;

  return false;
};

export const buildRecompileDocs = (matter) => {
  const saved = matter.compileOrder || [];
  const removed = new Set(matter.compileExcluded || []);
  const position = new Map(saved.map((id, i) => [id, i]));

  // excludedFromCompile is computed by the middleware — don't reimplement the
  // pattern list here, or this screen will offer an order the compile ignores.
  //
  // **The file test is `hasMergeableFile`, not `driveFileUrl`.** A document
  // staff uploaded lives in our bucket and has no Drive link, so filtering on
  // one dropped every upload off this screen — they merged into the PDF and
  // could not be re-ordered or removed, which is the same fault the middleware
  // had before hasMergeableFile existed and the reason it is one shared rule
  // there.
  const returned = (matter.searches || [])
    .filter(s => isCompilable(s) && !removed.has(s.id))
    .map(s => ({
      id: s.id,
      // The tidied name where the middleware sent one — the same words the
      // search list and section 13's attachment list use, so the three screens
      // cannot call one document three things.
      name: s.displayName || s.name,
      // Both halves, because renaming needs them: what somebody typed, and
      // what the supplier called it — which is what clearing the box restores.
      customName: s.customName || "",
      suppliedName: s.name || "",
      type: "pdf",
      url: documentUrl(matter.ref ?? matter.id, s),
      isUpload: Boolean(s.isUpload),
      // **Where the file is, carried rather than resolved once.** `url` is
      // enough to open a document and not enough to *preview* one: the modal
      // asks `previewUrlFor`, which needs to know whether the bytes are ours
      // or Drive's. Replacing one needs the status for the same reason the
      // search list does.
      status: s.status || "",
      storagePath: s.storagePath || "",
      driveFileUrl: s.driveFileUrl || "",
      editable: false,
      // Carried through so the fallback sort below can use it — dropping it
      // makes that comparison NaN and the order silently arbitrary.
      compileOrderIndex: s.compileOrderIndex ?? 0,
      isNewSinceOrder: saved.length > 0 && !position.has(s.id),
    }));

  const placed = returned.filter(d => position.has(d.id))
    .sort((a, b) => position.get(a.id) - position.get(b.id));

  // Anything without a saved position falls back to the standard Section 32
  // order, using the index the middleware computed. This must match the
  // server's own fallback, or the first "Save order" would freeze whatever
  // arbitrary sequence the API happened to return.
  const unplaced = returned.filter(d => !position.has(d.id))
    .sort((a, b) => (a.compileOrderIndex - b.compileOrderIndex) || (a.name || "").localeCompare(b.name || ""));

  return [...placed, ...unplaced].map((d, i) => ({ ...d, order: i + 1 }));
};

// ── The compile workspace ──────────────────────────────────────────────────
//
// Pressing Compile opens this before anything is merged: the pack on the left,
// the document on the right, the way staff already check a combined PDF in
// Triconvey's *Edit combined PDF* — a list beside a preview, rather than a
// matter page where the list and the document are screens apart and, on the
// classic screen, the navigation takes the width a second column would need.
//
// **It is a layer over the window, not a new view.** The matter page under it
// is exactly as it was, and so is everything that reads `view`; closing it
// puts staff back where they pressed. Above the navigation rail (80) and the
// matter bar (60), below the compile confirmation (300), so a matter with
// searches outstanding still asks before it compiles — on top of this.
//
// **The list is the merge, never a second rule for it.** The pack is
// `buildRecompileDocs`, the same order the compile and Arrange documents use,
// with the contract and section 32 first because the compile puts them first.
// What is not back yet is listed under it, unclickable, so a missing
// certificate is seen rather than inferred from its absence.
//
// **The draft is the editable one.** It is `ContractPreview`, so a value
// spotted wrong is fixed here, and it is kept mounted while a search is shown:
// unmounting it throws away the fetch, the pagination and the reader's place.
export const COMPILE_WORKSPACE_Z = 200;
export const COMPILE_WORKSPACE_NARROW = "(max-width: 760px)";
export const WORKSPACE_CONTRACT = "__contract";
export const WORKSPACE_END = "__end";

// The pack in a dragged order: the ids in `ids` first, in that order, then
// anything that arrived since (a late return, an upload) at the end — where
// the compile puts a document with no saved position anyway.
export const workspaceOrder = (pack = [], ids = []) => {
  const byId = new Map(pack.map(d => [d.id, d]));
  const placed = ids.map(id => byId.get(id)).filter(Boolean);
  const kept = new Set(placed.map(d => d.id));
  return [...placed, ...pack.filter(d => !kept.has(d.id))];
};

export const compileWorkspaceDocs = (matter = {}) => {
  const byId = new Map((matter.searches || []).map(s => [s.id, s]));
  const pack = buildRecompileDocs(matter).map(d => ({
    id: d.id,
    name: d.name,
    isUpload: d.isUpload,
    isNewSinceOrder: Boolean(d.isNewSinceOrder),
    search: byId.get(d.id) || d,
  }));
  const inPack = new Set(pack.map(d => d.id));
  const outstanding = (matter.searches || [])
    .filter(s => !inPack.has(s.id) && isNotBack(s))
    .map(s => ({ id: s.id, name: s.displayName || s.name, status: s.status || "" }));
  // What staff took out of the pack, so it can be put back from here. Only a
  // row with a file: the middleware's own exclusions (`excludedFromCompile`)
  // are a rule, not somebody's choice, and are not offered back.
  const excluded = new Set(matter.compileExcluded || []);
  const leftOut = (matter.searches || [])
    .filter(s => excluded.has(s.id) && !s.excludedFromCompile && hasFile(s))
    .map(s => ({ id: s.id, name: s.displayName || s.name, isUpload: Boolean(s.isUpload), search: s }));
  return { pack, outstanding, leftOut };
};

// **A row is a card, the searches list's**: white, a hairline border, 8px
// corners. The selected one is a grey card — the fill and one step up the
// border greys (`borderMid`, the searches list's hover) — in both looks. A
// coloured bar read as a fault, and an ink or brand outline was louder than
// anything else in the column.
const WorkspaceRow = ({ active, onClick, children, sub, dim = false, drag = null, dropAbove = false, menu = null }) => {
  const mark = T.textSoft;
  return (
  <div {...(drag || {})} data-workspace-row
    style={{ position: "relative", display: "flex", alignItems: "center", gap: 2, marginBottom: 4,
      background: active ? T.bgAlt : T.white, border: `1px solid ${active ? T.borderMid : T.border}`, borderRadius: 8,
      cursor: drag ? "grab" : undefined, opacity: dim ? 0.75 : 1 }}>
    {dropAbove && <span aria-hidden="true" style={{ position: "absolute", left: 6, right: 6, top: -4, height: 2, borderRadius: 2, background: mark }} />}
  <button type="button" onClick={onClick} disabled={!onClick} aria-current={active ? "true" : undefined}
    style={{
      flex: 1, minWidth: 0,
      display: "flex", alignItems: "flex-start", gap: 8, width: "100%", textAlign: "left",
      padding: "9px 12px", border: "none", borderRadius: 8, cursor: drag ? "grab" : onClick ? "pointer" : "default",
      background: "transparent",
      color: dim ? T.textFaint : T.text, fontSize: 12.5, lineHeight: 1.35, fontFamily: "Inter, sans-serif",
    }}>
    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto", marginTop: 2, color: dim ? T.textFaint : T.textSoft }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
    </svg>
    <span style={{ minWidth: 0, flex: 1 }}>
      <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>{children}</span>
      {sub && <span style={{ display: "block", fontSize: 11, color: T.textFaint, marginTop: 1 }}>{sub}</span>}
    </span>
  </button>
  {menu && <span style={{ display: "flex", alignItems: "center", paddingRight: 4 }}>{menu}</span>}
  </div>
  );
};

export const CompileWorkspace = ({
  matter, registry, compiling = false, onClose, onCompile, onArrangeDocuments,
  onSaveOrder, onAddDocument, uploading = false, searchInbox = "",
  onExclude, onRename, onReplace, onDelete,
  onSaveField, onSaveFields, onSaveClause, onRemoveClause, onRevertField, savingFields, savingClause,
  onAdoptAgent, adoptingAgent, onSaveToDirectory,
}) => {
  const ref = matter.ref ?? matter.id;
  const { pack: served, outstanding, leftOut } = compileWorkspaceDocs(matter);
  // **The row's own menu** — the searches list's actions for a document in
  // the pack, through the same handlers, so the two cannot disagree: replace
  // the PDF, rename it, leave it out (and put it back), and delete one staff
  // uploaded. An ordered search cannot be deleted; the middleware refuses.
  const [renaming, setRenaming] = useState(null);
  const replaceInput = useRef(null);
  const [replacing, setReplacing] = useState(null);
  const menuFor = (d, out = false) => {
    const s = d.search || d;
    const items = [
      onReplace && canReplaceFile(s) && { label: "Replace PDF", onClick: () => { setReplacing(s); setTimeout(() => replaceInput.current?.click(), 0); } },
      onRename && { label: "Rename…", onClick: () => setRenaming(s) },
      // The searches list's rule: anything in the files is Removed (and Added
      // back), uploads included; only an upload already removed can be
      // deleted, from the Removed group.
      onExclude && { label: out ? "Add back" : "Remove", onClick: () => onExclude(s, !out) },
      ...(onDelete && s.isUpload && out ? ["divider", { label: "Delete permanently", danger: true, onClick: () => onDelete(s) }] : []),
    ].filter(Boolean);
    return items.length ? <OverflowMenu items={items} label={`Actions for ${d.name}`} size={26} tour={false} /> : null;
  };
  // **Dragging a row reorders the pack here, and saves it** — the same
  // `/compile-order` write Arrange searches makes, so the two cannot disagree.
  // The new order is shown at once and held until the matter comes back
  // carrying it; a failed save puts the served order back. Desktop only: on a
  // phone, Arrange searches has the arrows.
  const [moved, setMoved] = useState(null);
  const [orderState, setOrderState] = useState("");
  const [dragId, setDragIdState] = useState("");
  // Mirrored in a ref so the drop reads the row being dragged even when the
  // events arrive faster than a render.
  const dragRef = useRef("");
  const setDragId = (id) => { dragRef.current = id; setDragIdState(id); };
  const [overId, setOverId] = useState("");
  const servedKey = served.map(d => d.id).join("|");
  useEffect(() => { setMoved(null); }, [servedKey]);
  const pack = moved ? workspaceOrder(served, moved) : served;
  const saveIds = (ids) => {
    setMoved(ids);
    setOrderState("saving");
    Promise.resolve(onSaveOrder(ids.map(id => ({ id }))))
      .then(() => setOrderState("saved"))
      .catch(() => { setMoved(null); setOrderState("error"); });
  };
  const dropOn = (targetId) => {
    const from = dragRef.current;
    setDragId(""); setOverId("");
    if (!from || from === targetId || !onSaveOrder) return;
    const ids = pack.map(d => d.id).filter(id => id !== from);
    const at = targetId === WORKSPACE_END ? ids.length : ids.indexOf(targetId);
    if (at < 0) return;
    ids.splice(at, 0, from);
    saveIds(ids);
  };
  // **Arrows where a drag cannot happen** — a phone or a touch screen — so
  // the workspace reorders everywhere and Arrange searches is not needed to.
  const move = (id, delta) => {
    const ids = pack.map(d => d.id);
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length || !onSaveOrder) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    saveIds(ids);
  };
  // Required contract data nothing has filled — the half of the compile
  // confirmation that the searches review does not carry. The middleware's
  // list (`compileOutstanding.blocking`), never one worked out here.
  const unfilled = (compileOutstandingFor(matter)?.blocking || []).filter(b => b.kind === "field");
  // **On a phone the two columns stack**: the list on top, scrolling in a
  // band of its own, and the document under it. Side by side, 380px of list
  // left nothing of a 390px screen for the document.
  const narrow = useMediaQuery(COMPILE_WORKSPACE_NARROW);
  const coarse = useCoarsePointer();
  const canDrag = Boolean(onSaveOrder) && !narrow && !coarse;
  const [selected, setSelected] = useState(WORKSPACE_CONTRACT);
  const doc = selected === WORKSPACE_CONTRACT ? null
    : (pack.find(d => d.id === selected) || leftOut.find(d => d.id === selected));
  // A document that left the pack while it was on screen (removed, or marked
  // not required) puts the contract back rather than showing nothing.
  useEffect(() => {
    if (selected !== WORKSPACE_CONTRACT && !doc) setSelected(WORKSPACE_CONTRACT);
  }, [selected, doc]);
  const preview = doc ? previewUrlFor(ref, doc.search) : null;

  // **A press that opens a panel on the matter page closes this.** "Ask the
  // client for the 137B report" is in the searches review here, and the panel
  // it opens is on the matter page underneath — so it opened, scrolled itself
  // into view behind this layer, and the press looked like it did nothing.
  useEffect(() => {
    const away = () => onClose();
    window.addEventListener(OPEN_137B_EVENT, away);
    return () => window.removeEventListener(OPEN_137B_EVENT, away);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !document.querySelector("[data-field-popover]")) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const label = matter.status === "compile_failed" ? "Retry compile"
    : matter.status === "compiled" || matter.status === "released" || matter.compileVersion ? "Re-compile" : "Compile";
  const address = matter.address || "";

  return (
    <div role="dialog" aria-modal="true" aria-label="Compile the contract" data-compile-workspace
      style={{ position: "fixed", inset: 0, zIndex: COMPILE_WORKSPACE_Z, background: T.bg, display: "flex", flexDirection: "column", fontFamily: "Inter, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", gap: narrow ? 6 : 10, flexWrap: "wrap", padding: narrow ? "8px 10px" : "10px 16px", background: T.white, borderBottom: `1px solid ${T.border}` }}>
        <Btn variant="ghost" size="sm" onClick={onClose}>
          <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          {narrow ? "Back" : "Back to the matter"}
        </Btn>
        {/* On a phone the address is a line of its own under the buttons,
            rather than three words and an ellipsis between them. */}
        {narrow && <span style={{ flex: 1 }} />}
        <span title={address || undefined} style={narrow
          ? { order: 1, flex: "1 1 100%", fontSize: 13, color: T.text, lineHeight: 1.35 }
          : { fontSize: 15, fontWeight: 300, letterSpacing: "-0.02em", color: T.text, flex: "1 1 200px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {address || `Matter ${ref}`}
        </span>
        {/* No Arrange searches here: the list beside the document reorders
            (a drag on a desktop, arrows on a phone), and each row's menu
            replaces, renames and removes — everything that page did. */}
        <Btn variant="primary" size="sm" onClick={onCompile} disabled={compiling}>
          {compiling ? "Compiling…" : label}
        </Btn>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: narrow ? "column" : "row" }}>
        <div style={narrow
          ? { flex: "0 0 auto", maxHeight: "38%", overflowY: "auto", borderBottom: `1px solid ${T.border}`, background: T.white, padding: 8 }
          : { width: 380, flex: "0 0 380px", overflowY: "auto", borderRight: `1px solid ${T.border}`, background: T.white, padding: 12 }}>
          {unfilled.length > 0 && (
            <div data-unfilled style={{ border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
              <div style={{ padding: "9px 12px", background: T.bgAlt, borderBottom: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#b4433a", textTransform: "uppercase", letterSpacing: "0.06em" }}>Contract data still needed</span>
                <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 500, marginLeft: 8 }}>{unfilled.length}</span>
                <div style={{ fontSize: 11.5, color: T.textSoft, lineHeight: 1.5, marginTop: 3 }}>Required, and nothing has filled it. Click the value in the draft to fill it.</div>
              </div>
              {unfilled.map((f, i) => (
                <div key={f.key} style={{ padding: "7px 12px", borderTop: i > 0 ? `1px solid ${T.border}` : "none", fontSize: 12, color: T.text }}>{f.label}</div>
              ))}
            </div>
          )}
          <SearchesReview
            review={matter.documentReview}
            registry={registry}
            defaultOpen
            stage={matter.status}
            matterRef={ref}
            searches={matter.searches || []}
          />
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "14px 10px 6px" }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Files · {pack.length + 1}
            </span>
            <span style={{ flex: 1 }} />
            <span data-order-state={orderState} style={{ fontSize: 11, color: orderState === "error" ? T.red : T.textFaint }}>
              {orderState === "saving" ? "Saving order…" : orderState === "saved" ? "Order saved" : orderState === "error" ? "Order not saved" : canDrag ? "Drag to reorder" : onSaveOrder ? "Arrows to reorder" : ""}
            </span>
          </div>
          <WorkspaceRow active={selected === WORKSPACE_CONTRACT} onClick={() => setSelected(WORKSPACE_CONTRACT)} sub="Draft — click any value to edit">
            Contract of sale &amp; section 32
          </WorkspaceRow>
          {pack.map((d, i) => (
            <WorkspaceRow key={d.id} active={selected === d.id} onClick={() => setSelected(d.id)}
              dropAbove={Boolean(dragId) && overId === d.id && dragId !== d.id}
              menu={<>
                {!canDrag && onSaveOrder && (
                  <span style={{ display: "flex" }}>
                    {[[-1, "Move up", "m18 15-6-6-6 6"], [1, "Move down", "m6 9 6 6 6-6"]].map(([delta, label, path]) => (
                      <button key={label} type="button" aria-label={`${label}: ${d.name}`} onClick={() => move(d.id, delta)}
                        disabled={(delta < 0 && i === 0) || (delta > 0 && i === pack.length - 1)}
                        style={{ width: 32, height: 32, border: "none", background: "transparent", color: T.textSoft, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", opacity: ((delta < 0 && i === 0) || (delta > 0 && i === pack.length - 1)) ? 0.3 : 1 }}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d={path}/></svg>
                      </button>
                    ))}
                  </span>
                )}
                {menuFor(d)}
              </>}
              drag={canDrag ? {
                draggable: true,
                onDragStart: (e) => { setDragId(d.id); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", d.id); } catch (err) { /* older browsers */ } },
                onDragOver: (e) => { e.preventDefault(); if (overId !== d.id) setOverId(d.id); },
                onDrop: (e) => { e.preventDefault(); dropOn(d.id); },
                onDragEnd: () => { setDragId(""); setOverId(""); },
              } : null}
              sub={[d.isUpload ? "Uploaded" : "", d.isNewSinceOrder ? "Came back after the order was saved — goes at the end" : ""].filter(Boolean).join(" · ") || null}>
              {d.name}
            </WorkspaceRow>
          ))}
          {canDrag && dragId && (
            <div onDragOver={(e) => { e.preventDefault(); if (overId !== WORKSPACE_END) setOverId(WORKSPACE_END); }}
              onDrop={(e) => { e.preventDefault(); dropOn(WORKSPACE_END); }}
              style={{ height: 22, margin: "0 10px", borderTop: overId === WORKSPACE_END ? `2px solid ${T.textSoft}` : "2px solid transparent" }} />
          )}
          {onAddDocument && (
            <div style={{ margin: "8px 10px 0" }}>
              <AddDocument
                onAdd={(file, name, onProgress, link) => onAddDocument(ref, file, name, onProgress, link)}
                uploading={uploading}
                disabled={compiling}
              />
              <SearchInboxNote inbox={searchInbox} matterRef={ref} />
            </div>
          )}
          {outstanding.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 500, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", margin: "14px 10px 6px" }}>
                Not back yet · {outstanding.length}
              </div>
              {outstanding.map(s => (
                <WorkspaceRow key={s.id} dim sub={s.status || null}>{s.name}</WorkspaceRow>
              ))}
            </>
          )}
          {leftOut.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 500, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", margin: "14px 10px 6px" }}>
                Removed · {leftOut.length}
              </div>
              {leftOut.map(d => (
                <WorkspaceRow key={d.id} dim active={selected === d.id} onClick={() => setSelected(d.id)} menu={menuFor(d, true)}>
                  {d.name}
                </WorkspaceRow>
              ))}
            </>
          )}
          <input ref={replaceInput} type="file" accept="application/pdf" style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              const target = replacing;
              e.target.value = "";
              setReplacing(null);
              if (file && target) onReplace(target, file);
            }} />
          {renaming && (
            <RenameDocument
              supplied={renaming.name}
              custom={renaming.customName || ""}
              onCancel={() => setRenaming(null)}
              onRename={(name) => { const target = renaming; setRenaming(null); onRename(target, name); }}
            />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", padding: narrow ? 6 : 12 }}>
          <div style={{ display: doc ? "none" : "block", flex: 1, minHeight: 0 }} aria-hidden={doc ? true : undefined}>
            <ContractPreview
              embedded
              fill
              defaultOpen
              stage={matter.status}
              matterRef={ref}
              registry={registry}
              contractFields={matter.contractFields}
              specialConditions={matter.specialConditions}
              clauseOverrides={matter.clauseOverrides}
              clausesRemoved={matter.clausesRemoved}
              onRemoveClause={onRemoveClause}
              generatedAttachments={matter.generatedAttachments}
              searches={matter.searches}
              agentDirectory={matter.agentDirectory}
              onAdoptAgent={onAdoptAgent ? (choice) => onAdoptAgent(ref, choice) : undefined}
              adoptingAgent={adoptingAgent}
              onSaveToDirectory={onSaveToDirectory ? (name) => onSaveToDirectory(ref, name) : undefined}
              onSave={onSaveField}
              onSaveFields={onSaveFields}
              onSaveClause={onSaveClause}
              onRevert={onRevertField}
              savingFields={savingFields}
              savingClause={savingClause}
            />
          </div>
          {doc && (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: T.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</span>
                {preview?.url && (downloadUrl(preview.url) === preview.url
                  ? <Btn variant="ghost" size="sm" onClick={() => window.open(preview.url, "_blank")}>Open</Btn>
                  : <Btn variant="ghost" size="sm" onClick={() => saveAs(downloadUrl(preview.url))}>Download</Btn>)}
              </div>
              <div style={{ flex: 1, minHeight: 0, background: T.bgAlt }}>
                {(() => {
                  // The drawn view on a touch screen — an upload included —
                  // and the PDF itself elsewhere (`documentPreview`).
                  const shown = documentPreview(preview || {}, coarse);
                  if (shown.src) {
                    return <iframe key={shown.src} title={doc.name} src={shown.src} {...(shown.paginates ? { "data-s32-view": true } : {})} style={{ width: "100%", height: "100%", border: 0, display: "block" }} />;
                  }
                  return null;
                })() || (needsDeviceViewer(preview?.url, coarse) ? (
                  <PdfInDeviceViewer url={preview.url} />
                ) : (
                  <div style={{ padding: 24, fontSize: 13, color: T.textSoft, lineHeight: 1.6 }}>
                    This document&rsquo;s link can&rsquo;t be shown in place — use Open to see it.
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// What a saved order posts: the order given, and everything mergeable that is
// not in it — so a document missing from the order lands in the excluded list
// rather than coming back at the end of the statement, which an upload once
// did. The compile workspace's drag and arrows save through it.
export const orderPayload = (searches, docs) => {
  const order = docs.map(d => d.id);
  const kept = new Set(order);
  const excluded = (searches || [])
    .filter(s => isCompilable(s) && !kept.has(s.id))
    .map(s => s.id);
  return { order, excluded };
};

// ── Compiling banner ───────────────────────────────────────────────
// The compile runs on the server for ~30s. A toast fades long before it
// finishes, and the button label is at the top of the page where it's easy to
// miss — so this sits with the document and stays until the work is done.
//
// The bar is deliberately indeterminate. We can't know how long a merge will
// take (it depends on how many PDFs and how big they are), and a percentage
// we invented would just be a lie that stalls at 90%.
// What is happening, while it happens.
//
// Two very different waits, and saying "compiling" for both was the problem.
// A merge takes about half a minute. Reading the certificates hands them to the
// AI and legitimately takes minutes — during which the matter looks idle, has
// no new version, and the only honest thing to show is what it is waiting for.
//
// `startedAt` is when this page started watching; `since` is the server's own
// stamp, so the elapsed time survives a reload and a second person opening the
// same matter sees the same number.
// How long a read is given before the wait stops being ordinary progress.
//
// The middleware's own window is 30 minutes, but the sweep that acts on it runs
// hourly — so a read that never comes back can sit here for up to 90. Saying
// "this usually takes a few minutes" at minute 40 is how a screen stops being
// believed.
const READ_WINDOW_MS = 30 * 60 * 1000;

// **Stop says what it can actually do, and the two are not the same thing.**
// Both jobs spend most of their time inside somebody else's process — the
// merge is at pdf.co, the reading is at the extractor, which took the document
// minutes ago and is still working on it with no request in flight. Neither can
// be recalled.
//
// So a compile stops at its next checkpoint and leaves the current version
// alone, and a read stops us *waiting* while the extractor finishes anyway. A
// button that implied it had killed the work would be a lie, and the wording
// is the only place that can say so.
// When the reading now running began, in ms.
//
// Two paths, two stamps, and only one of them was being read. The bundle read
// stamps the matter (`awaitingExtractionSince`); a per-document read stamps
// each row and the middleware reports the earliest as `readingSince`. Reading
// only the first meant every per-document read counted from nothing.
//
// The earlier of the two when both exist, since either is a wait this matter
// is genuinely in.
export const readingStartedAt = (matter = {}) => {
  const stamps = [matter.readingSince, matter.awaitingExtractionSince]
    .map(v => Date.parse(v))
    .filter(n => Number.isFinite(n));
  return stamps.length ? Math.min(...stamps) : null;
};

// **No `confirm` any more, and it is gone rather than merely unrendered.** The
// dialog carried the caveats — that stopping a read does not stop the reader,
// and that a compile near its end will finish — and both are said afterwards
// by the route's own message, which the toast shows, and by the landing
// notice that names the version left alone. A string nothing renders is dead
// data that tests go on asserting.
export const stopWording = (reading) => reading
  ? { label: "Stop waiting", pending: "Stopping…" }
  : { label: "Stop", pending: "Stopping…" };

// **How long before "this is taking longer than usual" is true.**
//
// A merge was 60 seconds, and a 150-page pack takes just over two minutes on a
// perfectly healthy run — so the ordinary large matter was told something had
// gone wrong, one minute into work that had another seventy seconds to go. A
// warning that fires on the normal case is a warning staff learn to read as
// noise, which is the same fault a completeness list carrying furniture has.
//
// Three minutes is measured rather than guessed: 1760416's own log put the
// Chromium render at ~32s, the signing at ~24s (now overlapped with the render)
// and pdf.co's merge, download and store at ~90s. Two minutes is the ordinary
// large pack, so the sentence only appears past what a healthy one costs.
//
// The read keeps its ten minutes — a Gemini pass over a pack is a different
// shape of wait, and `READ_WINDOW_MS` is what actually decides that one.
export const SLOW_AFTER = { merging: 180, reading: 600 };

// **A stopwatch that stops moving reads as a stopwatch that has stopped.**
// This rounded to whole minutes past 90 seconds, so a compile sat on "2 min
// elapsed" from 1:30 to 2:29 — a full minute of a screen that looks frozen,
// on exactly the run somebody is already watching because it feels slow.
//
// Seconds under a minute, `m:ss` after it. Every tick moves something.
export const elapsedStamp = (seconds) => {
  const n = Math.max(0, Math.round(seconds));
  if (n < 60) return `${n}s elapsed`;
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")} elapsed`;
};

const CompilingBanner = ({ startedAt, isFirst, phase = "merging", rebuilds = true, onStop, stopping = false, stopRequested = false }) => {
  const [elapsed, setElapsed] = useState(0);

  // **The fallback has to be pinned, or it moves.** `startedAt || Date.now()`
  // is recomputed on every render, and the matter polls every three seconds —
  // so with no stamp to count from, the start moved forward with each poll and
  // "seconds elapsed" sat at zero for the whole of a multi-minute read. It is
  // taken once and re-taken only when the work itself changes.
  const [from, setFrom] = useState(() => startedAt || Date.now());
  useEffect(() => { setFrom(startedAt || Date.now()); }, [startedAt, phase]);

  useEffect(() => {
    const tick = () => setElapsed(Math.max(0, Math.round((Date.now() - from) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [from]);

  const reading = phase === "reading";
  // **Asked, and not stopped yet.** A compile stops at its *next* checkpoint,
  // which on a merge is after the merge returns — so there is a real stretch
  // between the press being acknowledged and the work ending, and a button
  // that went back to reading *Stop* through all of it is one somebody presses
  // again. Only ever a compile: a read's banner comes down on the press,
  // because the middleware has released the claims and there is nothing left
  // to wait for.
  const asked = stopping || (!reading && stopRequested);
  const slow = elapsed >= (reading ? SLOW_AFTER.reading : SLOW_AFTER.merging);
  // Past the window the read has almost certainly failed. Nothing here can
  // prove that — but presenting it as progress is the one thing that is
  // definitely wrong.
  const overdue = reading && elapsed * 1000 >= READ_WINDOW_MS;
  const stamp = elapsedStamp(elapsed);

  const accent = reading ? "#1a7ac4" : T.brown;
  const wording = stopWording(reading);

  return (
    <div style={{
      background: reading ? "rgba(26,122,196,0.05)" : T.white,
      border: `1px solid ${reading ? "rgba(26,122,196,0.3)" : T.border}`,
      borderRadius: 10, padding: 16, marginBottom: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: reading ? accent : T.text }}>
          {reading ? "Reading the certificates…" : isFirst ? "Compiling…" : "Re-compiling…"}
        </span>
        <span style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <span style={{ fontSize: 11, color: T.textFaint }}>{stamp}</span>
          {/* **In the banner, not in the action row.** This is the one place
              the running work is represented, so it is where somebody looks
              when they want it to stop — and it disappears with the banner,
              which is what stops a Stop button being offered for work that has
              already finished. */}
          {onStop && (
            <button
              type="button"
              // **No confirmation.** Stopping is not destructive — the version
              // already on the matter is left exactly as it was, which is the
              // whole guarantee — so a dialog in front of it is a second press
              // for an action that has nothing to undo. Compile is one press
              // away either way.
              onClick={() => onStop()}
              // **Disabled only while this press is in flight, never on what
              // the server said.** `stopRequested` comes off the matter, and a
              // stamp written by a press made when nothing was running is
              // cleared by nothing until some later compile reaches a
              // checkpoint — so it can be true as a banner opens, and the
              // button is then dead before anybody has pressed it. A press
              // that silently does nothing is the fault this whole area keeps
              // producing; pressing again while it is already stopping costs
              // one request and is what somebody does when unsure.
              disabled={stopping}
              style={{
                background: "none", border: `1px solid ${T.border}`, borderRadius: 999,
                padding: "2px 10px", fontSize: 11, fontFamily: "inherit", fontWeight: 500,
                color: asked ? T.textFaint : T.textSoft,
                cursor: stopping ? "default" : "pointer",
              }}
            >
              {asked ? wording.pending : wording.label}
            </button>
          )}
        </span>
      </div>

      <div style={{ position: "relative", height: 4, background: T.bgAlt, borderRadius: 999, overflow: "hidden" }}>
        <div style={{
          position: "absolute", top: 0, height: "100%", width: "40%",
          background: accent, borderRadius: 999,
          animation: "s32-indeterminate 1.4s ease-in-out infinite",
        }} />
      </div>

      <div style={{ fontSize: 12, color: T.textSoft, lineHeight: 1.5, marginTop: 10 }}>
        {reading && overdue ? (
          <>
            This has been waiting longer than a read should take and has most
            likely failed. <strong>Press Re-read certificates to start
            again.</strong>
            {rebuilds
              ? " If nothing comes back, the hourly sweep builds the contract from what's on the matter."
              : ""}
          </>
        ) : reading ? (
          <>
            {/* **How it is being read is not the reader's business.** This
                said "the searches have been merged and sent to be read",
                which is the whole-bundle path's story — and every matter
                from here on is read one certificate at a time, merging
                nothing. Even where it was true it was mechanism nobody
                needed. What is left is the one thing to do about it. */}
            Searches are being read.{" "}
            {rebuilds
              ? <>The contract is built from what comes back — <strong>nothing to do until it lands.</strong></>
              : <><strong>Re-compile afterwards to put the new values in the PDF.</strong></>}
            {slow ? " It's been a while." : ""}
          </>
        ) : slow
          ? "Taking longer than usual. Still running — the page updates when it lands."
          : <>Merging the documents. <span style={{ color: T.textFaint }}>A large pack can take up to three minutes.</span></>}
      </div>
    </div>
  );
};

// ── Access denied ──────────────────────────────────────────────────
const AccessDenied = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "100px 20px" }}>
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 32, maxWidth: 420, textAlign: "center" }}>
      <div style={{ fontSize: 26, marginBottom: 10 }}>🔒</div>
      <h1 style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: 20, color: T.text, margin: "0 0 8px", letterSpacing: "-0.02em" }}>
        Access key required
      </h1>
      <p style={{ fontSize: 13, color: T.textFaint, margin: 0, lineHeight: 1.6 }}>
        This portal is opened from the staff intranet. Use the link there, or ask
        for a current access link if yours has stopped working.
      </p>
    </div>
  </div>
);

// ── Signing in as a person ─────────────────────────────────────────
//
// Opened on its own, a portal with no key — or a refused one — offers a
// sign-in rather than a dead end: an emailed link, Google or Microsoft, then
// the second factor. Inside WordPress the key is how staff arrive and the
// providers will not run in a frame, so the frame keeps the old message.
//
// On success the tab forgets any key it was handed (the session is in
// `localStorage` now) and reloads, because `ACCESS_TOKEN` is read once at
// module load.
// **Setting a firm up is part of signing up.** The owner or admin of a firm
// that has not chosen its contract template is asked for it after the
// two-step sign-in (`ContractTemplateSetup`, which goes straight through when
// nothing is owed). The default firm and staff without Settings are not asked.
const sessionApi = (token) => (path, options = {}) => fetch(`${MIDDLEWARE_URL}${path}`, {
  ...options,
  headers: { ...(options.headers || {}), "x-portal-token": token },
});
const FirmSetup = ({ account, token, onDone }) => {
  const asks = Boolean(token) && !account?.firm?.isDefault && (account?.capabilities || []).includes("users:role");
  // One function per token: the card's reads are keyed on it.
  const api = useMemo(() => sessionApi(token), [token]);
  useEffect(() => { if (!asks) onDone(); }, [asks, onDone]);
  return asks ? <ContractTemplateSetup api={api} onDone={onDone} /> : null;
};
const firmSetupStep = (props) => <FirmSetup {...props} />;

export const StaffSignIn = () => (
  <SignInScreen
    setupStep={firmSetupStep}
    refusedToken={ACCESS_TOKEN}
    intro="Sign in with your work email, or with Google or Microsoft. Staff opening the portal from the intranet can keep using that link."
    onSignedIn={() => {
      try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* storage blocked */ }
      window.location.reload();
    }}
  />
);

// Who is signed in, in the header, and the way to their account: their name
// opens Settings, where their own two-step sign-in and (for an owner or admin)
// the firm's users are. A super admin also gets **Firms**, the platform page.
// Only on a session — the shared key is nobody's account.
const AccountLink = ({ onBrand = false, onGo }) => {
  const { me } = useAccount(IN_FRAME ? "" : ACCESS_TOKEN);
  if (!me) return null;
  const look = { fontSize: 13, fontWeight: 500, textDecoration: "none", whiteSpace: "nowrap", background: "none", border: "none",
    padding: 0, cursor: "pointer", fontFamily: "Inter, sans-serif", color: onBrand ? T.onBrand : T.textSoft, WebkitAppRegion: "no-drag" };
  return (
    <span style={{ display: "inline-flex", gap: 16, alignItems: "center" }}>
      {me.superAdmin && <a href="/console" style={look}>Firms</a>}
      <button type="button" onClick={() => onGo?.("settings")} title="Your sign-in, two-step and your firm's users are in Settings" style={look}>
        {me.user.name || me.user.email}
      </button>
    </span>
  );
};

// ── Loading spinner ────────────────────────────────────────────────
const LoadingSpinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "120px 0" }}>
    <div style={{ width: 32, height: 32, border: `3px solid ${T.border}`, borderTopColor: T.brown, borderRadius: "50%", animation: "s32-spin 0.8s linear infinite" }} />
  </div>
);

// A notice for work that has not finished yet.
//
// **The notice slot was built for outcomes** — "the outcome, where the bar
// was" — so a line put in it while something is still running reads as a
// result that happens to be worded oddly. The Vicmap lookup is the case: no
// `compileWork`, so the busy banner never shows, and a static sentence was
// the only thing on screen for thirty seconds.
//
// A spinner rather than `CompilingBanner`, deliberately: that one carries
// elapsed seconds and a **Stop**, and this answers inside its own request —
// there is nothing to stop and nothing worth counting.
const NoticeSpinner = () => (
  <span style={{
    width: 11, height: 11, flex: "none",
    border: `2px solid ${T.border}`, borderTopColor: T.textSoft,
    borderRadius: "50%", animation: "s32-spin 0.8s linear infinite",
  }} />
);

// ── One toast ──────────────────────────────────────────────────────
//
// **It stays while somebody is reading it.** Each toast keeps its own timer
// and holds it while the pointer is on it or focus is inside it, then gives it
// the full time again on the way out. A refusal from the middleware is often
// two lines, and it used to fade at five seconds whether or not anybody had
// finished it — with nowhere to read it again.
//
// **The stack is a live region** (`role="status"` on the container), so a
// screen reader announces each toast as it arrives; an error is `role="alert"`
// so it interrupts rather than waits.
export const TOAST_MS = { info: 4000, error: 8000, undo: 8000 };
export const TOAST_FADE_MS = 800;

export const ToastItem = ({ toast: t, onDismiss, onUndo }) => {
  const [held, setHeld] = useState(false);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    if (held) { setFading(false); return undefined; }
    const ms = t.ms || TOAST_MS.info;
    const fade = setTimeout(() => setFading(true), ms);
    const gone = setTimeout(() => onDismiss(t.id), ms + TOAST_FADE_MS);
    return () => { clearTimeout(fade); clearTimeout(gone); };
  }, [held, t.id, t.ms, onDismiss]);
  const error = t.variant === "error";
  return (
    <div role={error ? "alert" : undefined}
      onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHeld(false); }}
      style={{ pointerEvents: "auto", display: "flex", alignItems: "center", gap: 8, background: error ? T.red : T.brown, color: error ? T.white : T.warmGrey, padding: t.tag ? "7px 12px 8px 14px" : "8px 12px 8px 14px", borderRadius: t.tag ? 12 : 999, fontSize: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", maxWidth: "min(560px, calc(100vw - 40px))", opacity: fading ? 0.35 : 1, transition: "opacity 0.8s" }}>
      <span style={{ minWidth: 0 }}>
        {t.tag && (
          <span style={{ display: "block", opacity: 0.6, fontSize: 10.5, letterSpacing: "0.02em" }}>{t.tag}</span>
        )}
        {t.msg}
      </span>
      {t.onUndo && (
        <button onClick={() => onUndo(t.id, t.onUndo)} aria-label={`Undo — ${t.msg}`}
          style={{ fontSize: 12, fontWeight: 600, color: T.red, background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 999, padding: "2px 8px", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
          Undo
        </button>
      )}
      <button onClick={() => onDismiss(t.id)} aria-label="Dismiss" title="Dismiss"
        style={{ fontSize: 13, color: error ? T.white : T.warmGrey, background: "none", border: "none", cursor: "pointer", opacity: 0.6, padding: "0 2px", lineHeight: 1, fontFamily: "Inter, sans-serif" }}>
        ×
      </button>
    </div>
  );
};

// ── Root ───────────────────────────────────────────────────────────
export default function App() {
  const { me } = useAccount(isSessionToken(ACCESS_TOKEN) ? ACCESS_TOKEN : "");
  const [view, setView] = useState("dashboard");
  const [matters, setMatters] = useState([]);
  // Whether feedback mode is on, and what counts as a selection. Absent means
  // on, the same as the middleware — a portal that has not heard back yet
  // should not be the reason somebody cannot report what they are looking at.
  const [feedbackMode, setFeedbackMode] = useState({ enabled: false, minSelection: 8 });
  useEffect(() => {
    let cancelled = false;
    apiFetch("/settings/feedback")
      .then(r => r.json())
      .then(data => {
        if (cancelled || !data?.ok) return;
        setFeedbackMode({
          enabled: data.feedback?.enabled !== false,
          minSelection: Number(data.feedback?.minSelection) || 8,
        });
      })
      .catch(() => { /* off is the safe read: no chip rather than a broken one */ });
    return () => { cancelled = true; };
  }, []);

  // **The read-only key every document link is built on.** Fetched once, over
  // XHR with the staff token, so it never enters this app's own URL — the one
  // staff bookmark and paste. Until it arrives `apiUrl` falls back to the staff
  // token, which is what the links carried before and what a deployment with
  // `PORTAL_READ_TOKEN` unset keeps carrying.
  //
  // **A re-render is forced once it lands.** `apiUrl` reads a module variable
  // rather than state — every caller of it is a plain function, not a hook —
  // so nothing would rebuild the `<iframe src>` on a matter already open.
  // The value is never read — the re-render is the whole point — so it is not
  // bound. CRA's build treats an unused binding as an error under CI.
  const [, setDocTokenReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    apiFetch("/document-token")
      .then(r => r.json())
      .then(data => {
        if (cancelled || !data?.ok || !data.token) return;
        setDocumentToken(data.token);
        setDocTokenReady(true);
      })
      .catch(() => { /* the staff token keeps the links working */ });
    return () => { cancelled = true; };
  }, []);

  const routeRef = useRef({ view: "dashboard", selectedRef: null });
  const mattersRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [searchesLoading, setSearchesLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [denied, setDenied] = useState(false);
  const [toasts, setToasts] = useState([]);
  // Which tab of the send dialog is open: "agent" (release), "vendor", or none.
  const [sendTab, setSendTab] = useState(null);
  const [showCrossCheck, setShowCrossCheck] = useState(false);
  const [showReread, setShowReread] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  // Which matter is being worked on, and since when — not merely *whether*
  // something is.
  //
  // This was a bare `compiling` boolean, so pressing Re-compile on one matter
  // put "Re-compiling…" on the next matter opened, and the one after that. The
  // work was real and the banner was honest about it existing; it was wrong
  // about whose it was, which on a screen that decides what goes into a legal
  // document is the worse half. Holding the reference alongside the timestamp
  // keeps the two from drifting — a ref with no start time, or a start time
  // belonging to a previous matter, is the same bug in a smaller form.
  const [compileWork, setCompileWork] = useState(null);
  // Which matter-page layout this tab is looking at. State as well as a URL
  // parameter so the switch is instant — the parameter is read once at module
  // load, which is right for `?fh=` and `?token=` and wrong for something
  // meant to be flipped back and forth while comparing the two.
  const [fieldRegistry, setFieldRegistry] = useState([]);
  // Whose deployment this is. Today it carries the links to the client's form
  // and the practice system, which are Provey's and are withheld from a
  // deployment serving another firm.
  const [tenant, setTenant] = useState(null);
  const [savingFields, setSavingFields] = useState(new Set());
  const [revertingAll, setRevertingAll] = useState(false);
  const [markingNotRequired, setMarkingNotRequired] = useState(false);
  const [adoptingAgent, setAdoptingAgent] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [confirmCompile, setConfirmCompile] = useState(null);
  // The matter whose compile workspace is open, or "". Keyed on the ref so a
  // workspace never outlives the matter it was opened for.
  const [compileWorkspace, setCompileWorkspace] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingConditions, setSavingConditions] = useState(false);
  const [savingBlockClauses, setSavingBlockClauses] = useState(false);
  const notesTimer = useRef(null);
  const pollTimer = useRef(null);

  // The field registry is fetched, not bundled, so the panel can't drift from
  // what the middleware will actually use when it compiles.
  useEffect(() => {
    let cancelled = false;
    apiFetch("/contract-fields")
      .then(r => (r.status === 401 ? null : r.json()))
      .then(data => { if (!cancelled && data?.ok) setFieldRegistry(data.fields || []); })
      .catch(err => console.error("Failed to fetch the field registry", err));
    // Separately, because a middleware that does not serve this yet must not
    // take the registry down with it — the links are a convenience and the
    // panel is not.
    apiFetch("/settings/tenant")
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (!cancelled && data?.ok) setTenant(data.tenant || null); })
      .catch(() => { /* older middleware, or no access — no links */ });
    // The firm's brand colour. Until it answers — or on a middleware without
    // it — every screen draws Outlook blue, the fallback inside `T.brand`.
    apiFetch("/practice/appearance")
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data?.appearance) return;
        setFirmAppearance(data.appearance);
      })
      .catch(() => { /* older middleware — the default colour stands */ });
    return () => { cancelled = true; };
  }, []);

  // A card the title bar's search asked the Contacts screen to open.
  const [contactToOpen, setContactToOpen] = useState("");
  const [newMatterOpen, setNewMatterOpen] = useState(false);
  // The ribbon's Duties form, when no extension answered: `{ outcome, ref }`.
  const [dutiesHelp, setDutiesHelp] = useState(null);
  const dutiesStatus = useDutiesExtension();
  // Which look this tab draws: the firm's choice, unless the tab asked.
  const [firmAppearance, setFirmAppearance] = useState(null);
  const lookStep = lookStepFor(UI_CHOICE, firmAppearance?.experience, IN_FRAME, PERSONAL_LOOK);
  const practiceOn = lookStep >= 3;
  // Steps 1 and 2 bring pieces of the practice look into the classic one: the
  // open-matters tabs and search (1), and the matter's own tabs (2).
  const stripOn = lookStep >= 1;
  const workspaceOn = lookStep >= 2;
  // The card the first time this browser sees a step, and the way back.
  const firmStep = lookStepFor("", firmAppearance?.experience, IN_FRAME);
  const [lookSeen, setLookSeen] = useState(() => {
    try { return Number(window.localStorage.getItem(LOOK_SEEN_KEY)) || 0; } catch { return 0; }
  });
  const markLookSeen = () => {
    setLookSeen(lookStep);
    try { window.localStorage.setItem(LOOK_SEEN_KEY, String(lookStep)); } catch { /* a private window shows it again */ }
  };
  const choosePersonalLook = (key) => {
    try {
      if (key) window.localStorage.setItem(PERSONAL_LOOK_KEY, key); else window.localStorage.removeItem(PERSONAL_LOOK_KEY);
    } catch { /* nowhere to keep it */ }
    window.location.reload();
  };
  // The installed app's title bar follows the page's theme colour, so the
  // practice look paints it the firm's brand; the classic look leaves it.
  // The palette follows the look this tab is drawing: Outlook blue in the
  // practice look (until the firm saves a colour), the firm's own otherwise.
  useEffect(() => {
    const tokens = brandTokensFor(firmAppearance, practiceOn);
    if (tokens) applyBrand(tokens);
  }, [practiceOn, firmAppearance]);
  useEffect(() => {
    const brand = brandTokensFor(firmAppearance, practiceOn)?.brand;
    if (!practiceOn || !brand) return;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", brand);
  }, [practiceOn, firmAppearance]);

  // ── Open matters, as tabs across the window ───────────────────────────────
  //
  // Every matter opened in this window stays a tab until it is closed, the
  // way the new Outlook keeps items open. Kept for the window's session only
  // (`sessionStorage`), so a reload keeps them and a new window starts clean.
  const [openMatters, setOpenMatters] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(OPEN_MATTERS_KEY) || "[]").filter(m => m && m.ref); } catch { return []; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(OPEN_MATTERS_KEY, JSON.stringify(openMatters)); } catch { /* storage blocked */ }
  }, [openMatters]);
  const selectedRef = view === "detail" ? selected?.ref : null;
  const selectedLabel = selected ? matterTabLabel(selected) : "";
  useEffect(() => {
    if (!selectedRef || POPOUT || !stripOn) return;
    setOpenMatters(list => (list.some(m => m.ref === selectedRef)
      ? list.map(m => (m.ref === selectedRef && selectedLabel && m.label !== selectedLabel ? { ...m, label: selectedLabel } : m))
      : [...list, { ref: selectedRef, label: selectedLabel || String(selectedRef) }]));
  }, [selectedRef, selectedLabel, stripOn]);

  // **Another window saved something on a matter.** A popped-out matter and
  // the main window are separate pages with separate copies of the matter;
  // each tells the others on a channel, and whichever is showing that matter
  // reads it again. Only writes are announced, never reads, or two windows
  // would set each other off for ever.
  const [refreshSignal, setRefreshSignal] = useState(0);
  const channelRef = useRef(null);
  const selectedRefRef = useRef(null);
  selectedRefRef.current = selectedRef;
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;
    const channel = new BroadcastChannel(MATTER_CHANNEL);
    channel.onmessage = (e) => {
      const ref = e?.data?.ref;
      if (ref && ref === selectedRefRef.current) {
        loadMatterRef.current(ref).catch(() => {});
        setRefreshSignal(n => n + 1);
      }
    };
    channelRef.current = channel;
    return () => { channel.close(); channelRef.current = null; };
  }, []);
  const announceMatterChanged = (ref) => {
    try { channelRef.current && channelRef.current.postMessage({ ref }); } catch { /* closed */ }
  };

  useEffect(() => () => { clearTimeout(notesTimer.current); clearTimeout(pollTimer.current); }, []);

  // A read runs for minutes on the server and doesn't care whether anyone has
  // the page open. Opening a matter that is mid-read — or reloading during one
  // — has to pick the watch back up, or the banner would sit there showing a
  // frozen elapsed time and never resolve.
  //
  // **It watched only the bundle read, and that is most of them missed.**
  // `awaitingExtractionSince` is the whole-pack path's stamp; a per-document
  // read sets nothing on the matter. So a read started by placing a banked
  // return, adding a document or replacing one was never polled at all — the
  // row said *being read…* and went on saying it until somebody left the
  // matter and came back, which is the one thing that re-fetches. It is the
  // same reading either way, and the condition is the same one every other
  // screen uses.
  const watchingRead = useRef(null);
  const readInFlight = Boolean(selected?.awaitingExtractionSince)
    || (selected?.documentsBeingRead || 0) > 0;
  useEffect(() => {
    const ref = selected?.ref;
    if (!ref || !readInFlight) { watchingRead.current = null; return; }
    if (watchingRead.current === ref) return;
    // **The button's own poll is already watching this.** There is one
    // `pollTimer`, so a second poller does not run alongside the first — it
    // takes the handle off it, and whichever one is left decides when the work
    // is finished. Pressing Re-read armed both: the button's finished
    // correctly and toasted, and this one carried on polling every three
    // seconds with a version it was never going to see, leaving the banner up.
    // 1760266 is that, and coming back to the matter still showed "reading".
    if (compileWork?.ref === ref) return;
    watchingRead.current = ref;
    setCompileWork({ ref, startedAt: readingStartedAt(selected) || Date.now() });
    clearTimeout(pollTimer.current);
    // **Only the bundle read produces a version.** Its answer starts the
    // document pass; a per-document read deliberately does not rebuild the
    // contract, so waiting for a version bump would leave the watch running
    // until it timed out.
    // **Only a read that will rebuild produces a version.** The whole-pack
    // pass after the searches complete does; a re-read deliberately does not,
    // so waiting for a version bump on one means waiting for something that
    // cannot arrive.
    pollCompile(ref, {
      delay: 10000,
      expectsVersion: Boolean(selected.awaitingExtractionSince)
        && selected.awaitingExtractionTrigger !== "reread",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.ref, readInFlight, compileWork?.ref]);

  // Archived and live matters are separate queries — Firestore can't return
  // both from one indexed query, and mixing them in the list would be worse UX
  // anyway.
  // **Only the first read covers the page.** Later reads (a status filter, the
  // Archived list) spin inside the list (`listLoading`), so the screen does
  // not blank as though the site were down.
  const [listLoading, setListLoading] = useState(false);
  const firstMattersRead = useRef(true);
  useEffect(() => {
    let cancelled = false;
    const first = firstMattersRead.current;
    firstMattersRead.current = false;
    if (first) setLoading(true); else setListLoading(true);
    // One request on the s32 portal and two on the whole book, because the
    // endpoint splits on `trackedReason` exactly and neither half is a
    // superset of the other.
    //
    // **All of it or none of it.** A partial list in the one mode whose point
    // is showing everything would be the silent-half fault this codebase
    // keeps naming — so a failure leaves the list where it was and says so in
    // the log, which is what a single failed request already did.
    Promise.all(mattersQueries({ showArchived, allMatters: ALL_MATTERS }).map(path =>
      apiFetch(path).then(async r => {
        // The middleware owns the access check — a 401 here is what locks
        // the portal, not anything this bundle decides.
        if (r.status === 401) { setDenied(true); return null; }
        return r.json();
      })))
      .then(answers => {
        if (cancelled || answers.some(a => !a?.ok)) return;
        const rows = answers.flatMap(a => a.matters || [])
          .map(m => ({ ...m, ref: m.matterRef ?? m.id }));
        // Re-sorted only where two lists were merged. One request already
        // arrives in the server's own order, and re-sorting it here would be
        // this screen deciding an order the endpoint owns.
        setMatters(ALL_MATTERS ? byNewest(rows) : rows);
      })
      .catch(err => console.error("Failed to fetch matters", err))
      .finally(() => { if (!cancelled) { setLoading(false); setListLoading(false); } });
    return () => { cancelled = true; };
  }, [showArchived]);

  const postJson = async (path, body) => {
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.status === 401) { setDenied(true); throw new Error("Access denied"); }
    if (!res.ok) {
      // The middleware's own message, not "returned 502". Every refusal here is
      // written to be read by whoever pressed the button — a size limit, a
      // missing setting, a matter with nothing compiled — and throwing the
      // status code away turned all of them into the same dead end.
      const body = await res.json().catch(() => null);
      const err = new Error(body?.error || `POST ${path} returned ${res.status}`);
      err.status = res.status;
      err.code = body?.code;
      err.body = body;
      throw err;
    }
    return res.json();
  };

  // The file itself as the body. Base64 in JSON is a third bigger and would
  // need the JSON limit raised past the size of the files it carries, on every
  // route — so this posts the bytes and puts the name in the query string.
  // **Sent with XHR rather than `fetch`, for the one thing `fetch` cannot do:
  // say how far it has got.** The documents that reach this route are the ones
  // that had to be filed by hand, and those are the *large* ones — a search
  // provider merges a big certificate straight into the practice system rather
  // than emailing it, so 18MB is ordinary here. Behind `fetch` that is half a
  // minute of a word that does not move, which reads as a button that did
  // nothing; it is the whole reason somebody presses it again.
  //
  // `onProgress` is handed a whole number of percent, and only while the
  // length is known — a request with no computable total reports nothing
  // rather than inventing a figure. The upload finishing is not the request
  // finishing: the middleware still has to store the bytes, write the search
  // row and re-read the matter, so 100% means *sent*, and the caller keeps
  // saying so until the answer lands.
  const postFile = (path, file, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${MIDDLEWARE_URL}${path}`);
    xhr.setRequestHeader("Content-Type", file.type || "application/pdf");
    if (ACCESS_TOKEN) xhr.setRequestHeader("x-portal-token", ACCESS_TOKEN);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) {
          onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        }
      };
    }

    // Network-level failure — the connection dropped, or the request never
    // opened. `fetch` rejected here too, so the callers are unchanged.
    xhr.onerror = () => reject(new Error("Upload failed — the connection dropped"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));

    xhr.onload = () => {
      if (xhr.status === 401) { setDenied(true); reject(new Error("Access denied")); return; }
      let body = null;
      try { body = JSON.parse(xhr.responseText); } catch { /* not JSON */ }
      if (xhr.status < 200 || xhr.status >= 300) {
        const err = new Error(body?.error || `Upload failed (${xhr.status})`);
        err.status = xhr.status;
        err.code = body?.code;
        reject(err);
        return;
      }
      resolve(body);
    };

    xhr.send(file);
  });

  // **A file too large for one request goes straight to the bucket.** Cloud
  // Run refuses anything past 32 MiB before the middleware sees it, so past
  // `MAX_IN_REQUEST_BYTES` the middleware opens an upload session, the file is
  // sent to that (with the same progress), and the route is then told which
  // upload to file — `{uploadId}` in place of the bytes. Below it nothing
  // changes: one request, as it always was.
  //
  // The session URL is Google's, not ours, so it carries no portal token.
  const putToSession = (url, file, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", "application/pdf");
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) {
          onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        }
      };
    }
    xhr.onerror = () => reject(new Error("Upload failed — the connection dropped"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.send(file);
  });

  const sendDocument = async (ref, path, file, onProgress) => {
    if (!sendsDirect(file)) return postFile(path, file, onProgress);
    const session = await postJson(
      `/documents/${encodeURIComponent(ref)}/upload-session`, { size: file.size });
    await putToSession(session.uploadUrl, file, onProgress);
    return postJson(path, { uploadId: session.uploadId });
  };

  // **A toast has to say which matter it is about.** These land seconds or
  // minutes after the press, by which time somebody has moved on — and
  // "Placed against Copy of Plan" is no use at all when three matters are in
  // flight. The reference and the client, read at raise time from the matters
  // already loaded, so navigating away cannot change what the toast claims.
  const matterTag = (ref) => {
    if (!ref) return null;
    const m = matters.find(x => x.ref === ref) || (selected?.ref === ref ? selected : null);
    return [ref, m?.client].filter(Boolean).join(" · ");
  };

  // **Both toast functions keep one identity for the life of the app.** They
  // are handed down as `onError`/`onToast`, and the directory's loaders list
  // `onError` in their dependencies — so a function rebuilt on every render
  // reloaded every list and editor on every render, and a toast renders three
  // times (raised, fading, gone). A save's own "Saved" toast put the list back
  // to page one and closed the record just saved; a load that failed raised a
  // toast that re-ran the load, for ever. `matterTag` reads state, so the
  // stable function reaches the current one through a ref.
  const matterTagRef = useRef(matterTag);
  matterTagRef.current = matterTag;

  // How long each toast stays is the toast's own (`ToastItem`), so it can
  // hold while somebody is reading it — see TOAST_MS.
  const showToast = useCallback((msg, ref) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, msg, tag: matterTagRef.current(ref), onUndo: null, ms: TOAST_MS.info }]);
  }, []);

  const showErrorToast = useCallback((msg, ref) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, msg, tag: matterTagRef.current(ref), variant: "error", onUndo: null, ms: TOAST_MS.error }]);
  }, []);

  const showUndoToast = ({ msg, onUndo }) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, msg, onUndo, ms: TOAST_MS.undo }]);
  };

  // Stable, because each toast's timer lists it as a dependency: a function
  // rebuilt on every render of the app would restart every timer with it.
  const dismissToast = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);

  const handleUndo = (id, onUndo) => { onUndo(); dismissToast(id); };

  const updateMatter = (ref, changes) => {
    setMatters(prev => prev.map(m => m.ref === ref ? { ...m, ...changes } : m));
    setSelected(prev => prev?.ref === ref ? { ...prev, ...changes } : prev);
  };
  // The server's answer, in place of what was there — see `withServerMatter`.
  // `updateMatter` is for a local change; this is for a read.
  const replaceMatter = (ref, next) => {
    setMatters(prev => prev.map(m => m.ref === ref ? withServerMatter(m, next) : m));
    setSelected(prev => prev?.ref === ref ? withServerMatter(prev, next) : prev);
  };

  // Read a matter and its searches back from the middleware. Shared by opening
  // one and by anything that changes what the compile would use, so the screen
  // is always showing the server's answer rather than a guess at it.
  const loadMatterRef = useRef(null);
  const loadMatter = async (ref) => {
    const res = await apiFetch(`/matters/${encodeURIComponent(ref)}`);
    if (res.status === 401) { setDenied(true); return null; }
    const data = await res.json();
    if (!data.ok) return null;
    const next = matterFromResponse(data);
    replaceMatter(next.ref, next);
    return next;
  };
  loadMatterRef.current = loadMatter;

  // Close a matter's tab; closing the one on screen moves to the tab before it,
  // or back to the list when it was the last.
  const closeMatterTab = (ref) => {
    const rest = openMatters.filter(m => m.ref !== ref);
    setOpenMatters(rest);
    if (selectedRef === ref) {
      const next = rest[rest.length - 1];
      if (next) handleSelect(matters.find(m => m.ref === next.ref) || { ref: next.ref });
      else handleBack();
    }
  };
  // A matter into a window of its own. In the installed app that is another
  // app window with no browser bar; in a browser, a popup. Its tab here closes:
  // the matter has moved, it is not open twice.
  const popOutMatter = (ref) => {
    const win = window.open(popOutUrl(ref), `matter-${ref}`, isInstalledApp() ? undefined : "popup,width=1280,height=900");
    if (win) closeMatterTab(ref);
  };
  useEffect(() => {
    // A session has not answered yet, so the tab stays unnamed rather than
    // saying Provey for a firm that is not Provey.
    if (isSessionToken(ACCESS_TOKEN) && !me) return;
    const firm = me?.firm?.isDefault === false ? me.firm : null;
    applyFirmTab(firm, { matter: POPOUT && selected ? matterTabLabel(selected) : "" });
  }, [me, selected]);

  // The list's sort and the place a matter was opened from (DA-26), so the
  // way back from a matter lands where the list was left.
  const [listSort, setListSort] = useState({ key: null, dir: "asc" });
  const listReturn = useRef(null);
  const handleSelect = async (m) => {
    if (view === "dashboard") listReturn.current = { y: window.scrollY || 0, ref: m.ref };
    setSelected({ ...m, searches: m.searches || [] });
    setView("detail");
    // The URL follows the screen, so Back has somewhere to go and a refresh
    // reopens the same matter. Guarded, or the hashchange this fires would
    // re-open the matter it just opened.
    if (refFromHash(window.location.hash) !== m.ref) window.location.hash = matterHash(m.ref);
    setSearchesLoading(true);
    try {
      await loadMatter(m.ref);
    } catch (err) {
      console.error("Failed to fetch matter detail", err);
    } finally {
      setSearchesLoading(false);
    }
  };

  // Take the matter out of the URL. Pushed when somebody is navigating away —
  // so Back returns them to the matter — and replaced when the matter is no
  // longer somewhere to return to (deleted, archived).
  const clearMatterUrl = (push) => {
    if (!window.location.hash) return;
    const bare = window.location.pathname + window.location.search;
    if (push) window.history.pushState(null, "", bare);
    else window.history.replaceState(null, "", bare);
  };
  // The in-app button pushes a dashboard entry rather than calling
  // history.back(): a matter opened in its own tab has no dashboard entry
  // behind it to go back to.
  const handleBack = () => { clearMatterUrl(true); setView("dashboard"); setSelected(null); };
  // Ordering searches from a matter: the order screen, for that matter, and
  // back to it — read again, since the order added searches to it.
  const [orderFor, setOrderFor] = useState(null);
  const handleOrderSearches = (m) => { setOrderFor({ ref: m.ref, address: m.address || "" }); setView("searchOrders"); };
  const handleBackFromOrder = () => {
    const ref = orderFor && orderFor.ref;
    setOrderFor(null);
    setView("detail");
    if (ref) loadMatter(ref);
  };
  // The portal's navigation. A list tab from a matter is the same move as
  // Back, so it goes through `handleBack` and the matter's URL is cleared the
  // same way; the two lists are one screen asked two questions.
  // Unsaved edits on Settings (DA-11). A ref, because the press reads it at
  // the moment of leaving; the setter is stable so the page's effect does not
  // re-run on every App render.
  const settingsDirtyRef = useRef(false);
  const setSettingsDirty = useCallback((d) => { settingsDirtyRef.current = d; }, []);
  // The practice look has no search box on the list (the title bar's is it),
  // so going to the list from the menu, or by "All matters", clears a search
  // the title bar sent rather than leaving the list narrowed out of sight.
  const handleNav = (tab, { keepSearch = false } = {}) => {
    if (view === "settings" && tab !== "settings" && settingsDirtyRef.current
        && !window.confirm(LEAVE_SETTINGS_PROMPT)) return;
    if (tab === "agents") { setView("directory"); return; }
    if (tab === "sharing") { setView("sharing"); return; }
    if (tab === "orders") { setOrderFor(null); setView("searchOrders"); return; }
    if (tab === "contacts") { setView("contacts"); return; }
    if (tab === "settings") { setView("settings"); return; }
    if (view !== "dashboard") handleBack();
    if (practiceOn && !keepSearch) setSearch("");
    setShowArchived(tab === "archived");
    setStatusFilter("Open");
  };
  // The practice look's matter list filters on status; anything but Open is
  // read from the archive list.
  const [statusFilter, setStatusFilter] = useState("Open");
  const chooseStatusFilter = (st) => { setStatusFilter(st); setShowArchived(st !== "Open"); };

  // Bound once, reading through refs: the listener outlives every render, and
  // what it needs at event time is the route now and the matters now.
  routeRef.current = { view, selectedRef: selected?.ref || null };
  mattersRef.current = matters;
  useEffect(() => {
    const applyRoute = () => {
      const ref = refFromHash(window.location.hash);
      const here = routeRef.current;
      if (ref) {
        if (here.selectedRef === ref && here.view !== "dashboard") return;
        // The list row seeds the screen when it is loaded; a tab opened
        // straight onto the hash starts from the shell and loadMatter fills
        // it in.
        const m = mattersRef.current.find(x => x.ref === ref) || { ref, searches: [] };
        handleSelect(m);
      } else if (here.view === "detail") {
        setView("dashboard");
        setSelected(null);
      }
    };
    // The hash is the route. A `?matter=` arriving from the embedder is an
    // instruction to open one, so it is spent here: written into the hash and
    // taken out of the query, or a later return to the dashboard followed by a
    // reload would re-open the matter somebody had just left.
    try {
      const wanted = refFromQuery(window.location.search);
      if (wanted && !refFromHash(window.location.hash)) {
        const url = new URL(window.location.href);
        url.hash = matterHash(wanted);
        url.searchParams.delete("matter");
        window.history.replaceState({}, "", url.toString());
      }
    } catch (e) { /* history blocked — the hash link still works */ }

    window.addEventListener("hashchange", applyRoute);
    if (refFromHash(window.location.hash)) applyRoute();
    return () => window.removeEventListener("hashchange", applyRoute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saving one field posts only that field. The middleware merges into
  // manualFields and hands back the whole resolved view, so provenance and the
  // missing list repaint from the server rather than being guessed at here —
  // the panel can't drift from what the compile would produce.
  // **Several answers in one write.** Turning the commercial lease block on
  // records the lease question and the possession answer together, and two
  // sequential saves would repaint twice and leave the matter briefly in a
  // state nobody chose. The route has always taken a map; this is the one
  // implementation behind both callers, so a single correction cannot drift
  // from a pair of them.
  // Saves in flight, so a directory write can wait for the correction it is
  // about to read. See `handleSaveToDirectory`.
  const pendingFieldSaves = useRef(new Set());

  const saveContractFieldsNow = async (ref, fields) => {
    const names = Object.keys(fields || {});
    setSavingFields(prev => { const next = new Set(prev); names.forEach(n => next.add(n)); return next; });
    try {
      const data = await postJson(`/contract-fields/${encodeURIComponent(ref)}`, { fields });
      if (data.ok) updateMatter(ref, { contractFields: data.contractFields });
      // A corrected agent email can take the matter off the agent the link went
      // to; the matter is read again so the menu stops offering a dead link.
      if (data.revokedLink) { showToast(REVOKED_LINK_NOTE, ref); loadMatter(ref); }
      return Boolean(data.ok);
    } catch (err) {
      // Loud on purpose. A correction that looks saved but isn't would go into
      // a legal document as the old value.
      showErrorToast(`Could not save that field — ${err.message}`);
      return false;
    } finally {
      setSavingFields(prev => { const next = new Set(prev); names.forEach(n => next.delete(n)); return next; });
    }
  };

  // Registered synchronously, before the request is sent — the popover calls
  // this and the directory write in the same tick, and the second has to find
  // the first already in the set.
  const saveContractFields = (fields) => {
    if (!selected) return Promise.resolve(false);
    if (!Object.keys(fields || {}).length) return Promise.resolve(false);
    const p = saveContractFieldsNow(selected.ref, fields);
    pendingFieldSaves.current.add(p);
    p.finally(() => pendingFieldSaves.current.delete(p));
    return p;
  };

  const handleSaveField = (name, value) => saveContractFields({ [name]: value });

  // Rewording a clause of the precedent on this matter.
  //
  // **Not a contract field.** The numbered special conditions and the two
  // Additional Special Condition blocks are the precedent's own wording in the
  // stored template, so there is no registry field to correct — the middleware
  // stores what was typed against the clause's own citation and the document
  // renders it in place of the form's words.
  //
  // **A blank is a revert.** Clearing an override restores the clause rather
  // than blanking it, which is the whole reason it is safe to offer, so the
  // Restore button is an empty save rather than a second operation.
  const [savingClause, setSavingClause] = useState(null);
  const saveClauseOverride = async (clause, text) => {
    if (!selected) return;
    const ref = selected.ref;
    setSavingClause(clause);
    try {
      const data = await postJson(`/clause-overrides/${encodeURIComponent(ref)}`,
        { overrides: { [clause]: text } });
      // The stored map goes back so the preview repaints from what was
      // actually saved rather than from what it hoped it sent — and the
      // preview keys its rebuild on it, so the document follows.
      if (data.ok) updateMatter(ref, { clauseOverrides: data.clauseOverrides });
    } catch (err) {
      // Loud on purpose. A clause that looks saved and is not would go out as
      // the precedent's wording on a matter somebody had deliberately changed.
      showErrorToast(`Could not save that clause — ${err.message}`);
    } finally {
      setSavingClause(null);
    }
  };

  // Taking a clause out of this matter's contract, or putting it back.
  //
  // **Its own action rather than a wording save**, because it is a different
  // thing: the clause keeps its number and prints `Not used`, and everything
  // filed underneath it stops printing. The middleware decides what that
  // means — a copy of the hierarchy here would drift from the document.
  const removeClause = async (clause, restore) => {
    if (!selected) return;
    const ref = selected.ref;
    setSavingClause(clause);
    try {
      const data = await postJson(`/clause-overrides/${encodeURIComponent(ref)}`,
        restore ? { restore: [clause] } : { remove: [clause] });
      // Both halves come back, because putting a clause back clears whatever
      // stood in for `Not used` as well as the removal itself.
      if (data.ok) {
        updateMatter(ref, {
          clausesRemoved: data.clausesRemoved,
          clauseOverrides: data.clauseOverrides,
        });
      }
    } catch (err) {
      // Loud, the same as a clause save: a clause that looks deleted and is
      // not would go out with wording the firm meant to take off the contract.
      showErrorToast(`Could not ${restore ? "restore" : "delete"} that clause — ${err.message}`);
    } finally {
      setSavingClause(null);
    }
  };

  // Adopting the directory's answer for this matter's agent.
  //
  // **The middleware decides which field gets which value**, and it saves them
  // as ordinary manual corrections — so this needs no rule of its own, the
  // panel repaints from the response like any other save, and every value is
  // revertable one at a time afterwards.
  //
  // It reloads rather than patching `contractFields` in: the offer itself has
  // to change too, from a list of candidates to "taken from the directory",
  // and that lives beside the matter rather than in the resolved fields.
  // `choice` is `{agentId}` or `{officeId}` — the agency on its own is most of
  // what an existing matter knows, and it prints the name and the address
  // notices are served on. The middleware decides which fields each fills.
  // Staff switching the agent's link off by hand. Confirmed rather than
  // undoable: the link stops working the moment this lands, and putting it
  // back is a release, which sends a fresh one.
  const handleRevokeAgentLink = async (ref) => {
    const m = matters.find(x => x.ref === ref) || selected || {};
    const who = String(m.releasedTo || "").trim();
    if (!window.confirm(`Switch off the agent's download link${who ? ` sent to ${who}` : ""}? `
      + "It stops opening the contract straight away. Purchasers they shared it with keep their access. "
      + "Releasing again sends a fresh link.")) return;
    try {
      await postJson(`/matters/${encodeURIComponent(ref)}/agent-link/revoke`, {});
      await loadMatter(ref);
      showToast("The agent's download link is switched off.", ref);
    } catch (err) {
      showErrorToast(`Could not switch the link off — ${err.message}`);
    }
  };

  const handleAdoptAgent = async (ref, choice) => {
    const body = typeof choice === "string" ? { agentId: choice } : (choice || {});

    // **A web result has no record to adopt, so it saves corrections
    // directly.** It is the same write the directory's own adoption ends in —
    // `manual` outranks every source, survives a re-extraction and reverts —
    // and it deliberately creates no directory record: a scraped agency is not
    // a verified one, and putting it in the directory is the separate "I can't
    // find my agent" route.
    if (body.fields) {
      setAdoptingAgent(true);
      try {
        // **`{fields: {...}}`, not the bare object.** The route rejects anything
        // else outright — "fields is required" — which is the right refusal and
        // was my mistake at the call site.
        //
        // **And it ends the directory link.** These values are no record's, so
        // a matter left naming the record it replaced would send a later *save
        // to the directory* to that record — the web's address on an agency
        // that prints for every agent there. An older middleware ignores the
        // flag, which is today's behaviour.
        const data = await postJson(`/contract-fields/${encodeURIComponent(ref)}`,
          { fields: body.fields, unlinkDirectory: true });
        if (data.ok) {
          await loadMatter(ref);
          showToast("Agency taken from the web. Check it, and revert if it is wrong."
            + (data.revokedLink ? ` ${REVOKED_LINK_NOTE}` : ""));
        }
      } catch (err) {
        showErrorToast(`Could not use that agency — ${err.message}`);
      } finally {
        setAdoptingAgent(false);
      }
      return;
    }

    setAdoptingAgent(true);
    try {
      const data = await postJson(`/matter-agent/${encodeURIComponent(ref)}`, body);
      if (data.ok) {
        await loadMatter(ref);
        showToast((body.officeId && !body.agentId
          ? "Agency taken from the directory. Every value is a correction you can revert."
          : "Agent taken from the directory. Every value is a correction you can revert.")
          + (data.revokedLink ? ` ${REVOKED_LINK_NOTE}` : ""));
      }
    } catch (err) {
      // Loud, like a field save: an agent's address is where notices are
      // served, so an adoption that looks saved and is not would be worse
      // than the button not working.
      showErrorToast(`Could not use that agent — ${err.message}`);
    } finally {
      setAdoptingAgent(false);
    }
  };

  // ── A correction, put back on the record it came from ────────────────────
  //
  // The other half of adopting: that pulls the directory's values onto a
  // matter, and this pushes a matter's correction back, so the next matter at
  // that agency prints it too. Without it a correction is per-matter for ever.
  //
  // **It names the field and nothing else.** The middleware reads the resolved
  // value off the matter — the way the signing request reads its addresses off
  // the record rather than off the request — so this cannot put a value on a
  // shared office record that nobody ever saw on a page.
  //
  // **It runs after the field is saved and never blocks it.** The correction
  // on the matter is what was asked for; a directory write that fails must
  // leave that standing, loudly rather than silently, because an agency's
  // address is where notices under the contract are served.
  //
  // **And it waits for the save.** The middleware reads the value off the
  // matter, so the two used to race: the popover fired the field save and the
  // directory write in the same tick, the directory write read the matter
  // before the correction had landed, and it pushed the *old* value back onto
  // the record — reporting success over a press that changed nothing. Staff
  // reported it as the feature not working, which is exactly what it was.
  const handleSaveToDirectory = async (ref, name) => {
    const pending = [...pendingFieldSaves.current];
    const results = await Promise.all(pending);
    if (results.some(ok => ok === false)) {
      showErrorToast("Not saved to the directory — the field itself did not save.");
      return;
    }
    try {
      const data = await postJson(`/matter-agent/${encodeURIComponent(ref)}/directory`,
        { fields: [name] });
      if (!data.ok) return;
      // **The middleware says which record it landed on**, because an office
      // is shared and a person is not — and a press that saved nothing says
      // so rather than reading as one that worked.
      const saved = data.saved || [];
      if (!saved.length) {
        showErrorToast(`Nothing was saved to the directory — ${directorySkipWording(data.skipped)}`);
        return;
      }
      showToast(saved.some(s => s.record === "office")
        ? "Saved to the agency's directory record, for every matter at it."
        : "Saved to the salesperson's directory record.");
    } catch (err) {
      showErrorToast(`Saved on this matter, but not to the directory — ${err.message}`);
    }
  };

  // Undoing one correction is the same write as saving one — an empty value
  // clears it, and the field falls back to whatever the certificates, the
  // matter or the firm record say. Nothing here decides what it falls back
  // *to*: the middleware resolves it and hands back the whole view.
  const handleRevertField = (name) => handleSaveField(name, "");

  // Undoing all of them at once. This one does need its own endpoint, because
  // "all of them" excludes corrections with nothing underneath — clearing
  // those would blank the field rather than restore anything — and that
  // decision belongs where the precedence order lives.
  const handleRevertAllFields = async (ref) => {
    setRevertingAll(true);
    try {
      const data = await postJson(`/contract-fields/${encodeURIComponent(ref)}/revert`, {});
      if (data.ok) {
        updateMatter(ref, { contractFields: data.contractFields });
        const n = data.reverted?.length || 0;
        showToast(n === 0
          ? "Nothing to revert — none of those edits had an extracted value under them."
          : `Reverted ${n} field${n !== 1 ? "s" : ""} to what the sources say.`);
      }
    } catch (err) {
      showErrorToast(`Could not revert those edits — ${err.message}`);
    } finally {
      setRevertingAll(false);
    }
  };
  // ── Saying a group of fields is not needed on this matter ─────────────────
  //
  // **One press for the group.** The agent's details are required because the
  // matter has an agent; a private sale, or an agency with nothing more to
  // give, is a real matter, and five rows nobody can clear is how a
  // completeness list stops being read.
  //
  // The matter is reloaded rather than patched: the middleware answers with
  // what this matter now requires, and the panel repaints from what was
  // stored rather than from what it hoped it sent.
  const handleNotRequired = async (ref, group) => {
    setMarkingNotRequired(true);
    try {
      const data = await postJson(
        `/contract-fields/${encodeURIComponent(ref)}/not-required`,
        { group, notRequired: true });
      if (data.ok) {
        await loadMatter(ref);
        showToast("Marked as not needed on this matter — the whole group.");
      }
    } catch (err) {
      showErrorToast(`Could not mark those as not needed — ${err.message}`);
    } finally {
      setMarkingNotRequired(false);
    }
  };

  // One block's added clauses, replaced; the other block's are untouched. True
  // when it saved, so the editor can close on success and stay open on a
  // failure with what was typed still in it.
  const handleSaveBlockClauses = async (ref, block, clauses) => {
    setSavingBlockClauses(true);
    try {
      const data = await postJson(`/block-clauses/${encodeURIComponent(ref)}`, { block, clauses });
      if (!data.ok) return false;
      updateMatter(ref, { blockClauses: data.blockClauses });
      const n = (data.blockClauses?.[block] || []).length;
      showToast(n === 0 ? "Added clauses cleared" : `Saved ${n} added clause${n === 1 ? "" : "s"}`);
      return true;
    } catch (err) {
      showErrorToast(`Could not save the clauses — ${err.message}`);
      return false;
    } finally {
      setSavingBlockClauses(false);
    }
  };

  // The whole list, replaced. The middleware hands back what it stored, and the
  // matter is updated from that rather than from what was sent — a clause that
  // was trimmed or dropped on the way in must show as it will print, not as it
  // was typed.
  const handleSaveSpecialConditions = async (ref, conditions) => {
    setSavingConditions(true);
    try {
      const data = await postJson(`/special-conditions/${encodeURIComponent(ref)}`, { conditions });
      if (data.ok) {
        updateMatter(ref, { specialConditions: data.specialConditions });
        const n = data.specialConditions.length;
        showToast(n === 0 ? "Special conditions cleared" : `Saved ${n} special condition${n === 1 ? "" : "s"}`);
      }
    } catch (err) {
      // Loud, for the same reason a field save is: a clause that looks saved
      // and isn't goes out of the contract silently.
      showErrorToast(`Could not save the special conditions — ${err.message}`);
    } finally {
      setSavingConditions(false);
    }
  };

  // Which matter's compile finished, and how many have. The ref travels with
  // the count so the panel can ask whether it is the matter on screen — see
  // `expandSignalFor`.
  const [compiledExpand, setCompiledExpand] = useState({ ref: "", n: 0 });
  // **Arrange searches opens the compile workspace.** Its list reorders,
  // replaces, renames, removes and adds, with the document beside it, so a
  // second screen for the same list was two places to learn. In the practice
  // look the press never reaches here — the searches list is the arrange
  // screen there (`arrangeHere`).
  const handleArrangeDocuments = () => {
    if (selected) setCompileWorkspace(selected.ref);
  };

  // The middleware answers /retry-compile immediately and merges in the
  // background, so poll the matter until it reaches a terminal state.
  // Functional, and matched on the ref: a poll that finishes for A must not
  // clear a banner that now belongs to B. Reading the state directly here
  // would also close over a stale value — the poll outlives several renders.
  // **Which matter a compile stop was asked for, and confirmed as running.**
  // A ref rather than state because the poll closes over its own render: the
  // press happens *during* a poll, so a value captured when the watch was
  // armed would never see it. Cleared with the banner, so it can never be read
  // against the next compile.
  const stopAskedFor = useRef("");

  const stopCompiling = (ref) => {
    if (!ref || stopAskedFor.current === ref) stopAskedFor.current = "";
    setCompileWork(cur => (!ref || cur?.ref === ref ? null : cur));
  };

  // **Asking the server to stop, which `stopCompiling` above does not do.**
  // That one only takes the banner off this tab's screen — the compile carries
  // on and lands a version anyway. They sit next to each other deliberately so
  // nobody reaches for the wrong one again.
  //
  // The two kinds stop in genuinely different ways and the toast has to say
  // which: a compile stops at its next checkpoint and leaves the current
  // version alone, and a read stops us waiting while the extractor finishes
  // regardless. Neither can recall a request already accepted.
  const handleStopWork = async (what) => {
    const ref = selected?.ref;
    if (!ref || stopping) return;
    setStopping(true);
    try {
      const res = await postJson(`/stop/${encodeURIComponent(ref)}`, { what });
      showToast(res?.message || "Asked it to stop", ref);
      // **Nothing was running, so there is nothing to wait for.** The banner is
      // raised by this tab and cleared by the poll when the matter settles — so
      // a bar over a compile that never started would sit there for its whole
      // budget with Stop apparently doing nothing. The middleware says which
      // case it is; an older one says nothing and this behaves as it did.
      if (what === "compile") {
        if (res?.running === false) stopCompiling(ref);
        // **And where something *was* running, remember that it was.** The
        // compile stops at its next checkpoint and writes no version, so
        // nothing the poll already watches for will ever fire — this is what
        // lets it read `compile.running` turning false as the end of the run
        // rather than as a run that has not started. `undefined` from an older
        // middleware records nothing, which is today's behaviour.
        else if (res?.running === true) {
          stopAskedFor.current = ref;
          // Said before the next poll, which is up to three seconds away. The
          // press was acknowledged; the work has not stopped yet, and a banner
          // that goes on reading *Compiling…* with nothing acknowledging the
          // button is the shape somebody presses twice.
          setCompileWork(cur => (cur?.ref === ref ? { ...cur, stopRequested: true } : cur));
        }
      }
      // The banner comes down on the read, because the middleware has released
      // the claims and there is nothing left to wait for. **Not on the
      // compile** — it stops at its next checkpoint, which has not happened
      // yet, and taking the banner down now would say it had stopped when it
      // may still be merging. The poll clears it when the matter settles.
      if (what === "read") {
        stopCompiling(ref);
        updateMatter(ref, { documentsBeingRead: 0, awaitingExtractionSince: null });
      }
    } catch (err) {
      showErrorToast(err.message || "Could not stop it", ref);
    } finally {
      setStopping(false);
    }
  };

  // One poll, two very different waits.
  //
  // A merge lands in about half a minute. Reading the certificates hands them
  // to the extractor, which the middleware gives 30 minutes before building the
  // contract anyway — and the matter never reaches `compiled` on its own while
  // that runs, because it is the *callback* that starts the compile producing
  // the version. Polling both on the merge's budget reported a perfectly
  // healthy read as a failure after three minutes.
  //
  // So the budget follows what the matter says it is doing, and a read is only
  // finished when the stamp has cleared.
  const MERGE_BUDGET_MS = 3 * 60 * 1000;
  const READ_BUDGET_MS = 35 * 60 * 1000; // the middleware's 30-minute window, plus the compile that follows it

  // What "finished" means, which is not the same for the two buttons.
  //
  // `fromVersion` is the version the matter had when the work started, and a
  // compile is done when it goes up. Waiting for `status === "compiled"`
  // instead looked right and was wrong on a re-compile: the matter is already
  // compiled three seconds later, so the bar vanished before anything had
  // happened.
  //
  // `expectsVersion` is false for a re-read, which since the middleware change
  // deliberately does not rebuild the contract. There is no version to wait
  // for — it is finished when the matter stops saying it is reading.
  // How long a read may take to *start* before the poll says so. The bundle
  // path merges the whole pack at pdf.co before it hands anything over, which
  // is seconds on a small matter and can be a minute on a large one — and an
  // extractor that refuses outright never stamps the matter at all, so
  // something has to bound the wait. Two minutes is well past the merge and
  // well short of leaving somebody watching a bar for half an hour.
  const START_GRACE_MS = 2 * 60 * 1000;

  // The middleware's own `PARCEL_CHANGE_DEBOUNCE_MS`, plus the lookup itself —
  // a query per parcel against a free government service, which has been seen
  // to take the better part of a minute.
  const PLANNING_LOOKUP_MS = 3 * 60 * 1000;

  const pollCompile = (ref, {
    startedAt = Date.now(), delay = 3000, fromVersion = 0,
    expectsVersion = true, startSeen = true,
  } = {}) => {
    pollTimer.current = setTimeout(async () => {
      let reading = false;
      let seen = startSeen;
      try {
        const res = await apiFetch(`/matters/${encodeURIComponent(ref)}`);
        const data = await res.json();
        if (data.ok) {
          // **The folded matter, and everything below reads it.** `compile`,
          // `lotSearch`, `signatures` and the rest are *siblings* of `matter`
          // in the response — `matterFromResponse` is what folds them in, and
          // `data.matter` alone does not carry one of them.
          //
          // Written as `data.matter` this fed `compileFinished` a matter with
          // no `compile` on it at all, so the branch that takes the bar down
          // when a stopped compile actually stops could never fire: the run
          // ended in four seconds and the banner sat there for its whole
          // three-minute budget saying "Stopping…". 1760201. The unit tests
          // passed throughout, because they hand `compileFinished` an object
          // with `compile` on it — they test the function, not what the call
          // site gives it.
          //
          // The same hand-naming reached no screen seven times over, and cost
          // `lotSearch` most recently: it decides whether *Find lot on plan* is
          // offered and turns from `not_compiled` to offered at exactly the
          // moment a compile finishes, so the section stayed hidden until
          // somebody reloaded. 1760462. One folded object, used everywhere, is
          // the answer to both.
          const m = matterFromResponse(data);
          replaceMatter(ref, m);
          reading = Boolean(m.awaitingExtractionSince) || (m.documentsBeingRead || 0) > 0;
          seen = seen || reading;

          const stopAsked = stopAskedFor.current === ref;
          if (compileFinished({ matter: m, fromVersion, expectsVersion, startSeen: seen, stopAsked })) {
            const stopped = stopAsked && (m.compileVersion || 0) <= fromVersion;
            stopCompiling(ref);
            if (stopped) {
              // **Say what it left behind, not merely that it stopped.** The
              // whole guarantee of stopping a compile is that the version
              // already on the matter is untouched, and the bar the person was
              // watching has just vanished — so the useful sentence names the
              // version they still have.
              const kept = m.compileVersion
                ? `v${m.compileVersion} is still the current version.`
                : "Nothing was compiled, so there is no version yet.";
              showToast(`Compile stopped — ${kept}`, ref);
              updateMatter(ref, { lastActionNotice: {
                text: `Compile stopped. ${kept}`, tone: "ink",
              } });
            } else if (!expectsVersion) {
              // Not "re-compiled": nothing was. Saying so would be the same
              // fault as the timeline claiming a step that never happened.
              // A per-document failure lives on its row rather than on
              // geminiError, so the rows are what say whether the read worked.
              const failedReads = (data.searches || [])
                .filter(x => x.readState === "failed").length;
              const readOutcome = failedReads > 0
                ? `${failedReads} certificate${failedReads === 1 ? "" : "s"} could not be read — press Re-read to try again`
                : m.geminiError
                  ? "The certificates could not be read"
                  : "Certificates read. Re-compile to put the new values in the PDF.";
              showToast(readOutcome, ref);
              // **Inline as well as the toast.** The toast lives in a corner
              // and is gone in seconds; the bar the person was watching just
              // vanished, so the outcome belongs where the bar was.
              updateMatter(ref, { lastActionNotice: {
                text: readOutcome,
                tone: failedReads > 0 || m.geminiError ? "amber" : "green",
              } });
              // **The planning map answers after the poll has stopped.**
              // Reading a title can change which parcels a matter has, and the
              // middleware asks Vicmap about them — but behind a two-minute
              // debounce, deliberately, so seven certificates finishing seconds
              // apart are one lookup rather than seven. The bar is long gone by
              // then, so the zone, the overlays and the bushfire answer only
              // showed up on a page reload, which is what staff were doing.
              //
              // One late re-check is the whole fix, and it is the same idiom as
              // `recheckForRead`: a single GET that costs nothing when there is
              // nothing new. Not a second poll — if the lookup is slower than
              // this, the values are on the matter next time it is opened.
              setTimeout(() => { loadMatter(ref).catch(() => {}); }, PLANNING_LOOKUP_MS);
            } else {
              showToast(`Re-compiled — now v${m.compileVersion || 1}`, ref);
              updateMatter(ref, { lastActionNotice: {
                text: `Re-compiled — now v${m.compileVersion || 1}.`, tone: "green",
              } });
              // **And show them the document.** This branch is the one that
              // produced a version — the read branch above deliberately did
              // not — so it is the only place there is something new to look
              // at. Two minutes of watching a bar used to end in a toast and a
              // collapsed panel.
              setCompiledExpand(prev => ({ ref, n: (prev.n || 0) + 1 }));
            }
            return;
          }
          if (m.status === "compile_failed") {
            stopCompiling(ref);
            showErrorToast("Compile failed — see the error on the matter", ref);
            return;
          }
        }
      } catch (err) {
        console.error("Compile poll failed", err);
      }
      // Asked for, and never seen to begin. Said rather than waited out: the
      // honest report is that the read does not look like it started, which is
      // a different thing from one that is taking a while — and the budget
      // below would otherwise hold the bar up for the full half hour on a
      // matter where nothing is happening at all.
      if (!seen && Date.now() - startedAt >= START_GRACE_MS) {
        stopCompiling(ref);
        const notStarted = "The certificate read has not started — reopen the matter to check";
        showErrorToast(notStarted, ref);
        updateMatter(ref, { lastActionNotice: { text: `${notStarted}.`, tone: "amber" } });
        return;
      }
      if (Date.now() - startedAt >= (reading ? READ_BUDGET_MS : MERGE_BUDGET_MS)) {
        stopCompiling(ref);
        showErrorToast(reading
          ? `${ref} is still waiting on the certificate read — reopen the matter to check`
          : `${ref} is still compiling after 3 minutes — reopen the matter to check`);
        updateMatter(ref, { lastActionNotice: {
          text: reading
            ? "Still waiting on the certificate read — reopen the matter to check."
            : "Still compiling — reopen the matter to check.",
          tone: "amber",
        } });
        return;
      }
      // Slower once it's a wait of minutes rather than seconds. Nothing here
      // changes second to second, and this runs for half an hour.
      pollCompile(ref, {
        startedAt, delay: reading ? 10000 : 3000, fromVersion, expectsVersion, startSeen: seen,
      });
    }, delay);
  };

  const triggerCompile = (ref) => {
    // Captured before the compile starts, because "it went up" is the only
    // honest signal that this compile produced something.
    const fromVersion = selected?.compileVersion || 0;
    setCompileWork({ ref, startedAt: Date.now() });
    updateMatter(ref, { lastActionNotice: null });
    postJson(`/retry-compile/${encodeURIComponent(ref)}`, {})
      .then(() => pollCompile(ref, { fromVersion }))
      .catch(err => {
        // **`compile_running` is not a failure — it is the answer.** A compile
        // takes a minute or two and this button used to say nothing until it
        // landed, so pressing again is what anybody would do; on matter 1760416
        // two presses twenty-three seconds apart produced two compiles that
        // both wrote v1, and two Chromium renders that starved each other until
        // one fell back to pdf.co and lost the contract's page numbers.
        //
        // The middleware refuses the second press now. Showing that as "could
        // not start the compile" would be worse than the bug: it says nothing
        // is happening when something is, which invites a third press. So the
        // banner stays and the poll runs — this press joins the compile that is
        // already going, which is what pressing it meant.
        const running = runningCompile(err);
        if (running) {
          // Backdated to when the compile actually started, not to when this
          // tab noticed. The banner's elapsed counter and the poll's budget
          // both measure the compile, so starting them from now would show a
          // minute-old compile as three seconds old and then wait a full budget
          // past its real end.
          setCompileWork({ ref, startedAt: running.startedAt });
          pollCompile(ref, { fromVersion, startedAt: running.startedAt });
          return;
        }
        console.error("Failed to trigger compile", err);
        stopCompiling(ref);
        showErrorToast("Could not start the compile", ref);
      });
  };

  // **Only when there is something to say.** A confirmation on every compile is
  // one people learn to click through without reading, and then it is worth
  // nothing on the matter that needed it. Nothing outstanding compiles straight
  // away, exactly as it always did.
  //
  // **Compile opens the compile workspace first** (`CompileWorkspace`): the
  // pack beside the document, and its own Compile is the press that runs this.
  // The confirmation for a matter with searches outstanding stays where it was,
  // on that press, and draws over the workspace.
  const handleManualCompile = () => setCompileWorkspace(selected.ref);

  // **No confirmation from the workspace.** That dialog lists the searches
  // review (on the workspace's left already) and the contract data nothing has
  // filled (listed there too, above the review) — so asking again at the press
  // was the same list a second time, over the screen that shows it.
  const runCompile = () => {
    setCompileWorkspace("");
    triggerCompile(selected.ref);
  };

  const handleConfirmCompile = () => {
    setConfirmCompile(null);
    setCompileWorkspace("");
    triggerCompile(selected.ref);
  };

  // Sends the certificates to be read again. Unlike a re-compile this doesn't
  // produce anything directly — the contract is rebuilt by the callback — so
  // what the button buys is the reading banner and a matter held against
  // compiling until the answer lands.
  // Asks which certificates first — see RereadModal. Pressing this used to
  // read every document unconditionally, which on a healthy matter is a full
  // Gemini pass per document to arrive at the answers already on the screen.
  const handleReread = () => setShowReread(true);

  // The zone, the overlays, the planning scheme and the bushfire prone area,
  // read from the state's own map for this matter's parcels.
  //
  // **It reports what came back, not that it ran.** A parcel the map could not
  // answer for leaves the bushfire question exactly as blank as it found it —
  // which is the whole of why that field is required: an unticked box on the
  // printed form asserts the land is *not* in a bushfire prone area, and there
  // is nothing on the page that could distinguish that from nobody having
  // looked. A toast saying "done" over an unresolved parcel would be the one
  // thing this must not do.
  //
  // Nothing to poll, no banner, no compile: the values are on the matter by the
  // time this answers, so the matter is read back and the panel shows them.
  const lookupVicmap = async (ref) => {
    try {
      const res = await postJson(`/vicmap-lookup/${encodeURIComponent(ref)}`, {});
      // The resolution is the middleware's, so the panel and the document only
      // agree with the new values once the matter has been read back.
      await loadMatter(ref);

      // **The notice has to be cleared here, and it was not.** `loadMatter`
      // spreads the server's answer over the matter, and `lastActionNotice`
      // is the browser's own state — not a key the response carries — so the
      // spread preserved it rather than dropping it. The comment claiming it
      // "clears itself" was simply wrong, and on every successful lookup the
      // matter went on saying it was still looking after the toast had
      // already reported the answer.
      const unresolved = res?.unresolved || [];
      if (unresolved.length) {
        const said = unresolved.length === 1
          ? `${unresolved[0].spi || "One parcel"} could not be looked up — the bushfire prone area is still unanswered`
          : `${unresolved.length} parcels could not be looked up — the bushfire prone area is still unanswered`;
        showErrorToast(said, ref);
        // Kept inline, like the refusal: a parcel nobody could answer for is
        // an outcome somebody has to act on, and a toast is gone in seconds.
        updateMatter(ref, { lastActionNotice: { text: said, tone: "amber" } });
        return;
      }
      // **Cleared rather than restated.** The values are on the panel and the
      // document now, which is a better report than a line above them
      // repeating what the toast just said.
      updateMatter(ref, { lastActionNotice: null });
      // A lookup takes tens of seconds, which is long enough to have moved to
      // the dashboard — and an unattributed zone and bushfire answer there
      // belongs to no matter in particular. The toast carries the reference
      // and the client itself now, so it is passed rather than written into
      // the message.
      showToast(vicmapSummary(res), ref);
    } catch (err) {
      // Worth reading rather than dismissing. The refusal that will happen most
      // is `no_parcel` — the title has not been read, so there is no lot and
      // plan to search on — and it names the thing to do about it.
      //
      // **Inline as well as the toast**, and it replaces the "looking up" line
      // rather than leaving it: a refusal that only toasted would leave the
      // matter saying it was still looking, for ever.
      showErrorToast(err.message, ref);
      updateMatter(ref, { lastActionNotice: { text: err.message, tone: "amber" } });
    }
  };

  const handleConfirmReread = async (scope) => {
    const ref = selected?.ref;
    if (!ref) { setShowReread(false); return; }
    // **The lookup runs behind the matter, not inside the modal.** It answers
    // inside its own request — no model, no PDF, nothing to poll — so the
    // modal used to stay up until it came back, with Cancel disabled. A slow
    // or hung ArcGIS call then trapped somebody on one screen with nothing to
    // press. It is minutes of somebody's time to save a page reload.
    //
    // So the modal closes on the press and the matter carries the notice
    // instead: staff can leave, work elsewhere, and the answer lands where it
    // belongs. Not awaited — nothing after it depends on the result.
    if (scope === "vicmap") {
      setShowReread(false);
      // The matter's own notice slot, which is where every other "this is
      // happening" line lives — and `loadMatter` drops it when the answer
      // lands, so it clears itself without a second piece of state.
      updateMatter(ref, { lastActionNotice: {
        text: "Looking up the planning map…",
        // Blue, like every other "this is happening" in the portal. It asked
        // for `ink` and got green, which read as an answer that had arrived.
        tone: "blue",
        // The spinner is what says it is still going, so the line does not
        // have to. It said "you can carry on, the answer lands here", which
        // is three clauses explaining a mechanism nobody asked about — and
        // the notice is attached to the matter, so carrying on has always
        // worked whether or not it said so.
        busy: true,
      } });
      lookupVicmap(ref);
      return;
    }
    setShowReread(false);
    setCompileWork({ ref, startedAt: Date.now() });
    // **Said to be a read from the first frame.** The busy bar reads the
    // matter, not the button, so between the press and the response it knew
    // only that *something* had started — and the label falls through to
    // "Compiling…", which is the one thing a re-read is not. A placeholder
    // count makes it say reading straight away; the response replaces it a
    // second later with the real number, or clears it if nothing was sent.
    updateMatter(ref, { lastActionNotice: null, documentsBeingRead: 1 });
    try {
      const res = await postJson(`/reread-certificates/${encodeURIComponent(ref)}`, { scope });
      // **What actually happened, not what the button implies.** A press that
      // found nothing outstanding has done nothing, and saying "sent to be
      // read" would leave somebody waiting for a banner that never comes.
      if (res?.reading === 0) {
        stopCompiling(ref);
        updateMatter(ref, { documentsBeingRead: 0 });
        showToast(res.message || "Every certificate has already been read", ref);
        return;
      }
      showToast(res?.message || "Certificates sent to be read", ref);
      // Optimistic, corrected by the first poll: the middleware stamps the
      // rows before answering, so the count is real — this just spares the
      // three seconds where the bar would say "Compiling…" about a read.
      updateMatter(ref, { documentsBeingRead: res?.reading || 0 });
      // **`startSeen: false` because this is the one poll that starts before
      // the work does.** Everywhere else the watch arms off a matter already
      // reading; here the button has just asked, and on the whole-bundle path
      // the matter says nothing is happening until the pack has been merged
      // and handed over. Without this the first tick reads that silence as
      // "finished".
      pollCompile(ref, { expectsVersion: false, startSeen: false });
    } catch (err) {
      stopCompiling(ref);
      // The placeholder has to go with it, or a refused read leaves the banner
      // saying the certificates are being read for ever.
      updateMatter(ref, { documentsBeingRead: 0 });
      // Both refusals are worth reading rather than dismissing: one means the
      // extractor isn't configured at all, the other that a read is already
      // running and a second would overwrite its answer with an older one.
      showErrorToast(err.message);
    }
  };
  // In the practice look a release with anything outstanding is reminded of it
  // first (`releaseOutstandingFor`); Continue opens the send dialog as before.
  const [confirmRelease, setConfirmRelease] = useState(null);
  const handleRelease = () => {
    // In both looks: it is the one reminder of the searches review before a
    // contract goes out.
    const outstanding = releaseOutstandingFor(selected);
    if (outstanding) { setConfirmRelease(outstanding); return; }
    setSendTab("agent");
  };

  // **Sending it to a colleague, which is not releasing it.** The middleware
  // leaves the matter's status, recipient and download link untouched, so
  // nothing here reloads the matter as though something had changed — the only
  // trace is a staff-only timeline line, which the History panel picks up on
  // its own.
  //
  // Throws on failure rather than handling it here, the same as a release: the
  // modal stays open and shows the reason, because "nothing compiled" and "too
  // large to attach" are both decisions to make right there.
  // ── The vendor's own copy, which is not a release ──────────────────────
  //
  // **Nothing here marks the matter released.** The middleware leaves
  // `status`, `releasedTo`, `releasedVersion` and the token exactly where they
  // were, so a portal that updated any of them locally would show a released
  // badge over a matter that has not gone out — until the next reload said
  // otherwise. The matter is reloaded instead, because the timeline gained a
  // line and, where a signature was asked for, so did the signing state.
  const handleConfirmVendorRelease = async ({ requestSignature, note, emails, cc }) => {
    const ref = selected.ref;
    const data = await postJson(`/release-to-vendor/${encodeURIComponent(ref)}`, {
      ...(PORTAL_USER ? { viewer: PORTAL_USER } : {}),
      ...(requestSignature === true ? { requestSignature: true } : {}),
      ...(note ? { note } : {}),
      ...(emails ? { emails } : {}),
      ...(cc ? { cc } : {}),
    });
    setSendTab(null);

    // **The sample rehearses, so nothing here may pretend otherwise.** The
    // middleware wrote nothing at all, and reloading would only confirm that.
    if (data.sample) {
      showToast(data.message
        || `Sample sent to ${data.sentTo}. The matter has not been changed.`, ref);
      return;
    }

    // **A send that reached nobody is said out loud.** One address that
    // bounces must not report as a success for the matter — the middleware
    // names exactly who did not get theirs, and a toast saying "sent" over two
    // of three vendors is how somebody comes to believe a client has their
    // contract.
    const sent = (data.emailed || []).length;
    const missed = (data.notEmailed || []).length;
    if (!sent) showErrorToast("Nobody was emailed — check the addresses above");
    else {
      showToast([
        `Contract sent to ${sent === 1 ? (data.emailed[0].name || "the vendor") : `${sent} vendors`}`,
        data.signatureRequested ? " with their signing link" : " with a link to read it",
        missed ? ` — ${missed} could not be emailed` : "",
      ].join(""), ref);
    }
    loadMatter(ref);
  };

  const handleCrossCheck = () => setShowCrossCheck(true);
  const handleConfirmCrossCheck = async (to, note, emailLink) => {
    const ref = selected.ref;
    const data = await postJson(`/cross-check/${encodeURIComponent(ref)}`, {
      to,
      ...(PORTAL_USER ? { viewer: PORTAL_USER } : {}),
      ...(note ? { note } : {}),
      ...(emailLink === true ? { emailLink: true } : {}),
    });
    setShowCrossCheck(false);
    showToast(data.message || `Sent to ${to} for cross-check`, ref);
    // The timeline gained a line, and nothing else did.
    loadMatter(ref);
  };
  // Records the release, including who it went to — which the modal collected
  // and discarded before /release-matter existed. It reports what actually
  // happened rather than what the button implies: the status moves and the
  // recipient is recorded, and the email is still a human's job.
  // Throws on failure rather than handling it here: the modal stays open and
  // shows the reason, because two of the refusals — a document too large to
  // email, and sending not configured — are decisions to make right there
  // rather than notices to dismiss.
  const handleConfirmRelease = async (to, cc, sendEmail, emailLink, updateOnly, note) => {
    const ref = selected.ref;
    const data = await postJson("/release-matter", {
      matterRef: ref, to, cc,
      // **Who is looking, for the sample matter and nothing else.** It is a
      // name rather than an address — the middleware matches it against the
      // handlers the office is observed to have, and ignores it entirely on
      // every other matter. See the sample-matter section in its CLAUDE.md.
      ...(PORTAL_USER ? { viewer: PORTAL_USER } : {}),
      ...(sendEmail === false ? { sendEmail: false } : {}),
      ...(emailLink === true ? { emailLink: true } : {}),
      ...(updateOnly === true ? { updateOnly: true } : {}),
      // What the covering letter says, as the modal previewed it. Sent only
      // when the preview answered: an absent note takes the middleware's own
      // suggestion, which is right, where an empty one would silently drop
      // the material facts it had already worked out.
      ...(note ? { note } : {}),
    });
    if (!data.ok) throw new Error("Release was not recorded");

    setSendTab(null);
    // The quiet update moves only what the existing link serves — nothing was
    // sent and the release record itself is unchanged.
    if (data.updated) {
      updateMatter(ref, { releasedVersion: data.releasedVersion });
      showToast(data.message || `The download link now serves v${data.releasedVersion}`, ref);
      return;
    }
    // **The sample rehearses, so nothing here may pretend otherwise.** The
    // middleware wrote nothing — no status, no recipient, no token — and a
    // portal that marked the matter released locally would show a released
    // badge over a matter that is not, until the next reload said otherwise.
    // It also stays on the matter rather than returning to the dashboard:
    // somebody rehearsing is still reading this screen.
    if (data.sample) {
      setSendTab(null);
      showToast(data.message
        || `Sample release sent to ${data.sentTo}. The matter has not been changed.`, ref);
      return;
    }
    updateMatter(ref, {
      status: "released",
      releasedTo: data.releasedTo,
      releasedVersion: data.releasedVersion,
      ...(data.releasedVia ? { releasedVia: data.releasedVia } : {}),
    });
    showToast(
      data.emailSent
        ? `${ref} emailed to ${data.releasedTo}`
        : `${ref} marked released to ${data.releasedTo} — send the document yourself`,
    );
    clearMatterUrl(true);
    setView("dashboard");
  };
  // Each change to the order, as it is made (DA-04). It throws on a failure
  // so the workspace can say *Order not saved*, and toasts too.
  const handleSaveOrder = async (ref, docs, searches) => {
    const { order, excluded } = orderPayload(searches, docs);
    try {
      await postJson(`/compile-order/${encodeURIComponent(ref)}`, { order, excluded });
      updateMatter(ref, { compileOrder: order, compileExcluded: excluded, compileOrderUpdatedAt: new Date().toISOString() });
    } catch (err) {
      showErrorToast("Could not save the document order", ref);
      throw err;
    }
  };
  // Archiving moves a matter out of whichever list you're looking at, so drop
  // it locally rather than leaving a row that no longer belongs.
  const setArchived = (ref, archived) => {
    showToast(archived ? "Archived" : "Restored", ref);
    setMatters(prev => prev.filter(m => m.ref !== ref));
    postJson("/archive-matter", { matterRef: ref, archived })
      .catch(() => showErrorToast(`Failed to ${archived ? "archive" : "restore"} it`, ref));
    clearMatterUrl(false);
    setView("dashboard");
    setSelected(null);
  };

  // Archive or restore several at once.
  //
  // One request per matter rather than a bulk endpoint: /archive-matter is a
  // single write, the counts here are tens not thousands, and a partial failure
  // has to be reportable per matter anyway. Sequential rather than parallel so
  // a bad key or a cold start fails the first one rather than all of them.
  //
  // Rows are removed as each write *succeeds*, not upfront — removing them all
  // first and putting the failures back would flash the list, and a matter that
  // silently reappeared would read as a bug rather than as a failure.
  // **The practice look's bulk change sets a status**, one matter at a time
  // like the archive did. A matter that no longer has the list's status leaves
  // the list.
  const handleBulkStatus = async (refs, status) => {
    setBulkBusy(true);
    const failed = [];
    for (const ref of refs) {
      try {
        await postJson(`/practice/matters/${encodeURIComponent(ref)}/status`, { status });
        setMatters(prev => (status === statusFilter
          ? prev.map(m => (m.ref === ref ? { ...m, practiceStatus: status } : m))
          : prev.filter(m => m.ref !== ref)));
      } catch (err) {
        console.error(`Failed to set ${ref} to ${status}`, err); failed.push(ref);
      }
    }
    setBulkBusy(false);
    const done = refs.length - failed.length;
    const parts = [`${done} matter${done !== 1 ? "s" : ""} set to ${status}`];
    if (failed.length) parts.push(`${failed.length} failed: ${failed.join(", ")}`);
    (failed.length ? showErrorToast : showToast)(parts.join("; "));
  };

  const handleBulkArchive = async (refs, archived) => {
    setBulkBusy(true);
    const failed = [];
    for (const ref of refs) {
      try {
        await postJson("/archive-matter", { matterRef: ref, archived });
        setMatters(prev => prev.filter(m => m.ref !== ref));
      } catch (err) {
        console.error(`Failed to ${archived ? "archive" : "restore"} ${ref}`, err);
        failed.push(ref);
      }
    }
    setBulkBusy(false);

    const done = refs.length - failed.length;
    if (failed.length === 0) {
      showToast(`${done} matter${done !== 1 ? "s" : ""} ${archived ? "archived" : "restored"}`);
    } else {
      // Named, not counted. "3 failed" leaves someone comparing two lists.
      showErrorToast(`${done} ${archived ? "archived" : "restored"}, ${failed.length} failed: ${failed.join(", ")}`);
    }
  };

  // Adding a document staff already hold.
  //
  // Deliberately does **not** compile, even when it completes the matter —
  // somebody uploading a document wants to see where it lands before a version
  // is produced. The middleware answers `allDone` and the portal offers the
  // button; the press stays a person's. A search *returning* on its own still
  // compiles by itself, unchanged.
  // The certificate lands in the searches list as a document, so the matter is
  // re-read rather than patched: its place in the merge order is the
  // middleware's to decide, and guessing would show it in one position on
  // screen and another in the PDF. Same rule as adding a document by hand.
  const handleOcCertificateDrafted = async (ref) => {
    await loadMatter(ref);
    showToast("Owners corporation certificate drafted — arrange or compile when you're ready", ref);
  };

  // Filing a document starts a read of it, and the claim that says so lands a
  // moment *after* the route has answered — it is fire-and-forget on the
  // middleware, unlike the re-read button, which stamps the rows before
  // replying. So the reload straight after the action can miss it, and then
  // nothing arms the watch and the row says *being read…* for ever.
  //
  // One re-check is the whole fix: the watch above arms itself from whatever
  // it finds, and finding nothing costs a single GET.
  const recheckForRead = (ref) => {
    setTimeout(() => { loadMatter(ref).catch(() => {}); }, 4000);
  };

  // A Drive link in place of the file: the middleware fetches it and keeps
  // its own copy, so what is merged is a file it holds.
  const handleAddDocument = async (ref, file, name, onProgress, driveFileUrl) => {
    setUploading(true);
    try {
      const path = `/documents/${encodeURIComponent(ref)}?name=${encodeURIComponent(name)}`;
      const data = driveFileUrl
        ? await postJson(path, { driveFileUrl })
        : await sendDocument(ref, path, file, onProgress);
      // Re-read rather than patched in: the document lands in the merge order
      // by its name, and guessing where would put it in a different place on
      // screen than in the PDF.
      await loadMatter(ref);
      recheckForRead(ref);
      showToast(data.allDone
        ? `"${data.name}" added — every search is now accounted for. Compile when you're ready.`
        : `"${data.name}" added`, ref);
      return data;
    } catch (err) {
      showErrorToast(err.message);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // A document that came back and matched nothing, put where it belongs.
  //
  // Two right answers, so two actions: it belongs against a search that was
  // ordered, or it is a document nobody ordered and belongs in the pack on its
  // own. Read back afterwards rather than patched in — where it lands in the
  // merge order is the middleware's to decide.
  const handlePlaceReturn = async (ref, returnId, searchId) => {
    try {
      const data = await postJson(`/unmatched-returns/${encodeURIComponent(ref)}/${encodeURIComponent(returnId)}/place`, { searchId });
      await loadMatter(ref);
      recheckForRead(ref);
      showToast(data.allDone
        ? `Placed against "${data.name}" — every search is now accounted for`
        : `Placed against "${data.name}"`, ref);
    } catch (err) {
      showErrorToast(err.message, ref);
    }
  };

  // A document that is not for this matter at all — set aside, never deleted.
  //
  // The row is the record that the firm received it, so it stays and simply
  // stops holding the matter's completion. It can be the press that completes
  // a matter, and like every other one here it does not compile.
  const handleDismissReturn = async (ref, returnId) => {
    try {
      const data = await postJson(`/unmatched-returns/${encodeURIComponent(ref)}/${encodeURIComponent(returnId)}/dismiss`, {});
      await loadMatter(ref);
      showToast(data.completed
        ? "Set aside — every search is now accounted for. Compile when you're ready."
        : "Set aside — it is no longer holding this matter", ref);
    } catch (err) {
      showErrorToast(err.message, ref);
    }
  };

  const handleAddReturn = async (ref, returnId) => {
    try {
      const data = await postJson(`/unmatched-returns/${encodeURIComponent(ref)}/${encodeURIComponent(returnId)}/add`, {});
      await loadMatter(ref);
      recheckForRead(ref);
      showToast(`"${data.name}" added to the matter`, ref);
    } catch (err) {
      showErrorToast(err.message, ref);
    }
  };

  // Swapping the file on a document that is already here.
  //
  // The row keeps its name and its place — a replacement is the same document,
  // better scanned — so the only thing that moves is which file it points at.
  // Read back rather than patched in, like adding one: the middleware decides
  // what the row now says about itself.
  // One route, two things it can be doing — and the difference is worth
  // saying. A row that already has a document is being corrected; a row still
  // waiting for one has just had it arrive, which may complete the matter.
  const handleReplaceDocument = async (ref, search, file, driveFileUrl, onProgress) => {
    const path = `/documents/${encodeURIComponent(ref)}/${encodeURIComponent(search.id)}/replace`;
    const had = hasFile(search);
    setUploading(true);
    try {
      const data = driveFileUrl
        ? await postJson(path, { driveFileUrl })
        : await sendDocument(ref, path, file, onProgress);
      await loadMatter(ref);
      recheckForRead(ref);
      const name = search.displayName || search.name;
      if (had) showToast(`"${name}" replaced — re-compile when you're ready`, ref);
      else if (data?.completed) {
        showToast(`"${name}" attached — every search is now accounted for. Compile when you're ready.`, ref);
      } else showToast(`"${name}" attached`, ref);
    } catch (err) {
      showErrorToast(err.message, ref);
    } finally {
      setUploading(false);
    }
  };

  // What a document is called.
  //
  // **The stored name is never touched.** Trisearch's own wording is what the
  // merge order, the account-line classification and the import's pairing all
  // read, and the nightly import re-posts every row — so the middleware keeps
  // it and stores the typed name beside it. Clearing the box puts the
  // supplier's wording straight back, which is what makes renaming safe.
  //
  // Re-read rather than patched in: `displayName` is the middleware's, and
  // section 13's attachment list is built from the same rule, so guessing the
  // new label here would be a second copy of it.
  const handleRenameDocument = async (ref, search, name) => {
    const was = search.displayName || search.name;
    try {
      await postJson(
        `/documents/${encodeURIComponent(ref)}/${encodeURIComponent(search.id)}/rename`,
        { name });
      await loadMatter(ref);
      showToast(name ? `Renamed to "${name}"` : `"${was}" is back to what the supplier called it`);
    } catch (err) {
      showErrorToast(err.message);
    }
  };

  // Reordering from the matter screen.
  //
  // **The same store Arrange documents writes**, so the two screens cannot
  // disagree about the order the PDF comes out in. `excluded` is sent back
  // unchanged: this screen orders documents, it does not remove them, and
  // omitting the field would clear every removal staff had made.
  //
  // Saved on the drop rather than behind a button — the previous version of
  // this list let staff drag rows that were put back by the next poll, which is
  // the worst possible outcome on the screen that decides what goes into a
  // legal document.
  // Leaving a document out of the pack, or putting it back, from the searches
  // list: the same `/compile-order` write as Arrange documents, the saved
  // order untouched.
  const handleExcludeSearch = async (ref, doc, out) => {
    const current = selected?.compileExcluded || [];
    const excluded = out ? [...new Set([...current, doc.id])] : current.filter(id => id !== doc.id);
    const order = selected?.compileOrder || [];
    updateMatter(ref, { compileExcluded: excluded, compileOrderUpdatedAt: new Date().toISOString() });
    try {
      await postJson(`/compile-order/${encodeURIComponent(ref)}`, { order, excluded });
      showToast(`"${doc.displayName || doc.name}" ${out ? "removed from the files" : "added back to the files"} — re-compile to see it`, ref);
    } catch (err) {
      showErrorToast("Could not save the document order", ref);
      await loadMatter(ref);
    }
  };

  const handleReorderSearches = async (ref, order) => {
    const excluded = selected?.compileExcluded || [];
    updateMatter(ref, { compileOrder: order });
    try {
      await postJson(`/compile-order/${encodeURIComponent(ref)}`, { order, excluded });
    } catch (err) {
      showErrorToast("Could not save the document order");
      await loadMatter(ref);
    }
  };

  // Removing a document staff added.
  //
  // Confirmed in a dialog rather than undone with a toast: the file is gone
  // from the bucket the moment this runs, so there is nothing an Undo could
  // put back. Archiving a matter is reversible and gets the toast; this does
  // not, and the difference should be visible before the click, not after.
  const handleDeleteDocument = (ref, doc) => setDeletingDocument({ ref, doc });

  const confirmDeleteDocument = async () => {
    const { ref, doc } = deletingDocument;
    setDeletingDocument(null);
    try {
      const res = await apiFetch(
        `/documents/${encodeURIComponent(ref)}/${encodeURIComponent(doc.id)}`,
        { method: "DELETE" },
      );
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Delete returned ${res.status}`);
      // Re-read for the same reason the upload does: the counts and the merge
      // order both move, and guessing at them here would disagree with the PDF.
      await loadMatter(ref);
      showToast(`"${data.name || doc.name}" removed`);
    } catch (err) {
      showErrorToast(err.message, ref);
    }
  };

  const handleArchive = () => setArchived(selected.ref, true);
  const handleUnarchive = () => setArchived(selected.ref, false);

  const handleNotesChange = (notes) => {
    const ref = selected.ref;
    updateMatter(ref, { notes });
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(() => {
      postJson("/update-notes", { matterRef: ref, notes })
        .catch(() => showErrorToast("Failed to save notes"));
    }, 1000);
  };
  // Marking the last outstanding search Not Required is the one way staff
  // complete a matter by hand. The middleware answers `allDone` so this doesn't
  // have to work it out from the search list (and get a different answer than
  // the compile would), and so the banner appears on the click rather than
  // whenever the next poll happens to run.
  //
  // **`allDone` is not "a compile started", and reading it as one showed a
  // progress bar over nothing.** Settling a search stopped compiling on the
  // middleware side — marking one Not Required is somebody writing a document
  // off, and the compile that follows is their decision too — and this went on
  // raising the banner. So staff saw "Compiling… Merging the documents" count
  // to its three-minute budget with nothing running, and Stop had nothing to
  // stop, which is how one change was reported as two separate bugs.
  //
  // `compiling` is the middleware's own answer to the only question the banner
  // is about: did this write actually start one. Falsy from an older
  // middleware, which is the safe direction — no bar rather than a wrong one.
  // **The save lands and the list does not hear about it.** `SearchList`
  // updates its own rows optimistically, so the matter screen is right
  // immediately — but `matters` still holds the counts from whenever the
  // dashboard was last built, and `handleSelect` seeds a re-opened matter from
  // that list item. So marking four searches not required read "15 of 17" on
  // the matter, "11 of 17" on the dashboard, and 11 again on going back in,
  // until a full page refresh. Nothing was lost: all four were `Not Required`
  // in Firestore the whole time.
  //
  // `loadMatter` writes both `selected` and the row in `matters`, so one call
  // puts every view back in agreement with what was actually stored. Awaited
  // after the post rather than run beside it, or it races the write it is
  // meant to read.
  //
  // **Through the staff route, never `/write-search`.** That one is Zapier's:
  // it runs as the default firm, so another firm's press landed on the default
  // firm's same-numbered matter, and once `ZAPIER_TOKEN` is set it refuses the
  // staff key — a 401 here, which drops staff to sign-in (#519). The staff
  // route runs as the session's firm and answers the same shape.
  //
  // **The fallback is for one deploy window, and is narrow on purpose.** The
  // middleware deploys by hand and this repo on merge, so for a while this
  // bundle can meet a middleware without the route. Express answers an unknown
  // route 404 with an HTML body; the route's own 404s (no such matter, no such
  // row) are JSON, carry `body`, and must never be retried on the Zapier door,
  // which would create the matter they refused. Remove it once the route is
  // live everywhere.
  const postSearchStatus = async (matterRef, searchId, status) => {
    try {
      return await postJson(
        `/documents/${encodeURIComponent(matterRef)}/${encodeURIComponent(searchId)}/status`,
        { status });
    } catch (err) {
      if (err.status === 404 && !err.body) {
        return postJson("/write-search", { matterRef, searchId, status });
      }
      throw err;
    }
  };
  const handleSearchStatusChange = async (matterRef, searchId, status) => {
    try {
      const data = await postSearchStatus(matterRef, searchId, status);
      if (data.compiling) {
        setCompileWork({ ref: matterRef, startedAt: Date.now() });
        pollCompile(matterRef);
      }
      await loadMatter(matterRef).catch(() => {});
    } catch {
      showErrorToast(`Failed to update search status`);
    }
  };

  // What the practice look's title bar needs — its search finds a matter in
  // the list this page already holds, and a person in the card file.
  const railOn = practiceOn && !IN_FRAME && !POPOUT && !loading && !denied;
  const matterTabsOn = !loading && !denied && !POPOUT && stripOn && openMatters.length > 0;
  const titleSearch = {
    api: apiFetch,
    matters,
    onOpenMatter: (m) => handleSelect(m),
    onOpenContact: (id) => { setContactToOpen(id); setView("contacts"); },
    onSearchAll: (q) => { setSearch(q); handleNav("matters", { keepSearch: true }); },
  };
  const practiceShell = practiceOn ? {
    // The left menu starts under the open-matters strip when there is one.
    railTop: TITLE_BAR_H + (matterTabsOn ? MATTER_TABS_H : 0),
    search: titleSearch,
    // Opening a matter is the main module's, and staff's: the number is the
    // firm's own counter on the middleware.
    onNewMatter: IS_ADMIN && !POPOUT ? () => setNewMatterOpen(true) : null,
  } : null;

  // A matter just opened goes on the list at the top and opens, read from the
  // middleware like any other (`handleSelect` → `loadMatter`).
  const handleMatterOpened = (ref, row) => {
    setNewMatterOpen(false);
    const fresh = { id: ref, matterRef: ref, ref, archived: false, status: "searches_pending", searchCount: 0, returnedCount: 0, ...row };
    setMatters(prev => (prev.some(m => m.ref === ref) ? prev : [fresh, ...prev]));
    handleSelect(fresh);
    showToast(`Matter ${ref} opened`);
  };

  // The Section 32 module's matter page: on its own in the classic look, the
  // Section 32 tab of the main module in the practice look.
  const s32Page = view === "detail" && selected
    ? <MatterDetailV2 key={selected.ref} embedded={workspaceOn} matter={selected} onOrderSearches={IS_ADMIN && selected.ref !== tenant?.sampleMatterRef ? handleOrderSearches : undefined} searchesLoading={searchesLoading} compiling={Boolean(compileWorkFor(compileWork, selected.ref))} compileStartedAt={compileWorkFor(compileWork, selected.ref)?.startedAt ?? null} onStop={handleStopWork} stopping={stopping} stopRequested={Boolean(compileWorkFor(compileWork, selected.ref)?.stopRequested)} onBack={handleBack} onArrangeDocuments={handleArrangeDocuments} onRelease={handleRelease} onCrossCheck={handleCrossCheck} onManualCompile={handleManualCompile} onReread={() => handleReread(selected.ref)} onNotesChange={handleNotesChange} onArchive={handleArchive} onUnarchive={handleUnarchive} onUndoToast={showUndoToast} onSearchStatusChange={handleSearchStatusChange} onSignaturesChanged={() => loadMatter(selected.ref)} onSaleReviewed={() => loadMatter(selected.ref)} onAddDocument={handleAddDocument} onDeleteDocument={handleDeleteDocument} onReplaceDocument={handleReplaceDocument} onRenameDocument={handleRenameDocument} onReorderSearches={handleReorderSearches} onExcludeSearch={handleExcludeSearch} onOcCertificateDrafted={() => handleOcCertificateDrafted(selected.ref)} onPlaceReturn={handlePlaceReturn} onAddReturn={handleAddReturn} onDismissReturn={handleDismissReturn} uploading={uploading} fieldRegistry={fieldRegistry} tenant={tenant} sampleRef={tenant?.sampleMatterRef || ""} savingFields={savingFields} onAdoptAgent={handleAdoptAgent} onRevokeAgentLink={handleRevokeAgentLink} adoptingAgent={adoptingAgent} onSaveToDirectory={handleSaveToDirectory} onSaveField={handleSaveField} onSaveFields={saveContractFields} onSaveClause={saveClauseOverride} onRemoveClause={removeClause} savingClause={savingClause} onRevertField={handleRevertField} onRevertAllFields={handleRevertAllFields} revertingAll={revertingAll} onNotRequired={handleNotRequired} markingNotRequired={markingNotRequired} savingConditions={savingConditions} onSaveSpecialConditions={handleSaveSpecialConditions} savingBlockClauses={savingBlockClauses} onSaveBlockClauses={handleSaveBlockClauses} compiledExpand={expandSignalFor(compiledExpand, selected.ref)} />
    : null;
  const searchOrderPage = view === "searchOrders" && IS_ADMIN
    ? <SearchOrderPage key={orderFor ? orderFor.ref : "all"} api={apiFetch} matter={orderFor} onBack={orderFor ? handleBackFromOrder : handleBack} />
    : null;

  return (
    <PracticeLook.Provider value={practiceShell}>
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "Inter, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />
      {!loading && !denied && !POPOUT && <PortalNav active={navTabFor(workspaceOn && view === "searchOrders" && orderFor ? "detail" : view, showArchived && !practiceOn)} onGo={handleNav} search={stripOn && !practiceOn ? titleSearch : null} />}
      {/* **Full width, under the title bar**, as the mock-up had it: the open
          matters are the window's, not the page's, so the strip runs over the
          left menu rather than beside it, and stays put as the page scrolls. */}
      {matterTabsOn && (
        // Across the window under the brand bar on the portal's own site; in
        // the page's centred column, flush with the menu row, wherever that
        // row is the classic one (steps 1–2, and the WordPress frame).
        <div style={{ position: "sticky", top: practiceOn && !IN_FRAME ? TITLE_BAR_H : 0, zIndex: 85, ...(practiceOn && !IN_FRAME ? {} : { background: T.bg }) }}>
          <div style={practiceOn && !IN_FRAME ? undefined : { maxWidth: PAGE_MAX, margin: "0 auto", padding: "0 24px" }}>
            <MatterTabs matters={openMatters} activeRef={selectedRef} homeActive={view === "dashboard"} inline={!practiceOn || IN_FRAME}
              onOpen={(ref) => handleSelect(matters.find(m => m.ref === ref) || { ref })}
              onClose={closeMatterTab} onHome={() => (practiceOn ? (setSearch(""), handleBack()) : handleNav(showArchived ? "archived" : "matters"))} />
          </div>
        </div>
      )}
      <div style={{ marginLeft: railOn ? RAIL_W : 0 }}>
      <main style={{ maxWidth: PAGE_MAX, margin: "0 auto", padding: "24px" }}>
        {loading ? <LoadingSpinner /> : denied ? (IN_FRAME ? <AccessDenied /> : <StaffSignIn />) : (
          <>
            {!POPOUT && firmAppearance && !LOOKS.includes(UI_CHOICE) && (
              // The classic matter page's bar pulls itself 24px up to sit
              // flush with the top of the page, over whatever is above it;
              // the card leaves it that room rather than lose its bottom.
              <div style={{ marginBottom: view === "detail" && !workspaceOn ? 24 : 0 }}>
                <LookNews step={lookStep} firmStep={firmStep} seen={lookSeen} onSeen={markLookSeen}
                  onKeepPrevious={() => { markLookSeen(); choosePersonalLook(LOOKS[lookStep - 1]); }}
                  onFollowFirm={() => choosePersonalLook("")} />
              </div>
            )}
            {view === "dashboard" && <Dashboard matters={matters} sort={listSort} setSort={setListSort} returnTo={listReturn} onSelect={handleSelect} sampleRef={tenant?.sampleMatterRef || ""} onOpenSample={tenant?.sampleMatterRef ? () => handleSelect({ ref: tenant.sampleMatterRef }) : null} filter={filter} setFilter={setFilter} search={search} setSearch={setSearch} showArchived={showArchived} onBulkArchive={handleBulkArchive} bulkBusy={bulkBusy}
              statusFilter={practiceOn ? statusFilter : null} onStatusFilter={practiceOn ? chooseStatusFilter : undefined}
              listLoading={listLoading} onBulkStatus={practiceOn ? handleBulkStatus : undefined} />}
            {/* **Keyed on the matter, not on how many searches it has.** The
                count was in the key so that adding or removing a row re-seeded
                the detail view's local `searches`; that job belongs to the
                signature effect inside it, which catches a row *changing* as
                well. Left here it remounted the whole page on every upload —
                tearing down the Add-document dialog mid-file so it never
                reached "Added", closing every panel somebody had opened, and
                taking ContractPreview with it, which re-fetches a third of a
                megabyte and drops the reader back to page one. */}
            {/* **The main module wraps the Section 32 module, it does not replace it.**
                The matter opens on its practice record — Matter details, tasks,
                events, memos — and the page that compiles and releases the
                contract is the Section 32 tab, mounted throughout so nothing
                part-done on it is lost by looking at another tab. The sample
                matter opens on Section 32, because that is where its tour is. */}
{view === "detail" && selected && !workspaceOn && s32Page}
            {/* **Arranging and ordering searches stay inside the matter
                window** in the practice look: the matter's header and tabs
                stay where they were, and the contract tab shows the screen. */}
            {selected && workspaceOn && (view === "detail" || (view === "searchOrders" && orderFor?.ref === selected.ref)) && (
              <MatterWorkspace key={selected.ref} api={apiFetch} matter={selected} registry={fieldRegistry}
                emails={<SentEmails matterRef={selected.ref} refreshKey={`${selected.status || ""}_${selected.compileVersion || 0}_${selected.releasedVersion || 0}`} />}
                onStatusChanged={selected.ref === tenant?.sampleMatterRef ? undefined : (status) => { updateMatter(selected.ref, { practiceStatus: status, archived: status !== "Open" }); showToast(`Status set to ${status}`, selected.ref); }}
                contractBadge={<Badge status={selected.status} short />}
                headerFacts={[
                  agentDisplay(selected).name ? `Agent ${agentDisplay(selected).name}` : "",
                ]}
                headerLinks={<MatterLinks matter={selected} tenant={tenant} buttons />}
                dutiesStatus={dutiesStatus}
                onOpenDutiesForm={selected.ref === tenant?.sampleMatterRef ? undefined : async () => {
                  const outcome = await openDutiesFormFor(selected.ref);
                  if (outcome === "copied" || outcome === "unsupported") setDutiesHelp({ outcome, ref: selected.ref });
                  return outcome === "unsupported" ? "copied" : outcome;
                }}
                defaultTab={selected.ref === tenant?.sampleMatterRef || lookStep < 4 ? "s32" : "matter"}
                onlyTabs={lookStep < 4 ? STEP_TABS : null}
                onSaveContractFields={async (fields) => { const ok = await saveContractFields(fields); if (ok) announceMatterChanged(selected.ref); return ok; }}
                onMatterChanged={() => { loadMatter(selected.ref).catch(() => {}); announceMatterChanged(selected.ref); }}
                documentHref={(s) => documentUrl(selected.ref, s)} refreshSignal={refreshSignal}
                postFile={postFile} fileHref={apiUrl}
                onPopOut={IN_FRAME || POPOUT ? null : popOutMatter}>
                {view === "searchOrders" ? searchOrderPage : s32Page}
              </MatterWorkspace>
            )}
            {view === "directory" && IS_ADMIN && <DirectoryPage onToast={showToast} onError={showErrorToast} onOpenMatter={(ref) => handleSelect({ ref })} />}
            {view === "sharing" && IS_ADMIN && <PurchaserSharingPage onBack={handleBack} onOpenMatter={(ref) => handleSelect({ ref })} />}
            {view === "searchOrders" && !(workspaceOn && selected && orderFor?.ref === selected.ref) && searchOrderPage}
            {newMatterOpen && practiceOn && <NewMatterDialog api={apiFetch} onOpened={handleMatterOpened} onClose={() => setNewMatterOpen(false)} />}
            {dutiesHelp && <DutiesHelpModal outcome={dutiesHelp.outcome} matterRef={dutiesHelp.ref} onClose={() => setDutiesHelp(null)} />}
            <DutiesCoach />
            {view === "contacts" && practiceOn && <ContactsPage api={apiFetch} openId={contactToOpen} onOpenMatter={(ref) => handleSelect({ ref })} />}
            {view === "settings" && IS_ADMIN && <SettingsPage onDirtyChange={setSettingsDirty} onSaved={(msg) => showToast(msg || "Firm settings saved")} onAutomationSaved={(key, on) => showToast(automationToast(key, on))} onError={showErrorToast} />}
          </>
        )}
      </main>
      </div>

      {confirmRelease && selected && (
        <CompileConfirmModal release matter={selected} outstanding={confirmRelease}
          onConfirm={() => { setConfirmRelease(null); setSendTab("agent"); }}
          onCancel={() => setConfirmRelease(null)} />
      )}
      {compileWorkspace && selected && selected.ref === compileWorkspace && view === "detail" && (
        <CompileWorkspace
          matter={selected}
          registry={fieldRegistry}
          compiling={Boolean(compileWorkFor(compileWork, selected.ref))}
          onClose={() => setCompileWorkspace("")}
          onCompile={runCompile}
          onSaveOrder={(docs) => handleSaveOrder(selected.ref, docs, selected.searches)}
          onAddDocument={handleAddDocument}
          uploading={uploading}
          searchInbox={tenant?.searchInbox || ""}
          onExclude={(doc, out) => handleExcludeSearch(selected.ref, doc, out)}
          onRename={(doc, name) => handleRenameDocument(selected.ref, doc, name)}
          onReplace={(doc, file) => handleReplaceDocument(selected.ref, doc, file)}
          onDelete={(doc) => handleDeleteDocument(selected.ref, doc)}
          onSaveField={handleSaveField}
          onSaveFields={saveContractFields}
          onSaveClause={saveClauseOverride}
          onRemoveClause={removeClause}
          onRevertField={handleRevertField}
          savingFields={savingFields}
          savingClause={savingClause}
          onAdoptAgent={handleAdoptAgent}
          adoptingAgent={adoptingAgent}
          onSaveToDirectory={handleSaveToDirectory}
        />
      )}
      {confirmCompile && selected && (
        <CompileConfirmModal
          matter={selected}
          outstanding={confirmCompile}
          onConfirm={handleConfirmCompile}
          onCancel={() => setConfirmCompile(null)}
        />
      )}

      {/* Feedback mode. Mounted at the root because a selection can be
          anywhere, including inside the contract preview's iframe — and
          because the chip has to sit above whatever was selected rather than
          inside it. */}
      <FeedbackWatcher
        enabled={feedbackMode.enabled}
        minSelection={feedbackMode.minSelection}
        view={view}
        matterRef={selected?.ref}
        onSent={() => showToast("Feedback sent — thank you")}
      />

      {deletingDocument && <DeleteDocumentModal doc={deletingDocument.doc} onConfirm={confirmDeleteDocument} onCancel={() => setDeletingDocument(null)} />}

      {sendTab && selected && (
        <>
          <ReleaseModal matter={selected} hidden={sendTab !== "agent"} tab={sendTab} onTab={setSendTab}
            onConfirm={handleConfirmRelease} onCancel={() => setSendTab(null)} />
          <VendorReleaseModal matter={selected} hidden={sendTab !== "vendor"} tab={sendTab} onTab={setSendTab}
            onConfirm={handleConfirmVendorRelease} onCancel={() => setSendTab(null)} />
        </>
      )}
      {showCrossCheck && selected && (
        <CrossCheckModal matter={selected} ccAddress={tenant?.crossCheck?.cc}
          onConfirm={handleConfirmCrossCheck} onCancel={() => setShowCrossCheck(false)} />
      )}
      {showReread && selected && <RereadModal onConfirm={handleConfirmReread} onCancel={() => setShowReread(false)} />}

      {/* Toast stack */}
      {/* **Bottom centre, well clear of the edge.** In the corner of a large
          screen these went unseen — the eye is on the middle of the page and
          20px from the bottom right is the one place nothing else happens.
          Centred and lifted, they land where somebody is already looking. */}
      <div role="status" aria-live="polite" aria-label="Notifications" style={{ position: "fixed", bottom: 44, left: 0, right: 0, zIndex: 200, display: "flex", flexDirection: "column", gap: 6, alignItems: "center", pointerEvents: "none" }}>
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={dismissToast} onUndo={handleUndo} />
        ))}
      </div>
      <style>{`* { box-sizing: border-box; } /* **Focus shows for the keyboard, and only for the keyboard.** This used to remove the outline from every focused button, which took the ring off every button in the portal — so tabbing to Release moved focus with nothing on screen to say so. \`:focus-visible\` is the browser's own judgement of when a ring helps (the keyboard, not a click), so a mouse user sees exactly what they saw before. Ink rather than red, so it never reads as an error or as the accent. */ :focus:not(:focus-visible) { outline: none; } button:focus-visible, a:focus-visible, [role="link"]:focus-visible, [role="tab"]:focus-visible, summary:focus-visible { outline: 2px solid ${T.text}; outline-offset: 2px; } /* The two-column matter page. It has to live here rather than inline because a media query is the one thing an inline style object cannot express — and without it the rail and the content stayed side by side on a phone, squeezing the reading column to about a hundred pixels and wrapping the searches review to one word a line. */ .s32-cols { display: grid; grid-template-columns: 160px minmax(0, 1fr); gap: 12px; align-items: start; } @media (max-width: 760px) { .s32-cols { grid-template-columns: 1fr; } .s32-rail { position: static !important; top: auto !important; } } /* The reorder arrows (DA-28): quiet until the row is hovered, but at full ink on a device with no hover and while one has keyboard focus — on a tablet they are the only way to reorder. */ .s32-move { opacity: 0.3; transition: opacity 0.12s; } .s32-move.s32-move-on, .s32-move:focus-within { opacity: 1; } @media (hover: none) { .s32-move { opacity: 1; } } /* **The matter list on a phone (DA-21, DA-22).** Its five fixed columns need about 650px, so on a 390px phone the browser laid the whole app out at that width and shrank it — every font at about 60%, and Status, the column that says what needs doing, past the right-hand edge. Under 760px each row is three short lines instead — ref and chips over status; client and address; handler, opened and searches — at real size, and the column headings give way to a Sort select. The navigation stays one line that scrolls sideways rather than two rows, and stays every destination one press away rather than a menu. */ @media (max-width: 760px) { .s32-mhead { display: none !important; } .s32-msort { display: inline-flex !important; } .s32-mrow { grid-template-columns: minmax(0, 1fr) auto auto !important; grid-template-areas: "main main status" "handler opened search"; column-gap: 12px !important; row-gap: 6px !important; } .s32-mrow.s32-mrow-sel { grid-template-columns: 28px minmax(0, 1fr) auto auto !important; grid-template-areas: "check main main status" "check handler opened search"; } .s32-mc-check { grid-area: check; } .s32-mc-main { grid-area: main; } .s32-mc-status { grid-area: status; align-self: start; } .s32-mc-handler { grid-area: handler; font-size: 12px !important; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } .s32-mc-opened { grid-area: opened; } .s32-mc-search { grid-area: search; } .s32-mc-addr { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } .s32-listtools { flex-wrap: wrap; width: 100%; } .s32-listsearch { flex: 1 1 100%; } .s32-listsearch input { width: 100% !important; } .s32-portalnav { flex-wrap: nowrap !important; overflow-x: auto; scrollbar-width: none; flex: 1 1 100%; min-width: 0; } .s32-portalnav::-webkit-scrollbar { display: none; } .s32-portalnav > button { white-space: nowrap; flex: none; } } /* A modal is bounded by the screen somebody can actually see. \`100vh\` on a phone is the viewport with the browser's toolbars hidden, so a card sized against it stands taller than the visible area and pushes its own header — which is where Close is — off the top. \`dvh\` is the height that is really on screen, and it goes here because a style object cannot hold the same property twice, so the older browsers that have no \`dvh\` keep the \`vh\` line above it. On a phone the card is the screen: a sheet with square corners and no margin, since a 20px gutter buys a backdrop nobody can hit accurately and costs reading width. */ .s32-sheet { height: 100vh; height: 100dvh; } @media (max-width: 760px) { .s32-sheet { padding: 0 !important; } .s32-sheet-card { height: 100% !important; border-radius: 0 !important; border-left: none !important; border-right: none !important; } } @keyframes s32-spin { to { transform: rotate(360deg); } } /* The border chases while a model is thinking. A rotating conic gradient behind a slightly smaller opaque inner box, so only the rim of it shows — which needs a pseudo-element and so cannot be an inline style. */ @keyframes s32-chase { to { transform: rotate(1turn); } } .s32-chase { position: relative; display: inline-block; border-radius: 999px; overflow: hidden; padding: 2px; } .s32-chase::before { content: ""; position: absolute; inset: -120%; background: conic-gradient(from 0turn, transparent 0turn, transparent 0.62turn, #2f6fed 0.82turn, #8ab6ff 0.95turn, transparent 1turn); animation: s32-chase 1.1s linear infinite; } .s32-chase > * { position: relative; } @keyframes s32-indeterminate { 0% { left: -40%; } 100% { left: 100%; } } @keyframes s32-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } } /* A text link says it is one when the pointer is on it, which an inline style has no syntax for. */ .s32-link:hover { text-decoration: underline; text-underline-offset: 3px; } .s32-tab:hover { color: ${T.text} !important; } .s32-railitem:hover { background: rgba(0,0,0,0.04) !important; } .s32-issue:hover { background: rgba(0,0,0,0.025) !important; }`}</style>
    </div>
    </PracticeLook.Provider>
  );
}
